/**
 * voice-v8 — PWA-Frontend Entry Point.
 */

// Session-Wache ZUERST: eine widerrufene Session (auth-revoked) → ALLE lokalen
// Daten löschen + sauber zur Anmeldung (kein Stale-Daten-Leck eines Vornutzers).
import { ergebnisAus, brauchtAbschluss, zaehltAlsArbeit, type Ergebnis } from "./lib/abschluss.js";
import { baueAbschlussKarte } from "./lib/abschluss-karte.js";
import { fuelleHdAuswahl, type HdKonto } from "./lib/hd-auswahl.js";
import { aboModellAus, kaltFuerChip, waermeAdresse, type AboModell } from "./lib/waerme-modell.js";
import {
  starteAnpassendenTakt,
  starteSichtbarenTakt,
} from "./lib/abfrage-takt.js";
import { meldeSpur, starteMikroSpur } from "./lib/mikro-spur.js";
import { meldeTastaturOhneFeld, starteScrollWache } from "./lib/scroll-wache.js";
import { nutzerScrolltGerade, wartAufRuhe } from "./lib/scroll-ruhe.js";
import { schreibeScrollKnopf } from "./lib/scroll-knopf.js";
import { beiRuckelAenderung, gesteuert, ruckelAus, ruckelKontoNachfuehren, sanft, wachhalteVideoErlaubt } from "./lib/ruckel-schalter.js";
import { starteScrollTakt } from "./lib/scroll-takt.js";
import { starteBildrateHinweis } from "./lib/bildrate-hinweis.js";
import { starteDomWachstum } from "./lib/dom-wachstum.js";
import { TASTATUR_AB_PX, feldImBlick, sichtLage, tastaturOffen } from "./lib/tastatur.js";
import { claudeName, nameFuerModell } from "../shared/modell-namen.js";
import { PTT_LEER_MUSTER } from "../shared/ptt-leer.js";
import { aboAnzeige, aboWahlAus } from "../shared/abo-modelle.js";
import { ANRUF_BEENDEN, jetztAuflegen } from "../shared/anruf-beenden.js";
import { KANN_ALLTAG } from "../shared/alltag-steuern.js";
import { zeigeMorseImMenue } from "./lib/morse-menue.js";
import { zeigeNeunEintrag } from "./lib/neun-eintrag.js";
import { isAlltagRecorderLive, isAlwaysRecordSupported } from "./lib/native-recorder.js";
import { istEigenesPttEcho } from "../shared/geraet-kennung.js";
import type { LiveStand } from "../shared/live-stand.js";
import { EIGENES_GERAET } from "./lib/geraet-kennung.js";
import { autoMikroAn, autoMikroGeltend, schalteAutoMikroChip, zeigeAutoMikroChip } from "./lib/auto-mikro-chip.js";
import { jevMoeglich, jevStufeGeschuetzt } from "./lib/jev-konto.js";
import {
  aufnahmeBeanspruchen,
  aufnahmeFreigeben,
  autoMikroNichtVorn,
  aufnahmeVomKonto,
  besitzVomKonto,
  beiFertigAnderswo,
  beiRennenVerloren,
  doppelstartZeile,
  fertigVomKonto,
  fremdeAufnahme,
  reihenfolgeAn,
  setzeAufnahmeQuelle,
  setzeReiheOffenQuelle,
  stimmenFertig,
  stimmeVomKonto,
  vornVomKonto,
  zweitesGeraetSperrt,
} from "./lib/seiten-abgleich.js";
import { REIHE_ENTWICKLER_HOECHSTENS, REIHE_ENTWICKLER_WARTEN_MS } from "../shared/stimme-konto.js";
import { markiereReiter } from "./lib/nav-markierung.js";
import { kanalAusQuelle, setzeKanalVermerk, type VermerkKanal } from "./lib/kanal-vermerk.js";
import { adressatenAus, entwicklerMerkmal, karteMerkmal, merkmalAusMetadaten, setzeFortsetzungsVermerk, type BlasenMerkmal } from "./lib/fortsetzung-vermerk.js";
import { ZUSATZ_ANGESPROCHEN, adressatVermerkAus, setzeAdressatVermerk, zusatzAusEreignis } from "./lib/adressat-vermerk.js";
import { fortsetzungBeiAufnahmestart, neuerFortsetzungSpeicher } from "./lib/fortsetzung.js";
import { darfMikroOeffnen, unterbrechePille, unterbrechenMikroOffen, unterbrechenVerwerfen } from "./lib/unterbrechen.js";
import { fertigDruecken, fertigHaeltSchmied, fertigHaeltStimme, fertigNurFuerKnopf, knopfWiedergabeLaeuft, type FertigKanal } from "./lib/fertig.js";
import { baueVorlesePille, verbergeVorlesePille, zeichneVorlesePille, type VorlesePille } from "./lib/vorlese-pille.js";
import { FORTSETZUNG_FENSTER_MS, type FortsetzungAngabe } from "../shared/fortsetzung.js";
import { ausBremse, bremseWeg, merkeBremse, zusammenJetztSenden, type BremseMerker } from "./lib/bremse-feld.js";
import { BREMSE_ZURUECK_HINWEIS, bremsFristMs, notausgangZuruecklegen, satzOffen } from "../shared/satz-vollstaendig.js";
import { bindeNutzerMenue } from "./lib/nutzer-menue.js";
import { aktualisiereVerbrauch, ermittleZugang, nachfragenBeiRueckkehr, setzeAnrufSichtbar } from "./lib/verbrauch-kachel.js";
import {
  kontoGewechselt,
  starteAbgleich,
  starteAbgleichSpaeter,
} from "./lib/einstellungen-abgleich.js";
import type { SpeicherErgebnis } from "./lib/einstellungen-ausstehend.js";
import { entwurfZurueck } from "./lib/entwurf-halten.js";
import { benenneChip, haeltChipNameAktuell, nachWerkzeug, waehleTiefe, zeigeTiefe } from "./lib/antwort-tiefe-schalter.js";
import { abschiedAbgeleitetAnsehen, abschiedAnsehen, abschiedEinloesenArt, merkeAbschied, zugOhneAbschied } from "./lib/gespraech-ende.js";
import { merkeRunde, rundeAnsehen, rundeEinloesen, zugOhneRunde } from "./lib/frage-runde.js";
import { jevMikroAnsehen, jevMikroEinloesen, merkeJevMikro, zugOhneJevMikro } from "./lib/jev-mikro.js";
import {
  abspielAuftrag,
  abspielenNachStimme,
  abspielenOhneKarte,
  KARTE_TRAEGT_PLAYER,
  abspielenWartet,
  baueAntippAngebot,
  merkeAbspielen,
  nimmKartenStart,
  zugOhneAbspielen,
  type AbspielAuftrag,
  type Abspieler,
} from "./lib/abspielen.js";
import { merkeSpiegelOffen, nimmSpiegelOffen, vergissSpiegelOffen } from "./lib/spiegel-offen-merker.js";
import { nachAusklang } from "./lib/ausklang.js";
import { FRAGE_IM_HINTERGRUND, FrageRueckkehr, antwortAnderswo, aufnahmeAnderswo, jaKarteOffen } from "./lib/frage-rueckkehr.js";
import {
  klingtEtwas,
  merkeNeuLaden,
  neuLadenAuftrag,
  neuLadenDarfJetzt,
  neuLadenOhneZuruf,
  neuLadenWartet,
  zugOhneNeuLaden,
  type NeuLadenUmgebung,
} from "./lib/app-neu-laden.js";
import { hoerzaehler } from "./lib/hoerzeit.js";
import { starteSternlauf } from "./lib/stern.js";
import "./lib/session-guard.js";
import {
  applyChatStyle,
  applyTheme,
  cycleTheme,
  getChatStyle,
  getTheme,
  applyZuhauseStil,
} from "./lib/appearance.js";
import { AudioMeter } from "./lib/audio-meter.js";
import {
  darfAmServerVerwerfen,
  hapticTap,
  LEERE_AUFNAHME_BYTES,
  playBeep,
  quittungFuerVersand,
  sendeHaptik,
  unlockAudio,
  freischaltenBeiErsterGeste,
  tonKontextLaeuft,
} from "./lib/audio-feedback.js";
import { fetchMe, type MeResponse } from "./lib/auth-api.js";
import { LoginView } from "./lib/login-view.js";
import {
  entferneKopplungAusAdresse,
  KOPPLUNG_ANMELDEN_HINWEIS,
  KOPPLUNG_WEITER,
  kopplungAusAdresse,
  kopplungBeimStart,
  merkeKopplungsErgebnis,
  setzeAngemeldetesKonto,
  merkeWeiter,
  vergissWeiter,
  weiterGemerkt,
} from "./lib/panel-kopplung-rueckkehr.js";
import { tauschEinmal } from "./lib/nativ-login-riegel.js";
import { CHAT_STREAM_STALLED, streamChat } from "./lib/chat-http.js";
import { passendeBotBlase } from "./lib/bot-antwort-einmal.js";
import { ContactsView } from "./lib/contacts-view.js";
// Pre.157: FilesView entfernt — Mediathek (LibraryView) zeigt alles. Type bleibt für quick-access.
import { type FileDto } from "./lib/file-types.js";
import {
  askImageCaption,
  type ImageCaptionResult,
  type ReceiptCategory,
} from "./lib/image-caption-modal.js";
import { LibraryView, type UploadDto } from "./lib/library-view.js";
import { MemoryView } from "./lib/memory-view.js";
import {
  GEMINI_REALTIME_MODELS,
  GOOGLE_NARRATOR_VOICES,
  getElevenVoices,
  chosenReadVoice,
  vorleseStimme,
  googleHdFallbackForVoice,
  loadSettings,
  MODEL_OPTIONS,
  naechsteAutoMikro,
  mikroNachAntwort,
  mikroNachAntwortZeile,
  nachlauschFenster,
  ABO_MODELLE,
  istAboWahl,
  warmEtikett,
  type AboWahl,
  saveSettings,
  VOICE_OPTIONS,
} from "./lib/settings.js";
import { SettingsView } from "./lib/settings-view.js";
import {
  aboTitel,
  blattHinweis,
  ersatzTitel,
  istErsatzModell,
} from "./lib/settings/modell-status.js";
import { buildRechnungCard, buildToolCard, oebbHatZuege } from "./lib/tool-cards.js";
import { istKnopfRueckfrage, meldeKnopfStand } from "./lib/karten/knopf-rueckfrage.js";
import { meldeLiedStand } from "./lib/lied-karte.js";
import { anrufZustandFuerLied, liedFortschrittEreignis, schliesseLiedFortschritt } from "./lib/lied-fortschritt.js";
import { haengeRechnungsKarteAn, istRechnungsKarte, leseRechnungsKarte } from "./lib/rechnung-karte.js";
// Ticket 2660: eine Rückfrage bleibt oben stehen, mit Ton und Vorlesen.
import {
  antwortKnoepfe,
  beantworteteKarten,
  loeseRueckfrage,
  offeneRueckfrage,
  rueckfrageAusKarte,
  zeigeRueckfrage,
  type Rueckfrage,
  type VerlaufsZeile,
} from "./lib/rueckfrage-oben.js";
import {
  entferneWerkzeugKarten,
  findeZugGruppe,
  gruppenTitel,
  istSchritt,
  legeZugAn,
  leiseZeile,
  schrittZeile,
  ueberholeSchritte,
} from "./lib/schritte.js";
import { buendleRecherche, haengeRechercheKarteAn, planeRechercheOrdnung } from "./lib/recherche.js";
import { werkzeugeMitPolitik } from "../shared/karten-politik.js";
import { haengeAnzeigeAn, istAnzeigeKarte, istLeiseKarte } from "./lib/anzeige-karte.js";
import { ToolsView } from "./lib/tools-view.js";
import { ProjectsView } from "./lib/projects-view.js";
import { setViewerPlayerBuilder } from "./lib/project-file-viewer.js";
import { getActiveProject, type ProjectDto } from "./lib/projects-api.js";
import {
  PttRecorder, streamPtt, pttTiming, wachwortAusloeser, releaseWarmMic, setzeAnrufAktiv,
  warmMikroSperrenFuerTagesaufnahme, nativMeiden, mikroSitzungsStatus,
  webMikroOffen, mikroOffenOhneWarmes, meldeWebMikro, setzeWachwortVorrang, hoereAufLeitungsWechsel, PTT_MAX_MS,
  mikroVorstarten, gibVorstartFrei, vorstartPlattformGrund,
  type PttToolCall } from "./lib/ptt.js";
import { setzeVorstart, type VorstartLage } from "./lib/mikro-vorstart.js";
// 24.09.2026 (Ticket 2202): die Messzeile „Loslassen bis Text" der Probewoche.
import {
  neueTextMessung,
  textMessungAbschliessen,
  NATIV_OHNE_MITSCHRIFT,
  type TextMessung,
} from "./lib/ptt-weg.js";
import { HandsFreeVad, SILENCE_MS as FREIHAND_STILLE_MS } from "./lib/hands-free.js";
// Ticket 2730: „klebt der Chat am Ende?" — ein Besitzer für die Frage.
import { erzeugeChatFolgen } from "./lib/chat-folgen.js";
// Ticket 2873: lange Antworten zuklappen und von außen am Verlauf ändern, ohne
// dass der Leser springt — in EINEM Schritt mit gehaltenem Ort.
import { erzeugeKlapper, stelleMehrKnopfHer, type FuellArt } from "./lib/blase-klappe.js";
import { browserHaeltOrt, haltenOhneSprung, merkeVerankerung } from "./lib/scroll-ort.js";
import { ordneTagestrenner } from "./lib/tagestrenner.js";
import { PttBubble } from "./lib/ptt-bubble.js";
import { fortsetzungErlaubt, openPttModal, type PttModalHandle } from "./lib/ptt-modal.js";
import { istAbgerissenerStrom, UNTERBROCHEN_OHNE_ANTWORT } from "./lib/ptt-haenger.js";
import { holeMikroRecht, letztesMikroRecht, oeffneAppEinstellungen, stummHinweisFuer } from "./lib/mikro-recht.js";
import { neueVerworfenAblage, VERDRAENGT_HINWEIS, ZURUECKHOLEN_MS, type Verworfenes } from "./lib/verworfen.js";
import { erkenneGanzeAufnahme } from "./lib/ganz-erkennen.js";
import { KLEINSTER_TEIL_BYTES } from "./lib/diktat-teile.js";
import { neueFeldAuftraege, zurueckholBefehl, type FeldAuftrag } from "./lib/feld-auftraege.js";
import { fuegeEin, stelleImFeld, type FeldStand } from "./lib/diktat-cursor.js";
// Ticket 2536: der Satz nach „Hey Vespera" — senden oder Mikro wie bisher.
import {
  satzMerkmale,
  wachwortAusfuehren,
  wachwortNachholenStoertWiedergabe,
  WachwortVormerkung,
  wachwortImHintergrund,
  wachwortFeldText,
  type WachwortSatz,
} from "./lib/wachwort-satz.js";
import { mikroBeimVorlesenAnwenden, mikroBeimVorlesenAuffrischen } from "./lib/mikro-beim-vorlesen.js";
import { beendeMailZug, beginneMailZug, mailStimmeAus, merkeMailAntwort, uebernimmLesemodus } from "./lib/lesemodus.js";
import { LAUTSPRECHER_EREIGNIS, lautsprecherAus, loadAutoPlay, saveAutoPlay } from "./lib/lautsprecher.js";
import { wachwortAntwortMelden } from "./lib/wachwort-antwort.js";
import { halteZeitAbAufnahme, istNachgereichterDruck } from "./lib/ptt-geste.js";
import { TtsQueue } from "./lib/tts-queue.js";
import { setzeVesperaTonweg } from "./lib/vespera-satz.js";
import { weichtDerSprechtaste } from "./lib/sprechtasten-vorrang.js";
import { stromAdresse, stromWegFuer, tokenFuerSatz } from "./lib/tts-strom.js";
import { pcmAdresseAusToken, pcmWegFuer, webAudioDa } from "./lib/tts-pcm.js";
import { pcmRueckfall } from "./lib/tts-pcm-rueckfall.js";
import { pcmGedaechtnis } from "./lib/tts-pcm-gedaechtnis.js";
import { TIPPEN_EREIGNIS, fortsetzBeruehrungGerade } from "./lib/datei-spieler.js";
import { tippAufLaufendeAntwort } from "./lib/gleiche-wiedergabe.js";
import { shareMedia } from "./lib/share.js";
import { baueBildBlase, makeShareButton } from "./lib/bild-blase.js";
import { baueAbbild } from "./lib/bildschirmfoto.js";
import { icon } from "./lib/icons.js";
import {
  initDeepQuestions,
  checkDeepQuestion,
} from "./lib/deep-questions-view.js";
import { renderMarkdown } from "./lib/markdown.js";
import { baueReaktionenAnzeige } from "./lib/reaktionen-chips.js";
import { baueAuftragsAnzeige } from "./lib/auftrags-karte.js";
import { baueJobAnzeige, sendeProzessStopp } from "./lib/job-karte.js";
import { baueLaufLeiste } from "./lib/lauf-leiste.js";
import { baueAktivitaetsZeile } from "./lib/aktivitaets-zeile.js";
import { kontoGruppen, kontoLage, setzeAnrufImPaket } from "./lib/konto-lage.js";
import { baueMorgenAnzeige, NACHSEHEN_MS } from "./lib/morgenreport-karte.js";
import { ZIEL_BESPRECHEN_EREIGNIS, entwurfMitZiel } from "../shared/morgenreport.js";
import { zeigeRauschKarte } from "./lib/rausch-karte.js";
import { ohneKopierBloecke } from "../shared/kopier-bloecke.js";
import { anzeigeEntwickler, baueEntwicklerBlase, setzeUnvorgelesen } from "./lib/entwickler-blase.js";
import { erzeugeVorleseReihe } from "./lib/vorlese-reihe.js";
import { bildAdressenAusEcho, bildEchoAn, bildSchonGezeichnet } from "./lib/bild-echo.js";
import { haengeSchmiedBilderAn } from "./lib/schmied-bilder.js";
import { BILD_ALT as SCHMIED_BILD_ALT, bilderAus, type SchmiedBild } from "../shared/schmied-bilder.js";
import { JA_KARTE_HINWEIS, gesprochenesJaVerwerfen, jaKarteHaeltMikro } from "./lib/ja-karte-offen.js";
import { baueFrageKnoepfe, buendleFrageMelder, frageTonErlaubt, karteAntwortSteht, setzeKarteVermerk, sperreBeantworteteFragen, type FrageAntwort, type FrageGesperrt, type FrageMelder, type FrageSender } from "./lib/frage-knoepfe.js";
import { sendeKarte } from "./lib/karte-anfrage.js";
import { karteFehlerText } from "../shared/schmied-karte.js";
import { fordereBeleg } from "./lib/beleg-anfrage.js";
import { baueRueckfrageKarte, sperreBeantworteteRueckfragen, type RueckfrageMelder } from "./lib/rueckfrage-karte.js";
import { entwicklerHauptAn, entwicklerKontoAn, entwicklerImChatAn, entwicklerImChatFuerKonto, frageAus, infoAus, istEntwicklerZeile, kartenantwortGilt, rueckfrageIdAus, weiterAus } from "../shared/entwickler-im-chat.js";
import { SCHMIED_NAME, entwicklerStimmeFuerKonto, entwicklerTempoFuerKonto, entwicklerVorlesenAn, schmiedFrageLeseMs } from "../shared/schmied.js";
import { ZUSATZ_IDS, entwickler, istZusatzId, type EntwicklerId, type ZusatzId } from "../shared/entwickler.js";
import { ANDERSWO_GESPROCHEN, entwicklerEinstellungsHoerer, entwicklerTtsAnfrage, schmiedBeitragFrisch, schmiedBeitragKennung, schmiedGanzGilt, schmiedSchlange, schmiedStilleBesetzt, schmiedVorlesenGilt, schmiedWarteAufStille, wartetAufText, hueterHintergrundVorlesenGilt, hintergrundRauschen, hintergrundFrageOeffnetMikro, zusatzVorlesenGilt, type SchmiedTonAnfrage } from "./lib/schmied-stimme.js";
import { erzeugeSchmiedWarteblase } from "./lib/schmied-warteblase.js";
import { WARTE_STANDARD_TEXT, baueVesperaWarteblase, baueWartePunkte, entferneWarteblase, setzeAktivitaet, setzeWartetext } from "./lib/warteblase.js";
import { zeigeSchmiedArbeit } from "./lib/schmied-arbeit.js";
import { baueEntwicklungKarte } from "./lib/entwicklung-karte.js";
import { OFFEN_SCHLUESSEL, OffeneFragen, frageVon, verdrahteOffeneFragen, type OffenAnzeige, type OffeneFrage } from "./lib/offene-fragen.js";
import { MAIL_OFFEN_SCHLUESSEL } from "./lib/mail-text.js";
import { SCHMIED_ANGESPROCHEN, SchmiedWarten, meldeSchmiedAngesprochen, MIKRO_BEREIT_HINWEIS, nurHinweisStattMikro, haltAnVesperaGilt, nachZurueckhalten, martinTippt, imEingabefeld, tasteSchliesstMikro, tippHinweisZeigen, TIPP_HINWEIS_NACH_MS, TIPP_SPERRE_HINWEIS, schmiedFragtMartin, schmiedAnAndereEntwickler, zielOhneMartin, kurzstandOhneWarten, type SchmiedAngesprochenDetail, type SchmiedOeffnetWeil } from "./lib/schmied-warten.js";
import { EMPFAENGER_GEAENDERT, baueEmpfaengerWahl, empfaengerWahl, sendeMitWahl, wartenderName } from "./lib/empfaenger-wahl.js";
import type { MartinsEmpfaenger } from "../shared/dreier-adressat.js";
import { fertigSeitMs, zugAufnahme, zugEndeStand, zugKennung } from "./lib/zug-ende.js";
import { buildMeldungCard } from "./lib/meldung-card.js";
import {
  buildCloseButton,
  buildPageHeader,
  attachEdgeSwipeBack,
  buildLoadingHint,
} from "./lib/sheet-ui.js";
import { maybeStartOnboarding } from "./lib/onboarding-view.js";
import {
  registerPageRoute,
  setPageHash,
  clearPageHash,
  openPagesFromHash,
  istSeitenHash,
  initPageRouter,
  schliesseAlleSeiten,
  eineSeiteOffen,
  oeffneSeite,
} from "./lib/page-routes.js";
import { VoiceGuard } from "./lib/voice-guard.js";
import { replayPendingGuard } from "./lib/voice-guard-store.js";
import { type VoiceState } from "./lib/webrtc.js";
import { createVoiceClient } from "./lib/voice-client.js";
import {
  applyNativeViewportFit,
  getCapacitorPlugin,
  isNativePlatform,
} from "./lib/native-voice.js";
import {
  ANRUF_HOECHSTDAUER,
  ANRUF_HOECHSTDAUER_TEXT,
  ANRUF_NICHT_MEHR_GUELTIG,
  ANRUF_VORBEI,
  anrufIdAusLink,
  anrufLink,
  behandleAnrufLink,
  behandeltSpeicher,
  type AnrufLinkWeg,
} from "./lib/anruf-link.js";
import {
  verbindeAnrufAuflegen,
  verbindeAnrufSpur,
  verbindeVoipToken,
} from "./lib/anruf-nativ.js";
import { anrufKarteTonErlaubt, erstelleAnrufKarte } from "./lib/anruf-karte.js";
import { zeigeAnrufKarte } from "./lib/anruf-karte-ansicht.js";
import { istPdfAdresse, oeffnePdf } from "./lib/pdf-schau.js";
import { openExternalUrl, richteAppBrowserEin } from "./lib/app-browser.js";
import { oeffneRouteWennGewuenscht } from "./lib/route-auto-oeffnen.js";
import { wiedergabeJetzt } from "./lib/wiedergabe-stand.js";
import { langeSpurLaeuft, mikroVonSelbstGesperrt, pausiereFuerHand, sollMikroVerwerfen, starteLangeSpur, WARTET_AUF_LANGE_SPUR_TEXT } from "./lib/lange-spur.js";
import { baueMusikKarte, parseMusicResult, type MusicResult } from "./lib/musik-karte.js";
import {
  aufFernbedienungHoeren,
  vorleseHaltZuruecksetzen,
  type FernBefehl,
} from "./lib/vorlesen-nativ.js";
import { aufStimmeNativPauseHoeren, stimmeNativZuruecksetzen } from "./lib/stimme-nativ.js";
import { ohneAusgeloestesEndwort, type EndwortAngabe } from "../shared/sende-wort.js";
import { frageStilleAuto } from "./lib/stille-auto-frage.js";
import { absendenModus } from "../shared/stille-auto.js";
import { festNurNachSatz } from "./lib/stille-wahl.js";
import { wacheUeberMitschrift, type MitschriftWache } from "./lib/nachhoeren.js";
import {
  NarrationEngine,
  synthFrist,
  type AudioSource,
  type NarrationState,
} from "./lib/narration-engine.js";
import {
  loadResume,
  saveResume,
  clearResume,
  contentVersion,
} from "./lib/playback-resume.js";
import {
  setzeAdminUmgebung,
} from "./lib/users-admin-view.js";
import {
  MAX_BILDER_JE_ZUG,
  MAX_GENERIC_UPLOAD_BYTES,
  MAX_IMAGE_UPLOAD_BYTES,
} from "../shared/limits.js";
import {
  istDateiVorgang,
  starteDateiVorgang,
  waehleWeitere,
  type VorgangsUmgebung,
} from "./lib/datei-vorgang.js";

/**
 * Die Upload-Grenze in Megabyte, fuer Meldungen an den Nutzer.
 *
 * ⚠️ 17.09.2026 — vorher stand „25 MB" an zwei Stellen als fester Text. Als
 *    die Grenze auf 100 MB stieg, hätten beide Meldungen gelogen: „Datei ist
 *    größer als 25 MB" bei einer 30-MB-Datei, die längst erlaubt ist. Zwei
 *    Besitzer derselben Zahl — das häufigste Fehlermuster in diesem Baum.
 */
function uploadGrenzeMb(): number {
  return Math.round(MAX_GENERIC_UPLOAD_BYTES / 1024 / 1024);
}

import {
  setzeVersionsQuellen,
  sichtbareVersionJetzt,
  webVersionAus,
} from "./lib/app-version.js";
import { zeichen } from "./lib/zeichen.js";
import { erzeugeNeuladenKnopf } from "./lib/neuladen-knopf.js";
import {
  baueTagesAnsicht,
  baueZeilenKarte,
  fuelleDatumsSpalte,
  ladeRueckblende,
  type TagesAnsicht,
} from "./lib/spiegel-zeitleiste.js";
import { nachbarTage, type ZeitleistenZeile } from "../shared/spiegel-zeitleiste.js";
import { baueTagesverlauf } from "./lib/tagesverlauf.js";
import { spiegelVorschau } from "../shared/spiegel-vorschau.js";
import { splitNarrationSections } from "../shared/vorlese-abschnitte.js";
import { ruesteBildAus } from "./lib/bildmass.js";
import { aktuellerTon } from "./lib/ton-leitstand.js";
import { erzeugeDisplayWach } from "./lib/display-wach.js";
import { ausfallSatz, rueckkehrSatz } from "./lib/ptt-wachter.js";
import { aufnahmeLaeuft } from "./lib/playback-hifi.js";
import { stillNachladenHindernis, stillNachladenZeile } from "./lib/stilles-update.js";
import { erzeugeWasIstNeu, KONTO_TAKT_MS, serverKontoMerker, verdrahteKnopf, type Stand } from "./lib/was-ist-neu.js";
import { baueNeuKarte } from "./lib/neu-chip.js";
import { erzeugeWorkerWechsel } from "./lib/worker-wechsel.js";
import { frageWorkerVersion, gleicheFassung } from "./lib/worker-version.js";
import {
  beiKlick as versuchBeiKlick,
  beimLaden as versuchBeimLaden,
  serverIstNeuer,
  hartNeuLaden,
  holeSpeicher as holeVersuchSpeicher,
  leseMerker as leseVersuchMerker,
  MELDUNG_HAENGT,
  schreibeMerker as schreibeVersuchMerker,
} from "./lib/neue-version-versuch.js";
import { starteTonLeiste } from "./lib/ton-leiste.js";
import { starteRekorderSchonung } from "./lib/ton-rekorder-schonung.js";
import { starteKopfOrdnung } from "./lib/kopf-ordnen.js";
import { PROJEKT_OEFFNEN, zeichneProjektZeile } from "./lib/projekt-zeile.js";
import { verlaufMeldung } from "./lib/verlauf-meldung.js";
import { zerlegeFuerVorlesen } from "./lib/tts-queue.js";
import { FORTSETZEN_MIN_ZEICHEN, darfKnopfZuruecksetzen, ladeVersatz, loescheVersatz } from "./lib/vorlese-fortsetzen.js";
import { baueVorleseHilfe } from "./lib/vorlese-karte.js";
import { EREIGNIS_STOPP, holeQuelle } from "./lib/vorlese-quelle.js";
import { erzeugeMitlauf, meldeMitlaufKnopf } from "./lib/vorlese-mitlauf.js";
import { baueKlangBlock, klangReiterZeigen } from "./lib/klang-block.js";
import { geburtslandFeldImRaster } from "./lib/geburtsland-auswahl.js";
import {
  fokusNachEinstellungen,
  menueAusDerFusszeile,
  rueckwegAusEinstellungen,
} from "./lib/einstellungen-rueckweg.js";
import { setzeKopfOrtMarke, setzeOrtMarke } from "./lib/ort-marke.js";
import { bindeFokusArt } from "./lib/fokus-art.js";
import { bindeNavGruppen } from "./lib/nav-gruppen.js";
import { ohneAudioMarke } from "../shared/audio-marke.js";
import { verlaufSchluessel, zeilenSchluessel } from "./lib/verlauf-schluessel.js";
import { istNurKarten } from "./lib/nur-karten.js";
import { spiegelStandardTag } from "../shared/spiegel-tag.js";
import { meditationAnhoeren } from "./lib/meditation-anhoeren.js";
import { lageGeaendert, verdrahteLage } from "./lib/lage-fenster.js";

import {
  teileAntwort,
  sprechQuelle,
  markeNochMoeglich,
} from "../shared/antwort-stimme.js";
import { bittetUmVorlesen, grosseTabellenErsetzen } from "../shared/tabelle-im-chat.js";
import { aufnahmeKennung, beginneAufnahme, mitAufnahmeKennung } from "./lib/aufnahme-kennung.js";
declare const __BUILD_VERSION__: string;

setzeVersionsQuellen(
  typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "",
  null,
);

// Native Hülle: viewport-fit=cover aktivieren, damit env(safe-area-inset-*) greift
// (Header nicht mehr unter der Statusleiste). No-op im Browser/PWA. Früh, vor erstem Layout.
applyNativeViewportFit();
// Ticket 2396 (Build 77): Links mit target=_blank und Datei-Links bleiben in
// der App (In-App-Browser bzw. eigene Ansicht). No-op im Browser und in
// App-Fassungen ohne das Browser-Plugin.
richteAppBrowserEin();
// Ticket 2547, Nachprüfung Build 77: Einen Halt der Tonsitzung, den die Seite
// vor dem Neuladen hinterließ, freigeben — diese Seite weiß von keinem.
vorleseHaltZuruecksetzen();
// 17.10.2026: ebenso Stücke, die eine frühere Seite der App übergab (stimme-nativ.ts) — ohne Kennung: alle.
stimmeNativZuruecksetzen();

interface UIRefs {
  state: HTMLElement;
  chat: HTMLElement;
  connectBtn: HTMLButtonElement;
  chatBtn: HTMLButtonElement;
  pttBtn: HTMLButtonElement;
  muteBtn: HTMLButtonElement;
  focusBtn: HTMLButtonElement;
  hangupBtn: HTMLButtonElement;
  banner: HTMLElement;
}

/**
 * Pre.280: Erkennt sinnlose STT-Fragmente die im Voice-Mode entstehen,
 * wenn Gemini Live Stille/Atmen/Geräusche als User-Aussage transkribiert.
 *
 * True wenn:
 *  - Text leer oder <= 1 char nach Trim
 *  - Text besteht nur aus Punktuation/Whitespace
 *  - Text ist 1 Wort UND <= 3 chars (z.B. "I", "Eu", "Mi")
 *    AUSSER es sind klare deutsche Wörter ("Ja", "Ok", "Nein", "Hm")
 *  - Text enthält >50% CJK/Hangul/Hiragana/Katakana (japanisch/koreanisch/chinesisch
 *    in deutscher Konversation = Halluzination)
 */
function isJunkTranscript(text: string): boolean {
  const t = text.trim();
  if (!t || t.length <= 1) return true;
  // Nur Punktuation/Symbole?
  if (/^[\s.,;:!?…—\-_'"(){}[\]<>/\\|*+=#@%&^~`]+$/u.test(t)) return true;
  // CJK / Hiragana / Katakana / Hangul (japanisch, chinesisch, koreanisch)?
  // Wenn dominant → Halluzination (App ist deutschsprachig).
  const cjk = (t.match(/[぀-ヿ㐀-䶿一-鿿가-힯]/gu) || []).length;
  const total = t.replace(/\s/g, "").length;
  if (total > 0 && cjk / total > 0.4) return true;
  // 1-Wort-Fragmente: "I", "Eu" etc. — aber deutsche Mini-Antworten erlauben
  const words = t.split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 1 && (words[0]?.length ?? 0) <= 3) {
    const lc = (words[0] ?? "").toLowerCase().replace(/[.,!?;:]/g, "");
    const okShort = ["ja", "nein", "ok", "hm", "äh", "mhm", "joa", "ne", "nö"];
    if (!okShort.includes(lc)) return true;
  }
  return false;
}

/**
 * Aus einem technischen Fehlerwort einen Satz machen, den man lesen kann.
 *
 * ⚠️ 15.09.2026, gefunden beim Durchgehen der Neuanmeldung: Im Chat stand
 *    „Fehler: http-401" — der rohe Statuscode aus lib/chat-http.ts, wörtlich
 *    in die Blase geschrieben. Das ist kein Satz, das ist ein Protokolleintrag.
 *    Wer das liest, weiß weder was los ist noch was er tun soll.
 *
 * ⚠️ Der Grund bleibt im Titel der Blase stehen — für mich beim Nachsehen,
 *    nicht für den Nutzer im Text.
 */
function fehlerSatz(err: string): string {
  if (/weekly\s*limit|claude-cli exit/i.test(err))
    return "Die Antwort konnte gerade nicht erzeugt werden — bitte gleich nochmal.";
  if (/http-401|auth-required|auth-revoked/i.test(err))
    return "Die Anmeldung ist abgelaufen. Bitte lade die Seite neu und melde dich wieder an.";
  if (/http-402|upgrade-required/i.test(err))
    return "Das gehört zu einem größeren Paket.";
  if (/http-429|rate/i.test(err))
    return "Gerade ist viel los — bitte in einem Moment nochmal.";
  if (/http-5\d\d/i.test(err))
    return "Auf meiner Seite ist etwas schiefgegangen — bitte gleich nochmal.";
  if (/failed to fetch|networkerror|load failed/i.test(err))
    return "Keine Verbindung — sobald du wieder Netz hast, geht es weiter.";
  return "Das hat gerade nicht geklappt — bitte nochmal.";
}

function requireEl<T extends HTMLElement = HTMLElement>(sel: string): T {
  const el = document.querySelector(sel);
  if (!el) throw new Error(`element not found: ${sel}`);
  return el as T;
}

const refs: UIRefs = {
  state: requireEl("#state"),
  chat: requireEl("#chat"),
  connectBtn: requireEl<HTMLButtonElement>("#dock-connect"),
  chatBtn: requireEl<HTMLButtonElement>("#dock-chat"),
  pttBtn: requireEl<HTMLButtonElement>("#dock-ptt"),
  muteBtn: requireEl<HTMLButtonElement>("#dock-mute"),
  focusBtn: requireEl<HTMLButtonElement>("#dock-focus"),
  hangupBtn: requireEl<HTMLButtonElement>("#dock-hangup"),
  banner: requireEl("#banner"),
};

// --- Scroll-to-Bottom + Unread-Counter (pre.95) -------------------------
const scrollToBottomBtn = document.getElementById(
  "scroll-to-bottom",
) as HTMLButtonElement | null;
const scrollToBottomBadge = document.getElementById(
  "scroll-to-bottom-badge",
);
let unreadCount = 0;
const scrollToBottomLabel = document.getElementById(
  "scroll-to-bottom-label",
);

/*
 * Ticket 2730 (28.09.2026): „Klebt der Chat am Ende?" hat EINEN Besitzer.
 *
 * Vorher maß jede Stelle neu, im Moment des Einfügens, mit eigener Schwelle
 * (80, 120, 200) — und etliche fragten gar nicht. Jetzt ist es ein Zustand,
 * den nur das Scrollen ändert (lib/chat-folgen.ts). `nachScroll` stellt den
 * schwebenden Knopf nach jeder Neubestimmung (Pfeil oder Haus, Zähler weg).
 */
const chatFolgen = erzeugeChatFolgen(refs.chat, () => updateScrollBtnVisibility());

/** 16.10.2026: die Warteblase des Schmieds am Ende des Verlaufs (lib/schmied-warteblase.ts) — statt der Pille über dem Eingabefeld. */
/** Die Aktivitätszeile zeichnet sich neu, sobald eine Warteblase erscheint/verschwindet/ausgeblendet wird (eine Anzeige je Entwickler). */
let aktivitaetNeuZeichnen: () => void = () => undefined;
const schmiedWarteblase = erzeugeSchmiedWarteblase(refs.chat, () => chatFolgen.folgen(), undefined, "schmied", undefined, () => aktivitaetNeuZeichnen());
/**
 * 17.10.2026 (Martin 15:08): „Zimmermann arbeitet“ — dieselbe Blase mit seinem Namen; hält NIE ein Mikro (nur `schmiedWarteblase` tut das).
 * 17.10.2026 (Martin 19:46): ebenso „Hüter arbeitet / hat deine Nachricht“ — je Zusatz-Entwickler eine eigene Blase.
 */
const zusatzWarteblasen = new Map(ZUSATZ_IDS.map((id) => [id, erzeugeSchmiedWarteblase(refs.chat, () => chatFolgen.folgen(), undefined, id, undefined, () => aktivitaetNeuZeichnen())] as const));
/** 16.10.2026, 18:29: der Server hat `SCHMIED_STATUS_HAELT_MIKRO=0` gesetzt (Stand `mikroFrei`) — die Anzeige hält das Mikro nicht. */
let schmiedStatusMikroFrei = false;

/**
 * Dem Ende folgen — aber nur, wenn Martin nicht gerade woanders liest.
 * (05.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Wenn ich jetzt praktisch den Verlauf nach oben scrolle im
 * Chatverlauf, dann springt das Bild immer hektisch hin und her, wie wenn es
 * nicht flüssig scrollbar wäre. Das ist ein großer Bug, weil es den
 * Arbeitsfluss komplett stört."*
 *
 * ## Was gemessen wurde
 *
 * In seinem echten Chat: **177 Blasen, davon 89 mit Bild.** Und im Code
 * standen zwölf Stellen, die `scrollTop = scrollHeight` setzen — sechs davon
 * BEDINGUNGSLOS, mitten in Vorgängen, die während des Lesens weiterlaufen:
 * die Aktivitätsanzeige („Schritt 7"), der Werkzeug-Fortschritt, die
 * streamende Antwort und `appendImageBubble` beim Nachladen älterer
 * Nachrichten.
 *
 * Wer hochscrollt, löst das Nachladen aus — und jede nachgeladene Bild-Blase
 * riss ihn zurück ans Ende. Das ist das „hektisch hin und her".
 *
 * ⚠️ Eine Prüfung („war er unten?") GAB es schon; die Hälfte der Stellen
 *    benutzte sie. Dasselbe Muster wie beim Ton und beim Vorwärmen: eine
 *    Wahrheit ist da, aber nicht alle lesen sie.
 *
 * ⚠️ Bedingungslos bleibt nur, was Martin SELBST ausgelöst hat — die
 *    Warte-Blase nach dem Senden etwa. Dort ans Ende zu springen ist keine
 *    Störung, sondern die Antwort auf seinen Fingertipp.
 */
function fuehreChatNach(): void {
  chatFolgen.folgen();
}

/**
 * Pre.147: scroll-to-bottom mit doppeltem rAF + setTimeout. Markdown-
 * Renderer (renderMarkdown) hängt noch DOM-Knoten an NACH unserem ersten
 * Scroll → die letzte Bubble wächst und der Inhalt rutscht unter den
 * Footer. Doppel-Tick fängt sowohl die synchrone als auch die async-Layout-
 * Phase ein.
 */
// 2026-07-02 (Martin): EINE Arbeits-Anzeige fuer Text UND Push-to-Talk —
// vorher zwei verschiedene Darstellungen fuer denselben Zustand.
// 16.10.2026 (Martin: „die Sprechblase von Vespera auch so schön wie bei dir“):
// statt Buch-Spinner und atmender Blase die Warteblase im Aufbau des Schmieds —
// Name (nur wenn bekannt), drei Pünktchen, kurzer Status (lib/warteblase.ts).
// Ihr Avatar steht schon da, damit die Antwort an derselben Stelle erscheint.
function buildPendingBubble(text: string | undefined, name: string | null): HTMLElement {
  const el = baueVesperaWarteblase(document, {
    text: text?.trim() ? text : WARTE_STANDARD_TEXT,
    // 20.10.2026: der Name kommt vom Aufrufer (lib/empfaenger-wahl.ts `wartenderName`) — vor der Weiche nie „Vespera“ geraten.
    name,
  });
  prependBotAvatar(el);
  return el;
}

// 2026-07-03 (Martin): kurze, menschliche Fortschritts-Phrase aus dem
// Werkzeug + der Anfrage — „Ich recherchiere: Surfcams …".
// 2026-07-19 (Martin, "geht mit den Pausen weiter"): Waehrend Werkzeug-
// Laeufen herrschte am Ohr FUNKSTILLE — gemessen bis 42 s (web_search x2,
// erster Text erst nach 42 s), obwohl die Wiedergabe selbst seit pre.1720
// nahtlos ist. Die Fortschritts-Phrasen gab es nur SICHTBAR
// (ensurePttWorking); unterwegs schaut Martin aber nicht aufs Display.
// Jetzt werden sie zusaetzlich GESPROCHEN — nur wenn gerade nichts spielt,
// und gedrosselt, damit mehrere Werkzeuge hintereinander nicht plappern.
let lastSpokenToolHintAt = 0;
const TOOL_HINT_MIN_GAP_MS = 12_000;

/**
 * Wann Vespera das Warten ueberhaupt kommentiert.
 *
 * Martin am 03.09.2026: *„Bitte dass keinen 'einen Augenblick noch', wenn er
 * recherchiert, abstellen. Das ist anstrengend. Also keine Zwischenmeldungen
 * mehr, ausser nach dreissig Sekunden vielleicht."*
 *
 * Es gab ZWEI Uhren mit verschiedenen Zeiten — 6 s fuer den werkzeug-
 * bezogenen Hinweis, 8 s fuer einen allgemeinen. Beide feuerten also im
 * ganz normalen Antwortfenster, in dem gar nichts schiefgeht. Jetzt EINE
 * Schwelle: dreissig Sekunden. Wer schneller fertig ist, hoert nichts.
 */
const ZWISCHENMELDUNG_MS = 30_000;

/**
 * Ticket 2257 (13.09.2026): "Wenn Vespera eine Audiospur abspielt und dann
 * eine Aufgabe im Hintergrund öffnet (also ein Werkzeug), dann stoppt immer
 * noch die Voice während der Aufnahme." — im Journal exakt vermessen
 * (13.09., 19:02:43–19:04:35): zwischen zwei Sprachstücken lag ein
 * Werkzeug-Aufruf, danach `wait=10576ms` — der Player wartete 10,58 echte
 * Sekunden auf nichts.
 *
 * Das ist NICHT dieselbe Situation wie ZWISCHENMELDUNG_MS oben — die war ein
 * gesprochener SATZ, der bei jedem x-beliebigen Werkzeug-Aufruf kam und
 * genau deshalb als "anstrengend" abgestellt wurde. Hier geht es um einen
 * kurzen, NICHT gesprochenen Ton (playWerkzeugWarteTon) für den Fall, dass
 * die Stille lange genug dauert, dass sie wie ein Aussetzer wirkt — deutlich
 * kürzer als die 30s-Schwelle für den gesprochenen Hinweis, aber lang genug,
 * dass kurze Werkzeug-Ketten (im Journal: zwei Aufrufe hintereinander,
 * wait=2180ms) ihn nie auslösen.
 */
const WERKZEUG_TON_MS = 3_500;
/**
 * 2026-07-28 (Martin: "Er muss nicht so oft Zwischenmeldungen geben mit 'ich
 * verarbeite das'. Es genuegt in zehn Sekunden einmal eine Information."):
 * Die zeitliche Drossel oben erfuellt das laengst — sie laesst hoechstens
 * alle zwoelf Sekunden etwas durch. Was stoerte, war die WIEDERHOLUNG: Es
 * kam immer derselbe Satz. Zweimal "Einen Moment, ich verarbeite das" sagt
 * beim zweiten Mal nichts Neues, klingt aber wie eine weitere Meldung.
 * Deshalb wird dieselbe Phrase nicht noch einmal gesprochen, solange sie die
 * zuletzt gesprochene war. Eine ANDERE Information (z.B. "ich sehe im
 * Kalender nach") kommt weiterhin durch — sie hat ja einen eigenen Inhalt.
 */
let letzteGesprocheneHintPhrase = "";

/** Spricht einen Fortschritts-Hinweis — gedrosselt und ohne Wiederholung. */
/**
 * 2026-07-29 (Martin: "Die kurzen Zwischenmeldungen — ich bleibe dran, das
 * braucht noch kurz — irgendwie noch lebendiger machen. Das ist anstrengend,
 * wenn es immer das Gleiche gibt, sodass das Ganze nicht so fahl wirkt. Aber
 * halt ganz zart. Warten soll ja kein Problem sein, dafuer kommt ja auch ein
 * ordentlicher Content raus."):
 *
 * Gestern habe ich die Wiederholung derselben Phrase abgestellt — richtig,
 * aber die Folge war Stille. Bei einer langen Werkzeug-Kette hoerte man einmal
 * "ich recherchiere" und danach nichts mehr, obwohl weiter gearbeitet wurde.
 *
 * Jetzt gibt es eine STAFFEL, die mit der Wartezeit mitwaechst. Das ist keine
 * Zufallsauswahl (die waere beliebig und faellt auf), sondern ein Verlauf: Je
 * laenger es dauert, desto mehr sagt der Satz ueber die Lage — und der letzte
 * nimmt Martins eigenen Gedanken auf, dass sich das Warten lohnt.
 *
 * Bewusst kurz, bewusst wenige. Zwoelf Sekunden Mindestabstand bleiben; wer
 * schnell fertig ist, hoert nur den ersten Satz und nie eine Durchhalte-Zeile.
 */
const DURCHHALTE_STAFFEL = [
  "Ich bleibe dran",
  "Das braucht noch einen Moment",
  "Immer noch dran — es wird was Ordentliches",
];
let durchhalteStufe = 0;

function sprichToolHint(phrase: string, sprich: (t: string) => void): void {
  const kern = phrase.replace(/\s*\u2026\s*$/u, "").trim();
  if (!kern) return;
  if (Date.now() - lastSpokenToolHintAt < TOOL_HINT_MIN_GAP_MS) return;

  let zuSagen = kern;
  if (kern === letzteGesprocheneHintPhrase) {
    // Dieselbe Taetigkeit laeuft weiter — statt sie zu wiederholen (sagt
    // nichts Neues) oder zu schweigen (wirkt tot), die naechste Stufe.
    const stufe = DURCHHALTE_STAFFEL[durchhalteStufe];
    if (!stufe) return; // Staffel erschoepft: lieber still als geschwaetzig.
    durchhalteStufe++;
    zuSagen = stufe;
  } else {
    // Neue Taetigkeit — Staffel beginnt von vorn.
    durchhalteStufe = 0;
    letzteGesprocheneHintPhrase = kern;
  }
  lastSpokenToolHintAt = Date.now();
  sprich(zuSagen + ". ");
}

function pttProgressPhrase(tool: string, input: unknown): string {
  const t = (tool || "").toLowerCase();
  const inp = (input ?? {}) as Record<string, unknown>;
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = inp[k];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
  };
  const snip = (v: string): string => (v.length > 60 ? v.slice(0, 60) + "…" : v);
  // 2026-07-12 (Martin, "was hat das mit dem Web zu tun"): Alltag-/Memory-
  // Tools enthalten das Wort "search" und wurden faelschlich als Web-Suche
  // beschriftet. Zuerst die EIGENEN Datenquellen abfangen.
  // 2026-07-21 (Martin, "Ich recherchiere im Web — was soll das?"):
  // Hintergrund-Auftraege nennen den ZWECK, nicht nur das Teil-Werkzeug.
  if (/heavy_task|task_delegate/.test(t)) {
    const auftrag = pick("topic", "auftrag", "task");
    return auftrag
      ? `Ich arbeite an: ${snip(auftrag)} …`
      : "Ich arbeite an deinem Auftrag …";
  }
  if (/image_search|web_image/.test(t)) return "Ich suche passende Bilder …";
  if (/alltag|tagebuch|diary/.test(t)) return "Ich schaue in deinen Alltag …";
  if (/memory|erinnerung|chat_history|verlauf/.test(t))
    return "Ich schaue in deine Erinnerungen …";
  // 2026-07-22 (Martin, "er recherchiert gar nicht im Web!"): das nackte
  // Wort "search" fing JEDES Such-Werkzeug (auch Datei-/Projekt-Suche).
  // Nur noch ECHTE Web-Werkzeuge duerfen so heissen.
  if (/web_search|recherche|exa|brave|google/.test(t)) {
    const q = pick("query", "q", "search", "text");
    return q ? `Ich recherchiere: ${snip(q)} …` : "Ich recherchiere im Web …";
  }
  if (/fetch|open_url|read_url|browse|url|scrape/.test(t)) {
    const u = pick("url", "link");
    return u ? `Ich sehe mir ${prettyUrl(u)} an …` : "Ich lese die Seite …";
  }
  if (/make_pdf|book_compile|pdf/.test(t)) return "Ich baue das PDF …";
  if (/weather|wetter/.test(t)) return "Ich schaue das Wetter …";
  if (/calendar|termin/.test(t)) return "Ich schaue in den Kalender …";
  if (/image|imagen|bild/.test(t)) return "Ich erstelle das Bild …";
  if (/project_file|projekt|note|material|document/.test(t))
    return "Ich schaue in deine Unterlagen …";
  if (/mail|email|ses/.test(t)) return "Ich bereite die E-Mail vor …";
  if (/timer|wecker|reminder|erinner/.test(t)) return "Ich stelle das ein …";
  if (/memory|erinnerung/.test(t)) return "Ich schaue in deine Erinnerungen …";
  if (/route|maps|navigat|distance/.test(t)) return "Ich plane die Route …";
  if (/flight|flug|duffel/.test(t)) return "Ich suche Flüge …";
  return "Ich arbeite daran …";
}

// 2026-07-20 (Martin, "Runterpfeil rutscht nur ein Stueckchen"): EIN
// scrollTop=scrollHeight reicht nicht — #chat hat scroll-behavior:smooth
// (die Animation zielt auf einen VERALTETEN Wert) und nachwachsende Blasen
// schieben das Ende weiter. Deshalb haelt chatFolgen.ansEnde das Ende einige
// Bilder lang fest (lib/chat-folgen.ts). Eine Nutzer-Geste bricht das sofort
// ab — nie gegenscrollen.
//
// ⚠️ Nur fuer SEINE Handlungen (Senden, Knopf, Chat oeffnen). Was von selbst
//    dazukommt, geht ueber fuehreChatNach — Ticket 2730.
function pinChatToBottom(durationMs = 900): void {
  chatFolgen.ansEnde(durationMs);
}

function scrollChatToBottomReliably(): void {
  pinChatToBottom(900);
}

// #1: Beim Zurueckkehren in den Vordergrund (native App) den Chat neu malen
// + ans Ende scrollen — sonst zeigt WKWebView ihn leer bis zum ersten Scrollen.
// Pre.1311: Chat-Session vorwaermen (Cold-Build ~3-30s im Hintergrund
// abfangen), sobald Martin wahrscheinlich gleich schreibt. Gedrosselt 1x/90s.
let lastChatPrewarm = 0;
function prewarmChatSession(): void {
  const now = Date.now();
  if (now - lastChatPrewarm < 90_000) return;
  lastChatPrewarm = now;
  try {
    void fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ prewarm: true }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* best-effort */
  }
}
// Beim Laden einmal vorwaermen.
setTimeout(prewarmChatSession, 1500);

/*
 * Der Waerme-Takt. (09.09.2026, Martins Wunsch nach „Monitoring in Real-Time")
 *
 * ⚠️ Nur solange die App SICHTBAR ist. Ein Takt, der im Hintergrund
 *    weiterlaeuft, fragt nachts stundenlang einen Zustand ab, den niemand
 *    ansieht — und genau solche Takte haben hier schon Akku und Protokoll
 *    gefressen (Alltag-Renderwelle 19.07., Runaway-Watcher 24.07.).
 *
 * ⚠️ 30 Sekunden, nicht 5: Der Zustand dreht sich nur an einem Zug oder an
 *    der Rotation danach — beides Ereignisse im Minutenabstand. Haeufiger
 *    fragen hiesse, dieselbe Antwort oefter zu holen.
 */
let waermeTakt: ReturnType<typeof setInterval> | null = null;
function waermeTaktSetzen(an: boolean): void {
  if (an && !waermeTakt) {
    void holeWaerme();
    waermeTakt = setInterval(gesteuert("live", () => void holeWaerme(), true), 30_000);
  } else if (!an && waermeTakt) {
    clearInterval(waermeTakt);
    waermeTakt = null;
  }
}
setTimeout(() => waermeTaktSetzen(!document.hidden), 2000);

/**
 * Ein Zug, der weiterlief, während die App weg war. (04.09.2026)
 *
 * Martin: *„Wenn Vespera gerade schreibt und ich die App nur klein mache —
 * nicht schließe —, wird sie im Hintergrund nicht fertig, und dann kommt die
 * Information, dass es zu einer Unterbrechung kam."*
 *
 * ## Was wirklich passiert
 *
 * iOS kappt den Ereignisstrom, sobald die App in den Hintergrund geht. Der
 * SERVER merkt davon nichts: Er schreibt die Antwort zu Ende und legt sie ab
 * — `claude-sdk-session.ts` sagt es ausdrücklich, *„Client kann weg sein
 * (Tab zu): send-Fehler darf NIE die Sitzung killen"*.
 *
 * Die Antwort ist also **da**. Sie wurde nur nie abgeholt: Beim Zurückkommen
 * lud die App den Verlauf nicht nach, also blieb die Fehlermeldung stehen —
 * und der Rat „bitte nochmal senden" war schlicht falsch.
 */
let zugLiefBeimWegschieben = false;

document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    /*
     * Läuft gerade eine Antwort?
     *
     * ⚠️⚠️ 14.09.2026 (Ticket 2256) — HIER STAND NUR DIE STREAMING-BLASE.
     *
     * Die entsteht erst beim ERSTEN WORT (ensureBubble wird aus onChunk
     * gerufen). Wer wegschiebt, während noch Werkzeuge laufen und kein Text
     * da ist, hinterlässt also KEINEN Beleg — und beim Zurückkommen wurde
     * nichts nachgeladen.
     *
     * Genau Martins Fall vom 13.09.: *„Ich habe während laufender
     * Werkzeug-Aufrufe (Protokoll-Erstellung, Recherche) die App
     * verlassen"* — und die fertige Antwort erschien nie von selbst. Der
     * Hinweis daneben behauptet das Gegenteil; falsch war nicht der Hinweis,
     * sondern diese Zeile.
     *
     * `laufenderZug` steht ab dem Absenden, lange vor dem ersten Wort. Die
     * Blase bleibt als zweiter Beleg stehen: Sie deckt den Fall ab, dass
     * eine Antwort noch gezeichnet wird, während der Zug schon geschlossen
     * ist.
     */
    zugLiefBeimWegschieben =
      laufenderZug !== null ||
      Boolean(document.querySelector(".bubble.is-streaming"));
    waermeTaktSetzen(false);
    return;
  }
  /*
   * ⚠️ 01.10.2026 (Ticket 2873): Hier stand der bedingungslose Sprung ans Ende —
   * bei JEDER Rückkehr, auch wenn er weiter oben gelesen hatte. Das Neuzeichnen
   * der Ansicht (WKWebView) besorgt `scheduleAppRepaints` weiter unten ohnehin.
   * Ans Ende gestellt wird nur, wer dort stand (chat-folgen.ts, EIN Besitzer
   * der Frage). Gemessen im Nachbau: Bezugsblase 2.499 Punkte weg in einem Bild.
   *
   * ⚠️ OFFEN, am iPhone zu prüfen (auf dem Server nicht prüfbar, WebKit startet
   *    dort nicht): Der entfernte Sprung ans Ende gehörte ursprünglich zur Abhilfe
   *    #1 vom 28.06. (WKWebView zeichnet den Chat bis zum ersten Wisch leer).
   *    Ob `scheduleAppRepaints` allein für einen Leser weiter oben reicht: weiter
   *    oben lesen, App wegschieben, zurückkehren — ist der Chat SOFORT gezeichnet?
   *    Falls nicht, ein ortserhaltender Mini-Scroll (+1/−1 im nächsten Bild) statt
   *    des Sprungs ans Ende. Ohne Gerät gebaut wird hier bewusst nichts.
   */
  if (chatFolgen.klebt()) scrollChatToBottomReliably();
  prewarmChatSession();
  waermeTaktSetzen(true);
  if (zugLiefBeimWegschieben) {
    zugLiefBeimWegschieben = false;
    // Nachsehen, was inzwischen fertig geworden ist.
    void hydrateFromServer({ replaceAll: true });
  }
});

// iOS-WebKit-Kaltstart-Repaint (2026-06-28): Inhalt (ganzer Chat) bleibt manchmal
// unsichtbar bis zur ERSTEN Touch-Interaktion — nativ UND PWA (WebKit-Engine-Bug).
// Voll-Repaint via Body-Opacity-Toggle (kann WebKit nicht wegoptimieren wie
// transform/scrollTop), gestaffelt nach Laden/Sichtbarwerden → zeichnet OHNE Touch.
function forceFullRepaint(): void {
  if (ruckelAus("ebenen")) return; // Ruckel-Test (Ticket 2912): kein Ebenen-Trick beim Wechsel
  // 2026-07-05 (Root-Cause-Fix, 3-Agenten-Workflow): NICHT mehr body.opacity
  // togglen — das promotet den ganzen <body> (mit background-attachment:fixed)
  // auf eine Compositing-Ebene und baut sie wieder ab; WKWebView verwirft dabei
  // die Tiles -> Content erscheint kurz, verschwindet dann bis zur Beruehrung.
  // Stattdessen NUR den sichtbaren View sanft neu komponieren: Reflow erzwingen,
  // kurz eine eigene GPU-Ebene aufbauen und ueber eine Frame-Grenze wieder
  // aufloesen (kein Full-Page-Teardown -> keine verlorenen Tiles).
  const el =
    document.querySelector<HTMLElement>(".view:not([hidden])") ??
    document.getElementById("app");
  if (!el) return;
  void el.offsetHeight;
  const prev = el.style.transform;
  el.style.transform = "translateZ(0)";
  requestAnimationFrame(() => {
    void el.offsetHeight;
    requestAnimationFrame(() => {
      el.style.transform = prev;
    });
  });
}
function scheduleAppRepaints(): void {
  forceFullRepaint();
  for (const ms of [80, 250, 600, 1200]) window.setTimeout(forceFullRepaint, ms);
}
// 2026-07-05 (Martin: "Header ueberall"): Topbar-Hoehe als CSS-Variable —
// Vollseiten-Overlays (Projekt-Detail, HD, Buch, ...) beginnen darunter,
// damit der Haupt-Header IMMER sichtbar bleibt.
/**
 * Die festen Möbel nachmessen: Kopfleiste (`--topbar-h`) und Fusszeile (`--dock-h`). Eine Stelle für beide
 * Fragen; Anlässe sind unten der Beobachter, `resize`, `load` — und seit 07.10.2026 der Wechsel von
 * `body.keyboard-open` (installViewportGuard): Quer mit Tastatur gehen beide weg, und der Beobachter hängt am
 * Schalter „Scroll-Messfühler aus“; mit Schalter blieb `--topbar-h` bei 53 und die Vollseiten begannen zu tief.
 */
function moebelMessen(): void {
  const tb = document.getElementById("topbar");
  if (tb) {
    document.documentElement.style.setProperty(
      "--topbar-h",
      `${tb.offsetHeight}px`,
    );
  }
  /*
   * 2026-08-29 — dasselbe fuer die Fusszeile. Martin: *„in zu Hause der
   * Button, wo man zurück kann zum Chat, sollte entsprechend unterhalb der
   * Fußzeile sein, weil es sonst so im Raum schwebt und irgendwie verloren
   * wirkt."*
   *
   * Gemessen schwebte er 124 Punkte ueber dem Dock — mitten im Leeren.
   * Damit er sich daran anlegen kann, muss das Blatt die Hoehe des Docks
   * kennen; sie schwankt mit dem Sicherheitsabstand am unteren Rand.
   *
   * Hier statt in einer eigenen Beobachtung: es ist dieselbe Frage
   * („wie hoch ist das feste Moebel?") und derselbe Anlass zum Nachmessen.
   */
  const dock = document.querySelector<HTMLElement>(".dock");
  if (dock) {
    document.documentElement.style.setProperty(
      "--dock-h",
      `${dock.offsetHeight}px`,
    );
  }
}
(function trackTopbarHeight(): void {
  const apply = moebelMessen;
  apply();
  window.addEventListener("resize", apply);
  window.addEventListener("load", apply);
  try {
    /*
     * ⚠️ 16.09.2026 — DIE FUSSZEILE WURDE NIE NACHGEMESSEN.
     *
     * Martin, mit einem Bild: *„Ploetzlich ist der eine Button wieder zu
     * weit unten. Warum? Wo ich zum Chat wechseln kann."* Auf dem Bild
     * klebte der runde Chat-Knopf auf dem Menue-Knopf, dessen Beschriftung
     * zu „M…" abgeschnitten war.
     *
     * Der schwebende Knopf haengt an `--dock-h` (`bottom: calc(var(--dock-h)
     * + 14px)`). Beobachtet wurde aber NUR die Kopfleiste — die Fusszeile
     * stand seit 29.08. mit in dieser Funktion, ohne je eine eigene
     * Beobachtung zu bekommen. Gemessen wurde sie genau dreimal: beim
     * Aufbau, bei `resize` und bei `load`.
     *
     * Die Fusszeile aendert ihre Hoehe aber OHNE diese drei Anlaesse: der
     * Anruf-Knopf kommt und geht, seit gestern auch der Wunsch-Knopf, und
     * beim ersten Aufbau sind die Schriften oft noch nicht geladen. Danach
     * blieb `--dock-h` auf dem alten, zu kleinen Wert stehen — und der
     * schwebende Knopf rutschte genau um die Differenz in die Fusszeile.
     *
     * ⚠️ Dieselbe Beobachtung fuer beide Moebel, nicht zwei verschiedene:
     *    es ist dieselbe Frage („wie hoch ist das feste Moebel?") und
     *    derselbe Anlass zum Nachmessen.
     */
    /*
     * ⚠️ `box: "border-box"` — NICHT die Vorgabe.
     *
     *    Der Beobachter meldet von sich aus nur den INHALTS-Kasten. Gemessen
     *    wird hier aber `offsetHeight`, also Inhalt PLUS Polster und Rand —
     *    und genau das Polster waechst am unteren Rand mit dem
     *    Sicherheitsabstand des Geraets. Mit der Vorgabe blieb `--dock-h`
     *    stehen, obwohl die Fusszeile im Browser nachweislich von 85 auf
     *    108 Punkte wuchs; der schwebende Knopf lag danach darauf.
     */
    const beobachter = new ResizeObserver(gesteuert("wachen", apply, true));
    const tb = document.getElementById("topbar");
    if (tb) beobachter.observe(tb, { box: "border-box" });
    const dock = document.querySelector<HTMLElement>(".dock");
    if (dock) beobachter.observe(dock, { box: "border-box" });
  } catch {
    /* ResizeObserver fehlt — resize-Listener reicht */
  }
})();
window.addEventListener("pageshow", scheduleAppRepaints);
window.addEventListener("load", scheduleAppRepaints);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") scheduleAppRepaints();
});

/**
 * Der schwebende Knopf im Chat hat ZWEI Gesichter (2026-08-29).
 *
 * Martin: *„wenn ich im Chat bin, wäre irgendwie gut, wenn das Häuschen mit
 * dem Daumen leichter erreichbar ist [...] dort, wo wenn ich nach oben scrolle
 * dieses Zeichen erscheint, wo man runterscrollen kann. Und wenn ich ganz
 * unten bin, dann soll stattdessen das kleine Häuschen sein."*
 *
 *   oben im Verlauf  →  Pfeil nach unten (wie bisher, mit Ungelesen-Zaehler)
 *   ganz unten       →  Haus, ein Griff nach Zuhause
 *
 * ⚠️ Warum kein zweiter Knopf: Der Weg nach Zuhause existiert schon dreifach
 * (Markenzeichen oben links, Menue, Fusszeile). Ein VIERTER waere Ballast —
 * was fehlte, war ein Weg IN DAUMENREICHWEITE. Derselbe Knopf an derselben
 * Stelle loest das, ohne die Flaeche voller zu machen.
 */
function updateScrollBtnVisibility(): void {
  if (!scrollToBottomBtn) return;
  // Ticket 2730: derselbe Zustand, nach dem sich auch das Nachziehen richtet.
  const atBottom = chatFolgen.klebt();
  /*
   * ⚠️ 2026-08-29, DRITTE Runde — Pfeil beim Hochscrollen, Haus am Ende.
   *
   * Die Rolle dieses Knopfes hat an einem Tag zweimal gewechselt. Beide
   * Male auf Martins Wort, und beide Male mit gutem Grund — der zweite
   * Grund kam erst durch das Benutzen.
   *
   *   1. Anlauf: Haus am Ende, Pfeil beim Hochscrollen.
   *   2. Martin: *„Im Chat soll nicht der Pfeil nach unten sein, sondern
   *      das Häuschen, damit man weiß, wo man da hinkommt. Das verwirrt
   *      sonst."* — daraufhin IMMER das Haus.
   *   3. Martin, nach dem Benutzen: *„Wenn ich im Chatverlauf bin und nach
   *      oben scrolle, dann soll statt dem Häuschen der Pfeil kommen, der
   *      nach runter führt, und erst wenn ich ganz unten bin, soll wieder
   *      das Häuschen kommen, sodass ich besser navigieren kann."*
   *
   * ⚠️ Der Widerspruch ist keiner, und das ist die Lehre. Sein Einwand in
   * (2) galt dem RATEN: ein Knopf, der mal dies und mal jenes tut, muss
   * vor jedem Griff gelesen werden. Genau das gilt hier NICHT mehr — die
   * Rolle hängt jetzt an etwas, das er selbst gerade getan hat. Wer
   * hochgescrollt hat, WEISS, dass er hochgescrollt hat, und erwartet den
   * Weg zurück. Wer unten steht, hat nichts zum Zurückspringen und
   * bekommt den Weg nach Hause.
   *
   * Der Zustand ist also nicht verborgen, sondern die Folge seiner
   * letzten Bewegung. Das war beim ersten Anlauf schon so — was fehlte,
   * war die Erfahrung mit langen Verläufen, und die hatte nur er.
   *
   * Die Pille bei Ungelesenem bleibt unberührt: sie trägt das Wort
   * „N neue Nachrichten" und ist mit keinem der beiden zu verwechseln.
   */
  /*
   * ⚠️ Zuerst den Zaehler klaeren, DANN entscheiden.
   *
   * Andersherum (wie zuvor) wurde `heim` aus dem alten Stand berechnet und
   * das Zuruecksetzen kam danach — wer ans Ende scrollte, sah einen Atemzug
   * lang noch den Pfeil, obwohl nichts mehr ungelesen war.
   */
  if (atBottom && unreadCount !== 0) {
    unreadCount = 0;
    updateUnreadBadge();
  }
  /*
   * ⚠️ `atBottom`, nicht `unreadCount === 0`. Nach dem Block oben gilt am
   * Ende ohnehin `unreadCount === 0`; der alte Ausdruck war deshalb
   * gleichbedeutend mit „immer heim" und hätte die Bewegung nie gesehen.
   */
  const heim = atBottom;
  const ziel = heim ? "Zu Zuhause" : "Zum Ende des Chats";
  /*
   * Ticket 2730: Steht auf der Pille „1 neue Nachricht", muss das auch im
   * Namen des Knopfs stehen — sonst hört ein Bildschirmleser nur „Zum Ende
   * des Chats" und erfährt nicht, dass unten etwas wartet.
   */
  const neu = !heim && unreadCount > 0 ? (scrollToBottomLabel?.textContent ?? "") : "";
  // Schritt C: nur schreiben, was sich ändert (lib/scroll-knopf.ts) — die Funktion läuft bei jedem Scroll-Ereignis.
  schreibeScrollKnopf(scrollToBottomBtn, {
    rolle: heim ? "heim" : "runter",
    label: neu ? `${ziel}, ${neu}` : ziel,
    titel: heim ? "Zuhause" : "Zum Ende des Chats",
  });
  /*
   * ⚠️ Welches Zeichen sichtbar ist, entscheidet CSS ueber `data-rolle` —
   * NICHT das `hidden`-Merkmal.
   *
   *   Im Browser gemessen: `stb-pfeil hidden=true display=block` und
   *   `stb-haus hidden=false display=none` — also genau verkehrt. `hidden`
   *   ist nur eine Regel der Vorgabe-Stilvorlage; jede eigene
   *   `display`-Regel schlaegt sie, und bei SVG greift sie ohnehin nicht
   *   zuverlaessig. Der Knopf zeigte den Pfeil, obwohl der Zustand „heim"
   *   war und die Kurzhilfe „Zu Zuhause" sagte.
   */
}

function updateUnreadBadge(): void {
  if (!scrollToBottomBadge) return;
  if (unreadCount > 0) {
    scrollToBottomBadge.textContent =
      unreadCount > 99 ? "99+" : String(unreadCount);
    scrollToBottomBadge.hidden = false;
  } else {
    scrollToBottomBadge.hidden = true;
  }
  // 2026-07-05 (Martin, Telegram-Leiste): bei ungelesenen Nachrichten wird der
  // runde Knopf zur roten Pille "N neue Nachrichten".
  if (scrollToBottomLabel && scrollToBottomBtn) {
    if (unreadCount > 0) {
      const n = unreadCount > 99 ? "99+" : String(unreadCount);
      scrollToBottomLabel.textContent =
        unreadCount === 1 ? "1 neue Nachricht" : `${n} neue Nachrichten`;
      scrollToBottomLabel.hidden = false;
      scrollToBottomBadge.hidden = true; // Text ersetzt das Eck-Badge
      scrollToBottomBtn.classList.add("has-unread");
    } else {
      scrollToBottomLabel.hidden = true;
      scrollToBottomBtn.classList.remove("has-unread");
    }
  }
}

function notifyNewMessage(): void {
  if (!chatFolgen.klebt()) {
    unreadCount++;
    updateUnreadBadge();
    updateScrollBtnVisibility();
  }
}

// --- TTS-Vorlesen für Bot-Bubbles im Chat-Modus ----------------------
// Pre.159: Cancellable TTS — User kann Loading abbrechen, sonst hängt
// der Btn 4-5s ohne Reaktion (ElevenLabs-Latenz).
let currentTtsAbort: AbortController | null = null;
// Pre.161: TTS-Audio-Cache. ElevenLabs ist teuer — denselben Text bei
// jedem Klick neu zu synthetisieren ist Verschwendung. Wir cachen den
// fertigen Blob (als Object-URL) pro Text-Key. LRU-Limit 30 Einträge,
// alte werden mit URL.revokeObjectURL() befreit.
const TTS_CACHE_MAX = 30;
const ttsCache = new Map<string, string>();

/*
 * 29.09.2026 (Ticket 2736): mit dem Tempo im Schlüssel. Das Vorlese-Tempo
 * lässt sich jetzt mitten im Gespräch umstellen (Blatt der Antwortlänge);
 * ohne Tempo im Schlüssel spielte derselbe Text danach aus diesem Speicher
 * zehn Minuten lang im alten Tempo weiter — die Einstellung hätte scheinbar
 * nicht gewirkt.
 */
function ttsCacheKey(voiceId: string, text: string, tempo: number): string {
  return `${voiceId}::${tempo}::${text}`;
}

function ttsCacheGet(key: string): string | undefined {
  const url = ttsCache.get(key);
  if (url) {
    // Re-insert to refresh LRU position
    ttsCache.delete(key);
    ttsCache.set(key, url);
  }
  return url;
}

function ttsCacheSet(key: string, url: string): void {
  ttsCache.set(key, url);
  while (ttsCache.size > TTS_CACHE_MAX) {
    const oldestKey = ttsCache.keys().next().value;
    if (!oldestKey) break;
    const oldestUrl = ttsCache.get(oldestKey);
    if (oldestUrl) {
      try {
        URL.revokeObjectURL(oldestUrl);
      } catch {
        /* ignore */
      }
    }
    ttsCache.delete(oldestKey);
  }
}
// Der Lautsprecher-Schlüssel (voice-v8.autoplay) wird in lib/lautsprecher.ts gelesen und geschrieben —
// seit 15.10.2026 auch von der Einstellungsseite, mit denselben zwei Funktionen.

/**
 * Wird eine GETIPPTE Frage laut beantwortet? (15.09.2026)
 *
 * ## Woher das kommt
 *
 * Ein Kunde ueber Martin: *„Er moechte mit Vespera auch lautlos schreiben
 * koennen. Das heisst: Wenn er tippt, soll er nur schreiben."*
 *
 * ⚠️ Das ist die Umkehrung einer Entscheidung, die Martin am 21.07.2026
 *    selbst getroffen hat („getippt spricht wie PTT — satzweise ab dem
 *    ersten Satz"). Deshalb wird sie NICHT einfach umgedreht, sondern zur
 *    Wahl: ein Schalter im Plus-Menue.
 *
 * ⚠️ 15.10.2026 (Martin: „Ja, für alle auf ‚an‘ stellen"): die VORGABE ist
 *    jetzt AN — ohne gespeicherte Wahl wird Getipptes vorgelesen. Wer es
 *    nicht will, schaltet es aus; ein gespeichertes Aus (Konto oder alter
 *    Geräteschlüssel) bleibt aus (settings.ts, DEFAULT_SETTINGS).
 *
 * ⚠️ Gesprochenes bleibt unberuehrt. Wer die Sprechtaste drueckt oder
 *    anruft, bekommt weiter eine gesprochene Antwort — dieser Schalter
 *    entscheidet allein ueber den getippten Weg.
 *
 * Verloren geht nichts: jede Antwort traegt ihren eigenen Vorlese-Knopf.
 * Wer eine getippte Antwort doch hoeren will, tippt ihn an.
 */
/*
 * ⚠️ 23.09.2026, Ticket 2511: ein KONTOWERT, kein Gerätewert mehr. Vorher lag
 *    er nur in `voice-v8.tippen-vorlesen` dieses Browsers — wer am Telefon
 *    „nur schreiben" wählte, bekam am Rechner weiter vorgelesen. Der alte
 *    Schlüssel zählt nur noch, solange das Konto nichts weiß (settings.ts,
 *    leseAltesTippenVorlesen). Vorgabe seit 15.10.2026 AN.
 */
function loadTippenVorlesen(): boolean {
  return loadSettings().tippenVorlesen === true;
}
function saveTippenVorlesen(an: boolean): void {
  saveSettings({ ...loadSettings(), tippenVorlesen: an });
}

let autoPlayEnabled = loadAutoPlay();
uebernimmLesemodus(); // Start: die zuletzt angemeldete Kennung gibt den Schlüssel (lib/lesemodus.ts)
let tippenVorlesen = loadTippenVorlesen();
// Die Zeile „Antworten vorlesen“ in den Einstellungen schreibt denselben Schlüssel (lib/lautsprecher.ts):
// die vier Knöpfe hier nachziehen. setAutoPlay meldet selbst — gleicher Wert, kein zweiter Lauf.
window.addEventListener(LAUTSPRECHER_EREIGNIS, (ev) => {
  const an = lautsprecherAus(ev);
  if (an !== null && an !== autoPlayEnabled) setAutoPlay(an);
});
// Ein anderes Gerät hat umgestellt (Abgleich, Ticket 2511): nachziehen.
window.addEventListener("voice-v8:settings-changed", () => {
  const jetzt = loadTippenVorlesen();
  if (jetzt === tippenVorlesen) return;
  tippenVorlesen = jetzt;
  zeichneTippenSchalter();
});

/**
 * Woher eine Frage im Chat kommt. (28.09.2026, Ticket 2740)
 *
 * Martin tippte im Zuhause auf die Tugend-Karte: die Antwort stand im Chat,
 * blieb aber stumm, bis er den Play-Knopf drückte. Die Karte stellt ihre
 * Frage über `voice-v8:ask-vespera`, und dieser Weg lief hier als GETIPPTE
 * Frage durch — „Getipptes vorlesen" war damals vorgegeben aus.
 *
 * Getippt hat dort niemand. „angetippt" heißt: eine Karte hat die Frage
 * gestellt. Ihre Antwort spricht wie nach der Sprechtaste.
 */
type FrageHerkunft = "getippt" | "angetippt";

/**
 * Darf die Antwort auf eine Frage aus dem Chat gesprochen werden?
 *
 * Der Lautsprecher ist der Hauptschalter — steht er aus, ist alles still.
 * Eine GETIPPTE Frage spricht nur, wenn das Vorlesen von Getipptem gewollt
 * ist (15.09.2026: „Wenn er tippt, soll er nur schreiben"). Eine
 * ANGETIPPTE fragt wie die Sprechtaste nur den Lautsprecher.
 */
function darfVorlesen(herkunft: FrageHerkunft = "getippt"): boolean {
  return autoPlayEnabled && (herkunft === "angetippt" || tippenVorlesen) && !mailStimmeAus(chatMailZug);
}
/** Der Zug der laufenden getippten Antwort (lib/lesemodus.ts) — nur darfVorlesen liest ihn: im Zug einer Mail-Karte spricht der getippte Weg im Lesemodus nicht von selbst. */
let chatMailZug: number | undefined;
let currentAudio: HTMLAudioElement | null = null;
let currentAudioBtn: HTMLButtonElement | null = null;
// 2026-07-19 (Pause/Weiter): der schmale Struktur-Typ reichte fuer cancel();
// jetzt braucht der Blasen-Knopf auch pause/resume/isSilentIdle.
let currentTtsQueue: {
  cancel(grund?: string): void;
  pause(): void;
  resume(): void;
  isSilentIdle(): boolean;
  isPaused(): boolean;
  /** 15.10.2026: liest gerade der Schmied (lib/tts-queue.ts)? Die Pille sagt es. */
  readonly sprecher?: "schmied" | "probe";
  /** 20.10.2026: wie der Sprecher heißt (Zimmermann, Hüter …) — die Pille nennt ihn. */
  readonly sprecherName?: string;
} | null = null;
// 2026-07-03 (Martin): ALLE aktiven TTS-Queues (playTts + PTT-Autoplay).
// stopCurrentTts bricht ausnahmslos alle ab → nie zwei Stimmen gleichzeitig.
const activeTtsQueues = new Set<{
  cancel(grund?: string): void;
  pause(): void;
  resume(): void;
  isSilentIdle(): boolean;
  /** Spielt oder wartet etwas in der Schlange? (Ticket 2669: „Seite neu laden“ wartet auf die Stimme.) */
  isAudioActive(): boolean;
  isPaused(): boolean;
  /** 16.10.2026: der Schmied — eine neue automatische Antwort Vesperas beendet ihn nicht (stoppeWiedergabenAusser). */
  readonly sprecher?: "schmied" | "probe";
}>();
/*
 * 23.09.2026 (Ticket 2551): die Warteschlange des laufenden Sprechtasten-
 * Zuges. EIN Besitzer: pttSendBlob setzt sie beim Anlegen und gibt nur die
 * eigene wieder frei — im finally, oder schon am Zugende, wenn ohne
 * Lautsprecher nie vorgelesen wird. Gelesen nur in beginTtsStream — siehe
 * frontend/lib/sprechtasten-vorrang.ts.
 */
let sprechtastenQueue: TtsQueue | null = null;

/**
 * Pause/Fortsetzen vom Sperrbildschirm. (24.09.2026, Ticket 2547,
 * Nachprüfung Build 77)
 *
 * Bis Build 76 reichte die App den Druck als `wiedergabeUmschalten` weiter —
 * und keine Seite hörte darauf. Ab Build 77 sagt sie, WAS gedrückt wurde;
 * ein nativ spielendes Stück (Bildschirm aus) hat es dann schon selbst
 * ausgeführt. Hier zieht die Seite nach: jede laufende Warteschlange und der
 * Knopf an der Blase bekommen denselben Stand — sonst spielte sie nach dem
 * Stück einfach das nächste.
 */
function fernbedienungAnwenden(befehl: FernBefehl): void {
  const anhalten = befehl === "anhalten";
  const queues = new Set<{ pause(): void; resume(): void }>(activeTtsQueues);
  if (currentTtsQueue) queues.add(currentTtsQueue);
  if (sprechtastenQueue) queues.add(sprechtastenQueue);
  for (const q of queues) {
    try {
      if (anhalten) q.pause();
      else q.resume();
    } catch {
      /* eine Warteschlange darf die anderen nicht aufhalten */
    }
  }
  const knopf = currentAudioBtn;
  if (knopf && (knopf.dataset.state === "playing" || knopf.dataset.state === "paused")) {
    knopf.dataset.state = anhalten ? "paused" : "playing";
    setVoicePrep(anhalten ? "paused" : "playing");
  }
}
void aufFernbedienungHoeren((befehl) => fernbedienungAnwenden(befehl));
// 17.10.2026: die App hielt ihre Stimme selbst an (Kopfhörer/Auto weg, stimme-nativ.ts) — Anzeige und Schlangen wie vom Sperrbildschirm.
aufStimmeNativPauseHoeren(() => fernbedienungAnwenden("anhalten"));

/**
 * Laufendes Vorlesen abbrechen.
 *
 * @param grund WER abbricht. Ticket 2232: Der Abbruch war der einzige
 * Ausgang ohne Namen — in der Sonde stand nur `via=?`. Gemessen ueber 30
 * Stunden trugen ALLE drei unvollstaendigen Wiedergaben genau diese Marke,
 * bei 18 vollstaendigen keine einzige. Zwei davon waren Martins eigener
 * PTT-Druck (so gewollt), beim dritten fand sich kein Ausloeser. Ohne Namen
 * sieht das Gewollte aus wie ein Fehler — und der Fehler versteckt sich
 * dahinter.
 */
function stopCurrentTts(grund = "unbekannt"): void {
  // 2026-07-04 (Martin: "laeutet immer noch"): JEDER Stop beendet auch den
  // Anruf-Klingelton — egal auf welchem Weg der Aufbau haengen blieb.
  stopRingingTone();
  // 16.10.2026: ein Stopp (Knopf, Sprechtaste, neuer Strom …) beendet auch das Mikro, das nach dem Schmied noch öffnen wollte.
  // 19.10.2026: ebenso beim Mikro, das nach Zimmermann oder Hüter noch öffnen wollte.
  for (const w of alleWarten()) {
    if (w.verwirfMikroNachVorlesen()) meldeDiagnose(`[ptt] mikro nach-schmied verworfen grund=${grund}`);
    // 16.10.2026, 18:29: ein Warten, das die Anzeige „Schmied arbeitet“ über die Frist hinaus hielt, endet ohne Öffnen.
    if (w.verwirfVerlaengerung()) meldeDiagnose(`[schmied] frist-verlaengert verworfen grund=${grund}`);
  }
  // 21.10.2026: ebenso ein Mikro, das auf das Ende der Vorlese-Reihe wartete (lib/vorlese-reihe.ts) — Martin hat gehandelt.
  if (vorleseReihe.verwirfMikroNachReihe()) meldeDiagnose(`[ptt] mikro nach-reihe verworfen grund=${grund}`);
  stoppeWiedergabenAusser(grund, null);
}

/** Läuft gerade ein Beitrag des Schmieds hörbar (oder wartet er in der Schlange auf seinen Ton)? */
function schmiedSprichtGerade(): boolean {
  for (const q of activeTtsQueues) if (q.sprecher === "schmied" && q.isAudioActive()) return true;
  return false;
}

/**
 * Der Kern von `stopCurrentTts`. `ausser`: Schlangen dieses Sprechers bleiben (16.10.2026: Vesperas NEUE automatische
 * Antwort beendet den Schmied nicht mehr — sie wartet in der Schlange, seiten-abgleich.ts `stimmeDarfBeginnen`). Ein
 * Eingriff Martins ruft immer `stopCurrentTts` — ohne Ausnahme.
 */
function stoppeWiedergabenAusser(grund: string, ausser: "schmied" | null): void {
  const bleibt = (q: { cancel(grund?: string): void; readonly sprecher?: "schmied" | "probe" } | null): boolean => ausser !== null && q?.sprecher === ausser;
  if (currentTtsAbort && !bleibt(currentTtsQueue)) {
    try {
      currentTtsAbort.abort();
    } catch {
      /* ignore */
    }
    currentTtsAbort = null;
  }
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = "";
    currentAudio = null;
  }
  if (currentTtsQueue && !bleibt(currentTtsQueue)) {
    try {
      currentTtsQueue.cancel(grund);
    } catch {
      /* ignore */
    }
    currentTtsQueue = null;
  }
  // 2026-07-03 (Martin): auch jede ANDERE noch laufende Queue stoppen
  // (z.B. die PTT-Autoplay-Queue) — sonst Doppel-Audio beim Play-Klick.
  for (const q of Array.from(activeTtsQueues)) {
    if (bleibt(q)) continue;
    try {
      q.cancel(grund);
    } catch {
      /* ignore */
    }
    activeTtsQueues.delete(q);
  }
  if (currentAudioBtn) {
    currentAudioBtn.dataset.state = "idle";
    currentAudioBtn = null;
  }
  if (ausser === null) setVoicePrep("hidden");
}

/**
 * Pre.164: Splittet langen Text in TTS-Chunks am Satzende. Server-Limit
 * ist 500 chars — wir teilen knapp drunter und finden Satz-Boundary.
 * Vorher: Text > 500 wurde am ersten Satzende abgeschnitten → User hörte
 * nur den Anfang (Live-Bug Martin pre.161).
 */
/** Lädt EINEN Chunk — Cache-Lookup zuerst, sonst fetch. */
// Pre.409: Gemini-Realtime-Voice → identische Chirp3-HD-Voice für Chat-TTS.
// Beide Voice-Families sind seit 2025 1:1 dieselben (Aoede/Kore/Zephyr/
// Charon/Puck). Für Leda/Fenrir/Orus nutzen wir die nahesten Chirp3-Stimmen.
const GEMINI_TO_CHIRP3: Record<string, string> = {
  aoede: "de-DE-Chirp3-HD-Aoede",
  kore: "de-DE-Chirp3-HD-Kore",
  zephyr: "de-DE-Chirp3-HD-Zephyr",
  charon: "de-DE-Chirp3-HD-Charon",
  puck: "de-DE-Chirp3-HD-Puck",
  leda: "de-DE-Chirp3-HD-Achernar",
  fenrir: "de-DE-Chirp3-HD-Achird",
  orus: "de-DE-Chirp3-HD-Algenib",
};

/**
 * Was von der wachsenden Antwort in die Stimme darf — und nur der neue Teil.
 *
 * Ruft man das je Brocken auf, wird aus der Sprechfassung genau einmal
 * gesprochen und die Anzeigefassung (Tabelle) gar nicht. Fehlt die Marke,
 * ist `quelle` der ganze Text und es bleibt beim Verhalten von vorher.
 *
 * ⚠️ Der Zeiger zaehlt in der QUELLE, nicht in der Rohantwort. Beide wachsen
 * unterschiedlich schnell: die Rohantwort enthaelt die Marken, die Quelle
 * nicht. Wer den Rohzeiger nimmt, verschluckt am Ende so viele Zeichen, wie
 * die Marken lang sind.
 */
/**
 * Ticket 2942 (Rest, 14.10.2026): ALLEIN im Browser – der Server ersetzt nichts (er sieht nur Stücke und
 * kennt weder die Bitte noch den Schalter). Große Tabellen werden im GANZEN Text zu einem
 * Satz ersetzt, BEVOR die Warteschlange ihn in Sätze schneidet (shared/tabelle-im-chat.ts).
 * Nicht, wenn der Nutzer das Vorlesen der Tabelle ausdrücklich verlangt hat.
 * Notschalter am Gerät: localStorage "sprech-tabelle-aus" = "1".
 */
// Seitenweit, nicht je Blase: der Abspielknopf einer aelteren Antwort nimmt die LETZTE Frage – harmlos
// (schlimmstenfalls bleibt eine Tabelle hoerbar bzw. wird ersetzt).
let zugNutzerFrage = "";
function tabellenFuerDieStimme(quelle: string, fertig: boolean): string {
  if (bittetUmVorlesen(zugNutzerFrage)) return quelle;
  try {
    if (localStorage.getItem("sprech-tabelle-aus") === "1") return quelle;
  } catch {
    /* ohne Speicher: die Regel gilt */
  }
  return grosseTabellenErsetzen(quelle, { fertig });
}

function sprechNachschub(
  roh: string,
  bisher: number,
  /** Ist die Antwort zu Ende? Dann wird auch eine kleine Tabelle am Schluss freigegeben. */
  fertig = false,
): { text: string; neu: number } {
  // Solange aus dem Anfang noch die Marke werden kann, ist nichts entschieden.
  if (markeNochMoeglich(roh)) return { text: "", neu: bisher };
  const g = teileAntwort(roh);
  const quelle = tabellenFuerDieStimme(sprechQuelle(g), fertig);
  /*
   * Ticket 2232 (13.09.2026): Vorlesen brach nach Werkzeug-Aufrufen ab, nur
   * 12% einer 1343-Zeichen-Antwort wurden vertont — Ursache nicht gefunden
   * (kein Marken-Text, kein Fehler im Code sichtbar). Eine der offenen
   * Fragen: schiesst `bisher` ueber `quelle.length` hinaus (z.B. weil eine
   * fruehere Fassung der Quelle laenger war als die aktuelle)? Dann gaebe
   * `sprechNachschub` bis zum Zugende dauerhaft "" zurueck, ohne dass sich
   * das je erholt — genau das gemessene Bild. Sollte nie passieren; wenn
   * doch, soll der naechste Vorfall sich selbst erklaeren statt erneut
   * geraten zu werden.
   */
  if (quelle.length < bisher) {
    meldeDiagnose(
      `[sprech-d] überschossen: bisher=${bisher} quelle=${quelle.length} roh=${roh.length}`,
    );
  }
  if (quelle.length <= bisher) return { text: "", neu: bisher };
  return { text: quelle.slice(bisher), neu: quelle.length };
}

async function fetchTtsChunk(
  text: string,
  voiceId: string,
  signal: AbortSignal,
  answerId?: string,
  /**
   * 15.10.2026 (Martin, 22:42: „eins zu eins dasselbe wie bei Vespera“): eine andere Stimme mit eigenem Tempo — der Schmied
   * (shared/schmied.ts). Nur die Stimme und das Tempo sind anders; der Weg (PCM, Strom, Datei), der Zwischenspeicher und
   * die Vorbereitung sind DIESELBEN. Ohne Angabe: Vesperas Vorlese-Stimme und ihr Tempo-Regler.
   */
  eigeneStimme?: { voiceId: string; speakingRate: number; ganz?: true; vorab?: true },
): Promise<string> {
  // #522 v2 (2026-07-23): Die BEWUSST gewaehlte Vorlese-Stimme gilt fuer
  // ALLE Antworten dieses Pfads (PTT, getippt, Vorlesen) — hier im Client
  // aufgeloest, NICHT per Server-Override: der ueberfuhr auch die Stimm-
  // Proben der Einstellungen (jede Probe klang identisch, pre.1898).
  const chosenRead = eigeneStimme ? null : chosenReadVoice();
  const effVoice = eigeneStimme?.voiceId ?? chosenRead ?? voiceId;
  const settings = loadSettings();
  // Ein frisches Objekt je Aufruf (lib/settings.ts) — Vesperas gespeichertes Tempo bleibt unberührt.
  if (eigeneStimme) settings.speed = eigeneStimme.speakingRate;
  const cacheKey = ttsCacheKey(effVoice, text, settings.speed);
  const cached = ttsCacheGet(cacheKey);
  if (cached) return cached;
  // 17.10.2026: der Schmied im Ganz-Modus lädt vor — am Server ohne Vorrang vor Vesperas Stücken (soniox-schleuse.ts `vorab`).
  // Nur solange das Tor des Ganz-Modus zu ist (lib/schmied-stimme.ts). Der PCM-Weg fragt erst beim Laden (`&vorab=1`, tts-pcm.ts).
  const vorab = eigeneStimme?.vorab === true;
  // Pre.347 / Pre.409: ttsProvider + googleTtsVoice aus Settings mitsenden.
  // Pre.409-Fix: Wenn User Gemini-Realtime nutzt, ist voiceId oft eine
  // alte ElevenLabs-ID (vom vorherigen Pipeline-Setup) — Server fällt
  // dann auf den hardcoded Wavenet-Fallback zurück, das klingt komplett
  // anders als der Live-Chat. Hier mappen wir die Gemini-Voice (oder
  // Aoede als Default) auf die identische Chirp3-HD-Stimme und ZWINGEN
  // ttsProvider=google. Damit redet der Chat exakt wie der Anruf.
  // (Die Einstellungen stehen schon oben — das Tempo gehört in den Schlüssel.)
  // Ticket 2448 (23.09.2026): Beta „Vorlesen sofort" + Soniox — eine
  // Strom-Adresse statt einer fertigen Datei (frontend/lib/tts-strom.ts).
  // NICHT im ttsCache: der Schlüssel darin läuft nach 10 min ab, der
  // Zwischenspeicher des Servers trägt die Wiederholung ohnehin.
  /*
   * Ticket 2919 (02.10.2026, Stufe 1): PCM-Strom über Web Audio — hinter dem
   * eigenen Schalter ttsPcmBeta (Vorgabe AUS). Aus = diese Zeilen tun nichts.
   * An: die Adresse zeigt auf /api/tts/pcm (frontend/lib/tts-pcm.ts), das
   * Tempo spricht Soniox selbst (bis 1,3, darüber der alte Weg). Scheitert die
   * Vorbereitung, geht es unten weiter wie bisher.
   */
  if (
    pcmWegFuer({
      beta: settings.ttsPcmBeta === true,
      voiceId: effVoice,
      tempo: settings.speed,
      // Der Dauer-Rückfall (50er-Regel) ODER das Störungs-Gedächtnis nach einem Stromfehler (zehn Minuten, nur im Speicher der Seite).
      // 16.10.2026: das Gedächtnis nicht für den Schmied im Ganz-Modus (lädt vor dem Sprechen alles, tts-queue.ts `ganzVorab`).
      rueckfall: pcmRueckfall.aktiv() || (pcmGedaechtnis.dateiWeg() && eigeneStimme?.ganz !== true),
      webAudio: webAudioDa(),
      sichtbar: document.visibilityState !== "hidden",
    })
  ) {
    const token = await tokenFuerSatz(text, effVoice, signal, fetch, settings.speed);
    if (token) return pcmAdresseAusToken(token);
  }
  if (
    stromWegFuer({
      beta: settings.ttsDirektBeta === true,
      voiceId: effVoice,
      tempo: settings.speed,
    })
  ) {
    const strom = await stromAdresse(text, effVoice, signal, fetch, vorab);
    if (strom) return strom;
  }
  const existingGoogleVoice =
    (settings as unknown as { googleTtsVoice?: string }).googleTtsVoice ?? null;
  // Pre.477: Wenn voiceMode=gemini-realtime, nutze das echte Gemini-TTS-API
  // (gleiche Voice-Engine wie Live) — gemini-Voice-Name (Aoede/Charon/...)
  // wird im geminiVoice-Field mitgegeben. Server fallback'd auf Chirp3-HD
  // wenn Gemini-TTS failt.
  // STIMMEN-PICKER (2026-06-27): IMMER die gewählte Stimme mitschicken (nicht
  // nur im Realtime-Modus). Der Server (toGeminiVoice) fällt bei leer/ungültig
  // auf Charon zurück → der Picker wirkt in jedem Modus, konsistent, kein Split.
  let geminiVoice: string | null = null;
  const rtVoice = settings.realtimeVoice?.trim();
  if (rtVoice && rtVoice.length > 0) geminiVoice = rtVoice;
  // Pre.413: ElevenLabs ist NICHT mehr Default. Default für Vorlesen ist
  // Chirp3-HD (gleiche Voice-Family wie Gemini Live) als Fallback.
  // ElevenLabs nur wenn User explizit ttsProvider="elevenlabs" gewählt hat.
  let effectiveProvider = settings.ttsProvider;
  let effectiveGoogleVoice = existingGoogleVoice;
  // 2026-07-22: Soniox-Klon ("soniox:")-Stimmen NIE auf Google zwingen —
  // der Server routet sie am Praefix vorbei an allen Heuristiken.
  if (effectiveProvider !== "elevenlabs" && !effVoice.startsWith("soniox:")) {
    if (chosenRead && !chosenRead.startsWith("de-DE-")) {
      // #522 v2: bewusste Klon-/ElevenLabs-Wahl NIE auf Google zwingen —
      // dieselbe Ausnahme, die Soniox-Stimmen seit 2026-07-22 haben.
      effectiveProvider = "elevenlabs";
    } else {
      effectiveProvider = "google";
    }
    const rtLower = (settings.realtimeVoice || "").toLowerCase();
    const voiceLower = effVoice.toLowerCase();
    // 2026-07-23 (Martin, "eine Stimme fuer alles"): eine bewusst gewaehlte
    // GEMINI-Vorlese-Stimme steuert den Einheits-Zweig — sonst spraeche er
    // stur die Anruf-Stimme (geminiVoice kam bisher nur aus realtimeVoice).
    if (chosenRead?.startsWith("de-DE-Chirp3-HD-")) {
      effectiveGoogleVoice = chosenRead;
      geminiVoice = chosenRead.slice("de-DE-Chirp3-HD-".length);
    }
    effectiveGoogleVoice =
      GEMINI_TO_CHIRP3[rtLower] ??
      GEMINI_TO_CHIRP3[voiceLower] ??
      existingGoogleVoice ??
      "de-DE-Chirp3-HD-Aoede";
  }
  const res = await fetch("/api/voice-preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      voiceId: effVoice,
      text,
      ttsProvider: effectiveProvider,
      googleTtsVoice: effectiveGoogleVoice,
      geminiVoice,
      // Pre.432: Tempo-Slider reaktiviert via Chirp3-HD speakingRate.
      speakingRate: settings.speed,
      // #305: alle Segmente EINER Antwort auf denselben Provider pinnen.
      answerId,
      ...(vorab ? { vorab: true } : {}),
    }),
    signal,
  });
  if (!res.ok) throw new Error(`tts http ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  ttsCacheSet(cacheKey, url);
  return url;
}

let voicePrepTeile: VorlesePille | null = null;
/** Meldet die Höhe des Verfassers an die Elemente, die sie lesen — gesetzt, sobald die Knopfzeile steht (verfasserHoeheMelden). */
let verfasserHoeheNachmessen: (() => void) | null = null;
function ensureVoicePrepEl(): void {
  if (voicePrepTeile) return;
  // 17.10.2026: das Gerüst steht in lib/vorlese-pille.ts — Hauptteil (unterbrechen) und „Fertig“ als zwei echte Knöpfe.
  voicePrepTeile = baueVorlesePille({
    // Tap/Klick/Enter auf die Pille bricht das laufende Vorlesen sofort ab.
    // 05.10.2026: außer sie wartet auf eine Berührung („Tippen zum Weiterspielen“) — dann setzt genau diese Berührung
    // fort (lib/datei-spieler.ts), und der Klick derselben Geste darf nicht gleich wieder stoppen.
    haupt: (taste) => {
      if (voicePrepZustand === "wartet" || fortsetzBeruehrungGerade()) return;
      pilleUnterbricht(taste ? "pille-taste" : "pille");
    },
    fertig: (taste) => pilleFertig(taste ? "pille-fertig-taste" : "pille-fertig"),
  });
  document.body.appendChild(voicePrepTeile.pille);
  verfasserHoeheNachmessen?.(); // die Pille liest `--verfasser-h` an sich selbst (app.ts, verfasserHoeheMelden)
}
/**
 * Ticket 2695 (Weg A): Tipp auf die sprechende Pille = stoppen UND sofort sprechen. Der Start ist der vorhandene
 * Freihand-Weg der Sprechtaste (startPttHandsFree) im selben Klick — kein zweiter Aufnahmeweg. Im Anruf-Modus und bei
 * laufender Aufnahme bleibt es beim bloßen Stoppen (lib/unterbrechen.ts).
 */
function pilleUnterbricht(kanal: "pille" | "pille-taste"): void {
  unterbrechePille({
    kanal,
    zustand: voicePrepZustand,
    // Nur im Chat ohne offene Seite: die Aufnahme springt in den Chat und schlösse sonst ungespeicherte Eingaben.
    aufnahmeMoeglich: darfMikroOeffnen({
      anruf: connected || loadSettings().pttMode === "call",
      pttIdle: pttState === "idle",
      sperre: aufnahmeStartSperre() !== null,
      ansicht: document.body.dataset.activeView,
      etwasUeberDemChat: liegtEtwasUeberDemChat(),
    }),
    ttsLief: () => ttsWiedergabeOffen(),
    stoppe: (grund) => stopCurrentTts(grund),
    starte: (ttsLief) => {
      startPttHandsFree(kanal, undefined, ttsLief);
      // Abgewiesen, bevor ein Start lief: keine Messung stehen lassen, die eine spätere Aufnahme verfälscht.
      if (pttState === "idle" && !pttStartLauf) unterbrechenVerwerfen();
    },
    jetzt: () => performance.now(),
  });
}

/**
 * 17.10.2026 (Martin, 09:45): „Fertig“ in der Pille — die Antwort ist abgeschlossen. Alles stoppt (auch Warteschlange,
 * noch nicht gestartete Vertonungen, vorbereitete und zurückgehaltene Beiträge des Schmieds), das Mikro bleibt zu
 * (lib/fertig.ts). `anderswo`: auf einer anderen Seite oder einem anderen Gerät gedrückt (lib/seiten-abgleich.ts).
 */
function pilleFertig(kanal: FertigKanal): void {
  fertigDruecken({
    kanal,
    // 20.10.2026 (Martin 23:05): spricht der Abspielknopf, beendet „Fertig“ nur ihn — die Reihe dahinter läuft weiter (lib/fertig.ts).
    nurKnopf: fertigNurFuerKnopf(kanal, knopfWiedergabeLaeuft(currentAudioBtn), !zugEndeStand().zugFertig),
    zug: zugKennung(),
    jetzt: () => Date.now(),
    stoppe: (grund) => stopCurrentTts(grund),
    verwirfSchmied: (grund) => verwirfSchmiedVorbereitet(grund),
    schmiedWartenFertig: () => alleWarten().map((w) => w.fertig()).some(Boolean),
    seitenFertig: (weiter, grund) => stimmenFertig(weiter, grund),
    melde: meldeDiagnose,
  });
}
beiFertigAnderswo(() => pilleFertig("anderswo"));

/**
 * Zustand der Sprech-Pille (unten):
 *  - "prep"    Spinner + "Stimme wird vorbereitet" (erstes Audio rendert noch)
 *  - "playing" Stop-Symbol + "tippen zum Stoppen" (klickbar -> Abbruch)
 *  - "wartet"  Weiter-Zeichen + "Tippen zum Weiterspielen" (05.10.2026): der Browser hat den Start abgelehnt
 *              oder das Stück stand nach mehreren frischen Starts — die nächste Berührung irgendwo setzt fort.
 *  - "hidden"  weg.
 */
type VoicePrepZustand = "prep" | "playing" | "paused" | "wartet" | "hidden";
/** Was die Pille gerade zeigt. EIN Schreiber: `setVoicePrep`. */
let voicePrepZustand: VoicePrepZustand = "hidden";
/**
 * 2026-07-28 (Martin: "wenn er sagt 'Ich arbeite daran', einen Moment warten,
 * bevor die Stopp-Pille kommt, weil er noch nicht spricht"): Die Pille erschien
 * schon in der Vorbereitung — also bevor ueberhaupt ein Ton da war. Bei kurzen
 * Antworten blitzte sie dadurch nur auf, und man tippt auf einen Stopp fuer
 * etwas, das noch gar nicht laeuft.
 * Jetzt wartet der VORBEREITUNGS-Zustand kurz ab. Kommt der Ton in dieser Zeit,
 * erscheint die Pille direkt als "Spricht" — kommt er nicht, meldet sie sich
 * erst nach der Frist mit "wird vorbereitet". Sprechen und Pause zeigen sich
 * weiterhin SOFORT, denn dort gibt es wirklich etwas zu stoppen.
 */
const VOICE_PREP_DELAY_MS = 900;
let voicePrepDelayTimer: number | null = null;

/**
 * 17.10.2026 (Gutachten): wartet die vorbereitende Schlange nur auf den ANDEREN Sprecher (seiten-abgleich.ts
 * `stimmeDarfBeginnen`), sagt die Pille das — statt „Stimme wird vorbereitet“. Der Text, oder null. Gilt nur im Zustand
 * „prep“; jeder neue Zustand (`setVoicePrep`) gibt ihn auf. Größe und Platz der Pille bleiben.
 */
let voicePrepWartetAuf: string | null = null;

/** Hörer an `TtsQueue.beiWartenAufStimme`: nur die Schlange, der die Pille gehört, und nur, solange sie vorbereitet. */
function pilleWartetAufStimme(queue: TtsQueue, wartet: boolean): void {
  if (queue !== currentTtsQueue || voicePrepZustand !== "prep") return;
  voicePrepWartetAuf = wartet ? wartetAufText(queue.sprecher) : null;
  // Steht die Pille schon, gleich umschreiben; sonst zeigt sie der Zeitgeber von „prep“ mit dem richtigen Text.
  if (voicePrepDelayTimer === null) zeigeVoicePrep("prep");
}

/**
 * 17.10.2026: Ereignis `voice-v8:wartet-auf-lange-spur` (verdrahteLangeSpurHinweise). Bei langer Spur setzt die Antwort „prep“ gar nicht
 * erst (siehe beginTtsStream) — die Pille wird hier geholt und sagt, worauf gewartet wird. Gleiche Pille, kein neues CSS; „true“ nur,
 * solange nichts spielt. „false“: der Text wird aufgegeben, spielt es danach, übernimmt `onAudioStart` wie immer.
 */
function pilleWartetAufLangeSpur(wartet: boolean): void {
  if (!wartet) {
    if (voicePrepWartetAuf !== WARTET_AUF_LANGE_SPUR_TEXT) return;
    voicePrepWartetAuf = null;
    if (voicePrepZustand === "prep" && voicePrepDelayTimer === null) zeigeVoicePrep("prep");
    return;
  }
  if (voicePrepZustand !== "prep" && voicePrepZustand !== "hidden") return;
  if (voicePrepZustand === "hidden") setVoicePrep("prep");
  voicePrepWartetAuf = WARTET_AUF_LANGE_SPUR_TEXT;
  if (voicePrepDelayTimer !== null) {
    clearTimeout(voicePrepDelayTimer);
    voicePrepDelayTimer = null;
  }
  zeigeVoicePrep("prep");
}
window.addEventListener("voice-v8:wartet-auf-lange-spur", (e) => pilleWartetAufLangeSpur((e as CustomEvent<{ wartet?: boolean }>).detail?.wartet === true));

/**
 * 21.10.2026 (lib/vorlese-mitlauf.ts, Martin 21:12): die Vorlese-Karte einer Antwort zeigt „Liest vor“/„Pausiert“ und den Teil,
 * auch wenn die Sprechtasten-Antwort sie ohne ihren Knopf spricht. Nur Anzeige; nur diese Schlange — nur bei ihr ist ein Tipp
 * auf den Knopf Pause/Weiter (makePlayButton, tippAufLaufendeAntwort).
 */
const vorleseMitlauf = erzeugeMitlauf(() => sprechtastenQueue, meldeDiagnose);

function setVoicePrep(state: VoicePrepZustand): void {
  voicePrepZustand = state;
  // Jede Änderung der Pille ist eine Änderung der Wiedergabe — die Karten gleichen nach (nach dem Setzen, siehe unten).
  queueMicrotask(() => vorleseMitlauf.anstossen());
  voicePrepWartetAuf = null;
  if (voicePrepDelayTimer !== null) {
    clearTimeout(voicePrepDelayTimer);
    voicePrepDelayTimer = null;
  }
  if (state === "hidden") {
    if (voicePrepTeile) verbergeVorlesePille(voicePrepTeile);
    return;
  }
  if (state === "prep") {
    voicePrepDelayTimer = window.setTimeout(() => {
      voicePrepDelayTimer = null;
      zeigeVoicePrep("prep");
    }, VOICE_PREP_DELAY_MS);
    return;
  }
  zeigeVoicePrep(state);
}

function zeigeVoicePrep(state: "prep" | "playing" | "paused" | "wartet"): void {
  ensureVoicePrepEl();
  if (!voicePrepTeile) return;
  const active = state === "playing" || state === "paused";
  const text =
    state === "wartet"
      ? "Tippen zum Weiterspielen"
      : state === "paused"
        ? "Pausiert \u2013 Unterbrechen, ich spreche"
        : active
          ? currentTtsQueue?.sprecher === "schmied"
            ? `${currentTtsQueue?.sprecherName ?? SCHMIED_NAME} spricht \u2013 unterbrechen`
            : "Unterbrechen \u2013 ich spreche"
          : (voicePrepWartetAuf ?? "Stimme wird vorbereitet \u2026");
  // Klassen, Beschriftung, „Fertig“ ein/aus: lib/vorlese-pille.ts (EIN Ort, den auch der Chromium-Test zeichnet).
  zeichneVorlesePille(voicePrepTeile, state, text);
}

interface TtsStream {
  push(t: string): void;
  finish(): Promise<void>;
  /** Ticket 2257: fuer den Werkzeug-Warte-Ton — nur in echte Stille hinein. */
  isAudioActive(): boolean;
  /** Ticket 2257 Teil a: den angefangenen, noch ungesprochenen Satzrest
   *  sofort aussprechen — vor einem Werkzeug-Aufruf, analog zum PTT-Pfad. */
  flushPending(): void;
  /** 20.10.2026 (Lesemodus): die Mail-Karte erschien — nichts mehr sprechen, was läuft, abbrechen. */
  stumm(): void;
}

// 2026-07-21 (Martin, "genauso wie bei Push-to-Talk"): geteilter Kern fuer
// Knopf-/Auto-Vorlesen UND das satzweise Streamen getippter Antworten.
// Pre.1111-Mechanik: die SATZWEISE TtsQueue rendert parallel und spielt
// sequenziell — erstes Audio nach ~1 Satz; finish() gibt den Rest frei und
// wartet, bis alles gesprochen (oder per Pillen-Stop abgebrochen) ist.
/**
 * Ticket 2232 (14.09.2026): Was aus dem Text dieses Zuges wirklich wurde.
 *
 * Die Nachschub-Bilanz vom 13.09. ist beantwortet — gemessen am 14.09. im
 * Journal: sechs Zug-Enden, jedes mit bisher === botText, zwei davon mit
 * Werkzeug-Aufrufen davor (also Martins Fehlerfall). Der Nachschub liefert
 * vollstaendig ab.
 *
 * Damit bleibt die Wiedergabe. Diese Meldung kommt, wenn die Warteschlange
 * LEER ist — also nach dem letzten Ton, nicht schon beim Abschicken. Klafft
 * hier "gesprochen" unter "eingereiht", ist der Abbruch gefunden; "verloren"
 * trennt dabei die gescheiterte Vertonung vom stillen Verschwinden.
 */
function meldeSprechBilanz(queue: TtsQueue, wo: string): void {
  const b = queue.sprechBilanz();
  meldeDiagnose(
    `[sprech-d] wiedergabe(${wo}) eingereiht=${b.eingereiht} ` +
      `gesprochen=${b.gesprochen} verloren=${b.verloren} offen=${b.offen}` +
      (b.abbruch ? ` abbruch=${b.abbruch}` : ""),
  );
  // 15.10.2026: EINE Zeile je Zug mit den Zahlen des Vorlesens (Neustarts, Lücken, langsamstes Stück — vorlese-zugmessung.ts).
  meldeDiagnose(queue.zugMessZeile());
}

/**
 * 23.09.2026 (Ticket 2551): Was ein automatisches Vorlesen bekommt, wenn es
 * der offenen Sprechtasten-Antwort weicht — ein Strom, der annimmt und
 * schweigt. Die Zeile sagt im Protokoll, warum es still blieb.
 */
function stillerStromFuerSprechtaste(): TtsStream {
  meldeDiagnose(`[sprech-d] weicht der Sprechtaste: automatisches Vorlesen bleibt still`);
  return stillerStrom();
}

/** Ein Strom, der annimmt und schweigt (Sprechtaste hat Vorrang, oder „Fertig“ wurde gedrückt). */
function stillerStrom(): TtsStream {
  return {
    push: (): void => undefined,
    finish: (): Promise<void> => Promise.resolve(),
    // Es klingt schon eine Stimme — hier soll auch kein Warte-Ton dazwischen.
    isAudioActive: (): boolean => true,
    flushPending: (): void => undefined,
    stumm: (): void => undefined,
  };
}

/** Eine andere Stimme als Vesperas Vorlese-Stimme, mit eigenem Tempo — der Schmied (lib/schmied-stimme.ts). */
type EigeneStimme = { voiceId: string; speakingRate: number };

/**
 * 17.10.2026 (lange-spur.ts): was eine Schlange zeigt, die auf eine LANGE SPUR wartet.
 *   - Ereignis `voice-v8:wartet-auf-lange-spur` {wartet} — die Pille hängt sich daran (statt „Stimme wird vorbereitet“);
 *   - läuft die Frist ab (45 min), verfällt die Antwort NICHT still: die Blase trägt `data-vorgelesen="nicht-vorgelesen"`, ein
 *     Hinweis sagt es, der Abspielknopf an der Blase holt sie nach.
 */
function verdrahteLangeSpurHinweise(queue: TtsQueue, blase?: HTMLElement | null): void {
  queue.beiWartenAufLangeSpur = (wartet): void => {
    window.dispatchEvent(new CustomEvent("voice-v8:wartet-auf-lange-spur", { detail: { wartet } }));
  };
  queue.beiLangeSpurFrist = (): void => {
    meldeDiagnose("[lange-spur] antwort nicht vorgelesen frist");
    if (blase) blase.dataset.vorgelesen = "nicht-vorgelesen";
    window.dispatchEvent(
      new CustomEvent("voice-v8:toast", { detail: { message: "Antwort nicht vorgelesen – der Abspielknopf an der Blase holt sie nach.", kind: "info" } }),
    );
  };
}

/**
 * `stimme`: 15.10.2026 (Martin, 22:42: „eins zu eins dasselbe wie bei Vespera“) — liest der Schmied, ist NUR die Stimme eine
 * andere (fetchTtsChunk). Schlange, Weg, Vorbereitung und Bedienung sind dieselben; die Schlange trägt `sprecher: "schmied"`
 * (die Pille sagt „Schmied spricht“, das Mikro entscheidet an seinem Ende nichts).
 */
function beginTtsStream(btn?: HTMLButtonElement, stimme?: EigeneStimme, blase?: HTMLElement | null, fortsetzen?: TtsQueueFortsetzen): TtsStream {
  /*
   * ⚠️ Ticket 2232: DAS hier ist der Abbruch, der KEINER sein duerfte.
   *
   * Ein neuer Strom loescht den laufenden. Bei einem Knopfdruck ist das
   * richtig. Startet aber irgendein zweiter Weg waehrend einer laufenden
   * Antwort einen Strom — ein Bereitschafts-Ping, ein Echo des anderen
   * Kanals —, verstummt Martin mitten im Satz, und niemand hat gedrueckt.
   * Genau dieser Fall stand am 14.09. um 19:13 ohne Ausloeser im Protokoll.
   * Taucht `abbruch:neuer-strom` in der Sonde auf, ist er gefunden.
   *
   * 23.09.2026 (Ticket 2551): gefunden, am 24.09. um 00:36 bei Albert. Der
   * getippte Zug las seine Antwort vor, während die Sprechtasten-Antwort
   * noch auf ihren Text wartete. Dieser Abbruch leerte auch ihre Schlange,
   * und ihre 1542 Zeichen kamen in eine tote Schlange. Ohne Knopf weicht
   * ein neuer Strom jetzt der offenen Sprechtasten-Antwort und bleibt still.
   */
  if (weichtDerSprechtaste({ knopf: !!btn, sprechtaste: sprechtastenQueue }))
    return stillerStromFuerSprechtaste();
  // 17.10.2026 (lib/fertig.ts): Martin hat „Fertig“ gedrückt — in diesem noch laufenden Zug beginnt keine neue automatische Stimme.
  if (!stimme && fertigHaeltStimme({ zug: zugKennung(), zugLaeuft: !zugEndeStand().zugFertig, knopf: !!btn })) {
    meldeDiagnose("[fertig] neue stimme bleibt still");
    return stillerStrom();
  }
  /*
   * 16.10.2026 (Martin: „eine laufende Spur darf nicht unterbrochen werden“): liest gerade der Schmied, beendet eine
   * AUTOMATISCHE Antwort Vesperas (kein Knopf) ihn nicht mehr — ihre Schlange wartet, bis er fertig ist
   * (seiten-abgleich.ts `stimmeDarfBeginnen`). Journal 16.10.: `wiedergabe(schmied) … abbruch=neue-antwort`.
   * Der Abspielknopf (ein Eingriff Martins) stoppt weiter alles.
   */
  const schmiedBleibt = !btn && !stimme && schmiedSprichtGerade();
  if (!schmiedBleibt) stopCurrentTts("neuer-strom");
  else {
    stopRingingTone();
    stoppeWiedergabenAusser("neuer-strom", "schmied");
    meldeDiagnose("[stimme-schlange] neuer-strom wartet auf schmied");
  }
  const settings = loadSettings();
  // Pre.159: Cancellable. Stop-Click bricht den fetch ab UND stoppt das Audio.
  const abort = new AbortController();
  currentTtsAbort = abort;

  if (btn) {
    currentAudioBtn = btn;
    btn.dataset.state = "loading";
  }
  // Sprech-Pille: erst Spinner ("wird vorbereitet"), ab dem ersten Audio
  // wechselt sie auf den klickbaren Stop. 16.10.2026: liest der Schmied noch, bleibt seine Pille (sein Stopp) stehen.
  // 17.10.2026: wartet die Schlange auf eine lange Spur, steht kein „wird vorbereitet“-Spinner minutenlang da.
  if (!schmiedBleibt && (btn || !langeSpurLaeuft())) setVoicePrep("prep");
  // 17.10.2026: der Abspielknopf ist Martins Hand — eine lange Spur wird pausiert, nicht überdeckt (lange-spur.ts).
  if (btn) pausiereFuerHand("abspielknopf");

  let startedSpeaking = false;
  const queue = new TtsQueue({
    ausdruecklich: !!btn,
    fetchTts: (t, signal) => fetchTtsChunk(t, settings.voiceId, signal, undefined, stimme),
    direktBeta: () => loadSettings().ttsDirektBeta === true,
    sprecher: stimme ? "schmied" : undefined,
    fortsetzen,
    onAudioStart: () => {
      if (startedSpeaking) return;
      startedSpeaking = true;
      setVoicePrep("playing");
      if (btn && currentAudioBtn === btn) btn.dataset.state = "playing";
    },
  });
  verdrahteLangeSpurHinweise(queue, blase);
  currentTtsQueue = queue;
  queue.beiWartenAufStimme = (w): void => pilleWartetAufStimme(queue, w);
  activeTtsQueues.add(queue);
  const onAbortCancel = (): void => queue.cancel();
  abort.signal.addEventListener("abort", onAbortCancel);

  return {
    push: (t: string): void => {
      try {
        queue.push(t);
      } catch {
        /* Vorlesen ist Bonus */
      }
    },
    isAudioActive: (): boolean => queue.isAudioActive(),
    flushPending: (): void => {
      queue.flushPending();
    },
    stumm: (): void => queue.cancel("lesemodus"),
    finish: async (): Promise<void> => {
      try {
        queue.flush();
        await queue.idle();
      } catch (err) {
        if ((err as DOMException)?.name !== "AbortError")
          // TTS-Fehler bleiben still — Bot-Text ist sichtbar.
          console.warn("[tts] playback failed:", err);
      } finally {
        meldeSprechBilanz(queue, stimme ? "schmied-knopf" : "stream");
        abort.signal.removeEventListener("abort", onAbortCancel);
        // A1 (19.10.2026): Ist schon ein neuer Strom dran (Sprung des Schiebers), gehören Knopf und Pille ihm.
        const meins = darfKnopfZuruecksetzen(currentTtsQueue, queue);
        if (meins) setVoicePrep("hidden");
        if (meins && btn && currentAudioBtn === btn) {
          btn.dataset.state = "idle";
          currentAudioBtn = null;
        }
        activeTtsQueues.delete(queue);
        if (currentTtsQueue === queue) currentTtsQueue = null;
        if (currentTtsAbort === abort) currentTtsAbort = null;
      }
    },
  };
}

/** Der Text, den die Stimme bekommt (Sprechfassung, Tabellen ersetzt) — Maßstab für Versatz, Hinweis und Schieber. */
function sprechTextVon(text: string): string {
  const geteilt = teileAntwort(text);
  return tabellenFuerDieStimme(sprechQuelle(geteilt), true).trim();
}

type TtsQueueFortsetzen = NonNullable<ConstructorParameters<typeof TtsQueue>[0]["fortsetzen"]>;

/**
 * `ab`: ab diesem Zeichen des Sprechtextes lesen (Schieber der Vorlese-Karte); `0` = von vorn („Von vorn“-Knopf).
 * Ohne `ab` setzt ein Knopfdruck an der gemerkten Stelle fort (vorlese-fortsetzen.ts); nur der Knopf liest sie.
 */
async function playTts(text: string, btn?: HTMLButtonElement, stimme?: EigeneStimme, ab?: number): Promise<void> {
  /*
   * 2026-08-30: Auch das Vorlesen auf Knopfdruck nimmt die Sprechfassung,
   * wenn die Antwort eine traegt. Ohne diese Zeile haette der Knopf die
   * Tabelle vorgelesen und der Sprachweg die Zusammenfassung — dieselbe
   * Antwort klaenge je nach Weg anders. Genau solche Zwillingsstellen sind
   * heute schon viermal aufgefallen.
   */
  const trimmed = sprechTextVon(text);
  if (!trimmed) return;
  // 19.10.2026: lange Texte merken ihre Stelle (auch automatisches Vorlesen); fortgesetzt wird nur auf Knopfdruck.
  const merken = trimmed.length >= FORTSETZEN_MIN_ZEICHEN;
  let basis = 0;
  if (merken) {
    if (ab !== undefined) basis = Math.max(0, Math.min(ab, trimmed.length - 1));
    else if (btn) basis = ladeVersatz(trimmed) ?? 0;
    if (basis === 0) loescheVersatz(trimmed);
  }
  const stand = (versatz: number): void => {
    btn?.dispatchEvent(new CustomEvent("voice-v8:vorlese-stand", { detail: { versatz } }));
  };
  // Führende Leerzeichen am Fortsetz-Punkt gehören nicht zum gelesenen Text — der Versatz zählt sie mit.
  basis += trimmed.slice(basis).length - trimmed.slice(basis).trimStart().length;
  const stream = beginTtsStream(btn, stimme, undefined, merken ? { gesamtText: trimmed, basis, beiStand: stand } : undefined);
  if (basis > 0) {
    meldeDiagnose(`[vorlesen] setzt fort ab=${basis} von=${trimmed.length}`);
    stand(basis);
  }
  const rest = trimmed.slice(basis);
  stream.push(rest.trimStart());
  await stream.finish();
}

/*
 * ═══ DER SCHMIED SPRICHT MIT (15.10.2026, shared/schmied.ts) ═══
 *
 * Martin: der Schmied (die Entwickler-Sitzung im Dreier-Modus) soll eine EIGENE hörbare Stimme bekommen. Seine Beiträge kommen
 * nur über die angemeldete Connect-Tür (server/dreier-routen.ts) als Systemzeile mit der Quelle „entwickler“ — nur die liest
 * diese Stelle vor. Vorlesen ändert nichts daran, was sie sind: Fremdtext, nie Martins Ja, nie ein Auftrag.
 *
 *   - Nur bei „Entwickler vorlesen“ (Konto 1, Vorgabe aus) UND eingeschaltetem Lautsprecher (Antworten vorlesen).
 *   - Er fällt Vespera nicht ins Wort: gelesen wird erst, wenn keine Stimme mehr klingt (höchstens SCHMIED_STILLE_MAX_MS
 *     gewartet, sonst bleibt er still — sein Text steht ja in der Blase). Mehrere Beiträge der Reihe nach.
 *   - Seine Stimme ist die gewählte „Stimme des Entwicklers“ (Soniox), nicht Vesperas Vorlese-Stimme; die Pille sagt
 *     „Schmied spricht“.
 */
/**
 * Die Beiträge des Schmieds der Reihe nach — einer nach dem anderen, nie über Vespera. 28.10.2026 (REIHENFOLGE, Schalter am Server
 * REIHENFOLGE_WIEDERGABE): höchstens REIHE_ENTWICKLER_HOECHSTENS warten, höchstens REIHE_ENTWICKLER_WARTEN_MS lang, Fragen an
 * Martin vor Berichten (nie mitten im Satz) — der Rest bleibt Blase. Solange die Reihe offen ist, behält diese Seite die
 * Wiedergabe über alle Geräte (seiten-abgleich.ts „Besitz“).
 */
const vorleseReihe = erzeugeVorleseReihe({
  grenze: () => (reihenfolgeAn() ? { anzahl: REIHE_ENTWICKLER_HOECHSTENS, wartenMs: REIHE_ENTWICKLER_WARTEN_MS } : null),
  melde: (z) => meldeDiagnose(z),
});
setzeReiheOffenQuelle(() => vorleseReihe.offen() > 0);
/**
 * 17.10.2026 (lib/fertig.ts): vertonte, noch nicht gesprochene Beiträge des Schmieds. Sie stehen in keiner Schlange der Seite,
 * solange sie in der Kette auf Stille warten — „Fertig“ verwirft sie hier, sonst kämen sie danach noch.
 */
const schmiedVorbereitet = new Set<TtsQueue>();

/** „Fertig“: jeden vorbereiteten Beitrag des Schmieds abbrechen. Rückgabe: wie viele. */
function verwirfSchmiedVorbereitet(grund: string): number {
  const n = schmiedVorbereitet.size;
  for (const q of [...schmiedVorbereitet]) {
    try {
      q.cancel(grund);
    } catch {
      /* eine Schlange darf die andere nicht aufhalten */
    }
  }
  schmiedVorbereitet.clear();
  return n;
}
/**
 * So lange wartet ein Beitrag höchstens auf Stille, bevor er ungelesen bleibt. 16.10.2026, 07:02: Vesperas Antwort sprach
 * länger als 60 s vor (der Schmied liest ihren TEXT mit und antwortet währenddessen) — er wurde verworfen, Martin hat ihn
 * nie gehört. Darum 5 Minuten: länger als jede Antwort, aber ein Netz, falls eine Stimme wirklich hängt.
 */
const SCHMIED_STILLE_MAX_MS = 300_000;
/**
 * Das Mikro wartet nach einer Anrede an den Schmied (lib/schmied-warten.ts). 16.10.2026, 18:29: steht beim Fristablauf die
 * Warteblase „Schmied arbeitet“, bleibt es zu (höchstens SCHMIED_STATUS_GRENZE_MS länger) — außer der Server sagt `mikroFrei`.
 */
const schmiedWarten = new SchmiedWarten(undefined, undefined, {
  arbeitet: () => !schmiedStatusMikroFrei && schmiedWarteblase.sichtbar(),
  melde: meldeDiagnose,
});
/**
 * 19.10.2026: Zimmermann und Hüter haben je ein EIGENES Warten, dieselbe Klasse wie das des Schmieds (lib/schmied-warten.ts): Übergabe oder
 * Anrede Vesperas am Ende ihrer Antwort bzw. Martins Nachricht an sie halten das Mikro zu, bis ihre Antwort da und vorgelesen ist (oder die
 * Frist ohne Frage nur den leisen Hinweis zeigt). Ihre Warteblase („Zimmermann übernimmt …“) hält es, solange sie steht.
 */
const zusatzWarten = new Map(
  ZUSATZ_IDS.map((id) => [id, new SchmiedWarten(undefined, undefined, { arbeitet: () => zusatzWarteblasen.get(id)?.sichtbar() === true, melde: meldeDiagnose })] as const),
);
/** Das Warten dieses Entwicklers. */
function wartenVon(id: EntwicklerId): SchmiedWarten {
  return (id === "schmied" ? schmiedWarten : zusatzWarten.get(id)) ?? schmiedWarten;
}
/** Alle drei Warten (Schmied, Zimmermann, Hüter) — für Stopp, „Fertig“ und das Ende von Vesperas Antwort. */
function alleWarten(): SchmiedWarten[] {
  return [schmiedWarten, ...zusatzWarten.values()];
}

/**
 * Einen Satz des Schmieds vertonen — über EXAKT denselben Weg wie Vespera (`fetchTtsChunk`: PCM-Strom, Strom oder Datei,
 * je nach denselben Schaltern; derselbe Zwischenspeicher). Nur Stimme und Tempo kommen aus lib/schmied-stimme.ts.
 * ⚠️ Bis 15.10.2026, 22:42 ging er immer als fertige Datei an /api/voice-preview — an PCM- und Strom-Weg vorbei.
 */
function holeSchmiedTon(anfrage: SchmiedTonAnfrage, signal: AbortSignal): Promise<string> {
  return fetchTtsChunk(anfrage.text, anfrage.voiceId, signal, undefined, anfrage);
}

/*
 * 17.10.2026 (Jarvis-Hörprobe, lib/vespera-satz.ts): die Hörprobe der Sofort-Sätze spricht über EXAKT Vesperas Weg
 * (`fetchTtsChunk`, Stimme und Tempo der Konto-Einstellung) und meldet ihre Schlange beim globalen Stopp an.
 */
setzeVesperaTonweg({
  holeTon: (text, signal) => fetchTtsChunk(text, loadSettings().voiceId, signal),
  // Läuft eine lange Spur (Meditation, Musik …: lange-spur.ts), startet die Probe nicht — sie hielte die Spur an.
  // Spricht Vespera gerade (oder wartet eine Sprechtasten-Antwort), ebenso nicht — sie bräche die Antwort ab.
  belegt: () =>
    langeSpurLaeuft()
      ? "lange-spur"
      : sprechtastenQueue !== null || [...activeTtsQueues].some((q) => q.isAudioActive())
        ? "antwort"
        : null,
  melde: (q) => {
    activeTtsQueues.add(q);
    return () => void activeTtsQueues.delete(q);
  },
});

/**
 * Die gewählte Stimme eines anderen Entwicklers (16.10.2026, Seite „Entwickler-Team“: „Stimme des Zimmermanns“) — nur Konto 1 hat eine
 * Wahl, jedes andere Konto hört seine Vorgabe (shared/schmied.ts `entwicklerStimmeFuerKonto`).
 */
function eigeneStimmeJetzt(id: EntwicklerId): string {
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  const feld = entwickler(id).stimmeFeld;
  return entwicklerStimmeFuerKonto(id, (loadSettings() as unknown as Record<string, unknown>)[feld], uid);
}

/** Das gewählte Sprechtempo eines Entwicklers (17.10.2026, Seite „Entwickler-Team“): nur Konto 1 hat eine Wahl, sonst sein Festwert. */
function eigenesTempoJetzt(id: EntwicklerId): number {
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  return entwicklerTempoFuerKonto(id, (loadSettings() as unknown as Record<string, unknown>)[entwickler(id).tempoFeld], uid);
}

/**
 * Stimme und Tempo eines Entwicklers, wie sie gerade eingestellt sind — die EINE Auflösung für den Knopf an seiner Blase UND das
 * automatische Vorlesen (`bereiteSchmiedVor`). Schmied, Zimmermann, Hüter: je ihre eigene Stimme; nur Konto 1 hat eine Wahl.
 */
function entwicklerStimmeJetzt(id: EntwicklerId): EigeneStimme {
  return entwicklerTtsAnfrage(id, "", loadSettings().entwicklerStimme, eigeneStimmeJetzt(id), eigenesTempoJetzt(id));
}

/**
 * Wartet, bis keine Stimme mehr klingt (Vespera, Sprechtaste) UND Martin nicht aufnimmt (16.10.2026: der Schmied sprach in
 * seine Aufnahme hinein; `pttState` allein sagt es nicht — `naechsteAufnahmeLaeuft` fragt Start-Versprechen und Rekorder).
 * `false` nach der Frist — der Beitrag bleibt dann Text und bekommt „nicht vorgelesen“. Die Tagesaufnahme zählt nicht.
 */
function warteAufStille(maxMs: number): Promise<boolean> {
  // 28.10.2026 (REIHENFOLGE): höchstens bis zur Wartegrenze der Reihe (lib/vorlese-reihe.ts `restMs`), danach Blase.
  // `null` (Schalter aus, Frage an Martin — Prüfbefund M1): nur `maxMs`.
  const rest = vorleseReihe.restMs();
  if (rest !== null) maxMs = Math.max(0, Math.min(maxMs, rest));
  return schmiedWarteAufStille(
    () =>
      schmiedStilleBesetzt({
        stimmeKlingt: ttsWiedergabeOffen(),
        sprechtasteSpricht: sprechtastenQueue !== null,
        aufnahme: pttState === "recording" || naechsteAufnahmeLaeuft(),
      }),
    maxMs,
  );
}

/** Martin nimmt gerade auf (Sprechtaste/Auto-Mikro, auch im Anlauf)? */
function aufnahmeBelegt(): boolean {
  return pttState === "recording" || naechsteAufnahmeLaeuft();
}

/*
 * VORWÄRMEN WIE BEI VESPERA (Martin, 22:42: „die Vorbereitung dauert deutlich länger als bei Vespera“).
 *
 * Gemessen im Journal (22:36–22:37): die Vertonung selbst ist gleich schnell (Vespera `[tts-t] seg=0 synth=1467–3397ms`,
 * der Schmied `synth=2283ms`, beide über denselben Server-Weg). Der Unterschied war die REIHENFOLGE: Vesperas Sätze werden
 * vertont, während ihr Text noch kommt; der Schmied fing erst NACH dem Warten auf Stille an zu vertonen — Beitrag 22:36:57,
 * Stille 22:37:04, erster Ton 22:37:06. Darum jetzt wie bei ihr: seine Schlange entsteht, sobald der Beitrag da ist, und
 * vertont sofort (angehalten, `pause`); in die Stille hinein wird sie nur noch freigegeben (`resume`). Sie zählt bis dahin
 * NICHT als klingende Stimme (nicht in `activeTtsQueues`), sonst wartete sie auf sich selbst.
 */
/*
 * 16.10.2026 (Martin, 17:18: „in einem Stück“): `ganz` — die Schlange vertont den GANZEN Beitrag, bevor sie spricht
 * (lib/tts-queue.ts `ganzVorab`, höchstens SCHMIED_GANZ_MAX_MS, dann stückweise). Der Server schaltet es mit dem Kontoschalter „Schmied-Stimme im Ganz-Modus“ (Vorgabe aus).
 */
function bereiteSchmiedVor(text: string, ganz: boolean, kennung?: string, id: EntwicklerId = "schmied"): TtsQueue | null {
  const sprechbar = tabellenFuerDieStimme(sprechQuelle(teileAntwort(text)), true).trim();
  if (!sprechbar) return null;
  const queue = schmiedSchlange({
    // 20.10.2026: Stimme und Tempo je Entwickler über dieselbe Auflösung wie der Knopf der Blase (`entwicklerStimmeJetzt`).
    stimme: () => entwicklerStimmeJetzt(id),
    holeTon: holeSchmiedTon,
    direktBeta: () => loadSettings().ttsDirektBeta === true,
    onAudioStart: () => setVoicePrep("playing"),
    ganz,
    kennung,
    id,
  });
  queue.pause();
  queue.push(sprechbar);
  queue.flush();
  schmiedVorbereitet.add(queue);
  return queue;
}

/**
 * Einen vorgewärmten Beitrag mit der Stimme des Schmieds vorlesen. Löst immer auf (fertig, abgebrochen, gescheitert) —
 * `true` nur, wenn er ohne Abbruch gesprochen wurde (Vorlese-Stand der Blase, lib/entwickler-blase.ts); `false`, wenn er
 * verworfen wurde (keine Stille, ausgeschaltet) — Martin hat ihn dann nicht gehört.
 */
async function sprichSchmied(queue: TtsQueue | null, id: EntwicklerId = "schmied", createdAt?: string, ohneWarten = false): Promise<"ja" | "anderswo" | "nein"> {
  if (!queue) return "nein";
  try {
    return await sprichSchmiedVorbereitet(queue, id, createdAt, ohneWarten);
  } finally {
    schmiedVorbereitet.delete(queue);
  }
}

async function sprichSchmiedVorbereitet(queue: TtsQueue, id: EntwicklerId = "schmied", createdAt?: string, ohneWarten = false): Promise<"ja" | "anderswo" | "nein"> {
  // 17.10.2026: „Fertig“ hat ihn verworfen (verwirfSchmiedVorbereitet) — keine Pille, kein Ton.
  if (!schmiedVorbereitet.has(queue)) {
    meldeDiagnose("[schmied] vorlesen verworfen fertig");
    return "nein";
  }
  // 19.10.2026 (Martin: „wenn ich mit Zimmermann kommuniziere, wird nie was vorgelesen“): alle Entwickler warten gleich — auf die Stimmen
  // UND auf Martins Aufnahme. Sie sprechen nie in sie hinein, werden aber nicht verworfen: ist die Aufnahme zu Ende, kommt der Beitrag dran
  // (solange er frisch genug ist, s. u.). Die Reihe `vorleseReihe` (lib/vorlese-reihe.ts) hält die Reihenfolge: einer nach dem anderen, nie gleichzeitig.
  const stilleOk = await warteAufStille(SCHMIED_STILLE_MAX_MS);
  if (!stilleOk) {
    queue.cancel("schmied-keine-stille");
    meldeDiagnose(`[schmied] vorlesen übersprungen keine-stille von=${id}`);
    return "nein";
  }
  // Letzte Prüfung vor dem Sprechen: nimmt Martin genau jetzt wieder auf, darf nichts klingen (kein Verwerfen, die Schleife oben wartet nicht
  // erneut — darum hier abbrechen statt in die Aufnahme zu sprechen; der Beitrag bleibt dann Text).
  if (aufnahmeBelegt()) {
    queue.cancel("zusatz-aufnahme");
    meldeDiagnose(`[schmied] vorlesen übersprungen aufnahme von=${id}`);
    return "nein";
  }
  // Während des Wartens auf Stille kam „Fertig“.
  if (!schmiedVorbereitet.has(queue)) {
    meldeDiagnose("[schmied] vorlesen verworfen fertig");
    return "nein";
  }
  // 17.10.2026: inzwischen läuft eine lange Spur? Dann bleibt er still (nur Blase).
  if (langeSpurLaeuft()) {
    queue.cancel("lange-spur");
    meldeDiagnose("[lange-spur] schmied vorlesen verworfen");
    return "nein";
  }
  // Inzwischen ausgeschaltet? Dann still.
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  const gilt =
    id === "schmied"
      ? schmiedVorlesenGilt({ wert: loadSettings().entwicklerVorlesen, uid, lautsprecher: autoPlayEnabled, jetzt: Date.now() })
      : zusatzVorlesenGilt({ imGespraech: true, wert: loadSettings().zusatzVorlesen, uid, lautsprecher: autoPlayEnabled, createdAt, jetzt: Date.now() });
  if (!gilt) {
    queue.cancel(id !== "schmied" && !schmiedBeitragFrisch(createdAt, Date.now()) ? "zusatz-zu-alt" : "schmied-aus");
    return "nein";
  }
  const abort = new AbortController();
  currentTtsAbort = abort;
  currentTtsQueue = queue;
  // Prüfer-Auflage 2: die Knopf-Wiedergabe davor ist verklungen (Stille), ihr `finally` lief aber evtl. noch nicht — ihr Knopf darf
  // jetzt nicht mehr als „spricht“ gelten (sonst wirkte „Fertig“ auf diesen Beitrag nur wie ein Knopf-Stopp).
  if (currentAudioBtn) {
    currentAudioBtn.dataset.state = "idle";
    currentAudioBtn = null;
  }
  setVoicePrep("prep");
  queue.beiWartenAufStimme = (w): void => pilleWartetAufStimme(queue, w);
  /*
   * 17.10.2026 (seiten-abgleich.ts „Geparkt“): diese Seite ist nicht vorn, der Beitrag wartet, ob sie es wird. Keine Pille
   * „wird vorbereitet“. Das Warten des Mikros auf den Schmied bleibt stehen (es öffnet während des Zurückhaltens nie: das
   * Vorlesen ist offen) — es endet ZU nur, wenn der Beitrag hier nicht mehr gesprochen wird (`nachZurueckhalten`). Wird die
   * Seite vorn und spricht ihn, öffnet es danach wie immer.
   */
  queue.beiGeparkt = (geparkt, ergebnis): void => {
    if (currentTtsQueue === queue) setVoicePrep(geparkt ? "hidden" : "prep");
    if (!geparkt && !ohneWarten && nachZurueckhalten(wartenVon(id), ergebnis)) meldeDiagnose(`[ptt] schmied-warten zu weil=${ergebnis ?? "-"}`);
  };
  activeTtsQueues.add(queue);
  const onAbortCancel = (): void => queue.cancel("schmied-abbruch");
  abort.signal.addEventListener("abort", onAbortCancel);
  let gesprochen: "ja" | "anderswo" | "nein" = "nein";
  try {
    queue.resume();
    await queue.idle();
    const bilanz = queue.sprechBilanz();
    // „anderswo“: eine andere Seite hat ihn gesprochen (seiten-abgleich.ts „Geparkt“) — gehört, aber nicht hier.
    gesprochen =
      bilanz.abbruch === null && bilanz.gesprochen > 0 ? "ja" : bilanz.abbruch === ANDERSWO_GESPROCHEN ? "anderswo" : "nein";
  } catch (err) {
    if ((err as DOMException)?.name !== "AbortError") console.warn("[schmied] vorlesen gescheitert:", err);
    return "nein";
  } finally {
    meldeSprechBilanz(queue, id);
    abort.signal.removeEventListener("abort", onAbortCancel);
    activeTtsQueues.delete(queue);
    if (currentTtsQueue === queue) {
      currentTtsQueue = null;
      setVoicePrep("hidden");
    }
    if (currentTtsAbort === abort) currentTtsAbort = null;
  }
  return gesprochen;
}

/**
 * Ein Beitrag des Schmieds kam live. Liest ihn vor, wenn „Entwickler vorlesen“ an ist, und sagt dem wartenden Mikro
 * Bescheid (a: Beitrag da — bei Vorlesen erst, wenn er fertig gesprochen hat). Vertont wird sofort (vorgewärmt, oben),
 * gesprochen der Reihe nach.
 */
function schmiedBeitragKam(
  text: string,
  createdAt: string | undefined,
  blase: HTMLElement | null = null,
  weiter = false,
  /** 16.10.2026: erst ganz vertonen, dann sprechen (`schmiedGanzGilt`, Bus-Feld `stueckweise`). */
  ganz = true,
  /** 16.10.2026, 18:19: der Beitrag geht an Vespera (`haltAnVesperaGilt`) — das Mikro bleibt zu, bis sie antwortet (lib/schmied-warten.ts). */
  anVespera = false,
  /** 16.10.2026, 20:01: „nur zur Info“ (`infoAus`) — nach dem Vorlesen öffnet das Mikro nicht und wartet nicht (lib/schmied-warten.ts). */
  info = false,
  /** 17.10.2026: die Kennung des Beitrags (seine Sitzung) — damit ihn bei mehreren Seiten genau eine spricht (seiten-abgleich.ts „Geparkt“). */
  kennung?: string,
  /** 17.10.2026 (Martin 11:15): der Beitrag fragt Martin (`schmiedFragtMartin`) — das Mikro öffnet danach wie bei Vespera (lib/schmied-warten.ts `frageKam`). */
  fragt = false,
  /** 22.10.2026: der Beitrag geht ausdrücklich an Zimmermann/Hüter (`schmiedAnAndereEntwickler`, Feld `an`) — er löst kein Warten auf Martin ein (lib/schmied-warten.ts). */
  anAndere = false,
  /** 28.10.2026: Kurzstand vom Server mit `weiter` (`kurzstandOhneWarten`) — nur Blase und Stimme; das Warten auf die echte Antwort bleibt unberührt (Frist, offene Frage). */
  ohneWarten = false,
): void {
  // 17.10.2026: ein Kopier-Block (shared/kopier-bloecke.ts) wird nicht vorgelesen.
  text = ohneKopierBloecke(text);
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  const langeSpur = langeSpurLaeuft();
  // 17.10.2026 (lib/fertig.ts): kurz nach „Fertig“ im selben Zug kommt nichts mehr laut nach — der Beitrag steht nur als Blase.
  const nachFertig = fertigHaeltSchmied(zugKennung());
  if (nachFertig) meldeDiagnose(`[fertig] schmied-beitrag nur blase seit=${Math.round(fertigSeitMs())}`);
  const vorlesen =
    !langeSpur &&
    !nachFertig &&
    schmiedVorlesenGilt({
      wert: loadSettings().entwicklerVorlesen,
      uid,
      lautsprecher: autoPlayEnabled,
      createdAt,
      jetzt: Date.now(),
    });
  // 17.10.2026: läuft eine lange Spur, erscheint der Beitrag nur als Blase — nicht laut (lange-spur.ts).
  if (langeSpur) meldeDiagnose("[lange-spur] schmied-beitrag nur blase");
  // Halt an Vespera: ein Zug, der schon fertig war, als der Beitrag kam, ist nicht ihre Antwort (nur sein Ende zählt nicht).
  const halt = anVespera ? { alterZug: zugEndeStand().zugFertig ? zugKennung() : null } : null;
  if (ohneWarten) {
    meldeDiagnose(`[ptt] mikro kurzstand ohne-warten vorlesen=${vorlesen ? 1 : 0}`);
    if (!vorlesen) return setzeUnvorgelesen(blase);
    return lieseBeitragVor("schmied", text, ganz, kennung, blase, weiter, info, undefined, true);
  }
  if (info && !weiter) meldeDiagnose(`[ptt] mikro info vorlesen=${vorlesen ? 1 : 0}`);
  else if (halt && !weiter) meldeDiagnose(`[ptt] mikro halt an=vespera vorlesen=${vorlesen ? 1 : 0}`);
  schmiedWarten.beitragKam(vorlesen, zugKennung(), weiter, halt, info, anAndere);
  // Nach „Fertig“ (nur Blase, oben) öffnet auch eine Frage nichts — ebenso keine alte, nachgelieferte (Abriss, Hintergrund).
  if (fragt && !nachFertig && !schmiedBeitragFrisch(createdAt, Date.now())) meldeDiagnose("[ptt] mikro schmied-frage zu grund=alt");
  else if (fragt && !nachFertig) {
    meldeDiagnose(`[ptt] mikro schmied-frage vorlesen=${vorlesen ? 1 : 0} wartet=${schmiedWarten.wartet() ? 1 : 0}`);
    // 17.10.2026 (Sicherheitsprüfung M2): eine Frage des Schmieds ist immer eine Entscheidung — kein 400-ms-Ja, auch nicht nach einer einfachen Vespera-Frage davor.
    mikroEntscheidungOffen = true;
    schmiedWarten.frageKam(vorlesen, schmiedFrageLeseMs(text), schmiedFrageOeffnet(zugKennung(), "schmied", blase));
  }
  if (!vorlesen) return setzeUnvorgelesen(blase);
  lieseBeitragVor("schmied", text, ganz, kennung, blase, weiter, info, undefined, false, fragt && !nachFertig);
}

/**
 * Einen angenommenen Beitrag eines Entwicklers der Reihe nach vorlesen (vorgewärmt, mit SEINER Stimme) und seinem Warten Bescheid sagen.
 * Genau EIN `vorgelesen` je angenommenem Beitrag; `gehoert=false`, wenn er verworfen wurde (Martin hat ihn nicht gehört).
 */
function lieseBeitragVor(
  id: EntwicklerId,
  text: string,
  ganz: boolean,
  kennung: string | undefined,
  blase: HTMLElement | null,
  weiter: boolean,
  info: boolean,
  createdAt?: string,
  /** 20.10.2026: Hintergrundbericht ohne Frage an Martin — er öffnet kein Mikro, also sagt sein Ende dem Warten nichts (21.10.: mit Frage zählt er). */
  ohneWarten = false,
  /**
   * 28.10.2026: eine Frage an Martin — sie rückt in der Reihe vor die wartenden Berichte (nie mitten in einen Satz). Vorgabe: wer auf
   * Martins Antwort wartet (`!ohneWarten`, der Hintergrundbericht mit Frage); Schmied und Gespräch sagen es ausdrücklich.
   */
  vorrang = !ohneWarten,
): void {
  let vorbereitet: TtsQueue | null = null;
  try {
    vorbereitet = bereiteSchmiedVor(text, ganz, kennung, id);
  } catch (err) {
    console.warn("[schmied] vorwärmen gescheitert:", err);
  }
  // 16.10.2026: die Blase zeigt, ob ihr Beitrag (noch) vorgelesen wird — nur, wenn es überhaupt etwas zu sprechen gibt.
  // 20.10.2026: eine Reihe für ALLE Sprecher in Ankunftsreihenfolge — nur der vorderste trägt „wird vorgelesen“, die übrigen „unvorgelesen“.
  const stand = vorbereitet && blase ? blase : null;
  vorleseReihe.einreihen({
    blase: stand,
    vorrang,
    sprich: () => sprichSchmied(vorbereitet, id, createdAt, ohneWarten),
    danach: (ergebnis) => {
      // 28.10.2026: die Reihe ließ ihn nicht dran (voll, zu lange gewartet) — der vorgewärmte Ton wird verworfen, er bleibt Blase.
      if (vorbereitet && schmiedVorbereitet.delete(vorbereitet)) vorbereitet.cancel("reihe-grenze");
      if (!ohneWarten) wartenVon(id).vorgelesen(ergebnis === "ja", weiter, info);
    },
  });
}

/**
 * Ein Beitrag von Zimmermann oder Hüter kam live (19.10.2026). Nur im GESPRÄCH (`imGespraech`, der Server sagt es am Beitrag: Martins Faden
 * zu ihm ist offen oder Vespera hat ihm übergeben) zählt er: dann liest ihn die App mit seiner eigenen Stimme von selbst vor
 * (`zusatzVorlesenGilt`: eigener Schalter, nicht bei Aufnahme, frisch) und sagt seinem Warten Bescheid — das Mikro öffnet erst danach, bei
 * einer Frage an Martin wie beim Schmied. Ein Bericht aus dem Hintergrund bleibt Text mit Knopf (Ausnahme seit 20.10.: Hüter/Zimmermann ohne `--info` wird vorgelesen); er hält nie ein Mikro, nur Zimmermann öffnet es, wenn er Martin fragt (21.10.).
 */
const hintergrundLetzte = new Map<string, { text: string; at: number }>();
function zusatzBeitragKam(
  id: ZusatzId,
  text: string,
  createdAt: string | undefined,
  blase: HTMLElement | null,
  /** `ziel`: 22.10.2026 — Server-Feld (Bus) „an einen anderen als Martin“; nimmt nur die Frage an Martin weg, öffnet nie etwas. */
  o: { weiter: boolean; info: boolean; ganz: boolean; kennung?: string; imGespraech: boolean; erwartet?: boolean; ziel?: unknown },
): void {
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  if (!o.imGespraech) {
    // 20.10.2026 (Martin): ein Hüter-Bericht aus dem Hintergrund wird vorgelesen, außer `--info`. Ohne Mikro, ohne Warten.
    const hintergrund =
      !langeSpurLaeuft() &&
      !fertigHaeltSchmied(zugKennung()) &&
      hueterHintergrundVorlesenGilt({ id, info: o.info, wert: loadSettings().zusatzVorlesen, uid, lautsprecher: autoPlayEnabled, createdAt, jetzt: Date.now() });
    if (!hintergrund) {
      meldeDiagnose(`[${id}] beitrag nur text (kein gespraech)`);
      setzeUnvorgelesen(blase);
      return;
    }
    // 27.10.2026: Rauschen-Bremse — gleicher Text binnen 60 s oder kurzer Info-Status wird nicht (noch einmal) gesprochen.
    if (hintergrundRauschen({ id, text, info: o.info, letzte: hintergrundLetzte, jetzt: Date.now() })) {
      meldeDiagnose(`[${id}] beitrag nur text (rauschen)`);
      setzeUnvorgelesen(blase);
      return;
    }
    // 21.10.2026 (Martin 07:14): fragt ZIMMERMANNS Bericht Martin, öffnet das Mikro nach seinem Vorlesen wie im Gespräch — sein Warten
    // erfährt Beitrag und Frage (sonst kam es nie davon: `ohneWarten`, keine `[ptt]`-Zeile). Der Hüter und Berichte ohne Frage: wie bisher ohne Mikro.
    const sauber = ohneKopierBloecke(text);
    const fragt =
      hintergrundFrageOeffnetMikro(id) &&
      schmiedBeitragFrisch(createdAt, Date.now()) &&
      schmiedFragtMartin({ text: sauber, frage: null, weiter: o.weiter, info: o.info, an: o.ziel, erwartet: o.erwartet });
    meldeDiagnose(`[${id}] hintergrund vorlesen frage=${fragt ? 1 : 0}`);
    if (fragt) {
      const warten = wartenVon(id);
      warten.beitragKam(true, zugKennung());
      mikroEntscheidungOffen = true;
      warten.frageKam(true, schmiedFrageLeseMs(sauber), schmiedFrageOeffnet(zugKennung(), id, blase));
    }
    lieseBeitragVor(id, sauber, o.ganz, o.kennung, blase, false, o.info, createdAt, !fragt);
    return;
  }
  text = ohneKopierBloecke(text);
  const langeSpur = langeSpurLaeuft();
  const nachFertig = fertigHaeltSchmied(zugKennung());
  // Belegt die Aufnahme gerade das Mikro, wird der Beitrag NICHT verworfen: er wird vorgemerkt und gesprochen, sobald sie zu Ende ist.
  const vorlesen =
    !langeSpur &&
    !nachFertig &&
    zusatzVorlesenGilt({ imGespraech: true, wert: loadSettings().zusatzVorlesen, uid, lautsprecher: autoPlayEnabled, createdAt, jetzt: Date.now() });
  if (vorlesen && aufnahmeBelegt()) meldeDiagnose(`[${id}] vorlesen vorgemerkt bis aufnahme zu ende`);
  const warten = wartenVon(id);
  // 22.10.2026 (Martin 10:26): seine Frage an einen anderen Entwickler (Server-Feld `ziel`) löst das Warten auf ihn nicht ein — kein Mikro.
  warten.beitragKam(vorlesen, zugKennung(), o.weiter, null, o.info, zielOhneMartin(o.ziel) !== null);
  // Fragt er Martin (Text endet mit Frage) und nichts wartete: das Mikro öffnet nach dem Vorlesen wie beim Schmied (nie nach „Fertig“, nie bei altem Beitrag).
  const fragtMartin =
    !nachFertig && schmiedBeitragFrisch(createdAt, Date.now()) && schmiedFragtMartin({ text, frage: null, weiter: o.weiter, info: o.info, an: o.ziel, erwartet: o.erwartet });
  if (fragtMartin) {
    mikroEntscheidungOffen = true;
    warten.frageKam(vorlesen, schmiedFrageLeseMs(text), schmiedFrageOeffnet(zugKennung(), id, blase));
  }
  if (!vorlesen) return setzeUnvorgelesen(blase);
  lieseBeitragVor(id, text, o.ganz, o.kennung, blase, o.weiter, o.info, createdAt, false, fragtMartin);
}
/**
 * Einen Umschalter auf den Stand bringen.
 *
 * Der Lautsprecher hat vier Schalter an vier Orten (Menue, Eingabe-Pille,
 * Plus-Menue, Fusszeile). Sie unterscheiden sich nur darin, WO ihre
 * Beschriftung steht — der Zustand steht ueberall am selben data-Attribut.
 * Deshalb eine Funktion statt vier fast gleicher Bloecke.
 */
function zeichneUmschalter(
  id: string,
  an: boolean,
  text: { wohin: "title" | "aria-label" | "text" | "stand"; an: string; aus: string },
): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.dataset.active = String(an);
  // Gibt sich der Knopf im Markup als Umschalter aus, sagt er auch seinen
  // Stand — sonst liest die Sprachausgabe einen veralteten Wert vor.
  if (el.hasAttribute("aria-pressed"))
    el.setAttribute("aria-pressed", String(an));
  // Ein Schalter (role="switch", Plus-Menü seit 15.10.2026) sagt ihn über aria-checked.
  if (el.hasAttribute("aria-checked"))
    el.setAttribute("aria-checked", String(an));
  const wort = an ? text.an : text.aus;
  if (text.wohin === "text") el.textContent = wort;
  // „stand“: nur das sichtbare Wort neben dem Schieber („An“/„Aus“), nicht der
  // ganze Knopf — Name und Kurzzeile daneben bleiben stehen.
  else if (text.wohin === "stand") {
    const stand = el.querySelector("[data-stand]");
    if (stand) stand.textContent = wort;
  } else el.setAttribute(text.wohin, wort);
}

function setAutoPlay(enabled: boolean): void {
  autoPlayEnabled = enabled;
  saveAutoPlay(enabled);
  zeichneUmschalter("autoplay-toggle", enabled, {
    wohin: "title",
    an: "Auto-Vorlesen aktiv — klick zum Ausschalten",
    aus: "Auto-Vorlesen aus — klick zum Einschalten",
  });
  // Pre.147: Footer-Toggle synchron halten (siehe index.html #footer-tts).
  zeichneUmschalter("footer-tts", enabled, {
    wohin: "aria-label",
    an: "Auto-Vorlesen ausschalten",
    aus: "Auto-Vorlesen einschalten",
  });
  // 2026-07-22 (Martin, "Fusszeile ueberladen"): der Umschalter zog damals
  // ins Plus-Menue. Er steht dort weiter — seit 15.09. zusaetzlich in der
  // Fusszeile, ohne sie zu verbreitern (siehe #dock-speaker im Markup).
  // 15.10.2026: dort ein echter Schalter im Block „Schnell einstellen“ —
  // Name und Kurzzeile stehen im Markup, hier wechselt nur das Stand-Wort.
  zeichneUmschalter("plus-tts-toggle", enabled, {
    wohin: "stand",
    an: "An",
    aus: "Aus",
  });
  zeichneUmschalter("dock-speaker", enabled, {
    wohin: "title",
    an: "Vorlesen an — tippen schaltet stumm",
    aus: "Vorlesen aus — tippen liest wieder vor",
  });
  // 15.09.2026: der Schalter fuer Getipptes haengt am Hauptschalter — steht
  // der Lautsprecher aus, ist auch dieser Eintrag ohne Wirkung und sagt es.
  zeichneTippenSchalter();
  if (!enabled) stopCurrentTts("vorlesen-aus");
}

/**
 * Die Kurzzeile unter „Getipptes vorlesen“ im Plus-Menü — sie sagt, was der
 * Stand bedeutet, und ohne Lautsprecher, warum der Schalter stumpf ist.
 */
const TIPPEN_SATZ = {
  ohneLautsprecher: "Erst „Antworten vorlesen“ einschalten",
  an: "Auch getippte Fragen bekommen eine laute Antwort",
  aus: "Getippte Fragen bekommen nur eine geschriebene Antwort",
} as const;

/** Den Schalter im Plus-Menue auf den Stand bringen. */
function zeichneTippenSchalter(): void {
  const btn = document.getElementById("plus-tippen-toggle");
  if (!btn) return;
  zeichneUmschalter("plus-tippen-toggle", tippenVorlesen, {
    wohin: "stand",
    an: "An",
    aus: "Aus",
  });
  // Ohne Lautsprecher ist der Eintrag wirkungslos; das soll man sehen.
  btn.toggleAttribute("disabled", !autoPlayEnabled);
  const satz = btn.querySelector("[data-satz]");
  if (satz)
    satz.textContent = !autoPlayEnabled
      ? TIPPEN_SATZ.ohneLautsprecher
      : tippenVorlesen
        ? TIPPEN_SATZ.an
        : TIPPEN_SATZ.aus;
}

function setTippenVorlesen(an: boolean): void {
  tippenVorlesen = an;
  saveTippenVorlesen(an);
  zeichneTippenSchalter();
  // Abschalten heisst: auch das, was gerade laeuft, hoert auf.
  if (!an) stopCurrentTts("getipptes-vorlesen-aus");
}

document.getElementById("autoplay-toggle")?.addEventListener("click", () => {
  setAutoPlay(!autoPlayEnabled);
});
// Pre.147: Footer-Lautsprecher (im Text-Input-Pill) duplizert den Drawer-
// Toggle damit User es findet ohne ins Hamburger-Menü zu klicken.
document.getElementById("footer-tts")?.addEventListener("click", () => {
  setAutoPlay(!autoPlayEnabled);
});
document.getElementById("plus-tts-toggle")?.addEventListener("click", () => {
  setAutoPlay(!autoPlayEnabled);
});
// 15.09.2026 (Kundenwunsch): derselbe Umschalter in Daumen-Reichweite.
document.getElementById("dock-speaker")?.addEventListener("click", () => {
  hapticTap(20); // fuehlbare Quittung wie die uebrigen Fusszeilen-Knoepfe
  setAutoPlay(!autoPlayEnabled);
});
document.getElementById("plus-tippen-toggle")?.addEventListener("click", () => {
  setTippenVorlesen(!tippenVorlesen);
});

/** 16.10.2026 (Dreier-Modus Stufe 1): die Anzeige „An: Vespera | Schmied“ über dem Eingabefeld — gebaut beim Eingabefeld unten. */
let empfaengerAnzeige: { el: HTMLElement; zeichne: () => void } | null = null;

/**
 * „Entwickler im Chat“ im Plus-Menü (15.10.2026, Dreier-Modus). Nur Konto 1 sieht ihn (shared/entwickler-im-chat.ts);
 * der Wert ist ein Kontowert wie „Getipptes vorlesen“ und steht auch in den Einstellungen (Seite „Entwickler-Team“).
 */
/**
 * 20.10.2026: Die Versuche („Erweitert“: Mikro beim Vorlesen offen, Ruckel-Test, Hintergrund-Vorlesen) sieht nur Konto 1.
 * In anderen Konten gelten sie als AUS, auch wenn das Gerät einen Stand gemerkt hat — der Stand selbst bleibt unberührt.
 */
function mikroOffenGilt(): boolean {
  return kontoGruppen().versuche && loadSettings().mikroOffenBeimVorlesen === true;
}

/** Das Konto ist bekannt oder hat gewechselt: Ruckel-Test und „Mikro offen beim Vorlesen“ nachführen. */
function kontoSperrenNachfuehren(): void {
  ruckelKontoNachfuehren();
  void mikroBeimVorlesenAnwenden(mikroOffenGilt());
}

function zeichneEntwicklerSchalter(): void {
  const btn = document.getElementById("plus-entwickler-toggle");
  if (!btn) return;
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  btn.hidden = !entwicklerImChatFuerKonto(uid);
  zeichneUmschalter("plus-entwickler-toggle", entwicklerImChatAn(loadSettings().entwicklerImChat, uid), {
    wohin: "stand",
    an: "An",
    aus: "Aus",
  });
  // Die Empfänger-Wahl folgt dem Schalter (und dem Konto): nur bei „Entwickler im Chat“ zu sehen.
  empfaengerAnzeige?.zeichne();
}
document.getElementById("plus-entwickler-toggle")?.addEventListener("click", () => {
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  if (!entwicklerImChatFuerKonto(uid)) return;
  const jetzt = loadSettings();
  saveSettings({ ...jetzt, entwicklerImChat: !entwicklerImChatAn(jetzt.entwicklerImChat, uid) });
  zeichneEntwicklerSchalter();
});
// Pre.147: Initial-State der Toggle-Buttons mit dem persistierten
// autoPlayEnabled synchronisieren (sonst hängt Drawer/Footer-Btn auf
// data-active="false" obwohl localStorage "1" sagt).
setAutoPlay(autoPlayEnabled);
/*
 * 16.10.2026 (Einstellungen neu ordnen, Entscheidung 1 — Martin, 20:16): „Entwickler vorlesen“ steht nicht mehr im Plus-Menü, nur
 * noch auf der Seite „Entwickler-Team“ (frontend/lib/settings-view.ts). Was der Knopf im Plus-Menü zusätzlich tat, bleibt: geht der
 * Schalter aus — gleich wo —, hört auch der Schmied auf, der gerade spricht.
 */
// Prüfauflage 16.10.: EIN Hörer — zeichnet das Plus-Menü bei jeder Änderung neu und stoppt nur beim Wechsel an → aus.
const entwicklerHoerer = entwicklerEinstellungsHoerer({
  vorlesenAn: () => entwicklerVorlesenAn(loadSettings().entwicklerVorlesen, (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid),
  zeichne: zeichneEntwicklerSchalter,
  stoppeSchmied: () => {
    if (currentTtsQueue?.sprecher === "schmied") stopCurrentTts("entwickler-vorlesen-aus");
  },
});
window.addEventListener("voice-v8:settings-changed", entwicklerHoerer.hoere);
// 17.10.2026: die Wahl „Zimmermann“ / „Hüter“ erscheint und verschwindet mit seinem Kontoschalter (Seite „Entwickler-Team“).
window.addEventListener("voice-v8:settings-changed", () => empfaengerAnzeige?.zeichne());

// 2026-07-01 (Martin): flaechiges Scrollen — das Mausrad scrollt den
// Chat-Verlauf auch, wenn der Zeiger AUSSERHALB der schmalen Textspalte
// steht (Seitenraender). Nur wenn unter dem Zeiger nichts anderes
// scrollbar ist und der Chat sichtbar ist.
// 2026-07-02 (Martin): EINE einheitliche Scroll-Mechanik fuer ALLE Seiten.
// Steht der Zeiger ueber einem Element, das SELBST scrollt (Liste, Sheet,
// Editor), bleibt alles nativ. Sonst (nackte Raender neben dem Inhalt) wird
// das Rad an den Hauptscroller der AKTIVEN Ansicht weitergereicht — wie im
// Chat, auf jeder Seite gleich. Instant, weil .chat scroll-behavior:smooth
// hat (sonst wuergen sich die Rad-Ticks ab -> "ruckelt"). deltaMode 1 =
// Zeilen (Firefox) -> in px.
let _wheelScrollerKey = "";
let _wheelScroller: HTMLElement | null = null;
function activeViewScroller(): HTMLElement | null {
  // 2026-07-21 (Martin, "kann schon wieder nichts scrollen" — Regression):
  // Vollseiten-Overlays (Tiefe Fragen, Material, HD, Essenz, ...) liegen
  // UEBER den .view-Ansichten — das Rad muss an DEREN Scroller gehen,
  // sonst scrollte es den verdeckten Chat (oder nichts).
  const overlay = Array.from(
    document.querySelectorAll<HTMLElement>(
      ".page-sheet, " +
        ".project-sheet-overlay--full",
    ),
    // fixed-Elemente haben offsetParent null — Sichtbarkeit deshalb
    // ueber getClientRects (leer = display:none).
  ).find((o) => !o.hidden && o.getClientRects().length > 0);
  if (overlay) {
    const key = "overlay:" + (overlay.id || overlay.className);
    if (key === _wheelScrollerKey && _wheelScroller?.isConnected)
      return _wheelScroller;
    let best: HTMLElement | null = null;
    let bestH = 0;
    const consider = (el: HTMLElement): void => {
      const st = getComputedStyle(el);
      if (!/(auto|scroll)/.test(st.overflowY)) return;
      if (el.scrollHeight <= el.clientHeight + 4) return;
      if (el.clientHeight > bestH) {
        bestH = el.clientHeight;
        best = el;
      }
    };
    consider(overlay);
    overlay.querySelectorAll<HTMLElement>("*").forEach(consider);
    _wheelScrollerKey = key;
    _wheelScroller = best;
    return best;
  }
  const view = Array.from(
    document.querySelectorAll<HTMLElement>(".view"),
  ).find((v) => !v.hidden && v.offsetParent !== null);
  if (!view) return null;
  if (view.id === "view-chat") return refs.chat;
  // Scroller-Suche nur bei Ansichts-Wechsel (Cache je View-Id).
  if (view.id === _wheelScrollerKey && _wheelScroller?.isConnected)
    return _wheelScroller;
  let best: HTMLElement | null = null;
  let bestH = 0;
  const consider = (el: HTMLElement): void => {
    const st = getComputedStyle(el);
    if (!/(auto|scroll)/.test(st.overflowY)) return;
    if (el.scrollHeight <= el.clientHeight + 4) return;
    if (el.clientHeight > bestH) {
      bestH = el.clientHeight;
      best = el;
    }
  };
  consider(view);
  view.querySelectorAll<HTMLElement>("*").forEach(consider);
  _wheelScrollerKey = view.id;
  _wheelScroller = best;
  return best;
}
document.addEventListener(
  "wheel",
  gesteuert("wachen", (e: WheelEvent) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    // Ueber einem eigenen Scroller? -> nativ lassen.
    let el: HTMLElement | null = t;
    while (el && el !== document.body) {
      const st = getComputedStyle(el);
      if (
        /(auto|scroll)/.test(st.overflowY) &&
        el.scrollHeight > el.clientHeight + 4
      )
        return;
      el = el.parentElement;
    }
    const scroller = activeViewScroller();
    if (!scroller || scroller.offsetParent === null) return;
    const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    scroller.scrollBy({ top: dy, behavior: "instant" as ScrollBehavior });
  }),
  { passive: true },
);

// --- Screen-Wake-Lock (pre.151) --------------------------------------
// Verhindert dass das Display abdunkelt während die App offen ist —
// Standard bei Voice/Chat-Systemen (WhatsApp Web, Telegram). Browser
// release't den Lock automatisch wenn der Tab in den Background geht;
// auf visibilitychange (zurück sichtbar) re-acquiren wir.
interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: "release", listener: () => void): void;
}
interface NavigatorWakeLock {
  request(type: "screen"): Promise<WakeLockSentinelLike>;
}
// Pre.835: iOS-Display-Wachhalter, Stufe 2 — WakeLock wird von iOS in
// PWAs still beendet (Martins „Bildschirm wird schwarz" trotz Lock).
// Ein unsichtbares Mini-Video (2 KB, schwarz, loop) hält das Display
// zuverlässig an, solange der Anruf läuft. Start NUR in der User-Geste
// (Anruf-Klick), sonst blockt iOS das play().
let noSleepVideo: HTMLVideoElement | null = null;
function startNoSleep(): void {
  // Ruckel-Test (Ticket 2912), Schalter „Display-Wachhalter aus (Video)“. Läuft die Tagesaufnahme,
  // bleibt das Video an: sonst legt WebKit die Audio-Sitzung still und der Recorder bricht ab (siehe unten).
  // 21.10.2026: in der App nie (ausser Tagesaufnahme) — jedes play() riss dem Mikro-Motor und Vesperas Stimme die
  // Tonsitzung weg, im 30-s-Takt des Wächters unten (ruckel-schalter.ts `wachhalteVideoErlaubt`).
  if (!wachhalteVideoErlaubt(isAlltagRecorderLive(), isNativePlatform())) {
    stopNoSleep();
    return;
  }
  if (!noSleepVideo) {
    const v = document.createElement("video");
    v.src = "/nosleep.mp4";
    v.loop = true;
    v.muted = true;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.setAttribute("aria-hidden", "true");
    v.className = "nosleep-video";
    document.body.appendChild(v);
    noSleepVideo = v;
  }
  // Pre.872 (#151): nur neu anwerfen wenn (noch) pausiert — iOS pausiert
  // das Video beim Hintergrund/Bildschirm-Sperre still; auf Foreground-
  // Return spielen wir es wieder ab (siehe visibilitychange). Bei laufendem
  // Loop kein redundanter play()-Aufruf.
  if (noSleepVideo.paused) {
    void noSleepVideo.play().catch(() => {
      /* kein Drama — WakeLock-Netz läuft weiter */
    });
  }
}
function stopNoSleep(): void {
  noSleepVideo?.pause();
}
beiRuckelAenderung((id, jetztAn) => {
  if (id !== "nosleep") return;
  if (jetztAn) {
    if (!isAlltagRecorderLive()) stopNoSleep();
  } else if (document.visibilityState === "visible") startNoSleep();
});

let wakeLockSentinel: WakeLockSentinelLike | null = null;
// Pre.937 (#151-Fix): Display-Wachhalter NICHT mehr ans Anruf-Leben koppeln.
// Der Nutzer will den Live-Chat auch OHNE aktiven Anruf lesen, ohne dass das
// iPhone-Display abdunkelt. WakeLock-API + NoSleep-Video laufen jetzt, solange
// die App sichtbar ist (Anruf ODER reines Lesen); freigegeben/pausiert wird nur
// beim Hintergrund-Wechsel (visibilitychange → hidden). callIsLive() entfiel —
// war ausschließlich hier genutzt.
async function releaseWakeLock(): Promise<void> {
  const s = wakeLockSentinel;
  wakeLockSentinel = null; // erst null setzen → der release-Listener fasst nicht nach
  try {
    await s?.release();
  } catch {
    /* schon released / Tab weg — egal */
  }
}
async function requestWakeLock(): Promise<void> {
  const wl = (navigator as Navigator & { wakeLock?: NavigatorWakeLock })
    .wakeLock;
  if (!wl?.request) return; // API nicht verfügbar (Safari < 16.4)
  try {
    wakeLockSentinel = await wl.request("screen");
    wakeLockSentinel.addEventListener("release", () => {
      wakeLockSentinel = null;
      // Pre.834: iOS released den Lock still (Route-Wechsel, Last) — bei
      // sichtbarer App sofort nachfassen statt aufs 30s-Netz zu warten.
      if (document.visibilityState === "visible") {
        window.setTimeout(() => {
          if (!wakeLockSentinel) void requestWakeLock();
        }, 1000);
      }
    });
  } catch (err) {
    // Häufig: Tab nicht sichtbar / Permission-Denied. Silent — User merkt
    // es spätestens wenn das Display ausgeht.
    console.warn("[wake-lock] request failed:", err);
  }
}
/**
 * Ticket 744 (2026-07-28, Akku-Auswertung): Das Display wurde wachgehalten,
 * SOLANGE die App sichtbar war — ohne jede Bedingung, im Web ueber die
 * WakeLock-API und nativ ueber isIdleTimerDisabled. Fuer das Lesen im Chat war
 * das gedacht; lag das Handy aber mit offener App da, blieb das Display die
 * ganze Nacht an. Drei Stunden davon leeren einen vollen Akku — und genau
 * dann brach in der Nacht auf den 28.07. die Tages-Aufnahme um 00:26 ab.
 *
 * Jetzt gilt es nur noch, solange wirklich jemand da ist: Jede Beruehrung,
 * jeder Tastendruck und jedes Scrollen verlaengert das Fenster. Nach der
 * Ruhezeit darf das Display schlafen; die erste Beruehrung holt es sofort
 * zurueck. Der Anruf-Pfad und die Wiedergabe setzen ihren Wachhalter
 * weiterhin selbst.
 *
 * 28.09.2026 (Ticket 2754): Zustand und Regel stehen in lib/display-wach.ts —
 * dort auch, warum die Seite der App ihren Stand beim Start und bei jeder
 * Rueckkehr nach vorne sagt.
 */
const displayWach = erzeugeDisplayWach({
  seite: (wach) => {
    if (wach) {
      void requestWakeLock();
      startNoSleep();
    } else {
      void releaseWakeLock();
      // 2026-07-29 (Audit Paket D): Das Freigeben des WakeLock allein genuegte
      // NICHT. Das Display wird von ZWEI Dingen wachgehalten — der WakeLock-API
      // und einem endlos laufenden Video (NoSleep), das iOS als "Wiedergabe"
      // wertet. Wer nur eines abschaltet, hat nichts abgeschaltet.
      stopNoSleep();
    }
  },
  app: (wach) => {
    void import("./lib/native-recorder.js")
      .then((m) => m.setDisplayWach(wach))
      .catch(() => {});
  },
});

if (typeof window !== "undefined") {
  for (const ev of ["pointerdown", "keydown", "wheel", "touchstart"]) {
    window.addEventListener(ev, () => displayWach.beruehrt(), {
      capture: true,
      passive: true,
    });
  }
  window.setInterval(() => {
    if (document.visibilityState !== "visible") return;
    /*
     * ⚠️ 05.09.2026 — Wiedergabe zählt als Tätigkeit.
     *
     * Martin: *„Display always an ist plötzlich nicht mehr. Wenn irgendeine
     * Seite bei Vespera offen ist, beginnt das Display zu schlummern und
     * ich muss mich neu einwählen."*
     *
     * Die Regel von Ticket 744 zählte NUR Berührungen. Der native Kommentar
     * versprach „während Lesen, Wiedergabe und Anruf bleibt es wach" — die
     * Wiedergabe hat aber nie gezählt. Wer den Spiegel hörte, ohne zu
     * tippen, wurde nach fünf Minuten ausgesperrt. Aufgefallen ist es erst
     * heute, weil der Player bis gestern ständig abbrach und Martin
     * deshalb dauernd tippte.
     *
     * Der Leitstand weiß, ob etwas klingt — dieselbe Wahrheit wie für die
     * Leiste und die Rekorder-Schonung. Eine dritte Rechnung wäre der
     * nächste Widerspruch.
     *
     * ⚠️ NUR Wiedergabe, NICHT die Tagesaufnahme: Die läuft stundenlang, und
     *    genau ihretwegen gibt es die Ruheregel (Akku über Nacht, #744).
     */
    // 30.10.2026: auch eine laufende Sprechtasten-Aufnahme ist Tätigkeit (sonst schläft das Display mitten im Diktat ein).
    displayWach.takt(aktuellerTon()?.klingt === true, pttRecorder.isRecording());
  }, 30_000);
}

void requestWakeLock();
// 2026-07-01 (Martin): Liegengebliebene/abgewuergte Aufnahmen beim App-Start
// UND bei jedem Vordergrund-Wechsel hochladen + abschliessen (resumeAll im
// nativen Plugin). Vorher wurde flushRecorder() NIE aufgerufen → mitten in der
// Aufnahme abgebrochene Sessions blieben auf dem Geraet liegen und gingen
// verloren. Web/PWA = No-op (kein nativer Recorder).
const flushPendingRecordings = (): void => {
  void import("./lib/native-recorder.js")
    .then((m) => {
      // 2026-09-01: Nach WebView-Reload weiss JS nichts von der laufenden
      // nativen Aufnahme. Ohne Abgleich waere dayRecorderBusy false und
      // das PTT-Warm-Mikro wuerde das Mikro stehlen.
      void m.syncFromNative();
      void m.flushRecorder();
    })
    .catch(() => {});
};
flushPendingRecordings();
// 2026-07-23 (Martin, "ich habe nie auf Pause gedrueckt"): Pause-Wache —
// "Immer aufzeichnen" AN + pausiert ohne aktiven Grund heilt sich selbst.
void import("./lib/native-recorder.js")
  .then((m) => m.startDayRecorderPauseWatch())
  /*
   * 06.09.2026 — die Absicht „durchgehend aufnehmen" braucht eine Wache,
   * nicht nur die Pause. Martin: „Danach sollte es automatisch weiterlaufen,
   * weil ich Daueraufnahme eingestellt habe." Begruendung in
   * lib/aufnahme-wache.ts.
   */
  .then(() =>
    import("./lib/aufnahme-wache.js").then((w) => w.starteAufnahmeWache()),
  )
  .catch(() => {});
// 2026-07-06/2026-07-18 (Martin): Die Alltag-Zahl von HEUTE fuers Widget kam
// hier UND in refreshAlltagWidget (unten) — zwei Abfragen, die sich gegenseitig
// ueberschrieben (einmal Aufnahmen, einmal Eintraege). Jetzt schreibt nur noch
// refreshAlltagWidget() das Widget, ebenfalls beim App-Start.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    if (!wakeLockSentinel) void requestWakeLock();
    // Ticket 2754: Wer die App nach vorne holt, ist da — das zählt wie eine
    // Berührung, und die App bekommt den Stand der Seite neu gesagt.
    displayWach.vorne();
    flushPendingRecordings();
    // 2026-07-09 (Martin, "Widget nicht in Theme-Farbe"): bei jedem Vordergrund
    // die aktuellen Palette-Farben ans Widget schieben (koennte sich geaendert
    // haben). Farben-only Push; Web/PWA ohne Plugin = stiller No-op.
    void import("./lib/native-recorder.js")
      .then((m) => m.pushAlltagWidgetInfo({}))
      .catch(() => {});
    // Pre.937 (#151-Fix): Wachhalter-Video bei Rückkehr in den Vordergrund
    // IMMER wieder anwerfen (Anruf ODER reines Lesen) — iOS pausiert es im
    // Hintergrund/bei Bildschirm-Sperre still. Vorher nur bei aktivem Anruf,
    // darum dunkelte das Display beim Lesen ab und der Nutzer musste tippen.
    startNoSleep();
  } else {
    // Im Hintergrund Display-Waechter ruhen lassen (Akku; iOS taete das
    // WakeLock ohnehin). Das NoSleep-Video aber NICHT stoppen, solange
    // die Alltag-Aufnahme laeuft: WebKit deaktiviert sonst die
    // AVAudioSession, der native Recorder bekommt interruption .began
    // und geht in weiche Pause — genau Martins fehlende 30 Minuten.
    void import("./lib/native-recorder.js")
      .then((m) => {
        if (!m.isAlltagRecorderLive()) stopNoSleep();
      })
      .catch(() => {
        stopNoSleep();
      });
    void releaseWakeLock();
  }
  // Pre.380: App-State ans Backend pingen — Server entscheidet bei
  // Bot-Antwort ob Push fällig ist (App im Background) oder nicht.
  void reportAppState();
});

// Pre.394: WakeLock-Verstärkung. User-Bug 2026-05-19: Display dunkelt
// trotz pre.151 nach paar Sekunden ab. Mögliche Ursachen:
//  - Sentinel wird vom Browser still released (passiert auf iOS unter
//    Last oder beim Page-Layout-Switch)
//  - WakeLock-API gar nicht supported (alte Safari) → Fallback fehlt
// Wir versuchen alle 30s reactivieren wenn Sentinel weg, plus bei
// jeder User-Interaktion (click/touchstart) als Failsafe.
setInterval(() => {
  // 2026-07-29 (Audit Paket D): DIESER Waechter hat den Ruhe-Fix von gestern
  // wirkungslos gemacht. Er prueft nur, ob die App sichtbar ist, und holt den
  // Wachhalter dann zurueck — den Ruhe-Zustand kannte er nicht. Nach fuenf
  // Minuten Stille wurde also freigegeben und spaetestens 30 Sekunden spaeter
  // wieder angefordert. Das Display brannte weiter, und ich habe den Fix
  // trotzdem als erledigt gemeldet.
  if (!displayWach.wach) return;
  if (document.visibilityState === "visible") {
    if (!wakeLockSentinel) void requestWakeLock();
    // 2026-07-11 (Martin, "Display schaltet sich wieder aus"): das NoSleep-
    // Video pausiert STILL bei Audio-Session-Wechseln (Recorder-Stopps, TTS,
    // nativer Session-Pin 09.07.) — bisher fasste nur eine Geste oder ein
    // Sichtbarkeits-Wechsel nach. Der 30s-Waechter wirft es jetzt selbst
    // wieder an (startNoSleep ist No-op, wenn es laeuft).
    startNoSleep();
  }
}, 30_000);
["pointerdown", "touchstart", "keydown"].forEach((evt) => {
  window.addEventListener(
    evt,
    () => {
      // Eine echte Beruehrung HOLT die Wachheit zurueck (displayWach.beruehrt()
      // setzt sie wieder auf wach) — die Reihenfolge der Zuhoerer ist
      // dabei egal, weil beide dasselbe Fenster verlaengern.
      if (document.visibilityState === "visible") {
        if (!wakeLockSentinel) void requestWakeLock();
        // Pre.937 (#151-Fix): das NoSleep-Video in einer ECHTEN User-Geste
        // anwerfen — iOS erlaubt den ersten play() nur so. Beim reinen Lesen
        // (kein Connect-Klick) ist das der einzige Gesten-Anker; renderDocks
        // play() beim Cold-Load lehnt iOS sonst still ab. startNoSleep() ist
        // mehrfach-aufruf-sicher (No-op, wenn das Video schon läuft).
        startNoSleep();
      }
    },
    { passive: true },
  );
});

// Pre.380: Visibility-Heartbeat. Frontend pingt App-State an /api/app-state.
// Server hält das im RAM (in-memory Map); bei Bot-Antwort prüft chat.ts ob
// User gerade im Background ist → sendet WebPush statt nur In-App-Bubble.
/**
 * 2026-09-14 (Ticket 2246) — WER meldet, nicht nur WAS.
 *
 * Bis hierher trug die Meldung nur `hidden`. Der Server hielt einen
 * einzigen Zustand je Nutzer — also gewann immer das Geraet, das zuletzt
 * gefunkt hatte. Mit Vespera auf Telefon UND Rechner ueberschrieb der
 * 50-Sekunden-Herzschlag des Hintergrund-Geraets fortlaufend das Geraet,
 * auf das Martin gerade schaute. Ergebnis: 98 gezaehlte "Sitzungen" in 75
 * Minuten bei 14 echten Sichtbarkeitswechseln — und Push-Nachrichten,
 * obwohl er danebensass.
 *
 * Diese Kennung lebt genau so lange wie diese Seite. Zwei Tabs, zwei
 * Geraete, zwei Kennungen; nach einem Neuladen eine neue (der alte
 * Eintrag faellt serverseitig nach 60 s heraus).
 */
const clientId = ((): string => {
  try {
    return crypto.randomUUID();
  } catch {
    // Aeltere WebViews ohne randomUUID — eine schwaechere, aber ausreichend
    // eindeutige Kennung. Sie muss nur zwei offene Seiten trennen.
    return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
})();

async function reportAppState(): Promise<void> {
  try {
    await fetch("/api/app-state", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        hidden: document.visibilityState !== "visible",
        clientId,
      }),
      keepalive: true,
    });
  } catch {
    /* fail-quiet — Push ist nice-to-have */
  }
}
/**
 * 2026-07-31 (Martin: "mein Akku ist immer extrem schnell leer"):
 *
 * Hier stand ein starrer 30-Sekunden-Takt — gemessen 243 Anfragen pro
 * Stunde, nur um dem Server zu sagen "App ist sichtbar" oder "App ist
 * versteckt". Diese Angabe aendert sich ein paar Dutzend Mal am Tag, nicht
 * hundertmal pro Stunde.
 *
 * Jetzt: Bei jeder ECHTEN Aenderung sofort melden (das Ereignis dafuer gibt
 * es ohnehin), dazu ein langsamer Herzschlag von 50 Sekunden als
 * Rueckfallebene. Der Server verwirft einen Eintrag nach 60 Sekunden
 * (STALE_MS) — 50 laesst genug Luft fuer eine verschluckte Anfrage, ohne
 * doppelt so oft zu funken wie noetig.
 */
let letzterGemeldeterZustand: boolean | null = null;
const meldeWennGeaendert = (): void => {
  const versteckt = document.visibilityState !== "visible";
  if (versteckt === letzterGemeldeterZustand) return;
  letzterGemeldeterZustand = versteckt;
  void reportAppState();
};
meldeWennGeaendert();
document.addEventListener("visibilitychange", meldeWennGeaendert);
/*
 * ⚠️ Hier NICHT ganz abschalten. Der Server haelt einen Client nach einer
 *    Weile fuer weg (STALE_MS) und entscheidet danach, ob eine Meldung in
 *    die App oder als Push geht. Im Hintergrund genuegt aber ein Schlag alle
 *    fuenf Minuten statt alle fuenfzig Sekunden — die Tatsache „versteckt"
 *    meldet ohnehin schon der visibilitychange-Hoerer darueber.
 */
starteSichtbarenTakt(() => void reportAppState(), 50_000, {
  auchVersteckt: 5 * 60_000,
  nichtPausieren: true, // Ruckel-Test (Ticket 2912): Meldung an den Server, kein Zeichnen
});
window.addEventListener("pagehide", () => void reportAppState());

// Pre.510: Theme + Chat-Style aus appearance.ts (Import oben in der Datei).
// Sofort beim Bootstrap anwenden — vor allem anderen UI-Code, sonst Flicker.
applyTheme(getTheme());
applyChatStyle(getChatStyle());
// 2026-08-29: Zuhause-Stil ebenfalls SOFORT — sonst baut Zuhause im
// falschen Stil und springt sichtbar um, sobald die Einstellung ankommt.
applyZuhauseStil();
document.getElementById("theme-toggle")?.addEventListener("click", cycleTheme);

/*
 * 23.09.2026, Ticket 2511: Hier stand ein eigener Start-Abgleich für
 * Darstellung und zwei Modellfelder — der ZWEITE neben dem allgemeinen
 * (weiter unten). Er schrieb in den Speicher, während der andere noch
 * wartete; der hielt das für eine Eingabe des Nutzers und schob den alten
 * Gerätestand hoch. Beide sind ersetzt durch EINEN Abgleich nach dem
 * Anmelden (starteAbgleich in bootstrapAuth, lib/einstellungen-abgleich.ts).
 */

// pre.949 (Ticket #184): Datum UND Uhrzeit komplett aus dem Header entfernt —
// jedes Gerät zeigt die Uhrzeit ohnehin in der System-Statusleiste (redundant).
// #topbar-clock + updateTopbarClock sind damit entfallen.

// Pre.508: "Verlauf neu vom Server" — User-getriggerter Hard-Reset.
// Cleart localStorage history + DOM, dann frischer Pull vom Server.
// Wichtig: setzt KEINEN chat-cutoff (anders als clear-chat). Memory,
// Cards, alles bleibt — nur die UI wird vom Server-Stand neu aufgebaut.
/*
 * 22.09.2026 (Martin): *„Wir brauchen auch den Reload in der Sidebar nicht
 * mehr … den drücke ich nie."* Der Knopf ist aus der Symbol-Leiste
 * verschwunden; die FUNKTION bleibt und steht unter Einstellungen → Verlauf.
 * Darum eine benannte Funktion statt eines Handlers am Knopf — EIN Weg,
 * zwei moegliche Ausloeser.
 */
async function verlaufNeuHolen(): Promise<void> {
  try {
    // Drawer schließen für unverstellten Blick
    const drawer = document.querySelector(".nav-drawer");
    drawer?.removeAttribute("open");
    const menuBtn = document.getElementById("nav-menu-btn");
    menuBtn?.setAttribute("aria-expanded", "false");
    setHydrateStatus(true, "Verlauf vom Server neu holen…");
    await resyncHistoryFromServer();
  } catch (err) {
    window.alert(
      `Resync fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}
(window as unknown as { __v8VerlaufNeuHolen?: () => Promise<void> }).__v8VerlaufNeuHolen =
  verlaufNeuHolen;
document
  .getElementById("resync-history-btn")
  ?.addEventListener("click", () => void verlaufNeuHolen());

// 2026-06-30 (Martin): Einstellungen als Zahnrad in der Schnell-Symbol-Leiste
// (statt Listeneintrag unter Mehr). Oeffnet dieselbe Settings-Ansicht.
/*
 * 22.09.2026 (Martin, mit Foto der Symbol-Leiste): *„die beiden Chat- und
 * Home-Buttons … runter verlegen, da wo auch Einstellungen sind. Wenn ich
 * die Bedienung mache, komme ich mit dem Daumen dann sehr schnell auf den
 * Home-Screen beziehungsweise auf den Chat-Screen."*
 *
 * 08.10.2026 (Martin): *„oben, wie gesagt, wieder die beiden Chat- und
 * Zuhause-Buttons einfügen."* Sie stehen seit heute als Knöpfe mit Wort OBEN
 * in der Seitenleiste (`.nav-ort`), nicht mehr als Symbole in der Reihe
 * unten — dieselben Ids, derselbe Weg, nur anderer Ort.
 *
 * ⚠️ Kein eigener Navigations-Weg. Die Reiter in der Liste darunter sind
 *    der EINE Besitzer des Ansichtswechsels (`.nav-item[data-tab]`); diese
 *    Knöpfe tippen sie an. Zwei Stellen, die denselben Zustand setzen, sind
 *    das haeufigste Fehlermuster in diesem Haus — hier gibt es sie nicht.
 */
function reiterAntippen(tab: string): void {
  document
    .querySelector<HTMLElement>(
      `.nav-drawer-list .nav-item[data-tab="${tab}"]`,
    )
    ?.click();
}
// Fokusrahmen nur per Tastatur (08.10.2026) — die Regel steht in style.css.
bindeFokusArt();
// Schubladen der Seitenleiste (22.10.2026): gemerkt je Gerät, „du bist hier“ klappt auf.
{
  const liste = document.querySelector<HTMLElement>(".nav-drawer-list");
  if (liste) bindeNavGruppen(liste);
}
document
  .getElementById("nav-action-zuhause")
  ?.addEventListener("click", () => reiterAntippen("zuhause"));
document
  .getElementById("nav-action-chat")
  ?.addEventListener("click", () => reiterAntippen("chat"));
// Der Umschalter in der Kopfzeile (Telefon): derselbe Weg, dieselbe Ortsmarke.
document
  .getElementById("kopf-ort-zuhause")
  ?.addEventListener("click", () => reiterAntippen("zuhause"));
document
  .getElementById("kopf-ort-chat")
  ?.addEventListener("click", () => reiterAntippen("chat"));

/*
 * Zeigen, wo man steht. ⚠️ Martins Regel vom 27.08.: „NIE ein Knopf, der
 * dorthin fuehrt, wo man schon steht." Ganz ausblenden waere hier falsch —
 * die zwei Knöpfe stuenden mal zu zweit, mal allein da und wuerden bei jedem
 * Wechsel springen. Stattdessen wird der aktuelle Ort ruhig markiert und
 * der Knopf ist nicht mehr anzutippen.
 */
function markiereOrtInSymbolLeiste(): void {
  for (const [id, tab] of [
    ["nav-action-zuhause", "zuhause"],
    ["nav-action-chat", "chat"],
    ["kopf-ort-zuhause", "zuhause"],
    ["kopf-ort-chat", "chat"],
  ] as const) {
    const knopf = document.getElementById(id) as HTMLButtonElement | null;
    if (!knopf) continue;
    const hier =
      document
        .querySelector(`.nav-drawer-list .nav-item[data-tab="${tab}"]`)
        ?.getAttribute("aria-selected") === "true";
    /* ⚠️ Die Markierung, die es im Haus schon gibt — keine zweite
       Schreibweise fuer denselben Zustand (lib/ort-marke.ts, seit 29.09.
       auch fuer den Einstellungs-Knopf der Fusszeile). */
    if (id.startsWith("kopf-ort-")) setzeKopfOrtMarke(knopf, hier, document);
    else setzeOrtMarke(knopf, hier);
  }
}
// Vollseiten hängen sich an <body> und gehen wieder: dann stimmt die Marke des Umschalters neu.
new MutationObserver(() => setTimeout(markiereOrtInSymbolLeiste, 0)).observe(document.body, { childList: true });
window.addEventListener("voice-v8:page-open", () => setTimeout(markiereOrtInSymbolLeiste, 0));
window.addEventListener("voice-v8:nav-changed", () =>
  setTimeout(markiereOrtInSymbolLeiste, 0),
);
window.addEventListener("hashchange", () =>
  setTimeout(markiereOrtInSymbolLeiste, 0),
);
// Jeder Ansichtswechsel (switchToTab) setzt die Reiter zuerst — danach kommt dieses Ereignis.
window.addEventListener("voice-v8:ansicht-gewechselt", markiereOrtInSymbolLeiste);
markiereOrtInSymbolLeiste();

document
  .getElementById("open-settings-action")
  ?.addEventListener("click", () => openEinstellungen());
// 29.09.2026, Ticket 2797: Die Nutzerkarte unten öffnet das Konto-Menü.
// Vorher führte ein Tipp aufs Bild direkt in die Einstellungen — die stehen
// jetzt als erster Punkt im Menü. Abmelden ist DERSELBE Weg wie in den
// Einstellungen (handleLogout, mit Rückfrage), kein zweiter.
{
  const karte = document.getElementById("nav-user");
  if (karte) {
    bindeNutzerMenue(karte, {
      einstellungen: () => openEinstellungen(),
      abmelden: () => void handleLogout(),
    });
  }
}
// Ticket #1926: derselbe Weg aus der Anruf-Fusszeile.
document
  .getElementById("dock-settings")
  ?.addEventListener("click", () => openEinstellungen());

// Pre.211: "Chat leeren" — DB-Cutoff setzen + localStorage clearen + DOM leeren.
/*
 * 22.09.2026 (Martin): *„Ich möchte vermerken, dass der Mistkübel
 * verschwinden darf … ich verwende ihn jetzt schon seit Wochen nicht mehr."*
 *
 * Der Knopf ist aus der Symbol-Leiste weg. Die Funktion bleibt — sie steht
 * unter Einstellungen → Verlauf, und ab sofort nimmt die Zeit sie einem
 * ohnehin ab (Auto-Archiv). Darum eine benannte Funktion statt eines
 * Handlers am Knopf: EIN Weg, mehrere Ausloeser.
 */
/*
 * 02.10.2026 (pre.3603): Der Knopf ist zurück — in der Seitenleiste, als
 * „Verlauf kürzen“. Seit 08.10.2026 (Martin: „neben dem Mond, also da, wo die
 * kleinen Icons sind") ist er ein Besen in der Symbolreihe unten, kein
 * Menüpunkt mehr; Id und diese Funktion sind dieselben. Er behält
 * die letzten VERLAUF_BEHALTEN Nachrichten (je Frage, Antwort oder Meldung eine;
 * Werkzeugkarten gehen mit ihrer Antwort), der Rest wandert ins Archiv. Nichts
 * wird gelöscht. Danach baut sich der Chat ohne Neuladen schlank neu auf.
 */
const VERLAUF_BEHALTEN = 5;
export async function verlaufArchivieren(): Promise<void> {
  // Sidebar zu, sonst liegt sie auf Mobile (z-index 3000) über dem
  // Dialog — „Kürzen" und Abbrechen sind nicht zu treffen.
  closeDrawer();
  // Pre.796: App-Dialog statt Browser-confirm (einheitlich, #54-Muster).
  const { confirmSheet } = await import("./lib/sheet-ui.js");
  const ok = await confirmSheet({
    title: "Verlauf kürzen?",
    message: `Die letzten ${VERLAUF_BEHALTEN} Nachrichten bleiben stehen, alles davor wandert ins Archiv — dort ist es jederzeit wieder da. Erinnerungen, Projekte und Mediathek bleiben unberührt.`,
    confirmLabel: "Kürzen",
  });
  if (!ok) return;
  try {
    const res = await fetch("/api/chat/clear", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ behalte: VERLAUF_BEHALTEN }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const antwort = (await res.json().catch(() => ({}))) as {
      unveraendert?: boolean;
      sichtbar?: number;
    };
    // localStorage history-Cache löschen
    try {
      localStorage.removeItem("voice-v8.chat-history");
    } catch {
      /* ignore */
    }
    // SANFTES LEEREN: nicht alles wegwischen — vom Server neu holen, sodass
    // die letzten Nachrichten als Anschluss sichtbar bleiben (statt leerem
    // Bildschirm + Verwirrung "der letzte ist weg").
    /*
     * VOR dem Leeren: Älteres gibt der Verlauf jetzt nicht mehr her — es liegt hinter dem Stichtag, im Archiv. Ohne
     * diese Zeile löste schon der leere, dann kurze Verlauf (scrollTop < 80) sofort „Lade ältere Nachrichten …“ aus,
     * holte nichts und wischte dabei die Rückmeldung unten weg (Nachbau: nach 7 ms).
     */
    noMoreOlder = true;
    history.length = 0;
    while (refs.chat.firstChild) refs.chat.removeChild(refs.chat.firstChild);
    try {
      await resyncHistoryFromServer();
    } catch {
      /* leerer Zustand ist auch ok */
    }
    // Die Rückmeldung NACH dem Neuaufbau — dessen Pille würde sie sonst überschreiben.
    const n = antwort.sichtbar ?? VERLAUF_BEHALTEN;
    setHydrateStatus(
      true,
      antwort.unveraendert
        ? "Nichts zu kürzen — der Verlauf ist schon so kurz."
        : `Verlauf gekürzt: ${n} ${n === 1 ? "Nachricht bleibt" : "Nachrichten bleiben"}`,
      antwort.unveraendert ? undefined : "Der Rest liegt im Archiv.",
      "fertig",
    );
    setTimeout(() => setHydrateStatus(false), 4000);
  } catch (err) {
    setHydrateStatus(
      true,
      "Kürzen hat nicht geklappt",
      `Der Verlauf ist unverändert (${err instanceof Error ? err.message : String(err)}).`,
      "fertig",
    );
    setTimeout(() => setHydrateStatus(false), 6000);
  }
}
(window as unknown as { __v8VerlaufArchivieren?: () => Promise<void> })
  .__v8VerlaufArchivieren = verlaufArchivieren;
document
  .getElementById("clear-chat-btn")
  ?.addEventListener("click", () => void verlaufArchivieren());

// Pre.544: Archiv — geleerte (ausgeblendete) Chats wieder ansehen/wiederher-
// stellen. Nichts ist gelöscht; „Chat leeren" setzt nur einen Cutoff in der DB.
interface ArchiveMsg {
  role: string;
  content: string;
  createdAt?: string;
  metadata?: {
    imageUrl?: string;
    toolCards?: Array<{
      tool?: unknown;
      callId?: unknown;
      isError?: unknown;
      result?: unknown;
      anzeige?: unknown;
    }>;
  } | null;
}

/**
 * Pre.549: Server-Zeitstempel format-agnostisch parsen. created_at liegt
 * gemischt vor („…T…Z" UND SQLite „YYYY-MM-DD HH:MM:SS", BEIDES UTC). Ohne das
 * angehängte „Z" würde der Space-Form-String als LOKALzeit interpretiert →
 * Bubble-Uhrzeit um den UTC-Offset daneben (Martin: „2 Stunden falsch").
 * Gibt ein Date oder null. Geteilt von Archiv UND Chat-Replay.
 */
function parseServerTs(s?: string): Date | null {
  if (!s) return null;
  let iso = s.includes("T") ? s : s.replace(" ", "T");
  if (!/[zZ]$|[+-]\d\d:?\d\d$/.test(iso)) iso += "Z"; // beide Quellen sind UTC
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

document
  .getElementById("open-archive-btn")
  ?.addEventListener("click", () => void openArchiveSheet());

// pre.856: der ganze Changelog als gerendertes Markdown im Sheet — im Menü
// „Alle Änderungen" (bis 05.10.2026 „Was ist neu?"; den Namen trägt seit
// Ticket 2931 der Knopf am Fuß des Menüs).
document
  .getElementById("open-changelog-btn")
  ?.addEventListener(
    "click",
    () =>
      void import("./lib/changelog-view.js").then((m) =>
        m.openChangelogSheet(),
      ),
  );

async function openArchiveSheet(): Promise<void> {
  // Drawer schließen
  // 2026-07-05 (Martin): der ECHTE Drawer laeuft ueber body.drawer-open —
  // Alt-Mechanismus schloss nichts, Menue blieb ueber dem Inhalt haengen.
  closeDrawerForNavigation();
  document
    .getElementById("nav-menu-btn")
    ?.setAttribute("aria-expanded", "false");

  const existing = document.querySelector(".archive-overlay");
  if (existing) existing.remove();

  // 2026-07-21 (Audit NORM v3): page-sheet-Norm — buildPageHeader,
  // Escape schliesst, kein toter Rand-Klick (Karte deckt das Overlay).
  const overlay = document.createElement("div");
  overlay.className = "archive-overlay page-sheet";
  const sheet = document.createElement("div");
  sheet.className = "archive-sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Chat-Archiv");

  const closeArchive = (): void => {
    overlay.remove();
    document.removeEventListener("keydown", onArchiveKey);
  };
  const onArchiveKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") closeArchive();
  };
  document.addEventListener("keydown", onArchiveKey);
  const pageHead = buildPageHeader("Archiv", closeArchive);
  sheet.appendChild(pageHead);
  // Der Archiv-Titel wechselt mit der Ansicht (Liste/Detail).
  const title = pageHead.querySelector<HTMLElement>(".hd-title")!;

  // Pre.550: Zurück-Button im Kopf — nur in der Detail-Ansicht sichtbar.
  const backBtn = document.createElement("button");
  backBtn.type = "button";
  backBtn.className = "archive-back";
  backBtn.textContent = "Zurück";
  backBtn.classList.add("is-hidden");
  pageHead.insertBefore(backBtn, pageHead.children[1] ?? null);

  // Toolbar — Suche (filtert Unterhaltungen) + „Neue Unterhaltung".
  const toolbar = document.createElement("div");
  toolbar.className = "archive-toolbar";
  const searchInput = document.createElement("input");
  searchInput.type = "search";
  searchInput.className = "archive-search";
  searchInput.placeholder = "Unterhaltungen durchsuchen …";
  toolbar.appendChild(searchInput);
  const newConvBtn = document.createElement("button");
  newConvBtn.type = "button";
  newConvBtn.className = "icm-btn icm-btn--primary archive-newconv";
  newConvBtn.textContent = "Neue Unterhaltung";
  toolbar.appendChild(newConvBtn);
  sheet.appendChild(toolbar);

  const body = document.createElement("div");
  body.className = "archive-body page-body";
  sheet.appendChild(body);

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);

  let hasArchive = false;

  const renderBody = (msgs: ArchiveMsg[], filtered: boolean): void => {
    body.innerHTML = "";
    if (msgs.length === 0) {
      const empty = document.createElement("div");
      empty.className = "archive-empty";
      empty.textContent = !hasArchive
        ? "Kein archivierter Verlauf — du hast den Chat noch nicht geleert."
        : filtered
          ? "Keine Treffer für diese Filter."
          : "Kein archivierter Verlauf.";
      body.appendChild(empty);
      return;
    }
    let lastDay = "";
    for (const m of msgs) {
      const ts = parseServerTs(m.createdAt);
      const day = (m.createdAt ?? "").slice(0, 10);
      if (day && day !== lastDay) {
        lastDay = day;
        const sep = document.createElement("div");
        sep.className = "archive-daysep";
        sep.textContent = ts
          ? ts.toLocaleDateString("de-DE", {
              weekday: "short",
              day: "2-digit",
              month: "long",
              year: "numeric",
            })
          : day;
        body.appendChild(sep);
      }
      const row = document.createElement("div");
      row.className = `archive-msg archive-${m.role === "assistant" ? "bot" : m.role}`;

      // Kopfzeile: Wer + Uhrzeit
      const meta = document.createElement("div");
      meta.className = "archive-msgmeta";
      const who = document.createElement("span");
      who.className = "archive-who";
      who.textContent = m.role === "assistant" ? "Vespera" : "Du";
      meta.appendChild(who);
      if (ts) {
        const time = document.createElement("span");
        time.className = "archive-time";
        time.textContent = ts.toLocaleTimeString("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        });
        meta.appendChild(time);
      }
      row.appendChild(meta);

      if (m.content) {
        const txt = document.createElement("span");
        txt.className = "archive-text";
        txt.textContent = ohneAudioMarke(m.content);
        row.appendChild(txt);
      }

      // Pre.549: Attachments + alles andere mit anzeigen — Bild-Upload und
      // Tool-Karten (Belege, Amazon-Treffer, …) wie im Live-Chat rendern.
      if (m.metadata?.imageUrl) {
        const a = document.createElement("a");
        a.className = "archive-img";
        a.href = m.metadata.imageUrl;
        a.target = "_blank";
        a.rel = "noopener";
        const img = document.createElement("img");
        img.src = m.metadata.imageUrl;
        img.loading = "lazy";
        img.alt = "Bild";
        ruesteBildAus(img, m.metadata.imageUrl);
        a.appendChild(img);
        row.appendChild(a);
      }
      if (Array.isArray(m.metadata?.toolCards)) {
        for (const c of m.metadata.toolCards) {
          try {
            if (isSilentToolCard(String(c.tool ?? ""), c.result)) continue;
            const card = buildToolCard({
              tool: String(c.tool ?? ""),
              callId: String(c.callId ?? ""),
              isError: Boolean(c.isError),
              result: c.result,
              anzeige: c.anzeige,
            });
            card.classList.add("archive-card");
            row.appendChild(card);
          } catch {
            /* eine kaputte Karte überspringen, Rest zeigen */
          }
        }
      }

      body.appendChild(row);
    }
  };

  // --- Detail: eine Unterhaltung anzeigen (Rich-Render) ---
  const showConversation = async (c: {
    startAt: string;
    endAt: string;
    title: string;
  }): Promise<void> => {
    title.textContent = c.title.slice(0, 42);
    backBtn.classList.remove("is-hidden");
    toolbar.classList.add("is-hidden");
    body.replaceChildren(buildLoadingHint()); // Paket 6
    let msgs: ArchiveMsg[] = [];
    try {
      const res = await fetch(
        `/api/chat/conversation?from=${encodeURIComponent(c.startAt)}&to=${encodeURIComponent(c.endAt)}`,
        { credentials: "same-origin" },
      );
      const data = (await res.json()) as { items?: ArchiveMsg[] };
      msgs = Array.isArray(data.items) ? data.items : [];
    } catch (err) {
      body.textContent = `Unterhaltung konnte nicht geladen werden: ${err instanceof Error ? err.message : String(err)}`;
      return;
    }
    hasArchive = true;
    renderBody(msgs, false);
  };

  // Pre.551: KI-Titel werden server-seitig im Hintergrund generiert. Wir
  // halten die Titel-Elemente per startAt und aktualisieren sie in-place
  // (ohne Re-Render / Scroll-Verlust), während sie nachgeneriert werden.
  const titleEls = new Map<string, HTMLElement>();
  let titleTimers: number[] = [];
  const refreshTitles = async (): Promise<void> => {
    if (!document.body.contains(overlay)) return;
    if (!backBtn.classList.contains("is-hidden")) return; // nur in Listen-Ansicht
    try {
      const res = await fetch("/api/chat/conversations", {
        credentials: "same-origin",
      });
      const data = (await res.json()) as {
        conversations?: Array<{ startAt: string; title: string }>;
      };
      for (const c of data.conversations ?? []) {
        const el = titleEls.get(c.startAt);
        if (el && el.textContent !== c.title) el.textContent = c.title;
      }
    } catch {
      /* still fallback titles */
    }
  };

  // --- Liste: alle Unterhaltungen (auto-gruppiert nach Pausen) ---
  const showList = async (): Promise<void> => {
    title.textContent = "Unterhaltungen";
    backBtn.classList.add("is-hidden");
    toolbar.classList.remove("is-hidden");
    body.replaceChildren(buildLoadingHint()); // Paket 6
    titleEls.clear();
    for (const id of titleTimers) window.clearTimeout(id);
    titleTimers = [];
    const q = searchInput.value.trim();
    let convs: Array<{
      startAt: string;
      endAt: string;
      count: number;
      title: string;
    }> = [];
    try {
      const res = await fetch(
        `/api/chat/conversations${q ? `?q=${encodeURIComponent(q)}` : ""}`,
        { credentials: "same-origin" },
      );
      const data = (await res.json()) as {
        conversations?: Array<{
          startAt: string;
          endAt: string;
          count: number;
          title: string;
        }>;
      };
      convs = Array.isArray(data.conversations) ? data.conversations : [];
    } catch (err) {
      body.textContent = `Unterhaltungen konnten nicht geladen werden: ${err instanceof Error ? err.message : String(err)}`;
      return;
    }
    body.innerHTML = "";
    if (convs.length === 0) {
      const empty = document.createElement("div");
      empty.className = "archive-empty";
      empty.textContent = q
        ? "Keine Unterhaltung mit Treffer."
        : "Noch keine Unterhaltungen.";
      body.appendChild(empty);
      return;
    }
    for (const c of convs) {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "archive-conv";
      const t = document.createElement("div");
      t.className = "archive-conv-title";
      t.textContent = c.title;
      card.appendChild(t);
      const meta = document.createElement("div");
      meta.className = "archive-conv-meta";
      const sd = parseServerTs(c.startAt);
      const ed = parseServerTs(c.endAt);
      const dateStr = sd
        ? sd.toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "";
      const timeStr =
        sd && ed
          ? `${sd.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}–${ed.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`
          : "";
      meta.textContent = [dateStr, timeStr, `${c.count} Nachrichten`]
        .filter(Boolean)
        .join(" · ");
      card.appendChild(meta);
      card.addEventListener("click", () => void showConversation(c));
      body.appendChild(card);
      titleEls.set(c.startAt, t);
    }
    // KI-Titel werden im Hintergrund nachgeneriert — mehrfach nachladen und
    // in-place ersetzen (nur in der ungefilterten Liste).
    if (!q) {
      for (const ms of [4000, 11000, 22000, 40000]) {
        titleTimers.push(window.setTimeout(() => void refreshTitles(), ms));
      }
    }
  };

  newConvBtn.addEventListener("click", () => {
    void (async () => {
      const { confirmSheet } = await import("./lib/sheet-ui.js");
      const ok = await confirmSheet({
        title: "Neue Unterhaltung starten?",
        message:
          "Der aktuelle Chat wird abgeschlossen und du beginnst frisch. Du findest ihn jederzeit in der Unterhaltungs-Liste.",
        confirmLabel: "Neu starten",
      });
      if (!ok) return;
      try {
        const res = await fetch("/api/chat/clear", {
          method: "POST",
          credentials: "same-origin",
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        try {
          localStorage.removeItem("voice-v8.chat-history");
        } catch {
          /* ignore */
        }
        overlay.remove();
        window.dispatchEvent(new CustomEvent("voice-v8:hydrate-from-server"));
      } catch (err) {
        window.alert(
          `Neue Unterhaltung fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    })();
  });

  backBtn.addEventListener("click", () => void showList());

  let debounce: number | undefined;
  searchInput.addEventListener("input", () => {
    if (debounce) window.clearTimeout(debounce);
    debounce = window.setTimeout(() => void showList(), 300);
  });

  void showList();
}

/* ======================================================================
 * Pre.554: „Mein Leben" — Lese-Sheet für die wachsende Biografie.
 *
 * Forked vom Archiv-Sheet (gleiche Overlay-Mechanik, Schließen-/Backdrop-
 * Verhalten), aber als ruhige Lese-Ansicht: breitere Lesespalte (~720px),
 * Serifen-Typografie, „Aktualisieren"-Button der das Buch via Opus neu
 * komponiert (langläufig, bis ~90s).
 * ==================================================================== */

interface BookChapter {
  scope: "life" | "theme";
  themeKey: string;
  title: string;
  bodyMd: string;
  version: number;
  wordCount: number;
  coveredFrom: string | null;
  coveredTo: string | null;
  updatedAt: string | null;
  createdAt: string;
}

/** Phase 2: strukturierte Entität (Wissen-Tab) aus /api/biography/book. */
interface BookEntity {
  kind: "person" | "place" | "org" | "project" | "event" | "other";
  name: string;
  aliases: string[];
  summary: string | null;
  mentionCount: number;
}

/** Phase 2: Lebensereignis (Zeitleiste-Tab) aus /api/biography/book. */
interface BookEvent {
  date: string | null;
  dateText: string | null;
  title: string;
  description: string | null;
}

/** Welcher Tab im „Mein Leben"-Overlay aktiv ist. */
type BookTab = "buch" | "chronik" | "wissen" | "zeitleiste";

/**
 * Ein Hörbuch-Kapitel aus /api/biography/audiobook. `url` ist nur gesetzt,
 * wenn die MP3 schon auf der Platte gecacht ist — sonst muss sie via
 * POST /api/biography/narrate {chapter} erst synthetisiert werden.
 */
interface BookAudioChapter {
  index: number;
  title: string;
  charCount: number;
  estSeconds: number;
  cached: boolean;
  url?: string;
}

/** Deutsche Gruppen-Überschriften + Reihenfolge für die Entitäten-Arten. */
const BOOK_ENTITY_GROUPS: Array<{ kind: BookEntity["kind"]; label: string }> = [
  { kind: "person", label: "Menschen" },
  { kind: "place", label: "Orte" },
  { kind: "org", label: "Organisationen" },
  { kind: "project", label: "Projekte" },
  { kind: "event", label: "Ereignisse" },
  { kind: "other", label: "Sonstiges" },
];

/** Formatiert ein Ereignis-Datum: ISO → "7. Mai 2026", sonst dateText, sonst „—". */
function bookEventDate(ev: BookEvent): string {
  if (ev.date) {
    const d = new Date(`${ev.date}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return ev.date;
  }
  if (ev.dateText && ev.dateText.trim()) return ev.dateText.trim();
  return "—";
}

/** Relativ-Zeit für das Buch-Meta („vor 3 Stunden"), null bei ungültig. */
function bookRelativeTime(s?: string | null): string | null {
  const d = parseServerTs(s ?? undefined);
  if (!d) return null;
  const deltaSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (deltaSec < 0) return "gerade eben";
  if (deltaSec < 60) return "gerade eben";
  const min = Math.floor(deltaSec / 60);
  if (min < 60) return min === 1 ? "vor 1 Minute" : `vor ${min} Minuten`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return hours === 1 ? "vor 1 Stunde" : `vor ${hours} Stunden`;
  const days = Math.floor(hours / 24);
  if (days < 30) return days === 1 ? "vor 1 Tag" : `vor ${days} Tagen`;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

document
  .getElementById("open-book-btn")
  ?.addEventListener("click", () => void openBookSheet());

document
  .getElementById("open-human-design-btn")
  ?.addEventListener("click", () => void openHumanDesignSheet());

// --- Human-Design Deep-Link: der Hash spiegelt die offene Analyse, damit sie
//     a) nach einem Reload wieder erscheint und b) als Link teilbar ist.
//     #human-design = eigenes Chart · #human-design/<userId> = gezieltes Chart
//     (serverseitig Admin-gated). history.replaceState feuert KEIN hashchange →
//     kein Open/Close-Loop mit dem Listener unten. ---
function parseHdHash(): { open: boolean; userId?: number } {
  const m = location.hash.match(/^#human-design(?:\/(\d+))?$/);
  return m
    ? { open: true, userId: m[1] ? Number(m[1]) : undefined }
    : { open: false };
}
function setHdHash(target?: { userId: number }): void {
  const h = target ? `#human-design/${target.userId}` : "#human-design";
  if (location.hash !== h)
    window.history.replaceState(
      null,
      "",
      location.pathname + location.search + h,
    );
  window.dispatchEvent(new Event("voice-v8:nav-changed"));
}
function clearHdHash(): void {
  if (location.hash.startsWith("#human-design"))
    window.history.replaceState(null, "", location.pathname + location.search);
  window.dispatchEvent(new Event("voice-v8:nav-changed"));
}
function openHdFromHash(): void {
  const { open, userId } = parseHdHash();
  if (!open || document.querySelector(".hd-overlay")) return;
  void openHumanDesignSheet(userId ? { userId, name: "" } : undefined);
}
// Zurück/Vor, geteilter Link oder manuelle Hash-Änderung → öffnen/schließen.
window.addEventListener("hashchange", () => {
  const onHd = parseHdHash().open;
  const isOpen = !!document.querySelector(".hd-overlay");
  if (onHd && !isOpen) openHdFromHash();
  else if (!onHd && isOpen && hdActiveCleanup) hdActiveCleanup();
});
// Hinweis: Der Reload/Deep-Link-Auto-Open passiert NICHT hier, sondern erst in
// bootstrapAuth NACH dem Setzen von currentUser — sonst fehlen die Admin-Steuerungen
// (User-Picker / Verbindung / Kunde anlegen), die auf currentUser?.id === 1 prüfen.

// Pre.750: Essenz-Spiegel + Mein Leben am geteilten Deep-Link-Router anmelden
// (HD bleibt bei seiner älteren, admin-fähigen Eigenlogik oben). EIN
// hashchange-Listener für alle (DRY) — Vor/Zurück + manuelle Hash-Änderung.
/*
 * 08.09.2026 — der Fund wird vorgelesen.
 *
 * Martin: *„Diese Seite ist für mich so schwer verwertbar, denn
 * grundsätzlich lese ich nichts und lasse mir die Dinge vorlesen."*
 *
 * ⚠️ Der Player wird HEREINGEREICHT, nicht dort gebaut: `buildNarrationPlayer`
 *    lebt hier, und ein Import aus lib/ zurück nach app.ts wäre ein Kreis.
 *    Dasselbe Muster wie bei Projekten und Projekt-Dateien.
 */
void import("./lib/fund-blatt.js").then((m) =>
  m.setzeFundSpieler((o) =>
    buildNarrationPlayer({
      klebt: true,
      titel: o.titel,
      initialVoice: vorleseStimme(),
      endpoint: o.endpoint,
      getBody: o.getBody,
      sectionable: o.sectionable,
    }),
  ),
);
/*
 * Ticket „Meldungen vorlesen" (09.09.2026). Martin: *„grundsaetzlich lese ich
 * nichts und lasse mir die Dinge vorlesen."* — dieselbe Einreichung wie beim
 * Fund, weil `buildNarrationPlayer` hier lebt und nur der eine Player von der
 * Abspielleiste ueber der Fusszeile weiss.
 *
 * ⚠️ `klebt: false`: Die Meldung ist eine Karte in einer Liste, keine Seite.
 *    Eine heftende Leiste je Karte waere ein Zaun quer durch die Liste.
 */
void import("./lib/tickets-view.js").then((m) =>
  m.setzeMeldungsSpieler((o) =>
    buildNarrationPlayer({
      klebt: false,
      titel: o.titel,
      initialVoice: vorleseStimme(),
      endpoint: o.endpoint,
      getBody: o.getBody,
    }),
  ),
);

registerPageRoute({
  key: "essence",
  isOpen: () => !!document.querySelector(".essence-overlay"),
  open: () => openEssenceSheet(undefined, "seiten-adresse"),
  close: () => essenceActiveCleanup?.(),
});
registerPageRoute({
  key: "book",
  isOpen: () => !!document.querySelector(".book-overlay"),
  open: () => void openBookSheet(),
  close: () => bookActiveCleanup?.(),
});
/*
 * ⚠️ 09.09.2026 — die „Rechnungen"-Seite (Pre.758) ist weg.
 *
 * Martin: *„Ich sehe immer noch Rechnungen im System."* Die Werkzeuge waren
 * seit pre.2752 aus dem Gespraech genommen, die ANSICHT stand noch im Menue
 * — halb aufgeraeumt ist schlimmer als gar nicht, weil es aussieht, als
 * ginge es noch.
 *
 * Gemessen an den Serveraufrufen 25.08.-06.09.: 8 Aufrufe. Zum Vergleich:
 * Aufgaben 42.988, Spiegel 23.294. Die Rechnungen liegen im Panel
 * (panel.ssi.at), und dort gehoeren sie hin.
 */
registerPageRoute({
  key: "material",
  // Pre.792 (#36/#62): reload-fest (Muster der Vollseiten). DOM-Query statt
  // Modul-Export — material-view ist lazy geladen.
  isOpen: () => !!document.querySelector("#material-sheet:not([hidden])"),
  open: () =>
    void import("./lib/material-view.js").then((m) => m.openMaterialSheet()),
  close: () =>
    void import("./lib/material-view.js").then((m) => m.closeMaterialSheet()),
});
// Pre.988: „Mein Alltag"-Reiter — Alltags-Aufnahmen, Digests, Roll-up.
registerPageRoute({
  key: "alltag",
  isOpen: () => !!document.querySelector("#alltag-sheet:not([hidden])"),
  open: () =>
    void import("./lib/alltag-view.js").then((m) => m.openAlltagSheet()),
  close: () =>
    void import("./lib/alltag-view.js").then((m) => m.closeAlltagSheet()),
});
// 2026-07-21 (Martin): „Verfassung" — WHOOP-Vitaldaten als Seite.
registerPageRoute({
  key: "verfassung",
  isOpen: () => !!document.getElementById("verfassung-sheet"),
  open: () =>
    void import("./lib/verfassung-view.js").then((m) =>
      m.openVerfassungView(),
    ),
  close: () =>
    void import("./lib/verfassung-view.js").then((m) =>
      m.closeVerfassungView(),
    ),
});
// 2026-07-20 (Martin): „Tiefe Fragen"-Seite — Historie der Faeden.
registerPageRoute({
  key: "tiefe-fragen",
  isOpen: () => !!document.getElementById("deep-questions-sheet"),
  open: () =>
    void import("./lib/deep-questions-view.js").then((m) =>
      m.openDeepQuestionsView(),
    ),
  close: () =>
    void import("./lib/deep-questions-view.js").then((m) =>
      m.closeDeepQuestionsView(),
    ),
});
// 19.10.2026 (Martin): „Morgenreport" — die Einzelheiten IN der App (Ziel von „Alle Einzelheiten“ an der Chat-Karte), reloadfest.
registerPageRoute({
  key: "morgenreport",
  isOpen: () => !!document.getElementById("morgenreport-sheet"),
  open: () =>
    void import("./lib/morgenreport-seite.js").then((m) => m.openMorgenreportSeite()),
  close: () =>
    void import("./lib/morgenreport-seite.js").then((m) => m.closeMorgenreportSeite()),
});
// 21.10.2026 (Martin): „Training" — Woche und Statistik, nur Konto 1 (der Server sperrt alle anderen), reloadfest.
registerPageRoute({
  key: "training",
  isOpen: () => !!document.getElementById("training-sheet"),
  open: () => void import("./lib/training-seite.js").then((m) => m.openTrainingSeite()),
  close: () => void import("./lib/training-seite.js").then((m) => m.closeTrainingSeite()),
});
// 27.09.2026 (Ticket 2637): „Ankermomente" — alle Anker mit Bild, reloadfest.
registerPageRoute({
  key: "anker",
  isOpen: () => !!document.getElementById("anker-sheet"),
  open: () =>
    void import("./lib/anker-view.js").then((m) => m.openAnkerView()),
  close: () =>
    void import("./lib/anker-view.js").then((m) => m.closeAnkerView()),
});
// 08.09.2026 (Martin: „die Seite genau dort wieder geladen wird, wo ich
// gerade bin"): „Offen" war die letzte Vollseite ohne Route.
registerPageRoute({
  key: "offen",
  isOpen: () => !!document.querySelector(".tickets-backdrop"),
  open: () =>
    void import("./lib/tickets-view.js").then((m) => m.openTicketsModal()),
  close: () =>
    void import("./lib/tickets-view.js").then((m) => m.closeTicketsModal()),
});
initPageRouter();

// 15.09.2026 (Ticket 2297, Martin: „ein dynamisches Element … als
// Erkennungswert"): der Stern im Kopf nimmt die Farbe der Stunde an.
starteSternlauf();

// 2026-07-21 (Martin): der aktive Sidebar-Eintrag zeigt, WO man gerade ist.
// Vollseiten melden sich ueber den Hash; ohne Overlay gehoert die Markierung
// wieder der View-Logik (Chat als Standard).
const NAV_BY_HASH: Record<string, string> = {
  essence: 'open-essence-btn',
  'tiefe-fragen': 'open-deepq-btn',
  verfassung: 'open-verfassung-btn',
  alltag: 'open-alltag-btn',
  material: 'open-material-btn',
  book: 'open-book-btn',
  'human-design': 'open-human-design-btn',
  anker: 'open-anker-btn',
  morgenreport: 'open-morgenreport-btn',
  training: 'open-training-btn',
};
function updateNavActive(): void {
  const key = location.hash.replace(/^#/, '').split(/[/?:]/)[0] ?? '';
  const id = NAV_BY_HASH[key] ?? null;
  for (const bid of Object.values(NAV_BY_HASH)) {
    document
      .getElementById(bid)
      ?.setAttribute('aria-selected', bid === id ? 'true' : 'false');
  }
  const tabs = Array.from(
    document.querySelectorAll<HTMLElement>('.nav-drawer-list .nav-item[data-tab]'),
  );
  // 23.09.2026 (Ticket 2542): hier stand „Chat als Standard", sobald kein
  // Reiter ausgewählt war — in den Einstellungen galt Chat damit als „hier",
  // und die Symbolleiste sperrte genau diesen Knopf. Siehe nav-markierung.ts.
  markiereReiter(tabs, !!id, document.body.dataset.activeView);
}
window.addEventListener('hashchange', updateNavActive);
window.addEventListener('voice-v8:nav-changed', () => {
  setTimeout(updateNavActive, 0);
});

// 2026-07-21 (Martin, "liebevolles Feedback, auch wenn die KI nicht
// spricht"): die Verfassungs-Glut — ein sanft pulsierender Punkt in der
// Topbar. Farbe = koerperliche Verfassung (WHOOP-Erholung, gedaempft durch
// Schlafdefizit); Tipp oeffnet die Verfassung-Seite. Stufe 2 (mental/
// psychisch aus der Stimmungs-Reihe) kommt mit dem Korrelations-Paket.
// 06.09.2026: Die Verfassungs-Glut in der Kopfzeile ist WEG — die Verfassung
// wohnt jetzt in der Körper-Karte im Zuhause (zuhause-view, blickKoerper),
// mit Stand und Zeitpunkt. Ein Smiley ohne Kontext, der den ganzen Tag
// traurig schaut, war Martin zu Recht zu viel.
window.addEventListener('voice-v8:page-open', () => {
  setTimeout(updateNavActive, 60);
});
updateNavActive();

document
  .getElementById("open-essence-btn")
  ?.addEventListener("click", () => void openEssenceSheet(undefined, "menue"));

// 2026-07-20 (Martin): „Tiefe Fragen" im Menue → Historie der Faeden.
document
  .getElementById("open-verfassung-btn")
  ?.addEventListener("click", () => {
    closeDrawerForNavigation();
    void import("./lib/verfassung-view.js").then((m) =>
      m.openVerfassungView(),
    );
  });
document
  .getElementById("open-morgenreport-btn")
  ?.addEventListener("click", () => {
    closeDrawerForNavigation();
    void import("./lib/morgenreport-seite.js").then((m) => m.openMorgenreportSeite());
  });
document
  .getElementById("open-training-btn")
  ?.addEventListener("click", () => {
    closeDrawerForNavigation();
    void import("./lib/training-seite.js").then((m) => m.openTrainingSeite());
  });
// 27.09.2026 (Ticket 2637): „Ankermomente" in der Gruppe „Mehr". Das
// Schliessen der Leiste steht hier zusaetzlich zum page-open-Haken: ist die
// Seite schon offen, meldet sie sich nicht neu, und am Telefon bliebe die
// Leiste sonst ueber ihr liegen.
document
  .getElementById("open-anker-btn")
  ?.addEventListener("click", () => {
    closeDrawerForNavigation();
    void import("./lib/anker-view.js").then((m) => m.openAnkerView());
  });
document
  .getElementById("open-deepq-btn")
  ?.addEventListener(
    "click",
    () =>
      void import("./lib/deep-questions-view.js").then((m) =>
        m.openDeepQuestionsView(),
      ),
  );

// Pre.752 (Ticket #36): „Material" im Menü → direkter Zugang zu eingespeisten
// Texten (project_files im Projekt „Eingespeistes Material").
document
  .getElementById("open-material-btn")
  ?.addEventListener(
    "click",
    () =>
      void import("./lib/material-view.js").then((m) => m.openMaterialSheet()),
  );

// Pre.988: „Mein Alltag" im Menü → Alltags-Aufnahmen ansehen.
document
  .getElementById("open-alltag-btn")
  ?.addEventListener(
    "click",
    () => void import("./lib/alltag-view.js").then((m) => m.openAlltagSheet()),
  );

// 21.09.2026: Nutzer- und Modulverwaltung liegen im Adminbereich (/admin) —
// die Sidebar der App fuehrt nur noch dorthin (open-admin-link).

async function openBookSheet(): Promise<void> {
  // Drawer schließen (wie beim Archiv).
  // 2026-07-05 (Martin): der ECHTE Drawer laeuft ueber body.drawer-open —
  // Alt-Mechanismus schloss nichts, Menue blieb ueber dem Inhalt haengen.
  closeDrawerForNavigation();
  document
    .getElementById("nav-menu-btn")
    ?.setAttribute("aria-expanded", "false");

  const existing = document.querySelector(".book-overlay");
  if (existing) {
    (existing.querySelector("audio"))?.pause();
    existing.remove();
  }

  const overlay = document.createElement("div");
  overlay.className = "sheet book-overlay page-sheet";
  // Pre.750: reload-fester, teilbarer Deep-Link #book (geteilter page-routes-Router).
  setPageHash("book");
  // EIN gemeinsamer Vorlese-Kern (Zustandsautomat, Timeouts, Stall-Watchdog,
  // Abbruch) — derselbe wie in Essenz & Human Design (buildNarrationPlayer).
  // „Ein Player überall, eine Wahrheit": die Engine spielt nur EINE Quelle
  // robust ab; das Hörbuch orchestriert die Kapitel DARÜBER (löst URLs selbst
  // auf, reicht der Engine pro Kapitel eine fertige Datei und springt bei
  // `onEnded` zum nächsten). `audio` ist das engine-eigene Element — die
  // bestehenden Lese-Helfer (Dauer/Position/Seek/Mitlauf) greifen unverändert
  // darauf zu.
  const engine = new NarrationEngine();
  const audio = engine.audio;
  overlay.appendChild(audio);
  // AbortController für die orchestrator-eigene Kapitel-URL-Auflösung
  // (Wiedergabe-Pfad + Prefetch). Stimmwechsel/Schließen brechen laufende
  // Synthesen ab, damit nie eine VERALTETE Stimme nachträglich abgespielt oder
  // gecacht wird. Früh deklariert, weil die Schließen-Handler sie referenzieren.
  let resolveAbort: AbortController | null = null;
  let prefetchAbort: AbortController | null = null;
  // Vorwärts-Referenz: die echte Implementierung wird unten in der
  // Vorlese-Logik zugewiesen (braucht posKey/lifeVersion). Bis dahin No-op,
  // damit die früh gebundenen Schließen-Handler sicher aufrufbar sind.
  let saveNarrationPos: () => void = () => {};

  const sheet = document.createElement("div");
  sheet.className = "sheet-card";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Mein Leben — Biografie");

  // 2026-07-21 (Audit Stufe 3c): NORM-Kopf via buildPageHeader — die
  // Meta-Infos kommen als eigene Zeile unterm Kopf. closeBook ist weiter
  // unten definiert; die Handler lesen es erst beim Klick (kein TDZ).
  const metaLine = document.createElement("div");
  metaLine.className = "book-meta";

  // Play/Pause des Hörbuchs lebt jetzt in der GETEILTEN Player-Leiste
  // (buildPlayerBar, unten) — exakt wie Human Design & Essenz-Spiegel. Der Kopf
  // behält nur Stimme/Probe/Aktualisieren/PDF/Schließen.

  // Erzähler-Stimme: der GETEILTE Stimm-Picker (Select + Vorhören, gender-
  // gruppiert) — IDENTISCH zu Human Design & Essenz-Spiegel. Er sitzt NICHT im
  // Kopf, sondern (zusammen mit der Player-Leiste) als `.narration-player` über
  // dem Inhalt; siehe `playerWrap` weiter unten. Default = GRATIS Google-Chirp3-HD.
  const GOOGLE_NARRATOR_DEFAULT = "de-DE-Chirp3-HD-Charon";
  let narratorVoice = vorleseStimme();
  const voicePicker = buildVoicePicker({
    initial: narratorVoice,
    onChange: (id) => {
      narratorVoice = id;
      try {
        saveSettings({ ...loadSettings(), narratorVoiceId: narratorVoice });
      } catch {
        /* localStorage/sync-Fehler ignorieren — Stimme gilt trotzdem für jetzt */
      }
      // Laufende Lesung stoppen + Hörbuch zurücksetzen → nächster Klick lädt mit
      // der neuen Stimme.
      engine.destroy();
      bar.setState("idle", true);
      resetPlayer();
    },
  });

  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "book-refresh";
  const refreshIcon = refreshIconHtml("book-refresh-icon");
  const refreshLabel = "Aktualisieren";
  refreshBtn.innerHTML = `${refreshIcon}<span class="book-refresh-label">${refreshLabel}</span>`;


  // Pre.750: EINE Schließ-Logik für Zurück-Pfeil und Deep-Link-Router (DRY).
  // #91: Das zusätzliche X entfällt — der ←-Pfeil ist der einzige Schließ-Weg
  // (echte Unterseite), die Doppelung machte den Kopf unruhig.
  const closeBook = (): void => {
    saveNarrationPos();
    resolveAbort?.abort();
    prefetchAbort?.abort();
    engine.destroy();
    voicePicker.stopPreview();
    overlay.remove();
    clearPageHash("book");
    bookActiveCleanup = null;
  };
  bookActiveCleanup = closeBook;
  attachEdgeSwipeBack(overlay, () => closeBook());

  // 2026-08-20: Auffrischen ist "neu berechnen" — gehoert ins Menue (siehe
  // die Regel beim Essenz-Spiegel).
  const head = buildPageHeader("Mein Leben", () => closeBook(), {
    menuItems: [
      {
        label: refreshLabel || "Neu schreiben",
        onSelect: () => refreshBtn.click(),
      },
      {
        label: "PDF ansehen",
        onSelect: () =>
          window.open("/api/biography/pdf?inline=1", "_blank", "noopener"),
      },
    ],
  });
  sheet.appendChild(head);
  // Meta-Infos als eigene, volle Zeile unterm Kopf.
  sheet.appendChild(metaLine);

  // --- Tab-Leiste: Buch | Wissen | Zeitleiste ---
  let activeTab: BookTab = "buch";
  // Vorwärts-Referenz: Kapitel-Mitlauf (Hervorhebung + Auto-Scroll des
  // laufenden Kapitels im Buch-Tab). Wird unten in der Vorlese-Logik mit der
  // echten Implementierung belegt; bis dahin No-op, damit setTab/renderBook
  // (oben definiert) sie gefahrlos aufrufen können.
  let reapplyFollow: () => void = () => {};
  // Forward-Ref wie reapplyFollow: Beim Öffnen des Buch-Tabs die ersten
  // Hörbuch-Kapitel still vor-wärmen (echte Impl in der Vorlese-Logik).
  let maybePrewarmAudiobook: () => void = () => {};
  const tabBar = document.createElement("div");
  tabBar.className = "book-tabs";
  tabBar.setAttribute("role", "tablist");
  const tabDefs: Array<{ id: BookTab; label: string }> = [
    { id: "buch", label: "Buch" },
    { id: "chronik", label: "Chronik" },
    { id: "wissen", label: "Wissen" },
    { id: "zeitleiste", label: "Zeitleiste" },
  ];
  const tabButtons = new Map<BookTab, HTMLButtonElement>();
  for (const def of tabDefs) {
    const tabBtn = document.createElement("button");
    tabBtn.type = "button";
    tabBtn.className = "book-tab";
    tabBtn.setAttribute("role", "tab");
    tabBtn.textContent = def.label;
    tabBtn.addEventListener("click", () => setTab(def.id));
    tabBar.appendChild(tabBtn);
    tabButtons.set(def.id, tabBtn);
  }
  sheet.appendChild(tabBar);

  // --- GETEILTE Player-Leiste (Play/Pause + Seek + Zeit + Gesamt) — IDENTISCH
  // zu Human Design & Essenz-Spiegel (buildPlayerBar). Sichtbar über allen Tabs.
  // Play/Pause → onNarrate; Scrubben → Position im aktuellen Kapitel setzen.
  const bar = buildPlayerBar({
    onToggle: () => onNarrate(),
    onSeek: (s) => {
      try {
        audio.currentTime = s;
      } catch {
        /* manche Browser erst nach Metadaten — egal */
      }
      savePos();
    },
    // Kapitel-Blättern: zum vorherigen/nächsten Kapitel springen (auch während
    // des Abspielens — playChapter bricht das laufende sauber ab).
    onPrev: () => {
      const prev = audioChapters.find((c) => c.index === currentChapter - 1);
      if (prev) void playChapter(prev.index);
    },
    onNext: () => {
      const next = audioChapters.find((c) => c.index === currentChapter + 1);
      if (next) void playChapter(next.index);
    },
    // Pre.976: GANZES Hörbuch als ein MP3 — Server vertont das Gesamtbuch
    // (Ganzbuch-Pfad von /api/biography/narrate ohne `chapter`).
    onDownload: async () => {
      try {
        const res = await fetch("/api/biography/narrate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            voiceId: narratorVoice,
            googleVoice: googleHdFallbackForVoice(narratorVoice),
          }),
        });
        const j = (await res.json().catch(() => null)) as {
          ok?: boolean;
          url?: string;
        } | null;
        if (!res.ok || !j?.ok || !j.url) throw new Error("narrate failed");
        /*
         * ⚠️ 11.09.2026: Hier stand `<a download>` — in der WKWebView der
         *    nativen App tut das nichts (siehe der lange Kommentar bei
         *    `downloadName` oben). `j.url` ist bereits eine fertige
         *    `/uploads/...`-Datei, also greift share.ts' bestehende
         *    Erkennung ohne weiteres Zutun.
         */
        await shareMedia({
          url: j.url,
          title: "Mein Leben",
          dateiname: "Mein-Leben.mp3",
        });
      } catch {
        showToast("Gerade nicht möglich — bitte später erneut versuchen.", "warning");
      }
    },
  });
  bar.setState("idle", true); // im Buch sofort startbereit (Klick lädt das Hörbuch)
  // Stimm-Picker + Leiste als EINE Einheit (`.narration-player`) — exakt dieselbe
  // Struktur + Platzierung wie Human Design & Essenz-Spiegel (Picker über der
  // Leiste). Der `book-`-Modifier zentriert sie unter den Tabs.
  // 2026-07-27 (Martin, Ticket „Player sticky"): Die Stimmwahl gehoert NICHT
  // in die klebende Einheit — man waehlt sie einmal und liest dann. Sie sitzt
  // jetzt in einem eigenen Block darueber, der mit dem Text wegscrollt.
  const voiceWrap = document.createElement("div");
  voiceWrap.className = "book-voicebar";
  voiceWrap.appendChild(voicePicker.el);
  // 2026-07-01 (Martin): einmalige Vertonungs-Erklaerung als dezente Zeile —
  // nicht mehr als Bandwurm-Label im Play-Button.
  const narrHint = document.createElement("div");
  narrHint.className = "player-bar-hint";
  narrHint.textContent = "Einmalige Vertonung — danach sofort abrufbar.";
  narrHint.hidden = true;
  voiceWrap.appendChild(narrHint);
  sheet.appendChild(voiceWrap);

  const playerWrap = document.createElement("div");
  // pre.916: tote --book-Klasse entfernt (CSS-Regel war seit pre.913 weg) —
  // Player nutzt die geteilte .narration-player-Basis, identisch zu HD/Essenz.
  // 2026-07-27: Die Klasse bleibt BEWUSST auf diesem Element — daran haengen
  // die Spaltenbreite (MASKE v6) und die Beruehr-Regeln fuer die Leiste.
  // Waere die Klebe-Einheit ein neues Element mit neuem Namen, muessten vier
  // verstreute CSS-Bloecke nachgezogen werden.
  playerWrap.className = "narration-player narration-player--sticky";
  playerWrap.appendChild(bar.el);
  sheet.appendChild(playerWrap);

  // --- Kapitel-Liste (Hörbuch-Inhaltsverzeichnis) — klappbar, direkt unter
  // dem Player. Zeigt Titel + geschätzte Dauer + „geladen"-Status pro Kapitel;
  // klicken springt zum Kapitel. Erst sichtbar, sobald das Hörbuch lädt.
  const chaptersBox = document.createElement("details");
  chaptersBox.className = "book-chapters book-chapters--player";
  // 2026-07-02 (Martin): von Anfang an sichtbar (konsistentes Bild, kein
  // spaeteres Einblenden). Die Liste fuellt sich, sobald die TOC geladen ist.
  chaptersBox.hidden = false;
  const chaptersSummary = document.createElement("summary");
  chaptersSummary.className = "book-chapters-summary";
  const chaptersSummaryLabel = document.createElement("span");
  chaptersSummaryLabel.className = "book-chapters-summary-label";
  chaptersSummaryLabel.textContent = "Kapitel";
  chaptersSummary.appendChild(chaptersSummaryLabel);
  const chaptersTotal = document.createElement("span");
  chaptersTotal.className = "book-chapters-total";
  chaptersSummary.appendChild(chaptersTotal);
  chaptersBox.appendChild(chaptersSummary);
  const chaptersList = document.createElement("div");
  chaptersList.className = "book-chapters-list";
  chaptersBox.appendChild(chaptersList);
  // 2026-07-01 (Martin): Kapitel-Zeile gehoert zur Player-Einheit (schmal,
  // zentriert) — kein Vollbreiten-Band mehr quer ueber die Seite.
  playerWrap.appendChild(chaptersBox);
  // Aussenklick schliesst die Kapitel-Liste (wie ein Menue).
  document.addEventListener("click", (e) => {
    if (!chaptersBox.open) return;
    if (chaptersBox.contains(e.target as Node)) return;
    chaptersBox.open = false;
  });

  // --- Inline-Fehler (über dem Body, dezent) ---
  const errBar = document.createElement("div");
  errBar.className = "book-error";
  errBar.hidden = true;
  sheet.appendChild(errBar);

  // --- Lese-Body ---
  const body = document.createElement("div");
  body.className = "book-body";
  sheet.appendChild(body);

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);

  let chapters: BookChapter[] = [];
  let entities: BookEntity[] = [];
  let timeline: BookEvent[] = [];
  let busy = false;

  const showError = (msg: string): void => {
    errBar.textContent = msg;
    errBar.hidden = false;
  };
  const clearError = (): void => {
    errBar.hidden = true;
    errBar.textContent = "";
  };

  const setRefreshLabel = (label: string): void => {
    const span = refreshBtn.querySelector(".book-refresh-label");
    if (span) span.textContent = label;
  };

  const renderMeta = (life: BookChapter | undefined): void => {
    metaLine.textContent = "";
    if (!life) return;
    const parts: string[] = [];
    const rel = bookRelativeTime(life.updatedAt ?? life.createdAt);
    if (rel) parts.push(`Zuletzt aktualisiert ${rel}`);
    if (Number.isFinite(life.version) && life.version > 0)
      parts.push(`Version ${life.version}`);
    if (Number.isFinite(life.wordCount) && life.wordCount > 0)
      parts.push(`${life.wordCount.toLocaleString("de-DE")} Wörter`);
    metaLine.append(document.createTextNode(parts.join(" · ")));
    // pre.947 (Martin): Der „Neu aufbauen"-Link (deep, baute das Buch von Null
    // neu = riskanter Total-Verlust, Vorfall 17.06.) ist ENTFERNT. Es gibt nur
    // noch „Aktualisieren" — das ERWEITERT die bestehende Biografie, baut sie
    // nie von Grund auf neu. „Nur weiterer Aufbau, kein neuer Aufbau."
  };

  // Render: leerer Zustand mit prominentem Aufruf, das Buch zu erzeugen.
  /*
   * Noch kein Buch — aber es gibt ZWEI Gründe dafür, und sie verlangen
   * verschiedene Sätze. (16.09.2026)
   *
   * ⚠️ Martin: *„Ob wir das Buch ‚Mein Leben' derweilen herausnehmen und in
   *    einer späteren Folge freischalten, so dass der User erst was erzeugen
   *    muss, damit es dann auch wirklich einen Content gibt, den man für ein
   *    Buch verwenden kann."*
   *
   *    Nachgezählt: von allen Konten haben genau ZWEI überhaupt ein Kapitel —
   *    Martin vier, Albert eines. Alle übrigen null. Wer neu ist, hat nichts,
   *    und auch ein bezahltes Paket ändert daran nichts.
   *
   * ⚠️ Hier stand für ALLE: „tippe auf Aktualisieren, damit Vespera deine
   *    Biografie schreibt." Wer noch nichts erzählt hat, drückt darauf und es
   *    passiert nichts — aus null Stoff entsteht kein Kapitel. Dieselbe Sorte
   *    Knopf wie das „Erneut versuchen" hinter der Bezahlgrenze: er kann
   *    nicht halten, was er verspricht.
   *
   *    Statt zu verstecken wird es benannt. Ein Menüpunkt, der später
   *    auftaucht, verwirrt; ein Satz, der sagt, wie das Buch entsteht, lädt
   *    ein.
   */
  const renderEmpty = (): void => {
    body.innerHTML = "";
    // Gibt es überhaupt Stoff? Namen und Ereignisse kommen aus demselben
    // Gedächtnis wie die Kapitel — sind auch sie leer, wurde noch nichts
    // erzählt, und „Aktualisieren" wäre ein leeres Versprechen.
    const hatStoff = entities.length > 0 || timeline.length > 0;
    const empty = document.createElement("div");
    empty.className = "book-empty";
    const lead = document.createElement("p");
    lead.className = "book-empty-lead";
    lead.textContent = hatStoff
      ? "Noch kein Buch — tippe auf Aktualisieren, damit Vespera deine Biografie schreibt."
      : "Dein Buch entsteht aus dem, was du erzählst. Sobald du ein paar Tage von dir berichtet hast, schreibt Vespera daraus die ersten Kapitel.";
    empty.appendChild(lead);
    const cta = document.createElement("button");
    cta.type = "button";
    cta.className = "icm-btn icm-btn--primary book-empty-cta";
    if (hatStoff) {
      cta.textContent = "Aktualisieren";
      cta.addEventListener("click", () => void regenerate());
    } else {
      // Kein Knopf, der nichts bewirkt — sondern der Weg dorthin.
      cta.textContent = "Jetzt erzählen";
      cta.addEventListener("click", () => {
        closeBook();
        document.dispatchEvent(new CustomEvent("vespera:go-chat"));
      });
    }
    empty.appendChild(cta);
    body.appendChild(empty);
  };

  // Render: das Buch + Themen-Kapitel als ein-/ausklappbares Verzeichnis.
  const renderBook = (): void => {
    body.innerHTML = "";
    const life = chapters.find((c) => c.scope === "life");
    const themes = chapters.filter((c) => c.scope === "theme");

    if (!life && themes.length === 0) {
      renderEmpty();
      return;
    }

    // Hauptkapitel „Mein Leben"
    if (life) {
      const main = document.createElement("article");
      main.className = "book-chapter book-chapter--life";
      renderMarkdown(main, life.bodyMd ?? "");
      body.appendChild(main);
    }

    // Themen-Kapitel als Inhaltsverzeichnis (klappbar)
    if (themes.length > 0) {
      const tocHead = document.createElement("h2");
      tocHead.className = "book-toc-head";
      tocHead.textContent = "Schwerpunkte";
      body.appendChild(tocHead);

      const toc = document.createElement("div");
      toc.className = "book-toc";
      for (const t of themes) {
        const item = document.createElement("details");
        item.className = "book-toc-item";

        const summary = document.createElement("summary");
        summary.className = "book-toc-summary";
        const tTitle = document.createElement("span");
        tTitle.className = "book-toc-title";
        tTitle.textContent = t.title || t.themeKey || "Schwerpunkt";
        summary.appendChild(tTitle);
        const tMeta = document.createElement("span");
        tMeta.className = "book-toc-meta";
        const mparts: string[] = [];
        if (Number.isFinite(t.wordCount) && t.wordCount > 0)
          mparts.push(`${t.wordCount.toLocaleString("de-DE")} Wörter`);
        const trel = bookRelativeTime(t.updatedAt ?? t.createdAt);
        if (trel) mparts.push(trel);
        tMeta.textContent = mparts.join(" · ");
        summary.appendChild(tMeta);
        item.appendChild(summary);

        const content = document.createElement("div");
        content.className = "book-toc-body";
        // Lazy: erst beim ersten Aufklappen rendern.
        let rendered = false;
        item.addEventListener("toggle", () => {
          if (item.open && !rendered) {
            renderMarkdown(content, t.bodyMd ?? "");
            rendered = true;
          }
        });
        item.appendChild(content);
        toc.appendChild(item);
      }
      body.appendChild(toc);
    }

    // Nach (Neu-)Render die Kapitel-Mitlauf-Hervorhebung wieder anwenden, falls
    // gerade vorgelesen wird — überlebt Tab-Wechsel / Reload / Re-Render.
    reapplyFollow();
    // 2026-07-27: Kapitel anspringbar machen. Beim ERSTEN Rendern ist das
    // Hoerbuch-Verzeichnis meist noch leer — dann passiert hier nichts und die
    // Vergabe holt das nach, sobald das Verzeichnis eintrifft.
    vergibKapitelAnker?.();
    // Buch-Tab offen → die ersten Hörbuch-Kapitel still vor-wärmen, damit
    // „Vorlesen" sofort startet statt erst zu synthetisieren.
    maybePrewarmAudiobook();
  };

  // Render: Wissen-Tab — Entitäten nach Art gruppiert, jede als kompakte Karte.
  const renderEntities = (): void => {
    body.innerHTML = "";
    if (entities.length === 0) {
      const empty = document.createElement("div");
      empty.className = "book-empty book-empty--mini";
      const lead = document.createElement("p");
      lead.className = "book-empty-lead";
      lead.textContent = "Noch keine Einträge — tippe auf Aktualisieren.";
      empty.appendChild(lead);
      body.appendChild(empty);
      return;
    }
    for (const group of BOOK_ENTITY_GROUPS) {
      const inGroup = entities.filter((e) => e.kind === group.kind);
      if (inGroup.length === 0) continue;
      const gHead = document.createElement("h2");
      gHead.className = "book-group-head";
      gHead.textContent = group.label;
      body.appendChild(gHead);

      const grid = document.createElement("div");
      grid.className = "book-entity-grid";
      for (const e of inGroup) {
        const card = document.createElement("div");
        card.className = "book-entity";

        const name = document.createElement("strong");
        name.className = "book-entity-name";
        name.textContent = e.name;
        card.appendChild(name);

        const aliases = (e.aliases ?? []).filter((a) => a && a.trim());
        if (aliases.length > 0) {
          const al = document.createElement("div");
          al.className = "book-entity-aliases";
          al.textContent = `auch: ${aliases.join(", ")}`;
          card.appendChild(al);
        }

        if (e.summary && e.summary.trim()) {
          const sum = document.createElement("p");
          sum.className = "book-entity-summary";
          sum.textContent = e.summary.trim();
          card.appendChild(sum);
        }

        if (Number.isFinite(e.mentionCount) && e.mentionCount > 0) {
          const meta = document.createElement("div");
          meta.className = "book-entity-meta";
          meta.textContent = `${e.mentionCount.toLocaleString("de-DE")} ${
            e.mentionCount === 1 ? "Erwähnung" : "Erwähnungen"
          }`;
          card.appendChild(meta);
        }

        grid.appendChild(card);
      }
      body.appendChild(grid);
    }
  };

  // Render: Zeitleiste-Tab — Ereignisse in gelieferter Reihenfolge (datiert
  // zuerst ASC, undatiert zuletzt). Jede Zeile: Datum links, Titel + Text.
  const renderTimeline = (): void => {
    body.innerHTML = "";
    if (timeline.length === 0) {
      const empty = document.createElement("div");
      empty.className = "book-empty book-empty--mini";
      const lead = document.createElement("p");
      lead.className = "book-empty-lead";
      lead.textContent = "Noch keine Ereignisse — tippe auf Aktualisieren.";
      empty.appendChild(lead);
      body.appendChild(empty);
      return;
    }
    const list = document.createElement("div");
    list.className = "book-timeline";
    for (const ev of timeline) {
      const row = document.createElement("div");
      row.className = "book-event";

      const dateEl = document.createElement("div");
      dateEl.className = "book-event-date";
      dateEl.textContent = bookEventDate(ev);
      row.appendChild(dateEl);

      const main = document.createElement("div");
      main.className = "book-event-main";
      const evTitle = document.createElement("strong");
      evTitle.className = "book-event-title";
      evTitle.textContent = ev.title;
      main.appendChild(evTitle);
      if (ev.description && ev.description.trim()) {
        const desc = document.createElement("p");
        desc.className = "book-event-desc";
        desc.textContent = ev.description.trim();
        main.appendChild(desc);
      }
      row.appendChild(main);
      list.appendChild(row);
    }
    body.appendChild(list);
  };

  // „Chronik"-Reiter: Monats-Kapitel + letzte Tages-Digests (2026-06-29 aus
  // den Einstellungen hierher gezogen). Lazy geladen + gecacht, damit ein
  // Tab-Wechsel nicht erneut vom Server holt.
  let chronikEl: HTMLElement | null = null;
  const renderChronik = (): void => {
    body.innerHTML = "";
    if (chronikEl) {
      body.appendChild(chronikEl);
      return;
    }
    // 2026-08-30 (Paket 6): .book-loading war ein zentrierter Text ohne
    // Zeichen — jetzt dieselbe Anzeige wie in jeder anderen Ansicht.
    const loading = buildLoadingHint();
    body.appendChild(loading);
    void import("./lib/biography-chronik.js").then(
      ({ buildBiographyChronik }) => {
        /*
         * 08.09.2026 — die Chronik wird vorgelesen. Martin: *„Grundsaetzlich
         * lese ich nichts und lasse mir die Dinge vorlesen."* Der Player
         * wird hereingereicht, weil buildNarrationPlayer hier lebt.
         */
        chronikEl = buildBiographyChronik({
          buildPlayer: (o) =>
            buildNarrationPlayer({
              klebt: true,
              titel: o.titel,
              initialVoice: vorleseStimme(),
              endpoint: o.endpoint,
              getBody: o.getBody,
              sectionable: o.sectionable,
            }),
        });
        if (activeTab === "chronik") {
          body.innerHTML = "";
          body.appendChild(chronikEl);
        }
      },
    );
  };

  // Rendert den aktiven Tab aus den gecachten Daten (kein erneuter Fetch).
  // Die Meta-Zeile im Kopf bleibt über alle Tabs sichtbar (Buch-Stand).
  const renderActive = (): void => {
    renderMeta(chapters.find((c) => c.scope === "life"));
    if (activeTab === "wissen") renderEntities();
    else if (activeTab === "zeitleiste") renderTimeline();
    else if (activeTab === "chronik") renderChronik();
    else if (chapters.length === 0) renderEmpty();
    else renderBook();
  };

  // Tab wechseln → nur neu rendern (Daten sind gecacht).
  function setTab(id: BookTab): void {
    activeTab = id;
    for (const [tabId, btn] of tabButtons) {
      const on = tabId === id;
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
    }
    // Audit Stufe 3c: die Hoerbuch-Leiste gehoert zu Buch/Chronik — auf
    // Wissen/Zeitleiste verschwindet sie, ausser es spielt gerade etwas
    // (dann bleibt der Stopp-Zugriff erhalten).
    playerWrap.hidden =
      !(id === "buch" || id === "chronik") && audio.paused;
    // 2026-07-27: Die Stimmwahl sitzt seit dem Sticky-Umbau in einem eigenen
    // Block und muss dieselbe Sichtbarkeit erben.
    voiceWrap.hidden = playerWrap.hidden;
    renderActive();
  }

  // Buch laden + rendern.
  const load = async (): Promise<void> => {
    body.innerHTML = "";
    const loading = document.createElement("div");
    loading.className = "book-loading";
    loading.textContent = "Lade …";
    body.appendChild(loading);
    try {
      const res = await fetch("/api/biography/book", {
        credentials: "same-origin",
      });
      /*
       * ⚠️ 15.09.2026: Hier stand nur der Wurf, und der Fang unten schrieb ihn
       *    wörtlich auf den Schirm: „Konnte das Buch nicht laden — später
       *    nochmal. (status 402)". Ein neuer Nutzer auf der Stufe frei tippt
       *    „Mein Leben" im Menü an und liest genau das.
       *
       *    Die Biografie ist für ihn gesperrt (server/entitlements.ts,
       *    biography: false), nicht kaputt. „Später nochmal" lädt in eine
       *    Schleife ein, die nie endet — derselbe Befund wie beim Human
       *    Design, dieselbe Antwort.
       *
       * ⚠️ Das Angebot macht der globale 402-Abfang (showUpgradeSheet). Hier
       *    wird nur gesagt, was man sieht.
       */
      if (res.status === 402) {
        chapters = [];
        entities = [];
        timeline = [];
        body.innerHTML = "";
        showError(
          "Mein Leben gehört zu einem größeren Paket. Was du aufnimmst, wird weiter gesammelt — sobald das Paket offen ist, entsteht daraus dein Buch.",
        );
        renderActive();
        return;
      }
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = (await res.json()) as {
        chapters?: BookChapter[];
        entities?: BookEntity[];
        timeline?: BookEvent[];
      };
      chapters = Array.isArray(data.chapters) ? data.chapters : [];
      entities = Array.isArray(data.entities) ? data.entities : [];
      timeline = Array.isArray(data.timeline) ? data.timeline : [];
    } catch (err) {
      chapters = [];
      entities = [];
      timeline = [];
      body.innerHTML = "";
      // Der technische Grund gehört ins Protokoll, nicht auf den Schirm.
      console.warn("[buch] laden gescheitert:", err);
      showError("Das Buch konnte gerade nicht geladen werden — bitte gleich nochmal.");
      renderActive();
      return;
    }
    renderActive();
  };

  // Aktualisieren: Opus komponiert neu (bis ~90s). Button sperren, klare
  // Lade-Anzeige, danach Buch neu laden. Button NIE dauerhaft gesperrt lassen.
  const regenerate = async (deep = false): Promise<void> => {
    if (busy) return;
    busy = true;
    clearError();
    refreshBtn.disabled = true;
    refreshBtn.classList.add("is-busy");
    setRefreshLabel(deep ? "Baut neu …" : "Schreibt …");

    // Langläufer-Overlay im Body (deckt auch den leeren Zustand ab).
    const prevBody = body.innerHTML;
    body.innerHTML = "";
    const longRun = document.createElement("div");
    longRun.className = "book-longrun";
    longRun.innerHTML = '<span class="book-spinner" aria-hidden="true"></span>';
    const lr = document.createElement("p");
    lr.className = "book-longrun-text";
    lr.textContent = deep
      ? "Baue dein Buch komplett neu auf … aus allen deinen Gesprächen. Das dauert etwas länger."
      : // Kein Modellname: das Buch-Modell folgt EINER Einstellung für alle
        // Nutzer — ein Name hier wäre für jeden anderen eine Behauptung.
        "Dein Buch wird geschrieben … das dauert einen Moment.";
    longRun.appendChild(lr);
    body.appendChild(longRun);

    try {
      const res = await fetch("/api/biography/regenerate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: deep ? JSON.stringify({ deep: true }) : "{}",
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = (await res.json()) as { ok?: boolean };
      if (data?.ok === false) throw new Error("nicht aktualisiert");
      // Erfolg → frisch laden (Buch ist gewachsen).
      await load();
    } catch (err) {
      // Fehler → vorherigen Stand wiederherstellen + Inline-Fehler.
      void err;
      if (prevBody) body.innerHTML = prevBody;
      else renderActive();
      showError(
        deep
          ? "Neu-Aufbau gerade nicht möglich — später nochmal."
          : "Konnte gerade nicht aktualisieren — später nochmal.",
      );
    } finally {
      busy = false;
      refreshBtn.disabled = false;
      refreshBtn.classList.remove("is-busy");
      setRefreshLabel(refreshLabel);
    }
  };

  refreshBtn.addEventListener("click", () => void regenerate(false));

  // --- Hörbuch-Player (kapitelweise, über die gemeinsame NarrationEngine) ---
  // Statt EINER großen MP3 lädt der Player das Buch kapitelweise:
  //   1) Klick „Vorlesen" → GET /api/biography/audiobook (Kapitel-Liste +
  //      Gesamtdauer). Kapitel 0 wird sofort gestartet (gecacht → direkt;
  //      sonst kurz „Bereitet Kapitel vor …" + POST narrate {chapter:0}).
  //   2) Während Kapitel i spielt, wird Kapitel i+1 im Hintergrund vorbereitet
  //      (eine Synthese gleichzeitig) → nahtloser Übergang.
  //   3) engine.onEnded → Kapitel i+1 automatisch weiter; am Ende Reset auf 0.
  // Der Orchestrator löst die Kapitel-URL selbst auf (eigener „Bereitet vor"-
  // Zustand, kein Synth-Timeout der Engine) und übergibt der Engine pro Kapitel
  // eine FERTIGE Datei (preResolvedUrl); die Engine spielt nur robust ab und
  // meldet Zustand/Zeit/Ende über Callbacks.
  // Resume merkt sich Kapitel-Index UND Position pro (Version, Stimme, Kapitel).
  let audioVersion: number | null = null; // Buch-Version der geladenen Liste
  let audioChapters: BookAudioChapter[] = [];
  let totalEstSeconds = 0;
  let currentChapter = -1; // gerade geladenes/spielendes Kapitel
  let starting = false; // true, solange Kapitelliste/Kapitel vorbereitet wird (Re-Entrancy-Schutz)
  let prefetchInFlight: number | null = null; // Index der laufenden Vorab-Synthese
  // Client-Timeout für die Kapitel-Synthese (Server cappt pro Chunk bei 60 s;
  // ein Kapitel kann mehrere Chunks haben). Verhindert ein ewiges „Bereitet
  // Kapitel vor …" bei hängendem Provider.
  // 2026-07-02 (Martin): 240s — lange Kapitel brauchen laenger; 90s brach
  // Erstvertonungen ab und der Player wirkte kaputt.
  const CHAPTER_SYNTH_TIMEOUT_MS = 240_000;
  // Vorlauf-Tiefe beim Vorladen: Kapitel 0 ist nur der Titel (~3 s) → mehr
  // Vorlauf, damit der Übergang aufs erste echte Kapitel nicht stockt.
  const PREFETCH_AHEAD = 2;
  const lifeVersion = (): number | null =>
    chapters.find((c) => c.scope === "life")?.version ?? null;

  // --- Resume-Position (Scrubber + Zeit liefert die geteilte Player-Leiste) ---
  let pendingResume: number | null = null; // beim nächsten loadedmetadata setzen
  let lastSave = 0; // throttle für timeupdate-Speicherung

  // Kurze Gesamtdauer-Anzeige: ≥ 1 Min → „~12 Min", sonst „~M:SS".
  const formatTotal = (sec: number): string => {
    if (!Number.isFinite(sec) || sec <= 0) return "";
    if (sec >= 60) return `~${Math.round(sec / 60)} Min`;
    return `~${formatClock(sec)}`;
  };

  // Positions-Resume über den GETEILTEN Helfer (playback-resume) — eine einzige
  // Implementierung für alle Vorlese-Player. Schlüssel pro Kapitel (`bio:c<idx>`),
  // Version bindet an Buch-Version + Stimme: ein neu erzeugtes Buch oder eine
  // andere Stimme beginnt von vorn. Der zuletzt gespielte Kapitel-Index wird
  // SEPARAT gemerkt (kein Positions-, sondern ein Navigations-Zustand), damit
  // Wieder-Öffnen im richtigen Kapitel fortsetzt.
  const posKey = (idx: number): string => `bio:c${idx}`;
  const posVersion = (): string => `${lifeVersion() ?? "x"}:${narratorVoice}`;
  const lastChapterKey = (): string =>
    `bio-narr-chap:${lifeVersion() ?? "x"}:${narratorVoice}`;
  const savePos = (): void => {
    if (currentChapter < 0) return;
    const dur = audio.duration;
    if (
      !Number.isFinite(dur) ||
      audio.currentTime <= 1 ||
      audio.currentTime >= dur - 1
    )
      return;
    saveResume(posKey(currentChapter), posVersion(), audio.currentTime);
    try {
      localStorage.setItem(lastChapterKey(), String(currentChapter));
    } catch {
      /* private mode etc. — Kapitel-Index halt nicht persistieren */
    }
  };
  // Vorwärts-Referenz aus den Schließen-Handlern auf die echte Implementierung.
  saveNarrationPos = savePos;
  const clearPos = (idx: number): void => clearResume(posKey(idx));
  const readPos = (idx: number): number | null =>
    loadResume(posKey(idx), posVersion());
  const readLastChapter = (): number | null => {
    try {
      const raw = localStorage.getItem(lastChapterKey());
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
    } catch {
      return null;
    }
  };

  // Kapitel-Liste (Hörbuch-TOC) rendern: Titel + geschätzte Dauer + Status-Dot,
  // klickbar (springt zum Kapitel). Hebt das aktuelle Kapitel hervor.
  const chapterRows = new Map<number, HTMLButtonElement>();
  const renderChapterList = (): void => {
    chapterRows.clear();
    chaptersList.innerHTML = "";
    // 2026-07-02 (Martin): Box bleibt sichtbar (konsistent) — bei noch
    // leerer TOC nur ein ruhiger Platzhalter statt Verstecken.
    chaptersBox.hidden = false;
    if (audioChapters.length === 0) {
      const ph = document.createElement("div");
      ph.className = "book-chap-placeholder";
      ph.textContent = "Kapitel werden vorbereitet …";
      chaptersList.appendChild(ph);
      return;
    }
    updateChaptersSummary();
    for (const ch of audioChapters) {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "book-chap-row";
      row.classList.toggle("is-active", ch.index === currentChapter);

      const dot = document.createElement("span");
      dot.className = "book-chap-dot";
      dot.classList.toggle("is-loaded", ch.cached);
      dot.setAttribute("aria-hidden", "true");
      row.appendChild(dot);

      const tt = document.createElement("span");
      tt.className = "book-chap-title";
      tt.textContent = ch.title || `Kapitel ${ch.index + 1}`;
      row.appendChild(tt);

      const dur = document.createElement("span");
      dur.className = "book-chap-dur";
      dur.textContent = formatClock(ch.estSeconds);
      row.appendChild(dur);

      row.addEventListener("click", () => {
        chaptersBox.open = false;
        void playChapter(ch.index);
      });
      chapterRows.set(ch.index, row);
      chaptersList.appendChild(row);
    }
  };
  const markChapterCached = (idx: number): void => {
    const ch = audioChapters.find((c) => c.index === idx);
    if (ch) ch.cached = true;
    const row = chapterRows.get(idx);
    row?.querySelector(".book-chap-dot")?.classList.add("is-loaded");
  };
  const highlightChapter = (idx: number): void => {
    for (const [i, row] of chapterRows)
      row.classList.toggle("is-active", i === idx);
    // Blätter-Buttons: vor/zurück nur freischalten, wenn es ein Kapitel gibt.
    bar.setChapterNav(
      audioChapters.some((c) => c.index === idx - 1),
      audioChapters.some((c) => c.index === idx + 1),
    );
    updateChaptersSummary(idx);
  };

  // 2026-07-01 (Martin): Die zugeklappte Kapitel-Zeile zeigt den ECHTEN
  // Titel des laufenden Kapitels ("Sechstes Kapitel: …") + Position/Dauer
  // ("6/19 · ~61 Min") — vorher stand dort missverstaendlich "Kapitel 19".
  const chapterLabelOf = (ch: BookAudioChapter): string =>
    ch.title?.trim() || `Kapitel ${ch.index + 1}`;
  const updateChaptersSummary = (idx = currentChapter): void => {
    const total = formatTotal(totalEstSeconds);
    const cur = audioChapters.find((c) => c.index === idx);
    if (cur) {
      chaptersSummaryLabel.textContent = chapterLabelOf(cur);
      chaptersTotal.textContent = `${idx + 1}/${audioChapters.length}${total ? " · " + total : ""}`;
    } else {
      chaptersSummaryLabel.textContent = audioChapters.length
        ? "Kapitel wählen"
        : "Kapitel";
      chaptersTotal.textContent = audioChapters.length
        ? `${audioChapters.length} Kapitel${total ? " · " + total : ""}`
        : "";
    }
  };

  // --- Kapitel-Mitlauf (Follow-along) ---------------------------------------
  // Während ein Hörbuch-Kapitel vorgelesen wird, hebt der Buch-Tab das aktuelle
  // Kapitel hervor, scrollt es in den Blick und markiert satzweise (per
  // verstrichenem Anteil) den ungefähr passenden Absatz. Reines Frontend ohne
  // Wort-Timings — die Absatz-Annäherung ist absichtlich grob.
  const prefersReducedMotion = (): boolean => {
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  };
  const scrollBehavior = (): ScrollBehavior =>
    prefersReducedMotion() ? "auto" : sanft();
  // Robuster Text-Vergleich für die Titel→Überschrift-Zuordnung.
  const normalizeFollowText = (s: string): string =>
    (s || "").replace(/\s+/g, " ").trim().toLowerCase();

  // Alle Kapitel-Überschriften (## → <h3>) des Lebens-Kapitels in Dokument-
  // Reihenfolge; die führende <h2> (# Titel) repräsentiert Kapitel 0.
  /**
   * 2026-07-27 (Ticket „Player sticky", Teil 3): Wird nach playChapter gesetzt.
   * Ueber diesen Halter ruft renderBook die Anker-Vergabe auf, ohne auf eine
   * spaeter deklarierte Konstante zuzugreifen (kein TDZ-Risiko).
   */
  let vergibKapitelAnker: (() => void) | null = null;

  const lifeArticle = (): HTMLElement | null =>
    body.querySelector<HTMLElement>(".book-chapter--life");
  const lifeHeadings = (): HTMLElement[] => {
    const art = lifeArticle();
    if (!art) return [];
    return Array.from(art.querySelectorAll<HTMLElement>("h3"));
  };
  // Liefert die DOM-Überschrift für einen Hörbuch-Kapitel-Index. Zuordnung per
  // TITEL-Text (robust gegen leere/gedroppte Kapitel). Kapitel 0 (Intro/Titel)
  // → die <h2> (oder der Artikel-Anfang, falls keine <h2>).
  const chapterHeadingFor = (idx: number): HTMLElement | null => {
    const art = lifeArticle();
    if (!art) return null;
    const ch = audioChapters.find((c) => c.index === idx);
    if (idx === 0 || !ch) {
      const h2 = art.querySelector<HTMLElement>("h2");
      if (h2) return h2;
      // Kein Titel-h2 → erstes Block-Element als Anker.
      return (art.firstElementChild as HTMLElement | null) ?? null;
    }
    const want = normalizeFollowText(ch.title);
    if (!want) return null;
    for (const h of lifeHeadings()) {
      if (normalizeFollowText(h.textContent || "") === want) return h;
    }
    return null;
  };
  // Block-Bereich eines Kapitels: die Überschrift + alle Folge-Geschwister bis
  // zur nächsten Kapitel-Überschrift (<h3>) — exklusive.
  const chapterBlocks = (headingEl: HTMLElement | null): HTMLElement[] => {
    if (!headingEl) return [];
    const blocks: HTMLElement[] = [headingEl];
    let el = headingEl.nextElementSibling as HTMLElement | null;
    while (el) {
      if (el.tagName === "H3") break; // nächstes Kapitel beginnt
      blocks.push(el);
      el = el.nextElementSibling as HTMLElement | null;
    }
    return blocks;
  };

  // Aktuell hervorgehobenes Kapitel + dessen Absatz-Blöcke (gecacht fürs
  // timeupdate-Following). lastFollowParaIdx throttelt das Absatz-Scrollen.
  let followBlocks: HTMLElement[] = [];
  let followParas: HTMLElement[] = [];
  let lastFollowParaIdx = -1;

  const clearFollow = (): void => {
    const art = lifeArticle();
    const root: ParentNode = art ?? body;
    root
      .querySelectorAll(".book-follow-chapter")
      .forEach((el) => el.classList.remove("book-follow-chapter"));
    root
      .querySelectorAll(".book-follow-para")
      .forEach((el) => el.classList.remove("book-follow-para"));
    followBlocks = [];
    followParas = [];
    lastFollowParaIdx = -1;
  };

  // Hebt das laufende Kapitel hervor und scrollt es in den Blick (nur im
  // Buch-Tab). Merkt sich die Blöcke + Absätze fürs Absatz-Following.
  const followChapter = (idx: number): void => {
    if (activeTab !== "buch") {
      // Tab nicht sichtbar → nur Zustand zurücksetzen, beim Zurückkehren neu.
      followBlocks = [];
      followParas = [];
      lastFollowParaIdx = -1;
      return;
    }
    clearFollow();
    if (idx < 0) return;
    const headingEl = chapterHeadingFor(idx);
    if (!headingEl) return;
    headingEl.classList.add("book-follow-chapter");
    followBlocks = chapterBlocks(headingEl);
    followParas = followBlocks.filter((el) => el.tagName === "P");
    lastFollowParaIdx = -1;
    // Das Kapitel wechselt von selbst (Wiedergabe): nie gegen den Finger oder ein Nachlaufen (lib/scroll-ruhe.ts).
    if (nutzerScrolltGerade()) return;
    try {
      headingEl.scrollIntoView({ block: "start", behavior: scrollBehavior() });
    } catch {
      headingEl.scrollIntoView();
    }
  };

  // Absatz-Annäherung im timeupdate: per verstrichenem Anteil den passenden
  // Absatz markieren; scrollt nur bei Absatz-Wechsel (Throttle gegen Jitter).
  const followParagraph = (): void => {
    if (activeTab !== "buch") return;
    if (followParas.length === 0) return;
    const dur = audio.duration;
    if (!Number.isFinite(dur) || dur <= 0) return;
    const frac = audio.currentTime / dur;
    const n = followParas.length;
    const active = Math.max(0, Math.min(n - 1, Math.floor(frac * n)));
    if (active === lastFollowParaIdx) return;
    lastFollowParaIdx = active;
    followParas.forEach((p, i) =>
      p.classList.toggle("book-follow-para", i === active),
    );
    const para = followParas[active];
    if (!para) return;
    // Nur scrollen, wenn der Absatz nicht im sichtbaren Bereich liegt.
    const bRect = body.getBoundingClientRect();
    const pRect = para.getBoundingClientRect();
    const visible = pRect.top >= bRect.top && pRect.bottom <= bRect.bottom;
    // Hintergrundereignis (timeupdate): nie gegen den Finger oder ein Nachlaufen (lib/scroll-ruhe.ts).
    if (!visible && !nutzerScrolltGerade()) {
      try {
        para.scrollIntoView({ block: "center", behavior: scrollBehavior() });
      } catch {
        para.scrollIntoView();
      }
    }
  };

  // Beim (Neu-)Render / Tab-Rückkehr die Hervorhebung wieder anwenden, sofern
  // gerade ein Kapitel geladen ist und der Buch-Tab aktiv ist.
  reapplyFollow = (): void => {
    if (activeTab !== "buch") return;
    if (currentChapter < 0 || !audio.src) return;
    followChapter(currentChapter);
  };

  // Player zurück auf Start + verstecken (bei Stimmwechsel / Reset).
  const resetPlayer = (): void => {
    clearFollow();
    // Laufende Kapitel-Auflösungen (Wiedergabe + Prefetch + Vor-Wärmen) abbrechen
    // UND alle in-flight Synthesen killen, sonst setzt eine späte Antwort der
    // ALTEN Stimme nachträglich eine (jetzt falsche, weil voice-getaggte) URL.
    resolveAbort?.abort();
    resolveAbort = null;
    prefetchAbort?.abort();
    prefetchAbort = null;
    prewarmAbort?.abort();
    prewarmAbort = null;
    for (const ctl of inflightChapterCtl.values()) ctl.abort();
    inflightChapterUrl.clear();
    inflightChapterCtl.clear();
    pendingResume = null;
    bar.setProgress(0, 0);
    bar.setTotal("");
    bar.setState("idle", true);
    audioChapters = [];
    audioVersion = null;
    totalEstSeconds = 0;
    currentChapter = -1;
    prefetchInFlight = null;
    // Vor-Wärm-Marke löschen → die neue Stimme/Version wird neu vor-gewärmt.
    prewarmedKey = null;
    chaptersBox.hidden = true;
    chaptersList.innerHTML = "";
    chapterRows.clear();
  };

  // Holt NUR die Hörbuch-Kapitelliste in den State (kein UI). Geteilt von
  // loadAudiobook (mit UI) und dem stillen Vor-Wärmen (ohne UI). Liefert false
  // bei Fehler / leerem Buch. Bei abgebrochenem `signal` (Reset/Stimmwechsel
  // während des stillen Vor-Wärmens) wird der State NICHT mehr angefasst — sonst
  // überschriebe eine späte Antwort der ALTEN Stimme die frisch gesetzte Liste.
  const fetchAudiobookData = async (signal?: AbortSignal): Promise<boolean> => {
    try {
      const res = await fetch(
        `/api/biography/audiobook?voice=${encodeURIComponent(narratorVoice)}`,
        { credentials: "same-origin", signal },
      );
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = (await res.json()) as {
        version?: number;
        totalEstSeconds?: number;
        chapters?: BookAudioChapter[];
      };
      if (signal?.aborted) return false; // nach Abbruch nichts mehr setzen
      audioChapters = Array.isArray(data.chapters) ? data.chapters : [];
      // 2026-07-27: zweiter Anlauf fuer die Kapitel-Anker — jetzt ist bekannt,
      // welche Kapitel es ueberhaupt gibt.
      vergibKapitelAnker?.();
      audioVersion =
        typeof data.version === "number" ? data.version : lifeVersion();
      totalEstSeconds =
        typeof data.totalEstSeconds === "number" ? data.totalEstSeconds : 0;
      return audioChapters.length > 0;
    } catch {
      // Bei Abbruch die (evtl. vom Reset bereits geleerte) Liste NICHT anrühren.
      if (signal?.aborted) return false;
      audioChapters = [];
      return false;
    }
  };

  // Zeigt Kapitelliste + Gesamtdauer in der Player-Leiste (das sichtbare Pendant
  // zu fetchAudiobookData). Idempotent — auch sicher, wenn schon vor-geladen.
  const renderAudiobookUi = (): void => {
    renderChapterList();
    // 2026-07-01 (Martin): Gesamtdauer lebt in der Kapitel-Zeile — die
    // doppelte Pille in der Leiste entfaellt.
    bar.setTotal("");
  };

  // Lädt die Hörbuch-Kapitelliste für die aktuelle Stimme (einmal pro
  // Vorlese-Sitzung) UND zeigt sie an. Liefert false bei Fehler / leerem Buch.
  const loadAudiobook = async (): Promise<boolean> => {
    const ok = await fetchAudiobookData();
    renderAudiobookUi();
    return ok;
  };

  // Stilles Vor-Wärmen: Sobald der Buch-Tab offen ist, im Hintergrund die ersten
  // Kapitel synthetisieren — so startet „Vorlesen" sofort statt erst ~20 s zu
  // rendern. Best-Effort, leise, ohne UI; läuft NUR im Leerlauf und einmal pro
  // (Buch-Version, Stimme). Bei Fehler darf es später erneut versuchen.
  let prewarmedKey: string | null = null;
  let prewarmAbort: AbortController | null = null;
  const prewarmAudiobook = async (): Promise<void> => {
    if (activeTab !== "buch") return;
    if (starting || engine.getState() !== "idle") return; // Sitzung läuft schon
    const key = `${lifeVersion() ?? "x"}:${narratorVoice}`;
    if (prewarmedKey === key) return;
    prewarmedKey = key;
    // Eigenes Abbruchsignal: resetPlayer (Stimmwechsel/Schließen) bricht das
    // stille Vor-Wärmen ab, damit weder eine späte Listen-Antwort noch eine
    // Synthese der ALTEN Stimme den frischen Zustand überschreibt.
    const ab = new AbortController();
    prewarmAbort = ab;
    try {
      if (audioChapters.length === 0 || audioVersion !== lifeVersion()) {
        const ok = await fetchAudiobookData(ab.signal); // still — keine Liste
        if (!ok || ab.signal.aborted) {
          // prewarmedKey nur bei ECHTEM Fehler löschen (Retry erlauben). Bei
          // Abbruch NICHT — resetPlayer hat ihn bereits geleert bzw. die neue
          // Stimme/Version bekommt ohnehin einen neuen key.
          if (!ab.signal.aborted) prewarmedKey = null;
          return;
        }
      }
      // 2026-07-03 (Martin): Kapitelliste SOFORT beim Oeffnen zeigen (ohne
      // Wiedergabe) — vorher blieb das Dropdown leer bis zum Player-Start.
      if (!ab.signal.aborted && audioChapters.length > 0) renderChapterList();
      // Titel (Kapitel 0, winzig) + erstes echtes Kapitel vorbereiten. Abbrechen,
      // sobald der Tab gewechselt wird, eine echte Sitzung startet oder reset.
      for (const idx of [0, 1]) {
        if (ab.signal.aborted) return;
        if (activeTab !== "buch" || starting) break;
        if (engine.getState() !== "idle") break;
        const ch = audioChapters.find((c) => c.index === idx);
        if (ch && !ch.url) await ensureChapterUrl(idx, ab.signal);
      }
    } catch {
      if (!ab.signal.aborted) prewarmedKey = null;
    } finally {
      if (prewarmAbort === ab) prewarmAbort = null;
    }
  };
  // Forward-Ref belegen (Aufruf aus dem Buch-Render, s. oben).
  maybePrewarmAudiobook = (): void => {
    void prewarmAudiobook();
  };

  // Laufende Kapitel-Synthesen (Index → Promise / AbortController). Dedup:
  // Vorwärmen, Wiedergabe UND Prefetch können gleichzeitig dasselbe Kapitel
  // anfragen — wir lösen dann nur EIN POST narrate aus und teilen das Ergebnis.
  // resetPlayer (Stimmwechsel/Schließen) bricht alle ab und leert die Maps, so
  // dass nie eine Synthese der ALTEN Stimme nachträglich eine URL setzt.
  const inflightChapterUrl = new Map<number, Promise<string | null>>();
  const inflightChapterCtl = new Map<number, AbortController>();

  // Stellt die URL eines Kapitels sicher: gecacht → sofort; sonst synthetisieren
  // (POST narrate {chapter}). Liefert die URL oder null bei Fehler/Abbruch. Der
  // optionale `signal` bricht die EIGENE Synthese ab; ein Timeout begrenzt sie
  // zusätzlich, damit „Bereitet Kapitel vor …" nie ewig steht.
  const ensureChapterUrl = (
    idx: number,
    signal?: AbortSignal,
  ): Promise<string | null> => {
    const ch = audioChapters.find((c) => c.index === idx);
    if (!ch) return Promise.resolve(null);
    if (ch.url) return Promise.resolve(ch.url);
    // Läuft schon eine Synthese für dieses Kapitel → dieselbe Promise teilen.
    const shared = inflightChapterUrl.get(idx);
    if (shared) return shared;

    // Timeout + externes Abbruchsignal zu EINEM Signal bündeln. Die erste
    // Synthese dauert ~15–25 s (Chirp3-HD), danach ist das Kapitel gecacht.
    const ctl = new AbortController();
    const onExternalAbort = (): void => ctl.abort();
    if (signal) {
      if (signal.aborted) ctl.abort();
      else signal.addEventListener("abort", onExternalAbort, { once: true });
    }
    const timer = window.setTimeout(
      () => ctl.abort(),
      CHAPTER_SYNTH_TIMEOUT_MS,
    );
    // Stimme zum Anfrage-Zeitpunkt festhalten — die URL ist voice-getaggt, also
    // darf das Ergebnis NUR übernommen werden, wenn die Stimme bis zum Schreiben
    // unverändert blieb (sonst landet eine alte-Stimme-URL an der neuen Liste).
    const voiceAtStart = narratorVoice;
    const run = async (): Promise<string | null> => {
      try {
        const res = await fetch("/api/biography/narrate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            voiceId: voiceAtStart,
            chapter: idx,
            googleVoice: googleHdFallbackForVoice(voiceAtStart),
          }),
          credentials: "same-origin",
          signal: ctl.signal,
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as { url?: string };
        if (!data.url) throw new Error("keine Audio-URL");
        // Nur übernehmen, wenn das Kapitel noch in der AKTUELLEN Liste steht UND
        // die Stimme dieselbe ist (Stimmwechsel/Reset könnte beides geändert
        // haben) — sonst verwerfen, damit nie eine URL der alten Stimme an der
        // neuen Liste landet.
        const cur = audioChapters.find((c) => c.index === idx);
        if (cur && narratorVoice === voiceAtStart) {
          cur.url = data.url;
          markChapterCached(idx);
        }
        return data.url;
      } catch {
        return null;
      } finally {
        window.clearTimeout(timer);
        signal?.removeEventListener("abort", onExternalAbort);
        inflightChapterUrl.delete(idx);
        inflightChapterCtl.delete(idx);
      }
    };
    const p = run();
    inflightChapterUrl.set(idx, p);
    inflightChapterCtl.set(idx, ctl);
    return p;
  };

  // Progressives Vorladen: bereitet die NÄCHSTEN Kapitel im Hintergrund vor,
  // damit der Auto-Übergang sofort spielt. Nur eine Synthese gleichzeitig
  // (sequenziell verkettet), `remaining` begrenzt die Vorlauf-Tiefe. Schon
  // gecachte Kapitel werden übersprungen und der Vorlauf rückt eins weiter —
  // so deckt der Vorlauf das kurze Titel-Kapitel 0 (~3 s) zuverlässig ab.
  const prefetchAhead = (
    afterIdx: number,
    remaining = PREFETCH_AHEAD,
  ): void => {
    if (remaining <= 0) return;
    const next = audioChapters.find((c) => c.index === afterIdx + 1);
    if (!next) return; // letztes Kapitel
    if (next.url) {
      prefetchAhead(next.index, remaining - 1); // schon da → eins weiter vorladen
      return;
    }
    if (prefetchInFlight === next.index) return; // läuft schon
    prefetchInFlight = next.index;
    prefetchAbort = new AbortController();
    void ensureChapterUrl(next.index, prefetchAbort.signal).finally(() => {
      if (prefetchInFlight === next.index) prefetchInFlight = null;
      // Kette: nächstes Kapitel im Vorlauf-Fenster vorbereiten.
      prefetchAhead(next.index, remaining - 1);
    });
  };

  // Spielt ein Kapitel über die gemeinsame Engine: URL sicherstellen (gecacht/
  // prefetched → sofort, sonst „Bereitet Kapitel vor …" + POST narrate), dann
  // der Engine die FERTIGE Datei als preResolvedUrl übergeben — die Engine
  // übernimmt das robuste Abspielen (Puffer/Stall/Abbruch/Ende) und meldet
  // Zustand/Zeit/Ende über onState/onTime/onEnded. So bleibt der (potenziell
  // lange) Kapitel-Render im Orchestrator (kein Engine-Synth-Timeout).
  // `resume` führt an die gespeicherte Position weiter (nur beim Sitzungsstart);
  // Kapitel-Sprung / Auto-Advance starten jeweils am Kapitelanfang.
  /**
   * 2026-07-27 (Martin: „an das entsprechende Kapitel scrollen und dort Play
   * starten"): Gibt jeder Kapitel-Ueberschrift im Text einen eigenen
   * Abspiel-Knopf.
   *
   * Die Zuordnung laeuft ueber chapterHeadingFor(idx), also ueber den
   * TITELTEXT — NICHT ueber das Durchzaehlen der Ueberschriften. Grund: bei
   * der Ton-Zerlegung fallen leere Kapitel heraus und die uebrigen werden neu
   * nummeriert (server/biography-narrate.ts). Ein Frontend-Zaehler liefe damit
   * frueher oder spaeter auseinander und wuerde das FALSCHE Kapitel abspielen.
   *
   * Idempotent: schon vergebene Knoepfe werden nicht verdoppelt.
   */
  const kapitelAnkerVergeben = (): void => {
    if (audioChapters.length === 0) return;
    for (const ch of audioChapters) {
      const h = chapterHeadingFor(ch.index);
      if (!h) continue;
      h.dataset.chapterIndex = String(ch.index);
      if (h.querySelector(".book-chapter-play")) continue;
      const b = document.createElement("button");
      b.type = "button";
      b.className = "book-chapter-play";
      b.title = `„${ch.title}" ab hier vorlesen`;
      b.setAttribute("aria-label", `Kapitel „${ch.title}" ab hier vorlesen`);
      // Symbol per DOM bauen statt per innerHTML — der Inhalt ist zwar fest
      // verdrahtet, aber das Muster soll gar nicht erst einreissen.
      const NS = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(NS, "svg");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("width", "16");
      svg.setAttribute("height", "16");
      svg.setAttribute("aria-hidden", "true");
      const pfad = document.createElementNS(NS, "path");
      pfad.setAttribute("fill", "currentColor");
      pfad.setAttribute("d", "M8 5v14l11-7z");
      svg.appendChild(pfad);
      b.appendChild(svg);
      b.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        void playChapter(ch.index);
      });
      h.prepend(b);
    }
  };
  // Halter fuellen — ab hier duerfen renderBook und das Laden des
  // Hoerbuch-Verzeichnisses die Anker vergeben.
  vergibKapitelAnker = kapitelAnkerVergeben;

  const playChapter = async (idx: number, resume = false): Promise<void> => {
    const ch = audioChapters.find((c) => c.index === idx);
    if (!ch) return;
    // 2026-07-02 (Martin): Kapitelwechsel UNTERBRICHT — nur ein Doppel-Tap
    // aufs SELBE Kapitel wird verschluckt. Laufendes Audio sofort anhalten;
    // eine laufende Vorbereitung bricht unten via resolveAbort ab.
    if (starting && idx === currentChapter) return;
    audio.pause();
    starting = true;
    clearError();
    // Frische Auflösung: eine evtl. noch laufende abbrechen, damit ein
    // Stimmwechsel/Schließen mitten in der Synthese nie nachträglich die ALTE
    // Vertonung abspielt. `myAbort` markiert DIESEN Aufruf als den aktuellen.
    resolveAbort?.abort();
    const myAbort = new AbortController();
    resolveAbort = myAbort;
    currentChapter = idx;
    highlightChapter(idx);
    // Mitlauf sichtbar machen: auf den Buch-Tab wechseln (falls woanders), dann
    // das laufende Kapitel hervorheben + in den Blick scrollen.
    if (activeTab !== "buch") setTab("buch");
    followChapter(idx);
    // Resume-Position nur beim Wieder-Aufnehmen (Sitzungsstart), nicht beim
    // Springen/Auto-Advance — dort jeweils am Kapitelanfang. Wird im
    // loadedmetadata-Handler angewandt, sobald die Dauer bekannt ist.
    const saved = resume ? readPos(idx) : null;
    pendingResume = saved != null && saved > 2 ? saved : null;
    // URL sicherstellen: gecacht/prefetched → sofort, sonst synthetisieren.
    let url = ch.url ?? null;
    if (!url) {
      // Erstvertonung braucht einen Moment (Chirp3-HD), danach ist das Kapitel
      // gecacht und kommt sofort — das dem Nutzer ehrlich sagen, statt nur stumm
      // zu spinnen oder fälschlich „sofort" zu suggerieren.
      bar.setBusy("Vertont …");
      narrHint.hidden = false;
      url = await ensureChapterUrl(idx, myAbort.signal);
      narrHint.hidden = true;
    }
    starting = false;
    // Inzwischen abgebrochen (Stimmwechsel/Schließen) oder von einem neueren
    // Aufruf überholt? Dann diese veraltete Wiedergabe verwerfen — sonst spielte
    // die alte Stimme nach.
    if (myAbort.signal.aborted || resolveAbort !== myAbort) return;
    if (!url) {
      bar.setState(currentChapter >= 0 ? "paused" : "idle", true);
      showError("Konnte das Kapitel gerade nicht vorlesen — später nochmal.");
      return;
    }
    // Fertige Datei an die Engine — sie spielt robust ab. resolveUrl ist nur ein
    // defensiver Fallback und wird bei gesetzter preResolvedUrl nicht aufgerufen.
    engine.loadAndPlay(
      {
        async resolveUrl(sig: AbortSignal): Promise<string> {
          const u = await ensureChapterUrl(idx, sig);
          if (!u) throw new Error("kapitel ohne url");
          return u;
        },
      },
      url,
    );
    // Sobald dieses Kapitel läuft, die nächsten im Hintergrund vorbereiten.
    prefetchAhead(idx);
  };

  audio.addEventListener("loadedmetadata", () => {
    const dur = audio.duration;
    if (!Number.isFinite(dur) || dur <= 0) return;
    // Resume-Position anwenden, sobald die Dauer bekannt ist.
    if (pendingResume != null && pendingResume > 2 && pendingResume < dur - 2) {
      try {
        audio.currentTime = pendingResume;
      } catch {
        /* manche Browser erlauben das erst kurz später — egal */
      }
    }
    pendingResume = null;
    if (!bar.isSeeking()) bar.setProgress(audio.currentTime, dur);
  });
  // Fortschritt kommt aus der Engine (das timeupdate-Event ist engine-intern):
  // die geteilte Leiste aktualisiert Slider + Zeit; dazu Absatz-Mitlauf + das
  // gedrosselte Sichern der Position. Seek/Scrub kapselt die Leiste selbst.
  engine.onTime = (cur: number, dur: number): void => {
    bar.setProgress(cur, dur);
    // Absatz-Mitlauf (nur wenn nicht gescrubbt wird und Buch-Tab aktiv).
    if (!bar.isSeeking()) followParagraph();
    const now = Date.now();
    if (now - lastSave > 3000) {
      lastSave = now;
      savePos();
    }
  };

  // Die geteilte Leiste folgt dem Engine-Zustand (ein Player, eine Wahrheit);
  // das Buch ergänzt nur seine Eigenheiten: Position sichern bei Pause, Inline-
  // Fehlerzeile bei „error". Die Synthese-Phase setzt der Orchestrator selbst
  // über bar.setBusy — die Engine ist dort noch idle.
  engine.onState = (state: NarrationState): void => {
    if (state === "paused") savePos();
    if (state === "error")
      showError("Wiedergabe unterbrochen — tippe zum Fortsetzen.");
    bar.setState(state, true);
  };
  // Kapitel zu Ende → nahtlos zum nächsten; am letzten Kapitel stoppen + Reset.
  engine.onEnded = (): void => {
    clearPos(currentChapter);
    const next = audioChapters.find((c) => c.index === currentChapter + 1);
    if (next) {
      void playChapter(next.index);
      return;
    }
    // Buch zu Ende: zurück auf Kapitel 0 (Anfang), Player auf Null.
    currentChapter = audioChapters.length ? audioChapters[0].index : -1;
    highlightChapter(currentChapter);
    clearFollow();
    bar.setState("idle", true);
    bar.setProgress(0, 0);
    try {
      localStorage.removeItem(lastChapterKey());
    } catch {
      /* ignore */
    }
  };

  // Startet eine frische Vorlese-Sitzung: Hörbuch ggf. laden (Buch gewachsen /
  // Stimme neu), dann Startkapitel (zuletzt gehörtes, sonst 0) mit Resume.
  const startNarration = async (): Promise<void> => {
    if (starting) return;
    clearError();
    // Kapitelliste fehlt oder veraltet (Buch gewachsen / Stimme neu) → laden.
    if (audioChapters.length === 0 || audioVersion !== lifeVersion()) {
      starting = true;
      bar.setBusy("Bereitet vor …");
      const ok = await loadAudiobook();
      starting = false;
      if (!ok) {
        bar.setState("idle", true);
        showError("Konnte das Hörbuch gerade nicht laden — später nochmal.");
        return;
      }
    } else {
      // Schon (evtl. still vor-gewärmt) im State → Kapitelliste + Gesamtdauer
      // jetzt sichtbar machen (fetchAudiobookData zeigt keine UI).
      renderAudiobookUi();
    }
    // Startkapitel: zuletzt gehörtes (falls vorhanden), sonst 0. Resume an
    // die gespeicherte Position — der schnelle Start braucht nur dieses eine.
    const last = readLastChapter();
    const startIdx =
      last != null && audioChapters.some((c) => c.index === last)
        ? last
        : (audioChapters[0]?.index ?? 0);
    await playChapter(startIdx, true);
  };
  // Haupt-Aktion „Vorlesen": Läuft/puffert/pausiert die Engine → einfach
  // Play/Pause umschalten (kein Neu-Laden). Sonst (idle/Fehler/frisch) → eine
  // neue Vorlese-Sitzung starten.
  const onNarrate = (): void => {
    const st = engine.getState();
    if (st === "playing" || st === "stalled" || st === "paused") {
      engine.togglePlayPause();
      return;
    }
    if (engine.isBusy()) return; // Engine beschafft gerade
    void startNarration();
  };

  // (Stimmwechsel wird vom geteilten Stimm-Picker via onChange erledigt — siehe
  // buildVoicePicker oben: persistieren, laufende Lesung stoppen, Hörbuch reset.)

  // Default-Tab markieren, dann initial laden.
  setTab("buch");
  await load();
}

// Pre.575: Human Design — eigenes Lese-Sheet (Bodygraph + Deutung), spiegelt
// openBookSheet(). Kein Tab/View, sondern ein Overlay-Sheet. Lädt sofort den
// Bodygraph (schnell), dann die LLM-Auswertung (langsam, gecacht).
// Modul-weiter Cache-Buster für den Bodygraph (statt Math.random/Date.now in
// der URL — ein einfacher Zähler reicht zum Umgehen des Browser-Cache).
let hdImgCacheBuster = 0;
// Aufräum-Funktion des AKTUELL offenen HD-Sheets (Listener + Tooltip + Overlay).
// Wird beim erneuten Öffnen genutzt, um das vorherige Sheet sauber zu schließen.
let hdActiveCleanup: (() => void) | null = null;
// Pre.750: dieselbe Aufräum-Referenz für Essenz + Mein Leben — damit der
// geteilte Deep-Link-Router (page-routes) sie bei „Zurück" sauber schließt.
let essenceActiveCleanup: (() => void) | null = null;
let bookActiveCleanup: (() => void) | null = null;

// --- Zentren-Tooltips (Hover-Info auf den Bodygraph-Zentren) ----------------
interface HdCenterInfo {
  de: string;
  fn: string;
  defined: string;
  open: string;
}
const HD_CENTER_INFO: Record<string, HdCenterInfo> = {
  Head: {
    de: "Kopf",
    fn: "Mentaler Druck, Inspiration, Fragen.",
    defined: "Feste Art zu inspirieren und Fragen zu stellen.",
    open: "Nimmt mentalen Druck anderer auf — denkt über viele Fragen nach.",
  },
  Ajna: {
    de: "Ajna · Verstand",
    fn: "Konzepte, Denkweise, Meinungen.",
    defined: "Feste Denkweise, sichere Konzepte.",
    open: "Offen für viele Sichtweisen — Weisheit über das Denken.",
  },
  Throat: {
    de: "Kehle",
    fn: "Kommunikation, Handeln, Manifestation.",
    defined: "Verlässlicher Ausdruck & Manifestation.",
    open: "Anpassungsfähige Stimme — spricht, wenn eingeladen.",
  },
  Self: {
    de: "G-Zentrum · Identität",
    fn: "Identität, Liebe, Richtung im Leben.",
    defined: "Feste Identität & Richtung.",
    open: "Sucht Richtung über Umgebung & Menschen.",
  },
  Heart: {
    de: "Herz · Ego",
    fn: "Willenskraft, Selbstwert, Material.",
    defined: "Verlässliche Willenskraft — kann versprechen.",
    open: "Muss sich nichts beweisen — kein fester Wille.",
  },
  Sacral: {
    de: "Sakral",
    fn: "Lebens- & Arbeitsenergie, Sexualität.",
    defined: "Nachhaltige Arbeits-/Lebenskraft (Generator).",
    open: "Kennt kein 'genug' — Energie kommt von außen.",
  },
  Spleen: {
    de: "Milz",
    fn: "Intuition, Immunsystem, Wohlbefinden.",
    defined: "Stetige Intuition & Gesundheit.",
    open: "Feine Wahrnehmung — hält evtl. an Ungesundem fest.",
  },
  "Solar Plexus": {
    de: "Solarplexus · Emotion",
    fn: "Emotionen, Stimmungswellen.",
    defined: "Eigene Gefühlswellen — Autorität: Klarheit über Zeit.",
    open: "Nimmt Emotionen anderer auf & verstärkt sie.",
  },
  Root: {
    de: "Wurzel",
    fn: "Druck, Stress, Antrieb zum Handeln.",
    defined: "Eigener Stress-Rhythmus, fester Antrieb.",
    open: "Spürt Druck von außen — Eile, Dinge loszuwerden.",
  },
};

let hdCenterTipEl: HTMLDivElement | null = null;
function hdCenterTooltipEl(): HTMLDivElement {
  if (hdCenterTipEl && document.body.contains(hdCenterTipEl))
    return hdCenterTipEl;
  const el = document.createElement("div");
  el.className = "hd-center-tip";
  el.hidden = true;
  document.body.appendChild(el);
  hdCenterTipEl = el;
  return el;
}
function positionHdTip(tip: HTMLElement, ev: MouseEvent): void {
  const pad = 16;
  const r = tip.getBoundingClientRect();
  let x = ev.clientX + pad;
  let y = ev.clientY + pad;
  if (x + r.width > window.innerWidth - 8) x = ev.clientX - r.width - pad;
  if (y + r.height > window.innerHeight - 8) y = ev.clientY - r.height - pad;
  tip.style.left = `${Math.max(8, x)}px`;
  tip.style.top = `${Math.max(8, y)}px`;
}
/** Hängt Hover-Tooltips an alle `.hd-center`-Elemente eines Inline-SVG. */
function setupCenterTooltips(container: HTMLElement): void {
  const tip = hdCenterTooltipEl();
  const centers = container.querySelectorAll<SVGElement>(".hd-center");
  centers.forEach((el) => {
    el.addEventListener("mouseenter", (ev) => {
      const name = el.getAttribute("data-center") ?? "";
      const info = HD_CENTER_INFO[name];
      if (!info) return;
      const defined = el.getAttribute("data-defined") === "1";
      const badge = defined ? "definiert" : "offen";
      const stateText = defined ? info.defined : info.open;
      tip.innerHTML =
        `<div class="hd-tip-head"><span class="hd-tip-name">${info.de}</span>` +
        `<span class="hd-tip-badge hd-tip-badge--${defined ? "def" : "open"}">${badge}</span></div>` +
        `<div class="hd-tip-fn">${info.fn}</div>` +
        `<div class="hd-tip-state">${stateText}</div>`;
      tip.hidden = false;
      positionHdTip(tip, ev);
    });
    el.addEventListener("mousemove", (ev) => positionHdTip(tip, ev));
    el.addEventListener("mouseleave", () => {
      tip.hidden = true;
    });
  });
}

// --- Planeten-Tooltips (Design/Persönlichkeit-Spalten) --------------------
// Erklärt beim Hover/Tap: welcher Planet, Design vs. Persönlichkeit, und was
// „Tor.Linie" bedeutet (z.B. „8₆" = Tor 8, Linie 6).
const HD_PLANET_INFO: Record<string, { de: string; role: string }> = {
  Sun: {
    de: "Sonne",
    role: "Dein Kern-Ausdruck — rund 70 % deines Wesens. Was du ausstrahlst.",
  },
  Earth: {
    de: "Erde",
    role: "Erdung und Balance zur Sonne. Was dich trägt und stabilisiert.",
  },
  "North Node": {
    de: "Nordknoten",
    role: "Lebensrichtung der zweiten Lebenshälfte — wohin du gehst.",
  },
  "South Node": {
    de: "Südknoten",
    role: "Herkunft, erste Lebenshälfte — woher du kommst.",
  },
  Moon: { de: "Mond", role: "Antrieb — was dich nährt und in Bewegung hält." },
  Mercury: {
    de: "Merkur",
    role: "Kommunikation und Denken — was du mitteilen willst.",
  },
  Venus: { de: "Venus", role: "Werte, Liebe und Moral — was dich anzieht." },
  Mars: {
    de: "Mars",
    role: "Unreife, treibende Energie — besonders in der Jugend.",
  },
  Jupiter: {
    de: "Jupiter",
    role: "Segen, Gesetz und Expansion — wo du Fülle und Schutz findest.",
  },
  Saturn: {
    de: "Saturn",
    role: "Disziplin, Struktur und Lektionen — wo du wächst.",
  },
  Uranus: {
    de: "Uranus",
    role: "Das Ungewöhnliche, Wandel und Individualität.",
  },
  Neptune: {
    de: "Neptun",
    role: "Spiritualität, Illusion und das Verborgene.",
  },
  Pluto: {
    de: "Pluto",
    role: "Wahrheit und Transformation — Tod und Wiedergeburt.",
  },
};
const HD_SIDE_INFO: Record<string, { de: string; expl: string }> = {
  design: {
    de: "Design (rot)",
    expl: "Unbewusst — rund 88 Tage vor der Geburt berechnet. Dein Körper und genetisches Erbe: wer du bist, ohne es bewusst zu wissen.",
  },
  personality: {
    de: "Persönlichkeit (schwarz)",
    expl: "Bewusst — auf den Geburtsmoment berechnet. Dein Verstand und Selbstbild: wer du zu sein glaubst.",
  },
};
const HD_LINE_NAMES: Record<number, string> = {
  1: "Forscher (Fundament)",
  2: "Einsiedler (natürliches Talent)",
  3: "Märtyrer (Versuch & Irrtum)",
  4: "Opportunist (Netzwerk/Beziehungen)",
  5: "Ketzer (praktische Lösung)",
  6: "Rollenvorbild (ab ~50 weise)",
};
// Standard-HD-Keynotes der 64 Tore (kurz). Bei Bedarf einzeln korrigierbar.
const HD_GATE_NAMES: Record<number, string> = {
  1: "Selbst-Ausdruck",
  2: "Richtung des Selbst",
  3: "Ordnung / Anfang",
  4: "Formulierung",
  5: "Feste Muster / Rhythmus",
  6: "Reibung / Intimität",
  7: "Die Rolle des Selbst",
  8: "Beitrag",
  9: "Fokus / Detail",
  10: "Verhalten des Selbst",
  11: "Ideen",
  12: "Vorsicht / Stillstand",
  13: "Der Zuhörer",
  14: "Macht / Schlüssel",
  15: "Extreme / Bescheidenheit",
  16: "Fähigkeiten / Begeisterung",
  17: "Meinungen",
  18: "Korrektur",
  19: "Bedürfnis / Annäherung",
  20: "Das Jetzt / Gegenwart",
  21: "Der Jäger / Kontrolle",
  22: "Offenheit / Anmut",
  23: "Assimilation",
  24: "Rationalisierung",
  25: "Geist des Selbst / Unschuld",
  26: "Der Egoist",
  27: "Fürsorge",
  28: "Der Spieler / Kampf",
  29: "Hingabe / Engagement",
  30: "Gefühle / Schicksal",
  31: "Führung / Einfluss",
  32: "Kontinuität / Dauer",
  33: "Rückzug / Privatheit",
  34: "Macht / Kraft",
  35: "Wandel / Fortschritt",
  36: "Krise",
  37: "Freundschaft / Familie",
  38: "Der Kämpfer",
  39: "Provokation",
  40: "Einsamkeit / Befreiung",
  41: "Kontraktion / Fantasie",
  42: "Wachstum",
  43: "Einsicht / Durchbruch",
  44: "Wachsamkeit / Muster",
  45: "Der Sammler / Herrscher",
  46: "Liebe zum Körper",
  47: "Realisierung",
  48: "Die Tiefe",
  49: "Prinzipien / Revolution",
  50: "Werte",
  51: "Schock / Initiative",
  52: "Stille / Berg",
  53: "Beginn / Entwicklung",
  54: "Ehrgeiz / Aufstieg",
  55: "Geist / Fülle",
  56: "Stimulation / Der Wanderer",
  57: "Intuition",
  58: "Vitalität / Freude",
  59: "Sexualität",
  60: "Akzeptanz / Begrenzung",
  61: "Innere Wahrheit / Mysterium",
  62: "Details",
  63: "Zweifel",
  64: "Verwirrung",
};
// Kanal-Namen (36), Schlüssel „kleineresTor-größeresTor" — Standard-HD-Keynotes.
const HD_CHANNEL_NAMES: Record<string, string> = {
  "1-8": "Inspiration",
  "2-14": "Schlagrhythmus (Schlüsselhüter)",
  "3-60": "Mutation",
  "4-63": "Logik",
  "5-15": "Rhythmus",
  "6-59": "Intimität / Fortpflanzung",
  "7-31": "Alpha (Führung)",
  "9-52": "Konzentration",
  "10-20": "Erwachen",
  "10-34": "Erforschung / Überzeugung",
  "10-57": "Perfektionierte Form (Überleben)",
  "11-56": "Neugier (Ideen suchen)",
  "12-22": "Offenheit",
  "13-33": "Der Zeuge",
  "16-48": "Wellenlänge (Talent)",
  "17-62": "Akzeptanz (Organisation)",
  "18-58": "Wertschätzung (Urteil)",
  "19-49": "Synthese (Empfindsamkeit)",
  "20-34": "Charisma",
  "20-57": "Gehirnwellen (Durchdringung)",
  "21-45": "Geld / Materielles",
  "23-43": "Struktur (Genie zu Freak)",
  "24-61": "Bewusstsein (Denker)",
  "25-51": "Initiation",
  "26-44": "Übertragung (Unternehmer)",
  "27-50": "Bewahrung (Fürsorge)",
  "28-38": "Kampf (Sturheit)",
  "29-46": "Entdeckung (Hingabe)",
  "30-41": "Erkennung (Fantasie)",
  "32-54": "Transformation (Antrieb)",
  "34-57": "Macht / Kraft (Archetyp)",
  "35-36": "Vergänglichkeit (Tausendsassa)",
  "37-40": "Gemeinschaft (Unterhalt)",
  "39-55": "Emotionalität (Launen)",
  "42-53": "Reife (Zyklus)",
  "47-64": "Abstraktion",
};
const HD_COLOR_DESC: Record<string, string> = {
  personality: "schwarz (bewusst)",
  design: "rot (unbewusst)",
  both: "rot+schwarz (beides)",
  none: "—",
};

let hdTipPinned: SVGElement | null = null;
let hdTipDocBound = false;

function buildPlanetTipHtml(el: SVGElement): string | null {
  const planet = el.getAttribute("data-planet") ?? "";
  const sideKey = el.getAttribute("data-side") ?? "";
  const gate = Number(el.getAttribute("data-gate"));
  const line = Number(el.getAttribute("data-line"));
  const p = HD_PLANET_INFO[planet];
  const side = HD_SIDE_INFO[sideKey];
  if (!p || !side) return null;
  const isDesign = sideKey === "design";
  const gateName = HD_GATE_NAMES[gate] ?? "";
  const lineName = HD_LINE_NAMES[line] ?? "";
  return (
    `<div class="hd-tip-head"><span class="hd-tip-name">${p.de}</span>` +
    `<span class="hd-tip-badge hd-tip-badge--${isDesign ? "def" : "open"}">${side.de}</span></div>` +
    `<div class="hd-tip-fn">${p.role}</div>` +
    `<div class="hd-tip-gate"><strong>Tor ${gate}</strong>${gateName ? ` — ${gateName}` : ""} · <strong>Linie ${line}</strong>${lineName ? ` — ${lineName}` : ""}</div>` +
    `<div class="hd-tip-state">${side.expl}</div>`
  );
}

function buildGateTipHtml(el: SVGElement): string | null {
  const gate = Number(el.getAttribute("data-gate"));
  const a = el.getAttribute("data-activation") ?? "none";
  if (!gate) return null;
  const name = HD_GATE_NAMES[gate] ?? "";
  const active = a !== "none";
  const expl =
    a === "personality"
      ? "Aktiviert durch deine Persönlichkeit (bewusst, schwarz) — ein Planet im Geburtsmoment steht in diesem Tor."
      : a === "design"
        ? "Aktiviert durch dein Design (unbewusst, rot) — ein Planet ~88 Tage vor der Geburt steht in diesem Tor."
        : a === "both"
          ? "Aktiviert in BEIDEN — bewusst (schwarz) UND unbewusst (rot). Darum ist der Punkt halb schwarz, halb rot."
          : "Bei dir nicht aktiviert (graues Ringerl) — kein Planet steht in diesem Tor.";
  return (
    `<div class="hd-tip-head"><span class="hd-tip-name">Tor ${gate}${name ? ` — ${name}` : ""}</span>` +
    `<span class="hd-tip-badge hd-tip-badge--${active ? "def" : "open"}">${active ? "aktiv" : "offen"}</span></div>` +
    `<div class="hd-tip-fn">Ein „Tor" ist eine von 64 Energien (aus dem I-Ging). Es sitzt an einem Zentrum und kann mit einem Gegen-Tor einen Kanal bilden.</div>` +
    `<div class="hd-tip-state">${expl}</div>`
  );
}

function buildChannelTipHtml(el: SVGElement): string | null {
  const g1 = Number(el.getAttribute("data-g1"));
  const g2 = Number(el.getAttribute("data-g2"));
  const a1 = el.getAttribute("data-a1") ?? "none";
  const a2 = el.getAttribute("data-a2") ?? "none";
  if (!g1 || !g2) return null;
  const lo = Math.min(g1, g2);
  const hi = Math.max(g1, g2);
  const name = HD_CHANNEL_NAMES[`${lo}-${hi}`] ?? "";
  const on1 = a1 !== "none";
  const on2 = a2 !== "none";
  const defined = on1 && on2;
  const partial = (on1 || on2) && !defined; // genau ein Tor aktiv = hängendes Tor
  // Badge: definiert (kräftig) / offen (grau). „hängend" ist ein Sonderfall von offen.
  const badge = defined ? "definiert" : partial ? "hängend" : "offen";
  let state: string;
  if (defined) {
    state =
      "Beide Tore aktiv → dieser Kanal ist bei dir <strong>fest definiert</strong> und verbindet die zwei Zentren.";
  } else if (partial) {
    const activeGate = on1 ? g1 : g2;
    const otherGate = on1 ? g2 : g1;
    state =
      `Nur Tor ${activeGate} ist aktiv — ein <strong>hängendes Tor</strong>. Der Kanal ist <strong>offen</strong>: ` +
      `die Tor-Energie ist da, aber er verbindet die zwei Zentren noch NICHT. Erst wenn auch Tor ${otherGate} aktiviert ist (z.B. durch einen Partner oder Transit), wird er voll definiert.`;
  } else {
    state =
      "Kein Tor aktiv → der Kanal ist <strong>offen</strong> (nur Potenzial; das blasse Gold-Netz zeigt ihn).";
  }
  return (
    `<div class="hd-tip-head"><span class="hd-tip-name">Kanal ${lo}-${hi}${name ? ` — ${name}` : ""}</span>` +
    `<span class="hd-tip-badge hd-tip-badge--${defined ? "def" : "open"}">${badge}</span></div>` +
    `<div class="hd-tip-fn">Ein Kanal verbindet zwei Tore (${g1} ↔ ${g2}) und damit zwei Zentren. Erst wenn BEIDE Tore aktiv sind, ist er „definiert" und gibt dir eine feste Eigenschaft.</div>` +
    `<div class="hd-tip-gate">Tor ${g1}: ${HD_COLOR_DESC[a1] ?? "—"} · Tor ${g2}: ${HD_COLOR_DESC[a2] ?? "—"}</div>` +
    `<div class="hd-tip-state">${state}<br>Farben: rot = Design (unbewusst), schwarz = Persönlichkeit (bewusst), rot+schwarz = beides.</div>`
  );
}

// Fakten-Erklärungen (Lern-Tool): jede Zeile der Übersicht (Typ, Strategie …)
// bekommt eine kurze, fundierte Erklärung. Konzept-Texte nach HD_GROUNDING.
const HD_FACT_INFO: Record<string, { title: string; expl: string }> = {
  type: {
    title: "Typ",
    expl: "Dein Energietyp — die Grundausrichtung deiner Aura. Er legt deine Strategie fest (wie du dich richtig durchs Leben bewegst). Es gibt 5: Manifestor, Generator, Manifestierender Generator, Projektor, Reflektor.",
  },
  strategy: {
    title: "Strategie",
    expl: "Der richtige Weg, Entscheidungen zu treffen und zu handeln — so vermeidest du Widerstand und bleibst im Flow. Folgst du ihr, erlebst du deine Signatur.",
  },
  authority: {
    title: "Autorität",
    expl: "Deine verlässliche innere Entscheidungs-Instanz — IHR folgen, nicht dem Kopf. Z.B. sakral (Bauch-Reaktion im Moment), emotional (über eine Welle Klarheit gewinnen, nie im Affekt), splenisch (leiser Instinkt).",
  },
  profile: {
    title: "Profil",
    expl: "Zwei Linien (bewusst/unbewusst, aus den beiden Sonnen) — beschreibt, wie du lernst und mit der Welt umgehst. Linien: 1 Forscher · 2 Einsiedler · 3 Märtyrer · 4 Opportunist · 5 Ketzer · 6 Rollenvorbild.",
  },
  signature: {
    title: "Signatur",
    expl: "Das Gefühl, wenn du deinem Design folgst (im Flow) — dein innerer Kompass dafür, dass es stimmt. Je Typ: Zufriedenheit, Frieden, Erfolg oder Überraschung.",
  },
  notSelf: {
    title: "Not-Self-Thema",
    expl: "Das Warnsignal, wenn du gegen dein Design lebst. Spürst du es, korrigier sanft über Strategie + Autorität — es ist ein Wegweiser, kein Fehler. Je Typ: Frustration, Wut, Bitterkeit oder Enttäuschung.",
  },
  cross: {
    title: "Inkarnationskreuz",
    expl: "Dein übergeordnetes Lebensthema (~70 % deines Zwecks), gebildet aus den 4 Toren von Sonne & Erde (bewusst + unbewusst). Der Winkel zeigt die Art des Lebensweges.",
  },
  definedCenters: {
    title: "Definierte Zentren",
    expl: "Eingefärbte Zentren — konsistente, verlässliche Energie von Geburt an. Das strahlst du beständig aus, darauf ist Verlass.",
  },
  openCenters: {
    title: "Offene Zentren",
    expl: "Weiße Zentren — hier nimmst du die Energie der Umgebung auf und verstärkst sie: größtes Lern- und Weisheitspotenzial, aber auch wo Konditionierung/das Not-Self sitzt. Offen heißt NICHT schlecht.",
  },
};
function crossAngleNote(value: string): string {
  if (/right angle|rechtswinkel/i.test(value))
    return "<br><strong>Rechtswinkelkreuz</strong> (Right Angle) = persönliches Schicksal: du bist auf deinen EIGENEN Prozess fokussiert.";
  if (/left angle|linkswinkel/i.test(value))
    return "<br><strong>Linkswinkelkreuz</strong> (Left Angle) = transpersonales/karmisches Thema: dein Weg ist eng mit anderen verwoben.";
  if (/juxtaposition/i.test(value))
    return "<br><strong>Juxtaposition</strong> = ein fixiertes Schicksal: eine feste, ganz eigene Aufgabe.";
  return "";
}
function buildFactTipHtml(el: SVGElement): string | null {
  const key = el.getAttribute("data-fact") ?? "";
  const info = HD_FACT_INFO[key];
  if (!info) return null;
  const value = el.querySelector(".hd-fact-value")?.textContent ?? "";
  const expl = key === "cross" ? info.expl + crossAngleNote(value) : info.expl;
  return (
    `<div class="hd-tip-head"><span class="hd-tip-name">${info.title}</span></div>` +
    (value ? `<div class="hd-tip-fn">${value}</div>` : "") +
    `<div class="hd-tip-state">${expl}</div>`
  );
}

/** Bindet einmal global: Tap irgendwo sonst löst ein angeheftetes Tooltip wieder. */
function bindHdTipDocDismiss(): void {
  if (hdTipDocBound) return;
  hdTipDocBound = true;
  document.addEventListener("click", () => {
    if (hdTipPinned) {
      hdTipPinned = null;
      hdCenterTooltipEl().hidden = true;
    }
  });
}

/** Generischer Hover- (Desktop) + Tap- (Mobile, angeheftet) Tooltip-Attacher. */
function attachHdTooltips(
  container: HTMLElement,
  selector: string,
  build: (el: SVGElement) => string | null,
): void {
  const tip = hdCenterTooltipEl();
  const show = (el: SVGElement, ev: MouseEvent): void => {
    const html = build(el);
    if (!html) return;
    tip.innerHTML = html;
    tip.hidden = false;
    positionHdTip(tip, ev);
  };
  container.querySelectorAll<SVGElement>(selector).forEach((el) => {
    el.addEventListener("mouseenter", (ev) => {
      if (!hdTipPinned) show(el, ev);
    });
    el.addEventListener("mousemove", (ev) => {
      if (!hdTipPinned) positionHdTip(tip, ev);
    });
    el.addEventListener("mouseleave", () => {
      if (!hdTipPinned) tip.hidden = true;
    });
    el.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (hdTipPinned === el) {
        hdTipPinned = null;
        tip.hidden = true;
      } else {
        hdTipPinned = el;
        show(el, ev);
      }
    });
  });
  bindHdTipDocDismiss();
}

// Pre.738: Bedeutung der 4 Variable-Pfeile (PHS) fürs Hover-Tooltip.
const HD_VAR_INFO: Record<string, { de: string; fn: string }> = {
  digestion: {
    de: "Verdauung",
    fn: "Wie dein Körper am besten aufnimmt — Nahrung UND Eindrücke. Aus der Design-Sonne (Körper, unbewusst).",
  },
  environment: {
    de: "Umgebung",
    fn: "Die Orte und Räume, in denen du dich richtig fühlst und am besten entscheidest. Aus dem Design-Knoten (Körper, unbewusst).",
  },
  motivation: {
    de: "Motivation",
    fn: "Der innere Antrieb deines Geistes — das Warum hinter deinem Wahrnehmen. Aus der Persönlichkeits-Sonne (Geist, bewusst).",
  },
  perspective: {
    de: "Perspektive",
    fn: "Die Sichtweise/Linse, durch die dein Geist die Welt betrachtet. Aus dem Persönlichkeits-Knoten (Geist, bewusst).",
  },
};

/** Tooltip für die 4 Variable-Pfeile (PHS) — liest data-hd-var/-dir/-tone/-color. */
function buildVarTipHtml(el: SVGElement): string | null {
  const key = el.getAttribute("data-hd-var") ?? "";
  const info = HD_VAR_INFO[key];
  if (!info) return null;
  const dir = el.getAttribute("data-dir") ?? "";
  const tone = el.getAttribute("data-tone") ?? "";
  const color = el.getAttribute("data-color") ?? "";
  const isAktiv = dir === "aktiv";
  const dirExpl = isAktiv
    ? "Pfeil nach links = <strong>aktiv</strong>: eine fokussierte, gerichtete Ausprägung dieses Themas."
    : "Pfeil nach rechts = <strong>passiv</strong>: eine empfangende, entspannte Ausprägung dieses Themas.";
  const meta =
    tone || color
      ? `<div class="hd-tip-fn">Die Pfeilrichtung folgt dem Ton (${tone || "?"}); die Farbe (${color || "?"}) verfeinert das Thema.</div>`
      : "";
  return (
    `<div class="hd-tip-head"><span class="hd-tip-name">${info.de}</span>` +
    `<span class="hd-tip-badge hd-tip-badge--${isAktiv ? "def" : "open"}">${dir || "Pfeil"}</span></div>` +
    `<div class="hd-tip-fn">${info.fn}</div>` +
    `<div class="hd-tip-state">${dirExpl}</div>` +
    meta
  );
}

/** Hängt Hover/Tap-Tooltips an Planeten-Zeilen, Kanäle, Tor-Punkte UND die 4
 * Variable-Pfeile. (Die globale „Tap-außerhalb-löst"-Bindung macht attachHdTooltips selbst.) */
function setupHdInfoTooltips(container: HTMLElement): void {
  attachHdTooltips(container, ".hd-planet", buildPlanetTipHtml);
  attachHdTooltips(container, ".hd-channel", buildChannelTipHtml);
  attachHdTooltips(container, ".hd-gate", buildGateTipHtml);
  attachHdTooltips(container, ".hd-var", buildVarTipHtml);
}

/**
 * Öffnet ein sauberes Druck-/PDF-Fenster mit dem Inline-SVG-Chart + Auswertung.
 * Browser-„Als PDF sichern" liefert ein vektorscharfes PDF (das Chart ist SVG).
 * Genutzt für die eigene HD-Auswertung UND die Verbindungs-/Match-Auswertung.
 */
function printHdReport(opts: {
  title: string;
  subtitle?: string;
  chartSvg?: string;
  /** Erweiterte Darstellung im Druck: nur Design (rot) links, nur
   *  Persönlichkeit (schwarz) rechts — unter dem Hauptchart. */
  designSvg?: string;
  personalitySvg?: string;
  factsHtml?: string;
  extraHtml?: string;
  interpHtml?: string;
}): void {
  const esc = (s: string): string =>
    s.replace(
      /[&<>]/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c] ?? c,
    );
  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) {
    showToast(
      "Bitte Popups für diese Seite erlauben, um zu drucken.",
      "warning",
    );
    return;
  }
  // Grid-Kompakt-Layout (3 Charts garantiert auf EINER A4-Seite, da der Bodygraph
  // hochformatig ist → die SEITE wird höhen-gedeckelt, SVGs skalieren höhen-getrieben,
  // overflow:hidden als Failsafe). Seite 2 = linksbündige Fakten, Seite 3 = Deutung.
  const css = `
    /* Pre.738: echte @page-Ränder (oben/unten) wiederholen sich auf JEDER
       physischen Seite → auch Folgeseiten langer Fakten/Deutung haben oben+unten
       Rand. Vorher: @page margin:0 + Block-Padding → Folgeseiten randlos ("fortlaufend"). */
    @page { size: A4; margin: 14mm 16mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1c1c22; line-height: 1.5; font-size: 12px; }
    :root { --c-design: #c0392b; --c-pers: #1c1c22; --c-line: #e3e3e6; --c-muted: #6c6c74; }
    /* Seitenränder kommen jetzt aus @page → .pr-page selbst ohne Padding. */
    .pr-page { padding: 0; page-break-after: always; break-after: page; }
    .pr-page:last-child { page-break-after: auto; break-after: auto; }
    /* Hinweis-Banner: nur am Bildschirm, im Druck/PDF ausgeblendet. */
    .pr-hint { background: #fff8e1; border: 1px solid #f0d98a; color: #6b5a18; padding: 8px 12px; font-size: 12px; border-radius: 6px; margin: 10px; line-height: 1.45; }
    @media print { .pr-hint { display: none !important; } }
    /* Seite 1: Charts-Höhe an neue druckbare Höhe (297-2*14=269mm) angepasst, mit
       Slack gegen Rundungs-Leerseite. Charts höhen-getrieben + overflow-Failsafe. */
    .pr-page--charts { height: 262mm; display: flex; flex-direction: column; overflow: hidden; }
    .pr-head { border-bottom: 2px solid var(--c-design); padding-bottom: 6px; margin-bottom: 10px; flex: 0 0 auto; }
    .pr-head h1 { font-size: 20px; margin: 0; color: var(--c-design); line-height: 1.2; }
    .pr-sub { margin: 3px 0 0; color: var(--c-muted); font-size: 12px; }
    .pr-charts { flex: 1 1 auto; min-height: 0; display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: minmax(0, 1fr) minmax(0, 1.3fr); grid-template-areas: "design pers" "main main"; gap: 10px 16px; align-items: stretch; }
    .pr-charts--single { grid-template-columns: 1fr; grid-template-rows: minmax(0, 1fr); grid-template-areas: "main"; }
    .pr-variant { min-height: 0; display: flex; flex-direction: column; align-items: center; page-break-inside: avoid; break-inside: avoid; }
    .pr-variant--design { grid-area: design; }
    .pr-variant--pers { grid-area: pers; }
    .pr-cap { font-size: 12px; font-weight: 700; letter-spacing: .02em; margin: 0 0 4px; text-align: center; }
    .pr-variant--design .pr-cap { color: var(--c-design); }
    .pr-variant--pers .pr-cap { color: var(--c-pers); }
    .pr-main { grid-area: main; min-height: 0; display: flex; flex-direction: column; align-items: center; page-break-inside: avoid; break-inside: avoid; }
    .pr-main .pr-cap { color: var(--c-muted); font-weight: 600; }
    .pr-svg-box { flex: 1 1 auto; min-height: 0; width: 100%; display: flex; align-items: center; justify-content: center; }
    .pr-svg-box svg { display: block; width: auto; height: 100%; max-width: 100%; max-height: 100%; }
    /* Seite 2: Fakten linksbündig, EINSPALTIG (alles untereinander). */
    .pr-facts-title { font-size: 15px; font-weight: 700; color: var(--c-design); margin: 0 0 10px; padding-bottom: 5px; border-bottom: 1.5px solid var(--c-design); }
    .pr-facts .hd-fact { display: grid; grid-template-columns: 168px 1fr; column-gap: 14px; align-items: baseline; padding: 5px 0; border-bottom: 1px solid var(--c-line); font-size: 12px; break-inside: avoid; page-break-inside: avoid; }
    .pr-facts .hd-fact-label { color: var(--c-muted); font-weight: 500; text-align: left; }
    .pr-facts .hd-fact-value { color: var(--c-pers); font-weight: 600; text-align: left; }
    .pr-extra { font-size: 12px; margin: 12px 0 0; }
    .pr-extra .conn-group { border: 1px solid var(--c-line); border-radius: 8px; padding: 8px 10px; margin: 0 0 8px; break-inside: avoid; page-break-inside: avoid; }
    .pr-extra .conn-badge { font-weight: 700; }
    .pr-extra .conn-chan { display: inline-block; padding: 2px 7px; margin: 2px; border: 1px solid var(--c-line); border-radius: 6px; font-size: 11px; }
    .pr-extra .conn-legend { margin-bottom: 8px; }
    .pr-extra .conn-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin: 0 4px 0 10px; vertical-align: -1px; }
    .pr-extra .conn-dot--a { background: #2563eb; } .pr-extra .conn-dot--b { background: #e8730c; } .pr-extra .conn-dot--both { background: #7c3aed; }
    /* Seite 3: Deutung (Serif-Fließtext für wertigen Lese-Look). */
    .pr-interp { font-size: 12.5px; font-family: "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; }
    .pr-interp-title { font-size: 16px; font-weight: 700; color: var(--c-design); margin: 0 0 12px; padding-bottom: 5px; border-bottom: 1.5px solid var(--c-design); font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
    .pr-interp h1, .pr-interp h2, .pr-interp h3, .pr-interp h4 { color: var(--c-pers); margin: 13px 0 4px; page-break-after: avoid; break-after: avoid; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
    .pr-interp h2 { font-size: 15px; } .pr-interp h3 { font-size: 14px; } .pr-interp h4 { font-size: 13px; }
    .pr-interp p { margin: 0 0 8px; orphans: 3; widows: 3; }
    .pr-interp ul, .pr-interp ol { margin: 0 0 8px; padding-left: 18px; }
    .pr-interp li { margin: 0 0 3px; break-inside: avoid; }
    .pr-foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid var(--c-line); color: #9a9aa2; font-size: 9.5px; text-align: center; }
  `;
  const hasVariants = !!(opts.designSvg && opts.personalitySvg);
  const page1 =
    `<section class="pr-page pr-page--charts">` +
    `<header class="pr-head"><h1>${esc(opts.title)}</h1>${opts.subtitle ? `<p class="pr-sub">${esc(opts.subtitle)}</p>` : ""}</header>` +
    `<div class="pr-charts${hasVariants ? "" : " pr-charts--single"}">` +
    (hasVariants
      ? `<div class="pr-variant pr-variant--design"><p class="pr-cap">Design</p><div class="pr-svg-box">${opts.designSvg}</div></div>` +
        `<div class="pr-variant pr-variant--pers"><p class="pr-cap">Persönlichkeit</p><div class="pr-svg-box">${opts.personalitySvg}</div></div>`
      : "") +
    `<div class="pr-main"><div class="pr-svg-box">${opts.chartSvg ?? ""}</div></div>` +
    `</div>` +
    `</section>`;
  const hasFacts = !!(opts.factsHtml || opts.extraHtml);
  const page2 = hasFacts
    ? `<section class="pr-page pr-page--facts">` +
      `<h2 class="pr-facts-title">Übersicht &amp; Fakten</h2>` +
      (opts.factsHtml ? `<div class="pr-facts">${opts.factsHtml}</div>` : "") +
      (opts.extraHtml ? `<div class="pr-extra">${opts.extraHtml}</div>` : "") +
      `</section>`
    : "";
  const page3 = opts.interpHtml
    ? `<section class="pr-page pr-page--interp"><div class="pr-interp"><h2 class="pr-interp-title">Persönliche Auswertung</h2>${opts.interpHtml}<div class="pr-foot">Erstellt mit Vespera · Human Design</div></div></section>`
    : `<div class="pr-foot">Erstellt mit Vespera · Human Design</div>`;
  const parts = page1 + page2 + page3;
  w.document.write(
    `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${esc(opts.title)}</title><style>${css}</style></head><body><div class="pr-hint">Tipp: Falls im PDF oben oder unten die Browser-Zeile (URL/Datum) erscheint, im Druckdialog „Kopf- und Fußzeilen" deaktivieren — dann ist das Dokument randlos sauber.</div>${parts}</body></html>`,
  );
  w.document.close();
  // Nach Layout/Font-Load drucken; Fenster bleibt offen (User kann „Als PDF sichern").
  const fire = (): void => {
    try {
      w.focus();
      w.print();
    } catch {
      /* Druck-Dialog evtl. blockiert — Fenster bleibt offen */
    }
  };
  if (w.document.readyState === "complete") setTimeout(fire, 300);
  else w.onload = () => setTimeout(fire, 300);
}

/**
 * Pre.649 (User-Wunsch): Stimmen-Picker nach GESCHLECHT gruppiert (Weiblich /
 * Männlich) statt nach Anbieter — je mit ♂/♀-Symbol davor, alphabetisch
 * sortiert. Gratis-Google-Stimmen mit „· gratis" markiert. Befüllt ein
 * <select> mit zwei optgroups. Geteilt von allen Stimmen-Pickern (Mein Leben,
 * Human Design, Essenz-Spiegel) — eine Quelle, kein Duplikat.
 */
function fillVoiceSelectByGender(
  select: HTMLSelectElement,
  selectedId: string,
): void {
  type V = { id: string; label: string; gender: string; free: boolean };
  const all: V[] = [
    ...GOOGLE_NARRATOR_VOICES.map((v) => ({ ...v, free: true })),
    ...getElevenVoices().map((v) => ({ ...v, free: false })),
  ];
  // Pre.828 (#85/#92): KEINE ♀/♂-Glyphen mehr in Optionen — iOS rendert
  // sie als Emoji (abgeschnitten, im zugeklappten Zustand falsch). Die
  // Gruppen-Überschrift trägt die Information in Worten.
  const groups = [
    // 2026-07-20 (Martin, Klon-Vorbereitung): eigene Stimmen (gender
    // "neutral") als EIGENE Gruppe zuoberst — vorher landeten sie
    // irrefuehrend unter "Männlich".
    { label: "Eigene Stimme", match: (g: string) => g === "neutral" },
    { label: "Weiblich", match: (g: string) => g === "female" },
    { label: "Männlich", match: (g: string) => g !== "female" && g !== "neutral" },
  ];
  for (const grp of groups) {
    const voices = all
      .filter((v) => grp.match(v.gender))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
    if (voices.length === 0) continue;
    const og = document.createElement("optgroup");
    og.label = grp.label;
    for (const v of voices) {
      const o = document.createElement("option");
      o.value = v.id;
      // Gratis = Google (funktioniert immer). Sonst ElevenLabs → braucht
      // Credits; ohne Credits fällt die Vertonung still auf eine Google-Stimme
      // zurück. Klar kennzeichnen, damit die Wahl nicht „falsch" wirkt.
      o.textContent = `${v.label}${v.free ? " · gratis" : " · ElevenLabs (Credits nötig)"}`;
      if (v.id === selectedId) o.selected = true;
      og.appendChild(o);
    }
    select.appendChild(og);
  }
}

// Google-Stimmen (gratis) erkennt man am `xx-XX-`-Präfix bzw. den Modellnamen —
// entscheidet, ob die Vorschau/Vertonung über Google oder ElevenLabs läuft.
function isGoogleNarratorVoice(id: string): boolean {
  return (
    /^[a-z]{2}-[A-Z]{2}-/.test(id) || /Chirp3-HD|Wavenet|Neural2/i.test(id)
  );
}

const VOICE_PICKER_SAMPLE =
  "Hallo, so klingt diese Stimme. Schön, dass du da bist.";

interface VoicePicker {
  el: HTMLElement;
  getVoice: () => string;
  setVoice: (id: string) => void;
  stopPreview: () => void;
}

/**
 * EINE wiederverwertbare Stimm-Auswahl MIT Vorhören (Probe-Knopf) — genutzt in
 * Human Design, „Mein Leben" UND Essenz-Spiegel, damit die Stimme überall gleich
 * gewählt UND vorab gehört werden kann (`/api/voice-preview`). Liefert einen
 * Container `<span>` mit Dropdown + Probe-Button.
 */
function buildVoicePicker(opts: {
  initial: string;
  onChange: (id: string) => void;
  sample?: string;
  signal?: AbortSignal;
}): VoicePicker {
  const wrap = document.createElement("span");
  wrap.className = "voice-picker";

  const sel = document.createElement("select");
  sel.className = "voice-picker-select";
  sel.setAttribute("aria-label", "Erzähler-Stimme");
  sel.title = "Erzähler-Stimme";
  fillVoiceSelectByGender(sel, opts.initial);
  wrap.appendChild(sel);

  // Probe-Button (Stimm-Vorschau) — kurzes Sample über /api/voice-preview.
  const previewBtn = document.createElement("button");
  previewBtn.type = "button";
  previewBtn.className = "voice-picker-preview";
  previewBtn.setAttribute("aria-label", "Stimme anhören");
  previewBtn.title = "Stimme anhören";
  const speakerIcon =
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
    '<path fill="currentColor" d="M3 10v4h4l5 5V5L7 10H3zm13.5 2a4.5 4.5 0 0 0-2.5-4.03v8.06A4.5 ' +
    '4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06a9 9 0 0 0 0-17.54z"/></svg>';
  const spinner =
    '<span class="book-spinner book-spinner--sm" aria-hidden="true"></span>';
  previewBtn.innerHTML = speakerIcon;
  wrap.appendChild(previewBtn);

  const previewAudio = document.createElement("audio");
  previewAudio.preload = "none";
  let previewAbort: AbortController | null = null;
  let previewBusy = false;
  const stopPreview = (): void => {
    previewAbort?.abort();
    if (!previewAudio.paused) previewAudio.pause();
    previewAudio.removeAttribute("src");
    previewBtn.innerHTML = speakerIcon;
  };
  const playPreview = async (): Promise<void> => {
    if (!previewAudio.paused && !previewAudio.ended) {
      stopPreview();
      return;
    }
    if (previewBusy) return;
    previewBusy = true;
    previewAbort?.abort();
    previewAbort = new AbortController();
    const myAbort = previewAbort;
    previewBtn.innerHTML = spinner;
    previewBtn.disabled = true;
    const selected = sel.value;
    const google = isGoogleNarratorVoice(selected);
    try {
      const res = await fetch("/api/voice-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        signal: myAbort.signal,
        body: JSON.stringify({
          voiceId: selected,
          text: opts.sample ?? VOICE_PICKER_SAMPLE,
          ttsProvider: google ? "google" : "elevenlabs",
          googleTtsVoice: google ? selected : undefined,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const blob = await res.blob();
      if (myAbort.signal.aborted) return;
      const objUrl = URL.createObjectURL(blob);
      previewAudio.src = objUrl;
      previewAudio.onended = () => {
        URL.revokeObjectURL(objUrl);
        previewBtn.innerHTML = speakerIcon;
      };
      await previewAudio.play();
      previewBtn.innerHTML = speakerIcon;
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") return;
      showToast("Vorschau gerade nicht möglich.", "warning");
      previewBtn.innerHTML = speakerIcon;
    } finally {
      previewBusy = false;
      previewBtn.disabled = false;
    }
  };
  previewBtn.addEventListener("click", () => void playPreview());

  sel.addEventListener("change", () => {
    stopPreview();
    opts.onChange(sel.value);
  });
  opts.signal?.addEventListener("abort", stopPreview);

  return {
    el: wrap,
    getVoice: () => sel.value,
    setVoice: (id: string) => {
      sel.value = id;
    },
    stopPreview,
  };
}

interface NarrationPlayer {
  el: HTMLElement;
  reset: () => void;
  pause: () => void;
  /** Arbeitet der Player (alles außer „idle“/„error“)? Siehe ViewerNarrationPlayer.aktiv. */
  aktiv: () => boolean;
  setReady: (ready: boolean) => void;
  getVoice: () => string;
  /**
   * Synthese im Hintergrund anstoßen, damit der erste Klick sofort spielt.
   * No-op, wenn noch kein Text/Body bereit ist oder bereits geprefetcht wurde.
   */
  prefetch: () => void;
  /**
   * Abschnitts-Modus (nur bei `sectionable`): die Vorlesung in blätterbare
   * Abschnitte zerlegen (◀ ▶ im Player), jeder wird einzeln vertont. Leere
   * Liste = wieder Einzel-Vorlesung. No-op ohne `sectionable`.
   */
  setSections: (items: Array<{ title: string; text: string }>) => void;
  /** Pre.756: optionaler Zusatz am Abschnitts-Label (z.B. Erzeugungs-Uhrzeit). */
  setSectionSuffix: (suffix: string) => void;
  /**
   * Von aussen starten — derselbe Weg wie der Spielknopf. (Ticket 2395)
   *
   * ⚠️ Ob Ton kommt, entscheidet der Browser: ohne vorangehende Geste bleibt
   *    die Wiedergabe gesperrt (Ticket 2329). Anbieten, nicht versprechen.
   */
  play: () => void;
  /**
   * Kennung fuers Positions-Gedaechtnis umsetzen (2026-09-03).
   *
   * Fuer Blaetter, deren Inhalt wechselt — der Essenz-Spiegel zeigt jeden Tag
   * einen anderen —, gehoert zu jedem Inhalt eine eigene Position. `null`
   * schaltet das Gedaechtnis ab.
   */
  setResumeId: (id: string | null) => void;
}

// Die Zerlegung steht in shared/ — der Server waermt DIESELBEN Abschnitte
// vor. Lagen sie auseinander, fuellte sich ein Zwischenspeicher, der nie traf.

/**
 * EIN wiederverwertbarer Vorlese-Player (Stimm-Picker mit Vorhören + Play/Pause)
 * für EINEN Text — genutzt in Human Design und Essenz-Spiegel, damit beide exakt
 * gleich aussehen/funktionieren. `getBody(voiceId)` liefert den POST-Body für den
 * narrate-Endpoint (oder `null`, wenn noch kein Text da ist). `reset()` aufrufen,
 * sobald sich der zugrunde liegende Text ändert (neue Vertonung nötig).
 */
// Einheitliche Zeitanzeige „M:SS" für ALLE Player (geteilt).
function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

// Refresh-/Aktualisieren-Icon (gedrehter Pfeil). `cls` ist der Animations-Hook
// (z.B. book-refresh-icon / hd-refresh-icon → .is-busy-Dreh-Spin) — daher
// parametrisiert statt fester Klasse. EINE Quelle für das SVG-Markup.
function refreshIconHtml(cls: string): string {
  return (
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" ' +
    'fill="none" stroke="currentColor" stroke-width="2" ' +
    `stroke-linecap="round" stroke-linejoin="round" class="${cls}">` +
    '<path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/></svg>'
  );
}

// Gemeinsame Transport-Icons (Play/Pause/Spinner) — EINE Quelle für alle Player.
const NARR_PLAY_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
  '<path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
const NARR_PAUSE_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
  '<path fill="currentColor" d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>';
const NARR_SPINNER =
  '<span class="book-spinner book-spinner--sm" aria-hidden="true"></span>';
// Kapitel-Blättern (nur „Mein Leben"): vorheriges / nächstes Kapitel.
const NARR_PREV_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">' +
  '<path fill="currentColor" d="M7 6h2v12H7zM20 6v12l-9-6z"/></svg>';
const NARR_NEXT_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">' +
  '<path fill="currentColor" d="M15 6h2v12h-2zM4 6l9 6-9 6z"/></svg>';
// Pre.976: MP3-Download (Pfeil nach unten in Ablage).
const NARR_DOWNLOAD_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">' +
  '<path fill="currentColor" d="M12 3v10m0 0l-4-4m4 4l4-4M5 19h14" ' +
  'stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" ' +
  'stroke-linejoin="round"/></svg>';

/** Die geteilte Player-Leiste (Play/Pause + Seek + Zeit [+ Gesamt]). */
interface PlayerBar {
  el: HTMLElement;
  /** Engine-Zustand → Play/Pause-Icon + Label + disabled. `ready=false` sperrt. */
  setState: (state: NarrationState, ready: boolean) => void;
  /** Spinner + eigenes Label (z.B. „Bereitet Kapitel vor …") + gesperrt. */
  setBusy: (label: string) => void;
  /** Fortschritt → Seek-Slider + Zeit (übersprungen, solange gescrubbt wird). */
  /** `geschaetzt`: Dauer aus der Zeichenzahl gerechnet — Anzeige mit Tilde. */
  setProgress: (cur: number, dur: number, geschaetzt?: boolean) => void;
  /** Optionales Gesamt-Badge rechts (nur „Mein Leben": „~12 Min gesamt"). */
  setTotal: (text: string) => void;
  /** True, solange der Finger den Slider zieht — Caller kann Updates gaten. */
  isSeeking: () => boolean;
  /** Kapitel-Blätter-Buttons (nur Buch) aktivieren/sperren je nach Position. */
  setChapterNav: (hasPrev: boolean, hasNext: boolean) => void;
}

/**
 * EINE Player-Leiste (Play/Pause + Seek + Zeit [+ Gesamt]) — GETEILT von „Mein
 * Leben", Human Design und Essenz-Spiegel, damit der Player ÜBERALL identisch
 * aussieht UND funktioniert (eine Wahrheit). Reines UI: der Aufrufer verdrahtet
 * `onToggle`/`onSeek` und treibt die Anzeige über `setState`/`setProgress`.
 * Kennt KEINE Engine/Quellen — das bleibt beim jeweiligen Orchestrator.
 */
function buildPlayerBar(opts: {
  onToggle: () => void;
  onSeek: (seconds: number) => void;
  idleLabel?: string; // Erst-Start, default „Vorlesen"
  resumeLabel?: string; // nach Pause, default „Weiter"
  // Kapitel-Blättern (nur „Mein Leben"): gesetzt → Vor/Zurück-Buttons flankieren
  // den Play-Knopf.
  onPrev?: () => void;
  onNext?: () => void;
  // Pre.976: gesetzt → ⬇-Knopf zum Herunterladen des GANZEN MP3 (rechts).
  onDownload?: () => void | Promise<void>;
}): PlayerBar {
  const idleLabel = opts.idleLabel ?? "Vorlesen";
  const resumeLabel = opts.resumeLabel ?? "Weiter";

  const bar = document.createElement("div");
  bar.className = "player-bar";

  /* Traegt den Zustandstext, der frueher IM Abspielknopf stand. */
  const zustandEl = document.createElement("span");
  zustandEl.className = "player-bar-zustand";

  const playBtn = document.createElement("button");
  playBtn.type = "button";
  playBtn.className = "player-bar-play";
  /**
   * 2026-08-25: Der Knopf trug nur Zeichen und Wort — kein `aria-label` (ein
   * Screenreader las den Zustand also nur, solange das Wort sichtbar ist) und
   * keine Markierung, in welchem Zustand er steht.
   *
   * Der Zustand steht jetzt als `data-state` am Knopf. Damit kann die
   * Spielleiste zeigen, was gerade zaehlt: solange noch nichts gelaufen ist
   * (`idle`), braucht es weder Schieberegler noch Zeitanzeige — es gibt nichts
   * zu spulen. Beim ersten Antippen ist alles da und bleibt.
   */
  const setBtn = (
    icon: string,
    label: string,
    disabled: boolean,
    zustand: string,
  ): void => {
    /*
     * ⚠️ 2026-09-03 (Martin: *„der Platz — wenn ich abgespielt habe, steht
     * dort ‚Erneut abspielen'. Das braucht so viel Platz, dass der Player
     * zweiteilig wird."*)
     *
     * Die Leiste traegt SIEBEN Kinder nebeneinander: zurueck, abspielen,
     * vor, Schieber, Zeit, Gesamt, herunterladen. Der Abspielknopf hatte
     * dabei eine feste Mindestbreite von 120 Punkten, weil sein Text
     * wechselt („Vorlesen" / „Bereitet vor …" / „Erneut versuchen") und die
     * Leiste sonst bei jedem Wechsel sprang.
     *
     * Beides zusammen ging auf 390 Punkten nicht auf — die Leiste brach um.
     *
     * Jetzt: der Knopf zeigt nur noch sein Zeichen, in derselben Groesse wie
     * die anderen Bedienknoepfe. Der TEXT verschwindet nicht, er wandert in
     * die Statuszeile unter dem Schieber, wo ohnehin die Zeit steht. Damit
     * kann er beliebig lang werden, ohne die Leiste zu bewegen — und die
     * Bedienknoepfe haben endlich alle dieselbe Gestalt.
     *
     * ⚠️ Der Name bleibt am Knopf (aria-label + title): fuer Bildschirmleser
     * und fuer den langen Druck aendert sich nichts.
     */
    playBtn.innerHTML = icon;
    playBtn.disabled = disabled;
    playBtn.dataset.state = zustand;
    playBtn.setAttribute("aria-label", label);
    playBtn.title = label;
    if (zustandEl) {
      /*
       * Die Fusszeile spricht nur, wenn sie etwas zu sagen hat.
       *
       * „Pause" waehrend des Laufens sagt nichts — die Zeit sagt mehr. Und im
       * Ruhezustand stand hier „Vorlesen": genau das Wort, das der Knopf
       * daneben ohnehin traegt (als Zeichen, mit aria-label). Eine ganze
       * Zeile fuer eine Wiederholung — bei einem Player von 142 Punkten.
       *
       * ⚠️ „Bereitet vor …" und Fehler BLEIBEN. Ein Player, der zehn Sekunden
       *    lang nichts tut und nichts sagt, wirkt kaputt.
       */
      const stumm = zustand === "playing" || zustand === "idle";
      zustandEl.textContent = stumm ? "" : label;
      zustandEl.hidden = stumm;
      const f = zustandEl.closest(".player-bar-fuss");
      if (f instanceof HTMLElement) f.hidden = stumm;
    }
  };
  playBtn.addEventListener("click", () => opts.onToggle());

  // Kapitel-Blätter-Buttons (nur wenn onPrev/onNext gesetzt = „Mein Leben").
  let prevBtn: HTMLButtonElement | null = null;
  let nextBtn: HTMLButtonElement | null = null;
  if (opts.onPrev || opts.onNext) {
    prevBtn = document.createElement("button");
    prevBtn.type = "button";
    prevBtn.className = "player-bar-skip";
    prevBtn.title = "Vorheriges Kapitel";
    prevBtn.setAttribute("aria-label", "Vorheriges Kapitel");
    prevBtn.innerHTML = NARR_PREV_ICON;
    prevBtn.disabled = true;
    prevBtn.addEventListener("click", () => opts.onPrev?.());
    nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = "player-bar-skip";
    nextBtn.title = "Nächstes Kapitel";
    nextBtn.setAttribute("aria-label", "Nächstes Kapitel");
    nextBtn.innerHTML = NARR_NEXT_ICON;
    nextBtn.disabled = true;
    nextBtn.addEventListener("click", () => opts.onNext?.());
  }

  const seek = document.createElement("input");
  seek.type = "range";
  seek.className = "player-bar-seek";
  seek.min = "0";
  seek.max = "1000";
  seek.value = "0";
  seek.step = "1";
  seek.setAttribute("aria-label", "Position");

  const time = document.createElement("span");
  time.className = "player-bar-time";
  time.textContent = "0:00 / 0:00";

  const total = document.createElement("span");
  total.className = "player-bar-total";
  total.hidden = true;

  // Pre.976: Download-Knopf — lädt das GANZE Audio als ein MP3 (alle Abschnitte).
  let downloadBtn: HTMLButtonElement | null = null;
  if (opts.onDownload) {
    downloadBtn = document.createElement("button");
    downloadBtn.type = "button";
    downloadBtn.className = "player-bar-download";
    // 11.09.2026: heisst nicht mehr "herunterladen" — der Knopf geht ueber
    // shareMedia() (siehe die Aufrufer unten) und kann seitdem BEIDES.
    // Dieselbe Umbenennung wie beim Klang-des-Tages-Knopf am 07.09.
    downloadBtn.title = "Sichern oder teilen";
    downloadBtn.setAttribute("aria-label", "Sichern oder teilen");
    downloadBtn.innerHTML = NARR_DOWNLOAD_ICON;
    downloadBtn.addEventListener("click", () => {
      if (!downloadBtn) return;
      downloadBtn.disabled = true;
      downloadBtn.innerHTML = NARR_SPINNER;
      void Promise.resolve(opts.onDownload?.()).finally(() => {
        if (downloadBtn) {
          downloadBtn.disabled = false;
          downloadBtn.innerHTML = NARR_DOWNLOAD_ICON;
        }
      });
    });
  }

  if (prevBtn) bar.append(prevBtn);
  bar.append(playBtn);
  if (nextBtn) bar.append(nextBtn);
  /*
   * ⚠️ Reihenfolge = Zeilen. Die Leiste bricht um, also entscheidet die
   * DOM-Ordnung, was zusammensteht:
   *   Zeile 1  alle Knoepfe (zurueck, abspielen, vor, herunterladen)
   *   Zeile 2  der Schieber ueber die volle Breite
   *   Zeile 3  Zeit und Zustand
   *
   * Mein erster Anlauf hatte den Herunterladen-Knopf HINTER den Schieber
   * gesetzt — daraus wurden fuenf Zeilen und 167 Punkte Hoehe.
   */
  if (downloadBtn) bar.append(downloadBtn);
  bar.append(seek);
  /*
   * Zeit und Zustand in EINER Fusszeile, die ueber die volle Breite laeuft.
   * Vorher standen Zeit und Gesamtdauer als eigene Kinder IN der Knopfreihe
   * und drueckten sie mit ueber den Rand.
   */
  /*
   * 05.09.2026 — Martin: *„Der Player ist irgendwie viel zu groß, verbraucht
   * extrem viel Platz."* Im Browser gemessen, 390 Punkte breit:
   *
   *     Knopfreihe   52    davon 118 Punkte rechts UNGENUTZT
   *     Schieber     40    Wellenform ist 18 hoch, der Rest war Luft
   *     Fusszeile    20    trug Zeit + Zustand, meist nur die Zeit
   *     ------------------
   *     zusammen    142
   *
   * Die Zeit zieht in die freie Haelfte der Knopfreihe. Die Fusszeile bleibt
   * fuer echte Meldungen („Bereitet vor …", Fehler) — und verschwindet, wenn
   * es nichts zu melden gibt, statt eine leere Zeile Platz zu halten.
   */
  const uhr = document.createElement("div");
  uhr.className = "player-bar-uhr";
  uhr.append(total, time);
  bar.append(uhr);
  const fuss = document.createElement("div");
  fuss.className = "player-bar-fuss";
  fuss.append(zustandEl);
  bar.append(fuss);

  let seeking = false;
  let dur = 0;
  /** Ist `dur` gerechnet statt gemessen? Dann steht eine Tilde davor. */
  let dauerGeschaetzt = false;
  const target = (): number => {
    const max = Number(seek.max) || 1000;
    return dur > 0 ? (Number(seek.value) / max) * dur : 0;
  };
  const setTimeLabel = (cur: number): void => {
    // Pre.979: Gesamtdauer noch unbekannt (gestreamter Cache-Miss → Infinity →
    // dur=0) → wenigstens die laufende Position zeigen statt „/ 0:00".
    // 05.09.: Eine geschätzte Dauer trägt eine Tilde — ehrlicher als ein
    // Strich, und ehrlicher als eine Zahl, die wie gemessen aussieht.
    time.textContent =
      dur > 0
        ? `${formatClock(cur)} / ${dauerGeschaetzt ? "~" : ""}${formatClock(dur)}`
        : `${formatClock(cur)} / –`;
  };
  /*
   * ⚠️ 2026-09-03 (Martin, mit Foto: „obwohl die Audiospur läuft, sehe ich
   * keine Zeit laufen") — die Zeit stand auf 0:06, während vorgelesen wurde.
   *
   * Die Kette, ausgelöst von der Glaubwürdigkeits-Prüfung in setProgress:
   *   1. Dauer unglaubwürdig (gestreamter Ton)      -> dur = 0
   *   2. setProgress setzt den Regler dann bei JEDEM Takt hart auf "0"
   *   3. Eine Berührung feuert `input`              -> seeking = true
   *   4. Der Wert springt zurück auf 0, ist also UNVERÄNDERT — und ein
   *      range-Element feuert `change` nur bei geändertem Wert
   *   5. `seeking` bleibt ewig wahr, setProgress steigt vorher aus:
   *      die Zeit friert ein.
   *
   * Zwei Riegel, weil einer nicht reicht:
   *   (a) Ohne bekannte Dauer ist Springen ohnehin sinnlos — der Regler wird
   *       dann gar nicht erst bedienbar. Damit entsteht Fall 3 nicht mehr.
   *   (b) Loslassen gibt IMMER frei, auch ohne `change`. Ein hängendes
   *       `seeking` darf die Anzeige nie wieder stilllegen.
   */
  const freigeben = (): void => {
    seeking = false;
  };
  // Slider ziehen: Live-Zeit anzeigen, Audio erst beim Loslassen setzen.
  seek.addEventListener("input", () => {
    if (dur <= 0) return; // (a) nichts zu springen — Anzeige nicht anhalten
    seeking = true;
    setTimeLabel(target());
  });
  seek.addEventListener("change", () => {
    if (dur > 0) {
      const t = target();
      opts.onSeek(t);
      setTimeLabel(t);
    }
    seeking = false;
  });
  // (b) Sicherheitsnetz: jedes Loslassen beendet das Ziehen.
  for (const ev of ["pointerup", "pointercancel", "touchend", "blur"]) {
    seek.addEventListener(ev, freigeben);
  }

  setBtn(NARR_PLAY_ICON, idleLabel, true, "idle");

  return {
    el: bar,
    setState: (state, ready) => {
      switch (state) {
        case "idle":
          setBtn(NARR_PLAY_ICON, idleLabel, !ready, "idle");
          break;
        case "paused":
          setBtn(NARR_PLAY_ICON, resumeLabel, !ready, "paused");
          break;
        case "preparing":
        case "buffering":
          /*
           * ⚠️ 16.09.2026 (Ticket 2285) — hier stand `true`, der Knopf war
           * also WAEHREND DES WARTENS TOT.
           *
           * Martin: *„Wiedergabe startet oft erst, wenn man manuell einen
           * Track zurueck und wieder vor springt."* Das ist kein Zufall und
           * kein Cache-Effekt. Der Abspielknopf war gesperrt und `start()`
           * stieg zusaetzlich bei `engine.isBusy()` wortlos aus — die
           * Blaetter-Knoepfe dagegen blieben bedienbar und riefen
           * `engine.destroy()` ohne Pruefung. Zurueck-und-vor war damit der
           * EINZIGE erreichbare Abbruch. Er hat den Ausweg gefunden, den wir
           * ihm gelassen hatten.
           *
           * Jetzt sagt der Knopf, was er kann. Der Kreisel bleibt — es
           * arbeitet ja wirklich etwas —, aber er ist bedienbar.
           */
          setBtn(NARR_SPINNER, "Abbrechen", false, "preparing");
          break;
        case "playing":
          setBtn(NARR_PAUSE_ICON, "Pause", false, "playing");
          break;
        case "stalled":
          setBtn(NARR_SPINNER, "Puffert …", true, "stalled");
          break;
        case "error":
          // Erholbar — der nächste Klick startet eine frische Beschaffung.
          setBtn(NARR_PLAY_ICON, "Erneut versuchen", false, "error");
          break;
      }
    },
    setBusy: (label) => setBtn(NARR_SPINNER, label, true, "busy"),
    setProgress: (cur, d, geschaetzt = false) => {
      // 2026-07-25 (Martin: "steht auf 0:02"): Gestreamte Abschnitte haben
      // keine Content-Length — der Browser schaetzt die Laenge aus dem ersten
      // Frame und liegt dabei oft voellig daneben. Eine Dauer, die KLEINER
      // ist als die bereits gespielte Zeit, ist beweisbar falsch; dann lieber
      // gar keine Zahl als eine falsche.
      /*
       * ⚠️ 2026-09-02 (Martin, mit Foto: „obwohl eine Voice läuft, steht der
       * Player") — der Regler stand ganz rechts und die Zeit auf 0:01 / 0:01,
       * während ein Abschnitt von 2421 Zeichen (~173 Sekunden) lief.
       *
       * Der Browser meldet für einen gestreamten Ton keine echte Gesamtdauer,
       * sondern die Kante des bisher Geladenen. Die wächst mit dem Abspielen
       * mit — und liegt deshalb IMMER knapp über der laufenden Zeit. Die alte
       * Bedingung `d >= cur` hält genau diese Lüge für wahr: 1 Sekunde geladen
       * bei 1 Sekunde gespielt ergibt „0:01 / 0:01" und einen vollen Balken.
       *
       * Eine Dauer, die dem Abspielkopf nur um Haaresbreite vorausläuft, ist
       * keine Dauer — sie ist der Pufferrand. Erst wenn ein spürbarer Rest
       * bleibt, ist die Zahl eine Aussage über das Ende. Sonst: „–", ehrlich
       * und ohne vollen Balken.
       *
       * ⚠️ 0,75 s ist die Schwelle, nicht der Kern: der Kern ist, dass
       * `d ≈ cur` beim LAUFENDEN Ton nichts über das Ende sagt. Am wirklichen
       * Ende schadet die Schwelle nicht — dort hört das Stück ohnehin auf.
       */
      // 05.09.: Eine GESCHÄTZTE Dauer ist kein Pufferrand — sie kommt aus
      // der Zeichenzahl und darf auch dann stehen, wenn der Kopf sie gerade
      // einholt. Die 0,75-s-Regel gilt nur für gemessene Werte.
      /*
       * ⚠️ 16.09.2026 (Ticket 2285) — „einholen" ja, ÜBERHOLEN nein.
       *
       * Hier stand `geschaetzt ||`, also: eine Schätzung gilt immer. Läuft
       * das echte Audio länger als geschätzt, wird `cur > d` — und dann
       * zeigte die Uhr eine GESAMTZEIT KLEINER ALS DIE POSITION, bei vollem
       * Regler. Genau Martins „Player zeigt die falsche Zeit an".
       *
       * Die Schätzkonstante ist nicht schuld: an 182 echten Stücken
       * nachgemessen 15,49 Zeichen/s gegen 15,5 im Code. Sie trifft im
       * Mittel — und liegt im Einzelfall eben mal darunter.
       *
       * Die Entscheidung vom 05.09. bleibt: eine Schätzung darf stehen,
       * während der Kopf sich ihr nähert. Sobald er sie überholt hat, ist
       * sie nachweislich falsch — dann lieber „–“ als eine Zahl, die dem
       * Auge widerspricht.
       */
      const glaubwuerdig =
        Number.isFinite(d) &&
        d > 0 &&
        (geschaetzt ? d > cur : d - cur > 0.75);
      dur = glaubwuerdig ? d : 0;
      dauerGeschaetzt = glaubwuerdig && geschaetzt;
      if (seeking) return;
      const max = Number(seek.max) || 1000;
      // Ein Anschlag ist ein Anschlag: auch wenn oben etwas durchrutscht,
      // rechnet der Regler nie darueber hinaus.
      seek.value =
        dur > 0
          ? String(Math.min(max, Math.max(0, Math.round((cur / dur) * max))))
          : "0";
      // Ohne bekannte Dauer gibt es kein Ziel zum Springen. Der Regler bleibt
      // sichtbar (die Leiste soll nicht huepfen), nimmt aber keine Eingabe an.
      // Bei einer geschätzten Dauer gibt es kein exaktes Ziel — der Regler
      // zeigt den Stand, nimmt aber keine Eingabe an.
      // 06.09.: auch bei geschätzter Dauer darf man springen — die Engine
      // rechnet das Ziel auf das Stück um (springeZu).
      seek.disabled = dur <= 0;
      setTimeLabel(cur);
    },
    setTotal: (text) => {
      total.textContent = text;
      total.hidden = !text;
    },
    isSeeking: () => seeking,
    setChapterNav: (hasPrev, hasNext) => {
      if (prevBtn) prevBtn.disabled = !hasPrev;
      if (nextBtn) nextBtn.disabled = !hasNext;
    },
  };
}

function buildNarrationPlayer(opts: {
  initialVoice: string;
  endpoint: string;
  getBody: (voiceId: string) => Record<string, unknown> | null;
  sample?: string;
  signal?: AbortSignal;
  onVoiceChange?: (id: string) => void;
  /** Blätter-Knöpfe (◀ ▶) + Abschnitts-Modus aktivieren (s. setSections). */
  sectionable?: boolean;
  /**
   * Positions-Resume (Sekunden) über den GETEILTEN Helfer `playback-resume`:
   * NUR aktiv, wenn `resumeId` gesetzt ist — sonst ist das Verhalten 100 %
   * identisch zu vorher. Merkt die zuletzt gehörte Sekunde pro Inhalt und
   * springt beim Play wieder dorthin (version-bewusst über `resumeVersion`:
   * neuer Text ⇒ frischer Start). Ergänzt das bestehende ABSCHNITTS-Resume
   * (das den Abschnitts-Index merkt) — beide existieren unabhängig.
   * 10.09.2026: Bei Abschnitts-Playern trägt der Schlüssel den Abschnitt
   * (`id#idx`), damit die Sekunde aus Abschnitt 1 nie in Abschnitt 2 landet.
   */
  resumeId?: string;
  resumeVersion?: string;
  /**
   * Dateiname (ohne .mp3) für den ⬇-Knopf. (Pre.976)
   *
   * ⚠️⚠️ 11.09.2026 — DER KNOPF LUD DAMIT NIE ETWAS HERUNTER.
   *
   * Martin: *„Alles, was der Player abspielt, möchte ich auch teilen können,
   * nicht nur herunterladen, wobei ich nicht einmal sicher bin, ob das
   * Herunterladen funktioniert."*
   *
   * Er hatte recht. Der Knopf baute `<a download>` und klickte sich selbst —
   * und genau das tut in der WKWebView der nativen App NICHTS (derselbe
   * Befund steht seit dem 07.09. in share.ts, für den Klang-des-Tages-
   * Knopf: „Auf dem iPhone gibt es keinen Download-Ordner, und `<a
   * download>` tut in der WKWebView nichts."). Der Klang-des-Tages-Knopf
   * geht seither über `shareMedia()` — Datei holen, dem Teilen-Blatt
   * mitgeben, „In Dateien sichern" IST dort der Download. Der Vorlese-Knopf
   * hier bekam diesen Weg nie, weil er als eigene Sache entstand (Pre.976,
   * vor dem Klang des Tages).
   *
   * Jetzt derselbe Weg für beide: EINE Regel, kein zweiter Mechanismus
   * daneben. Das schliesst gleichzeitig Martins erste Frage — das Teilen
   * funktioniert jetzt überall dort, wo bisher nur (kaputtes) Herunterladen
   * stand: Essenz-Spiegel, Human Design, Buch, Projekt-Dateien,
   * Biography-Chronik, Tiefe Fragen — jeder Aufrufer dieser Funktion auf
   * einen Schlag, ohne dass an neun Stellen etwas geändert werden musste.
   */
  downloadName?: string;
  /**
   * Was hier vorgelesen wird — für die Abspielleiste über der Fußzeile.
   * (05.09.2026)
   *
   * Martin: *„Es wäre gut, dass man weiß, was man genau hört, ob es
   * Essenz-Spiegel, Mein Alltag ist oder anderes."*
   *
   * Die Leiste zeigt seit jeher `stand.titel` — nur setzte ihn niemand, also
   * stand dort immer „Vorlesen". Der Weg war da, die Angabe fehlte.
   */
  titel?: string;
  /**
   * Wurde dieser Inhalt wirklich GEHÖRT? (23.09.2026)
   *
   * Martin: *„Der Spiegel soll erst abgehakt sein, wenn ich ihn auch real
   * angehört habe."* Vorher galt: acht Sekunden offen = angesehen. Am Morgen
   * reichte ein kurzes Öffnen über die Kopfzeile (07:48:08 offen, 07:48:16
   * vermerkt), abgespielt wurde nichts.
   *
   * Feuert EINMAL je Inhalt (`reset()` stellt neu scharf): sobald
   * {@link GEHOERT_NACH_SEK} Sekunden wirklich abgespielt sind — Sprünge im
   * Schieber zählen nicht — oder das Ende erreicht ist, falls der Inhalt
   * kürzer ist.
   */
  onGehoert?: () => void;
  /**
   * Bleibt die Leiste beim Scrollen oben stehen? (07.09.2026)
   *
   * Martin, nachdem er den Spiegel vorlesen liess: *„Ausserdem moechte ich,
   * wenn ich nach unten scrolle, dass oben der Player einheftet."*
   *
   * ⚠️ Bewusst eine Angabe des Aufrufers und keine Vorgabe. Im Alltag steht
   *    in JEDER Karte ein eigener Player; klebten die alle, schoeben sie
   *    sich am oberen Rand uebereinander. Nur die langen Lese-Seiten, wo es
   *    genau EINE Leiste gibt, bekommen sie.
   */
  klebt?: boolean;
}): NarrationPlayer {
  const wrap = document.createElement("div");
  wrap.className = opts.klebt
    ? "narration-player narration-player--sticky"
    : "narration-player";

  // Abschnitts-Modus: ist die Liste nicht leer, blättert der Player durch die
  // Abschnitte (statt einer Einzel-Vorlesung); jeder Abschnitt wird über den
  // schnellen Streaming-Pfad (text) vertont.
  let sections: Array<{ title: string; text: string }> = [];
  let sectionIdx = 0;

  // EIN gemeinsamer Audio-Kern (Zustandsautomat, Timeouts, Stall-Watchdog,
  // Abbruch laufender Anfragen). Der Player liefert hier nur die UI + die
  // konkrete URL-Beschaffung (Streaming-Token vs. fertige Datei) als
  // AudioSource — die Engine spielt für beide Pfade identisch robust ab.
  const engine = new NarrationEngine();
  wrap.appendChild(engine.audio);

  let ready = false;
  /*
   * ⚠️ 10.09.2026 — der Spieler holt NICHTS mehr selbst vor.
   *
   * Bis heute lud er den naechsten Abschnitt als GANZE Datei herunter
   * (`warmUrl`, Pre.819), waehrend der laufende noch spielte — und den
   * laufenden zusaetzlich: einmal per fetch zum „Waermen", einmal durch
   * <audio>. Auf Martins Telefon (Nginx, 07:57): 7,5 MB spielen, 4,7 MB
   * nebenher laden, die Wiedergabe verhungert, nach 18 s bricht der Waechter
   * ab — und ohne sauberes Ende gibt es keinen naechsten Abschnitt.
   *
   * Vorwaermen heisst jetzt: EIN POST an /api/tts/prepare mit `waermen`.
   * Der Server erzeugt die Datei im Hintergrund (Spur „hintergrund", die
   * dem Vorlesen immer einen Platz laesst); heruntergeladen wird erst, wenn
   * gespielt wird — genau einmal, durch <audio>.
   */
  /** Die laufende Vorwaerm-Anfrage — ein kurzer POST, kein Strom. */
  let waermeAbbruch: AbortController | null = null;
  /** Welche Texte in dieser Liste schon zum Vorwaermen angemeldet sind. */
  const gewaermt = new Set<string>();

  const resumeKey = (): string =>
    `voice-v8.narr-sec:${aktiveResumeId ?? opts.endpoint}`;
  const sectionsSig = (): string =>
    `${sections.length}:${sections.reduce((a, x) => a + x.text.length, 0)}`;
  const saveSectionPos = (): void => {
    try {
      localStorage.setItem(
        resumeKey(),
        JSON.stringify({ sig: sectionsSig(), idx: sectionIdx }),
      );
    } catch {
      /* ignore */
    }
  };
  const clearSectionPos = (): void => {
    try {
      localStorage.removeItem(resumeKey());
    } catch {
      /* ignore */
    }
  };

  // Positions-Resume (Sekunden) über den geteilten Helfer — NUR wenn der
  // Aufrufer eine resumeId gesetzt hat (sonst bleibt alles wie bisher). Ergänzt
  // das Abschnitts-Resume oben: dieses merkt die Sekunde im aktuellen Abschnitt.
  const resumeVer = opts.resumeVersion ?? "";
  /*
   * ⚠️ 2026-09-03 (Martin: "inwieweit natuerlich auch der Player memorisiert,
   * also speichert"):
   *
   * Die Kennung war fest — beim Bauen gelesen, danach unveraenderlich. Fuer
   * Blaetter, die IHREN INHALT wechseln (der Essenz-Spiegel zeigt jeden Tag
   * einen anderen), taugt das nicht: entweder gar kein Gedaechtnis, oder die
   * Position eines Tages laege auf dem naechsten.
   *
   * Jetzt haelt eine Variable die aktuelle Kennung, und `setResumeId` setzt
   * sie um, wenn ein anderer Tag geladen wird.
   */
  let aktiveResumeId: string | null = opts.resumeId ?? null;
  let posLastSave = 0; // throttle für das gedrosselte Sichern in onTime
  /*
   * ⚠️ 10.09.2026 — Martin: *„schneidet vorne jetzt schon was ab."*
   *
   * EINE Sekunde fuer den ganzen Spiegel: gesichert in Abschnitt 1, beim
   * Laden JEDES weiteren Abschnitts wieder angewandt (der Zuhoerer an
   * `loadedmetadata` kannte keine Abschnitte). Wer in Abschnitt 1 bei 0:25
   * unterbrochen wurde, hoerte Abschnitt 2, 3 und 4 ab 0:25 — der Anfang
   * fehlte jedes Mal.
   *
   * Jetzt traegt der Schluessel den Abschnitt, und der Einstieg geht als
   * `startSek` an die Engine, die ihn EINMAL anwendet — in der Datei wie in
   * den Stuecken. Den Zuhoerer am <audio>-Element gibt es nicht mehr.
   */
  const resumeSchluessel = (): string | null =>
    aktiveResumeId ? `${aktiveResumeId}#${sectionIdx}` : null;
  const gemerkteSekunde = (): number => {
    const k = resumeSchluessel();
    return k ? (loadResume(k, resumeVer) ?? 0) : 0;
  };
  /*
   * ⚠️ 27.09.2026 (Ticket 2691) — Martin: Beim Vorlesen im Essenz-Spiegel
   * fehlte am Kapitelwechsel Text.
   *
   * Der Wechsel las die gemerkte Sekunde des ZIEL-Kapitels. Gemerkt wird
   * alle 3 s, geloescht wurde nur am Kapitelende — wer mit ◀ zurueckging,
   * liess sie stehen, und beim naechsten Wechsel begann das Kapitel mitten
   * drin (nginx: ab 6,8 s, dann ab 21,8 s). Jetzt vergisst `switchSection`
   * die Sekunde des verlassenen und des Ziel-Kapitels: ein Wechsel beginnt
   * bei 0:00. Die gemerkte Sekunde gilt nur beim Wiederoeffnen
   * (`setSections` stellt Kapitel und Sekunde wieder her).
   */
  const vergissSekunde = (): void => {
    const k = resumeSchluessel();
    if (k) clearResume(k);
  };

  // Geteilte Player-Leiste (Play/Pause + Seek + Zeit) — IDENTISCH zu „Mein
  // Leben". Seek scrubt innerhalb der EINEN Quelle (HD/Essenz sind einquellig).
  const bar = buildPlayerBar({
    onToggle: () => start(),
    onSeek: (s) => {
      // 06.09.: springt in der Engine — im Stück exakt, sonst ins Zielstück.
      engine.springeZu(s);
      const k = resumeSchluessel();
      if (k) saveResume(k, resumeVer, s);
    },
    // Blätter-Knöpfe nur im Abschnitts-Modus (sonst keine Skip-Buttons).
    ...(opts.sectionable
      ? {
          onPrev: () => switchSection(-1),
          onNext: () => switchSection(1),
        }
      : {}),
    // Pre.976: ⬇ lädt das GANZE Audio als EIN MP3 — alle Abschnitte (bzw. den
    // Einzel-Text) zusammen über den Streaming-Pfad rendern + herunterladen.
    onDownload: async () => {
      const text = sections.length
        ? sections.map((s) => s.text).join("\n\n")
        : (() => {
            const b = opts.getBody(picker.getVoice());
            const t = (b as { text?: unknown } | null)?.text;
            return typeof t === "string" ? t : "";
          })();
      if (!text.trim()) {
        showToast("Nichts zum Sichern oder Teilen.", "warning");
        return;
      }
      const voiceId = picker.getVoice();
      try {
        const pres = await fetch("/api/tts/prepare", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            text,
            voiceId,
            googleVoice: googleHdFallbackForVoice(voiceId),
            // 2026-07-25: Hier hat der Nutzer die Stimme GERADE im Waehler
            // ausgesucht — das ist eine bewusste Wahl fuer genau diese Datei
            // und darf die gespeicherte Stimme uebersteuern. Frueher hing das
            // an einer gespeicherten Marke, was den Sinn verfehlte.
            voiceSource: "user",
          }),
        });
        const pj = (await pres.json().catch(() => null)) as {
          ok?: boolean;
          token?: string;
        } | null;
        if (!pres.ok || !pj?.ok || !pj.token) throw new Error("prepare failed");
        /*
         * ⚠️⚠️ 11.09.2026 — HIER LAG DER FEHLER.
         *
         * `<a download>` auf einer `/api/tts/stream?token=…`-Adresse tat in
         * der WKWebView nichts — Martins Zweifel war begruendet. Jetzt
         * derselbe Weg wie beim Klang des Tages: `shareMedia()` holt die
         * Datei selbst (die URL traegt keine Endung, darum `alsDatei: true`
         * — siehe share.ts) und legt sie dem Teilen-Blatt bei. „In Dateien
         * sichern" IST dort der Download; und jede andere App im selben
         * Blatt ist das Teilen, um das Martin gebeten hat.
         */
        await shareMedia({
          url: `/api/tts/stream?token=${encodeURIComponent(pj.token)}`,
          title: opts.titel ?? "Vorlesen",
          dateiname: `${opts.downloadName ?? "vorlesen"}.mp3`,
          alsDatei: true,
        });
      } catch {
        showToast("Gerade nicht möglich — bitte später erneut versuchen.", "warning");
      }
    },
  });

  // Body der AKTUELLEN Vorlesung: im Abschnitts-Modus der Streaming-Text des
  // gewählten Abschnitts, sonst der vom Caller gelieferte Einzel-Body.
  // Body für einen BESTIMMTEN Abschnitt — Basis für den aktuellen UND fürs
  // Vorausladen des nächsten (Pre.758).
  const bodyFor = (idx: number): Record<string, unknown> | null => {
    if (sections.length) {
      const s = sections[idx];
      if (!s) return null;
      const voiceId = picker.getVoice();
      return {
        voiceId,
        text: s.text,
        googleVoice: googleHdFallbackForVoice(voiceId),
      };
    }
    return opts.getBody(picker.getVoice());
  };
  const currentBody = (): Record<string, unknown> | null => bodyFor(sectionIdx);

  // Pre.756: optionaler Zusatz am Abschnitts-Label (z.B. Erzeugungs-Uhrzeit der Essenz).
  let labelSuffix = "";
  // Abschnitts-Titelzeile (nur sichtbar mit >1 Abschnitt).
  const sectionTitle = document.createElement("div");
  sectionTitle.className = "narration-section-title";
  sectionTitle.hidden = true;
  const updateSectionUi = (): void => {
    const s = sections[sectionIdx];
    sectionTitle.hidden = sections.length <= 1 || !s;
    sectionTitle.textContent =
      s && sections.length > 1
        ? `${sectionIdx + 1}/${sections.length} · ${s.title}${labelSuffix ? " " + labelSuffix : ""}`
        : "";
    // 10.09.2026 — Martin: „keine Zeit und keine Anzahl der Tracks". Der
    // Zaehler steht AUCH in der Leiste, vor der Uhr — dort, wo man hinschaut.
    bar.setTotal(
      sections.length > 1 ? `${sectionIdx + 1}/${sections.length}` : "",
    );
    bar.setChapterNav(sectionIdx > 0, sectionIdx < sections.length - 1);
  };
  const switchSection = (delta: number): void => {
    const next = sectionIdx + delta;
    if (next < 0 || next >= sections.length) return;
    vergissSekunde();
    sectionIdx = next;
    vergissSekunde();
    saveSectionPos();
    engine.destroy();
    cancelPrefetch();
    /*
     * 10.09.2026: Kein Uebernehmen und kein Abbrechen mehr — es gibt keinen
     * clientseitigen Vorlauf (siehe oben). Ob der Abschnitt schon als Datei
     * daliegt, fragt die Engine den Server (pruefeGanz); liegt er noch im
     * Bau, kommen Stuecke, und der erste Ton kommt nach Sekunden statt nach
     * der ganzen Datei. Die Vorwaermung laeuft am Server weiter.
     */
    updateSectionUi();
    /*
     * ⚠️ 2026-08-31 — Martin, mit Foto des Alltag-Spielers: *„ich habe nur
     * acht Sekunden auf dem Player gehabt und trotzdem ist er
     * weitergelaufen."*
     *
     * Auf dem Bild steht 0:08 / 0:08, der Regler ganz rechts, und der Knopf
     * zeigt PAUSE — es lief also, waehrend die Anzeige „fertig" behauptete.
     *
     * Hier lag die Ursache: der Abschnittswechsel raeumte die Engine ab und
     * startete neu, ruehrte die ANZEIGE aber nicht an. Sie blieb auf dem
     * Endstand des vorigen Abschnitts stehen, bis das neue Stueck seine erste
     * Zeitmeldung schickt. Wie lange das dauert, ist gemessen: Median 2,0 s
     * bis zum ersten Ton, p90 6,3 s — so lange zeigt der Spieler das Ende
     * eines Stuecks, das nicht mehr laeuft.
     *
     * Ein Wechsel faengt bei null an. Das gehoert an den Anfang des
     * Wechsels, nicht ans Eintreffen des ersten Tons.
     */
    bar.setProgress(0, 0);
    start();
  };

  // Wirklich gehörte Zeit (siehe opts.onGehoert und lib/hoerzeit.ts).
  const onGehoert = opts.onGehoert;
  const hoer = onGehoert ? hoerzaehler(GEHOERT_NACH_SEK, onGehoert) : null;

  // Button + Seek/Zeit folgen AUSSCHLIESSLICH dem Engine-Zustand.
  engine.onState = (state: NarrationState): void => {
    if (state === "error") {
      // Erholbar — der nächste Klick startet eine frische Beschaffung.
      showToast("Vorlesen gerade nicht möglich.", "warning");
    }
    // Pre.809: GESTREAMTE Vertonungen haben keine endliche Dauer (Infinity)
    // — der Zeit-Trigger in onTime (durSec−cur ≤ 20) griff dort NIE, der
    // nächste Abschnitt startete kalt („lädt"-Stopp im Essenz-Spiegel).
    // Darum: sobald ein Abschnitt SPIELT, den nächsten direkt vorbereiten
    // (Token-TTL 10 Min ≫ Abschnittslänge; prefetchNext dedupliziert selbst).
    if (state === "playing") {
      window.setTimeout(() => prefetchNext(), 2000);
    }
    bar.setState(state, ready);
  };
  engine.onTime = (cur, durSec, geschaetzt) => {
    bar.setProgress(cur, durSec, geschaetzt);
    hoer?.zeit(cur);
    // Pre.758: gegen Ende des Abschnitts den nächsten vorbereiten → nahtloser
    // Auto-Übergang ohne erneutes Warten auf die Vertonung.
    if (durSec > 0 && durSec - cur <= 20) prefetchNext();
    // Positions-Resume gedrosselt sichern (nur Mittelteil — nicht am Anfang
    // und nicht kurz vors Ende, sonst springt es beim nächsten Mal störend).
    const schluessel = resumeSchluessel();
    if (
      schluessel &&
      cur > 2 &&
      durSec > 0 &&
      cur < durSec - 1 &&
      Date.now() - posLastSave > 3000
    ) {
      posLastSave = Date.now();
      saveResume(schluessel, resumeVer, cur);
    }
  };
  // Pre.740: Abschnitts-Modus läuft automatisch weiter — endet ein Abschnitt,
  // springt der Player zum nächsten und spielt ihn (wie das Hörbuch von Kapitel
  // zu Kapitel). Am letzten Abschnitt bleibt er stehen. EIN Player-Verhalten
  // überall (Essenz + HD), auf allen Geräten gleich.
  engine.onEnded = (): void => {
    // Das Ende des LETZTEN Abschnitts zählt als gehört — auch wenn der
    // ganze Inhalt kürzer war als die Schwelle.
    if (!(sections.length > 1 && sectionIdx < sections.length - 1)) hoer?.ende();
    if (sections.length > 1 && sectionIdx < sections.length - 1) {
      // Der Wechsel vergisst die Sekunde des fertigen Kapitels selbst.
      switchSection(1);
    } else {
      // Pre.810: fertig gehört → Sekunde und Kapitel löschen, nächstes Mal von vorn.
      vergissSekunde();
      clearSectionPos();
    }
  };

  /** Stimme und Google-Ersatzstimme aus dem Body — oder aus dem Picker. */
  const stimmen = (
    body: Record<string, unknown>,
  ): { voiceId: string; googleVoice: string } => {
    const voiceId =
      typeof (body as { voiceId?: unknown }).voiceId === "string"
        ? ((body as { voiceId?: string }).voiceId as string)
        : picker.getVoice();
    // Gender-gematchte Google-Fallback-Stimme: aus dem Body übernehmen
    // (falls der Caller sie schon mitschickt), sonst aus der voiceId
    // ableiten. Muss bis /api/tts/prepare durchgereicht werden, sonst
    // landet der ElevenLabs-Quota-Fallback wieder auf der Default-Stimme.
    const googleVoice =
      typeof (body as { googleVoice?: unknown }).googleVoice === "string"
        ? ((body as { googleVoice?: string }).googleVoice as string)
        : googleHdFallbackForVoice(voiceId);
    return { voiceId, googleVoice };
  };

  /**
   * Vertonung vorbereiten — EIN Weg für Stück, Ganzes und Vorwärmen.
   * (05.09.2026, seit 10.09. auch für die Vorwärmung des nächsten Abschnitts)
   *
   * `waermen` sagt dem Server: Das ist ein ganzer Abschnitt; liegt er
   * noch nicht im Cache, erzeuge ihn im Hintergrund fürs nächste Mal.
   * `cached` kommt zurück und entscheidet, ob eine Datei reicht oder
   * Stücke nötig sind.
   */
  const bereite = async (
    body: Record<string, unknown>,
    text: string,
    signal: AbortSignal,
    waermen = false,
  ): Promise<{ url: string; cached: boolean }> => {
    const { voiceId, googleVoice } = stimmen(body);
    const pres = await fetch("/api/tts/prepare", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      signal,
      body: JSON.stringify({ text, voiceId, googleVoice, waermen }),
    });
    const pdata = (await pres.json().catch(() => null)) as {
      ok?: boolean;
      token?: string;
      cached?: boolean;
      leer?: boolean;
      error?: string;
    } | null;
    if (pdata?.ok && pdata.leer) return { url: "", cached: false };
    if (!pres.ok || !pdata?.ok || !pdata.token) {
      throw new Error(pdata?.error || "prepare failed");
    }
    return {
      url: `/api/tts/stream?token=${encodeURIComponent(pdata.token)}`,
      cached: pdata.cached === true,
    };
  };

  /**
   * Ein Stueck holen — als blob:-Adresse, also WIRKLICH da. (10.09.2026)
   *
   * Vorher kam hier nur die Token-Adresse zurueck, und die Engine hielt das
   * Stueck fuer fertig, sobald sie da war. Der Vorrat (07.09.) zaehlte also
   * Sekunden, die noch gar nicht vertont waren; zwischen zwei Stuecken
   * wartete <audio> auf den Server. Jetzt zaehlt nur, was heruntergeladen
   * ist — und <audio> spielt aus dem Speicher: ohne Netz, ohne Waechter.
   *
   * Die Frist folgt der Zeichenzahl (synthFrist). Scheitert es, versucht
   * es die Engine beim Abspielen noch einmal und meldet dann laut, statt
   * still zu ueberspringen. Leer heisst „nichts zu sprechen" (Auszeichnung).
   */
  const holeStueck = async (
    body: Record<string, unknown>,
    teil: string,
    signal: AbortSignal,
  ): Promise<string> => {
    const { url } = await bereite(body, teil, signal);
    if (!url) return "";
    const steuer = new AbortController();
    const abbrechen = (): void => steuer.abort();
    signal.addEventListener("abort", abbrechen, { once: true });
    const frist = window.setTimeout(abbrechen, synthFrist(teil.length));
    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        signal: steuer.signal,
      });
      if (!res.ok) throw new Error(`stueck http ${res.status}`);
      const blob = await res.blob();
      if (blob.size < 100) throw new Error("stueck leer");
      return URL.createObjectURL(blob);
    } finally {
      window.clearTimeout(frist);
      signal.removeEventListener("abort", abbrechen);
    }
  };

  // Baut die AudioSource aus der bestehenden Zwei-Pfad-Logik — komplett
  // gekapselt in resolveUrl: hat der body einen `text`, läuft der schnelle
  // Streaming-Pfad (prepare→Token→Stream-URL), sonst der klassische Endpoint
  // (Server leitet den Text ab → fertige MP3-URL). Die Engine kennt den
  // Unterschied nicht.
  const buildSourceFromBody = (
    body: Record<string, unknown> | null,
  ): AudioSource | null => {
    if (!body) return null;
    // Fuer die verschachtelten Funktionen: dort gilt die Pruefung oben nicht.
    const quelle: Record<string, unknown> = body;
    return {
      // Was die Leiste anzeigt. Ohne Angabe bleibt es bei „Vorlesen".
      ...(opts.titel ? { titel: opts.titel } : {}),
      // 2026-09-04: Die Zeichenzahl bestimmt, wie lange der Abspieler auf
      // die Vertonung wartet. Soniox vertont in Echtzeit — ohne diese
      // Angabe brach jeder Text ueber ~1600 Zeichen in die feste Frist.
      zeichen:
        typeof (body as { text?: unknown }).text === "string"
          ? ((body as { text: string }).text.length)
          : undefined,
      // Ticket 2047: Der Text wird in Stücken vertont, damit der erste Ton
      // nach dem ERSTEN Stück kommt statt nach der ganzen Antwort (gemessen:
      // 11.800 ms Stille bei 272 Zeichen). Kurze Texte bleiben ein Stück.
      stuecke: zerlegeFuerVorlesen(
        typeof (body as { text?: unknown }).text === "string"
          ? ((body as { text: string }).text)
          : "",
      ),
      resolveStueck: (teil: string, signal: AbortSignal): Promise<string> =>
        holeStueck(quelle, teil, signal),
      // 05.09.: Erst fragen, ob der GANZE Abschnitt schon vertont daliegt —
      // dann eine Datei statt Stücke (Dauer exakt, springbar).
      // 10.09.: OHNE `waermen`. Der Abschnitt, der jetzt in Stücken läuft,
      // braucht keine zweite, ganze Vertonung nebenher — die kostete die
      // Zeichen doppelt und dem Vorlesen einen der zwei Soniox-Plätze.
      // Vorgewärmt wird der NÄCHSTE (prefetchNext) und der erste vor dem
      // ersten Klick (prefetch).
      pruefeGanz: async (signal: AbortSignal): Promise<string | null> => {
        const ganz =
          typeof (body as { text?: unknown }).text === "string"
            ? ((body as { text: string }).text)
            : "";
        if (!ganz.trim()) return null;
        try {
          const r = await bereite(quelle, ganz, signal);
          return r.cached ? r.url : null;
        } catch {
          return null;
        }
      },
      resolveUrl: (signal: AbortSignal): Promise<string> =>
        vertone(
          typeof (body as { text?: unknown }).text === "string"
            ? ((body as { text: string }).text)
            : "",
          signal,
        ),
    };

    async function vertone(text: string, signal: AbortSignal): Promise<string> {
        if (text.trim()) {
          // Schneller Streaming-Pfad.
          return (await bereite(quelle, text, signal)).url;
        }

        // Klassischer Pfad (Server leitet den Text ab → fertige MP3-URL).
        const res = await fetch(opts.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          signal,
          body: JSON.stringify(body),
        });
        const data = (await res.json().catch(() => null)) as {
          ok?: boolean;
          url?: string;
          message?: string;
        } | null;
        if (!res.ok || !data || data.ok === false || !data.url) {
          throw new Error(data?.message || "narrate failed");
        }
        return data.url;
    }
  };
  const buildSource = (): AudioSource | null => {
    const source = buildSourceFromBody(currentBody());
    if (!source) return null;
    const start = gemerkteSekunde();
    const s = sections[sectionIdx];
    // Der Ton-Leitstand nennt dann auch den Abschnitt („Essenz-Spiegel 2/4").
    const titel =
      opts.titel && s && sections.length > 1
        ? `${opts.titel} ${sectionIdx + 1}/${sections.length}`
        : opts.titel;
    return {
      ...source,
      ...(titel ? { titel } : {}),
      ...(start > 2 ? { startSek: start } : {}),
    };
  };

  const start = (): void => {
    // Läuft/puffert gerade → einfach umschalten (Pause / Fortsetzen).
    if (
      engine.getState() === "playing" ||
      engine.getState() === "stalled" ||
      engine.getState() === "paused"
    ) {
      engine.togglePlayPause();
      return;
    }
    if (engine.isBusy()) {
      // 16.09.2026 (Ticket 2285): Ein Klick waehrend des Beschaffens ist
      // kein Versehen — er ist die Bitte, das Warten zu beenden. Frueher
      // wurde er verschluckt. Derselbe Handgriff wie beim Abschnittswechsel:
      // Engine abraeumen, Vorlauf abbestellen. destroy() endet mit
      // setState("idle"), der Knopf findet also von selbst zurueck.
      engine.destroy();
      cancelPrefetch();
      return;
    }
    const source = buildSource();
    if (!source) return;
    /*
     * ⚠️ 10.09.2026 — die Engine bekommt die GANZE Quelle.
     *
     * Bis heute bekam sie nur `{ resolveUrl }`: eine Huelle um einen
     * clientseitigen Vorlauf, der den ganzen Abschnitt als Datei zog. Damit
     * war alles, was die Engine seit Ticket 2047 kann — erst das Ganze
     * fragen, sonst Stuecke mit Vorrat, Zeichenzahl fuer Frist und
     * Zeitanzeige — fuer diesen Spieler toter Code. Ein Abschnitt von 4.489
     * Zeichen brauchte rund vier Minuten bis zum ersten Byte; die Frist
     * ohne Zeichenzahl lag bei zwei (Journal 07:41:55: „timed out after
     * 120000ms"). Martin sah „Bereitet vor …", dann den Fehler.
     */
    engine.loadAndPlay(source);
  };

  /**
   * Einen Text am Server vorwaermen — ein POST, kein Download.
   *
   * Antwortet der Server `cached`, liegt die Datei schon da; sonst erzeugt
   * er sie im Hintergrund. Fuer den Spieler ist beides dasselbe: beim
   * Start fragt die Engine erneut (pruefeGanz) und nimmt, was da ist.
   */
  const waerme = (body: Record<string, unknown> | null): void => {
    if (!body) return;
    const text = (body as { text?: unknown }).text;
    if (typeof text !== "string" || !text.trim()) return;
    const schluessel = `${stimmen(body).voiceId}:${contentVersion(text)}`;
    if (gewaermt.has(schluessel)) return;
    gewaermt.add(schluessel);
    const controller = new AbortController();
    waermeAbbruch = controller;
    opts.signal?.addEventListener("abort", () => controller.abort(), {
      once: true,
    });
    void bereite(body, text, controller.signal, true)
      .catch(() => {
        // Nicht angekommen → beim naechsten Anlass noch einmal anmelden.
        gewaermt.delete(schluessel);
      })
      .finally(() => {
        if (waermeAbbruch === controller) waermeAbbruch = null;
      });
  };
  const cancelPrefetch = (): void => {
    waermeAbbruch?.abort();
    waermeAbbruch = null;
  };
  /** Den NAECHSTEN Abschnitt vorwaermen, waehrend dieser laeuft. */
  const prefetchNext = (): void => {
    if (sections.length <= 1) return;
    const nextIdx = sectionIdx + 1;
    if (nextIdx >= sections.length) return;
    waerme(bodyFor(nextIdx));
  };
  /*
   * Beim Oeffnen der Seite: NICHT den ersten Abschnitt, sondern den zweiten.
   *
   * ⚠️ Im Netz-Log gemessen (11.08.-Spiegel, kalt): Der Spiegel wird
   *    geoeffnet und sofort gespielt. Eine Vorwaermung des ERSTEN Abschnitts
   *    beginnt dann zwei Sekunden vor dem Klick, haelt einen der zwei
   *    Soniox-Plaetze fuer die ganze Dauer — und genau in dieser Zeit
   *    wachsen die Stuecke (120 → 216 → 389 Zeichen), die auf EINEM Platz
   *    nicht mit der Wiedergabe mithalten (Synthese 0,82 x Dauer, Wachstum
   *    1,8): Stueck 4 kam 13 s zu spaet. Der erste Abschnitt kommt in
   *    Stuecken schnell genug; der zweite soll als Datei daliegen, wenn er
   *    dran ist.
   */
  const prefetch = (): void => prefetchNext();

  const picker = buildVoicePicker({
    initial: opts.initialVoice,
    sample: opts.sample,
    signal: opts.signal,
    onChange: (id) => {
      // Neue Stimme → alte Vertonung verwerfen, laufendes Audio + Prefetch
      // stoppen, Cache leeren, damit eine frische Stimme sauber startet.
      engine.destroy();
      cancelPrefetch();
      bar.setState("idle", ready);
      // 2026-07-23 (Martin, "eine Stimme fuer alles"): Stimmwechsel in
      // einem Player gilt GLOBAL — dieselbe Wahrheit wie der Settings-
      // Picker, kein per-Player-Sonderweg mehr.
      saveSettings({ ...loadSettings(), voiceId: id, readVoiceChosen: true });
      opts.onVoiceChange?.(id);
    },
  });
  wrap.appendChild(picker.el);
  if (opts.sectionable) wrap.appendChild(sectionTitle);
  wrap.appendChild(bar.el);

  opts.signal?.addEventListener("abort", () => {
    cancelPrefetch();
    engine.pause();
  });

  return {
    el: wrap,
    /**
     * Die Kennung fuers Positions-Gedaechtnis umsetzen.
     *
     * Fuer Blaetter, deren Inhalt wechselt (Essenz-Spiegel: ein anderer Tag),
     * gehoert zu jedem Inhalt eine eigene Position. Ohne das laege die Stelle
     * von gestern im Vorlesen von heute.
     *
     * `null` schaltet das Gedaechtnis fuer diesen Inhalt ab.
     */
    setResumeId: (id: string | null): void => {
      aktiveResumeId = id;
    },
    reset: () => {
      // Zugrunde liegender Text hat sich geändert → alles verwerfen.
      hoer?.neu();
      engine.destroy();
      cancelPrefetch();
      bar.setState("idle", ready);
    },
    pause: () => engine.pause(),
    // Still sind nur „idle“ und „error“ — Pause, Puffern und die Lücke zwischen zwei Stücken zählen als aktiv.
    aktiv: () => {
      const s = engine.getState();
      return s !== "idle" && s !== "error";
    },
    /*
     * Von aussen starten. (19.09.2026, Ticket 2395)
     *
     * Derselbe Weg wie der Spielknopf (`onToggle: () => start()`) — es gibt
     * keinen zweiten Startpfad, der auseinanderlaufen koennte.
     *
     * ⚠️ Ob wirklich Ton kommt, entscheidet der Browser: ohne vorangehende
     *    Geste bleibt die Wiedergabe gesperrt (Ticket 2329). Der Aufrufer
     *    darf das also anbieten, aber nicht versprechen.
     */
    play: () => start(),
    setReady: (r: boolean) => {
      ready = r;
      // Zustand neu anwenden, damit der Button die neue „ready"-Sperre zeigt.
      bar.setState(engine.getState(), r);
    },
    getVoice: () => picker.getVoice(),
    prefetch,
    setSections: (items) => {
      if (!opts.sectionable) return;
      sections = Array.isArray(items) ? items : [];
      sectionIdx = 0;
      // Pre.810: gleicher Inhalt wie zuletzt → beim gemerkten Abschnitt
      // weitermachen (Hänger/Reload warf den User sonst immer zu „1/5").
      try {
        const raw = localStorage.getItem(resumeKey());
        if (raw) {
          const j = JSON.parse(raw) as { sig?: string; idx?: number };
          if (
            j?.sig === sectionsSig() &&
            Number.isInteger(j.idx) &&
            (j.idx as number) > 0 &&
            (j.idx as number) < sections.length
          ) {
            sectionIdx = j.idx as number;
          }
        }
      } catch {
        /* ignore */
      }
      labelSuffix = ""; // Pre.756: Suffix bei neuem Inhalt zurücksetzen (kein Stale aus anderer Ansicht)
      engine.destroy();
      cancelPrefetch();
      gewaermt.clear();
      bar.setState("idle", ready);
      updateSectionUi();
    },
    // Pre.756: optionaler Zusatz am Abschnitts-Label (z.B. „(14:23 Uhr)").
    setSectionSuffix: (suffix: string) => {
      labelSuffix = suffix || "";
      updateSectionUi();
    },
  };
}

/**
 * Lieblingsstücke — Herz und Dateiname. (07.09.2026)
 *
 * Martin: *„ein Herz-Button als Favorit, damit ich die Lieder auch zusätzlich
 * speichern und abspielen kann. Eine eigene Mediathek für Musikstücke."*
 */


// Essenz-Spiegel: gleicht den HD-Wesenskern mit dem heutigen Chat-Verlauf ab und
// zeigt sanft, wo der Nutzer im Flow ist und wo alte Prägungen ihn überlagern.
/**
 * Der Liedtext als eingeklappte Lade.
 *
 * 2026-09-03 (Martin: "bitte den Text vom Musikplayer einklappen, damit er
 * nicht so viel Platz verbraucht"). Der Text stand als offener Absatz unter
 * dem Spieler und schob auf dem Spiegel alles Weitere nach unten — bei rund
 * zwei Minuten Lied wird er noch laenger.
 *
 * EINE Stelle fuer beide Spieler (Essenz-Blatt und Verlaufs-Tag); vorher
 * stand derselbe Block zweimal im Code.
 */

/**
 * Wer den Klang gemacht hat — nur, wenn es nicht der Regelweg war.
 *
 * Martin, 08.09.2026: *„Mit der Klang erzeugen ist da eine Fehlmeldung, dass
 * ElevenLabs leer ist. Hat mir nicht gesagt, dass wir Alternative machen."*
 * Der Ersatz lief still: das Stueck kam von Lyria, die Seite sagte nichts.
 */

/**
 * Der Essenz-Spiegel.
 *
 * @param tag Optional ein Tagesschluessel (YYYY-MM-DD). Dann oeffnet das
 *   Blatt DIESEN Tag statt heute.
 *
 *   2026-08-29 (Martin: „Hier steht: 'Spiegel von Montag steht noch aus'.
 *   Warum kann ich nicht da einfach draufklicken und der Spiegel wird dann
 *   anhörbar?"):
 *
 *   Der Klick auf Zuhause oeffnete bis heute den Spiegel von HEUTE — die
 *   Zeile sagte Montag, das Blatt zeigte Samstag. Das Datum lag am Klick-Ort
 *   die ganze Zeit vor (die Zeile traegt `spiegel:YYYY-MM-DD`), es wurde nur
 *   weggeworfen.
 *
 *   ⚠️ Vorlesen braucht dafuer KEINE Server-Aenderung: der Abspiel-Knopf
 *   schickt den angezeigten TEXT im Rumpf mit (`getBody` → `currentReflection`),
 *   nicht ein Datum. Wer den richtigen Tag anzeigt, kann ihn auch hoeren.

 */
/**
 * Der Hinweis „dein Spiegel liegt bereit". (16.09.2026)
 *
 * 11.10.2026: steht nicht mehr als Chip in der Kopfleiste, sondern als EINE Zeile in der Lage
 * unter der Glocke (index.html, #topbar-spiegel). Die Regeln unten gelten unverändert; die
 * Glocken-Zahl zählt ihn weiterhin NICHT mit.
 *
 * Martin: *„Vielleicht kann man im Header-Bereich anzeigen lassen, dass der
 * Spiegel des Tages noch nicht angesehen wurde. Das waere eine optimale
 * Methode, weil dann sieht es der User und hat Motivation drauf zu klicken."*
 *
 * ## Der Unterschied, auf den es ankommt
 *
 * ⚠️ Die Kopfleiste ist die sichtbarste Stelle der App. Ein dauerhaftes „du
 *    hast X noch nicht gemacht" waere dort genau der Druck, den Martin
 *    gerade aus den Wegen herausnehmen liess. Die Botschaft muss „FUER DICH
 *    IST ETWAS DA" sein, nicht „du schuldest etwas".
 *
 *    Daraus folgen drei Regeln, und jede ist eine Bedingung im Code:
 *      1. Nur wenn wirklich einer BEREITLIEGT (`spiegelDa`) — einladen darf
 *         man nur zu etwas, das da ist. Sonst tippt jemand und bekommt eine
 *         Absage.
 *      2. Nur solange er nicht angesehen wurde.
 *      3. Keine Zahl, kein Aufstauen. Beim ersten Tippen ist es weg.
 */
function zeigeSpiegelHinweis(sichtbar: boolean): void {
  const knopf = document.getElementById("topbar-spiegel");
  if (knopf) knopf.hidden = !sichtbar;
  // Die Zeile steht in der Lage unter der Glocke: Tafel und Glocke erfahren es (zählt an der Glocke NICHT mit).
  lageGeaendert();
}

/** Einmal beim Start fragen — kein Dauerpuls fuer eine Einladung. */
function pruefeSpiegelHinweis(): void {
  void fetch("/api/tagesbild", { credentials: "same-origin" })
    .then((r) => (r.ok ? (r.json() as Promise<TagesbildAntwort>) : null))
    .then((d) => {
      if (!d?.ok) return;
      const spiegel = (d.felder ?? []).find((f) => f.schluessel === "spiegel");
      zeigeSpiegelHinweis(d.spiegelDa === true && spiegel?.erfuellt !== true);
      /*
       * Die Verfassung erscheint im Menue, sobald sie etwas zu zeigen hat.
       *
       * Martin: *„Wenn kein Whoop installiert ist, braucht der User dann
       * ueberhaupt die Verfassung, weil es ja sonst keine Infos gibt?"*
       *
       * ⚠️ Nicht „ohne WHOOP ausblenden": die Seite zeigt auch Geist und
       *    Seele aus der Stimmanalyse und die Mondzeile. Ausgeblendet wird,
       *    solange KEINE der Quellen etwas hergibt — dann erscheint sie von
       *    selbst, sobald sich das aendert.
       */
      const nav = document.getElementById("nav-verfassung");
      if (nav) nav.hidden = d.verfassungHatInhalt !== true;
    })
    .catch(() => {
      /* Eine Einladung darf nie stoeren. */
    });
}

interface TagesbildAntwort {
  ok?: boolean;
  spiegelDa?: boolean;
  verfassungHatInhalt?: boolean;
  felder?: Array<{ schluessel?: string; erfuellt?: boolean }>;
}

document
  .getElementById("topbar-spiegel")
  ?.addEventListener("click", () => void openEssenceSheet(undefined, "kopfzeile"));

/*
 * Die Glocke. (Ticket 2331, 17.09.2026)
 *
 * ⚠️ Nachgeladen, nicht fest verlinkt: die Liste erscheint selten und darf
 *    das Startbuendel nicht mittragen — dieselbe Regel wie bei den Aufgaben
 *    und dem Aktions-Menue.
 */
/*
 * 08.10.2026 (Martin, Richtung C): Antippen klappt zuerst die Lage unter dem Kopf auf;
 * deren Zeile „N neue Meldungen ansehen“ öffnet DIESE Liste. Ohne Lage (anderes Konto)
 * öffnet die Glocke wie bisher gleich die Liste (lib/lage-fenster.ts).
 */
verdrahteLage();

/**
 * Die Zahl an der Glocke neu holen — beim Start und nach jedem Zug.
 * (27.09.2026, Ticket 2670) Was Vespera eben erzählt oder Martin eben
 * beantwortet hat, zählt dort nicht mehr; der Server zählt, der Browser nie
 * selbst. Kein Intervall: gefragt wird nur, wenn gerade gesprochen wurde.
 */
function glockeNeuFragen(): void {
  void import("./lib/glocke.js").then((m) => m.pruefeGlocke());
}

/**
 * Der Wesenskern als Kopfzeile — Zusatzinformation, nicht Hauptsache.
 * (18.09.2026)
 *
 * Martin: *„Den kann man auch als Subinformation im Headerbereich anbringen,
 * aber nur als Zusatzinformation, so wie das Wetter."*
 *
 * Das Vorbild ist woertlich gemeint: Die Wetterzeile im Zuhause ist eine
 * Zeile mit Trennpunkten (`zeigeMeta`, zuhause-view.ts) — man liest sie im
 * Vorbeigehen oder gar nicht, und sie nimmt niemandem den Platz weg.
 *
 * ⚠️ DREI von fuenf Angaben, nicht alle. Typ (wer), Autoritaet (wie
 *    entscheiden), Im Flow (woran merkt man, dass es stimmt). Die Strategie
 *    folgt aus dem Typ, Not-Self ist ihre Kehrseite — beide stehen im
 *    `title` fuer die Maus und vollstaendig im Human-Design-Blatt. Eine
 *    Zeile, die alles traegt, ist keine Zusatzinformation mehr, sondern der
 *    Block von vorher in schlecht.
 *
 * ⚠️ Der Erklaersatz bleibt, solange KEINE Daten da sind. Wer den Spiegel
 *    zum ersten Mal oeffnet, braucht ihn; wer ihn zum hundertsten Mal
 *    oeffnet, nicht mehr.
 */
function setzeKernZeile(
  kopf: HTMLElement,
  s: {
    type?: string | null;
    strategy?: string | null;
    authority?: string | null;
    signature?: string | null;
    notSelf?: string | null;
  },
): void {
  const sub = kopf.querySelector<HTMLElement>(".hd-subtitle");
  if (!sub) return;
  const kurz = [s.type, s.authority, s.signature]
    .map((x) => (x ?? "").trim())
    .filter(Boolean);
  if (kurz.length === 0) return;
  sub.textContent = kurz.join(" · ");
  sub.classList.add("hd-subtitle--kern");
  const alles = [
    s.type && `Typ: ${s.type}`,
    s.strategy && `Strategie: ${s.strategy}`,
    s.authority && `Autorität: ${s.authority}`,
    s.signature && `Im Flow: ${s.signature}`,
    s.notSelf && `Not-Self: ${s.notSelf}`,
  ].filter(Boolean);
  sub.title = alles.join("\n");
}

/**
 * Ab wie vielen wirklich abgespielten Sekunden ein Inhalt als gehört gilt.
 *
 * ⚠️ 23.09.2026 — ersetzt „8 Sekunden offen = angesehen". Offen ist nicht
 *    gehört: Martin öffnete den Spiegel kurz über die Kopfzeile, spielte
 *    nichts ab, und der Haken stand. Eine Minute Zuhören ist eine Absicht;
 *    ein Antippen ist keine.
 */
const GEHOERT_NACH_SEK = 60;

/**
 * @param anlass Wer den Spiegel geöffnet hat. (23.09.2026)
 *
 * ⚠️ Martin: *„Bei ‚Guten Morgen Martin' sehe ich bei Wege, dass der Spiegel
 *    schon markiert ist, obwohl ich hundertprozentig den Spiegel noch nicht
 *    angesehen habe. Wieso?"*
 *
 *    Das Protokoll zeigte sekundengenau, DASS die Ansicht aufging (06:48:03,
 *    `GET /api/essence-mirror?peek=1` und im selben Atemzug `POST
 *    /api/gelesen`) — aber nicht WODURCH. Fünf Wege öffnen sie, keiner
 *    nannte sich. Jetzt steht der Weg im Protokoll: `[spiegel] offen
 *    anlass=…`.
 */
/**
 * Ticket 3021 (Weg 2): nach dem Neuladen das Spiegel-Blatt wieder öffnen, wenn
 * der Spiegel eben per Zuruf gestartet wurde. Nicht bei Aufnahme, nicht wenn
 * das Blatt (z. B. über #essence) schon offen ist. Startet keinen Ton.
 */
function oeffneSpiegelNachNeuladen(): void {
  if (!nimmSpiegelOffen()) return;
  if (!essenzFrei) return;
  if (document.querySelector(".essence-overlay")) return;
  if (aufnahmeStartSperre() !== null) return;
  openEssenceSheet(undefined, "nach-neuladen");
}

function openEssenceSheet(
  tag?: string,
  anlass = "unbenannt",
  /**
   * 23.09.2026 — zwei Wege aus der Zuhause-Karte „Aus deinem Spiegel":
   * `abspielen` startet den heutigen Spiegel, sobald sein Text steht (der
   * Knopf „Spiegel anhören"); `verlauf` öffnet gleich die Liste der Tage
   * (Rückblick → „Alle Spiegel im Verlauf").
   */
  wunsch: { abspielen?: boolean; verlauf?: boolean } = {},
): void {
  meldeSpur("spiegel", `offen anlass=${anlass}${tag ? " tag=" + tag : ""}`);
  // Wer ihn oeffnet, braucht die Einladung nicht mehr — sofort, nicht erst
  // beim naechsten Laden.
  zeigeSpiegelHinweis(false);
  document.querySelector(".essence-overlay")?.remove();
  // 2026-07-05 (Martin): echter Drawer laeuft ueber body.drawer-open —
  // der data-open-Altcode schloss nichts, Menue blieb ueber der Topbar.
  closeDrawerForNavigation();
  document
    .getElementById("nav-menu-btn")
    ?.setAttribute("aria-expanded", "false");

  const overlay = document.createElement("div");
  overlay.className = "sheet essence-overlay page-sheet";
  // Pre.750: reload-fester, teilbarer Deep-Link #essence (geteilter page-routes-Router).
  setPageHash("essence");
  const abort = new AbortController();
  const close = (): void => {
    abort.abort();
    overlay.remove();
    clearPageHash("essence");
    essenceActiveCleanup = null;
    // Ticket 3021: wer das Blatt schließt, braucht keine Wiederkehr nach dem Neuladen.
    vergissSpiegelOffen();
  };
  essenceActiveCleanup = close;
  attachEdgeSwipeBack(overlay, () => close());
  document.addEventListener(
    "keydown",
    (ev) => {
      if (ev.key === "Escape") close();
    },
    { signal: abort.signal },
  );

  const sheet = document.createElement("div");
  sheet.className = "sheet-card";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Essenz-Spiegel");

  // --- Vorlesen (Play) — narriert den aktuellen Spiegel als MP3 ---
  // EINER geteilter Vorlese-Player (Stimm-Picker mit Vorhören + Play/Pause) —
  // exakt wie in Human Design & „Mein Leben". Der aktuelle Spiegel/Roter-Faden
  // ist der vorzulesende Text; `reset()` bei jedem neuen Text.
  let currentReflection = "";
  const player = buildNarrationPlayer({
    // 07.09.: Die Leiste heftet oben ein (Martin).
    klebt: true,
    titel: "Essenz-Spiegel",
    // Abgehakt wird beim ANHÖREN, nicht beim Öffnen (siehe onGehoert).
    onGehoert: () => vermerkeSpiegelGehoert(),
    initialVoice: vorleseStimme(),
    endpoint: "/api/essence-mirror/narrate",
    getBody: (voiceId) =>
      currentReflection
        ? {
            text: currentReflection,
            voiceId,
            googleVoice: googleHdFallbackForVoice(voiceId),
          }
        : null,
    signal: abort.signal,
    onVoiceChange: (id) => {
      try {
        saveSettings({ ...loadSettings(), narratorVoiceId: id });
      } catch {
        /* sync-Fehler ignorieren — Stimme gilt trotzdem für jetzt */
      }
    },
    // Pre.739: Blätter-Modus (◀▶) wie HD — der Spiegel wird an seinen
    // Zwischenüberschriften in Abschnitte zerlegt, jeder einzeln vorgelesen.
    // Aktiviert je Ansicht über player.setSections(...).
    sectionable: true,
  });
  player.el.classList.add("essence-player");
  /*
   * Positions-Gedaechtnis fuer den Spiegel (2026-09-03, Martin: "inwieweit
   * natuerlich auch der Player memorisiert, also speichert").
   *
   * ⚠️ Die Kennung haengt am TEXT, nicht am Datum. Der Spiegel eines Tages
   * kann neu erzeugt werden — dann ist es ein anderer Text, und die alte
   * Stelle waere die falsche. Ausserdem haben die vier Stellen, an denen ein
   * Spiegel geladen wird, unterschiedlichen Zugriff auf den Tagesschluessel;
   * der Text liegt an allen vier vor.
   */
  /**
   * Tage, die in DIESER Sitzung geoeffnet wurden. (10.09.2026)
   *
   * Der Server weiss es auch, aber die Liste wird nicht neu geladen, wenn man
   * aus einem Tag zurueckkommt — ohne diese Menge stuende der Punkt dann noch
   * an einer Zeile, die man gerade gelesen hat.
   */
  const gelesenLokal = new Set<string>();
  /**
   * Einen Spiegel als angesehen vermerken — aber erst, wenn er wirklich
   * angesehen wurde. (23.09.2026)
   *
   * ⚠️⚠️ BIS HEUTE GALT: „Wer den Spiegel öffnet, hat ihn angesehen" — und
   *    zwar in derselben Sekunde, in der der Text ankam. Ein Öffnen für den
   *    Bruchteil einer Sekunde reichte: ein Fehltipp in die Kopfzeile, ein
   *    Wischen, eine wiederhergestellte Seite. Martin sah am Morgen den Haken
   *    bei „Wege", obwohl er den Spiegel „hundertprozentig" nicht angesehen
   *    hatte.
   *
   * Die Regel ist jetzt immer noch in einem Satz sagbar — das war am 11.09.
   * der Grund, keinen Prozentsatz und keine Schwelle einzuführen: **Wer den
   * Spiegel ein paar Sekunden offen hatte, hat ihn angesehen.**
   *
   * ⚠️ Das Warten hängt am Abbruch-Signal der Ansicht. Wird sie geschlossen,
   *    bevor die Zeit um ist, wird nichts vermerkt — ohne eigenen Merker, der
   *    das Schließen überdauern könnte.
   *
   * ⚠️ Wer den Spiegel anhört, hat ihn offen, während die Stimme beginnt —
   *    das Anhören zählt also weiterhin (die Sorge vom 11.09.: „Ich habe den
   *    Spiegel angehört, aber das Häkchen ist nicht gekommen").
   */
  /**
   * Welcher Spiegel-Tag gerade im Player liegt — und was danach geschehen
   * soll. Gesetzt, wenn ein Tag geladen wird (heute oder aus dem Verlauf);
   * vermerkt wird erst, wenn der Player „gehört" meldet.
   *
   * ⚠️ 23.09.2026 — hier stand ein 8-Sekunden-Wecker ab dem ÖFFNEN. Martin:
   *    *„Ich habe die Seite zwar kurz geöffnet, aber nicht abgespielt. Der
   *    Spiegel soll erst abgehakt sein, wenn ich ihn auch real angehört
   *    habe."* Im Protokoll: 07:48:08 offen über die Kopfzeile, 07:48:16
   *    vermerkt. Offen ist nicht gehört.
   */
  let hoerTag: { tag: string; danach?: () => void } | null = null;
  const vermerkeSpiegelGehoert = (): void => {
    const ziel = hoerTag;
    if (!ziel || abort.signal.aborted) return;
    void fetch("/api/gelesen", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ art: "spiegel", schluessel: ziel.tag }),
    })
      .then(() => ziel.danach?.())
      .catch(() => undefined);
    gelesenLokal.add(ziel.tag);
    meldeSpur("spiegel", `gehört tag=${ziel.tag}`);
  };
  const merkeSpiegelPosition = (): void => {
    player.setResumeId(
      currentReflection ? `spiegel:${contentVersion(currentReflection)}` : null,
    );
  };

  // Pre.739: Datum + Generierungs-Uhrzeit in Klammern, z.B. „Fr, 06. Juni 2026
  // (14:23)". Uhrzeit aus generatedAt (ISO); fehlt sie, nur das Datum.
  const essenceDateLabel = (
    day: string | undefined,
    generatedAt?: string,
  ): string => {
    const base = day ? `${day}T12:00:00` : generatedAt;
    const dateStr = base
      ? new Date(base).toLocaleDateString("de-AT", {
          weekday: "short",
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "";
    if (generatedAt) {
      const t = new Date(generatedAt);
      if (!Number.isNaN(t.getTime())) {
        const time = t.toLocaleTimeString("de-AT", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Europe/Vienna",
        });
        return dateStr ? `${dateStr} (${time})` : `(${time})`;
      }
    }
    return dateStr;
  };

  // Verlauf — das wachsende Tages-Archiv durchblättern.
  const verlaufBtn = document.createElement("button");
  verlaufBtn.type = "button";
  verlaufBtn.className = "btn btn--ghost btn--sm essence-verlauf";
  verlaufBtn.textContent = "Verlauf";

  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn btn--primary btn--sm essence-refresh";
  refreshBtn.textContent = "Neu spiegeln";
  // pre.907 (Martin): X entfernt — der Zurück-Pfeil ist der einzige Schließen-Weg.
  // 2026-07-21 (Audit Stufe 3a): NORM-Kopf — Untertitel integriert,
  // „Heute neu spiegeln" als einzige sichtbare Aktion, Verlauf im ⋯-Menue.
  // 2026-08-20 (Martin: "Bei Spiegelessenz habe ich den gleichen Scheiss mit
  // dem Button ... Kannst du bitte nicht jede Seite genau so machen, dass sie
  // passt?"):
  //
  //   EINE REGEL STATT SEITENWEISE PFLASTER — nach der Art der Aktion:
  //     etwas ANLEGEN  ("Neu", "+ Einspeisen", "Hochladen") bleibt sichtbar,
  //                     das ist der Grund, warum man die Seite oeffnet.
  //     neu BERECHNEN  ("Neu spiegeln", "Auswerten", Auffrischen) geht ins
  //                     ⋯-Menue — man braucht es selten, und im Kopf draengt
  //                     es Titel und Zurueck-Pfeil zusammen.
  //
  //   Dieselbe Regel gilt seit heute fuer Human Design und Mein Leben.
  const head = buildPageHeader("Essenz-Spiegel", close, {
    subtitle: "Dein Wesenskern, abgeglichen mit deinem heutigen Tag.",
    menuItems: [
      // „Verlauf" ist seit 07.09. ein sichtbarer Knopf unter dem Player —
      // ein zweiter Weg an dieselbe Stelle waere Doppelung.
      { label: "Heute neu spiegeln", onSelect: () => refreshBtn.click() },
    ],
  });
  sheet.appendChild(head);

  // Vorlese-Player als eigene Reihe zwischen Kopf und Body — wie Human Design &
  // „Mein Leben". Sheet-Ebene (nicht im Body), damit er Reloads übersteht
  // (load() leert nur den Body via body.innerHTML="").
  /*
   * ⚠️⚠️ 18.09.2026 — HÖREN AN EINER STELLE, MIT TABS.
   *
   * Martin: *„Man koennte den Player mit den ganzen Elementen
   * zusammenfuehren, vielleicht mit Tabs, weil man sowieso nicht
   * gleichzeitig das Musikstueck hoert und sich das anhoert. So wird das
   * ganze kompakter und uebersichtlicher."*
   *
   * Er hat recht, und die Messung war deutlicher als erwartet: Auf 1440
   * Punkten Breite hatte das Blatt SIEBEN verschiedene linke Kanten —
   *
   *     Kopf 320 · Verlauf/PDF 336 · Text 414 · Klang 430 ·
   *     Kernzeile 438 · Player-Leiste 540 · Player-Titel 635
   *
   * Der Player schwebte mittig in einer leeren Flaeche, waehrend die Knoepfe
   * darunter ganz links klebten. Dazu 484 Punkte Klang-Kasten, der auch
   * dann dastand, wenn man gerade vorlesen liess.
   *
   * Jetzt: EINE Zone, EINE Kante, zwei Reiter. Was man nicht hoert, ist
   * nicht da.
   *
   * ⚠️ Die Reiter nutzen `.book-tabs`/`.book-tab` — die Norm, die Buch und
   *    Biografie schon tragen, und die von sich aus mit der Lesespalte
   *    fluchtet. Ein eigener Tab-Stil waere der zweite fuer dieselbe Sache.
   */
  const hoerTabs = document.createElement("div");
  hoerTabs.className = "book-tabs essence-hoer-tabs";
  hoerTabs.setAttribute("role", "tablist");
  const hoerZone = document.createElement("div");
  hoerZone.className = "essence-hoer-zone";

  const narrateRow = document.createElement("div");
  narrateRow.className = "hd-narrate-row";
  narrateRow.appendChild(player.el);
  hoerZone.appendChild(narrateRow);

  /** Wohin der Klang-Kasten wandert, sobald `load` ihn gebaut hat. */
  const klangFach = document.createElement("div");
  klangFach.className = "essence-klang-fach";
  klangFach.hidden = true;
  hoerZone.appendChild(klangFach);

  type HoerTab = "vorlesen" | "klang";
  let hoerTab: HoerTab = "vorlesen";
  /** Hat der Nutzer selbst getippt? Dann wird seine Wahl nicht ueberstimmt. */
  let tabVomNutzer = false;
  const tabKnoepfe = new Map<HoerTab, HTMLButtonElement>();
  const setzeHoerTab = (t: HoerTab): void => {
    hoerTab = t;
    for (const [id, btn] of tabKnoepfe) {
      const an = id === t;
      btn.classList.toggle("is-active", an);
      btn.setAttribute("aria-selected", an ? "true" : "false");
    }
    /*
     * ⚠️ Nur verstecken, NICHT ausbauen. Ein laufender Klang soll
     *    weiterspielen, waehrend man zum Vorlese-Reiter wechselt — wer ein
     *    Audio-Element aus dem Blatt nimmt, bricht die Wiedergabe ab.
     */
    narrateRow.hidden = t !== "vorlesen" || !spiegelHoerbar;
    klangFach.hidden = t !== "klang";
  };
  for (const [id, wort] of [
    ["vorlesen", "Vorlesen"],
    ["klang", "Klang des Tages"],
  ] as Array<[HoerTab, string]>) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "book-tab";
    btn.setAttribute("role", "tab");
    btn.textContent = wort;
    btn.addEventListener("click", () => {
      tabVomNutzer = true;
      setzeHoerTab(id);
    });
    hoerTabs.appendChild(btn);
    tabKnoepfe.set(id, btn);
  }
  sheet.appendChild(hoerTabs);
  sheet.appendChild(hoerZone);

  /** Ist ein vorlesbarer Text da? Steuert den Vorlese-Reiter. */
  let spiegelHoerbar = false;
  /** Liegt ein Klang vor? Ohne ihn braucht es den zweiten Reiter nicht. */
  let klangDa = false;
  const richteTabsAus = (): void => {
    // 23.09.2026 (Ticket 2549): Für den Hauptnutzer steht der Reiter auch
    // ohne Klang — er trägt die Komponieren-Knöpfe (klangReiterZeigen).
    const klangReiter = klangReiterZeigen(klangDa);
    tabKnoepfe.get("vorlesen")!.hidden = !spiegelHoerbar;
    tabKnoepfe.get("klang")!.hidden = !klangReiter;
    /*
     * ⚠️ Genau EIN Reiter ist keine Wahl — dann ist die Leiste nur Zierrat.
     *    Sie erscheint erst, wenn es wirklich etwas zu entscheiden gibt.
     */
    hoerTabs.hidden = !(spiegelHoerbar && klangReiter);
    /*
     * ⚠️⚠️ WER NICHT GEWAEHLT HAT, BEKOMMT DIE VORGABE — UND ZWAR JEDES MAL.
     *
     * Erster Versuch, im Browser gemessen: Der Klang meldet sich frueher
     * fertig als der Vorlese-Player (er ist schon komponiert, der Text muss
     * erst vertont werden). Die Zone sprang deshalb auf „Klang", und als
     * Sekunden spaeter das Vorlesen bereitstand, blieb sie dort — Martin
     * haette den Spiegel-Player gar nicht mehr gesehen.
     *
     * Solange niemand selbst getippt hat, gilt also: Vorlesen, wenn es geht.
     * Erst ein Klick friert die Wahl ein.
     */
    if (!tabVomNutzer) {
      setzeHoerTab(spiegelHoerbar ? "vorlesen" : "klang");
      return;
    }
    if (hoerTab === "klang" && !klangReiter) setzeHoerTab("vorlesen");
    else if (hoerTab === "vorlesen" && !spiegelHoerbar) setzeHoerTab("klang");
    else setzeHoerTab(hoerTab);
  };
  // 2026-07-15 (Martin, "Abspiel-Knopf fuer den Text fehlt"): die Vorlese-
  // Reihe folgt IMMER dem ready-Zustand des Players. So erscheint der Knopf
  // automatisch, sobald ein Spiegel-/Synthese-Text vorliegt, und verschwindet
  // beim Neu-Spiegeln/Laden/Verlauf — setReady kapselt alle Uebergaenge an
  // EINER Stelle (vorher blieb narrateRow.hidden nach dem Laden auf true).
  narrateRow.hidden = true;
  hoerTabs.hidden = true;
  const _essSetReady = player.setReady;
  player.setReady = (r: boolean): void => {
    _essSetReady(r);
    spiegelHoerbar = r;
    richteTabsAus();
  };

  /*
   * 07.09.2026 — der Verlauf bekommt einen sichtbaren Knopf.
   *
   * Martin: *„Auf der Seite Essenz-Spiegel haette ich gerne einen Button
   * direkt zum Verlauf, ohne dass ich in ein Menue muss."*
   *
   * ⚠️ Der Knopf existierte schon — und war NIE im Blatt. Er wurde gebaut
   *    (`verlaufBtn`) und diente ausschliesslich als Klickziel fuer den
   *    Eintrag im Drei-Punkte-Menue. Sichtbar war er nirgends.
   *
   * ⚠️ Er kommt NICHT in den Kopf. Die Kopf-Norm (Zurueck, Titel, ⋯) gilt
   *    fuer sechzehn Ansichten; ein dritter Knopf dort waere der Anfang
   *    vom Ende. Er steht in einer eigenen, leisen Zeile unter dem Player —
   *    dort, wo im Bild auch der Inhalt beginnt.
   *
   * ⚠️ EIN Knopf, kein zweiter Weg: der Menue-Eintrag faellt weg. Martins
   *    Regel vom 27.08.: nie ein Knopf, der dorthin fuehrt, wo man schon
   *    steht — und nie zwei, die dasselbe tun.
   */
  /*
   * 08.09.2026 — der Spiegel zum Abheften und Weitergeben.
   *
   * Martin: *„Den Essenzspiegel zum Herunterladen oder als PDF zum
   * Ausdrucken, weil ich das gerne abheften möchte. Das ist so tief und so
   * reichhaltig, dass ich es auch gerne weiterschicken wollen würde."*
   *
   * ⚠️ Kein neuer Weg im Kopf: Der Knopf stellt sich neben den Verlauf, in
   *    dieselbe leise Zeile. Die Kopf-Norm (Zurück, Titel, ⋯) gilt für
   *    sechzehn Ansichten und bleibt unangetastet.
   */
  const pdfBtn = document.createElement("button");
  pdfBtn.type = "button";
  pdfBtn.className = "btn btn--ghost btn--sm essence-pdf";
  pdfBtn.textContent = "Als PDF";
  pdfBtn.title = "Diesen Spiegel als PDF laden — zum Abheften oder Weitergeben";
  pdfBtn.addEventListener("click", () => {
    /*
     * Der Tag steht im Titel der gerade gezeigten Fassung; ohne ihn nimmt
     * der Server den heutigen und faellt sonst auf den juengsten zurueck.
     */
    const tag = sheet.dataset.essenzTag ?? "";
    const ziel = tag
      ? `/api/essence-mirror/pdf?date=${encodeURIComponent(tag)}`
      : "/api/essence-mirror/pdf";
    window.open(ziel, "_blank", "noopener");
  });

  const verlaufZeile = document.createElement("div");
  verlaufZeile.className = "essence-verlauf-zeile";
  verlaufZeile.append(verlaufBtn, pdfBtn);
  sheet.appendChild(verlaufZeile);

  const body = document.createElement("div");
  body.className = "hd-body";
  sheet.appendChild(body);
  overlay.appendChild(sheet);
  document.body.appendChild(overlay);
  head.querySelector<HTMLElement>("button")?.focus();


  const load = (force: boolean): void => {
    body.innerHTML = "";
    /*
     * ⚠️ 18.09.2026 — der gemeinsame Rahmen von Wesenskern und Klang ist
     *    weg, weil der Wesenskern weg ist. Er stand seit dem 16.09. hier,
     *    damit beide am Rechner nebeneinander passen („Das wirkt alles zu
     *    gestreckt"). Mit nur noch EINEM Bewohner waere er eine Huelle um
     *    nichts; der Klang steht jetzt direkt im Koerper.
     */
    // Der Knopf gehoert wieder her, sobald der HEUTIGE Spiegel gezeigt
    // wird; im Leer-Zustand und beim einzelnen Tag blenden ihn die
    // jeweiligen Zweige aus (siehe dort).
    verlaufZeile.hidden = false;
    delete sheet.dataset.essenzTag;
    const loading = document.createElement("div");
    loading.className = "hd-longrun";
    loading.innerHTML =
      '<div class="book-spinner"></div><p>Ich halte dir gerade den Spiegel hin … das kann einen Moment dauern.</p>';
    body.appendChild(loading);
    refreshBtn.disabled = true;
    // Vorlesen zurücksetzen (neuer Spiegel = neue Vertonung).
    // 2026-07-14 (Martin): Player WAEHREND des Spiegelns ausblenden —
    // sonst wirkt er anklickbar, obwohl noch nichts erzeugt ist.
    narrateRow.hidden = true;
    currentReflection = "";
    player.reset();
    player.setReady(false);

    // 2026-07-01 (Martin): Beim Oeffnen NUR peeken (nicht erzeugen); der
    // Knopf erzeugt (force). So spiegelt ein frueher Aufruf nicht ungewollt.
    void fetch(`/api/essence-mirror?${force ? "force=1" : "peek=1"}`)
      .then((r) => r.json())
      .then(
        (data: {
          ok?: boolean;
          reason?: string;
          message?: string;
          reflection?: string;
          musicUrl?: string | null;
          musicLyrics?: string | null;
          musicStuecke?: Array<{
            url: string;
            anbieter: "elevenlabs" | "lyria";
            nr: number;
          }>;
          musicAnbieter?: string;
          summary?: {
            type?: string;
            strategy?: string;
            authority?: string;
            signature?: string;
            notSelf?: string;
          };
          cached?: boolean;
          dayMessageCount?: number;
          generatedAt?: string;
        }) => {
          body.innerHTML = "";
          refreshBtn.disabled = false;
          if (!data.ok) {
            const box = document.createElement("div");
            box.className = "hd-empty";
            const lead = document.createElement("p");
            lead.textContent =
              data.message || "Der Spiegel ist gerade nicht verfügbar.";
            box.appendChild(lead);
            if (data.reason === "not_generated") {
              const cta = document.createElement("button");
              cta.type = "button";
              cta.className = "icm-btn icm-btn--primary hd-empty-cta";
              // 16.09.2026: hiess „Heute spiegeln". Der Spiegel meint seit
              // dem Nachtlauf den VORTAG — „heute" waere eine Zusage, die
              // der Knopf nicht einloest.
              cta.textContent = "Jetzt spiegeln";
              cta.addEventListener("click", () => load(true));
              box.appendChild(cta);
            }
            if (data.reason === "missing_birth") {
              const cta = document.createElement("button");
              cta.type = "button";
              cta.className = "icm-btn icm-btn--primary hd-empty-cta";
              cta.textContent = "Zu den Einstellungen";
              cta.addEventListener("click", () => {
                close();
                goToView("settings");
              });
              box.appendChild(cta);
            }
            body.appendChild(box);
            // 2026-07-14 (Martin): im Leer-Zustand den Verlauf gleich
            // darunter — letzter Tag ohne Extra-Klick erreichbar.
            if (data.reason === "not_generated") {
              /*
               * ⚠️ 08.09.2026 — Martin: *„Bei Spiegelessenz habe ich zweimal
               *    Verlauf stehen: einmal mit Pfeil, einmal ohne."*
               *
               * Im Leer-Zustand steht der Verlauf schon HIER, unter dem
               * Hinweis — und darueber stand zusaetzlich der Knopf, der
               * genau dorthin fuehrt. Zwei Wege an dieselbe Stelle, einer
               * davon ohne Wirkung. Das ist seine eigene Regel vom 27.08.:
               * nie ein Knopf, der dorthin fuehrt, wo man schon steht.
               */
              verlaufZeile.hidden = true;
              const histHead = document.createElement("h3");
              histHead.className =
                "essence-section-head essence-section-head--gap";
              histHead.textContent = "Vergangene Tage";
              body.appendChild(histHead);
              renderHistoryInto(body, { letztenAnbieten: true });
            }
            return;
          }
          /*
           * ⚠️⚠️ 18.09.2026 — DER WESENSKERN STEHT NICHT MEHR IM WEG.
           *
           * Martin: *„Da braucht man diesen Block eigentlich so nicht mehr.
           * Den kann man auch als Subinformation im Headerbereich anbringen,
           * aber nur als Zusatzinformation, so wie das Wetter. Denn den
           * Essenzspiegel sieht man sowieso immer … Das weiss der User dann
           * sowieso nach dem hundertsten Mal."*
           *
           * Gemessen, bevor ich etwas angefasst habe: Der eigentliche
           * Spiegeltext begann bei 1386 Pixeln. Davor lagen Player (164 px)
           * und Wesenskern (235 px plus Ueberschrift) — und der Wesenskern
           * ist an jedem Tag derselbe.
           *
           * ⚠️ Die Angaben gehen NICHT verloren: Drei stehen jetzt als
           *    Kopfzeile da (Typ · Autoritaet · Im Flow), alle fuenf im
           *    `title` fuer die Maus, und das Human-Design-Blatt traegt sie
           *    ohnehin vollstaendig. Was hier wegfaellt, ist die
           *    Wiederholung, nicht die Information.
           */
          const s = data.summary ?? {};
          if (s.type) setzeKernZeile(head, s);
          /*
           * Klang des Tages — seit 08.09. BEIDE Fassungen nebeneinander.
           * Martin: „Kann man da beide Buttons anlegen, sodass ich dann
           * beide hören kann?" Der ganze Kasten steht in lib/klang-block.ts;
           * der Verlaufstag weiter unten baut denselben.
           */
          /*
           * ⚠️ 18.09.2026 — der Klang steht NEBEN dem Player, nicht im Text.
           *    Er wandert ins Hoer-Fach (Reiter „Klang des Tages"), statt
           *    484 Punkte mitten im Spiegeltext zu belegen. Gebaut wird er
           *    weiterhin hier, weil er zum Tag gehoert; nur sein Platz ist
           *    ein anderer.
           */
          klangFach.replaceChildren();
          klangFach.appendChild(
            baueKlangBlock({
              // 16.09.2026 (Martin): das Nebeneinander der Anbieter ist ein
              // Vergleich fuer die Entwicklung. Alle anderen sehen EINEN
              // Kasten — "Einfachheit geht vor".
              alleAnbieter: currentUser?.id === 1,
              /*
               * ⚠️ 17.09.2026 — hier stand ebenfalls HEUTE. Der Kasten fragte
               * damit nach der Musik eines Tages, fuer den es keinen Spiegel
               * gibt. Fuenfte Kopie derselben Regel; jetzt aus shared/, wo
               * Server und Oberflaeche dieselbe lesen.
               *
               * Der Verlaufstag weiter unten macht es laengst richtig
               * (`tag: e.day`) — zwei Aufrufe desselben Kastens, zwei
               * verschiedene Antworten auf dieselbe Frage.
               */
              tag: spiegelStandardTag(),
              stuecke: data.musicStuecke ?? null,
              // ⚠️ Ohne `musicStuecke` (aelterer Server) traegt die alte
              //    `musicUrl` die eine Fassung — sonst stuende der Kasten
              //    leer, obwohl Musik da ist.
              antwort: {
                musicUrl: data.musicUrl ?? null,
                anbieter: data.musicAnbieter,
              },
              lyrics: data.musicLyrics ?? null,
            }),
          );
          /*
           * ⚠️ Der Reiter erscheint nur, wenn wirklich ein Klang da ist.
           *    Ein leerer zweiter Reiter waere eine Wahl ohne Inhalt — und
           *    genau die Art halbherzige Anzeige, die Martin heute
           *    beanstandet hat.
           */
          klangDa = Boolean(
            data.musicUrl ||
              (Array.isArray(data.musicStuecke) && data.musicStuecke.length),
          );
          richteTabsAus();
          const interp = document.createElement("div");
          interp.className = "hd-interpretation essence-text";
          renderMarkdown(interp, data.reflection || "");
          body.appendChild(interp);
          /*
           * ⚠️⚠️ 11.09.2026 — DER SPIEGEL VON HEUTE WURDE NIE ALS GELESEN
           *      VERMERKT.
           *
           * Martin: *„Ich habe den Spiegel des Tages angehoert, aber das
           * Haekchen ist trotzdem nicht gekommen. Ab wann gilt 'Spiegel des
           * Tages angehoert'?"*
           *
           * Die Antwort war: gar nicht. Jeder VERGANGENE Tag wird beim
           * Oeffnen vermerkt (weiter unten, `art: "spiegel", schluessel:
           * e.day`) — der heutige, den man ueber diesen Weg oeffnet, als
           * einziger nicht. Im Bestand sieht man Martins Muster: sechs Tage
           * hintereinander der Spiegel vom Vortag, jeden Morgen. Der heutige
           * fehlt in der ganzen Reihe.
           *
           * ⚠️ Die Regel ist jetzt fuer ALLE Tage dieselbe und in einem Satz
           *    sagbar: Wer den Spiegel oeffnet, hat ihn angesehen. Kein
           *    Prozentsatz, kein letzter Satz — eine Schwelle, die man nicht
           *    erklaeren kann, ist eine, die niemand versteht. Und das Wort
           *    im Tagesbild heisst deshalb „angesehen", nicht mehr „gehoert":
           *    Die Regel und das Wort muessen dasselbe sagen.
           *
           * ⚠️ Nur mit Text. Ein leerer Spiegel ist nichts, das man ansehen
           *    koennte — ihn abzuhaken waere gelogen.
           */
          if (data.reflection?.trim()) {
            /*
             * ⚠️ 17.09.2026 — hier stand HEUTE, und das ging an der Sache
             * vorbei: markiert wird der Spiegel, den man gerade hoert, und
             * der beschreibt seit dem Nachtlauf den VORTAG.
             *
             * Am Bestand nachgesehen: am 17.09. um 06:14:46 steht in der
             * Tabelle `gelesen` der Schluessel „2026-09-17" — waehrend der
             * gehoerte Spiegel „2026-09-16" ist. Der Verlauf fragt aber
             * `gelesenLokal.has(e.day)`, also nach dem Spiegel-Tag; die
             * Marke griff deshalb nicht, und der Punkt blieb.
             *
             * ⚠️ Das Tagesbild ist davon NICHT betroffen — es fragt nach dem
             *    Zeitstempel („heute gehoert?"), nicht nach dem Schluessel.
             *    Nur der Verlauf und das Zuruecksetzen brauchen den Tag.
             */
            hoerTag = {
              tag: spiegelStandardTag(),
              // Das Tagesbild holt sich den neuen Stand — sonst bliebe der
              // Punkt bis zum naechsten Laden der Seite grau.
              danach: () =>
                window.dispatchEvent(new CustomEvent("voice-v8:tagesbild-frisch")),
            };
          }
          // Vorlesen freischalten (Text steht).
          currentReflection = data.reflection || "";
          merkeSpiegelPosition();
          player.reset();
          // Pre.739: in blätterbare Abschnitte zerlegen (◀▶ im Player) — wie HD.
          player.setSections(splitNarrationSections(currentReflection));
          player.setReady(!!currentReflection);
          // Synthese im Hintergrund anstoßen → erster Klick spielt sofort.
          if (currentReflection) player.prefetch();
          // „Spiegel anhören" aus dem Zuhause: derselbe Start wie der Knopf.
          // ⚠️ Ob Ton kommt, entscheidet der Browser (Geste) — angeboten,
          //    nicht versprochen (Ticket 2395).
          if (wunsch.abspielen && currentReflection) {
            wunsch.abspielen = false;
            player.play();
          }
          const foot = document.createElement("p");
          foot.className = "essence-foot";
          // Pre.741: zuletzt-aktualisiert-Uhrzeit. Pre.959: Datum EXPLIZIT mit
          // dazu — die Markdown-Überschrift „# Dein Spiegel — …" trägt es nicht
          // immer, dann fehlte das Datum (Martin). Beides aus generatedAt.
          const gt = data.generatedAt ? new Date(data.generatedAt) : null;
          const updatedAt =
            gt && !Number.isNaN(gt.getTime())
              ? gt.toLocaleTimeString("de-AT", {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "Europe/Vienna",
                })
              : "";
          const updatedDate =
            gt && !Number.isNaN(gt.getTime())
              ? gt.toLocaleDateString("de-AT", {
                  weekday: "short",
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                  timeZone: "Europe/Vienna",
                })
              : "";
          foot.textContent =
            (data.cached
              ? "Heutiger Spiegel — für eine frische Sicht oben auf „Neu spiegeln“ tippen."
              : "Frisch gespiegelt.") +
            (updatedDate
              ? ` · zuletzt aktualisiert am ${updatedDate} um ${updatedAt} Uhr`
              : "");
          body.appendChild(foot);
          // Pre.756: Erzeugungs-Uhrzeit auch im Abschnitts-Header zeigen
          // („1/5 · Dein Spiegel — 8. Juni 2026 (14:23 Uhr)").
          player.setSectionSuffix(updatedAt ? `(${updatedAt} Uhr)` : "");
        },
      )
      .catch(() => {
        body.innerHTML = "";
        refreshBtn.disabled = false;
        const p = document.createElement("p");
        p.className = "hd-empty";
        p.textContent =
          "Verbindung fehlgeschlagen — bitte später nochmal versuchen.";
        body.appendChild(p);
      });
  };

  // --- Verlauf (Tages-Archiv) — eine wachsende Liste vergangener Spiegel ---
  /**
   * Einen Tag aus dem Verlauf zeigen. (10.09.2026 nachgezogen)
   *
   * ⚠️ Der Text kommt NICHT mehr aus der Liste. Sie trug ihn fuer alle 60
   *    Tage mit sich (gemessen 353 kB), obwohl je Zeile 150 Zeichen zu sehen
   *    sind. Jetzt steht das Geruest sofort und der Text kommt nach
   *    (gemessen 74 ms) — und dieser Tag gilt als gelesen.
   */
  function showDay(
    e: { day: string; reflection?: string },
    // Seit 25.09. (Ticket 2560) trägt der Tag seinen Titel selbst
    // („Donnerstag, 24. September"); die Aufrufer bleiben, wie sie sind.
    _dateLabel: string,
  ): void {
    body.innerHTML = "";
    player.pause();
    /*
     * ⚠️ 08.09.2026 — DAS war Martins zweiter Verlauf.
     *
     * Er: *„Bei Spiegelessenz habe ich zweimal Verlauf stehen: einmal mit
     * Pfeil, einmal ohne Pfeil."* Genau hier: „← Verlauf" im Blatt, und
     * darueber in der leisen Zeile noch einmal „Verlauf". Zwei Knoepfe,
     * dasselbe Ziel — seine Regel vom 27.08. verbietet das.
     *
     * Der Knopf MIT Pfeil bleibt: Er steht im Zusammenhang („zurueck zur
     * Liste, aus der ich komme"). Der ohne Pfeil tritt so lange zurueck.
     */
    verlaufZeile.hidden = true;
    // Der PDF-Knopf soll GENAU diesen Tag laden, nicht den heutigen.
    sheet.dataset.essenzTag = e.day;
    const back = document.createElement("button");
    back.type = "button";
    back.className = "btn btn--ghost btn--sm essence-back";
    back.textContent = "← Verlauf";
    back.addEventListener("click", () => showHistory());
    body.appendChild(back);
    // 25.09.2026, Ticket 2560: der Tag als Rückblende (lib/spiegel-zeitleiste).
    const ansicht = baueTagesAnsicht({
      day: e.day,
      onSpiegelAnhoeren: () => player.play(),
      vorMeditation: () => player.pause(),
      onGeheZu: (tag) =>
        void zeigeTag(tag).then(() => {
          sheet.scrollTop = 0;
          overlay.scrollTop = 0;
          body.querySelector<HTMLElement>(".zl-tag-titel")?.focus();
        }),
    });
    body.appendChild(ansicht.el);
    const interp = ansicht.spiegelText;
    // Abheften, Klang und Neu spiegeln stehen leise unter dem Tag.
    const pdfHier = pdfBtn.cloneNode(true) as HTMLButtonElement;
    pdfHier.addEventListener("click", () => {
      window.open(
        `/api/essence-mirror/pdf?date=${encodeURIComponent(e.day)}`,
        "_blank",
        "noopener",
      );
    });
    ansicht.fuss.appendChild(pdfHier);
    // Klang des Tages auch für vergangene Tage (seit 14.07.), beide Fassungen.
    if (/^\d{4}-\d{2}-\d{2}$/.test(e.day)) {
      ansicht.fuss.before(
        baueKlangBlock({ tag: e.day, alleAnbieter: currentUser?.id === 1 }),
      );
    }
    void ladeRueckblende(e.day, abort.signal).then((r) => {
      if (sheet.dataset.essenzTag === e.day) ansicht.setzeRueckblende(r);
    });
    void setzeNachbarn(e.day, ansicht);
    const zeigeText = (text: string): void => {
      interp.innerHTML = "";
      renderMarkdown(interp, text);
      e.reflection = text;
      currentReflection = text; // diesen Tag vorlesbar machen
      merkeSpiegelPosition();
      player.reset();
      player.setSections(splitNarrationSections(text));
      player.setReady(!!text);
      if (text) player.prefetch(); // Vertonung vorbereiten
      // Ticket 2698: „lies mir den Spiegel von Dienstag vor“ — derselbe
      // Start wie beim heutigen (load), einmal, sobald der Text steht.
      if (wunsch.abspielen && text) {
        wunsch.abspielen = false;
        player.play();
      }
    };
    if (e.reflection) {
      zeigeText(e.reflection);
    } else {
      interp.innerHTML = '<div class="book-spinner"></div>';
      void fetch(`/api/essence-mirror?date=${encodeURIComponent(e.day)}`, {
        credentials: "same-origin",
      })
        .then((r) => r.json())
        .then((data: { ok?: boolean; reflection?: string; message?: string }) => {
          if (data.ok && data.reflection) {
            zeigeText(data.reflection);
          } else {
            interp.innerHTML = "";
            const p = document.createElement("p");
            p.className = "hd-empty";
            p.textContent =
              data.message || "Dieser Tag lässt sich gerade nicht öffnen.";
            interp.appendChild(p);
          }
        })
        .catch(() => {
          interp.innerHTML = "";
          const p = document.createElement("p");
          p.className = "hd-empty";
          p.textContent = "Dieser Tag lässt sich gerade nicht öffnen.";
          interp.appendChild(p);
        });
    }
    // Gehört — der Punkt an der Zeile im Verlauf wird still. Dieselbe
    // Regel wie beim heutigen Spiegel: erst, wenn er wirklich läuft.
    hoerTag = /^\d{4}-\d{2}-\d{2}$/.test(e.day) ? { tag: e.day } : null;

    // 2026-07-03 (Martin): vergangenen Tag neu spiegeln — exakt DIESER Tag
    // (00:00-24:00), inkl. nachtraeglich Wiederhergestelltem. Ersetzt den
    // alten Schnappschuss. Nur wenn ein Datum (YYYY-MM-DD) vorliegt.
    // 2026-07-14 (Martin, "zwei Buttons — dasselbe?"): fuer HEUTE ist der
    // untere Knopf ein Duplikat des oberen Neu spiegeln -> ausblenden.
    // Fuer VERGANGENE Tage bleibt er (der obere spiegelt immer nur heute).
    /*
     * ⚠️ 17.09.2026 — hier stand HEUTE. Der Kommentar darunter sagt, warum
     * das jetzt falsch waere: „fuer HEUTE ist der untere Knopf ein Duplikat
     * des oberen". Gemeint ist der Tag, den der OBERE Knopf bearbeitet — und
     * der spiegelt seit dem Nachtlauf den Vortag, nicht heute. Ohne diese
     * Aenderung erschiene am aktuellen Spiegel wieder ein zweiter Knopf, der
     * dasselbe tut.
     */
    const tagDesOberenKnopfs = spiegelStandardTag();
    if (/^\d{4}-\d{2}-\d{2}$/.test(e.day) && e.day !== tagDesOberenKnopfs) {
      /*
       * 07.09.2026 — leiser, und mit Rueckfrage.
       *
       * Martin: *„Ist es gut, einen Button zum Essenzspiegeln zu haben? Wenn
       * der Tag noch nicht gelaufen ist, wahrscheinlich schon — aber wenn er
       * schon gelaufen ist, glaube ich, dann waere es besser, wenn er nicht
       * waere."*
       *
       * Er hat recht, und der Grund steht im Kommentar darueber: der Knopf
       * ERSETZT den alten Schnappschuss. Bei einem abgeschlossenen Tag haben
       * sich die Daten aber nicht geaendert — nur das Modell. Ein zweiter
       * Spiegel desselben Tages ist deshalb nicht wahrer, sondern nur ein
       * anderer, und der erste, den Martin gehoert hat, ist danach weg.
       *
       * ⚠️ Nicht entfernt: Der urspruengliche Grund vom 07.07. gilt weiter —
       *    wurde spaeter etwas wiederhergestellt, ist der alte Spiegel
       *    unvollstaendig. Aber er steht nicht mehr in Primaerfarbe unter
       *    JEDEM Tag, wo er wie eine Einladung wirkt, sondern leise. Und er
       *    fragt vorher, weil er etwas wegnimmt.
       */
      const redo = document.createElement("button");
      redo.type = "button";
      redo.className = "btn btn--ghost btn--sm essence-redo";
      redo.textContent = "Neu spiegeln";
      redo.title = "Ersetzt den Spiegel dieses Tages";
      redo.addEventListener("click", () => {
        void (async () => {
          const { confirmSheet } = await import("./lib/sheet-ui.js");
          const ok = await confirmSheet({
            title: "Diesen Tag neu spiegeln?",
            message:
              "Der bestehende Spiegel dieses Tages wird ersetzt — der alte ist danach weg, samt seiner Vertonung. Sinnvoll ist das vor allem, wenn nachträglich Aufnahmen oder Nachrichten dazugekommen sind.",
            confirmLabel: "Ersetzen",
          });
          if (ok) redoJetzt();
        })();
      });
      const redoJetzt = (): void => {
        redo.disabled = true;
        player.pause();
        player.reset();
        player.setReady(false);
        currentReflection = "";
        interp.innerHTML =
          '<div class="book-spinner"></div><p>Ich spiegle diesen Tag neu \u2026 das kann einen Moment dauern.</p>';
        void fetch(
          `/api/essence-mirror?force=1&date=${encodeURIComponent(e.day)}`,
        )
          .then((r) => r.json())
          .then((data: { ok?: boolean; reflection?: string; message?: string }) => {
            redo.disabled = false;
            if (data.ok && data.reflection) {
              e.reflection = data.reflection;
              interp.innerHTML = "";
              renderMarkdown(interp, data.reflection);
              currentReflection = data.reflection;
              merkeSpiegelPosition();
              player.reset();
              player.setSections(splitNarrationSections(currentReflection));
              player.setReady(true);
              player.prefetch();
            } else {
              interp.innerHTML = "";
              const p = document.createElement("p");
              p.className = "hd-empty";
              p.textContent =
                data.message || "Konnte diesen Tag gerade nicht neu spiegeln.";
              interp.appendChild(p);
            }
          })
          .catch(() => {
            redo.disabled = false;
            interp.innerHTML = "";
            renderMarkdown(interp, e.reflection ?? "");
          });
      };
      ansicht.fuss.appendChild(redo);
    }
  }

  function showHistory(): void {
    // In der Liste ist der Knopf der Weg zurueck zum heutigen Spiegel.
    verlaufZeile.hidden = false;
    delete sheet.dataset.essenzTag;
    body.innerHTML = "";
    narrateRow.hidden = true;
    player.setSections([]); // Liste = Einzel-Modus (keine ◀▶)
    player.setReady(false);
    player.pause();
    /*
     * 29.09.2026, Ticket 2789 — über der Liste der Verlauf über die Monate
     * (lib/tagesverlauf). Er lädt seine Zahlen selbst und bleibt weg, wenn
     * es keine gemessene gibt. Nur hier, nicht im Leer-Zustand darüber:
     * dort geht es um den letzten Spiegel, nicht um Monate.
     */
    body.appendChild(baueTagesverlauf({ signal: abort.signal }));
    renderHistoryInto(body);
  }

  // 2026-07-14 (Martin): Verlauf auch INLINE nutzbar (Leer-Zustand zeigt
  // ihn direkt unter dem Hinweis — letzter Tag ohne Extra-Klick anhoerbar).
  /**
   * Wie viele Tage eine Seite traegt. (10.09.2026)
   *
   * Martin: *„Pagination zum Blättern … nur die neuesten … hunderte pro Seite
   * … verlängert die Ladezeiten."* Zwanzig sind zwei Bildschirme; alles
   * Weitere holt der Knopf darunter.
   */
  const VERLAUF_SEITE = 20;

  interface VerlaufEintrag extends Partial<ZeitleistenZeile> {
    day: string;
    generatedAt: string;
    vorschau?: string;
    reflection?: string;
    gelesen?: boolean;
  }

  /**
   * Die Reihe der Tage, wie die Liste sie kennt (neueste zuerst) — für
   * Vor/Zurück in der Ansicht eines Tages. (25.09.2026, Ticket 2560)
   *
   * ⚠️ Dieselbe Reihe wie die Liste, keine zweite Abfrage je Tipp: Die Liste
   *    füllt sie, „Ältere anzeigen" verlängert sie, und nur am Ende der
   *    bekannten Tage wird eine weitere Seite geholt.
   */
  const tagesReihe: VerlaufEintrag[] = [];
  let reiheMehr = false;
  const merkeTag = (e: VerlaufEintrag): void => {
    if (!tagesReihe.some((x) => x.day === e.day)) tagesReihe.push(e);
  };
  const ladeReihe = async (offset: number): Promise<void> => {
    const r = await fetch(
      `/api/essence-mirror/history?limit=${VERLAUF_SEITE}&offset=${offset}`,
      { credentials: "same-origin", signal: abort.signal },
    );
    if (!r.ok) return;
    const j = (await r.json()) as { entries?: VerlaufEintrag[]; mehr?: boolean };
    for (const e of j.entries ?? []) merkeTag(e);
    reiheMehr = Boolean(j.mehr) && (j.entries ?? []).length > 0;
  };
  const setzeNachbarn = async (day: string, ansicht: TagesAnsicht): Promise<void> => {
    try {
      if (!tagesReihe.some((x) => x.day === day)) await ladeReihe(0);
      const letzter = tagesReihe[tagesReihe.length - 1];
      if (reiheMehr && letzter?.day === day) await ladeReihe(tagesReihe.length);
    } catch {
      /* ohne Netz eben ohne Vor/Zurück */
    }
    if (sheet.dataset.essenzTag !== day) return;
    ansicht.setzeNachbarn(nachbarTage(tagesReihe.map((x) => x.day), day));
  };

  /**
   * @param opts.letztenAnbieten Den neuesten Tag als eigenen Knopf ueber die
   *   Liste stellen. (10.09.2026)
   *
   *   Martin, morgens: *„Ich warte schon wieder ewig auf das Erzeugen der
   *   Spiegel-Essenz, damit ich endlich hören kann, was gestern passiert
   *   ist."* Fuer HEUTE gibt es frueh noch keinen Spiegel (er entsteht am
   *   Abend) — und was er wirklich will, liegt eine Zeile tiefer in der
   *   Liste. Jetzt steht es oben, mit dem Datum im Wort.
   */
  function renderHistoryInto(
    container: HTMLElement,
    opts?: { letztenAnbieten?: boolean },
  ): void {
    const loadingP = document.createElement("p");
    loadingP.className = "essence-foot";
    loadingP.textContent = "Lade Verlauf …";
    container.appendChild(loadingP);
    void fetch(`/api/essence-mirror/history?limit=${VERLAUF_SEITE}`, {
      credentials: "same-origin",
    })
      .then((r) => r.json())
      .then(
        (data: {
          ok?: boolean;
          mehr?: boolean;
          entries?: VerlaufEintrag[];
        }) => {
          loadingP.remove();
          const entries = data.entries ?? [];
          tagesReihe.length = 0;
          reiheMehr = Boolean(data.mehr);
          if (entries.length === 0) {
            const p = document.createElement("p");
            p.className = "hd-empty";
            p.textContent =
              "Noch kein Verlauf — dein erster Spiegel entsteht heute Abend, oder gleich über den Knopf Neu spiegeln.";
            container.appendChild(p);
            return;
          }
          const intro = document.createElement("p");
          intro.className = "essence-foot essence-foot--left";
          intro.textContent = data.mehr
            ? "Die neuesten Tage — tippe einen an."
            : `${entries.length} ${entries.length === 1 ? "Tag" : "Tage"} gespeichert — tippe einen an.`;
          container.appendChild(intro);
          /*
           * 2026-08-26 (Martin: "Das schaut einfach nur scheisse aus."):
           *
           * Die zwei Knoepfe waren je ein eigener Block mit `margin: … auto`
           * — sie standen also ZENTRIERT und UNTEREINANDER, waehrend die ganze
           * Seite links ausgerichtet ist. Zwei schwebende Kaestchen mitten im
           * Blatt. Jetzt eine Reihe an derselben Kante wie alles andere.
           */
          const neuester = entries[0];
          if (opts?.letztenAnbieten && neuester) {
            const label = essenceDateLabel(
              neuester.day,
              neuester.generatedAt,
            );
            const gestern = document.createElement("button");
            gestern.type = "button";
            gestern.className = "essence-synth-cta essence-letzter";
            gestern.innerHTML =
              `<span class="essence-synth-zeichen">${zeichen("faden", 18)}</span>` +
              '<span class="essence-synth-text">' +
              '<span class="essence-synth-title">Den letzten Spiegel hören</span>' +
              `<span class="essence-synth-sub">${label}</span>` +
              "</span>";
            gestern.addEventListener("click", () => showDay(neuester, label));
            container.appendChild(gestern);
          }
          const wege = document.createElement("div");
          wege.className = "essence-wege";
          // Die beiden Wege haengen daran, dass es ueberhaupt genug Tage
          // gibt — mit Seiten ist `entries.length` nur noch die erste Seite.
          const genugTage = data.mehr ? VERLAUF_SEITE : entries.length;
          if (genugTage >= 2) {
            const synthBtn = document.createElement("button");
            synthBtn.type = "button";
            synthBtn.className = "essence-synth-cta";
            synthBtn.innerHTML =
              `<span class="essence-synth-zeichen">${zeichen("faden", 18)}</span>` +
              '<span class="essence-synth-text">' +
              '<span class="essence-synth-title">Roter Faden</span>' +
              `<span class="essence-synth-sub">Deine Entwicklung über ${data.mehr ? "alle deine" : `alle ${entries.length}`} Tage</span>` +
              "</span>";
            synthBtn.addEventListener("click", () => showSynthesis(false));
            wege.appendChild(synthBtn);
          }
          /*
           * Die Woche (2026-08-23, Martin: "Wäre auch eine geile Erweiterung").
           * Sie steht bewusst NEBEN dem Roten Faden, nicht statt seiner: Der
           * Faden zeigt den Weg über alle Tage und wird mit jedem Tag
           * allgemeiner. Die Woche ist ein abgeschlossener Zyklus — Anfang und
           * Ende, vergleichbar mit der Woche davor.
           *
           * Ab drei gespiegelten Tagen: Zwei Punkte sind kein Faden.
           *
           * 24.09.2026, Stufe 0a: und nur mit Spiegel im Paket (essenzFrei) —
           * /api/wochen-essenz steht hinter demselben Schalter wie der Spiegel.
           */
          if (genugTage >= 3 && essenzFrei) {
            const wochenBtn = document.createElement("button");
            wochenBtn.type = "button";
            wochenBtn.className = "essence-synth-cta";
            wochenBtn.innerHTML =
              `<span class="essence-synth-zeichen">${zeichen("tag", 18)}</span>` +
              '<span class="essence-synth-text">' +
              '<span class="essence-synth-title">Die Woche</span>' +
              '<span class="essence-synth-sub">Was sich durch die letzten sieben Tage zieht</span>' +
              "</span>";
            wochenBtn.addEventListener("click", () => zeigeWoche(false));
            wege.appendChild(wochenBtn);
          }
          if (wege.childElementCount > 0) container.appendChild(wege);
          const list = document.createElement("div");
          list.className = "essence-history";
          const zeile = (e: VerlaufEintrag): HTMLButtonElement => {
            const dateLabel = essenceDateLabel(e.day, e.generatedAt);
            // 2026-08-26: nahm den Text ROH und schnitt 150 Zeichen ab —
            // inklusive Ueberschrift ("Spiegel des Tages — 25. August 2026")
            // und erster Zwischenueberschrift ("Dein Tag"). Rund 40 von 150
            // Zeichen waren in jeder Zeile dieselben, und das Datum stand ein
            // zweites Mal direkt unter dem Datum.
            // 10.09.2026: dieselbe Funktion rechnet jetzt am SERVER (der Text
            // reist nicht mehr mit); der Weg hier bleibt fuer alte Antworten.
            const excerpt =
              e.vorschau ?? spiegelVorschau(e.reflection ?? "", dateLabel);
            const item = document.createElement("button");
            item.type = "button";
            item.className = "essence-history-item";
            /*
             * Martin: *„wenn etwas noch nicht gelesen ist, so wie Mittwoch,
             * dann sollte man das auch sehen können."* Die Marke lag seit dem
             * 29.08. in der Datenbank (Tabelle `gelesen`) — sie stand nur
             * nirgends. Ein Punkt an der Zeile, kein zweites Wort.
             */
            if (e.gelesen === false && !gelesenLokal.has(e.day)) {
              item.dataset.ungelesen = "1";
            }
            const dEl = document.createElement("span");
            dEl.className = "essence-history-date";
            dEl.textContent = dateLabel;
            const xEl = document.createElement("span");
            xEl.className = "essence-history-excerpt";
            xEl.textContent = excerpt;
            /*
             * 25.09.2026, Ticket 2560 — die Zeile als Tag der Zeitleiste:
             * Datumsspalte mit Linie, daneben die Karte mit Verfassung, dem
             * Tag in einer Zeile, dem Spiegelsatz und der Meditation.
             */
            fuelleDatumsSpalte(dEl, e.day, dateLabel);
            item.appendChild(dEl);
            item.appendChild(
              baueZeilenKarte(e, xEl, item.dataset.ungelesen === "1"),
            );
            merkeTag(e);
            item.addEventListener("click", () => showDay(e, dateLabel));
            return item;
          };
          for (const e of entries) list.appendChild(zeile(e));
          container.appendChild(list);
          if (data.mehr) {
            const mehrBtn = document.createElement("button");
            mehrBtn.type = "button";
            mehrBtn.className = "btn btn--ghost btn--sm essence-mehr";
            mehrBtn.textContent = "Ältere anzeigen";
            let offset = entries.length;
            mehrBtn.addEventListener("click", () => {
              mehrBtn.disabled = true;
              mehrBtn.textContent = "Lade …";
              void fetch(
                `/api/essence-mirror/history?limit=${VERLAUF_SEITE}&offset=${offset}`,
                { credentials: "same-origin" },
              )
                .then((r) => r.json())
                .then(
                  (weiter: { entries?: VerlaufEintrag[]; mehr?: boolean }) => {
                    const neue = weiter.entries ?? [];
                    for (const e of neue) list.appendChild(zeile(e));
                    offset += neue.length;
                    reiheMehr = Boolean(weiter.mehr) && neue.length > 0;
                    if (weiter.mehr && neue.length > 0) {
                      mehrBtn.disabled = false;
                      mehrBtn.textContent = "Ältere anzeigen";
                    } else {
                      mehrBtn.remove();
                    }
                  },
                )
                .catch(() => {
                  mehrBtn.disabled = false;
                  mehrBtn.textContent = "Nochmal versuchen";
                });
            });
            container.appendChild(mehrBtn);
          }
        },
      )
      .catch(() => {
        loadingP.textContent = "Verlauf konnte nicht geladen werden.";
      });
  }

  /*
   * --- Die Woche: der Faden über sieben Tage ---
   *
   * 2026-08-23 (Martin: "Wir haben neben dem Spiegel Essenz zum Beispiel auch
   * eine Wochenessenz. Wäre auch eine geile Erweiterung.")
   *
   * Die Webseite verspricht sie längst — "Über die Woche legt sich ein roter
   * Faden" — gegeben hat es sie nicht.
   *
   * Bewusst nach dem Vorbild von showSynthesis gebaut: gleiche Knöpfe, gleicher
   * Rückweg, gleiche Vorlese-Anbindung. Eine zweite Bedienlogik für dieselbe
   * Sache wäre für Martin nur eine Stolperstelle mehr.
   */
  function zeigeWoche(force: boolean): void {
    body.innerHTML = "";
    currentReflection = "";
    player.reset();
    player.setReady(false);
    const laedt = document.createElement("div");
    laedt.className = "hd-longrun";
    laedt.innerHTML =
      '<div class="book-spinner"></div><p>Ich lese deine Woche … das kann einen Moment dauern.</p>';
    body.appendChild(laedt);
    void fetch(`/api/wochen-essenz${force ? "?force=1" : ""}`)
      .then((r) => r.json())
      .then(
        (data: {
          ok?: boolean;
          reason?: string;
          message?: string;
          reflection?: string;
          zeitraum?: string;
          cached?: boolean;
        }) => {
          body.innerHTML = "";
          const back = document.createElement("button");
          back.type = "button";
          back.className = "btn btn--ghost btn--sm essence-back";
          back.textContent = "← Verlauf";
          back.addEventListener("click", () => showHistory());
          body.appendChild(back);
          if (!data.ok) {
            const box = document.createElement("div");
            box.className = "hd-empty";
            const lead = document.createElement("p");
            // "zu wenige Tage" ist kein Fehler, sondern ein Zustand — die
            // Meldung vom Server sagt genau, wie viele noch fehlen.
            lead.textContent =
              data.message || "Die Woche ist gerade nicht verfügbar.";
            box.appendChild(lead);
            body.appendChild(box);
            return;
          }
          const h = document.createElement("p");
          h.className = "essence-day-label";
          // 2026-08-26: das ✦ war ein Dingbat — auf jedem Geraet anders
          // gezeichnet und nicht einfaerbbar. Die Ueberschrift traegt den
          // Namen, das Zeichen sitzt beim Knopf, der hierher fuehrt.
          h.textContent = `Die Woche · ${data.zeitraum ?? ""}`;
          body.appendChild(h);
          const text = document.createElement("div");
          text.className = "hd-interpretation essence-text";
          renderMarkdown(text, data.reflection || "");
          body.appendChild(text);
          currentReflection = data.reflection || ""; // vorlesbar
          merkeSpiegelPosition();
          player.reset();
          player.setSections(splitNarrationSections(currentReflection));
          player.setReady(!!currentReflection);
          if (currentReflection) player.prefetch();
          const neuBtn = document.createElement("button");
          neuBtn.type = "button";
          neuBtn.className =
            "btn btn--primary btn--sm essence-refresh essence-refresh--inline";
          neuBtn.textContent = "Neu lesen";
          neuBtn.addEventListener("click", () => zeigeWoche(true));
          body.appendChild(neuBtn);
        },
      )
      .catch(() => {
        body.innerHTML = "";
        const p = document.createElement("p");
        p.className = "hd-empty";
        p.textContent = "Die Woche konnte nicht geladen werden.";
        body.appendChild(p);
      });
  }

  // --- Roter Faden: Synthese über alle gespiegelten Tage ---
  function showSynthesis(force: boolean): void {
    body.innerHTML = "";
    currentReflection = "";
    player.reset();
    player.setReady(false);
    const loadingS = document.createElement("div");
    loadingS.className = "hd-longrun";
    loadingS.innerHTML =
      '<div class="book-spinner"></div><p>Ich webe den roten Faden durch all deine Tage … das kann einen Moment dauern.</p>';
    body.appendChild(loadingS);
    void fetch(`/api/essence-mirror/synthesis${force ? "?force=1" : ""}`)
      .then((r) => r.json())
      .then(
        (data: {
          ok?: boolean;
          reason?: string;
          message?: string;
          synthesis?: string;
          daysCovered?: number;
          cached?: boolean;
        }) => {
          body.innerHTML = "";
          const back = document.createElement("button");
          back.type = "button";
          back.className = "btn btn--ghost btn--sm essence-back";
          back.textContent = "← Verlauf";
          back.addEventListener("click", () => showHistory());
          body.appendChild(back);
          if (!data.ok) {
            const box = document.createElement("div");
            box.className = "hd-empty";
            const lead = document.createElement("p");
            lead.textContent =
              data.message || "Der rote Faden ist gerade nicht verfügbar.";
            box.appendChild(lead);
            if (data.reason === "missing_birth") {
              const cta = document.createElement("button");
              cta.type = "button";
              cta.className = "icm-btn icm-btn--primary hd-empty-cta";
              cta.textContent = "Zu den Einstellungen";
              cta.addEventListener("click", () => {
                close();
                goToView("settings");
              });
              box.appendChild(cta);
            }
            body.appendChild(box);
            return;
          }
          const h = document.createElement("p");
          h.className = "essence-day-label";
          h.textContent = `Roter Faden · ${data.daysCovered ?? 0} Tage`;
          body.appendChild(h);
          const interp = document.createElement("div");
          interp.className = "hd-interpretation essence-text";
          renderMarkdown(interp, data.synthesis || "");
          body.appendChild(interp);
          currentReflection = data.synthesis || ""; // vorlesbar
          player.reset();
          player.setSections(splitNarrationSections(currentReflection));
          player.setReady(!!currentReflection);
          if (currentReflection) player.prefetch(); // Vertonung vorbereiten
          const reBtn = document.createElement("button");
          reBtn.type = "button";
          reBtn.className =
            "btn btn--primary btn--sm essence-refresh essence-refresh--inline";
          reBtn.textContent = "Neu weben";
          reBtn.addEventListener("click", () => showSynthesis(true));
          body.appendChild(reBtn);
        },
      )
      .catch(() => {
        body.innerHTML = "";
        const p = document.createElement("p");
        p.className = "hd-empty";
        p.textContent = "Der rote Faden konnte nicht geladen werden.";
        body.appendChild(p);
      });
  }

  /*
   * Ein Knopf, zwei Richtungen. Wer im Verlauf steht, will zurueck zum
   * heutigen Spiegel — ein zweiter Knopf dafuer waere derselbe Weg noch
   * einmal.
   */
  let imVerlauf = false;
  const zeigeVerlaufKnopf = (): void => {
    verlaufBtn.textContent = imVerlauf ? "Heutiger Spiegel" : "Verlauf";
    verlaufBtn.setAttribute(
      "aria-label",
      imVerlauf
        ? "Zurück zum heutigen Spiegel"
        : "Verlauf der bisherigen Spiegel",
    );
  };
  zeigeVerlaufKnopf();
  verlaufBtn.addEventListener("click", () => {
    imVerlauf = !imVerlauf;
    zeigeVerlaufKnopf();
    if (imVerlauf) showHistory();
    else load(false);
  });
  refreshBtn.addEventListener("click", () => load(true));

  /**
   * Einen bestimmten Tag zeigen — aus dem Verlauf, nicht neu erzeugt.
   *
   * ⚠️ Bewusst ueber `/history` und NICHT ueber `/api/essence-mirror?date=`:
   * Letzteres kennt zwar ein Datum, braucht aber `force=1`, um etwas zu
   * liefern — und `force` erzeugt den Spiegel NEU und ersetzt den
   * Schnappschuss des Tages. Wer nachlesen will, was am Montag stand, darf
   * ihn nicht dabei ueberschreiben.
   *
   * Faellt der Tag durch (kein Eintrag, Netz weg), oeffnet das Blatt wie
   * bisher heute — lieber der falsche Tag als eine leere Seite.
   */
  const zeigeTag = async (schluessel: string): Promise<boolean> => {
    try {
      /*
       * ⚠️ 25.09.2026 — hier wurde `reflection` verlangt. Die Liste trägt den
       *    Text seit dem 10.09. nicht mehr mit (showDay holt ihn nach) — der
       *    Weg aus dem Zuhause fiel deshalb IMMER auf heute zurück. Es reicht,
       *    dass es den Tag gibt; bekannte Tage (Vor/Zurück) ohne neue Abfrage.
       */
      let e = tagesReihe.find((x) => x.day === schluessel);
      if (!e) {
        const r = await fetch("/api/essence-mirror/history", {
          signal: abort.signal,
        });
        if (!r.ok) return false;
        const j = (await r.json()) as { ok?: boolean; entries?: VerlaufEintrag[]; mehr?: boolean };
        if (tagesReihe.length === 0) reiheMehr = Boolean(j.mehr);
        for (const x of j.entries ?? []) merkeTag(x);
        e = (j.entries ?? []).find((x) => x.day === schluessel);
      }
      if (!e) return false;
      showDay(e, essenceDateLabel(e.day, e.generatedAt));
      return true;
    } catch {
      return false;
    }
  };

  if (tag && /^\d{4}-\d{2}-\d{2}$/.test(tag)) {
    void zeigeTag(tag).then((ok) => {
      if (ok) return;
      // Ticket 2698 (Nachbesserung pre.3394): erbeten war DIESER Tag. Der
      // aktuelle Spiegel steht als Rückfall da, er spielt aber nicht still
      // an seiner Stelle los.
      wunsch.abspielen = false;
      load(false);
    });
  } else if (wunsch.verlauf) {
    // Derselbe Weg wie der Knopf „Verlauf" im Blatt — kein zweiter.
    verlaufBtn.click();
  } else {
    load(false);
  }
}

/**
 * „Kunde anlegen" — Modal, das einen Chart-only-Kunden anlegt (KEIN Login):
 * Name + Geburtsdaten → POST /api/admin/users OHNE E-Mail → der Server vergibt
 * eine Platzhalter-Adresse @chart.local und login_allowed=0. Der Kunde landet in
 * derselben DB wie echte Nutzer, also später per „Nutzer"-Bereich freigebbar.
 * Optional eine echte E-Mail für genau diese spätere Freigabe. Bei Erfolg ruft
 * `onCreated(neuerUser)` (z.B. um direkt sein Chart zu öffnen).
 */
function openCustomerModal(
  onCreated: (u: { id: number; name: string }) => void,
): void {
  const overlay = document.createElement("div");
  overlay.className = "cust-overlay";
  const card = document.createElement("form");
  card.className = "usr-card usr-form cust-modal";
  card.noValidate = true;

  const head = document.createElement("div");
  head.className = "cust-modal-head";
  const title = document.createElement("h3");
  title.className = "usr-card-title";
  title.textContent = "Kunde anlegen";
  head.appendChild(title);
  head.appendChild(buildCloseButton(() => overlay.remove()));
  card.appendChild(head);

  const sub = document.createElement("p");
  sub.className = "usr-hint";
  sub.textContent =
    "Legt einen Kunden NUR zum Chart-Ansehen an (kein Login). Gespeichert wie ein " +
    "normaler Nutzer — später im Bereich „Nutzer“ freigebbar.";
  card.appendChild(sub);

  const grid = document.createElement("div");
  grid.className = "usr-form-grid";
  const field = (
    labelText: string,
    type: string,
    placeholder = "",
  ): HTMLInputElement => {
    const wrap = document.createElement("label");
    wrap.className = "usr-field";
    const lab = document.createElement("span");
    lab.className = "usr-field-label";
    lab.textContent = labelText;
    const input = document.createElement("input");
    input.type = type;
    input.className = "usr-input";
    if (placeholder) input.placeholder = placeholder;
    wrap.appendChild(lab);
    wrap.appendChild(input);
    grid.appendChild(wrap);
    return input;
  };
  const nameInput = field("Name", "text", "z.B. Maria Muster");
  const birthdayInput = field("Geburtsdatum", "date");
  const birthTimeInput = field("Geburtszeit", "time");
  const birthPlaceInput = field("Geburtsort", "text", "z.B. Wien");
  // 24.09.2026, Ticket 2567: das Land bestimmt die Zeitzone des Charts.
  const birthCountrySelect = geburtslandFeldImRaster(grid);
  const emailInput = field(
    "E-Mail (optional, für spätere Freigabe)",
    "email",
    "leer = nur Chart",
  );
  card.appendChild(grid);

  const notice = document.createElement("p");
  notice.className = "cust-notice";
  notice.hidden = true;
  card.appendChild(notice);
  const showErr = (m: string): void => {
    notice.textContent = m;
    notice.hidden = false;
  };

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "usr-btn usr-btn--primary";
  submit.textContent = "Anlegen & Chart öffnen";
  card.appendChild(submit);

  card.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const name = nameInput.value.trim();
    if (!name) {
      showErr("Bitte einen Namen eingeben.");
      nameInput.focus();
      return;
    }
    if (!birthdayInput.value || !birthTimeInput.value) {
      showErr("Für das Chart brauche ich Geburtsdatum UND Geburtszeit.");
      return;
    }
    // IMMER Chart-only (kein Login), auch wenn eine E-Mail angegeben ist.
    const payload: Record<string, string | boolean> = {
      name,
      birthday: birthdayInput.value,
      birthTime: birthTimeInput.value,
      loginAllowed: false,
    };
    const place = birthPlaceInput.value.trim();
    if (place) payload.birthPlace = place;
    payload.birthCountry = birthCountrySelect.value;
    const email = emailInput.value.trim().toLowerCase();
    if (email) payload.email = email;

    submit.disabled = true;
    notice.hidden = true;
    void (async () => {
      try {
        const res = await fetch("/api/admin/users", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });
        const data = (await res.json().catch(() => null)) as {
          ok?: boolean;
          error?: string;
          user?: { id: number; name?: string | null };
        } | null;
        if (!res.ok || !data || data.ok === false || !data.user) {
          showErr(
            data?.error === "email_exists"
              ? "Diese E-Mail ist schon vergeben."
              : data?.error === "bad_email"
                ? "Die E-Mail ist ungültig."
                : "Anlegen fehlgeschlagen — bitte erneut versuchen.",
          );
          submit.disabled = false;
          return;
        }
        overlay.remove();
        onCreated({ id: data.user.id, name: data.user.name?.trim() || name });
      } catch {
        showErr("Keine Verbindung — bitte später erneut.");
        submit.disabled = false;
      }
    })();
  });

  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) overlay.remove();
  });
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      overlay.remove();
      document.removeEventListener("keydown", onKey);
    }
  };
  document.addEventListener("keydown", onKey);

  overlay.appendChild(card);
  document.body.appendChild(overlay);
  nameInput.focus();
}

// Teilen-Dialog: EIN Einstieg für alle Share-Aktionen (luftig) — Link kopieren,
// Geburtsdaten ausblenden (Toggle), Link widerrufen. Erzeugt/holt beim Öffnen den
// stabilen Token (create-or-get).
async function openHdShareDialog(userId: number): Promise<void> {
  const ac = new AbortController();
  const overlay = document.createElement("div");
  overlay.className = "hd-share-overlay";
  const close = (): void => {
    ac.abort();
    overlay.remove();
  };
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Escape") close();
    },
    { signal: ac.signal },
  );
  const card = document.createElement("div");
  card.className = "hd-share-card";
  card.innerHTML =
    `<h3 class="hd-share-title">Chart teilen</h3>` +
    `<p class="hd-share-note">Öffentlicher Link — jeder mit dem Link kann diesen Chart ansehen (nur lesen, kein Login).</p>` +
    `<p class="hd-share-note hd-share-ablauf" hidden></p>` +
    `<div class="hd-share-linkrow"><input class="hd-share-link" readonly value="…" aria-label="Share-Link"><button type="button" class="btn btn--primary btn--sm hd-share-copy">Kopieren</button></div>` +
    `<label class="hd-share-toggle"><input type="checkbox" class="hd-share-hidebirth"> Geburtsdaten auf der geteilten Seite ausblenden</label>` +
    `<div class="hd-share-actions"><button type="button" class="btn btn--danger btn--sm hd-share-revoke">Link widerrufen</button><button type="button" class="btn btn--ghost btn--sm hd-share-close">Schließen</button></div>`;
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const linkInput = card.querySelector(".hd-share-link") as HTMLInputElement;
  const copyBtn = card.querySelector(".hd-share-copy") as HTMLButtonElement;
  const hideBox = card.querySelector(".hd-share-hidebirth") as HTMLInputElement;
  const revokeBtn = card.querySelector(".hd-share-revoke") as HTMLButtonElement;
  const ablaufEl = card.querySelector(".hd-share-ablauf") as HTMLElement;
  card.querySelector(".hd-share-close")?.addEventListener("click", close);

  const share = async (
    hideBirth?: boolean,
  ): Promise<{
    ok?: boolean;
    url?: string;
    hideBirth?: boolean;
    expiresAt?: string;
  }> => {
    const body: { userId: number; hideBirth?: boolean } = { userId };
    if (hideBirth !== undefined) body.hideBirth = hideBirth;
    const r = await fetch("/api/human-design/share", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return (await r.json()) as {
      ok?: boolean;
      url?: string;
      hideBirth?: boolean;
      expiresAt?: string;
    };
  };

  try {
    const data = await share();
    if (!data?.ok || !data.url) {
      showToast("Teilen nicht möglich.", "warning");
      close();
      return;
    }
    linkInput.value = data.url;
    hideBox.checked = !!data.hideBirth;
    // 24.09.2026, Ticket 2567: Links laufen nach 30 Tagen ab. Danach legt
    // „Teilen" einen neuen an; „Link widerrufen" beendet ihn sofort.
    const bis = data.expiresAt ? new Date(data.expiresAt) : null;
    if (bis && !Number.isNaN(bis.getTime())) {
      ablaufEl.textContent = `Der Link gilt bis ${bis.toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" })}. Danach ist er ungültig; „Teilen“ erzeugt dann einen neuen.`;
      ablaufEl.hidden = false;
    }
  } catch {
    showToast("Teilen fehlgeschlagen.", "warning");
    close();
    return;
  }

  copyBtn.addEventListener("click", () => {
    void navigator.clipboard
      .writeText(linkInput.value)
      .then(() => showToast("Link kopiert", "success"))
      .catch(() => linkInput.select());
  });
  hideBox.addEventListener("change", () => {
    void share(hideBox.checked).then(() =>
      showToast(
        hideBox.checked
          ? "Geburtsdaten werden ausgeblendet"
          : "Geburtsdaten wieder sichtbar",
        "success",
      ),
    );
  });
  revokeBtn.addEventListener("click", () => {
    revokeBtn.disabled = true;
    void fetch("/api/human-design/share/revoke", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    })
      .then(() => {
        showToast("Link widerrufen", "success");
        close();
      })
      .catch(() => {
        showToast("Widerrufen fehlgeschlagen.", "warning");
        revokeBtn.disabled = false;
      });
  });
}

/**
 * Wozu Human Design hier steht — beim ersten Mal offen. (16.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„super waere wenn das System, aktiv beim ersten Mal den User hilft
 * Human Design zu verstehen und dass sie ein integraler Bestandteil unseres
 * Systems ist, da sie hilft besser noch seinen Weg zu finden, weisst du was
 * ich meine?"*
 *
 * Bis heute stand hier sofort der Bodygraph: ein Diagramm mit Dreiecken,
 * Zahlen und Linien, ohne ein Wort dazu, was es soll. Wer Human Design nicht
 * kennt, sieht ein Schaubild und geht wieder.
 *
 * ## Warum aufklappbar statt weggeklickt
 *
 * ⚠️ Ein Hinweis mit „Verstanden"-Knopf ist danach WEG, und wer ihn spaeter
 *    doch braucht, findet ihn nicht mehr. Ein `details` ist beim ersten Mal
 *    offen, danach eine ruhige Zeile — es nimmt nie wieder Platz weg und
 *    geht trotzdem nicht verloren.
 *
 * ⚠️ Der Ton ist bewusst zurueckhaltend. Diese App behauptet an keiner
 *    Stelle, Human Design sei wahr; sie benutzt es als Massstab, an dem sich
 *    ein gelebter Tag ablesen laesst. Ein Text, der mehr verspricht, waere
 *    eine Behauptung, die das System nicht einloest.
 */
const HD_EINFUEHRUNG_KEY = "voice-v8.hd-einfuehrung-zu";

function baueHdEinfuehrung(): HTMLElement {
  const block = document.createElement("details");
  block.className = "hd-einfuehrung";
  let zu = false;
  try {
    zu = localStorage.getItem(HD_EINFUEHRUNG_KEY) === "1";
  } catch {
    /* localStorage nicht verfuegbar — dann eben offen */
  }
  block.open = !zu;

  const titel = document.createElement("summary");
  titel.className = "hd-einfuehrung-titel";
  titel.textContent = "Wozu Human Design hier steht";
  block.appendChild(titel);

  for (const satz of [
    "Human Design ist eine Landkarte, die aus deinem Geburtsdatum, deiner Geburtszeit und deinem Geburtsort entsteht. Sie sagt nicht, wer du bist. Sie beschreibt, wie du von Natur aus entscheidest, wo deine eigene Kraft sitzt und wo du Fremdes aufnimmst.",
    "In Vespera ist sie kein eigenes Kapitel, sondern der Maßstab im Hintergrund. Der Essenz-Spiegel hält jeden Morgen deinen gelebten Tag daneben — und das Spannende ist nicht die Karte, sondern der Abstand zwischen ihr und dem, was der Tag aus dir gemacht hat.",
    "Du musst nichts davon glauben. Nimm es als Sprache für etwas, das dir an dir ohnehin auffällt. Wo sie trifft, hilft sie dir, deinen Weg zu erkennen. Wo sie nicht trifft, lässt du sie liegen.",
  ]) {
    const p = document.createElement("p");
    p.className = "hd-einfuehrung-satz";
    p.textContent = satz;
    block.appendChild(p);
  }

  // Zuklappen heisst: beim naechsten Mal ruhig bleiben.
  block.addEventListener("toggle", () => {
    try {
      localStorage.setItem(HD_EINFUEHRUNG_KEY, block.open ? "0" : "1");
    } catch {
      /* localStorage nicht verfuegbar */
    }
  });
  return block;
}

async function openHumanDesignSheet(target?: {
  userId: number;
  name: string;
}): Promise<void> {
  // Admin-Modus: ?userId=… an alle HD-Endpunkte hängen, damit ein Admin das
  // Chart eines anderen Nutzers sieht. Ohne target (Selbst-Ansicht) bleibt q
  // leer → exakt das alte Verhalten. Der Server ignoriert ?userId für
  // Nicht-Admins (kein Cross-User-Zugriff).
  const q = target ? `?userId=${target.userId}` : "";

  // Cache-Bust für das Chart-Bild: der Server liefert chart.svg mit Cache-
  // Control, daher MUSS die URL pro Build wechseln, sonst zeigt der Browser
  // nach einem Deploy stundenlang die alte gecachte Grafik (Bug 2026-05-31:
  // „nichts ändert sich"). `v` = Build-Version bricht den Cache bei jedem
  // Deploy; optional `t` für manuelles Neuladen.
  const chartVer = (
    typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "1"
  ).replace(/[^\w.-]/g, "");
  const chartSvgUrl = (bust?: number): string => {
    const params = new URLSearchParams();
    if (target) params.set("userId", String(target.userId));
    params.set("v", chartVer);
    if (bust !== undefined) params.set("t", String(bust));
    // Ansicht: nur Design (rot) / nur Persönlichkeit (schwarz) / beide.
    const view = loadSettings().hdChartView;
    if (view === "design" || view === "personality") params.set("view", view);
    return `/api/human-design/chart.svg?${params.toString()}`;
  };

  // Drawer schließen (wie beim „Mein Leben"-Sheet).
  // 2026-07-05 (Martin): der ECHTE Drawer laeuft ueber body.drawer-open —
  // Alt-Mechanismus schloss nichts, Menue blieb ueber dem Inhalt haengen.
  closeDrawerForNavigation();
  document
    .getElementById("nav-menu-btn")
    ?.setAttribute("aria-expanded", "false");

  const existing = document.querySelector(
    ".hd-overlay, .essence-overlay, .conn-overlay",
  );
  if (existing) {
    // Vorheriges Sheet SAUBER schließen (entfernt dessen keydown-Listener +
    // Tooltip), sonst leckt der Listener bei jedem Re-Open.
    if (hdActiveCleanup) hdActiveCleanup();
    else {
      (existing.querySelector("audio"))?.pause();
      existing.remove();
    }
  }

  const overlay = document.createElement("div");
  overlay.className = "sheet hd-overlay page-sheet";

  // Zentrales Aufräumen aus ALLEN Schließwegen (X, Backdrop, Esc, Re-Open) —
  // verhindert keydown-Listener- und Geister-Tooltip-Lecks (System-Audit).
  // AbortController entfernt den keydown-Listener automatisch bei abort();
  // der Vorlese-Player (buildNarrationPlayer) hört auf sheetAbort.signal und
  // stoppt sein eigenes Audio dabei selbst.
  const sheetAbort = new AbortController();
  function closeSheet(): void {
    if (hdCenterTipEl) hdCenterTipEl.hidden = true; // Geister-Tooltip weg
    sheetAbort.abort(); // entfernt den keydown-Listener
    overlay.remove();
    clearHdHash(); // Deep-Link wieder entfernen
    if (hdActiveCleanup === closeSheet) hdActiveCleanup = null;
  }
  hdActiveCleanup = closeSheet;
  attachEdgeSwipeBack(overlay, () => closeSheet());
  // Hash spiegelt die offene Analyse (Reload-fest + teilbarer Link).
  setHdHash(target);
  document.addEventListener(
    "keydown",
    (ev) => {
      if (ev.key === "Escape") closeSheet();
    },
    { signal: sheetAbort.signal },
  );

  const sheet = document.createElement("div");
  sheet.className = "sheet-card";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Human Design");

  // 2026-07-21 (Audit Stufe 3b): NORM-Kopf — eine sichtbare Aktion,
  // Admin-Werkzeuge im ⋯-Menue, Nutzer-Wahl als eigene Zeile unterm Kopf.
  const hdMenu: Array<{ label: string; onSelect: () => void }> = [];
  const adminRow = document.createElement("div");
  adminRow.className = "conn-picker-row hd-admin-row";
  adminRow.hidden = true;

  // Pre.647 (User-Wunsch): Als Superuser (uid 1) direkt im HD-Sheet zwischen
  // ALLEN angelegten Nutzern wechseln, um ihre Charts anzusehen — ohne Umweg
  // über den Nutzer-Bereich. Für Nicht-Admins unsichtbar (der Server ignoriert
  // ?userId ohnehin → kein Cross-User-Zugriff).
  if (currentUser?.id === 1) {
    const picker = document.createElement("select");
    picker.className = "hd-user-select";
    picker.setAttribute("aria-label", "Wessen Chart anzeigen");
    const selfOpt = document.createElement("option");
    selfOpt.value = "";
    selfOpt.textContent = currentUser?.name
      ? `${currentUser.name} (ich)`
      : "Mein Chart";
    if (!target) selfOpt.selected = true;
    picker.appendChild(selfOpt);
    adminRow.hidden = false;
    adminRow.appendChild(picker);
    // Nutzerliste async nachladen — blockiert das Öffnen des Sheets nicht.
    void fetch("/api/admin/users", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((d: { users?: HdKonto[] }) => {
        // 24.09.2026: getrennt nach Nutzern, nie benutzten Konten und
        // Human-Design-Profilen; technische Konten stehen nicht darin (hd-auswahl.ts).
        // „ich" (uid 1) steht schon oben.
        const users = Array.isArray(d?.users) ? d.users : [];
        fuelleHdAuswahl(picker, users, { ohne: 1, gewaehlt: target?.userId });
      })
      .catch(() => undefined);
    picker.addEventListener("change", () => {
      const val = picker.value;
      const label =
        picker.options[picker.selectedIndex]?.textContent?.trim() || "";
      closeSheet();
      if (!val) {
        void openHumanDesignSheet();
      } else {
        void openHumanDesignSheet({ userId: Number(val), name: label });
      }
    });
  }

  // „Auswertung neu" — refetcht mit ?force=1 und lädt den Bodygraph neu.
  // Das Chart selbst (Typ/Autorität/Profil/Tore) ist deterministisch und
  // ändert sich nie bei gleichen Geburtsdaten — neu geschrieben wird nur der
  // Auswertungs-Text. Sinnvoll v.a. nach einer Geburtsdaten-Korrektur oder
  // wenn man eine andere Formulierung möchte.
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn btn--ghost btn--sm hd-refresh";
  refreshBtn.title =
    "Schreibt die Text-Auswertung neu (das Chart selbst ändert sich nicht). " +
    "Nützlich nach Geburtsdaten-Korrektur.";
  refreshBtn.setAttribute("aria-label", "Auswertung neu schreiben");
  const refreshIcon = refreshIconHtml("hd-refresh-icon");
  refreshBtn.innerHTML = `${refreshIcon}<span class="hd-refresh-label">Auswerten</span>`;

  // „Verbindung" (Composite zweier Nutzer) — Unterkategorie von Human Design
  // (Admin-only), damit die Sidebar schlank bleibt. Öffnet die Verbindungs-Ansicht.
  if (currentUser?.id === 1) {
    const connBtn = document.createElement("button");
    connBtn.type = "button";
    connBtn.className = "btn btn--ghost btn--sm hd-refresh";
    connBtn.title = "Zwei Charts übereinanderlegen (Composite/Verbindung)";
    connBtn.setAttribute("aria-label", "Verbindung");
    connBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
      '<path fill="currentColor" d="M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm6 0a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM9 9a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm6 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"/></svg>' +
      '<span class="hd-refresh-label">Verbindung</span>';
    connBtn.addEventListener("click", () => {
      closeSheet();
      void openConnectionSheet();
    });
    hdMenu.push({ label: "Verbindung", onSelect: () => connBtn.click() });
  }

  // „Kunde anlegen" (Admin) — einen Chart-only-Kunden OHNE Login-User anlegen
  // und sein Chart sofort ansehen. Gespeichert in derselben DB → später freigebbar.
  if (currentUser?.id === 1) {
    const custBtn = document.createElement("button");
    custBtn.type = "button";
    custBtn.className = "btn btn--ghost btn--sm hd-refresh";
    custBtn.title =
      "Kunden (nur Chart, kein Login) anlegen und sein Chart ansehen";
    custBtn.setAttribute("aria-label", "Kunde anlegen");
    custBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
      '<path fill="currentColor" d="M15 14c-2.67 0-8 1.34-8 4v2h10v-2c0-.99.74-2.06 2-2.86C18.16 14.4 16.31 14 15 14zm0-2a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM6 10V7H4v3H1v2h3v3h2v-3h3v-2H6z"/></svg>' +
      '<span class="hd-refresh-label">Kunde anlegen</span>';
    custBtn.addEventListener("click", () => {
      openCustomerModal((u) => {
        closeSheet();
        void openHumanDesignSheet({ userId: u.id, name: u.name });
      });
    });
    hdMenu.push({ label: "Kunde anlegen", onSelect: () => custBtn.click() });
  }

  // „Teilen" (Admin) — öffentlichen, read-only Link zu DIESEM Chart erzeugen +
  // in die Zwischenablage kopieren (eigenständige Seite /hd/share/<token>, ohne Login).
  if (currentUser?.id === 1) {
    const shareBtn = document.createElement("button");
    shareBtn.type = "button";
    shareBtn.className = "btn btn--ghost btn--sm hd-refresh";
    shareBtn.title = "Öffentlichen Link zu diesem Chart kopieren (nur Ansehen)";
    shareBtn.setAttribute("aria-label", "Teilen");
    shareBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
      '<path fill="currentColor" d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>' +
      '<span class="hd-refresh-label">Teilen</span>';
    shareBtn.addEventListener("click", () => {
      void openHdShareDialog(target?.userId ?? currentUser?.id ?? 0);
    });
    hdMenu.push({ label: "Teilen", onSelect: () => shareBtn.click() });
  }

  // „Drucken / PDF" — sauberes, druckfertiges PDF der Auswertung (Chart + Fakten
  // + Deutung). Erst aktiv, wenn die Auswertung geladen ist (printReady).
  let printFactsHtml = "";
  let printInterpHtml = "";
  const printBtn = document.createElement("button");
  printBtn.type = "button";
  printBtn.className = "btn btn--ghost btn--sm hd-refresh";
  printBtn.title = "Auswertung als PDF drucken/speichern";
  printBtn.setAttribute("aria-label", "Drucken / PDF");
  printBtn.disabled = true;
  printBtn.innerHTML =
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
    '<path fill="currentColor" d="M19 8H5a3 3 0 0 0-3 3v6h4v4h12v-4h4v-6a3 3 0 0 0-3-3zm-3 11H8v-5h8v5zm3-7a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm-1-9H6v4h12V3z"/></svg>' +
    '<span class="hd-refresh-label">Print</span>';
  // Varianten-SVG (nur Design / nur Persönlichkeit, ohne Spalten = kompakt) für
  // die „erweiterte Darstellung" im Druck. Eigene URL (eigene view/columns),
  // unabhängig von der gewählten Ansicht im Sheet.
  const chartUrlFor = (
    view: "design" | "personality" | "both",
    columns: "both" | "left" | "right" | "none",
    arrows: boolean,
  ): string => {
    const params = new URLSearchParams();
    if (target) params.set("userId", String(target.userId));
    params.set("v", chartVer);
    params.set("view", view);
    params.set("columns", columns);
    if (!arrows) params.set("arrows", "0");
    return `/api/human-design/chart.svg?${params.toString()}`;
  };
  printBtn.addEventListener("click", () => {
    const name = (target?.name ?? currentUser?.name ?? "").trim();
    printBtn.disabled = true;
    void (async () => {
      // Alle 3 Charts frisch holen (unabhängig von der gewählten Sheet-Ansicht):
      // Varianten kompakt (ohne Spalten), Hauptchart als Gesamt-Ansicht mit Spalten.
      let designSvg = "";
      let personalitySvg = "";
      let mainSvg = "";
      try {
        [designSvg, personalitySvg, mainSvg] = await Promise.all([
          // Variante links: nur Design-Aktivierungen + Design-Planetenspalte (links),
          // ohne Pfeile/Header → aufgeräumt, Tore direkt zur Analyse daneben.
          fetch(chartUrlFor("design", "left", false), {
            credentials: "same-origin",
          }).then((r) => (r.ok ? r.text() : "")),
          // Variante rechts: nur Persönlichkeit + Persönlichkeit-Spalte (rechts).
          fetch(chartUrlFor("personality", "right", false), {
            credentials: "same-origin",
          }).then((r) => (r.ok ? r.text() : "")),
          // Gesamtchart: beide Aktivierungen, MIT beiden Planetenspalten (Martin:
          // die Zahlenreihe auch unten einblenden, auch wenn redundant) + Pfeile.
          fetch(chartUrlFor("both", "both", true), {
            credentials: "same-origin",
          }).then((r) => (r.ok ? r.text() : "")),
        ]);
      } catch {
        /* Charts optional — was geladen ist, wird gedruckt. */
      }
      printHdReport({
        title: "Human Design" + (name ? ` — ${name}` : ""),
        chartSvg: mainSvg || chartImg.innerHTML,
        designSvg,
        personalitySvg,
        factsHtml: printFactsHtml,
        interpHtml: printInterpHtml,
      });
      printBtn.disabled = false;
    })();
  });
  hdMenu.push({ label: "Drucken / PDF", onSelect: () => printBtn.click() });

  // 2026-08-20 (Martin: "Der Auswerten-Button sollte natuerlich ganz woanders
  // stehen. Das ist echt bescheuert!"):
  //
  //   „Auswerten" sass als Kopf-AKTION direkt neben dem Zurueck-Pfeil. Im Kopf
  //   standen damit fuenf Dinge — Zurueck, Titel, Haus-Symbol, Auswerten, ⋯ —
  //   und seit dem KOPF-VERTRAG (pre.2265) draengt ein zweizeiliges Raster mit
  //   !important alles zusammen. Der Zurueck-Pfeil war dadurch kaum noch zu
  //   treffen.
  //
  //   Es gehoert ohnehin nicht dorthin: Der Knopf schreibt die TEXT-Auswertung
  //   neu und ist laut eigenem Kommentar „sinnvoll v.a. nach einer
  //   Geburtsdaten-Korrektur" — also selten. Verbindung, Kunde anlegen, Teilen
  //   und Drucken stehen aus demselben Grund schon im ⋯-Menue. Auswerten steht
  //   jetzt bei ihnen, mit sprechendem Namen statt des knappen „Auswerten".
  //
  //   Der Knopf selbst bleibt bestehen (nicht im DOM, wie connBtn/custBtn/
  //   shareBtn/printBtn auch) — das Menue loest ihn aus. Der Ladezustand ist
  //   dadurch nicht verloren: renderLoading() zeigt ihn im Inhalt an.
  hdMenu.unshift({
    label: "Auswertung neu schreiben",
    onSelect: () => refreshBtn.click(),
  });

  const head = buildPageHeader(
    target ? `Human Design — ${target.name}` : "Human Design",
    closeSheet,
    { menuItems: hdMenu },
  );
  sheet.appendChild(head);
  sheet.appendChild(adminRow);

  /*
   * Beim ERSTEN Mal erklaert die Seite, wozu sie da ist.
   *
   * ⚠️ Der Block haengt am BLATT, nicht am Koerper. `renderLoading` und
   *    `renderMissing` setzen beide `body.innerHTML = ""` — im Koerper waere
   *    die Einfuehrung beim ersten Nachladen still verschwunden. Im Browser
   *    genau so gemessen: der Koerper trug danach nur noch Chart und Hinweis.
   */
  sheet.appendChild(baueHdEinfuehrung());

  // --- Body ---
  const body = document.createElement("div");
  body.className = "hd-body";
  sheet.appendChild(body);

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);

  // Fokus (Esc-Handling läuft zentral über closeSheet + sheetAbort, s.o.).
  head.querySelector<HTMLElement>("button")?.focus();

  // --- Bodygraph (INLINE-SVG, damit Hover/Tap-Tooltips auf Zentren/Toren/Kanälen
  //     gehen). Klick aufs Chart ist jetzt FÜR die Tooltips reserviert — Vergrößern
  //     läuft über einen eigenen Button (sonst kollidiert Tap-Tooltip mit Zoom). ---
  const chartWrap = document.createElement("div");
  chartWrap.className = "hd-chart-wrap";
  const chartImg = document.createElement("div");
  chartImg.className = "hd-chart-img";
  chartImg.setAttribute("role", "img");
  chartImg.setAttribute("aria-label", "Human Design Bodygraph");
  const zoomBtn = document.createElement("button");
  zoomBtn.type = "button";
  zoomBtn.className = "hd-chart-zoom";
  zoomBtn.setAttribute("aria-label", "Bild vergrößern");
  zoomBtn.title = "Bild vergrößern";
  zoomBtn.innerHTML =
    '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><path fill="currentColor" d="M15 3h6v6h-2V6.41l-3.29 3.3-1.42-1.42L17.59 5H15V3zM3 15h2v2.59l3.29-3.3 1.42 1.42L6.41 19H9v2H3v-6z"/></svg><span>Vergrößern</span>';
  zoomBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    // Echte Pan/Zoom-Ansicht auf dem VEKTOR-SVG (scharf bis tief hineingezoomt) —
    // statt der binären is-zoomed-Lightbox. Frisch laden (aktuelle Ansicht), bei
    // Fehler auf das bereits inline geladene SVG zurückfallen.
    void import("./lib/svg-zoom.js").then(({ openSvgZoom }) => {
      void fetch(chartSvgUrl(hdImgCacheBuster))
        .then((r) =>
          r.ok ? r.text() : Promise.reject(new Error(String(r.status))),
        )
        .then((svg) => openSvgZoom(svg, "Human Design Bodygraph"))
        .catch(() => {
          if (chartImg.innerHTML)
            openSvgZoom(chartImg.innerHTML, "Human Design Bodygraph");
        });
    });
  });
  chartWrap.appendChild(chartImg);
  chartWrap.appendChild(zoomBtn);
  const loadChart = (): void => {
    void fetch(chartSvgUrl(hdImgCacheBuster))
      .then((r) =>
        r.ok ? r.text() : Promise.reject(new Error(String(r.status))),
      )
      .then((svg) => {
        // Geister-Tooltip weg, BEVOR die alten .hd-center-Knoten ersetzt werden
        // (sonst feuert kein mouseleave → Tooltip bliebe hängen).
        if (hdCenterTipEl) hdCenterTipEl.hidden = true;
        hdTipPinned = null; // alte angeheftete Auswahl lösen (Knoten wird ersetzt)
        chartWrap.hidden = false;
        chartImg.hidden = false;
        chartImg.innerHTML = svg;
        const svgEl = chartImg.querySelector("svg");
        if (svgEl) {
          svgEl.setAttribute("width", "100%");
          if (!svgEl.getAttribute("height")) svgEl.setAttribute("height", "auto");
        }
        try {
          setupCenterTooltips(chartImg);
          setupHdInfoTooltips(chartImg);
        } catch {
          /* Tooltips sind optional — Chart bleibt sichtbar. */
        }
      })
      .catch(() => {
        // Nur verstecken, wenn wirklich kein Chart da ist. Ein früher 401/500
        // darf einen späteren Erfolg nicht dauerhaft hidden lassen (2233).
        if (!chartImg.querySelector("svg")) chartWrap.hidden = true;
      });
  };
  loadChart();
  const reloadChart = (): void => {
    hdImgCacheBuster += 1;
    loadChart();
  };

  // Ansichts-Wähler: nur Design (rot), nur Persönlichkeit (schwarz) oder beide.
  // Filtert NUR die Aktivierungslinien/-punkte — der Rest des Charts bleibt.
  // Setting gemerkt, Chart wird neu geladen. Reuse der `.hd-user-select`-Optik.
  const viewSel = document.createElement("select");
  viewSel.className = "hd-user-select hd-view-select";
  viewSel.setAttribute(
    "aria-label",
    "Chart-Ansicht: Design (rot), Persönlichkeit (schwarz) oder beide",
  );
  const viewOpts: Array<["design" | "personality" | "both", string]> = [
    ["both", "Beide"],
    ["design", "Design (rot)"],
    ["personality", "Persönlichkeit (schwarz)"],
  ];
  const curView = loadSettings().hdChartView;
  for (const [val, label] of viewOpts) {
    const o = document.createElement("option");
    o.value = val;
    o.textContent = label;
    if (val === curView) o.selected = true;
    viewSel.appendChild(o);
  }
  viewSel.addEventListener("change", () => {
    const v = viewSel.value as "design" | "personality" | "both";
    saveSettings({ ...loadSettings(), hdChartView: v });
    reloadChart();
  });
  // Stufe 3b: Ansichts-Wahl wohnt in der Werkzeug-Zeile unterm Kopf
  // (fuer ALLE sichtbar — die Zeile traegt fuer Admins auch die Nutzer-Wahl).
  adminRow.hidden = false;
  adminRow.appendChild(viewSel);

  // --- Vorlese-Player (geteilte Komponente: Stimm-Picker mit Vorhören +
  //     Play/Pause) — IDENTISCH zu Essenz-Spiegel & „Mein Leben". Der HD-narrate-
  //     Endpoint baut den Text serverseitig aus dem Chart → immer „bereit".
  const player = buildNarrationPlayer({
    // 07.09.: Die Leiste heftet oben ein (Martin).
    klebt: true,
    titel: "Human Design",
    initialVoice: vorleseStimme(),
    endpoint: "/api/human-design/narrate",
    getBody: (voiceId) =>
      target
        ? {
            voiceId,
            userId: target.userId,
            googleVoice: googleHdFallbackForVoice(voiceId),
          }
        : { voiceId, googleVoice: googleHdFallbackForVoice(voiceId) },
    signal: sheetAbort.signal,
    // Abschnitts-Blätter (◀ ▶): durch die Abschnitte der Auswertung blättern,
    // sobald der Text geladen ist (player.setSections in renderResult).
    sectionable: true,
    onVoiceChange: (id) => {
      try {
        saveSettings({ ...loadSettings(), narratorVoiceId: id });
      } catch {
        /* sync-Fehler ignorieren */
      }
    },
  });
  player.el.classList.add("hd-player");
  player.setReady(true);
  // HD nutzt den (langsamen) klassischen Endpoint — die Vertonung deshalb
  // sofort im Hintergrund anstoßen, damit der erste Klick ohne Wartezeit
  // spielt. Der Body (voiceId + optional userId) steht schon zur Build-Zeit.
  player.prefetch();

  // --- Render-Helfer: kompakter Fakten-Block aus chart ---
  type HdVariable = {
    tone?: number;
    color?: number;
    orientation?: "left" | "right";
  };
  type HdChart = {
    type?: string;
    authority?: string;
    profile?: string;
    profileName?: string;
    lifeQuestion?: string;
    motivation?: string;
    definedCenters?: string[];
    openCenters?: string[];
    incarnationCross?: {
      label?: string;
      name?: string;
      nameEn?: string;
      angle?: string;
      gates?: [number, number, number, number];
    };
    strategy?: string;
    signature?: string;
    notSelf?: string;
    gates?: number[];
    crossLines?: Array<number | null>;
    variables?: {
      digestion?: HdVariable;
      environment?: HdVariable;
      motivation?: HdVariable;
      perspective?: HdVariable;
    };
  };
  type BirthInfo = {
    date?: string;
    time?: string;
    place?: string;
    timezone?: string;
    /** 24.09.2026, Ticket 2567: Geburtsland als Name. */
    country?: string;
  };

  /** Eine Zeile „Etikett — Wert" im Fakten-Block; leerer Wert → keine Zeile. */
  const faktZeile = (
    facts: HTMLElement,
    label: string,
    value: string | undefined,
    factKey: string,
  ): void => {
    if (!value) return;
    const row = document.createElement("div");
    row.className = "hd-fact";
    row.dataset.fact = factKey; // → Tooltip (Lern-Tool)
    const l = document.createElement("span");
    l.className = "hd-fact-label";
    l.textContent = label;
    const v = document.createElement("span");
    v.className = "hd-fact-value";
    v.textContent = value;
    row.appendChild(l);
    row.appendChild(v);
    facts.appendChild(row);
  };

  const renderFacts = (
    chart: HdChart,
    birth?: BirthInfo | null,
  ): HTMLElement => {
    const facts = document.createElement("div");
    facts.className = "hd-facts";
    const addRow = (
      label: string,
      value: string | undefined,
      factKey: string,
    ): void => faktZeile(facts, label, value, factKey);
    // Pre.648 (User-Wunsch): Geburtsdaten zuoberst — alle Infos im Detail.
    if (birth) {
      const d = (birth.date || "").trim(); // "YYYY-MM-DD"
      const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const dateDe = m ? `${m[3]}.${m[2]}.${m[1]}` : d;
      if (dateDe) addRow("Geburtsdatum", dateDe, "birthDate");
      if (birth.time) addRow("Geburtszeit", `${birth.time} Uhr`, "birthTime");
      if (birth.place) addRow("Geburtsort", birth.place, "birthPlace");
      addRow("Geburtsland", birth.country, "birthCountry");
    }
    addRow("Typ", chart.type, "type");
    addRow("Lebensfrage", chart.lifeQuestion, "lifeQuestion");
    addRow("Strategie", chart.strategy, "strategy");
    addRow("Autorität", chart.authority, "authority");
    addRow("Profil", chart.profileName || chart.profile, "profile");
    addRow("Motivation", chart.motivation, "motivation");
    addRow("Signatur", chart.signature, "signature");
    addRow("Not-Self", chart.notSelf, "notSelf");
    // Die 4 Sonnen-/Erde-Tore (bewusst = Persönlichkeit, unbewusst = Design) aus
    // dem Inkarnationskreuz — die Bausteine des Lebensthemas, einzeln aufgeführt.
    const cg = chart.incarnationCross?.gates;
    if (cg?.length === 4) {
      const cl = chart.crossLines ?? [];
      // „Tor 29.4 — Die Tiefe": Tor + Linie + Tor-Name (I-Ging) für mehr Detail.
      const gateDetail = (gate: number, line: number | null): string => {
        const nm = HD_GATE_NAMES[gate];
        const base = line ? `Tor ${gate}.${line}` : `Tor ${gate}`;
        return nm ? `${base} — ${nm}` : base;
      };
      addRow("Bewusste Sonne", gateDetail(cg[0], cl[0] ?? null), "persSun");
      addRow("Bewusste Erde", gateDetail(cg[1], cl[1] ?? null), "persEarth");
      addRow("Design-Sonne", gateDetail(cg[2], cl[2] ?? null), "desSun");
      addRow("Design-Erde", gateDetail(cg[3], cl[3] ?? null), "desEarth");
    }
    addRow("Inkarnationskreuz", chart.incarnationCross?.label, "cross");
    addRow("Kreuz (englisch)", chart.incarnationCross?.nameEn, "crossEn");
    // Die 4 Variablen-Pfeile (PHS): Verdauung/Umgebung = Design, Motivation/
    // Perspektive = Persönlichkeit. orientation links = aktiv, rechts = passiv.
    const varVal = (vr?: {
      orientation?: "left" | "right";
    }): string | undefined =>
      vr?.orientation
        ? vr.orientation === "left"
          ? "◀ aktiv"
          : "▶ passiv"
        : undefined;
    const v = chart.variables;
    if (v) {
      addRow("Verdauung", varVal(v.digestion), "varDigestion");
      addRow("Umgebung", varVal(v.environment), "varEnvironment");
      addRow("Motivation (Variable)", varVal(v.motivation), "varMotivation");
      addRow("Perspektive", varVal(v.perspective), "varPerspective");
    }
    if (chart.definedCenters && chart.definedCenters.length > 0)
      addRow(
        "Definierte Zentren",
        chart.definedCenters.join(", "),
        "definedCenters",
      );
    if (chart.openCenters && chart.openCenters.length > 0)
      addRow("Offene Zentren", chart.openCenters.join(", "), "openCenters");
    if (chart.gates && chart.gates.length > 0)
      addRow("Aktive Tore", String(chart.gates.length), "gateCount");
    return facts;
  };

  // --- Lade-Zustand (Spinner) im Body ---
  const renderLoading = (): void => {
    body.innerHTML = "";
    body.appendChild(chartWrap);
    const longrun = document.createElement("div");
    longrun.className = "hd-longrun";
    const spinner = document.createElement("div");
    spinner.className = "book-spinner";
    longrun.appendChild(spinner);
    const text = document.createElement("p");
    text.className = "hd-longrun-text";
    text.textContent = "Auswertung wird erstellt …";
    longrun.appendChild(text);
    body.appendChild(longrun);
  };

  // --- „Geburtsdaten fehlen"-Zustand → Hinweis + Link zu Einstellungen ---
  // Selbst-Ansicht: Lead-Text + „Zu den Einstellungen" (öffnet die EIGENEN
  // Settings). Admin-Ansicht eines fremden Nutzers: dorthin zu schicken wäre
  // falsch (es sind nicht meine Geburtsdaten) — stattdessen ein Hinweis, die
  // Daten im Nutzer-Bereich bei diesem Nutzer zu ergänzen, und KEIN
  // Settings-Button.
  const renderMissing = (message: string): void => {
    body.innerHTML = "";
    chartImg.hidden = true;
    const box = document.createElement("div");
    box.className = "hd-empty";
    const lead = document.createElement("p");
    lead.className = "hd-empty-lead";
    if (target) {
      lead.textContent = `Für ${target.name} fehlen Geburtsdatum/-zeit. Bitte im Nutzer-Bereich bei diesem Nutzer ergänzen.`;
      box.appendChild(lead);
      const cta = document.createElement("button");
      cta.type = "button";
      cta.className = "icm-btn hd-empty-cta";
      cta.textContent = "Schließen";
      cta.addEventListener("click", () => closeSheet());
      box.appendChild(cta);
      body.appendChild(box);
      return;
    }
    lead.textContent =
      message ||
      "Für Human Design brauche ich deine Geburtsdaten (Datum, Uhrzeit, Ort).";
    box.appendChild(lead);
    const cta = document.createElement("button");
    cta.type = "button";
    cta.className = "icm-btn icm-btn--primary hd-empty-cta";
    cta.textContent = "Zu den Einstellungen";
    cta.addEventListener("click", () => {
      closeSheet();
      goToView("settings");
    });
    box.appendChild(cta);
    body.appendChild(box);
  };

  /*
   * --- Gehört zum Paket → sagen, was Sache ist. KEIN „Erneut versuchen".
   *
   * ⚠️ 15.09.2026, gefunden beim Durchgehen der Neuanmeldung: Ein neuer
   *    Nutzer (Stufe frei) tippt „Human Design" im Menü an. Der Server
   *    antwortet 402 mit {error, need} und OHNE message-Feld — hier fiel der
   *    Text deshalb auf „Die Auswertung konnte nicht geladen werden." zurück,
   *    mit einem Knopf „Erneut versuchen", der nie etwas bewirken kann.
   *
   *    Eine Bezahlsperre ist kein Fehler. Sie als einen auszugeben, schickt
   *    Menschen in eine Schleife und lässt das Produkt kaputt aussehen.
   *
   * ⚠️ Das Angebot selbst macht der globale 402-Abfang (showUpgradeSheet) —
   *    hier wird NICHT ein zweites Mal verkauft, nur erklärt, was man sieht.
   */
  const renderGesperrt = (): void => {
    body.innerHTML = "";
    chartImg.hidden = true;
    const box = document.createElement("div");
    box.className = "hd-empty";
    const lead = document.createElement("p");
    lead.className = "hd-empty-lead";
    lead.textContent =
      "Human Design gehört zu einem größeren Paket. Dein Geburtsbild ist gespeichert — sobald das Paket offen ist, steht die Auswertung hier.";
    box.appendChild(lead);
    const cta = document.createElement("button");
    cta.type = "button";
    cta.className = "icm-btn hd-empty-cta";
    cta.textContent = "Schließen";
    cta.addEventListener("click", () => closeSheet());
    box.appendChild(cta);
    body.appendChild(box);
  };

  /*
   * --- Kurzfassung statt Sperre (24.09.2026, Ticket 2567) ---
   *
   * Ein Konto ohne Deutungsrecht (Frei) sah hier bisher nur „gehört zu einem
   * größeren Paket" — und über den globalen 402-Abfang das Verkaufsfenster.
   * Jetzt: Bodygraph (für Frei offen) und die Kurzfassung aus festen Texten
   * (server/hd-kurzfassung.ts, kein Sprachmodell), darunter ein ruhiger
   * Satz, was die ausführliche Deutung wäre. Kein zweites Verkaufen.
   * `renderGesperrt` bleibt der Rückfall, falls selbst das nicht kommt.
   */
  type HdKurz = {
    typ?: string;
    kurz?: string;
    strategie?: string;
    autoritaet?: string;
    profil?: string;
    lebensfrage?: string;
    zeichen?: string;
    definierteZentren?: string[];
  };
  type HdKurzAntwort = {
    ok?: boolean;
    reason?: string;
    message?: string;
    kurz?: HdKurz;
  };
  const renderKurzfassung = async (): Promise<void> => {
    let data: HdKurzAntwort | null;
    try {
      const r = await fetch(`/api/human-design/kurzfassung${q}`, {
        credentials: "same-origin",
      });
      data = (await r.json().catch(() => null)) as HdKurzAntwort | null;
    } catch {
      data = null;
    }
    if (data?.ok === false && data.reason === "missing_birth") {
      renderMissing(data.message || "");
      return;
    }
    const k = data?.ok ? data.kurz : undefined;
    if (!k) {
      renderGesperrt();
      return;
    }
    body.innerHTML = "";
    body.appendChild(chartWrap);
    if (k.kurz) {
      const satz = document.createElement("p");
      satz.className = "hd-kurz-satz";
      satz.textContent = k.kurz;
      body.appendChild(satz);
    }
    const facts = document.createElement("div");
    facts.className = "hd-facts";
    faktZeile(facts, "Typ", k.typ, "type");
    faktZeile(facts, "Strategie", k.strategie, "strategy");
    faktZeile(facts, "Innere Autorität", k.autoritaet, "authority");
    faktZeile(facts, "Profil", k.profil, "profile");
    faktZeile(
      facts,
      "Lebensfrage",
      k.lebensfrage ? `„${k.lebensfrage}“` : undefined,
      "lifeQuestion",
    );
    faktZeile(facts, "Woran du es merkst", k.zeichen, "signature");
    faktZeile(
      facts,
      "Definierte Zentren",
      k.definierteZentren?.length ? k.definierteZentren.join(", ") : "keine",
      "definedCenters",
    );
    body.appendChild(facts);
    try {
      attachHdTooltips(facts, ".hd-fact", buildFactTipHtml);
    } catch {
      /* Tooltips optional */
    }
    const hinweis = document.createElement("p");
    hinweis.className = "hd-kurz-hinweis";
    hinweis.textContent =
      "Das ist die Kurzfassung. Die ausführliche Deutung — Zentren, Kanäle, Inkarnationskreuz, zum Vorlesen — gehört zu einem größeren Paket.";
    body.appendChild(hinweis);
  };

  // --- Echter Fehler → Hinweis + „Erneut versuchen" ---
  const renderError = (message: string): void => {
    body.innerHTML = "";
    body.appendChild(chartWrap);
    const box = document.createElement("div");
    box.className = "hd-empty";
    const lead = document.createElement("p");
    lead.className = "hd-empty-lead";
    lead.textContent =
      message || "Die Auswertung konnte nicht erstellt werden.";
    box.appendChild(lead);
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "icm-btn icm-btn--primary hd-empty-cta";
    retry.textContent = "Erneut versuchen";
    retry.addEventListener("click", () => void load(false));
    box.appendChild(retry);
    body.appendChild(box);
  };

  // --- Erfolgsfall: Bild + Fakten + Vorlesen + Deutung ---
  const renderResult = (
    chart: HdChart,
    interpretation: string,
    birth?: BirthInfo | null,
    generatedAt?: string | null,
  ): void => {
    body.innerHTML = "";
    body.appendChild(chartWrap);
    const factsEl = renderFacts(chart, birth);
    body.appendChild(factsEl);
    // Jede Fakten-Zeile erklären (Hover/Tap) — wie Tore/Kanäle, „Chart lesen lernen".
    try {
      attachHdTooltips(factsEl, ".hd-fact", buildFactTipHtml);
    } catch {
      /* Tooltips optional */
    }

    const narrateRow = document.createElement("div");
    narrateRow.className = "hd-narrate-row";
    narrateRow.appendChild(player.el);
    body.appendChild(narrateRow);
    // Abschnitts-Blätter: die geladene Auswertung in blätterbare Abschnitte
    // zerlegen (◀ ▶ schalten Abschnitt-für-Abschnitt weiter).
    player.setSections(splitNarrationSections(interpretation || ""));
    /*
     * ⚠️ 16.09.2026, Martin zu voice.vespera.at/#human-design: *„hier sollte
     *    von der Basis aus auch die Voice vorgeladen werden, damit der User es
     *    beim ersten Mal gleich sauber anhören kann."*
     *
     *    Es WURDE schon vorgewärmt — beim Bau der Leiste, mit dem Kommentar
     *    „damit der erste Klick ohne Wartezeit spielt". Nur kam die Wärme nie
     *    an: `setSections` verwirft ausdrücklich alles (engine.destroy,
     *    cancelPrefetch, gewaermt.clear), weil ein neuer Inhalt eine neue
     *    Vertonung braucht. Und die Abschnitte kommen IMMER nach dem Bau.
     *
     *    Also hier noch einmal, wenn der Text wirklich dasteht. Erst jetzt
     *    weiß der Spieler, welchen Abschnitt er vorbereiten soll.
     */
    player.prefetch();

    // Pre.805 (#79): Zeitpunkt der Deutung — unterscheidet aktuelle von
    // älteren (gecachten) Auswertungs-Ständen.
    if (generatedAt) {
      const when = document.createElement("p");
      when.className = "hd-generated";
      const d = new Date(generatedAt);
      when.textContent = isNaN(d.getTime())
        ? ""
        : `Auswertung erstellt am ${d.toLocaleString("de-AT", { dateStyle: "medium", timeStyle: "short" })}`;
      if (when.textContent) body.appendChild(when);
    }

    const interp = document.createElement("div");
    interp.className = "hd-interpretation";
    renderMarkdown(interp, interpretation || "");
    body.appendChild(interp);

    // Druck-Daten erfassen + „Drucken / PDF" freischalten.
    printFactsHtml = factsEl.outerHTML;
    printInterpHtml = interp.innerHTML;
    printBtn.disabled = false;
  };

  // --- Daten laden (langsam beim ersten Mal, dann gecacht) ---
  const setRefreshBusy = (busy: boolean): void => {
    refreshBtn.classList.toggle("is-busy", busy);
    refreshBtn.disabled = busy;
  };

  const load = async (force = false): Promise<void> => {
    renderLoading();
    setRefreshBusy(force);
    if (force) reloadChart();
    // 24.09.2026, Ticket 2567: ohne Deutungsrecht gar nicht erst nach der
    // Deutung fragen — das gäbe 402 und damit das Verkaufsfenster.
    if (!hdDeutungFrei && !target) {
      await renderKurzfassung();
      setRefreshBusy(false);
      return;
    }
    try {
      // ?userId (q) + optional &force=1 sauber zusammenführen.
      const url = force
        ? `/api/human-design${q ? `${q}&force=1` : "?force=1"}`
        : `/api/human-design${q}`;
      const res = await fetch(url, { credentials: "same-origin" });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        chart?: HdChart;
        birth?: {
          date?: string;
          time?: string;
          place?: string;
          timezone?: string;
          country?: string;
        } | null;
        interpretation?: string;
        generatedAt?: string | null;
        reason?: string;
        message?: string;
      } | null;
      // ⚠️ ZUERST die Sperre — sie kommt ohne message-Feld und saehe sonst
      //    wie ein Fehler aus (siehe renderGesperrt). Seit 24.09.2026
      //    (Ticket 2567) steht dann die Kurzfassung da.
      if (res.status === 402) {
        await renderKurzfassung();
        return;
      }
      if (!data) {
        renderError("Die Auswertung konnte nicht geladen werden.");
        return;
      }
      // Freundlicher „fehlt"-Fall (kein echter Fehler).
      if (data.ok === false) {
        if (data.reason === "missing_birth" || data.reason === "bad_format") {
          renderMissing(data.message || "");
        } else {
          renderError(data.message || "");
        }
        return;
      }
      if (!res.ok || !data.chart) {
        renderError(
          data.message || "Die Auswertung konnte nicht geladen werden.",
        );
        return;
      }
      renderResult(
        data.chart,
        data.interpretation || "",
        data.birth ?? null,
        data.generatedAt ?? null,
      );
    } catch {
      renderError("Keine Verbindung — bitte später erneut versuchen.");
    } finally {
      setRefreshBusy(false);
    }
  };

  refreshBtn.addEventListener("click", () => void load(true));

  await load(false);
}

// --- Verbindung (Composite-Chart zweier Nutzer) — Admin-only -----------------
const CONN_TYPE_META: Record<string, { label: string; expl: string }> = {
  electromagnetic: {
    label: "Elektromagnetisch (Anziehung)",
    expl: "Jeder bringt EIN Tor des Kanals — zusammen wird er ganz. Der klassische Magnetismus: ihr zieht euch an, weil ihr euch gegenseitig vervollständigt.",
  },
  companionship: {
    label: "Kameradschaft",
    expl: "Beide habt ihr den GANZEN Kanal — ihr tickt hier gleich, vertraut und unkompliziert.",
  },
  compromise: {
    label: "Kompromiss",
    expl: "Einer hat den ganzen Kanal, der andere nur ein Tor — der mit dem einen Tor passt sich an, weil der andere es schon ganz hat.",
  },
  dominance: {
    label: "Dominanz",
    expl: "Einer hat den ganzen Kanal, der andere KEIN Tor — der mit dem ganzen Kanal prägt dieses Thema; der andere erlebt es konstant von ihm/ihr.",
  },
};
const CONN_TYPE_ORDER = [
  "electromagnetic",
  "companionship",
  "compromise",
  "dominance",
];
interface ConnUser {
  id: number;
  name?: string;
  email?: string;
  /** Die Kontoart vom Server (24.09.2026). */
  art?: string;
}
interface ConnResp {
  ok?: boolean;
  message?: string;
  nameA?: string;
  nameB?: string;
  connections?: Array<{
    g1: number;
    g2: number;
    type: string;
    dominantBy?: "A" | "B";
  }>;
  compositeDefined?: string[];
  onlyA?: string[];
  onlyB?: string[];
}

/** Tooltip für einen Tor-Punkt im Composite: zeigt WER (A blau / B orange / beide) das Tor hat. */
function buildConnGateTipHtml(
  el: SVGElement,
  nameA: string,
  nameB: string,
): string | null {
  const gate = Number(el.getAttribute("data-gate"));
  const conn = el.getAttribute("data-conn") ?? "none";
  if (!gate) return null;
  const name = HD_GATE_NAMES[gate] ?? "";
  const active = conn !== "none";
  const badge =
    conn === "both"
      ? "beide"
      : conn === "A"
        ? "A"
        : conn === "B"
          ? "B"
          : "offen";
  const who =
    conn === "both"
      ? `Beide haben Tor ${gate} — ${nameA} (blau) UND ${nameB} (orange). Hier seid ihr gleich „verdrahtet".`
      : conn === "A"
        ? `Nur ${nameA} (blau) hat Tor ${gate}. Für ${nameB} ist es ein offenes/aufnehmendes Thema.`
        : conn === "B"
          ? `Nur ${nameB} (orange) hat Tor ${gate}. Für ${nameA} ist es ein offenes/aufnehmendes Thema.`
          : `Keiner von euch hat Tor ${gate} aktiviert (graues Ringerl).`;
  return (
    `<div class="hd-tip-head"><span class="hd-tip-name">Tor ${gate}${name ? ` — ${name}` : ""}</span>` +
    `<span class="hd-tip-badge hd-tip-badge--${active ? "def" : "open"}">${badge}</span></div>` +
    `<div class="hd-tip-fn">Eine von 64 Tor-Energien. Im Composite zeigt die Farbe, WER es aktiviert hat: blau = ${nameA}, orange = ${nameB}, violett = beide.</div>` +
    `<div class="hd-tip-state">${who}</div>`
  );
}

async function openConnectionSheet(): Promise<void> {
  document
    .querySelector(".hd-overlay, .essence-overlay, .conn-overlay")
    ?.remove();
  closeDrawerForNavigation();
  document
    .getElementById("nav-menu-btn")
    ?.setAttribute("aria-expanded", "false");

  const overlay = document.createElement("div");
  overlay.className = "sheet conn-overlay page-sheet";
  const abort = new AbortController();
  const close = (): void => {
    abort.abort();
    hdTipPinned = null; // evtl. angeheftetes Composite-Tooltip lösen
    if (hdCenterTipEl) hdCenterTipEl.hidden = true;
    overlay.remove();
  };
  document.addEventListener(
    "keydown",
    (ev) => {
      if (ev.key === "Escape") close();
    },
    { signal: abort.signal },
  );

  const sheet = document.createElement("div");
  sheet.className = "sheet-card";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Verbindung");
  const selA = document.createElement("select");
  selA.className = "voice-picker-select conn-select";
  selA.setAttribute("aria-label", "Person A");
  const xLabel = document.createElement("span");
  xLabel.className = "conn-x";
  xLabel.textContent = "↔";
  const selB = document.createElement("select");
  selB.className = "voice-picker-select conn-select";
  selB.setAttribute("aria-label", "Person B");
  // Pre.833 (#110): Beziehungsart DIREKT bei der Personen-Wahl, im selben
  // Dropdown-Format wie die Picker (vorher eigener Stil weiter unten).
  const relSel = document.createElement("select");
  relSel.className = "voice-picker-select conn-select";
  relSel.setAttribute("aria-label", "Art der Verbindung");
  for (const [v, l] of [
    ["", "Art: neutral"],
    ["partnerschaft", "Partnerschaft"],
    ["freundschaft", "Freundschaft"],
    ["geschaeftlich", "Geschäftlich"],
  ] as const) {
    const o = document.createElement("option");
    o.value = v;
    o.textContent = l;
    relSel.appendChild(o);
  }
  // „Mein Chart" — zurück zum eigenen Human-Design-Sheet (Hin-und-Her-Wechsel).
  const chartBtn = document.createElement("button");
  chartBtn.type = "button";
  chartBtn.className = "icm-btn conn-chartbtn";
  chartBtn.textContent = "Mein Chart";
  chartBtn.title = "Zurück zu deinem Human-Design-Chart";
  chartBtn.addEventListener("click", () => {
    close();
    void openHumanDesignSheet();
  });
  // „Drucken / PDF" der Verbindungs-Auswertung (Composite-Chart + Liste + Deutung).
  let connPrint: {
    chartSvg: string;
    extraHtml: string;
    interpHtml: string;
    title: string;
  } | null = null;
  const printBtn = document.createElement("button");
  printBtn.type = "button";
  printBtn.className = "icm-btn conn-printbtn";
  printBtn.textContent = "Drucken";
  printBtn.title = "Verbindungs-Auswertung als PDF drucken/speichern";
  printBtn.disabled = true;
  printBtn.addEventListener("click", () => {
    if (!connPrint) return;
    printHdReport({
      title: connPrint.title,
      subtitle: "Verbindungs-Auswertung (Composite)",
      chartSvg: connPrint.chartSvg,
      extraHtml: connPrint.extraHtml,
      interpHtml: connPrint.interpHtml,
    });
  });
  // 2026-07-21 (Audit Stufe 3a): NORM-Kopf — „Mein Chart" als sichtbare
  // Aktion, Drucken im ⋯-Menue; die Personen-Wahl wohnt unterm Kopf.
  const head = buildPageHeader("Verbindung", close, {
    subtitle:
      "Zwei Charts übereinander — wo ergänzt ihr euch, wo seid ihr gleich?",
    action: chartBtn,
    menuItems: [{ label: "Drucken / PDF", onSelect: () => printBtn.click() }],
  });
  sheet.appendChild(head);
  const pickerRow = document.createElement("div");
  pickerRow.className = "conn-picker-row";
  pickerRow.append(selA, xLabel, selB, relSel);
  sheet.appendChild(pickerRow);

  const body = document.createElement("div");
  body.className = "hd-body";
  sheet.appendChild(body);
  overlay.appendChild(sheet);
  document.body.appendChild(overlay);

  const hint = (text: string): void => {
    body.innerHTML = "";
    const p = document.createElement("p");
    p.className = "hd-empty";
    p.textContent = text;
    body.appendChild(p);
  };

  // Nutzer laden (Admin-Endpoint).
  let users: ConnUser[] = [];
  try {
    const r = await fetch("/api/admin/users", { credentials: "same-origin" });
    const d = (await r.json().catch(() => null)) as {
      users?: ConnUser[];
    } | null;
    users = Array.isArray(d?.users) ? d.users : [];
  } catch {
    /* ignore */
  }
  const fillSelect = (sel: HTMLSelectElement, selectedId?: number): void => {
    sel.innerHTML = "";
    const ph = document.createElement("option");
    ph.value = "";
    ph.textContent = "– wählen –";
    sel.appendChild(ph);
    // 24.09.2026: getrennt nach Nutzern, nie benutzten Konten und Human-Design-Profilen (hd-auswahl.ts).
    fuelleHdAuswahl(sel, users, { gewaehlt: selectedId });
  };
  // Gemerkte Auswahl wiederherstellen (leichter administrierbar — kein Neuwählen).
  let savedPair: { a?: number; b?: number } = {};
  try {
    savedPair = JSON.parse(localStorage.getItem("hdConnPair") || "{}");
  } catch {
    /* ignore */
  }
  const validId = (id: unknown): number | undefined =>
    typeof id === "number" && users.some((u) => u.id === id) ? id : undefined;
  fillSelect(selA, validId(savedPair.a) ?? currentUser?.id ?? 1);
  fillSelect(selB, validId(savedPair.b));

  const channelName = (lo: number, hi: number): string =>
    HD_CHANNEL_NAMES[`${lo}-${hi}`] ?? "";

  const renderResult = (d: ConnResp, aId: number, bId: number): void => {
    body.innerHTML = "";
    // Druck-Report initialisieren + „Drucken / PDF" freischalten.
    connPrint = {
      chartSvg: "",
      extraHtml: "",
      interpHtml: "",
      title: `Human Design Verbindung — ${d.nameA || "A"} ↔ ${d.nameB || "B"}`,
    };
    printBtn.disabled = false;
    // Composite-Chart (inline SVG).
    const chartWrap = document.createElement("div");
    chartWrap.className = "hd-chart-wrap";
    const img = document.createElement("div");
    img.className = "hd-chart-img conn-chart-img";
    chartWrap.appendChild(img);
    body.appendChild(chartWrap);
    void fetch(
      `/api/human-design/connection.svg?userId=${aId}&withUserId=${bId}`,
      { credentials: "same-origin" },
    )
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error())))
      .then((svg) => {
        chartWrap.hidden = false;
        img.innerHTML = svg;
        const svgEl = img.querySelector("svg");
        if (svgEl) svgEl.setAttribute("width", "100%");
        if (connPrint) connPrint.chartSvg = svg;
        // Tooltips auf den Composite-Tor-Punkten (zeigt WER das Tor hat).
        const an = d.nameA || "Person A";
        const bn = d.nameB || "Person B";
        try {
          attachHdTooltips(img, ".hd-gate", (el) =>
            buildConnGateTipHtml(el, an, bn),
          );
        } catch {
          /* Tooltips optional */
        }
      })
      .catch(() => {
        chartWrap.hidden = true;
      });

    // Legende + Gruppen in EINEN Wrapper (auch für den Druck als extraHtml).
    const summaryWrap = document.createElement("div");
    summaryWrap.className = "conn-summary";
    body.appendChild(summaryWrap);

    // Legende (Namen + Farben).
    const legend = document.createElement("div");
    legend.className = "conn-legend";
    const addLeg = (cls: string, label: string): void => {
      const dot = document.createElement("span");
      dot.className = `conn-dot ${cls}`;
      const txt = document.createElement("span");
      txt.textContent = label;
      legend.appendChild(dot);
      legend.appendChild(txt);
    };
    addLeg("conn-dot--a", d.nameA || "Person A");
    addLeg("conn-dot--b", d.nameB || "Person B");
    addLeg("conn-dot--both", "beide");
    summaryWrap.appendChild(legend);

    // Verbindungen gruppiert nach Typ.
    const conns = d.connections ?? [];
    const heading = document.createElement("h3");
    heading.className = "conn-section-title";
    heading.textContent = conns.length
      ? `Eure Verbindungen (${conns.length})`
      : "Eure Verbindungen";
    summaryWrap.appendChild(heading);

    if (!conns.length) {
      const none = document.createElement("p");
      none.className = "essence-foot";
      none.textContent =
        "Zwischen euren Charts entstehen keine gemeinsam definierten Kanäle.";
      summaryWrap.appendChild(none);
    }

    for (const type of CONN_TYPE_ORDER) {
      const group = conns.filter((c) => c.type === type);
      if (!group.length) continue;
      const meta = CONN_TYPE_META[type];
      const block = document.createElement("div");
      block.className = "conn-group";
      const gh = document.createElement("div");
      gh.className = "conn-group-head";
      const badge = document.createElement("span");
      badge.className = `conn-badge conn-badge--${type}`;
      badge.textContent = meta?.label ?? type;
      const count = document.createElement("span");
      count.className = "conn-group-count";
      count.textContent = String(group.length);
      gh.appendChild(badge);
      gh.appendChild(count);
      block.appendChild(gh);
      const expl = document.createElement("p");
      expl.className = "conn-group-expl";
      expl.textContent = meta?.expl ?? "";
      block.appendChild(expl);
      const list = document.createElement("div");
      list.className = "conn-chan-list";
      for (const c of group) {
        const lo = Math.min(c.g1, c.g2);
        const hi = Math.max(c.g1, c.g2);
        const item = document.createElement("span");
        item.className = "conn-chan";
        const nm = channelName(lo, hi);
        let label = `Kanal ${lo}-${hi}${nm ? ` — ${nm}` : ""}`;
        if (type === "dominance" && c.dominantBy)
          label += ` · ${c.dominantBy === "A" ? d.nameA : d.nameB} führt`;
        item.textContent = label;
        list.appendChild(item);
      }
      block.appendChild(list);
      summaryWrap.appendChild(block);
    }
    // Legende + Gruppen sind fertig → für den Druck festhalten.
    if (connPrint) connPrint.extraHtml = summaryWrap.innerHTML;

    // Beziehungs-Deutung (LLM) — lädt asynchron nach (Chart+Liste bleiben schnell).
    const interpHead = document.createElement("h3");
    interpHead.className = "conn-section-title";
    interpHead.textContent = "Beziehungs-Deutung";
    body.appendChild(interpHead);
    // Vorlese-Player (geteilte Komponente: Stimme mit Vorhören + Play/Pause).
    let interpText = "";
    const interpPlayer = buildNarrationPlayer({
      titel: "Human Design",
      initialVoice: vorleseStimme(),
      endpoint: "/api/human-design/narrate",
      getBody: (voiceId) =>
        interpText
          ? {
              text: interpText,
              voiceId,
              googleVoice: googleHdFallbackForVoice(voiceId),
            }
          : null,
      signal: abort.signal,
      onVoiceChange: (id) => {
        try {
          saveSettings({ ...loadSettings(), narratorVoiceId: id });
        } catch {
          /* sync-Fehler ignorieren */
        }
      },
    });
    interpPlayer.setReady(false);
    const narrRow = document.createElement("div");
    narrRow.className = "hd-narrate-row";
    narrRow.appendChild(interpPlayer.el);
    body.appendChild(narrRow);
    const interp = document.createElement("div");
    interp.className = "hd-interpretation conn-interp";
    interp.innerHTML =
      '<div class="hd-longrun"><div class="book-spinner"></div>' +
      "<p>Lese eure Verbindung …</p></div>";
    body.appendChild(interp);

    // „Ausführliche Deutung" — lädt auf Wunsch eine tiefere, gegliederte
    // Deutung nach (pro Kanal/Typ + gemeinsame Zentren + praktische Hinweise).
    // Die Kurzfassung bleibt der schnelle Default; der Button ersetzt sie und
    // schaltet sie zum Vorlesen frei. Erst sichtbar, wenn die Kurzfassung da ist.
    const deepRow = document.createElement("div");
    deepRow.className = "conn-deep-row";
    const deepBtn = document.createElement("button");
    deepBtn.type = "button";
    deepBtn.className = "icm-btn conn-deepbtn";
    deepBtn.textContent = "Ausführliche Deutung";
    deepBtn.hidden = true;
    deepRow.appendChild(deepBtn);
    body.appendChild(deepRow);
    deepBtn.addEventListener("click", () => {
      deepBtn.disabled = true;
      const prev = interp.innerHTML;
      interp.innerHTML =
        '<div class="hd-longrun"><div class="book-spinner"></div>' +
        "<p>Lese eure Verbindung tiefer …</p></div>";
      void fetch(
        `/api/human-design/connection/interpret?userId=${aId}&withUserId=${bId}&deep=1${relParam()}`,
        { credentials: "same-origin" },
      )
        .then((r) => r.json())
        .then((di: { ok?: boolean; interpretation?: string }) => {
          if (di.ok && di.interpretation) {
            interp.innerHTML = "";
            renderMarkdown(interp, di.interpretation);
            interpText = di.interpretation; // ausführliche Fassung vorlesen
            if (connPrint) connPrint.interpHtml = interp.innerHTML;
            interpPlayer.reset();
            interpPlayer.setReady(true);
            interpPlayer.prefetch();
            deepBtn.hidden = true; // erledigt
          } else {
            interp.innerHTML = prev;
            deepBtn.disabled = false;
          }
        })
        .catch(() => {
          interp.innerHTML = prev;
          deepBtn.disabled = false;
        });
    });

    const relParam = (): string =>
      relSel.value ? `&relation=${relSel.value}` : "";
    relSel.addEventListener("change", () => {
      // Neue Art → Deutung frisch holen (Server cached pro Art).
      interp.innerHTML =
        '<div class="hd-longrun"><div class="book-spinner"></div>' +
        "<p>Passe die Deutung an …</p></div>";
      fetchShortInterp();
    });

    const fetchShortInterp = (): void => {
      void fetch(
        `/api/human-design/connection/interpret?userId=${aId}&withUserId=${bId}${relParam()}`,
        { credentials: "same-origin" },
      )
        .then((r) => r.json())
        .then(
          (di: { ok?: boolean; interpretation?: string; message?: string }) => {
            if (di.ok && di.interpretation) {
              interp.innerHTML = "";
              renderMarkdown(interp, di.interpretation);
              interpText = di.interpretation; // vorlesbar freischalten
              if (connPrint) connPrint.interpHtml = interp.innerHTML; // für Druck
              interpPlayer.reset();
              interpPlayer.setReady(true);
              interpPlayer.prefetch(); // Vertonung im Hintergrund vorbereiten
              deepBtn.hidden = false; // „Ausführliche Deutung" jetzt anbieten
            } else {
              interp.innerHTML = `<p class="essence-foot">${di.message || "Beziehungs-Deutung gerade nicht möglich."}</p>`;
            }
          },
        )
        .catch(() => {
          interp.innerHTML =
            '<p class="essence-foot">Beziehungs-Deutung gerade nicht möglich.</p>';
        });
    };
    fetchShortInterp();
  };

  const update = async (): Promise<void> => {
    const aId = Number(selA.value);
    const bId = Number(selB.value);
    if (!aId || !bId) {
      hint("Wähle zwei Personen, um eure Verbindung zu sehen.");
      return;
    }
    if (aId === bId) {
      hint("Bitte zwei VERSCHIEDENE Personen wählen.");
      return;
    }
    body.innerHTML =
      '<div class="hd-longrun"><div class="book-spinner"></div>' +
      "<p>Lege eure Charts übereinander …</p></div>";
    try {
      const r = await fetch(
        `/api/human-design/connection?userId=${aId}&withUserId=${bId}`,
        { credentials: "same-origin" },
      );
      const d = (await r.json().catch(() => null)) as ConnResp | null;
      if (!d || d.ok === false) {
        hint(d?.message || "Die Verbindung konnte nicht berechnet werden.");
        return;
      }
      renderResult(d, aId, bId);
    } catch {
      hint("Verbindung fehlgeschlagen — bitte später nochmal.");
    }
  };

  // Auswahl merken (localStorage) + neu rendern.
  const onPick = (): void => {
    try {
      localStorage.setItem(
        "hdConnPair",
        JSON.stringify({ a: Number(selA.value), b: Number(selB.value) }),
      );
    } catch {
      /* ignore */
    }
    void update();
  };
  selA.addEventListener("change", onPick);
  selB.addEventListener("change", onPick);

  // Gemerkte Auswahl direkt anzeigen (kein erneutes Wählen nötig).
  if (Number(selA.value) && Number(selB.value)) void update();
  else hint("Wähle zwei Personen, um eure Verbindung zu sehen.");
}

// --- Nutzer-Verwaltung (Admin-Tool, nur id=1) ---------------------------
//
// Pre.577: Spiegelt openHumanDesignSheet() — Overlay + Sheet + Kopf + Body.
// Admin legt Nutzer an, schaltet Login frei, schickt Login-Links, bearbeitet
// Profile (inkl. Geburtsdaten fürs Human-Design-Modul) und löscht. Baut
// strikt gegen die /api/admin/users-Endpunkte. KEINE nativen Dialoge —
// Löschen läuft über eine Inline-Bestätigung (native confirm/alert/prompt
// blockieren den Render-Loop und sind in diesem Projekt verboten).





// Pre.109: Text-Input-Focus → Dock ausblenden für mehr Chat-Platz.
// User-Wunsch: "wenn ich auf 'tippen' gehe, kann man das 'Telefonfeld'
// überblenden". Body-Klasse, CSS macht den Rest.
const textFieldEl = document.getElementById("text-input-field");
textFieldEl?.addEventListener("focus", () => {
  document.body.classList.add("text-focus");
  prewarmChatSession();
});
textFieldEl?.addEventListener("blur", () => {
  document.body.classList.remove("text-focus");
});

scrollToBottomBtn?.addEventListener("click", () => {
  // Zwei Gesichter, zwei Wege — siehe updateScrollBtnVisibility.
  if (scrollToBottomBtn.dataset.rolle === "heim") {
    goToView("zuhause");
    return;
  }
  // 2026-07-20 (Martin): bis zum ECHTEN Ende — Pin-Loop statt Einmal-Sprung.
  pinChatToBottom(1200);
  unreadCount = 0;
  updateUnreadBadge();
  updateScrollBtnVisibility();
});

// Beim Scrollen stellt chatFolgen den Knopf nach (nachScroll) — NACH der
// Neubestimmung, sonst läse er den Zustand von vorher (Ticket 2730).

const STATE_LABELS: Record<VoiceState, string> = {
  idle: "bereit",
  connecting: "verbinde…",
  reconnecting: "verbinde neu…",
  listening: "ich höre",
  thinking: "ich denke nach",
  speaking: "ich spreche",
  "paused-phone": "telefonat läuft",
  error: "fehler",
};

// #122 (Martins Wunsch): Internetverbindungs-Anzeige in der Fußzeile.
// Bewusst NUR sichtbar, wenn die Verbindung wirklich weg ist — im Normal-
// betrieb herrscht Ruhe. Absolut positioniert: das Dock-Layout bewegt sich
// nicht (pre.829-Lektion: keine springenden Button-Reihen).
function wireOfflineBadge(): void {
  const dock = document.querySelector(".dock");
  if (!dock) return;
  const badge = document.createElement("span");
  badge.className = "dock-offline";
  badge.textContent = "Offline — keine Internetverbindung";
  badge.hidden = navigator.onLine !== false;
  dock.appendChild(badge);
  window.addEventListener("online", () => {
// 2026-07-29 (Audit Paket F): openUsersAdminSheet und seine beiden Helfer
// (usrErrorMessage, usrFormatDate) liegen jetzt in lib/users-admin-view.ts —
// zusammen rund 700 Zeilen weniger in dieser Datei. Nur verschoben, nicht
// umgeschrieben. Die Abhaengigkeiten werden dort ausdruecklich uebergeben
// (setzeAdminUmgebung) statt aus dem gemeinsamen Namensraum zu stammen.
    badge.hidden = true;
  });
  window.addEventListener("offline", () => {
    badge.hidden = false;
  });
}
wireOfflineBadge();

/**
 * Darf dieser Nutzer anrufen?
 *
 * ⚠️ 11.09.2026 — Der Anruf ist der einzige Kanal, in dem wirklich Geld
 *    fliesst (gemessen 1,36 bis 6,22 USD je Gespraech; Gemini Live rechnet
 *    Ton als Token). Martin: *„das Anrufen kommt bei anderen Usern
 *    ueberhaupt weg, weil es fuer mich nur Experimente ist."*
 *
 * ⚠️ Die harte Grenze steht am Server (/api/token antwortet 403). Hier wird
 *    nur der Weg weggeraeumt, damit niemand gegen eine verschlossene Tuer
 *    laeuft.
 */
function darfAnrufen(): boolean {
  return currentUser !== null && kontoLage().anruf;
}

/**
 * Den Anruf-Knopf ein- oder ausblenden.
 *
 * ⚠️ 15.09.2026, Martin: *„Ich bin gerade bei Albert eingeloggt. Ich sehe da
 *    immer noch das Anrufenzeichen vom Telefon."*
 *
 *    Die Frage hatte ZWEI Besitzer: renderDock() fragte nach dem Nutzer,
 *    showAuthenticatedUI() nur nach der Verbindung. Die zweite laeuft NACH
 *    dem Anmelden — und gewann. Dasselbe Muster wie schon mehrfach: eine
 *    zweite Regel neben die bestehende, die spaetere setzt sich durch.
 *
 *    Jetzt entscheidet EINE Stelle. `imGespraech` nennt jeder Aufrufer so,
 *    wie er es an seinem Ort kennt.
 */
function zeichneAnrufKnopf(imGespraech: boolean): void {
  refs.connectBtn.hidden = imGespraech || !darfAnrufen();
}

/**
 * „Vorlesen" steht nur im Chat-Modus, auf JEDER Ansicht gleich. (2026-06-30, 15.09.2026)
 * Der Tausch gegen „Chat" (17.10.2026) ist seit 28.10.2026 weg: die Fußzeile
 * ist überall dieselbe, Zuhause und Chat stehen in der Seitenleiste ("Menü").
 * Im Anruf hört man ohnehin live mit.
 * Der Schalter bleibt erreichbar: Plus-Menü „Schnell einstellen" und die
 * Einstellungen „Stimme und Vorlesen".
 */
function zeichneVorlesenKnopf(imGespraech: boolean): void {
  document
    .getElementById("dock-speaker")
    ?.toggleAttribute("hidden", imGespraech);
}

/**
 * Der EINE Besitzer der beiden Plätze neben dem Mikrofon: links Vorlesen,
 * rechts Anrufen oder Wunsch (sie schließen einander aus). Gezeichnet beim
 * Dock-Neuaufbau und nach dem Anmelden — NICHT mehr je Ansicht: die Leiste
 * ist überall gleich (28.10.2026). Zwei Stellen, die denselben Platz setzen,
 * wären das alte Muster „zwei Besitzer".
 */
function zeichneDockPlatz(imGespraech: boolean): void {
  zeichneAnrufKnopf(imGespraech);
  zeichneWunschKnopf(imGespraech);
  zeichneVorlesenKnopf(imGespraech);
}

/**
 * Den Wunsch-Knopf ein- oder ausblenden.
 *
 * ⚠️ 16.09.2026, Martin: *„Ab sofort ist Albert an Bord. […] ein weiteres
 *    Symbol, auf das er draufklicken kann, um seine Wünsche zu äußern. […]
 *    Trifft alle Beta-Tester!"*
 *
 *    Ein eigenes Merkmal „Beta-Tester" gibt es nicht — und es braucht auch
 *    keines: die App ist reine Einladung, eine unbekannte Adresse kommt gar
 *    nicht herein. Wer angemeldet ist, wurde von Martin freigeschaltet. „Alle
 *    Beta-Tester" heißt hier also schlicht „alle außer ihm selbst".
 *
 * ⚠️ Der Knopf sitzt auf dem Platz des Telefons und ist dazu INVERS. Damit
 *    bleibt die Leiste bei fünf Elementen — Martins Regel vom 27.08.
 *
 * ⚠️ Nicht über `login_allowed` gehen: Martin steht dort auf NULL, er kommt
 *    über die Adressliste herein. Ein Tor darauf hätte ausgerechnet ihn
 *    ausgesperrt.
 */
function zeichneWunschKnopf(imGespraech: boolean): void {
  const btn = document.getElementById("dock-wunsch");
  if (!btn) return;
  btn.hidden = imGespraech || !currentUser || darfAnrufen();
}

function renderDock(state: VoiceState): void {
  // Klare Modus-Trennung — entweder Chat oder Voice, nie beides:
  //   - mode === "chat": NUR Anruf-Button (= Voice starten). Kein Mic,
  //     kein Hangup. Text-Eingabe geht über HTTP /api/chat.
  //   - mode === "voice": NUR Mute + Focus + Hangup. Text-Eingabe geht
  //     über LiveKit-Datenkanal an den Voice-Agent.
  const isVoice = mode === "voice";
  // Pre.937 (#151-Fix): Display-Wachhalter NICHT mehr im Chat-Modus pausieren —
  // der Nutzer liest auch ohne Anruf mit und will, dass das Display anbleibt.
  // Das Video läuft, solange die App sichtbar ist; pausiert wird nur beim
  // Hintergrund-Wechsel (visibilitychange → hidden). startNoSleep() ist
  // mehrfach-aufruf-sicher (No-op, wenn schon läuft).
  if (document.visibilityState === "visible") startNoSleep();
  // Pre.829 (Martins „Reihe springt beim Record/Auflegen"): der Rechts-
  // Anker (margin-left:auto) muss je Modus auf einem SICHTBAREN Knopf
  // sitzen — CSS liest den Modus an diesem Marker ab.
  refs.connectBtn
    .closest(".dock")
    ?.setAttribute("data-mode", isVoice ? "voice" : "chat");
  zeichneDockPlatz(isVoice);
  refs.connectBtn.setAttribute("aria-label", "Anrufen");
  // Pre.144: Push-to-Talk parallel zum Connect-Btn — beide sichtbar im
  // chat-mode, beide weg sobald der Voice-Call läuft (das Mic ist dann
  // dauerhaft offen, PTT wäre redundant).
  refs.pttBtn.hidden = isVoice;
  // „Vorlesen" (dock-speaker) zeichnet zeichneDockPlatz oben.
  // Chat-Schnellweg: nur im Chat-Modus (im Anruf zeigt das Dock die Anruf-
  // Steuerung). Daumen-Weg zurück in den Verlauf aus jeder Unterseite.
  refs.chatBtn.hidden = isVoice;
  /*
   * ⚠️ 15.09.2026 — der Aufgaben-Knopf ist hier heraus. Martin: *„Ich glaube,
   *    die Aufgabenliste kann man unten in der Fusszeile weg. Das wuerde zu
   *    viel Platz verbrauchen. Fuenf Elemente genuegen."* Angestossen hatte
   *    es ein Kunde, der den Knopf nie geklickt hat. Der Weg zu den Aufgaben
   *    bleibt: Zuhause und das Widget fuehren hin.
   */
  refs.muteBtn.hidden = !isVoice;
  refs.focusBtn.hidden = !isVoice;
  refs.hangupBtn.hidden = !isVoice;
  // 2026-08-26 (Ticket #1926, Martin): Im Anruf verschwindet oben auch der
  // Menue-Knopf (refs.chatBtn) — damit gab es KEINEN Weg in die Einstellungen,
  // solange ein Gespraech laeuft. Der Knopf erscheint deshalb genau dann, wenn
  // der Menue-Weg zu ist. Im Chat bleibt er weg: dort fuehrt das Menue hin,
  // und die Fusszeile soll nicht zwei Wege fuer dasselbe zeigen.
  {
    const settingsBtn = document.getElementById("dock-settings");
    if (settingsBtn) settingsBtn.hidden = !isVoice;
  }

  // Text-Input-Form: immer sichtbar. Wenn nicht verbunden, löst der erste
  // Submit einen Auto-Connect aus (siehe handleTextSubmit).
  const textForm = document.getElementById(
    "text-input",
  ) as HTMLFormElement | null;
  if (textForm) {
    textForm.hidden = false;
    const field = document.getElementById(
      "text-input-field",
    ) as HTMLTextAreaElement | null;
    const sendBtn = document.getElementById(
      "text-input-send",
    ) as HTMLButtonElement | null;
    if (field) {
      field.disabled = false;
      field.placeholder = connected
        ? "Tippen statt sprechen…"
        : "Schreib oder sprich…";
    }
    if (sendBtn) {
      sendBtn.disabled = (field?.value.trim().length ?? 0) === 0;
    }
  }

  // Mute-State (Icon-only — Strike-Through im SVG)
  refs.muteBtn.dataset.active = String(muted);
  refs.muteBtn.setAttribute("aria-pressed", String(muted));
  refs.muteBtn.setAttribute(
    "aria-label",
    muted ? "Mikrofon einschalten" : "Mikrofon stumm schalten",
  );

  // Focus-Mode-State + Indikator
  refs.focusBtn.dataset.active = String(focusMode);
  refs.focusBtn.setAttribute("aria-pressed", String(focusMode));
  // Wenn Focus aktiv UND Bot gerade redet/denkt → visuell anzeigen dass
  // Mic gerade auto-stumm ist.
  const autoMuted = focusMode && (state === "speaking" || state === "thinking");
  refs.focusBtn.dataset.muting = String(autoMuted);
}

let currentState: VoiceState = "idle";

// Pre.644: true während eines GEPLANTEN preemptiven Sitzungs-Refreshs (vor
// dem ~10min-Provider-Cut). Solange das gesetzt ist, unterdrückt setState
// beim Übergang reconnecting→listening den lauten "reconnected"-Chime +
// Vibration + Erfolgs-Toast — der Refresh soll sich nahtlos anfühlen.
// Wird gecleart sobald wir wieder "listening" erreichen ODER ein echter
// Reconnect-Versuch/-Fehler auftritt (dann normal/laut).
let preemptiveRefreshActive = false;

const STATUS_BAR_STATE_LABELS: Record<VoiceState, string> = {
  idle: "Anruf aus",
  connecting: "verbinde…",
  reconnecting: "verbinde neu…",
  listening: "Anruf läuft",
  thinking: "denkt",
  speaking: "spricht",
  "paused-phone": "telefonat läuft",
  error: "fehler",
};

function shortLabel(
  options: ReadonlyArray<{ id: string; label: string }>,
  id: string,
): string {
  const found = options.find((o) => o.id === id);
  if (!found) return id.slice(0, 12);
  // "Claude Haiku 4.5" → "Haiku 4.5"; "Martin (Klon)" → "Martin"
  return found.label.replace(/^Claude\s+/, "").replace(/\s*\([^)]+\)\s*$/, "");
}

/**
 * 2026-07-31 (Martin: "Es waere gut, wenn man die Versionsnummer endlich im
 * Menue unterbringt, sodass man sieht, welche Version aktuell laeuft"):
 *
 * Die Anzeige kannte bisher nur die WEB-Version. Die kommt bei jedem Laden
 * frisch vom Server und sagt deshalb nichts darueber, welcher NATIVE Stand
 * auf dem Telefon liegt — und genau der entscheidet ueber Aufnahme, Mikrofon
 * und Widget.
 *
 * Was das gekostet hat: Beim Aufnahme-Fehler heute wurde zweimal falsch
 * geschlossen, weil niemand sehen konnte, welcher Build laeuft. Erst hiess
 * es, TestFlight sei schuld; dann, der Fix sei noch nicht installiert. Beides
 * war Raten. Eine Zahl haette beide Irrwege erspart.
 *
 * Capacitor liefert sie frei Haus (@capacitor/app ist ohnehin eingebunden).
 * Im Browser gibt es sie nicht — dann bleibt es bei der Web-Version allein,
 * ohne leeres Klammerpaar.
 */
let nativeBuildNummer: string | null = null;

async function ladeNativeVersion(): Promise<void> {
  if (nativeBuildNummer !== null) return;
  try {
    // NICHT via import("@capacitor/app") — das Modul liegt nur im nativen
    // Projekt, nicht im Server-Build; der Bau bricht daran ab. Zur Laufzeit
    // haengt Capacitor die Plugins ohnehin ans globale Objekt.
    const cap = (window as unknown as {
      Capacitor?: {
        Plugins?: {
          App?: { getInfo?: () => Promise<{ build?: string; version?: string }> };
        };
      };
    }).Capacitor;
    const info = await cap?.Plugins?.App?.getInfo?.();
    // build ist die fortlaufende Nummer (1, 2, 3...), version die sichtbare
    // ("1.0"). Fuer die Fehlersuche zaehlt die Build-Nummer.
    nativeBuildNummer = String(info?.build ?? "").trim() || null;
  } catch {
    // Kein Capacitor (Browser) — dann eben nur die Web-Version.
    nativeBuildNummer = null;
  }
  void meldeBuildAnServer();
}

/** Einmal je Seitenaufbau — mehr sagt nichts Neues. */
let buildGemeldet = false;

/**
 * Die Build-Nummer dem Server sagen (2026-09-03).
 *
 * Martin: *„Ja baue ein."* Bis heute sah der Server nicht, welche Version auf
 * dem Telefon läuft. Zweimal an einem Tag stand deshalb „ist der Fix
 * angekommen?" im Raum und war nur zu raten — bei den Aufnahme-Lücken (die
 * Reparatur steckt in Build 55) und beim Widget, wo eine fest eingetippte
 * „39" fast auf eine falsche Fährte geführt hätte.
 *
 * ⚠️ Fail-quiet und ohne `await` im Startweg: eine MESSUNG darf den Start
 * nie aufhalten und nie stören. Kein Netz, kein Server, kein Problem.
 *
 * ⚠️⚠️ 22.09.2026 — HIER STAND „NUR NATIV", UND DAS MACHTE DIE MESSUNG FUER
 *      BROWSER-NUTZER BLIND.
 *
 * Die alte Begruendung war: *„Im Browser gibt es keine Build-Nummer; eine
 * Meldung ohne Inhalt waere eine Zeile im Protokoll, die nichts sagt."* Das
 * stimmt nur zur Haelfte. Eine NATIVE Build-Nummer gibt es dort nicht — die
 * WEB-Version schon, und genau die ist es, die bei jeder Frontend-Reparatur
 * zaehlt.
 *
 * Gemessen am 22.09.: Albert arbeitet im Browser (MacBook, iPhone-Safari).
 * Sein Eintrag in `client_builds` stand auf pre.3139 vom 21.09. um 21:00 —
 * dem Stand seiner letzten NATIVEN Nutzung. Am selben Vormittag war er
 * mehrfach aktiv, und keine einzige dieser Sitzungen war sichtbar.
 *
 * ⚠️ Das ist genau die Frage, fuer die diese Messung am 03.09. gebaut wurde:
 *    „ist der Fix angekommen?" Am Morgen des 22.09. hat sie mich in die
 *    Irre gefuehrt — ich habe aus ihr gelesen, Martins App trage pre.3139,
 *    und daraus geschlossen, die Auslieferung stehe. Tatsaechlich war
 *    pre.3140 nie gebaut worden.
 */
async function meldeBuildAnServer(): Promise<void> {
  if (buildGemeldet) return;
  const web = typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "";
  // Ohne BEIDES gibt es nichts zu melden — dann stimmt die alte Begruendung.
  if (!nativeBuildNummer && !web) return;
  buildGemeldet = true;
  try {
    await fetch("/api/client/build", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        // Im Browser gibt es keine native Nummer — dann traegt die
        // Web-Version den Eintrag, damit die Zeile nicht leer bleibt.
        build: nativeBuildNummer || web,
        plattform: nativeBuildNummer ? "ios" : "web",
        webVersion: web,
      }),
    });
  } catch {
    /* Eine Messung, die scheitert, darf nichts weiter tun. */
  }
}

let laufzeitModellAngefragt = false;
/** Die feste Abo-Wahl — oder "auto", wenn der Server entscheiden soll. */
function aboWahl(s: { aboModell?: string }): AboWahl {
  // 23.09.2026, Ticket 2516: derselbe Leser wie am Server — „fable" wird Opus.
  return aboWahlAus(s.aboModell);
}


/**
 * Fragt den Server, was gerade antwortet, und merkt sich Grund und Ende der
 * Ruhe. (Ticket 2782) Ändert den Chip NICHT — das tun die Aufrufer.
 *
 * Der Server ist der eine Besitzer („welches Modell ruht“); hier liegt nur
 * seine letzte Antwort, und die wird neu geholt: nach jedem Zug
 * (frageGrund) und wenn die Ruhe endet (planeRuheEnde).
 */
async function frageLaufzeit(): Promise<{
  id?: string;
  fallback?: boolean;
  kunde?: boolean;
  grund?: string | null;
  ruhtBis?: number | null;
} | null> {
  const s = loadSettings();
  const wahl = s.modellBevorzugt;
  const { aktuelleBackup } = await import("./lib/settings/modell-status.js");
  const backup = aktuelleBackup();
  const q = new URLSearchParams();
  if (wahl === "grok" || wahl === "gemini") q.set("wahl", wahl);
  q.set("backup", backup);
  const r = await fetch(`/api/runtime-modell?${q.toString()}`, {
    credentials: "include",
  });
  if (!r.ok) return null;
  const j = (await r.json()) as {
    id?: string;
    fallback?: boolean;
    kunde?: boolean;
    grund?: string | null;
    ruhtBis?: number | null;
  };
  // Ticket 2779: Für ein Kundenkonto ist Gemini der Normalfall, kein Ersatz.
  kontoIstKunde = j.kunde === true;
  // Ticket 2782: warum ein Ersatz antwortet bzw. eine tiefere Abo-Stufe —
  // mit „ruht bis …“.
  ersatzGrund = !kontoIstKunde && j.fallback ? (j.grund ?? null) : null;
  stufenGrund = !kontoIstKunde && !j.fallback ? (j.grund ?? null) : null;
  planeRuheEnde(kontoIstKunde ? null : (j.ruhtBis ?? null));
  return j;
}

/**
 * Nach einem Zug: Grund und Ruhe neu holen und den Chip-Hinweis neu
 * schreiben — ohne das Modell am Chip anzutasten. (Prüfbefund zu Ticket
 * 2782: Der Grund kam nur über holeLaufzeitModell, das nach einem Zug
 * niemand rief. Der erste Ersatz-Zug stand ohne „ruht bis“ da.)
 */
async function frageGrund(): Promise<void> {
  try {
    await frageLaufzeit();
    if (lastEffectiveModel) updateFooterEffectiveModel(lastEffectiveModel);
  } catch {
    /* Netz — der Hinweis bleibt beim letzten bekannten Stand */
  }
}

/**
 * Endet eine Ruhe, fragt die App von selbst neu. (Prüfbefund zu Ticket
 * 2782) Sonst stand ein abgelaufenes „ruht bis 12:28“ da, bis zufällig
 * jemand die Statuszeile neu zeichnete. Antwortet noch der Ersatz, zeigt
 * der Chip danach wieder das Abo (holeLaufzeitModell) — es ist wieder dran.
 */
function planeRuheEnde(bis: number | null): void {
  if (ruheEndeUhr) clearTimeout(ruheEndeUhr);
  ruheEndeUhr = null;
  if (!bis) return;
  // setTimeout kennt höchstens ~24,8 Tage; ein Kontingent ruht ohnehin kürzer.
  const warte = Math.min(Math.max(bis - Date.now(), 0) + 1500, 2 ** 31 - 1);
  ruheEndeUhr = setTimeout(() => {
    ruheEndeUhr = null;
    void (fallbackLaeuft() ? holeLaufzeitModell() : frageGrund());
  }, warte);
}

async function holeLaufzeitModell(): Promise<void> {
  // 2026-08-27: Bei fester Abo-Wahl (Haiku/Sonnet/Opus) darf die Auskunft
  // NICHT die Wahl mit "claude-cli-warm" ueberschreiben — der Server kennt
  // aboModell nicht.
  //
  // 2026-09-01 (Martin: "Wenn ich auf Schreib oder Sprich schaue, steht
  // Sonnet Abo. Das stimmt aber ja gar nicht momentan!"): Wenn das Abo TOT
  // ist, antwortet Gemini. Dann muss der Chip das sagen, auch bei fester
  // Sonnet-Wahl. `fallback` ist die Weiche: tot → echte Id; lebend → Wahl.
  if (laufzeitModellAngefragt) return;
  laufzeitModellAngefragt = true;
  try {
    const j = await frageLaufzeit();
    if (!j?.id) return;
    if (j.fallback) {
      updateFooterEffectiveModel(j.id);
      return;
    }
    const fest = aboWahl(loadSettings());
    if (fest !== "auto") {
      updateFooterEffectiveModel(`claude-cli-warm-${fest}`);
      return;
    }
    updateFooterEffectiveModel(j.id);
  } catch {
    /* Netz — Fußzeile bleibt beim letzten bekannten Stand */
  } finally {
    laufzeitModellAngefragt = false;
  }
}


function renderStatusBar(): void {

  // Beim ersten Aufruf die native Build-Nummer nachladen und dann
  // erneut zeichnen — sie kommt asynchron, soll aber nicht fehlen.
  if (nativeBuildNummer === null) {
    void ladeNativeVersion().then(() => {
      if (nativeBuildNummer) renderStatusBar();
    });
  }
  // Eine Quelle: package.json via __BUILD_VERSION__. Native nie als
  // zweite Web-Nummer — wenn beide da sind: "Web … · App 39".
  setzeVersionsQuellen(
    typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "",
    nativeBuildNummer,
  );
  const vollStr = sichtbareVersionJetzt();
  for (const id of ["status-version", "nav-version-num", "settings-version"]) {
    const el = document.getElementById(id);
    if (el) el.textContent = vollStr || "—";
  }
  const settings = loadSettings();
  // Pre.490: Footer-Modell context-aware (nicht mehr nur voiceMode-aware).
  //
  // Pre.411 hat „Gemini 3.1" angezeigt sobald voiceMode=gemini-realtime in
  // Settings stand — auch wenn der User gerade PTT oder Text-Chat machte
  // (die laufen über settings.model, also Haiku/Sonnet/etc.).
  //
  // Jetzt:
  //   - Voice-Anruf läuft AKTIV (connected) → Realtime-Modell anzeigen
  //   - Sonst (PTT, Text-Chat, idle) → settings.model anzeigen
  //
  // Das entspricht der Realität: wer gerade NICHT telefoniert nutzt
  // Anthropic/Gemini-Pipeline mit dem gewählten Chat-Modell.
  const llmEl = document.getElementById("status-llm");
  if (llmEl) {
    // ⚠️ 27.09.2026 (Prüfbefund zu pre.3356): Die Werkzeug-Zahl gehört zum
    //    Zug, den der Chip zeigt — und das tut er nur über
    //    updateFooterEffectiveModel, das sie wieder einblendet. Im Anruf und
    //    solange der Chip „…“ zeigt, stand sonst die Zahl des letzten
    //    Chat-Zugs neben einem anderen Modell.
    zeigeWerkzeugZahl(false);
    // pre.902 (Martin): Badge richtet sich am VOICE-MODUS aus, nicht am
    // "connected"-Zustand. Vorher lief renderStatusBar VOR dem connected=true-
    // Flip (app.ts ~10070/10071) → Badge blieb auf dem Text-Modell (Sonnet 4.6)
    // haengen und wurde nie aktualisiert. Ist Realtime-Voice gewaehlt, zeigt der
    // Badge jetzt das echte Realtime-Modell (Gemini 3.1).
    // Pre.923 (Martin): das Realtime-Modell NUR im aktiven Anruf zeigen — sonst
    // (idle, PTT, Text-Chat) das Chat-Modell. Vorher stand „Gemini 3.1f" auch
    // ohne Anruf da. Der connected=true-Flip in handleConnect läuft jetzt VOR
    // renderStatusBar (siehe dort), daher ist `connected` hier verlässlich.
    const showRealtime = connected && settings.voiceMode === "gemini-realtime";
    if (showRealtime) {
      const rtId = settings.realtimeModel ?? "";
      const fullLabel = shortLabel(GEMINI_REALTIME_MODELS, rtId);
      llmEl.textContent = fullLabel
        // 23.09.2026: „— empfohlen" / „— bisher" gehören in die Auswahl,
        // nicht in den Kopf.
        .replace(/\s+—.*$/, "")
        .replace(/\s+\(.*?\)\s*$/, "")
        .replace(/\s+Flash\s+Live/i, " Flash")
        .replace(/^Gemini\s+(\d[\d.]*)\s+Flash$/i, "Gemini $1");
    } else if (fallbackLaeuft()) {
      // 2026-09-01: PTT rief renderStatusBar oft. Vorher: erst Sonnet-Wahl,
      // dann Fetch → Chip zuckelte Sonnet/Gemini. Wer schon als Fallback
      // laeuft, bleibt stehen.
      updateFooterEffectiveModel(lastEffectiveModel!);
      void holeLaufzeitModell(); // Ticket 2782: Grund, „ruht bis …“
    } else if (aboWahl(settings) !== "auto") {
      // 2026-08-27: Wer Haiku waehlt, will Haiku sofort sehen.
      // 2026-09-01: Nur vorlaeufig — holeLaufzeitModell ueberschreibt, wenn
      // das Abo tot ist und Gemini wirklich antwortet.
      updateFooterEffectiveModel(`claude-cli-warm-${aboWahl(settings)}`);
      void holeLaufzeitModell();
    } else if (lastEffectiveModel && !/^claude-cli/i.test(lastEffectiveModel)) {
      // Zuletzt wirklich geantwortet — kein veraltetes „Abo"-Schild.
      updateFooterEffectiveModel(lastEffectiveModel);
    } else {
      // 2026-07-06 (Martin: "warum steht immer noch Haiku 4.5 da?"): Bei
      // aktivem CLI-Abo-Chat wird die Modell-Auswahl (settings.model) UMGANGEN
      // — die warme Abo-Session antwortet. Also NICHT das ungenutzte
      // Wunsch-Modell zeigen. Das echte Abo-Modell (Sonnet/Opus) setzt
      // updateFooterEffectiveModel nach der ersten Antwort.
      let cliOn = false;
      try {
        cliOn =
          (
            JSON.parse(localStorage.getItem("voice-v8.settings") || "{}") as {
              cliChat?: boolean;
            }
          ).cliChat === true;
      } catch {
        /* ignore */
      }
      if (cliOn) {
        llmEl.textContent = "…";
        llmEl.title = "Frage den Server, welches Modell jetzt antwortet";
        void holeLaufzeitModell();
      } else {
        llmEl.textContent = shortLabel(MODEL_OPTIONS, settings.model);
        delete llmEl.dataset.fallback;
      }
    }
  }
  const voiceEl = document.getElementById("status-voice");
  if (voiceEl)
    voiceEl.textContent = shortLabel(VOICE_OPTIONS, settings.voiceId);
  // 27.09.2026 (Ticket 2671): der Schalter liest den Stand bei jedem Zeichnen
  // — nach dem Laden, nach jeder Aenderung und nach jedem Abgleich.
  zeigeTiefe(document.getElementById("status-tiefe"));
  // 2026-08-27 (Martin): Die Voice-Modus-Anzeige stand seit Pre.184 in der
  // Fusszeile. Sie ist entfallen — "das ist sowieso immer Gemini Realtime".
  // settings.voiceMode gibt es unveraendert; nur das Etikett ist weg.
}

/**
 * Pre.421: Wenn Server bei chat-done meldet welches LLM die Antwort
 * tatsächlich produziert hat, überschreiben wir den Footer-Text damit
 * User die Realität sieht (z.B. „Gemini Lite" wenn Anthropic credit-leer
 * war). Bei Reload zeigt renderStatusBar wieder das Settings-Default.
 */
// 2026-07-05 (Martin): das zuletzt WIRKLICH genutzte Modell — damit der
// Footer nicht bei jedem renderStatusBar auf das Wunsch-Modell zurueckfaellt.
const LAST_MODEL_KEY = "voice-v8.last-effective-model";
/**
 * Sagt der Server, dass dieses Konto Kunde ist? (28.09.2026, Ticket 2779)
 * Dann antwortet Gemini im Paket — die Fußzeile färbt es nicht als Notfall.
 * Gesetzt von holeLaufzeitModell, gelesen von updateFooterEffectiveModel.
 */
let kontoIstKunde = false;
/**
 * Der Grund des Servers, warum gerade ein Ersatz antwortet (Ticket 2782) —
 * mit „ruht bis …“. Gesetzt von holeLaufzeitModell, gelesen von
 * updateFooterEffectiveModel. null: noch nicht gefragt.
 */
let ersatzGrund: string | null = null;
/**
 * Warum eine tiefere Abo-Stufe antwortet, obwohl das Abo läuft (Ticket 2782):
 * „Opus 5.5 überlastet — ruht bis …, Sonnet 5 übernimmt“. Gesetzt von
 * frageLaufzeit, gelesen vom Chip-Hinweis und vom Blatt „Was antwortet“.
 */
let stufenGrund: string | null = null;
/** Die Uhr, die beim Ende der Ruhe neu fragt (planeRuheEnde). */
let ruheEndeUhr: ReturnType<typeof setTimeout> | null = null;
let lastEffectiveModel: string | null = (() => {
  try {
    return localStorage.getItem(LAST_MODEL_KEY);
  } catch {
    return null;
  }
})();

/** Claude tot, Gemini (oder Grok) antwortet wirklich. */
function fallbackLaeuft(): boolean {
  return (
    !!lastEffectiveModel &&
    /gemini|gpt-|grok/i.test(lastEffectiveModel) &&
    !/^gemini-flash-schnell$/i.test(lastEffectiveModel)
  );
}

function updateFooterEffectiveModel(model: string): void {
  const llmEl = document.getElementById("status-llm");
  if (!llmEl) return;
  if (model && !/^prewarm/i.test(model) && model !== "kein-modell") {
    lastEffectiveModel = model;
    try {
      localStorage.setItem(LAST_MODEL_KEY, model);
    } catch {
      /* private mode */
    }
  }
  // pre.901 (Martin): In einer Realtime-Voice-Session den Badge NICHT mit dem
  // Text-/Tool-LLM überschreiben — er soll das ECHTE Sprach-Modell zeigen
  // (Gemini 3.1), nicht z.B. Sonnet 4.6, das nebenbei eine Antwort lieferte.
  try {
    const vm = (
      JSON.parse(localStorage.getItem("voice-v8.settings") || "{}") as {
        voiceMode?: string;
      }
    ).voiceMode;
    if (connected && vm === "gemini-realtime") return;
  } catch {
    /* ignore */
  }
  let label = model;
  // Abo-Pfad (kein API-Guthaben): warme Sitzung = schnell, One-Shot = Fallback.
  // 2026-07-03 (Martin): Schnell-Spur unmissverstaendlich zeigen.
  const warm = warmEtikett(model);
  // 2026-08-30: Das Blitz-Emoji ist raus — Martins stehende Regel (keine
  // Emojis in der Oberflaeche). Das Wort allein sagt dasselbe.
  if (/schnell/i.test(model)) label = "Schnell";
  else if (warm) label = warm.label;
  else if (/claude-cli/i.test(model)) label = "Claude-Abo";
  else if (/^oebb$/i.test(model)) label = "ÖBB";
  /*
   * ⚠️⚠️ 23.09.2026 — HIER STANDEN DIE VERSIONEN FEST DRIN: „Opus 5",
   *    „Sonnet 5", „Haiku 4.5", „Gemini 3.7", „Grok 4.6".
   *
   *    Nach dem Wechsel auf Opus 5.5 hätte die Fußzeile weiter „Opus 5"
   *    gezeigt — und „Gemini 3.7" war schon falsch, seit das Register am
   *    14.09. auf 3.8 zog. Die Anzeige behauptete Dinge, die nicht stimmten.
   *    Jetzt kommt der Name aus der Kennung selbst (shared/modell-namen.ts).
   */
  else if (/gemini-2.5-flash-lite/i.test(model)) label = "Gemini Lite";
  else if (/claude-?(haiku|sonnet|opus|fable)/i.test(model))
    label = claudeName(model) || model;
  else if (/gemini|grok/i.test(model)) label = nameFuerModell(model);
  else if (/gpt-4o-mini/i.test(model)) label = "GPT-4o mini";
  else if (/gpt-4o/i.test(model)) label = "GPT-4o";
  // Ticket 2930: wechselte das gezeigte Modell nach der letzten Auskunft, gilt deren „kalt“ nicht mehr für den Chip —
  // lieber schweigen und für das neue Modell neu fragen, als „Sonnet · kalt“ für eine Opus-Sitzung zu zeigen.
  if (waermeKalt && !kaltFuerChip(true, waermeFuer, model)) {
    waermeKalt = false;
    waermeFuer = "";
    void holeWaerme();
  }
  llmEl.textContent = label + zugZusatz();
  zeigeWerkzeugZahl();
  const grund = /schnell/i.test(model)
    ? "Schnell-Spur — einfache Anfrage direkt via Gemini Flash beantwortet (~2 s)"
    : warm
      ? warm.titel
      : /claude-cli/i.test(model)
        ? "Chat über dein Max-Abo (CLI)"
        : `aktiv: ${model}`;
  /*
   * ⚠️ Ticket 2175 — die Zahl darf nicht falsch verstanden werden. Sie ist
   *    die Zeit bis zum ERSTEN WORT, nicht die Gesamtdauer. Ohne diesen Satz
   *    stuende eine Zahl da, deren Bedeutung niemand kennt; genau daran ist
   *    die Denkstufen-Messung am 08.09. schon einmal irregegangen.
   */
  llmEl.title = zugStand.msErstesWort
    ? `${grund}\n${(zugStand.msErstesWort / 1000).toFixed(1).replace(".", ",")} s bis zum ersten Wort` +
      (zugStand.werkzeuge > 0
        ? `, ${zugStand.werkzeuge} Werkzeug${zugStand.werkzeuge === 1 ? "" : "e"} im Zug`
        : "")
    : grund;
  // Die Regel (Amber nur für einen echten Ersatz, nie für ein Kundenkonto)
  // steht in lib/settings/modell-status.ts — Ticket 2779.
  const isFallback = istErsatzModell(model, kontoIstKunde);
  if (isFallback) {
    llmEl.dataset.fallback = "1";
    llmEl.title = ersatzTitel(label, ersatzGrund);
  } else {
    delete llmEl.dataset.fallback;
    // Ticket 2782: ruht das gewählte Modell, sagt der Hinweis es dazu.
    if (warm) llmEl.title = aboTitel(llmEl.title, stufenGrund);
  }
}

// Pre.191: Extension-Status (Globe-Icon) im Footer.
async function refreshExtensionStatus(): Promise<void> {
  const el = document.getElementById("status-ext");
  if (!el) return;
  try {
    const { fetchExtensionStatus } = await import("./lib/connectors-api.js");
    const s = await fetchExtensionStatus();
    const on = Boolean(s.connected);
    el.dataset.state = on ? "on" : "off";
    el.title = on
      ? "Browser-Extension verbunden"
      : "Browser-Extension nicht verbunden";
    el.setAttribute("aria-label", el.title);
  } catch {
    el.dataset.state = "off";
  }
}

function renderStatusBarState(state: VoiceState): void {
  const el = document.getElementById("status-bar-state");
  if (el) {
    el.dataset.state = state;
    el.textContent = STATUS_BAR_STATE_LABELS[state];
  }
  const modeEl = document.getElementById("status-bar-mode");
  if (modeEl) {
    modeEl.dataset.mode = mode;
    modeEl.textContent = mode === "voice" ? "Anruf" : "Chat";
  }
}

/**
 * Pre.327: Web-Audio-Chime für Connection-State-Übergänge.
 *
 * "ChatGPT-style" akustisches Signal damit der User auch OHNE auf den
 * Bildschirm zu schauen hört wann die Verbindung steht. Generiert live
 * mit OscillatorNodes — kein Asset-File nötig, kein Netzwerk-Roundtrip.
 *
 * - "connected": aufsteigendes C5→E5 (Dur-Terz), bell-like, freundlich.
 * - "reconnected": C5→D5 (kleinere Terz), kürzer, signalisiert "back".
 * - "lost": absteigendes E5→C5, leiser, dezent (kein Alarm-Ton).
 *
 * Defensiv: viele Browser blocken AudioContext bis zur ersten User-Geste.
 * Wir lazy-instanziieren beim ersten Call und brechen still ab wenn
 * `audioContext.state === "suspended"` (kein User-Gesten-Unlock vorhanden).
 *
 * Respektiert localStorage("voice-v8.connection-tones"):
 *   "off" → komplett stumm. Default = on.
 */
let _audioCtx: AudioContext | null = null;
function getAudioCtx(): AudioContext | null {
  if (_audioCtx) return _audioCtx;
  try {
    const Ctor =
      window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    _audioCtx = new Ctor();
    return _audioCtx;
  } catch {
    return null;
  }
}
/**
 * Pre.329: Muss VOM CLICK-HANDLER aus aufgerufen werden (synchron, gleicher
 * Tick wie das echte User-Event). Erzeugt den AudioContext und resumed ihn
 * sofort — damit der Browser den Geste-Trust an unseren Context bindet.
 * Ohne diesen "Prime" landet `playConnectionTone()` später (nach 1-3s
 * await room.connect) in einem `suspended` Context — Ton ist stumm.
 *
 * Zusätzlich spielen wir einen unhörbaren Mikro-Buffer (1 Sample, gain 0)
 * — manche WebKit-Varianten verlangen das, sonst gilt der Context als
 * "nicht-aktiviert" auch nach resume().
 */
function primeAudio(): void {
  // Pre.335: Vibration als zusätzliches Click-Feedback. Auf Phones IMMER
  // spürbar — auch wenn Audio stumm bleibt. iOS Safari unterstützt das
  // erst seit 16.4 selektiv; auf Android funktioniert es überall.
  if ("vibrate" in navigator) {
    try {
      navigator.vibrate(30);
    } catch {
      /* ignore */
    }
  }
  const ctx = getAudioCtx();
  if (!ctx) {
    showToast("Audio: kein AudioContext im Browser", "warning", 3000);
    return;
  }
  if (ctx.state === "suspended") {
    void ctx.resume().catch(() => undefined);
  }
  try {
    const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = 0;
    src.connect(g);
    g.connect(ctx.destination);
    src.start();
  } catch {
    /* old browser without createBuffer — playConnectionTone wird wenigstens versuchen */
  }
  // Pre.334: GARANTIERT hörbarer Click-Beep IM SELBEN TICK wie der echte
  // User-Klick. Wenn iOS / Browser Audio prinzipiell erlauben, MUSS dieser
  // Beep hörbar sein — wenn er auch stumm bleibt, ist das Problem ein
  // OS-Lautstärke- oder Silent-Mode-Issue, KEIN App-Bug.
  //
  // Sehr kurz (120ms), hoch genug zum Anstoßen (A5 = 880Hz), Volume HOCH
  // (0.5). Das ist der „Wähl-Ton" — bestätigt akustisch dass der
  // Anrufen-Klick angekommen ist. Der echte Connection-Chime kommt
  // später nach Connect.
  try {
    // P1.4 (Audit): Beep respektiert den Verbindungstöne-Schalter — und
    // mit weichem Envelope (linearRamp auf 0.12 statt 15ms-Sprung auf 0.5)
    // knackst er nicht mehr. Der AudioContext-Prime davor bleibt IMMER.
    if (localStorage.getItem("voice-v8.connection-tones") === "off") return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.025);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    osc.start(now);
    osc.stop(now + 0.18);
    console.log(
      "[chime] click-beep gespielt (ctx.state=" + ctx.state + ", vol=0.12, A5)",
    );
  } catch (e) {
    console.warn("[chime] click-beep fehlgeschlagen:", e);
  }
}
type ConnectionTone = "connected" | "reconnected" | "lost";
/**
 * Pre.336: Telefon-Ringing-Tone. Martin-Wunsch 2026-05-17 (7. Wdh):
 * "Vielleicht wie ein Telefonanruf, damit Feedback kommt."
 *
 * Klassisches Anruf-Ringing-Pattern: 2 Töne mit ~600ms Pause, dann 4s
 * Stille, dann wieder 2 Töne — wie ein europäisches Ring-Pattern.
 * Frequenz ~440Hz + 480Hz (Standard-Telefon-Ring-Mix).
 *
 * Läuft sobald der User auf "Anrufen" klickt UND wird gestoppt sobald
 * der State `listening` erreicht ist (= Bot da, Anruf geht durch).
 * Bei error/abort wird der Tone auch gestoppt.
 */
let _ringingInterval: ReturnType<typeof setInterval> | null = null;
let _ringingOsc: {
  osc1: OscillatorNode;
  osc2: OscillatorNode;
  gain: GainNode;
} | null = null;

function playRingPulse(): void {
  const ctx = getAudioCtx();
  if (ctx?.state !== "running") return;
  try {
    const now = ctx.currentTime;
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    osc1.type = "sine";
    osc2.type = "sine";
    osc1.frequency.value = 440;
    osc2.frequency.value = 480;
    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);
    // 2 kurze Pulse à 350ms mit 200ms Pause dazwischen — europäisches
    // Ringing-Pattern (vs US single long ring).
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.04);
    gain.gain.setValueAtTime(0.18, now + 0.32);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    gain.gain.setValueAtTime(0.0001, now + 0.55);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.59);
    gain.gain.setValueAtTime(0.18, now + 0.87);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.95);
    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.98);
    osc2.stop(now + 0.98);
    _ringingOsc = { osc1, osc2, gain };
  } catch {
    /* ignore — Audio-Stack einfach nicht stören */
  }
}

let _ringingSafetyTimer: ReturnType<typeof setTimeout> | null = null;
function startRingingTone(): void {
  if (_ringingInterval) return; // schon am Laufen
  // P1.6 (Audit): Klingeln NUR beim echten Aufbau aus der Ruhe — nie in
  // eine stehende Verbindung hinein (Zombie-"läutet" während Reconnects).
  if (
    connected ||
    connecting ||
    (currentState !== "idle" && currentState !== "error")
  )
    return;
  try {
    if (localStorage.getItem("voice-v8.connection-tones") === "off") return;
  } catch {
    /* ignore */
  }
  // Erstes Klingeln SOFORT (im Click-Tick, damit iOS Audio durchlässt),
  // danach alle 2.5s wiederholen bis stopRingingTone().
  playRingPulse();
  _ringingInterval = setInterval(() => playRingPulse(), 2500);
  // Pre.337 Safety-Net: Wenn der State-Übergang aus irgendeinem Grund
  // nicht greift (Realtime springt direkt zu speaking, race condition,
  // websocket-edge-case), spätestens nach 15s auto-stoppen — sonst
  // klingelt das Phone endlos während der Bot schon redet.
  if (_ringingSafetyTimer) clearTimeout(_ringingSafetyTimer);
  _ringingSafetyTimer = setTimeout(() => {
    console.warn("[chime] ringing safety-stop nach 15s");
    stopRingingTone();
  }, 15_000);
  console.log("[chime] ringing started");
}

function stopRingingTone(): void {
  if (_ringingInterval) {
    clearInterval(_ringingInterval);
    _ringingInterval = null;
  }
  if (_ringingSafetyTimer) {
    clearTimeout(_ringingSafetyTimer);
    _ringingSafetyTimer = null;
  }
  if (_ringingOsc) {
    try {
      // P1.5 (Audit): weich ausblenden statt hartem stop() — der harte
      // Schnitt mitten in der Sinuswelle war das Knacksen beim Abheben.
      const ctx = getAudioCtx();
      if (ctx) {
        const now = ctx.currentTime;
        _ringingOsc.gain.gain.cancelScheduledValues(now);
        _ringingOsc.gain.gain.setTargetAtTime(0, now, 0.015);
        _ringingOsc.osc1.stop(now + 0.08);
        _ringingOsc.osc2.stop(now + 0.08);
      } else {
        _ringingOsc.osc1.stop();
        _ringingOsc.osc2.stop();
      }
    } catch {
      /* schon fertig */
    }
    _ringingOsc = null;
  }
  console.log("[chime] ringing stopped");
}

function playConnectionTone(kind: ConnectionTone): void {
  // Pre.336: Ringing immer stoppen wenn Connection-Tone feuert — die
  // beiden Sounds dürfen sich nicht überlagern.
  stopRingingTone();
  try {
    if (localStorage.getItem("voice-v8.connection-tones") === "off") return;
  } catch {
    /* localStorage unavailable */
  }
  const ctx = getAudioCtx();
  if (!ctx) {
    console.warn("[chime]", kind, "→ kein AudioContext verfügbar");
    return;
  }
  // Pre.332: WENN suspended, blockierender resume() — kein fire-and-forget.
  // Vorher: void ctx.resume() return-te sofort, Tone wurde gespielt während
  // ctx noch suspended war → silent. Jetzt: wir warten kurz auf resume,
  // dann erst die Tones planen. Plus Logging damit man im Console sieht
  // was wirklich passiert.
  const actuallyPlay = (): void => {
    if (ctx.state !== "running") {
      console.warn(
        "[chime]",
        kind,
        "→ AudioContext state =",
        ctx.state,
        "(Browser-Audio-Policy blockiert)",
      );
      return;
    }
    // Pre.332: Volume verdoppelt (0.16 → 0.32). Vorher zu leise gegen
    // typische Phone-Lautsprecher und gegen LiveKit-Bot-Audio.
    const C5 = 523.25;
    const D5 = 587.33;
    const E5 = 659.25;
    const [f1, f2, stagger, peak] =
      kind === "connected"
        ? [C5, E5, 0.09, 0.32]
        : kind === "reconnected"
          ? [C5, D5, 0.07, 0.28]
          : [E5, C5, 0.1, 0.22];
    const now = ctx.currentTime;
    const playPartial = (freq: number, startAt: number): void => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t0 = now + startAt;
      const tAttack = t0 + 0.025;
      const tHold = tAttack + 0.06;
      const tEnd = tHold + 0.3;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(peak, tAttack);
      gain.gain.setValueAtTime(peak, tHold);
      gain.gain.exponentialRampToValueAtTime(0.0001, tEnd);
      osc.start(t0);
      osc.stop(tEnd + 0.02);
    };
    playPartial(f1, 0);
    playPartial(f2, stagger);
    console.log(
      "[chime]",
      kind,
      "→ gespielt (ctx.state=running, peak=" + peak + ")",
    );
  };
  if (ctx.state === "suspended") {
    ctx
      .resume()
      .then(() => actuallyPlay())
      .catch((e) => {
        console.warn(
          "[chime]",
          kind,
          "→ ctx.resume() rejected:",
          e instanceof Error ? e.message : String(e),
        );
      });
  } else {
    actuallyPlay();
  }
}

/**
 * Ticket 2257 (13.09.2026): kurzer, NICHT gesprochener Hinweiston für eine
 * Werkzeug-Pause, die lange genug dauert, dass sie wie Stillstand wirkt
 * (siehe WERKZEUG_TON_MS). Bewusst kein Satz — genau davon hat Martin sich
 * am 03.09. explizit abgemeldet ("Das ist anstrengend").
 *
 * Ein einzelner weicher Ton (A4, 220ms), deutlich leiser/kürzer als der
 * Connection-Chime — soll auffallen, aber nicht stören. Nutzt denselben
 * Schalter wie die Connection-Töne (`voice-v8.connection-tones`); ein
 * eigener Schalter dafür ist nicht Teil dieses Tickets.
 */
function playWerkzeugWarteTon(): void {
  try {
    if (localStorage.getItem("voice-v8.connection-tones") === "off") return;
  } catch {
    /* localStorage unavailable */
  }
  const ctx = getAudioCtx();
  if (ctx?.state !== "running") return;
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 440; // A4 — tiefer/weicher als der Click-Beep (A5)
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.1, now + 0.04);
    gain.gain.setValueAtTime(0.1, now + 0.14);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.start(now);
    osc.stop(now + 0.24);
  } catch (e) {
    console.warn("[chime] werkzeug-warte-ton fehlgeschlagen:", e);
  }
}

/**
 * Pre.326: Floating Toast für transiente Connection-Events (Reconnect-Versuch,
 * "wieder da"). Bewusst NICHT der Banner-Slot — der ist für persistente
 * Crashes (rot, mit Reconnect-Button). Toasts sind kurz sichtbar und blocken
 * keine UI. Mehrere Toasts gleichzeitig stapeln sich vertikal.
 */
type ToastKind = "info" | "success" | "warning" | "error";
// Pre.830: identische Toasts kurz hintereinander unterdrücken — bei
// Reconnect-Kaskaden stand „Verbindung wieder da" 3× untereinander.
let lastToastMsg = "";
let lastToastAt = 0;

function showToast(
  message: string,
  kind: ToastKind = "info",
  ttlMs = 3500,
): void {
  const nowT = Date.now();
  if (message === lastToastMsg && nowT - lastToastAt < 8000) return;
  lastToastMsg = message;
  lastToastAt = nowT;
  let host = document.getElementById("toast-host");
  if (!host) {
    host = document.createElement("div");
    host.id = "toast-host";
    document.body.appendChild(host);
  }
  const toast = document.createElement("div");
  toast.className = `toast toast--${kind}`;
  toast.setAttribute("role", "status");
  toast.textContent = message;
  host.appendChild(toast);
  // Animate-in nach 1 frame, sonst greift die CSS-Transition nicht.
  requestAnimationFrame(() => toast.classList.add("toast--in"));
  setTimeout(() => {
    toast.classList.remove("toast--in");
    toast.addEventListener("transitionend", () => toast.remove(), {
      once: true,
    });
    // Fallback falls die Transition nicht feuert (z.B. reduce-motion).
    setTimeout(() => toast.remove(), 400);
  }, ttlMs);
}

/**
 * 16.10.2026 (Signal an Martin, scripts/signal-martin.mjs): eine Karte, die NICHT von selbst verschwindet — nur der Schließen-Knopf
 * (Klick, Enter, Leertaste) räumt sie ab. role=alert/aria-live: Vorleser sagen sie an. Kein Ton.
 */
function showBleibtToast(title: string, body: string): void {
  let host = document.getElementById("toast-host");
  if (!host) {
    host = document.createElement("div");
    host.id = "toast-host";
    document.body.appendChild(host);
  }
  const karte = document.createElement("div");
  karte.className = "toast toast--bleibt";
  karte.setAttribute("role", "alert");
  karte.setAttribute("aria-live", "assertive");
  const kopf = document.createElement("strong");
  kopf.className = "toast__titel";
  kopf.textContent = title.slice(0, 60);
  const text = document.createElement("span");
  text.className = "toast__text";
  text.textContent = body.slice(0, 180);
  const zu = document.createElement("button");
  zu.type = "button";
  zu.className = "toast__zu";
  zu.textContent = "Schließen";
  zu.setAttribute("aria-label", `Meldung schließen: ${title}`);
  zu.addEventListener("click", () => karte.remove());
  karte.append(kopf, text, zu);
  host.appendChild(karte);
  requestAnimationFrame(() => karte.classList.add("toast--in"));
}

function setState(state: VoiceState): void {
  // Pre.327: prev-State festhalten BEVOR wir überschreiben — sonst können
  // wir keine Übergänge erkennen (connecting → listening = "verbunden",
  // reconnecting → listening = "wieder da"). Connection-Chime wird unten
  // anhand dieses Übergangs ausgelöst, NICHT bei jedem listening-Eintritt
  // (sonst klingelt es auch nach jedem Bot-Reply).
  const prev = currentState;
  currentState = state;
  // Pre.444: Quality-Indikator nur sichtbar wenn eine Voice-Session läuft.
  // Bei idle entfernen wir das data-Attribut → CSS-Selector matcht nicht
  // mehr → Indikator verschwindet.
  if (state === "idle") {
    delete document.body.dataset.voiceQuality;
    // 2026-07-12 (Martin): Anruf beendet/abgebrochen -> Dock zurueck auf
    // "Anrufen" (Telefon-Symbol), nicht auf "Auflegen" haengenbleiben.
    if (mode === "voice") mode = "chat";
  }
  // Pre.470: Voice-Live-Banner mit Timer. Sichtbar sobald Verbindung
  // steht (listening/speaking/thinking), ausgeblendet bei idle/error/
  // connecting/reconnecting (während connecting läuft der Klingelton,
  // während reconnecting der "Verbindung wackelt"-Banner).
  const isVoiceLive =
    state === "listening" ||
    state === "speaking" ||
    state === "thinking" ||
    state === "paused-phone";
  const wasVoiceLive =
    prev === "listening" ||
    prev === "speaking" ||
    prev === "thinking" ||
    prev === "paused-phone";
  if (isVoiceLive && !wasVoiceLive) {
    startVoiceLiveBanner();
  } else if (!isVoiceLive && wasVoiceLive) {
    stopVoiceLiveBanner();
  }
  // Pre.337: Ringing-Tone IMMER stoppen bei JEDEM Übergang weg von
  // connecting/reconnecting — auch wenn der State direkt nach speaking
  // (Realtime-Bot startet sofort mit Greeting) oder thinking springt
  // (Tool-Call im ersten Reply). Vorher hörte das Klingeln nur bei
  // "listening" auf — wenn der State "listening" übersprungen wurde,
  // klingelte das Phone in voller Lautstärke während der Bot redete.
  // Martin-Beschwerde 2026-05-17: "Es läutet die ganze Zeit das Telefon
  // im Hintergrund, obwohl ich schon sprechen kann."
  if (
    (prev === "connecting" || prev === "reconnecting") &&
    state !== "connecting" &&
    state !== "reconnecting"
  ) {
    stopRingingTone();
  }
  if (state === "listening" && prev === "connecting") {
    playConnectionTone("connected");
    // Pre.335: 3-Kanal-Signal nach 6 vergeblichen Audio-Versuchen.
    // Wenn AudioContext stumm bleibt (iOS-Restrictions, Silent-Mode),
    // sollen mindestens 2 der 3 Kanäle durchkommen:
    //   Kanal 1: Audio-Chime (siehe playConnectionTone)
    //   Kanal 2: Vibration — 100ms Puls (iOS/Android Phone-Hardware)
    //   Kanal 3: Visual-Toast (immer sichtbar wenn Display an)
    if ("vibrate" in navigator) {
      try {
        navigator.vibrate(120);
      } catch {
        /* ignore */
      }
    }
    // 2026-07-12 (Martin): Toast entfernt — die gruene Statuszeile oben zeigt
    // "verbunden" ohnehin; Chime + Vibration bleiben als Signal.
  } else if (state === "listening" && prev === "reconnecting") {
    if (preemptiveRefreshActive) {
      // Pre.644: GEPLANTER Refresh (vor dem ~10min-Cut) — nahtlos, kein
      // lauter Chime/Vibration/Toast. Die ruhige Pille kam schon vorher.
      preemptiveRefreshActive = false;
    } else {
      playConnectionTone("reconnected");
      if ("vibrate" in navigator) {
        try {
          navigator.vibrate([60, 40, 60]);
        } catch {
          /* ignore */
        }
      }
      showToast("Verbindung wieder da", "success", 2000);
    }
  } else if (
    state === "error" &&
    (prev === "listening" || prev === "speaking" || prev === "thinking")
  ) {
    playConnectionTone("lost");
  } else if (state === "error" || state === "idle") {
    // Pre.336: Ringing-Tone auch stoppen wenn Verbindungsaufbau scheitert
    // oder User auflegt — sonst klingelt das Phone endlos.
    stopRingingTone();
  }
  refs.state.dataset.state = state;
  refs.state.textContent = STATE_LABELS[state];
  // Ticket 3182: ruhige Zeile NUR solange der Telefon-Schutz das Mikro hält.
  const pauseNote = document.getElementById("phone-pause-note");
  if (pauseNote) pauseNote.hidden = state !== "paused-phone";
  // 2026-07-22 (Martin, "was bedeutet bereit?"): der Ruhezustand traegt
  // keine Information — der Chip erscheint nur, wenn wirklich etwas
  // passiert (verbinden/hoeren/denken/sprechen/Fehler).
  refs.state.hidden = state === "idle";
  renderStatusBarState(state);
  // Focus-Mode reagiert auf state-Wechsel: bei speaking → mic auto-aus,
  // bei listening → mic auto-an (sofern User nicht manuell muted).
  applyFocusMode(state);
  renderDock(state);

  // Pre.326: data-state auf den beiden Haupt-Buttons. CSS rendert daraus
  // den ChatGPT-artigen Ring/Pulse/Wave. Beide Buttons bekommen denselben
  // State — der gerade unsichtbare ignoriert ihn, der sichtbare zeigt ihn.
  refs.connectBtn.dataset.state = state;
  refs.hangupBtn.dataset.state = state;

  // Bei Wechsel auf listening/idle: NUR die getrackte liveBotBubble
  // räumen wenn sie noch is-recording ist. Pre.99 hatte einen DOM-weiten
  // querySelectorAll-Sweep der versehentlich auch frische User-Skeletons
  // entfernte (Race: STT-final noch nicht da, Bubble noch is-recording,
  // Cleanup räumt sie ab → Frage des Users verschwand). Zurück auf
  // konservative Variante.
  if (state === "listening" || state === "idle") {
    if (liveBotBubble?.classList.contains("is-recording")) {
      liveBotBubble.remove();
      liveBotBubble = null;
      /* botFrameStreak removed pre.105 */
    }
    clearBotSilenceTimer();
  }

  if (state === "error") {
    // Pre.273: Crash-Alert prominent — Banner + Reconnect-Button +
    // Vibrate + Audio-Bell damit User es nicht übersieht.
    showCrashBanner(
      "Verbindung zum Sprach-Server verloren. Reconnect probieren?",
    );
  } else if (state === "connecting") {
    refs.banner.dataset.kind = "info";
    refs.banner.dataset.owner = "state";
    refs.banner.classList.remove("banner--crash");
    refs.banner.replaceChildren();
    refs.banner.textContent = "Verbinde mit dem Sprach-Server…";
    refs.banner.hidden = false;
  } else if (state === "reconnecting") {
    // Pre.448: Banner WIEDER zeigen bei reconnecting — User soll sofort
    // sehen dass die Verbindung wackelt, nicht erst über den Toast warten.
    // Vorher (pre.326) war Banner ausgeblendet weil Toast da war — User-
    // Feedback 2026-05-22: "Brauche IMMER Feedback wenn was nicht stimmt".
    refs.banner.dataset.kind = "warning";
    refs.banner.dataset.owner = "state";
    refs.banner.classList.remove("banner--crash");
    refs.banner.replaceChildren();
    refs.banner.textContent =
      "Verbindung wackelt — verbinde neu… (System holt sich selbst zurück)";
    refs.banner.hidden = false;
  } else {
    // Pre.786: nur räumen, wenn der Banner dem State-Automaten GEHÖRT —
    // vorher räumte jeder State-Wechsel auch fremde Notizen weg (z.B.
    // die "Update verfügbar"-Notiz: konkurrierende Besitzer EINES Elements).
    if ((refs.banner.dataset.owner ?? "state") === "state") {
      refs.banner.replaceChildren();
      refs.banner.classList.remove("banner--crash");
      refs.banner.hidden = true;
    }
  }
}

// Pre.470: Voice-Live-Banner mit mm:ss Timer
let voiceLiveStartAt = 0;
let voiceLiveTimerHandle: ReturnType<typeof setInterval> | null = null;

function startVoiceLiveBanner(): void {
  const banner = document.getElementById(
    "voice-live-banner",
  ) as HTMLButtonElement | null;
  const timerEl = document.getElementById("voice-live-timer");
  if (!banner || !timerEl) return;
  // Wenn Banner schon sichtbar (z.B. listening → speaking) Timer nicht resetten
  if (banner.hidden) {
    voiceLiveStartAt = Date.now();
    timerEl.textContent = "00:00";
    timerEl.classList.remove("timer-refresh-soon"); // Pre.769: frischer Call
    banner.hidden = false;
  }
  if (voiceLiveTimerHandle) clearInterval(voiceLiveTimerHandle);
  voiceLiveTimerHandle = setInterval(() => {
    const sec = Math.floor((Date.now() - voiceLiveStartAt) / 1000);
    const mm = String(Math.floor(sec / 60)).padStart(2, "0");
    const ss = String(sec % 60).padStart(2, "0");
    timerEl.textContent = `${mm}:${ss}`;
  }, 1000);
}

function stopVoiceLiveBanner(): void {
  const banner = document.getElementById("voice-live-banner");
  if (banner) banner.hidden = true;
  if (voiceLiveTimerHandle) {
    clearInterval(voiceLiveTimerHandle);
    voiceLiveTimerHandle = null;
  }
  voiceLiveStartAt = 0;
}

// Click auf Banner = Auflegen
document.getElementById("voice-live-banner")?.addEventListener("click", () => {
  refs.hangupBtn.click();
});

/**
 * Pre.448: Universelle Status-Banner-Funktion für Frontend-Probleme die
 * NICHT direkt state-bezogen sind (Audio-Stuck, Poor-Connection, Server-
 * Update). User-Forderung 2026-05-22: "Brauche verdammtes Feedback wenn
 * was nicht stimmt — irgendein Scheißlabel".
 */
// Geteilter Banner-Inhalt (Flex-Row + Text + optionaler Aktions-Button +
// Close-×) für showStatusBanner UND showCrashBanner — EINE Quelle, Optik in CSS
// (.banner-row/.banner-text/.banner-action/.banner-close). `message` enthält das
// Icon-Präfix; `onClose` läuft NACH dem Ausblenden (z.B. crash-Klasse entfernen).
function buildBannerRow(opts: {
  message: string;
  action?: { label: string; onClick: () => void };
  onClose?: () => void;
  kind?: "info" | "warning" | "error" | "success";
}): HTMLElement {
  const row = document.createElement("div");
  row.className = "banner-row";

  // Pre.800: echtes SVG-Icon statt Text-Glyph (ℹ/⚠ rendern auf iOS als
  // bunte Emojis und sahen "zerrupft" aus).
  if (opts.kind) {
    const ic = document.createElement("span");
    ic.className = "banner-icon";
    ic.appendChild(
      icon(
        opts.kind === "warning" || opts.kind === "error"
          ? "alert"
          : opts.kind === "success"
            ? "check"
            : "info",
        { size: 18 },
      ),
    );
    row.appendChild(ic);
  }

  const text = document.createElement("span");
  text.className = "banner-text";
  text.textContent = opts.message;
  row.appendChild(text);

  if (opts.action) {
    const action = document.createElement("button");
    action.type = "button";
    // Pre.839: normaler App-Button statt Banner-Sonderling — Martins
    // "die Buttons stehen so verloren da drinnen".
    action.className = "btn btn--primary btn--sm banner-action";
    action.textContent = opts.action.label;
    action.addEventListener("click", opts.action.onClick);
    row.appendChild(action);
  }

  // Pre.839: der app-weite runde Schließen-Knopf (pre.665) auch hier.
  const close = buildCloseButton(() => {
    refs.banner.hidden = true;
    opts.onClose?.();
  });
  close.classList.add("banner-close");
  row.appendChild(close);
  return row;
}

function showStatusBanner(opts: {
  kind: "info" | "warning" | "error" | "success";
  message: string;
  actionLabel?: string;
  actionHandler?: () => void;
  autoDismissMs?: number;
  /**
   * Pre.786: Besitzer des Banner-Slots. Der Voice-State-Automat räumt
   * NUR owner "state" (Default = bisheriges Verhalten); "update"-Notizen
   * überleben State-Wechsel und gehen nur per × oder Aktion weg.
   */
  owner?: "state" | "update";
  /** 2026-07-21: Wegklicken per X ist eine Entscheidung — hierueber
   *  erfaehrt der Aufrufer davon (Tagesfrage: heute nicht mehr zeigen). */
  onDismiss?: () => void;
}): void {
  refs.banner.dataset.kind = opts.kind;
  refs.banner.dataset.owner = opts.owner ?? "state";
  refs.banner.classList.remove("banner--crash");
  // 2026-07-20 (Martin): die ruhige Tagesfrage-Variante darf nicht an
  // fremden Bannern kleben bleiben.
  refs.banner.classList.remove("banner--deepq");
  if (opts.kind === "error") {
    refs.banner.classList.add("banner--crash");
  }
  refs.banner.hidden = false;
  refs.banner.replaceChildren();

  const row = buildBannerRow({
    kind: opts.kind,
    message: opts.message,
    onClose: opts.onDismiss,
    action:
      opts.actionLabel && opts.actionHandler
        ? {
            label: opts.actionLabel,
            onClick: () => {
              refs.banner.hidden = true;
              opts.actionHandler?.();
            },
          }
        : undefined,
  });
  refs.banner.appendChild(row);

  if (opts.autoDismissMs && opts.autoDismissMs > 0) {
    setTimeout(() => {
      if (
        !refs.banner.hidden &&
        refs.banner.dataset.owner === (opts.owner ?? "state") &&
        refs.banner.textContent?.includes(opts.message)
      ) {
        refs.banner.hidden = true;
      }
    }, opts.autoDismissMs);
  }
}

/*
 * Ticket 2919 (02.10.2026): Wartet ein Stück auf eine Berührung, meldet die Warteschlange
 * `voice-v8:tippen-weiterspielen` (lib/datei-spieler.ts). Hier steht der Hinweis; die erste Berührung irgendwo
 * setzt die Wiedergabe fort (das macht die Warteschlange selbst), der Knopf ist nur die sichtbare Aufforderung.
 *
 * 05.10.2026: Der Hinweis kommt nur noch, wenn der Browser den Start wirklich abgelehnt hat oder das Stück nach
 * mehreren frischen Starts steht (lib/vorlese-wiederanlauf.ts) — und dann an DREI Orten, die derselbe Ruf setzt:
 * das Banner (liest ein Vorleseprogramm vor, `role="status"`), die Sprech-Pille über dem Verfasser (dort liegt der
 * Daumen: sie stoppt in diesem Zustand nicht, sie wartet) und der Vorlese-Knopf der Blase, die gerade spricht.
 */
let tippKnopf: { knopf: HTMLButtonElement; vorher: string } | null = null;
/** Der Vorlese-Knopf der Antwort, die gerade spricht: der eigene Strom kennt ihn, sonst ist es die jüngste Antwort. */
function vorleseKnopfDerStimme(): HTMLButtonElement | null {
  if (currentAudioBtn) return currentAudioBtn;
  const alle = refs.chat.querySelectorAll<HTMLButtonElement>(".bubble.bot .bubble-play");
  return alle.length > 0 ? (alle[alle.length - 1] ?? null) : null;
}
window.addEventListener(TIPPEN_EREIGNIS, (ev) => {
  const an = (ev as CustomEvent<{ an?: boolean }>).detail?.an === true;
  if (an) {
    showStatusBanner({
      kind: "info",
      message: "Tippen zum Weiterspielen",
      owner: "update",
      actionLabel: "Weiterspielen",
      actionHandler: () => undefined, // das Tippen selbst löst das Fortsetzen aus (Berührung irgendwo)
    });
    refs.banner.dataset.tippen = "1";
    setVoicePrep("wartet");
    const knopf = vorleseKnopfDerStimme();
    if (knopf && !tippKnopf) {
      tippKnopf = { knopf, vorher: knopf.dataset.state ?? "idle" };
      knopf.dataset.state = "wartet";
    }
    return;
  }
  if (refs.banner.dataset.tippen === "1") {
    refs.banner.hidden = true;
    delete refs.banner.dataset.tippen;
  }
  // Es läuft wieder (oder der Zug endet — dann blendet sein Ende die Pille gleich aus).
  if (voicePrepZustand === "wartet") setVoicePrep("playing");
  if (tippKnopf) {
    // Nur zurückstellen, was noch „wartet“ zeigt: ein Stopp hat den Knopf inzwischen selbst auf Ruhe gesetzt.
    if (tippKnopf.knopf.dataset.state === "wartet") tippKnopf.knopf.dataset.state = tippKnopf.vorher;
    tippKnopf = null;
  }
});

/**
 * Pre.273: Prominenter Crash-/Disconnect-Banner.
 * - Großer Text, Warn-Icon, Reconnect-Button, dismissable.
 * - Optional Audio-Beep + Vibrate.
 */
function showCrashBanner(
  msg: string,
  opts: { silent?: boolean; autoReconnect?: boolean } = {},
): void {
  // Pre.377: Bei Auto-Reconnect weicheren Banner-Style (nicht crash-rot),
  // weil das nur eine kurze Anbieter-Unterbrechung ist.
  refs.banner.dataset.kind = opts.autoReconnect ? "info" : "error";
  refs.banner.dataset.owner = "state";
  if (opts.autoReconnect) {
    refs.banner.classList.remove("banner--crash");
  } else {
    refs.banner.classList.add("banner--crash");
  }
  refs.banner.hidden = false;
  refs.banner.replaceChildren();

  const row = buildBannerRow({
    kind: opts.autoReconnect ? "info" : "warning",
    message: msg,
    action: {
      label: "Verbinden",
      onClick: () => {
        refs.banner.hidden = true;
        refs.connectBtn.click();
      },
    },
    onClose: () => refs.banner.classList.remove("banner--crash"),
  });
  refs.banner.appendChild(row);

  // Haptik + Beep — minimal, einmalig
  if (!opts.silent) {
    if (navigator.vibrate) {
      try {
        navigator.vibrate([60, 40, 60]);
      } catch {
        /* ignore */
      }
    }
    try {
      const ctx = new (
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext
      )();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = 440;
      g.gain.value = 0.05;
      o.start();
      o.stop(ctx.currentTime + 0.15);
      setTimeout(() => void ctx.close(), 300);
    } catch {
      /* ignore audio errors */
    }
  }
}

// --- Chatverlauf persistieren --------------------------------------------

interface PersistedEntry {
  kind:
    | "user"
    | "bot"
    | "system"
    | "tool"
    | "warn"
    | "image"
    | "music"
    | "url"
    | "file";
  text?: string;
  card?: { tool: string; callId: string; isError: boolean; result: unknown; anzeige?: unknown };
  warn?: { reason?: string; correction?: string };
  image?: { url: string; caption?: string | null; from: "user" | "bot" };
  music?: MusicResult;
  urlOpen?: { url: string; title: string | null };
  fileItem?: FileDto;
  /** Ticket 2864: der Kanal, wenn er nicht die App selbst ist („telegram“). */
  kanal?: VermerkKanal;
  /** 12.10.2026: Martins Nachricht setzt die vorige fort (frontend/lib/fortsetzung-vermerk.ts). */
  fortsetzung?: boolean;
  /** 12.10.2026: die Antwort wurde gestoppt, die Teilantwort bleibt stehen. */
  abgebrochen?: boolean;
  /** 15.10.2026 (Dreier-Modus): eine Systemzeile ist ein Beitrag des Entwicklers (lib/entwickler-blase.ts). */
  entwickler?: boolean;
  /** 16.10.2026: der Beitrag des Entwicklers bittet um Ja/Nein. */
  frage?: "ja-nein";
  /** 19.10.2026: Kennung der Rückfrage des Zimmermanns (Karte, nur Anzeige). */
  rueckfrage?: string;
  /** 20.10.2026: Kennung der Ja/Nein-Karte des Schmieds (am Beitrag) bzw. der beantworteten Karte (an Martins Nachricht). */
  karte?: string;
  karteAntwort?: string;
  /** 16.10.2026 (Dreier-Modus): an wen die Zeile ging, wenn belegt (lib/adressat-vermerk.ts). */
  an?: BlasenMerkmal["an"];
  sekundaer?: BlasenMerkmal["sekundaer"];
  /** 16.10.2026 (Zimmermann P1): der Beitrag ist nicht vom Schmied (lib/fortsetzung-vermerk.ts `entwicklerMerkmal`); fehlt = Schmied. */
  entwicklerId?: string;
  /** 16.10.2026: Bilder unter dem Beitrag des Schmieds — beim Wiederaufbau erneut über `bilderAus` geprüft. */
  bilder?: BlasenMerkmal["bilder"];
  /** 20.10.2026: Martins Tipp auf Ja/Nein einer Karte — beim Wiederaufbau ohne eigene Blase (lib/fortsetzung-vermerk.ts). */
  kartenantwort?: true;
  ts: number;
}

// Pre.285: getHistoryKey() user-scoped damit ein Account-Wechsel im selben
// Browser nicht den Chat des Vorgängers anzeigt. SICHERHEITS-Fix.
// Wenn current-uid noch nicht bekannt (vor fetchMe()): legacy key OHNE
// Suffix laden — wird aber gleich darunter beim Account-Mismatch geleert.
const HISTORY_KEY_PREFIX = "voice-v8.chat-history";
const CURRENT_UID_KEY = "voice-v8.current-uid";
let _historyKey: string = HISTORY_KEY_PREFIX;
function getHistoryKey(): string {
  return _historyKey;
}
function setHistoryUid(uid: number | null): void {
  _historyKey =
    uid != null ? `${HISTORY_KEY_PREFIX}.uid-${uid}` : HISTORY_KEY_PREFIX;
}
const HISTORY_MAX = 500;

function loadHistory(): PersistedEntry[] {
  try {
    const raw = localStorage.getItem(getHistoryKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Pre.124: Migration. Vorher hat der Chat-Modus open_url / generate_image
    // / edit_image / play_music DOPPELT gespeichert (kind:"tool" aus
    // onToolCard PLUS kind:"url"/"image"/"music" aus renderToolCard).
    // Beim Reload öffnete kind:"tool" für open_url einen NEUEN Tab —
    // Tabs kumulierten sich pro Reload. Ab pre.124 wird nur noch der
    // self-persisting kind gespeichert; existierende Doppel-Einträge
    // werden hier einmalig rausgefiltert.
    const SELF_PERSIST_TOOLS = new Set([
      "open_url",
      "generate_image",
      "edit_image",
      "play_music",
    ]);
    const cleaned = parsed.filter((e: PersistedEntry) => {
      if (e?.kind === "tool" && e.card && SELF_PERSIST_TOOLS.has(e.card.tool)) {
        return false;
      }
      return true;
    });
    if (cleaned.length !== parsed.length) {
      // Cleanup persistieren, sonst läuft die Migration bei jedem Bootstrap.
      try {
        localStorage.setItem(
          getHistoryKey(),
          JSON.stringify(cleaned.slice(-HISTORY_MAX)),
        );
      } catch {
        /* localStorage unavailable */
      }
    }
    return cleaned.slice(-HISTORY_MAX);
  } catch {
    return [];
  }
}

function saveHistory(entries: PersistedEntry[]): void {
  try {
    localStorage.setItem(
      getHistoryKey(),
      JSON.stringify(entries.slice(-HISTORY_MAX)),
    );
  } catch (err) {
    // Pre.790 (#61): NICHT mehr still schlucken — ein QuotaExceeded hieß
    // vorher: ab sofort persistiert gar nichts mehr, unsichtbar.
    console.warn("[history] save fehlgeschlagen (Quota?):", err);
  }
}

let history: PersistedEntry[] = loadHistory();

function pushHistory(
  entry: PersistedEntry,
  // 2026-07-19 (Traegheits-Jagd): der Hydrate-Pull schreibt viele Eintraege
  // am Stueck — vorher lief pro Eintrag ein synchrones JSON.stringify des
  // kompletten 500er-Fensters nach localStorage (~62 Voll-Serialisierungen
  // Richtung 1 MB bei JEDEM App-Wechsel, alles auf dem Main-Thread). Mit
  // deferSave sichert der Aufrufer EINMAL am Ende.
  opts?: { deferSave?: boolean },
): void {
  history.push(entry);
  if (!opts?.deferSave) saveHistory(history);
}

// Pre.148: pre-dedup-set ENTFERNT. War pre.145/146 als Race-Fix gedacht,
// hat aber den PTT-Doppelrender nicht zuverlässig gefangen. Lösung
// jetzt: SSE-Handler droppt m.source==="ptt" komplett (PTT ist single-
// device, kein Cross-Device-Sync nötig). Voice/Chat-Echos werden über
// den existierenden history-Loop + liveBotBubble-Check entdupliziert.

function clearHistory(): void {
  history = [];
  saveHistory(history);
  while (refs.chat.firstChild) refs.chat.removeChild(refs.chat.firstChild);
}

// Pre.101: Bubble-Reihenfolge per Timestamp. Macht uns unabhängig von der
// asynchronen Stream-Reihenfolge (LiveKit liefert STT-final + Bot-Text-
// Stream + tool-cards in beliebiger Reihenfolge — wir wollen aber
// chronologisch rendern). Jede Bubble bekommt data-ts; insertOrdered
// findet die richtige Position via numerischen Vergleich.
// Während des History-Replays gesetzt — alle insertOrdered-Aufrufe (auch
// die internen via appendBubble/appendMusicBubble/etc.) verwenden den
// History-Timestamp statt Date.now(), damit alte Einträge an ihrer
// chronologischen Position bleiben.
let replayingTs: number | null = null;
/**
 * Ticket 2892: der Kanal der Verlaufszeile, deren Karten gerade gezeichnet werden („telegram“ oder keiner).
 * Ein Bild, das eine Karte (`edit_image`, `generate_image`) als Bildblase zeichnet, trägt so dieselbe
 * Kanalmarke wie die Zeile, zu der es gehört — die Karte selbst weiß nichts von ihrem Kanal. Wie
 * `replayingTs`: nur um den Aufruf herum gesetzt, im `finally` zurück.
 */
let kartenKanal: VermerkKanal | undefined;
function currentTs(): number {
  return replayingTs ?? Date.now();
}

/**
 * Der Stempel, mit dem eine Blase eingeordnet wurde (Ticket 2397).
 *
 * `insertOrdered` schreibt ihn nach `dataset.ts` — er steht also schon da und
 * muss nicht daneben mitgefuehrt werden. Genau dieses Mitfuehren war das
 * Problem: der Verlaufseintrag bekam einen ZWEITEN, spaeteren Zeitpunkt, und
 * nach dem Neuladen stand die Antwort woanders als live.
 *
 * ⚠️ Der Chat-Weg bekam den Fix am 04.07.2026 (anchorTs). Der Sprachweg und
 *    der Anruf-Weg nie — dieselbe Regel, drei Stellen, zwei davon falsch.
 */
function blasenStempel(el: HTMLElement | null): number {
  const n = Number(el?.dataset.ts);
  return Number.isFinite(n) && n > 0 ? n : currentTs();
}

function insertOrdered(bubble: HTMLElement, ts: number): void {
  // Replay-Override: wenn ein älterer ts gesetzt ist, den verwenden.
  if (replayingTs !== null) ts = replayingTs;
  bubble.dataset.ts = String(ts);
  bubble.appendChild(makeTimestampLabel(ts));
  const kids = refs.chat.children;
  let placed = false;
  for (let i = 0; i < kids.length; i++) {
    const sib = kids[i] as HTMLElement;
    // Tagestrenner tragen kein data-ts → werden hier ignoriert (Position
    // richtet sich allein nach den Bubbles; Trenner setzt refreshDayDividers).
    const sibTs = Number(sib.dataset.ts ?? 0);
    if (sibTs && sibTs > ts) {
      refs.chat.insertBefore(bubble, sib);
      placed = true;
      break;
    }
  }
  if (!placed) refs.chat.appendChild(bubble);
  scheduleDayDividers();
  planeRechercheOrdnung(refs.chat); // ein Recherche-Block steht unter der Antwort (lib/recherche.ts)
}

// --- Tagestrenner im Chat (Datum, wenn der Tag wechselt) ------------------
// Wunsch Martin: im Verlauf das Datum sehen (Gespräche gehen über Tage). Ein
// Trenner steht vor der ersten Bubble jedes LOKALEN Tages. Gruppierung lokal,
// damit er zur Bubble-Uhrzeit (ebenfalls lokal) passt.
function chatDayKey(ts: number): string {
  return new Date(ts).toDateString();
}
function chatDayLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Heute";
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return "Gestern";
  return d.toLocaleDateString("de-AT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}
// Debounced (queueMicrotask): der History-Replay fügt viele Bubbles synchron
// ein → nur EIN Durchgang statt O(n²). Idempotent (seit pre.3603: ändert nur, was falsch steht).
//
// ⚠️ 01.10.2026 (Ticket 2873): Wer den Verlauf über dem Leser ändert (Nachladen,
// Neuaufbau), muss die Trenner IM SELBEN Schritt haben — sonst entstehen sie
// erst danach und schieben den Leser um ihre Höhe (ohne Verankerung gemessen:
// 195 Punkte bei fünf Tagen; nach dem Neuaufbau stand der Chat 56 bis 173
// Punkte über dem Ende und die nächste Antwort wurde nicht mehr nachgeführt). Dafür
// `spuelTagestrenner()`: führt den geplanten Durchgang jetzt aus; der
// Microtask danach findet nichts mehr zu tun.
let dayDividerScheduled = false;
function scheduleDayDividers(): void {
  if (dayDividerScheduled) return;
  dayDividerScheduled = true;
  queueMicrotask(spuelTagestrenner);
}
/** Den geplanten Trenner-Durchgang sofort ausführen (nur wenn einer aussteht). */
function spuelTagestrenner(): void {
  if (!dayDividerScheduled) return;
  dayDividerScheduled = false;
  refreshDayDividers();
}
// pre.3603: nur ändern, was sich ändert — Begründung und Messung in lib/tagestrenner.ts.
function refreshDayDividers(): void {
  ordneTagestrenner(refs.chat, { tag: chatDayKey, beschriftung: chatDayLabel });
}

/**
 * 2026-07-29 (Martin: "Vielleicht kann man, damit man das besser vergleichen
 * kann bezueglich der Qualitaet, ganz zart zu den Ausgaben dazuschreiben,
 * welches Modell tatsaechlich verwendet wurde. Vielleicht neben der Uhrzeit"):
 *
 * Seit gestern steht das Modell im Server-Protokoll — aber wer die QUALITAET
 * vergleichen will, muss es an der Antwort selbst sehen, nicht in einer
 * Logdatei. Gerade weil die Kaskade je nach Frage ein anderes Modell waehlt,
 * ist die Zuordnung sonst Ratearbeit.
 *
 * Bewusst nur der Kurzname (Opus, Sonnet, Haiku, Fable) und in derselben
 * gedaempften Zeile wie die Uhrzeit — es soll lesbar sein, wenn man hinschaut,
 * und verschwinden, wenn man es nicht sucht.
 */
function modellKurzname(voll: string | null | undefined): string {
  const m = (voll ?? "").toLowerCase();
  if (!m) return "";
  if (m.includes("opus")) return "Opus";
  if (m.includes("sonnet")) return "Sonnet";
  if (m === "oebb" || m.includes("oebb")) return "ÖBB";
  if (m.includes("haiku")) return "Haiku";
  if (m.includes("fable")) return "Fable";
  if (m.includes("gemini")) return m.includes("pro") ? "Gemini Pro" : "Gemini";
  if (m.includes("gpt")) return "GPT";
  return "";
}

/** Haengt den Modell-Vermerk an eine bestehende Zeitmarke (idempotent). */
function setzeModellVermerk(
  bubble: HTMLElement,
  modell: string | null | undefined,
): void {
  const kurz = modellKurzname(modell);
  if (!kurz) return;
  const ts = bubble.querySelector<HTMLElement>(".bubble-ts");
  if (!ts) return;
  let mark = ts.querySelector<HTMLElement>(".bubble-modell");
  if (!mark) {
    mark = document.createElement("span");
    mark.className = "bubble-modell";
    ts.appendChild(mark);
  }
  mark.textContent = ` · ${kurz}`;
  mark.title = `Antwort erzeugt mit ${modell}`;
}

function makeTimestampLabel(ts: number): HTMLElement {
  const el = document.createElement("time");
  el.className = "bubble-ts";
  const d = new Date(ts);
  el.dateTime = d.toISOString();
  el.textContent = formatBubbleTime(d);
  el.title = d.toLocaleString("de-AT");
  return el;
}

// Ticket #168 (Live-Bug Martin): die Uhrzeit erschien erst NACH einem Reload.
// Wurzel: insertOrdered hängt zwar bei JEDER Bubble die .bubble-ts an, aber die
// Live-Streaming-Pfade (fillBubbleWithText / fillBotBubble) überschreiben den
// Bubble-Inhalt via textContent= bzw. removeChild-Schleife — und löschen das
// Zeitlabel dabei wieder. Der Reload-Pfad (appendBubble) füllt VOR insertOrdered,
// dort überlebt es. Dieser Helfer stellt das Label aus dem schon gesetzten
// dataset.ts wieder her (gleiche Klasse, gleicher Helfer → kein Doppel-Code).
function restoreBubbleTs(bubble: HTMLElement): void {
  if (bubble.querySelector(":scope > .bubble-ts")) return;
  const raw = bubble.dataset.ts;
  if (!raw) return;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return;
  bubble.appendChild(makeTimestampLabel(ts));
}

// Pre.965: REINER Reply-Text einer Bot-Bubble — OHNE Zeitstempel (.bubble-ts),
// Avatar-Alt, Speaker-Label oder Play-Button-Text. Die Doppelanzeige-Dedup in
// onBotMessage verglich vorher `prev.textContent`; seit pre.958 hängt dort ein
// <time>06:51</time> dran → der Vergleich (Identität/Präfix) brach und Stub+Voll-
// Bubbles blieben beide stehen. `.bubble-content` trägt den vollständigen
// gerenderten Reply; dataset.text ist der saubere Fallback (#120-Doktrin).
function bubbleReplyText(el: HTMLElement): string {
  return (
    el.querySelector<HTMLElement>(":scope > .bubble-content")?.textContent ??
    el.dataset.text ??
    ""
  );
}

function findePassendeBotBlase(text: string): HTMLElement | null {
  const now = Date.now();
  const recent = Array.from(
    refs.chat.querySelectorAll<HTMLElement>(".bubble.bot"),
  )
    .slice(-8)
    .map((el) => ({
      el,
      reply: bubbleReplyText(el) || el.dataset.text || "",
      ts: Number(el.dataset.ts ?? now),
    }));
  const hit = passendeBotBlase(text, recent, now);
  return hit?.el ?? null;
}


function formatBubbleTime(d: Date): string {
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString("de-AT", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (sameDay) return time;
  if (isYesterday) return `gestern ${time}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  const date = d.toLocaleDateString("de-AT", {
    day: "2-digit",
    month: "short",
    ...(sameYear ? {} : { year: "2-digit" }),
  });
  return `${date} · ${time}`;
}

// pre.851: Selbstverbesserungs-System-Meldungen erkennen (für die lesbare
// Notiz-Karte). Trennzeichen nach dem Titel wird mit verschluckt.
const SELF_NOTICE_RE =
  /^(Selbstverbesserung fertig[^—:]*|Selbstverbesserung fehlgeschlagen|Rückfrage der Selbstverbesserung)\s*[—:]\s*/;

// pre.873 (Martins Wunsch „kraftvoller / eigene Cards"): ALLE System-Meldungen
// rund um Selbstverbesserung / Serien-Lauf / Freischalten als lesbare Karte
// rendern (statt blasser, zentrierter Mini-Zeile). Deckt auch die Serien-Lauf-
// Status- und Freischalt-Meldungen ab, die kein „Titel — Körper" haben.
const SYSTEM_NOTICE_RE =
  /^(Selbstverbesserung |Rückfrage der Selbstverbesserung|Serien-Lauf|Nehme Ticket|Ticket #\d+|Freischalten von Ticket)/;

// 2026-07-01 (Martin, mehrfach): Hintergrund-Task-Meldungen als lesbare Karte
// (nicht blasses Grau-in-Grau). Format: "Task <status> · <Titel> — <Datum> Uhr <Text>".
const TASK_NOTICE_RE =
  /^Task (fertig|abgebrochen|fehlgeschlagen|gestartet|läuft|angelegt|aktualisiert)/i;
const TASK_PARSE_RE =
  /^(Task [^—]+?)\s*—\s*([0-9.]+,\s*[0-9:]+\s*Uhr)\s*([\s\S]*)$/;

/**
 * 2026-07-29: Kennzeichnet eine Nachricht, die ans MODELL geht, aber nicht
 * als Blase erscheinen soll — weil die Datei-Karte daneben dasselbe schon
 * zeigt. Ein Wortverbinder (U+2060) ist unsichtbar, verändert keinen Umbruch
 * und übersteht Speichern und Abgleich unbeschadet.
 */
const DATEI_MARKER = "\u2060\u2060";

/** 16.10.2026: die Blase des Entwicklers aus dem LETZTEN appendBubble-Aufruf — einmal abholbar (`nimmEntwicklerBlase`), für ihren Vorlese-Stand. */
let frischeEntwicklerBlase: HTMLElement | null = null;
function nimmEntwicklerBlase(): HTMLElement | null {
  const blase = frischeEntwicklerBlase;
  frischeEntwicklerBlase = null;
  return blase;
}

/**
 * 20.10.2026: leiser Bestätigungston nach angenommenem Tipp auf Ja/Nein (lib/frage-knoepfe.ts). Die Karte ruft ihn nur bei echtem Tipp und
 * angenommenem Senden; hier kommen Anruf und offenes Mikro/Aufnahme dazu. Reine Rückmeldung — erzeugt nie ein Ja, ändert den Sendeweg nicht.
 */
function frageQuittung(antwort: FrageAntwort): void {
  // Nur LESEN: die Selbstheilung der Anruf-Flags kann einen hängenden Anruf-Raum hart trennen — das ist nicht Sache eines Rückmelde-Tons.
  const erlaubt = frageTonErlaubt({
    echt: true,
    angekommen: true,
    imAnruf: mode === "voice" && (connected || connecting),
    mikroOffen:
      pttModal !== null || pttState !== "idle" || freihandActive || pttRecorder.isRecording() || mikroSitzungsStatus().tagesaufnahme,
    tonAus: false,
    kontextLaeuft: tonKontextLaeuft(),
  });
  if (erlaubt) playBeep(antwort === "Ja" ? "ja" : "nein");
}

// Ticket 2864: `kanal` — aus welchem Kanal die Nachricht kam (nur „telegram“ trägt einen Vermerk).
function appendBubble(role: "user" | "bot" | "system", text: string, kanal?: string | null, merkmal?: BlasenMerkmal): void {
  frischeEntwicklerBlase = null; // nur die Blase DIESES Aufrufs ist abholbar (16.10.: Vorlese-Stand)
  // 2026-06-29 (Martin): interne Audio-Referenz von Sprachnachrichten NIE
  // als rohen Text zeigen. Zentral hier, weil User-Bubbles via
  // node.append(text) gerendert werden (kein Markdown).
  text = ohneAudioMarke(text);
  // 2026-07-29 (Martin, zur Datei-Darstellung: "das ist etwas bughaft"):
  // Nachrichten mit dem Datei-Marker gehen vollstaendig ans Modell, werden
  // aber NICHT gezeichnet — die Datei-Karte daneben zeigt Datei, Groesse und
  // Auftrag bereits. Vorher stand alles doppelt da, samt rohem Pfad.
  // Zentral hier, aus demselben Grund wie die Audio-Referenz darueber: So
  // greift es auf JEDEM Weg, auch beim Abgleich zwischen Geraeten.
  if (text.startsWith(DATEI_MARKER)) return;
  // 15.10.2026 (Dreier-Modus): ein Beitrag des Entwicklers — eigene Blase, nie mit Vesperas Stimme, nie als Martin oder Vespera.
  if (role === "system" && merkmal?.entwickler === true) {
    // 15.10.2026 (Martin, 22:42): derselbe Vorlese-Knopf wie an Vesperas Blasen — mit der Stimme des Schmieds.
    // 16.10.2026 (Martin): bittet der Beitrag um Ja/Nein (`frage`), stehen unter ihm zwei Knöpfe — nur für Konto 1. Der Klick ist Martins
    // eigene Nachricht über den gewöhnlichen Sendeweg; der Beitrag selbst bleibt Fremdtext (lib/frage-knoepfe.ts).
    // 16.10.2026 (Zimmermann P1): Knöpfe, Adressat und Vorlese-Stand NUR beim Schmied (`entwicklerId` fehlt). Ein anderer oder unbekannter
    // Absender zeigt nur Text mit seinem Namen — ein Martins Knopf-Ja auf ihn ginge sonst als Antwort an den Schmied (bis P3/P4).
    const istSchmied = merkmal.entwicklerId === undefined;
    const zeigeFrage =
      istSchmied && merkmal.frage === "ja-nein" && entwicklerImChatFuerKonto((window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid);
    // 20.10.2026 (Martin 22:07): auch Zimmermann und Hüter stellen eine Ja/Nein-KARTE mit Kennung vom Server — dieselben Knöpfe, der Tipp geht
    // an die Karten-Route und von dort NUR an den Absender der Karte (der Server kennt ihn; die Seite schickt nur Kennung und Antwort).
    const zusatzKarte = merkmal.karte;
    const zusatzKarteVon =
      !istSchmied && (merkmal.entwicklerId === "zimmermann" || merkmal.entwicklerId === "hueter") && zusatzKarte && entwicklerImChatFuerKonto((window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid)
        ? merkmal.entwicklerId
        : null;
    const anzeige = anzeigeEntwickler(merkmal.entwicklerId);
    const stimme = (): EigeneStimme => entwicklerStimmeJetzt(anzeige);
    const blase = baueEntwicklerBlase(
      text,
      renderMarkdown,
      (t) => makePlayButton(t, stimme),
      // Die Antwort geht an den Schmied (`an`, lib/frage-knoepfe.ts) — nie an Vespera, sonst löste das „Ja“ ihre offene Rückfrage ein.
      // 20.10.2026: trägt die Karte eine Kennung vom Server, geht der Tipp MIT Bezug an die Karten-Route (sendeKarteAntwort); ohne Kennung wie bisher.
      zeigeFrage ? (t) => baueFrageKnoepfe(t, schmiedFrageSender(merkmal.karte), meldeFrageGesperrt, frageQuittung, { kennung: merkmal.karte }) : zusatzKarteVon && zusatzKarte ? (t) => baueFrageKnoepfe(t, zusatzKarteSender(zusatzKarte, zusatzKarteVon), meldeFrageGesperrt, frageQuittung, { kennung: zusatzKarte }) : undefined,
      istSchmied ? "schmied" : merkmal.entwicklerId,
    );
    // 19.10.2026 (Befehlskanal P1): Rückfrage des Zimmermanns — Karte „Zimmermann fragt“ mit Ja/Nein, NUR Anzeige ohne Wirkung, nur Konto 1,
    // nie ein Mikro. Martins Tipp sendet eine gekennzeichnete Chat-Zeile an Zimmermann (lib/rueckfrage-karte.ts).
    if (!istSchmied && merkmal.entwicklerId === "zimmermann" && merkmal.rueckfrage && !merkmal.karte && entwicklerImChatFuerKonto((window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid)) {
      const karte = baueRueckfrageKarte(merkmal.rueckfrage, currentTs(), sendeRueckfrageAntwort, meldeRueckfrageGesperrt, Date.now, { text, fordere: fordereBeleg });
      if (karte) blase.appendChild(karte);
    }
    // 16.10.2026: „Schmied → Martin“ — nur mit belegtem Feld `an` (lib/adressat-vermerk.ts), nur beim Schmied.
    if (istSchmied) setzeAdressatVermerk(blase, adressatVermerkAus("schmied", merkmal));
    // 16.10.2026 (Martin, 20:16): Bilder des Schmieds unter dem Text — nur beim Schmied, nur geprüfte Adressen (lib/schmied-bilder.ts).
    if (istSchmied && merkmal.bilder?.length) haengeSchmiedBilderAn(blase, merkmal.bilder, oeffneSchmiedBilder);
    insertOrdered(blase, currentTs());
    frischeEntwicklerBlase = blase;
    sperreBeantworteteFragen(refs.chat, meldeFrageGesperrt);
    sperreBeantworteteRueckfragen(refs.chat, meldeRueckfrageGesperrt);
    fuehreChatNach();
    if (replayingTs === null) notifyNewMessage();
    return;
  }
  // 2312: persist/SSE darf eine schon gestreamte Antwort nicht noch einmal
  // zeichnen (erste Blase hat den Modell-Vermerk, die zweite nur die Uhrzeit).
  if (role === "bot") {
    const schon = findePassendeBotBlase(text);
    if (schon) return;
  }
  const node = document.createElement("div");
  node.className = `bubble ${role}`;
  // Pre.227: Errors prominent markieren — vorher gingen "Fehler: …"-System-
  // Bubbles in der dezenten grauen Italic-Stil unter, User sah es nicht.
  if (
    role === "system" &&
    /^(Fehler|Sprachnachricht-Fehler|Senden fehlgeschlagen|Mute-Fehler|Mikrofon-Zugriff|Vorlesen fehlgeschlagen|\[)/i.test(
      text,
    )
  ) {
    node.classList.add("is-error");
  }
  // 2026-07-03 (Martin): wichtige, aber NICHT alarmierende Hinweise
  // (Verbindung unterbrochen / Antwort kommt im Hintergrund) als lesbare
  // Warn-Box statt blassem Grau.
  else if (
    role === "system" &&
    /verbindung.*unterbroch|im hintergrund weiter|erscheint hier|hintergrund fertig/i.test(
      text,
    )
  ) {
    node.classList.add("is-warning");
  }
  // Pre.513: Speaker-Label für Transcript-Mode. Im Bubble-Mode per CSS
  // versteckt. Im Transcript-Mode sichtbar als Kapitälchen-Header.
  // System-Bubbles bekommen kein Label (sind eh zentriert/anders gestylt).
  if (role !== "system") {
    const speakerName =
      role === "user"
        ? (currentUser?.name?.split(" ")[0]?.toUpperCase() ?? "ICH")
        : (currentBotName?.toUpperCase() ?? "BÄRLI");
    node.dataset.speaker = speakerName;
    const label = document.createElement("span");
    label.className = "bubble-speaker";
    label.textContent = speakerName;
    node.appendChild(label);
  }
  // Pre.193: data-text als robuster Dedupe-Key für SSE-Echo. textContent
  // wird durch Avatar-/Time-/Button-Subnodes verfälscht — data-text hält
  // nur den reinen Bot-/User-Text.
  node.dataset.text = text.slice(0, 80).trim();
  if (role === "bot") {
    fillBotBubble(node, text);
    // Auto-Play nur für frische Replies, nicht bei History-Replay alter Einträge.
    if (autoPlayEnabled && replayingTs === null) void playTts(text);
  } else if (
    role === "system" &&
    (SYSTEM_NOTICE_RE.test(text) || TASK_NOTICE_RE.test(text))
  ) {
    // pre.851/873 + 2026-07-01: System-/Task-Meldungen als LESBARE Karte
    // statt blasser, zentrierter Mini-Zeile (Martins Befund, mehrfach).
    node.classList.add("is-notice");
    const taskM = TASK_PARSE_RE.exec(text);
    const m = SELF_NOTICE_RE.exec(text);
    if (taskM) {
      // "Task fertig · Titel — Datum Uhr" als Kopf, Rest als Karten-Text.
      const head = document.createElement("div");
      head.className = "bubble-notice-head";
      head.textContent = `${taskM[1].trim()} · ${taskM[2].trim()}`;
      node.appendChild(head);
      const content = document.createElement("div");
      content.className = "bubble-content";
      renderMarkdown(content, taskM[3].trim());
      node.appendChild(content);
    } else if (m) {
      // Bekannter „Titel — Körper"-Aufbau (Selbstverbesserung fertig/…).
      const head = document.createElement("div");
      head.className = "bubble-notice-head";
      head.textContent = (m[1] ?? "Selbstverbesserung").trim();
      node.appendChild(head);
      const content = document.createElement("div");
      content.className = "bubble-content";
      renderMarkdown(content, text.slice(m[0].length).trim());
      node.appendChild(content);
    } else {
      // Serien-Lauf-/Freischalt-/Ticket-Status: ganze Zeile als Karten-Körper.
      const content = document.createElement("div");
      content.className = "bubble-content";
      renderMarkdown(content, text.trim());
      node.appendChild(content);
    }
  } else {
    // Pre.771: append() statt textContent= — sonst löscht das Setzen des Texts
    // das zuvor angehängte .bubble-speaker-Label wieder (war der Grund, warum
    // im Transcript-Mode bei User-Zeilen das „ICH"-Label fehlte).
    node.append(text);
  }
  // 16.10.2026: „Martin → Schmied“ — nur mit belegtem Feld `an` (lib/adressat-vermerk.ts); Vesperas Blasen tragen keins.
  if (role === "user") setzeAdressatVermerk(node, adressatVermerkAus("martin", merkmal));
  // 20.10.2026: Martins Tipp auf Ja/Nein einer Karte — die Karte zeigt den Stand, die Zeile bleibt im DOM (Beleg „beantwortet“ für
  // sperreBeantworteteFragen). Nur die MARKE: versteckt wird sie erst dort, wenn eine Schmied-Karte davor sie als Antwort nimmt
  // (lib/frage-knoepfe.ts, Klasse `bubble--kartenantwort`) — ohne Karte, ohne belegten Empfänger Schmied bleibt sie eine sichtbare Blase.
  if (role === "user" && merkmal?.kartenantwort === true) node.dataset.kartenantwort = "1";
  // 20.10.2026: Martins Tipp auf eine Karte des Schmieds — die Zeile trägt die Kennung (sperrt genau diese Karte); sichtbar bleibt nur die Karte (lib/frage-knoepfe.ts).
  if (role === "user" && merkmal?.karteAntwort) setzeKarteVermerk(node, merkmal.karteAntwort, text);
  insertOrdered(node, currentTs());
  setzeKanalVermerk(node, kanal);
  // 16.10.2026: eine Nachricht von Martin beantwortet jede offene Ja/Nein-Frage des Entwicklers davor (lib/frage-knoepfe.ts).
  if (role === "user") sperreBeantworteteFragen(refs.chat, meldeFrageGesperrt);
  if (role === "user") sperreBeantworteteRueckfragen(refs.chat, meldeRueckfrageGesperrt);
  // 12.10.2026: „setzt die vorige Nachricht fort“ / „abgebrochen“ (nur mit ausdrücklichem Merkmal).
  setzeFortsetzungsVermerk(node, merkmal);
  // Auto-Scroll nur wenn User schon am Ende war (Telegram-Pattern). Sonst
  // bleibt seine Lese-Position erhalten und der Scroll-Button zeigt
  // "neue Nachricht" an.
  // Ticket 2730: Seine EIGENE Nachricht (getippt, ohne Wiedergabe aus dem
  // Verlauf) stellt immer ans Ende — er hat eben gesendet und erwartet die
  // Antwort dort.
  if (role === "user" && replayingTs === null) scrollChatToBottomReliably();
  else fuehreChatNach();
  // Pre.153: notifyNewMessage darf NICHT während History-Replay feuern,
  // sonst zählt der Cross-Device-Sync alte Server-Messages als "neu" hoch
  // (Live-Bug Martin: "10 zum runterfahren" beim Reload).
  if (role !== "system" && replayingTs === null) notifyNewMessage();
}

// Bot-Bubble bekommt: Markdown-gerenderten Text + Play-Button für TTS-Vorlesen.
// Pre.156: Aktiver Bot-Avatar — geladen über fetchPersona, gerendert
// links neben jeder Bot-Bubble + im Topbar. customAvatarUrl hat Vorrang
// vor dem Profil-Avatar.
let botAvatarUrl: string | null = null;
// Pre.513: Bot-Name für Transcript-Mode Speaker-Label. Wird in loadBotAvatar
// aus persona.botName gefüllt, fällt sonst auf "BÄRLI" zurück.
let currentBotName: string | null = null;

async function loadBotAvatar(): Promise<void> {
  try {
    const { fetchPersona, avatarUrlAus } = await import(
      "./lib/persona-api.js"
    );
    const cfg = await fetchPersona();
    // Pre.513: Bot-Name übernehmen für Transcript-Speaker-Label.
    // Pre.514: Topbar-Bot-Name aktualisieren — zentrales Display-Element.
    if (cfg.botName && cfg.botName.trim()) {
      currentBotName = cfg.botName.trim();
      const topbarBotName = document.getElementById("topbar-bot-name");
      if (topbarBotName) {
        topbarBotName.textContent = currentBotName.toUpperCase();
      }
    }
    // 2026-07-01 (Martin): Avatar ist von der Persoenlichkeit ENTKOPPELT.
    // Prioritaet: avatarKey=="custom"+Foto > avatarKey-Symbol-Profil >
    // profileKey-Profil (Rueckwaertskompat.). Eine Stelle: avatarUrlAus.
    botAvatarUrl = avatarUrlAus(cfg);
    // Topbar-Slot updaten
    syncBotAvatarTopbar();
    // Bestehende Bot-Bubbles nachträglich mit Avatar ausstatten
    syncBotBubbleAvatars();
  } catch (err) {
    console.warn("[bot-avatar] load failed:", err);
  }
}

function syncBotAvatarTopbar(): void {
  const slot = document.getElementById("topbar-bot-avatar");
  if (!slot) return;
  while (slot.firstChild) slot.removeChild(slot.firstChild);
  if (!botAvatarUrl) {
    (slot).hidden = true;
    return;
  }
  const img = document.createElement("img");
  img.src = botAvatarUrl;
  img.alt = "Aktiver Bot-Avatar";
  img.loading = "lazy";
  slot.appendChild(img);
  (slot).hidden = false;
}

function syncBotBubbleAvatars(): void {
  if (!botAvatarUrl) return;
  for (const bubble of refs.chat.querySelectorAll<HTMLElement>(
    ".bubble.bot:not(.has-avatar)",
  )) {
    prependBotAvatar(bubble);
  }
  // 2026-07-05 (Martin): Avatar-Wechsel gilt fuer den GESAMTEN Verlauf —
  // auch schon gerenderte Bubbles bekommen das neue Bild.
  for (const img of refs.chat.querySelectorAll<HTMLImageElement>(
    ".bubble-avatar",
  )) {
    if (img.src !== botAvatarUrl) img.src = botAvatarUrl;
  }
}

function prependBotAvatar(bubble: HTMLElement): void {
  if (!botAvatarUrl) return;
  if (bubble.classList.contains("has-avatar")) return;
  const av = document.createElement("img");
  av.src = botAvatarUrl;
  // Die Warteblase ist ein Status als Ganzes — ihr Bild sagt nichts dazu (lib/warteblase.ts).
  av.alt = bubble.classList.contains("is-pending") ? "" : "Bot";
  av.loading = "lazy";
  av.className = "bubble-avatar";
  bubble.insertBefore(av, bubble.firstChild);
  bubble.classList.add("has-avatar");
}

/**
 * 2026-07-29 (Martin: "Vielleicht ist es gut, wenn man zu lange Textpassagen
 * einklappt, sodass man beim Scrollen nicht ewig an einem Text festhängt"):
 * Was länger ist als etwa zwei Handy-Bildschirme, bekommt einen Deckel und einen
 * "Weiterlesen"-Knopf; die ZULETZT erzeugte Antwort bleibt offen, die vorige
 * klappt zu.
 *
 * 01.10.2026 (Ticket 2873): Das Zuklappen steht in lib/blase-klappe.ts — in
 * EINEM Durchgang mit gehaltenem Ort (lib/scroll-ort.ts) statt je Blase ein
 * Bild nach dem Einfügen. Warum, steht dort.
 */
const klapper = erzeugeKlapper(refs.chat);

function fillBotBubble(bubble: HTMLElement, text: string, art: FuellArt = "neu"): void {
  // 2026-07-28 (Martin: "schon wieder alles doppelt"): HIER lag die Wurzel.
  // Der Cross-Device-Abgleich erkennt eine schon gezeigte Antwort an
  // dataset.text — genau diese Kennung wurde beim Final-Render der GESTREAMTEN
  // Antwort nie gesetzt (nur an zwei anderen Stellen, Zeile ~9798/~11097).
  // Die live gestreamte Blase war fuer den Abgleich damit unsichtbar: Das
  // Echo derselben Nachricht kam ueber /api/chat/stream herein, fand keine
  // passende Blase und malte sie ein zweites Mal.
  // In der Datenbank stand sie immer nur EINMAL — es war reine Anzeige.
  bubble.dataset.text = text.slice(0, 80).trim();
  // 2026-07-05: Sobald die Bot-Antwort (Zusammenfassung) erscheint, den
  // laufenden Browser-Aktivitaets-Block beruhigen.
  settleBrowserActivity();
  bubble.classList.remove("is-recording");
  // 2026-07-24 (Martin): Schreib-Cursor aus, sobald der Final-Render laeuft.
  bubble.classList.remove("is-streaming");
  while (bubble.firstChild) bubble.removeChild(bubble.firstChild);
  // Pre.162: has-avatar zurücksetzen — sonst skipt prependBotAvatar
  // weil's die Bubble für "schon avatar drin" hält, obwohl wir gerade
  // alle Kinder entfernt haben.
  bubble.classList.remove("has-avatar");
  if (botAvatarUrl) prependBotAvatar(bubble);
  // 2026-07-12 (Martin, "Flut an Informationen"): automatische Meldungen
  // ([MELDUNG] <Titel> in Zeile 1, vom Job-Runner gesetzt) als aufklappbare
  // Karte — Kopf mit Titel + Kernzeile sichtbar, Details erst auf Tipp.
  const meldung = /^\[MELDUNG\]\s*([^\n]+)\n?([\s\S]*)$/.exec(text);
  if (meldung?.[1]) {
    const rest = (meldung[2] ?? "").trim();
    const wrap = buildMeldungCard(meldung[1].trim(), rest);
    wrap.classList.add("bubble-content");
    bubble.appendChild(wrap);
    if (rest.length > 0) {
      bubble.appendChild(makePlayButton(rest));
    }
    restoreBubbleTs(bubble);
    stelleMehrKnopfHer(bubble);
    return;
  }
  const content = document.createElement("div");
  content.className = "bubble-content";
  renderMarkdown(content, text);
  bubble.appendChild(content);
  // 2026-07-25 (Martin: "den Kopierbutton kann man ueberall wieder weg
  // machen"): nur noch Vorlesen. Der Kopier-Knopf sass in jeder Antwort und
  // wurde nie gebraucht — Ausgaben des Systems kopiert Martin nicht.
  if (text.trim().length > 0) {
    const knopf = makePlayButton(text);
    bubble.appendChild(knopf);
    // 19.10.2026: „Weiter ab …“ und, bei langen Antworten, die Vorlese-Karte mit Schieber.
    const hilfe = baueVorleseHilfe(knopf, sprechTextVon(text));
    if (hilfe) bubble.appendChild(hilfe);
    // 21.10.2026: Karte läuft mit, wenn diese Antwort ohne ihren Knopf gesprochen wird (lib/vorlese-mitlauf.ts) — dieselbe
    // Textfrage wie der Tipp auf eine laufende Antwort (makePlayButton, tippAufLaufendeAntwort).
    if (hilfe?.querySelector(".vorlese-karte")) {
      meldeMitlaufKnopf(knopf, () => sprechQuelle(teileAntwort(text)));
      vorleseMitlauf.anstossen();
    }
  }
  // Ticket #168: Zeitlabel nach dem Neu-Befüllen wiederherstellen (die
  // removeChild-Schleife oben hatte es entfernt).
  restoreBubbleTs(bubble);
  // Ticket 2873: Die Klassen eines zugeklappten Blase bleiben stehen, ihr
  // „Weiterlesen" nicht — die Schleife oben hat es mit entfernt.
  stelleMehrKnopfHer(bubble);

  // 2026-07-29: Die VORIGE Antwort einklappen (sie ist jetzt Vergangenheit),
  // diese hier offen lassen — sie wird gerade gelesen.
  // Ticket 2873: Geprüft und zugeklappt wird in EINEM Durchgang mit gehaltenem
  // Ort (lib/blase-klappe.ts), nicht je Blase ein Bild später.
  // Welche Blase offen bleibt und welche zuklappt, entscheidet der Klapper (tests/frontend/blase-klappe-2873.test.ts).
  klapper.gefuellt(bubble, art);
}

// Pre.160: Play-/Stop-/Loading-Icons je nach State. Beim Loading wird das
// SVG-Element selbst rotiert (CSS animation auf <svg> bei [data-state=
// "loading"]). Kein ::before-Pseudo → kein Layout-Shift (Live-Bug
// Martin pre.160: Spinner sprang nach links).
const PLAY_ICON_PATH = "M8 5v14l11-7z";
const PAUSE_ICON_PATH = "M7 5h4v14H7zM13 5h4v14h-4z";
const STOP_ICON_PATH = "M6 6h12v12H6z";
// Spinner-Ring mit Lücke oben — symmetrisch zum Center, rotiert sauber.
const SPINNER_ICON_PATH = "M12 4a8 8 0 1 0 8 8h-2a6 6 0 1 1-6-6V4z";

function setPlayBtnIcon(
  btn: HTMLButtonElement,
  kind: "play" | "stop" | "spinner" | "pause",
): void {
  const path = btn.querySelector("svg path");
  if (!path) return;
  if (kind === "stop") path.setAttribute("d", STOP_ICON_PATH);
  else if (kind === "pause") path.setAttribute("d", PAUSE_ICON_PATH);
  else if (kind === "spinner") path.setAttribute("d", SPINNER_ICON_PATH);
  else path.setAttribute("d", PLAY_ICON_PATH);
}

/**
 * Der Vorlese-Knopf an einer Blase. `stimme`: ohne — Vesperas Vorlese-Stimme; an der Blase „Entwickler“ die des Schmieds,
 * beim Tippen gelesen (15.10.2026, Martin 22:42: „derselbe Abspielknopf“). Bedienung, Zustände und Weg sind dieselben.
 */
function makePlayButton(text: string, stimme?: () => EigeneStimme): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "bubble-play";
  btn.setAttribute("aria-label", "Vorlesen");
  btn.title = "Vorlesen";
  btn.dataset.state = "idle";
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  // 2026-07-19 (Martin): groesser — mit dem Finger sicher treffbar.
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");
  svg.setAttribute("aria-hidden", "true");
  const p = document.createElementNS(ns, "path");
  p.setAttribute("fill", "currentColor");
  p.setAttribute("d", PLAY_ICON_PATH);
  svg.appendChild(p);
  btn.appendChild(svg);

  // Watch state-Änderungen → Icon umschalten + aria-label updaten.
  const updateIcon = (): void => {
    const state = btn.dataset.state ?? "idle";
    if (state === "loading") {
      setPlayBtnIcon(btn, "spinner");
      btn.setAttribute("aria-label", "Wird geladen — klick zum Abbrechen");
      btn.title = "Wird geladen — klick zum Abbrechen";
    } else if (state === "playing") {
      // 2026-07-19 (Martin): der Knopf PAUSIERT jetzt (Position bleibt);
      // komplett stoppen kann weiterhin die Sprech-Pille unten.
      setPlayBtnIcon(btn, "pause");
      btn.setAttribute("aria-label", "Pausieren");
      btn.title = "Pausieren";
    } else if (state === "paused") {
      setPlayBtnIcon(btn, "play");
      btn.setAttribute("aria-label", "Weiter vorlesen");
      btn.title = "Weiter vorlesen";
    } else if (state === "wartet") {
      // 05.10.2026: Das Vorlesen wartet auf eine Berührung (lib/datei-spieler.ts) — der Knopf zeigt es an der Blase.
      setPlayBtnIcon(btn, "play");
      btn.setAttribute("aria-label", "Tippen zum Weiterspielen");
      btn.title = "Tippen zum Weiterspielen";
    } else {
      setPlayBtnIcon(btn, "play");
      btn.setAttribute("aria-label", "Vorlesen");
      btn.title = "Vorlesen";
    }
  };
  // MutationObserver auf data-state. So müssen wir nicht alle State-Setter
  // aufrufen — playTts setzt einfach dataset.state und Icon updated.
  const obs = new MutationObserver(updateIcon);
  obs.observe(btn, { attributes: true, attributeFilter: ["data-state"] });

  // 19.10.2026: Schieber der Vorlese-Karte und „Von vorn“ (lib/vorlese-karte.ts) — dieselbe Wiedergabe, nur ab anderer Stelle.
  btn.addEventListener("voice-v8:vorlese-ab", (ev) => {
    const ab = (ev as CustomEvent<{ versatz?: number }>).detail?.versatz;
    if (typeof ab !== "number") return;
    btn.dataset.state = "loading";
    void playTts(text, btn, stimme?.(), ab);
  });
  // 19.10.2026: Mini-Player in der Fußzeile (lib/ton-leiste.ts) — „Stopp“ beendet die Wiedergabe ganz, die Stelle bleibt gemerkt.
  // Er ist derselbe Stopp wie der der Pille: `stopCurrentTts` verwirft auch ein wartendes Mikro nach einer Entwickler-Frage
  // (schmied-warten.ts, verwirfMikroNachVorlesen). Er öffnet das Mikro aber nie selbst — es gibt kein Auto-Mikro nach dem Stopp.
  btn.addEventListener(EREIGNIS_STOPP, () => {
    stopCurrentTts("minileiste-stopp");
    btn.dataset.state = "idle";
  });
  // Die EINE Quelle von Zustand und Stelle für Karte und Mini-Player (lib/vorlese-quelle.ts); der Sprechtext erst bei Bedarf.
  holeQuelle(btn, () => sprechTextVon(text), stimme ? "Entwickler" : "Antwort");
  btn.addEventListener("click", () => {
    // 05.10.2026: Die Berührung, die eben ein wartendes Vorlesen fortgesetzt hat, ist verbraucht — derselbe Klick
    // soll es nicht gleich pausieren oder von vorn lesen (lib/datei-spieler.ts, `fortsetzBeruehrungGerade`).
    if (fortsetzBeruehrungGerade()) return;
    const state = btn.dataset.state ?? "idle";
    if (state === "loading") {
      // Noch am Laden: Abbruch wie bisher.
      stopCurrentTts("knopf-laedt");
      btn.dataset.state = "idle";
      return;
    }
    if (state === "playing") {
      // 2026-07-19 (Martin): PAUSE statt Stopp — Position bleibt stehen.
      currentTtsQueue?.pause();
      btn.dataset.state = "paused";
      setVoicePrep("paused");
      return;
    }
    if (state === "paused") {
      // AUDIT-Fund 3 (20.07.): isSilentIdle() kennt die playQueue nicht —
      // eine ZWISCHEN zwei Saetzen pausierte Queue sah damit wie gestoppt
      // aus und "Weiter" startete von vorn (exakt die Regression, die
      // pre.1730 abschaffen sollte). isPaused() ist die ehrliche Frage.
      if (currentTtsQueue?.isPaused()) {
        currentTtsQueue.resume();
        btn.dataset.state = "playing";
        setVoicePrep("playing");
      } else {
        // Queue wurde inzwischen wirklich gestoppt (Pille) -> von vorn.
        btn.dataset.state = "loading";
        void playTts(text, btn, stimme?.());
      }
      return;
    }
    /*
     * Ticket 2919 (02.10.2026): Spricht die Sprechtasten-Antwort GERADE diesen Text, ist ein Tipp auf den Knopf
     * Pause/Weiter — nicht „von vorn“. Der Knopf steht während einer Sprechtasten-Antwort auf „idle“ (nur der eigene
     * Strom setzt seinen Zustand); der Tipp räumte sie ab und las den ersten Satz noch einmal (Journal 11:48,
     * gleiche-wiedergabe.ts). Ist die Antwort schon gesprochen, bleibt es beim Vorlesen von vorn.
     */
    if (
      state === "idle" &&
      !stimme &&
      sprechtastenQueue &&
      tippAufLaufendeAntwort(sprechtastenQueue, sprechQuelle(teileAntwort(text)), (z) => setVoicePrep(z), meldeDiagnose)
    ) {
      return;
    }
    // Journal: ein Tipp, der eine laufende Sprechtasten-Antwort abräumt und von vorn liest (der Fall vom 02.10. 11:48).
    if (sprechtastenQueue) meldeDiagnose(`[sprech-d] knopf-tipp: liest von vorn, die Sprechtasten-Antwort wird abgeräumt`);
    btn.dataset.state = "loading";
    void playTts(text, btn, stimme?.());
  });
  return btn;
}

// Pre.172: Dedupe gegen Doppel-Render derselben Tool-Card. Während der
// Tab gerade live ein Bild generiert, kommt der gleiche Tool-Result NOCH-
// MAL via SSE-Bus (eigener Echo) ODER beim nächsten Reload via /api/chat/
// history. Beide Pfade rufen renderToolCard mit derselben callId.
const renderedToolCallIds = new Set<string>();

// Pre.763: Saubere Rechnungs-PDF-Karte (Titel + „PDF öffnen"/„Herunterladen")
// statt roher Download-URL im Text. Nutzt das bestehende .btn-System.

// istPdfAdresse wohnt seit 28.09.2026 in lib/pdf-schau.ts — die Ansicht
// fragt selbst, ob sie Seiten zeichnen kann (Ticket 2396).

/**
 * Der „Oeffnen"-Knopf einer PDF-Karte.
 *
 * Bei einem PDF oeffnet er die Ansicht IN der App; sonst bleibt es beim
 * alten Weg (neuer Tab). Das `href` bleibt in beiden Faellen stehen — wer
 * mit der mittleren Maustaste oder „In neuem Tab oeffnen" arbeitet, soll
 * das weiter koennen.
 */
export function pdfKnopf(
  url: string,
  titel: string,
  beschriftung: string,
): HTMLAnchorElement {
  const a = document.createElement("a");
  a.className = "btn btn--primary btn--sm";
  a.textContent = beschriftung;
  a.href = url;
  a.rel = "noopener noreferrer";
  if (istPdfAdresse(url)) {
    a.addEventListener("click", (ev) => {
      // Nur der einfache Klick — Strg/Cmd/Mittelklick bleiben der Weg
      // nach draussen.
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || (ev as MouseEvent).button > 0)
        return;
      ev.preventDefault();
      oeffnePdf(url, titel);
    });
  } else {
    a.target = "_blank";
  }
  return a;
}

function appendPdfCard(label: string, url: string): void {
  const bubble = document.createElement("div");
  bubble.className = "bubble bot pdf-card chat-karte";
  const title = document.createElement("div");
  title.className = "pdf-card-title";
  title.textContent = `Rechnung ${label} — PDF`;
  const actions = document.createElement("div");
  actions.className = "pdf-card-actions";
  const open = pdfKnopf(url, `Rechnung ${label}`, "PDF öffnen");
  actions.appendChild(open);
  /*
   * ⚠️ Herunterladen nur im Browser — in der nativen Huelle reicht iOS die
   *    Datei an die Systemansicht weiter und schiebt die App weg
   *    (Ticket 2396). Ansehen geht drinnen, Speichern nicht.
   */
  if (!isNativePlatform()) {
    const dl = document.createElement("a");
    dl.className = "btn btn--ghost btn--sm";
    dl.textContent = "Herunterladen";
    dl.href = url;
    dl.setAttribute("download", "");
    actions.appendChild(dl);
  }
  bubble.append(title, actions);
  insertOrdered(bubble, Date.now());
  fuehreChatNach();
}

// 2026-07-06 (Martin): make_pdf-Ergebnis als KLICKBARES Vorschaubild (Seite 1)
// statt roher Link-Karte — "ein fertiges Ergebnis mit einem Screenshot, auf
// den ich klicken kann". Tippen aufs Bild oder "Oeffnen" -> PDF im neuen Tab.
function appendPdfPreviewCard(
  title: string,
  url: string,
  previewUrl: string | null,
): void {
  const bubble = document.createElement("div");
  bubble.className = "bubble bot pdf-preview-card";
  if (previewUrl) {
    const thumbLink = document.createElement("a");
    thumbLink.className = "pdf-preview-thumb";
    thumbLink.href = url;
    thumbLink.rel = "noopener";
    /*
     * ⚠️ Das Bild ist selbst ein Link — und fuehrte damit an derselben
     *    Stelle aus der App wie die Knoepfe darunter. Ein halber Fix waere
     *    hier besonders heimtueckisch: der Knopf bliebe drinnen, das
     *    groessere Ziel darueber nicht.
     */
    if (istPdfAdresse(url)) {
      thumbLink.addEventListener("click", (ev) => {
        ev.preventDefault();
        oeffnePdf(url, title);
      });
    } else {
      thumbLink.target = "_blank";
    }
    const img = document.createElement("img");
    img.src = previewUrl;
    img.alt = title;
    img.loading = "lazy";
    thumbLink.appendChild(img);
    const badge = document.createElement("span");
    badge.className = "pdf-preview-badge";
    badge.textContent = "PDF ansehen";
    thumbLink.appendChild(badge);
    bubble.appendChild(thumbLink);
  }
  const cap = document.createElement("div");
  cap.className = "pdf-preview-title";
  cap.textContent = title;
  const actions = document.createElement("div");
  actions.className = "pdf-card-actions";
  const open = pdfKnopf(url, title, "Öffnen");
  actions.appendChild(open);
  if (!isNativePlatform()) {
    const dl = document.createElement("a");
    dl.className = "btn btn--ghost btn--sm";
    dl.textContent = "Herunterladen";
    dl.href = url;
    dl.setAttribute("download", "");
    actions.appendChild(dl);
  }
  bubble.append(cap, actions);
  // 2026-07-07 (Martin: "kommt 3x"): dieselbe PDF (gleicher Titel) nicht
  // mehrfach — beim Neu-Bau ersetzt die neue Karte die alte.
  refs.chat
    .querySelectorAll<HTMLElement>(".bubble.pdf-preview-card")
    .forEach((el) => {
      if ((el.querySelector(".pdf-preview-title")?.textContent ?? "") === title)
        el.remove();
    });
  insertOrdered(bubble, Date.now());
  fuehreChatNach();
}

// Pre.937 (Martin: „verdammte Uhrzeit"): triviale, rein interne Helfer-Tools
// erzeugen KEINE sichtbare Chat-Card (current_time, Faeden, Projektlisten,
// ticket_update/-get, der Abschied ENDE_WERKZEUG …). Greift live UND beim
// History-Reload.
// ⚠️ 15.10.2026 (Plan „Karten zu 100 % einheitlich“): die Liste steht nicht mehr
// hier, sondern als `karte: "stumm"` je Werkzeug im Register
// (server/werkzeug-register-daten.ts → shared/karten-politik-daten.ts). Die
// bisherigen Einträge sind dort die Erstbelegung (tests/server/karten-politik.test.ts).
const SILENT_TOOL_CARDS = new Set([...werkzeugeMitPolitik("stumm")]);
/*
 * 27.09.2026 (Ticket 2698): ein gelungenes `abspielen` für Spiegel oder
 * Alltag hat keine Karte — der Player selbst ist die Antwort. Das hängt am
 * Ergebnis, nicht am Namen: die Meditation zeigt ihre Karte.
 */
function isSilentToolCard(tool: string, result?: unknown): boolean {
  return SILENT_TOOL_CARDS.has(tool) || abspielenOhneKarte(tool, result);
}

// 2026-07-05 (Martin): Browser-Suche buendeln. Statt pro Klick/Scroll/Lese-
// Schritt eine eigene Karte (Lawine + "Not-Found"-Rauschen) zeigt Vespera EINEN
// lebendigen Aktivitaets-Block mit laufenden Punkten (vorher Pfoten): "Durchsucht
// WhatsApp...". Endet ein anderes Tool oder kommt fuer ~2.5s nichts mehr,
// beruhigt er sich zu "\u2713 WhatsApp durchsucht \u00b7 N Schritte".
// ⚠️ 15.10.2026: welche das sind, sagt das Register — die Schritte der Familie
// „browser“ (die Ketten wie browser_ablauf haben ihre eigene Karte).
const BROWSER_STEP_TOOLS = new Set([...werkzeugeMitPolitik("schritt", "browser")]);
let _activityEl: HTMLElement | null = null;
/** Die Warteblase, die die Aktivität gerade führt (statt eines eigenen Blocks). */
let _activityBlase: HTMLElement | null = null;
let _activityStart = 0;
let _activitySteps = 0;
let _activityTarget = "";
let _activitySettleTimer = 0;

function friendlyBrowserTarget(host: string): string {
  const h = host.replace(/^www\./, "").toLowerCase();
  // 2026-07-24 (Martin): bekannte Seiten mit sauberem Namen — auch fuer
  // mehrteilige Hosts wie raw.githubusercontent.com.
  const bekannt: Array<[RegExp, string]> = [
    [/whatsapp/, "WhatsApp"],
    [/mail\.google|gmail/, "Gmail"],
    [/calendar\.google/, "Kalender"],
    [/youtube|youtu\.be/, "YouTube"],
    [/github/, "GitHub"],
    [/wikipedia/, "Wikipedia"],
    [/reddit/, "Reddit"],
    [/stackoverflow|stackexchange/, "Stack Overflow"],
    [/amazon/, "Amazon"],
    [/linkedin/, "LinkedIn"],
    [/\.maps\.|maps\.google/, "Karten"],
    [/google/, "Google"],
  ];
  for (const [re, name] of bekannt) if (re.test(h)) return name;
  // Sonst: die registrierbare Domain (vorletztes Label) sauber gross.
  const labels = h.split(".");
  const base = labels.length >= 2 ? labels[labels.length - 2] : h;
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : "Web";
}

/** 2026-07-24 (Martin): rohe URL -> saubere Kurzform (Host + kurzer Pfad,
 *  ohne Protokoll/www), z.B. github.com/DietrichGebert/ponytail. */
function prettyUrl(u: string): string {
  try {
    const url = new URL(u);
    const host = url.hostname.replace(/^www\./, "");
    const teile = url.pathname.split("/").filter(Boolean).slice(0, 2);
    const pfad = teile.length ? "/" + teile.join("/") : "";
    return host + pfad;
  } catch {
    return u.replace(/^https?:\/\//, "").replace(/^www\./, "").slice(0, 48);
  }
}

function browserActivityStep(tool: string, result: unknown): void {
  if (
    tool === "browser_open_url" ||
    tool === "browser_get_active_tab" ||
    tool === "tab_context"
  ) {
    const r = parseToolResult(result);
    const url = r && typeof r.url === "string" ? r.url : "";
    if (url) {
      try {
        _activityTarget = friendlyBrowserTarget(new URL(url).hostname);
      } catch {
        /* keine gueltige URL — Ziel bleibt wie es ist */
      }
    }
  }
  // 27.10.2026 (Martin: „komisch, wenn beide gleichzeitig laufen“): steht die Warteblase im Verlauf, führt SIE die Aktivität
  // (Hauptzeile „Durchsucht Amazon… · Schritt 3“, Wartetext klein darunter) — nie zwei Blasen untereinander. Erst ohne Warteblase
  // baut die Aktivität ihren eigenen Block.
  const wartend = [...document.querySelectorAll<HTMLElement>(".vespera-warteblase")].pop() ?? null;
  const geteilt = _activityBlase?.isConnected ? _activityBlase : _activityEl?.dataset.live === "1" ? null : wartend;
  if (geteilt) {
    if (_activitySteps === 0 || _activityBlase !== geteilt) _activityStart = Date.now();
    _activityBlase = geteilt;
  } else if (_activityEl?.dataset.live !== "1") {
    _activityEl = document.createElement("div");
    _activityEl.className = "bubble tool-activity bot";
    _activityEl.dataset.live = "1";
    // Dieselben drei Punkte wie die Warteblase (lib/warteblase.ts) — 21.10.2026 (Martin: „Pfoten durch Punkte“).
    const punkte = baueWartePunkte(document, "warte-punkte tool-activity-punkte");
    const txt = document.createElement("span");
    txt.className = "tool-activity-text";
    _activityEl.appendChild(punkte);
    _activityEl.appendChild(txt);
    insertOrdered(_activityEl, Date.now());
    _activitySteps = 0;
  }
  _activitySteps++;
  const base = _activityTarget ? `Durchsucht ${_activityTarget}` : "Arbeitet im Browser";
  const satz = `${base}\u2026 \u00b7 Schritt ${_activitySteps}`;
  if (_activityBlase) setzeAktivitaet(_activityBlase, satz);
  else {
    const txt = _activityEl?.querySelector(".tool-activity-text");
    if (txt) txt.textContent = satz;
  }
  fuehreChatNach();
  if (_activitySettleTimer) window.clearTimeout(_activitySettleTimer);
  // Notnetz: falls keine Textantwort kommt, nach 20s beruhigen. Primaer
  // schliesst der Block, sobald die Antwort erscheint (fillBotBubble) oder
  // ein anderes Tool laeuft (dispatchToolCard).
  _activitySettleTimer = window.setTimeout(
    () => settleBrowserActivity(),
    20000,
  );
}

function settleBrowserActivity(): void {
  if (_activitySettleTimer) {
    window.clearTimeout(_activitySettleTimer);
    _activitySettleTimer = 0;
  }
  let el = _activityEl;
  if (_activityBlase) {
    // Die geteilte Blase gibt die Hauptzeile zurück; das ruhige Ergebnis steht danach als eigene, flache Zeile an der Stelle des Starts.
    const blase = _activityBlase;
    _activityBlase = null;
    const schritte = _activitySteps;
    setzeAktivitaet(blase, null);
    if (schritte > 0) {
      el = document.createElement("div");
      el.className = "bubble tool-activity bot";
      el.appendChild(baueWartePunkte(document, "warte-punkte tool-activity-punkte"));
      const t = document.createElement("span");
      t.className = "tool-activity-text";
      el.appendChild(t);
      insertOrdered(el, _activityStart || Date.now());
    } else el = null;
    _activitySteps = schritte;
  } else if (el?.dataset.live !== "1") return;
  if (!el) {
    _activityTarget = "";
    _activitySteps = 0;
    return;
  }
  el.dataset.live = "0";
  el.classList.add("is-done");
  const target = _activityTarget || "Web";
  const txt = el.querySelector(".tool-activity-text");
  if (txt)
    txt.textContent = `${target} durchsucht \u00b7 ${_activitySteps} ${
      _activitySteps === 1 ? "Schritt" : "Schritte"
    }`;
  const punkte = el.querySelector(".tool-activity-punkte");
  if (punkte) punkte.textContent = "\u2713";
  _activityEl = null;
  _activityTarget = "";
  _activitySteps = 0;
}

function renderToolCard(card: {
  tool: string;
  callId: string;
  isError: boolean;
  result: unknown;
  anzeige?: unknown;
}): HTMLElement | undefined {
  if (card.callId && renderedToolCallIds.has(card.callId)) {
    return;
  }
  // 22.10.2026 (Martin 16:42): ein Knopf-Ja, das das Ja-Tor nicht einlöste, gibt den Knopf seiner Karte wieder frei — vor jeder Weiche,
  // denn die Absage ist meist eine Schritt-Zeile (karten/knopf-rueckfrage.ts).
  meldeKnopfStand(card.tool, card.result);
  // 2026-07-05: Browser-Mikroschritte bekommen KEINE eigene Karte (Echo/
  // Replay/History) — die Live-Buendelung uebernimmt dispatchToolCard.
  if (BROWSER_STEP_TOOLS.has(card.tool)) {
    if (card.callId) renderedToolCallIds.add(card.callId);
    return;
  }
  if (isSilentToolCard(card.tool, card.result)) {
    if (card.callId) renderedToolCallIds.add(card.callId);
    return;
  }
  /*
   * 06.09.2026 (Martin: „nicht das Technische, sondern nur die Information,
   * was es macht"): Ein Schritt bekommt keine Karte, sondern eine Zeile in
   * der gesammelten Gruppe. Der Verlauf speichert ihn wie bisher — das
   * geschieht in dispatchToolCard NACH diesem Aufruf.
   */
  // 21.09.2026 (Martin, #2418): ein gescheiterter Aufruf ist ein Schritt, keine
  // Karte — „Aufgabe angelegt · Fehler titel-ist-transkript" stand als ganze
  // Karte VOR der gelungenen Aufgabe. Die Gruppe oeffnet sich bei Fehlern und
  // nennt den Grund als Satz; die Karte bekommt nur das Gelungene.
  // 15.10.2026: auch ein Halt des Ja-Tors („wartet auf dein Ja“) ist eine Zeile, keine Karte.
  // Die Zeile liest die Anzeige des Servers, nie das Rohergebnis (lib/schritte.ts).
  const schritt = schrittZeile(card.tool, card.result, card.isError, card.anzeige);
  // 22.10.2026 (Martin 11:37: „es gibt nicht mal den Ja-Punkt, den ich anklicken kann“): eine Rückfrage MIT Ja-Knopf ist keine Zeile —
  // sie wird zur Karte. Vorher fing diese Weiche jedes Warten ab, die Knopf-Karte wurde nie gebaut. Seit 15:08 (Martin: „Da ist kein
  // Button zum Anklicken!“, Shorts) EINE Prüfung für alle Knopf-Rückfragen (karten/knopf-rueckfrage.ts), dieselbe wie in tool-cards.ts.
  const knopfKarte = istKnopfRueckfrage(card.tool, card.result);
  if (!knopfKarte && (istSchritt(card.tool) || schritt.fehler || schritt.wartet)) {
    if (card.callId) renderedToolCallIds.add(card.callId);
    // 21.10.2026: auch eine Absage oder ein Halt des Ja-Tors muss die Liedkarte erreichen — sonst blieb sie nach dem Ja gesperrt.
    if (card.tool === "generate_music") meldeLiedStand(card.result, card.isError);
    haengeSchrittAn(card, replayingTs ?? Date.now());
    // 05.10.2026 (Ticket 2996): eine neue Position ist ein Schritt — die
    // Rechnung, an der sie hängt, ist das Ergebnis. Sie bekommt ihre Karte
    // mit dem Knopf „Rechnung öffnen“, EINE je Rechnung und Zug.
    const rechnung = card.isError ? null : leseRechnungsKarte(card.tool, card.result);
    if (rechnung) {
      haengeRechnungsKarteAn(zugBehaelter(replayingTs ?? Date.now()), buildRechnungCard(rechnung));
      fuehreChatNach();
    }
    return;
  }
  // 2026-07-06 (Martin): make_pdf -> EINE klickbare Vorschau-Karte (Seite-1-
  // Screenshot) statt roher Link-Karte. Markiert die callId als gerendert,
  // damit der generische ok+url-Zweig KEINE zweite Karte legt.
  if (!card.isError && card.tool === "make_pdf") {
    const r = parseToolResult(card.result) as {
      ok?: boolean;
      url?: unknown;
      title?: unknown;
      preview_url?: unknown;
    } | null;
    if (r && r.ok && typeof r.url === "string") {
      if (card.callId) renderedToolCallIds.add(card.callId);
      appendPdfPreviewCard(
        typeof r.title === "string" ? r.title : "PDF",
        r.url,
        typeof r.preview_url === "string" ? r.preview_url : null,
      );
      return;
    }
  }
  // Pre.179: Failed Browser-Extension-Calls erzeugen leere "Tab geöffnet"-
  // Pillen (kein URL, kein Detail) wenn die Extension nicht verbunden ist.
  // Diese Cards bringen dem User nichts — die parallele open_url-Card
  // zeigt den Link sowieso. Komplett unterdrücken.
  if (card.tool.startsWith("browser_")) {
    const r = parseToolResult(card.result);
    const isFailed = card.isError || (r?.ok === false);
    if (isFailed) {
      if (card.callId) renderedToolCallIds.add(card.callId);
      return;
    }
  }
  // Pre.173: Bild-Tools auch via URL deduplizieren — der localStorage-
  // Replay malt Image-Bubbles direkt (ohne callId-Track), und der
  // History-Pull würde sie sonst nochmal als Tool-Card-Image rendern.
  // Pre.174: img.src wird browser-seitig zu absoluter URL aufgelöst
  // (https://host/uploads/...), die Result-URL bleibt aber relativ.
  // Daher iteriere und prüfe substring.
  if (
    !card.isError &&
    (card.tool === "generate_image" || card.tool === "edit_image")
  ) {
    const url = extractImageUrl(card.result);
    if (url) {
      const existingImgs = refs.chat.querySelectorAll(".bubble.image img");
      for (const img of Array.from(existingImgs)) {
        const src = (img as HTMLImageElement).src || "";
        if (src.endsWith(url) || src.includes(url)) {
          if (card.callId) renderedToolCallIds.add(card.callId);
          return;
        }
      }
    }
  }
  // Pre.177: open_url-Dedupe via URL — localStorage-Replay malt URL-Cards
  // direkt (kind:url), History würde sonst eine zweite Card rendern.
  if (!card.isError && card.tool === "open_url") {
    const data = parseUrlResult(card.result);
    if (data?.url) {
      const existing = refs.chat.querySelectorAll(".bubble.url-card a");
      for (const a of Array.from(existing)) {
        if ((a as HTMLAnchorElement).href.includes(data.url)) {
          if (card.callId) renderedToolCallIds.add(card.callId);
          return;
        }
      }
    }
  }
  if (card.callId) renderedToolCallIds.add(card.callId);
  // Pre.763: Rechnungs-PDF als saubere Karte (Buttons) statt roher URL im Text
  // (User: "nur ein verkackter Link"). Tool-Ergebnis enthält "Download: <url>".
  if (!card.isError && card.tool === "faktura_invoice_pdf") {
    const txt =
      typeof card.result === "string"
        ? card.result
        : JSON.stringify(card.result ?? "");
    const url = (txt.match(/https?:\/\/\S+?\.pdf/i) ?? [])[0];
    if (url) {
      const nr =
        (txt.match(/\b(?:RE|GS)-[0-9A-Za-z-]+/) ?? [])[0] ?? "Rechnung";
      appendPdfCard(nr, url);
      return;
    }
  }
  // Image-Tools rendern wir als Image-Bubble (großes Bild) statt als
  // generische Tool-Card — viel schöner UX.
  if (
    !card.isError &&
    (card.tool === "generate_image" || card.tool === "edit_image")
  ) {
    const url = extractImageUrl(card.result);
    if (url) {
      const caption =
        card.tool === "generate_image"
          ? extractField(card.result, "prompt")
          : extractField(card.result, "instruction");
      appendImageBubble(url, "bot", caption, kartenKanal);
      pushHistory({
        kind: "image",
        image: { url, caption: caption ?? null, from: "bot" },
        ...(kartenKanal ? { kanal: kartenKanal } : {}),
        ts: Date.now(),
      });
      return;
    }
  }
  // Music-Tool als Player-Card. Defensive: wenn die letzten beiden Bubbles
  // bereits eine music-bubble mit derselben ID/URL sind, nicht nochmal
  // einhängen (Schutz gegen Server-Doppel-Sends).
  if (!card.isError && card.tool === "play_music") {
    const data = parseMusicResult(card.result);
    if (data) {
      // Ticket 2937: KEIN Auto-Open nach draussen mehr — der Player laeuft in der
      // Karte (lib/musik-karte.ts). Frueher sprang die App hier zusaetzlich auf YouTube.
      // Pre.245: Bei History-Replay ZUERST prüfen ob die Card überhaupt
      // bereits irgendwo im DOM ist (nicht nur letzte Bubble) — dieselbe
      // Music-Card poppt sonst nach jedem Voice-Reconnect am Ende wieder
      // auf (Live-Bug Martin "warum steht wieder Herman van Veen am Ende").
      if (data.videoId) {
        const allMusic = refs.chat.querySelectorAll<HTMLElement>(
          ".bubble[data-music-id]",
        );
        for (const el of Array.from(allMusic)) {
          if (el.dataset.musicId === data.videoId) {
            return;
          }
        }
      }
      // Defensive (alt): direkt-aufeinanderfolgende Server-Doppel-Sends
      const last = refs.chat.lastElementChild as HTMLElement | null;
      const sameSong =
        last?.dataset.musicId &&
        data.videoId &&
        last.dataset.musicId === data.videoId;
      if (sameSong) return;
      // 11.10.2026 (Integrationsprüfung F1): LIVE (Sprechtaste, Anruf) startet das Lied in der Karte von selbst, wie im getippten
      // Chat (unten: onToolResult mit `autoplay: true`) — früher öffnete dieser Weg YouTube, seit Ticket 2937 nicht mehr, und die
      // Karte blieb still, obwohl Vespera „ein Lied startet jetzt“ sagt. Der Verlauf (`replayingTs`) bleibt stumm (Geistermusik).
      // Bewusst OHNE Warten auf Vesperas Satz (Ticket 2698): der Player ist ein eingebetteter YouTube-Spieler ohne Startknopf von
      // außen — spätes Starten hieße die Karte neu zu laden; vor Ticket 2937 lief die Musik auf diesem Weg ebenso sofort.
      appendMusicBubble(data, { autoplay: replayingTs === null });
      // Pre.245: bei Replay original-ts behalten damit Card an Original-
      // Position landet statt am Chat-Ende.
      pushHistory({
        kind: "music",
        music: data,
        ts: replayingTs ?? Date.now(),
      });
      return;
    }
  }
  // open_url als URL-Card. Tab-Öffnen via window.open() NUR bei live-
  // Calls, NICHT beim History-Replay. Pre.124 Live-Bug Martin: bei
  // jedem PWA-Reload öffneten sich alle früheren open_url-Tabs erneut
  // (Instagram, ORF.at, …) und kumulierten sich.
  if (!card.isError && card.tool === "open_url") {
    const data = parseUrlResult(card.result);
    if (data?.ok && data.url) {
      const isReplay = replayingTs !== null;
      // 2026-06-30 (Martin): Card ZUERST rendern, Tab-Öffnen erst danach und
      // best-effort. In der nativen WKWebView kann window.open() werfen oder
      // leer sein — vorher verschluckte das die Card komplett ("es kommt
      // keine Karte"). Jetzt erscheint die klickbare Karte IMMER.
      appendUrlBubble(data.url, data.title ?? null);
      if (!isReplay) {
        try {
          window.open(data.url, "_blank", "noopener,noreferrer");
        } catch {
          /* WKWebView ohne Tab-Support — Card-Klick genügt */
        }
        pushHistory({
          kind: "url",
          urlOpen: { url: data.url, title: data.title ?? null },
          ts: Date.now(),
        });
      }
      return;
    }
  }
  // Pre.450 Ticket #13: phone_play als grosse Tap-to-Play-Card.
  // Tool returnt {phone_open, label, provider, kind}. Da iOS keinen
  // autoplay erlaubt, MUSS der User selbst auf den roten Button tippen
  // damit YouTube Music / Spotify / Apple Music sich auf dem Handy öffnet.
  if (!card.isError && card.tool === "phone_play") {
    const result = parseToolResult(card.result);
    const url = typeof result?.phone_open === "string" ? result.phone_open : "";
    if (url) {
      const label =
        (typeof result?.label === "string" ? result.label : "") ||
        (typeof result?.query === "string" ? result.query : "Abspielen");
      const provider =
        typeof result?.provider === "string" ? result.provider : "youtube";
      appendPhonePlayBubble(url, label, provider);
      if (replayingTs === null) {
        pushHistory({
          kind: "tool",
          card: {
            tool: "phone_play",
            callId: card.callId,
            isError: card.isError,
            result: card.result,
          },
          ts: Date.now(),
        });
      }
      return;
    }
  }
  // 2026-07-07 (Ticket 283): Render-Fehler EINER Karte darf keinen falschen
  // "Sprachnachricht-Fehler" ausloesen — Karte weglassen, Turn laeuft weiter.
  let node: HTMLElement;
  try {
    node = buildToolCard(card);
  } catch (e) {
    console.error("[tool-card] render failed", card.tool, e);
    if (card.callId) renderedToolCallIds.add(card.callId);
    return;
  }
  // 2026-07-05 (Martin): Erledigt-Knopf gehoert IN die Erinnerungs-Bubble
  // (wo Titel+Text stehen), nicht als separate Karte darunter. Die Bubble
  // wird unmittelbar vor ihrer toolCard gerendert (Live + Replay) -> die
  // letzte .bubble.bot ist die zugehoerige Nachricht.
  if (card.tool === "reminder_done" && !card.isError) {
    // Ticket 573/572 (2026-07-25, Martin: "ein Wecker soll klingeln wie der
    // Timer"): Traegt die Karte ein Signal, wird es JETZT hoerbar. Nur LIVE —
    // beim Nachladen des Verlaufs (replayingTs gesetzt) darf ein alter Wecker
    // nicht erneut laeuten.
    if (replayingTs == null) {
      const erg = (card.result ?? {}) as {
        signal?: unknown;
        signalTitle?: unknown;
      };
      const sig = typeof erg.signal === "string" ? erg.signal : "";
      const titel =
        typeof erg.signalTitle === "string" && erg.signalTitle.trim()
          ? erg.signalTitle.trim()
          : "Erinnerung";
      if (sig && sig !== "still") {
        void spieleAlarmSignal(sig, titel);
      }
    }
    const bots = refs.chat.querySelectorAll<HTMLElement>(".bubble.bot");
    const target = bots.length > 0 ? bots[bots.length - 1] : null;
    if (target && !target.querySelector(".reminder-done-btn")) {
      node.classList.add("reminder-done--inline");
      /*
       * 2026-08-31 (Martin, mit Bild: "hier kann wieder eine Karte rein, die
       * was ja verloren wirkt ... auch das gehört zentralisiert und
       * vereinheitlicht").
       *
       * Trägt die Bubble eine MELDUNG (buildMeldungCard, oben im selben
       * bubble.appendChild(wrap) verdrahtet), landete die Erledigt-Zeile
       * bisher als GESCHWISTER von `.job-meldung` — beide lose in der Bubble,
       * nur eine Haarlinie dazwischen. `.job-meldung` selbst traegt in der
       * Bubble bewusst KEINEN Rahmen (Kommentar von 2026-07-12: "im Chat war
       * nur die Body-Trennlinie") — das war richtig fuer eine Meldung ALLEIN,
       * aber genau das liess die angehaengte Erledigt-Zeile wie einen
       * eigenen, verlorenen Fund aussehen.
       *
       * Jetzt: liegt eine Meldung vor, wird die Erledigt-Zeile HINEIN gehaengt
       * — ein gemeinsamer Rahmen fuer beide (siehe .job-meldung:has(>
       * .tool-reminder-done) im Blatt), keine Trennung mehr.
       */
      const meldung = target.querySelector<HTMLElement>(".job-meldung");
      const einhaengeziel = meldung ?? target;
      // 2026-07-25 (Martin: "dieses Abspielbutton weg", Screenshot mit dem
      // seltsam eingefaerbten Knopf): Der Vorlese-Knopf sitzt fest unten
      // rechts in der Blase — genau dort, wo jetzt die ERLEDIGT-Pille steht.
      // Beides uebereinander sah aus wie ein Fehler. Bei einer Erinnerung
      // braucht es ihn auch nicht, sie wurde gerade vorgelesen.
      target.querySelector(".bubble-play")?.remove();
      einhaengeziel.appendChild(node);
      fuehreChatNach();
      return;
    }
  }
  // Pre.245: bei History-Replay (replayingTs gesetzt) Original-Zeitstempel
  // verwenden — sonst landet die alte Card am Chat-Ende, nicht an ihrer
  // Ursprungs-Position. Fix für stale Tool-Card-Re-Insert (Music, etc.)
  // 23.09.2026 (#2418): die Karte kommt in den Behälter ihres Zuges, gleiche
  // Werkzeuge werden dort gefaltet — eine Frage, ein Element.
  /*
   * 15.10.2026 (Plan „Karten zu 100 % einheitlich“): kein eigener Bau und keine
   * Anzeige (eine alte gespeicherte Karte) — eine leise Zeile in den Schritten
   * („Name · erledigt“), nie ein Dump.
   */
  if (istLeiseKarte(node)) {
    haengeSchrittAn(card, replayingTs ?? Date.now(), leiseZeile(card.tool, card.isError));
    return;
  }
  // Eine Karte aus der Anzeige: EINE je Familie und Zug — weitere Aufrufe hängen ihren Eintrag an (lib/anzeige-karte.ts).
  if (istAnzeigeKarte(node)) {
    const anzeigeZug = zugBehaelter(replayingTs ?? Date.now());
    const traeger = haengeAnzeigeAn(anzeigeZug, node, haengeRechercheKarteAn);
    if (node.dataset.familie === "mail") buendleRecherche(anzeigeZug); // ein weiterer Anhang ändert die Zählung des Blocks
    planeRechercheOrdnung(refs.chat);
    fuehreChatNach();
    return traeger;
  }
  // 05.10.2026 (Ticket 2996): eine Rechnungskarte ersetzt die ältere Karte
  // DERSELBEN Rechnung in diesem Zug, statt sich darunter zu stapeln.
  if (istRechnungsKarte(node)) {
    haengeRechnungsKarteAn(zugBehaelter(replayingTs ?? Date.now()), node);
    fuehreChatNach();
    return node;
  }
  haengeRechercheKarteAn(zugBehaelter(replayingTs ?? Date.now()), node, card.tool);
  planeRechercheOrdnung(refs.chat);
  // 18.10.2026: „starte die Navigation“ — Karte steht, dann best-effort öffnen (nur live, einmal).
  if (card.tool === "route_directions") oeffneRouteWennGewuenscht(card, replayingTs === null);
  // 15.10.2026: Vorversuche desselben Werkzeugs sind überholt — der Kopf der Schritte zählt sie nicht mehr.
  const ueberholt = card.isError ? null : ueberholeSchritte(node, card.tool);
  if (ueberholt) schritteKopfNachziehen(ueberholt);
  fuehreChatNach();
  // Ticket 2660: dispatchToolCard braucht die Karte für die Rückfrage oben.
  return node;
}

/*
 * 06.09.2026 — Schritte sammeln sich in EINER Zeile.
 *
 * Martin: *„mit weniger Cards, eher zusammengeführt, dass man auch die
 * Prozesse nicht in tausend Einzelteile zerlegt, dass das Ganze flüssiger
 * und leichter zum Lesen wird."* Gemessen erzeugte ein einzelner Zug bis zu
 * 32 Karten hintereinander.
 *
 * Die Gruppe ist zugeklappt und nennt nur, was zuletzt getan wurde und wie
 * oft. Wer nachsehen will, klappt sie auf — nichts geht verloren. Welche
 * Werkzeuge Schritt und welche Ergebnis sind, steht in {@link ./lib/schritte.js}.
 */
/**
 * Welcher Zug gerade laeuft (Zeitstempel seines Anfangs), oder null.
 *
 * Ein „Zug" ist eine Frage und alles, was Vespera darauf tut. Die Schritte
 * eines Zuges gehoeren in EINE Gruppe — siehe haengeSchrittAn.
 */
let laufenderZug: number | null = null;

/**
 * Einen Zug eroeffnen; die Rueckgabe schliesst ihn wieder.
 *
 * ⚠️⚠️ 09.09.2026 — WER SCHRITTE ANZEIGT, MUSS EINEN ZUG EROEFFNEN.
 *
 * Am 07.09. bekam die Gruppierung die Kennung des Zuges (siehe
 * haengeSchrittAn). Gesetzt wurde die Kennung aber nur im GETIPPTEN Chat —
 * eine nackte Zuweisung mitten in `sendChatHttpInner`. Der Sprach-Weg und
 * der Anruf-Weg kannten sie nicht, also galt fuer sie weiter die alte
 * Nachbarschaftsregel, und die zerbricht live an der Puls-Blase, die nach
 * JEDEM Werkzeug-Ergebnis neu eingehaengt wird.
 *
 * Martin, mit Bild: *„Hier wurden schon wieder nicht diese Anfragen
 * gruppiert."* Gemessen zu seinem Zug: der Server lieferte EINE Antwort mit
 * fuenf Werkzeugkarten (`[ptt-stream] done total=97088ms tools=5`, eine
 * einzige Nachricht in der Datenbank) — das Frontend machte fuenf Anzeigen
 * daraus. Der Fix vom 07.09. war von Anfang an halb: Er deckte den Chat ab,
 * nicht das Mikrofon, ueber das Martin die meisten Fragen stellt.
 *
 * ⚠️ Die Pruefung beim Schliessen ist kein Zierrat: Ein alter, haengender
 *    Zug darf den gerade laufenden nicht zumachen. Genau dieses Muster
 *    steht im PTT-Weg schon einmal (`seq === pttSendSeq`).
 */
/**
 * Was der laufende Zug bisher gekostet hat — EINE Quelle fuer beide Wege.
 *
 * Ticket 2175 (Martin, 09.09.2026): *„Es wird noch nicht angezeigt, wie lang
 * die Verarbeitungsdauer ist und wie viele Werkzeuge pro Anfrage verwendet
 * werden."* Er will neben dem Modell sehen, worauf er gewartet hat — damit
 * eine langsame Antwort einzuordnen ist, statt nur ertragen zu werden.
 *
 * ⚠️ Gemessen wird bis zum ERSTEN WORT, nicht bis zum Ende. Das ist die
 *    Stille, die er aushaelt — und die einzige Groesse, die auf beiden Wegen
 *    dasselbe bedeutet. Die volle Antwortdauer steht im Sprachkanal nur im
 *    Journal; zwei verschiedene Dinge unter derselben Zahl waeren schlimmer
 *    als keine Zahl. Der Titel sagt es ausdruecklich dazu.
 */
const zugStand = { start: 0, msErstesWort: 0, werkzeuge: 0 };

function eroeffneZug(ts: number): () => void {
  laufenderZug = ts;
  zugStand.start = Date.now();
  zugStand.msErstesWort = 0;
  zugStand.werkzeuge = 0;
  // Ticket 2701: der Abschied gilt nur für seinen Zug — auch wenn dessen
  // Vorlesen abgebrochen wurde und nie ein Ende meldete.
  zugOhneAbschied();
  // Ticket 2698: ebenso ein vorgemerkter Player-Start.
  zugOhneAbspielen();
  // Ticket 2669: ebenso ein vorgemerktes Neuladen — es galt der Antwort davor, nicht diesem Zug.
  zugOhneNeuLaden();
  // Ticket 2790: ebenso die wartende Fragen-Runde — das Zugende meldet sie neu.
  zugOhneRunde();
  // Ticket 3048: ebenso Jevs Meldung über das Mikro.
  zugOhneJevMikro();
  // Ticket 2660: ein neuer Zug ist Martins Reaktion — die Rückfrage oben geht.
  loeseRueckfrage();
  // Ticket 2742: solange ein Zug laeuft, tritt der obere Teil des Chats zurueck (style.css, „Ruhe waehrend eines Zugs“).
  // Nur ein Merker am body — gezeichnet wird in CSS, es gibt keinen Takt und keinen Scroll-Hoerer.
  document.body.dataset.zug = "laeuft";
  return () => {
    // Nur der NEUESTE Zug darf den Merker loesen: ein alter, haengender Zug macht sonst den laufenden zu.
    if (laufenderZug === ts) {
      laufenderZug = null;
      delete document.body.dataset.zug;
    }
  };
}

/**
 * Das erste Wort ist da — ab hier steht die Wartezeit fest.
 *
 * ⚠️ Nur beim ERSTEN Aufruf je Zug. Beide Wege liefern den Text in Stuecken;
 *    ohne diese Sperre wuerde die Zahl mit jedem Stueck weiterlaufen und am
 *    Ende die Gesamtdauer zeigen — also genau das, was sie nicht zeigen soll.
 */
function zugErstesWort(): void {
  if (!zugStand.start || zugStand.msErstesWort) return;
  zugStand.msErstesWort = Date.now() - zugStand.start;
  updateFooterEffectiveModel(lastEffectiveModel ?? "");
}

/** Die Zug-Angabe hinter dem Modellnamen, oder "" wenn nichts zu sagen ist. */
/**
 * Warm oder kalt — der Zustand, bevor man fragt. (09.09.2026)
 *
 * Martin: *„Kann man nicht die Information, ob es kalt oder warm ist, in
 * Real-Time anzeigen lassen, also eine Art Monitoring im System?"*
 *
 * ## Warum nur „kalt" dasteht und „warm" nicht
 *
 * Warm ist der Normalfall und sagt einem nichts, was man tun koennte. Kalt
 * ist die Ausnahme und sagt genau das, was fehlte: **die naechste Antwort
 * dauert laenger, und es liegt nicht an der Frage.** Gemessen am 09.09.:
 * warm 1,5 s, kalt 11,3 s — bei derselben Art Frage, demselben Modell.
 *
 * Ein Etikett, das immer dasteht, wird nicht mehr gelesen; deshalb schweigt
 * die Anzeige, solange alles gut ist. (Dieselbe Ueberlegung wie beim
 * Voice-Etikett, das am 27.08. wegfiel: „ein Etikett, das immer dasselbe
 * sagt, sagt nichts.")
 *
 * ⚠️ Die Chip-Breite ist knapp — genau daran ist heute der Senden-Knopf aus
 *    dem Bild gefallen. Ein Wort statt zweier, und nur im Ausnahmefall.
 */
let waermeKalt = false;
let waermeTitel = "";
/**
 * Ticket 2930: für welches Modell der Server zuletzt Auskunft gab. „kalt“ gilt nur für das Modell, das der Chip zeigt
 * (frontend/lib/waerme-modell.ts) — vorher stand „Sonnet · kalt“ da, wenn die Auskunft einem anderen Modell galt.
 */
let waermeFuer: AboModell | "" = "";

function zugZusatz(): string {
  const kalt = waermeKalt ? " · kalt" : "";
  if (!zugStand.msErstesWort) return kalt;
  const sek = (zugStand.msErstesWort / 1000).toFixed(1).replace(".", ",");
  return ` · ${sek} s${kalt}`;
}

/**
 * Die Werkzeug-Zahl des Zuges — Symbol und Zahl neben dem Modell-Chip.
 *
 * 27.09.2026 (Ticket 2673, Martin: *„Platz sparen, nicht ausschreiben.
 * Statt ‚2 Werkzeuge‘ ein kleines Icon mit der Zahl.“*): Bis hierher stand
 * die Zahl als Wort im Chip selbst. Dort war sie auch das Erste, was die
 * Kürzung (text-overflow) abschnitt, sobald die Zeile eng wurde. Als eigenes
 * Element bleibt sie stehen; das Wort steht weiter da — für Vorleser.
 */
function zeigeWerkzeugZahl(zeigtZug = true): void {
  const el = document.getElementById("status-werkzeuge");
  if (!el) return;
  const w = zugStand.werkzeuge;
  // Nur, wenn es welche gab — sonst ist sie kein Hinweis, sondern Ballast.
  // Und nur, wenn der Chip den Zug zeigt (renderStatusBar, Anruf und „…“).
  el.hidden = !(zeigtZug && zugStand.msErstesWort && w > 0);
  const wort = w === 1 ? "Werkzeug" : "Werkzeuge";
  const zahl = el.querySelector(".composer-werkzeuge-zahl");
  if (zahl) zahl.textContent = String(w);
  const vorgelesen = el.querySelector(".composer-werkzeuge-wort");
  if (vorgelesen) vorgelesen.textContent = ` ${wort}`;
  el.title = `${w} ${wort} im Zug`;
  // 21.10.2026: die Zahl steht als kleiner Zusatz im EINEN Chip (style.css, .composer-modell::after) — nur wenn es welche gab.
  const chip = document.getElementById("status-llm");
  if (chip) {
    if (el.hidden) delete chip.dataset.werkzeuge;
    else chip.dataset.werkzeuge = ` · 🔧${w}`;
    benenneChip(chip);
  }
}

/**
 * Den Waermezustand holen und die Anzeige nachziehen.
 *
 * ⚠️ Diese Abfrage darf NICHTS waermen — sonst misst die Anzeige sich
 *    selbst. Die Route liest nur (server/index.ts, /api/system/waerme).
 */
async function holeWaerme(): Promise<void> {
  try {
    const r = await fetch(waermeAdresse(lastEffectiveModel), { cache: "no-store" });
    if (!r.ok) return;
    const d = (await r.json()) as {
      modell?: string;
      warm?: boolean;
      gelesen?: number;
      deckel?: number;
      anteil?: number;
      weitere?: string[];
    };
    const vorher = waermeKalt;
    waermeFuer = aboModellAus(d.modell);
    waermeKalt = kaltFuerChip(d.warm === false, waermeFuer, lastEffectiveModel);
    const tsd = (n: number): string => Math.round(n / 1000) + "k";
    waermeTitel = d.warm
      ? `Sitzung ist warm — ${tsd(d.gelesen ?? 0)} von ${tsd(d.deckel ?? 0)} Token, danach wird rotiert` +
        ((d.weitere?.length ?? 0) > 0 ? ` · auch warm: ${d.weitere!.join(", ")}` : "")
      : "Sitzung ist kalt — die nächste Antwort baut sie neu auf und dauert länger";
    const el = document.getElementById("status-llm");
    if (el) {
      // Ticket 2782: Antwortet ein Ersatz, sagt der Hinweis, warum und bis
      // wann das Abo ruht (updateFooterEffectiveModel) — nicht, wie warm
      // die Sitzung ist, die gerade gar nicht antwortet.
      if (!el.dataset.fallback) el.title = aboTitel(waermeTitel, stufenGrund);
      // Nur neu schreiben, wenn sich der Zustand wirklich gedreht hat.
      if (vorher !== waermeKalt && lastEffectiveModel)
        updateFooterEffectiveModel(lastEffectiveModel);
    }
  } catch {
    /* Die Anzeige ist Beiwerk — ein Fehler hier darf nichts stoeren. */
  }
}

/**
 * Der Behälter des laufenden Zuges — gefunden oder neu eingeordnet.
 *
 * ⚠️ 07.09.2026 — die Gruppe gehoert zum ZUG, nicht zur Nachbarschaft.
 *
 * Martin, mit Bild: *„warum schon wieder seriell?!"* — viermal „Gemerkt"
 * untereinander, jedes in einer eigenen zugeklappten Gruppe. Beim
 * Neuladen derselben Stelle stand dagegen sauber „Gemerkt · 4 Schritte".
 *
 * Hier wurde verlangt, dass die Gruppe UNMITTELBAR vor der Einfuegestelle
 * steht. Live schiebt sich zwischen zwei Schritte aber immer wieder etwas
 * Fluechtiges: die Fortschritts-Blase eines Werkzeugs, ein Platzhalter,
 * eine Skelett-Kachel. Sobald so etwas dahinter lag, machte der naechste
 * Schritt eine neue Gruppe auf — und die Teilung blieb bis zum Neuladen.
 * Deshalb sah Martin im Chat vier Zeilen und nach dem Neuladen eine.
 *
 * Jetzt traegt jede Gruppe die Kennung ihres Zuges. Ein Schritt findet
 * seine Gruppe daran, egal was inzwischen dazwischenliegt; ein neuer Zug
 * bekommt zwingend eine neue Gruppe (das war der Sinn der alten Regel).
 *
 * ⚠️ 23.09.2026 (#2418): Schritte UND Inhaltskarten suchen ihren Zug hier —
 *    EINE Stelle für die Regel, sonst landen Schritte und Karten derselben
 *    Frage in zwei Behältern.
 */
function zugBehaelter(ts: number): HTMLElement {
  const zug = replayingTs ?? laufenderZug;
  let behaelter = findeZugGruppe(refs.chat, zug, ts);
  if (!behaelter) {
    behaelter = legeZugAn(zug);
    insertOrdered(behaelter, ts);
  }
  return behaelter;
}

function haengeSchrittAn(
  card: { tool: string; result: unknown; isError: boolean; anzeige?: unknown },
  ts: number,
  vorgegeben?: { verb: string; gegenstand: string; fehler: boolean; wartet?: true },
): void {
  const zeile = vorgegeben ?? schrittZeile(card.tool, card.result, card.isError, card.anzeige);
  const behaelter = zugBehaelter(ts);
  let gruppe = behaelter.querySelector<HTMLElement>(":scope > .chat-schritte");
  if (!gruppe) {
    gruppe = document.createElement("details");
    gruppe.className = "chat-schritte";
    const kopf = document.createElement("summary");
    kopf.className = "chat-schritte-kopf";
    gruppe.appendChild(kopf);
    const liste = document.createElement("ol");
    liste.className = "chat-schritte-liste";
    gruppe.appendChild(liste);
    // Die Arbeit steht oben in der Karte, das Ergebnis darunter (#2418).
    behaelter.prepend(gruppe);
  }
  const liste = gruppe.querySelector<HTMLElement>(".chat-schritte-liste");
  if (!liste) return;
  const li = document.createElement("li");
  li.className = "chat-schritt";
  // 15.10.2026: das Werkzeug an der Zeile — ein späterer Erfolg überholt sie (ueberholeSchritte).
  li.dataset.werkzeug = card.tool;
  if (zeile.fehler) {
    li.dataset.fehler = "ja";
    // Ein Fehler darf nicht zugeklappt liegen (68 in 30 Tagen, #2418).
    (gruppe as HTMLDetailsElement).open = true;
  }
  if (zeile.wartet) li.dataset.wartet = "ja";
  const verb = document.createElement("span");
  verb.className = "chat-schritt-verb";
  verb.textContent = zeile.verb;
  li.appendChild(verb);
  if (zeile.gegenstand) {
    const was = document.createElement("span");
    was.className = "chat-schritt-was";
    was.textContent = zeile.gegenstand;
    li.appendChild(was);
  }
  li.dataset.verb = zeile.verb;
  liste.appendChild(li);
  schritteKopfNachziehen(gruppe);
  fuehreChatNach();
}

/** Der Kopf der Schritte: Verben und die Zahl der gescheiterten — nachgezogen bei jeder Zeile und nach jedem Überholen. */
function schritteKopfNachziehen(gruppe: HTMLElement): void {
  const liste = gruppe.querySelector<HTMLElement>(".chat-schritte-liste");
  if (!liste) return;
  const verben = Array.from(liste.children).map(
    (k) => (k as HTMLElement).dataset.verb ?? "",
  );
  const gescheitert = liste.querySelectorAll('[data-fehler="ja"]').length;
  const kopf = gruppe.querySelector<HTMLElement>(".chat-schritte-kopf");
  if (kopf) kopf.textContent = gruppenTitel(verben, gescheitert);
  gruppe.dataset.anzahl = String(verben.length);
}

/**
 * Ticket 573/572 (2026-07-25, Martin): hoerbares Signal zur faelligen Zeit.
 *
 * Zweck ist ein Aussenreiz, der aus dem Muster reisst — fuer Koerper-Routinen
 * (Klimmzuege, Wasser trinken, Augentraining) und fuer Wecker, die nicht
 * stumm verpuffen duerfen.
 *
 * Es wird NICHTS neu gebaut: der kurze Ton, die Glocke (aus dem Anruf) und
 * die Sprachausgabe existieren bereits. Hier werden sie nur kombiniert.
 * Alles fail-quiet — ein stummes Geraet oder ein blockierter Ton darf die
 * Meldung selbst nie verschlucken.
 */
async function spieleAlarmSignal(signal: string, titel: string): Promise<void> {
  try {
    // Der Browser gibt Ton nur nach einer Nutzergeste frei; getAudioCtx()
    // weckt einen schlafenden Kontext, so weit es geht.
    if (signal === "gong") {
      playConnectionTone("connected");
      return;
    }
    if (signal === "laeuten" || signal === "laeuten_stimme") {
      startRingingTone();
      // Kurz laeuten lassen, dann sauber beenden (der Anruf-Ton hat seine
      // eigene 15-s-Notbremse; hier reichen gut vier Sekunden).
      await new Promise((r) => setTimeout(r, 4000));
      stopRingingTone();
      if (signal === "laeuten") return;
    }
    if (signal === "stimme" || signal === "laeuten_stimme") {
      /*
       * 23.09.2026 (Ticket 2551): Ein Wecker ist ein ausdrücklicher
       * Außenreiz wie ein Knopfdruck — er weicht keiner offenen
       * Sprechtasten-Antwort, sondern unterbricht sie wie bisher, und die
       * Sonde nennt ihn beim Namen. Ob er sich stattdessen hinten anstellen
       * soll, entscheidet Martin.
       */
      stopCurrentTts("wecker");
      await playTts(titel);
    }
  } catch (err) {
    console.warn(
      "[alarm] Signal konnte nicht gespielt werden:",
      err instanceof Error ? err.message : err,
    );
  }
}

// Pre.144: Tool-Card-Render + Side-Effect-Dispatch + History-Push gemeinsam.
// Wird sowohl vom VoiceClient.onToolCard-Callback als auch vom Push-to-Talk-
// Pfad genutzt — vorher dupliziert.
const SELF_PERSISTING_TOOLS = new Set([
  "open_url",
  "generate_image",
  "edit_image",
  "play_music",
]);

function parseToolResult(r: unknown): Record<string, unknown> | null {
  if (!r) return null;
  if (typeof r === "string") {
    try {
      const parsed = JSON.parse(r) as unknown;
      return parsed && typeof parsed === "object"
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof r === "object") return r as Record<string, unknown>;
  return null;
}

/**
 * Pre.339: Live-Progress-Bubble für lang laufende Tools (claude_task_server,
 * book_compile, video-Analyse). Eine Bubble pro Tool-Name — bei jedem
 * Update wird sie in-place ersetzt statt eine neue zu spawnen.
 */
const _toolProgressBubbles = new Map<string, HTMLElement>();
const TOOL_PROGRESS_LABELS: Record<string, string> = {
  make_pdf: "PDF wird generiert",
  claude_task_server: "Claude CLI auf server8",
  book_compile: "Buch-Compiler",
  analyze_video: "Video-Analyse",
  narrate_to_mp3: "ElevenLabs-Vertonung",
  expense_analyze_upload: "Beleg-Analyse",
  // Ticket 3074: nur der Ersatz für ein Gerät mit älterem Frontend — die Karte mit den Stufen baut lib/lied-fortschritt.ts.
  generate_music: "Musik wird erzeugt",
};

// Pre.527: Globaler Hook für Tool-Cards die einen User-Text an den Bot
// schicken wollen (z.B. „Buche auf Konto 7110" wenn User auf einen
// Suggestion-Button klickt). Wrapped die existierende send-Logik —
// fügt die Bubble + history-Eintrag genauso ein wie wenn User selbst
// tippt + sendet.
/**
 * 2026-09-04 (Martin mit Bild: „Es steht mir die Option ja oder nein, ich
 * drücke ja und dann fragt er wofür ja?!"):
 *
 *   Der Knopf schickte nur das nackte Wort. Damit war die Antwort darauf
 *   angewiesen, dass die Frage im gespeicherten Verlauf steht — und genau das
 *   war sie nicht: Die Frage kam aus einem Sprach-Zug, der 23 Sekunden lief
 *   und erst am Ende speicherte. Martins Klick war schneller. In der
 *   Datenbank steht sein „Ja" um 10:16:30, die Frage um 10:16:33.
 *
 *   Ein Wettlauf, den er immer gewinnen kann — und der auch dann bleibt,
 *   wenn Frage und Antwort auf verschiedenen Wegen laufen (Stimme fragt,
 *   Chat antwortet).
 *
 *   Darum trägt die Antwort ihre Frage jetzt selbst. `bezug` geht an die KI,
 *   angezeigt bleibt das kurze Wort.
 */
async function sendUserMessageFromCard(
  text: string,
  bezug?: string,
  /** Prüfbefund 27.10.2026 (Kann 3): `false` für Wege, die KEIN Knopf einer Karte sind (Zuhause-Frage) — dann ohne Marke. */
  vonKarte = true,
): Promise<boolean> {
  const trimmed = text.trim();
  if (!trimmed) return false;
  appendBubble("user", trimmed);
  pushHistory({ kind: "user", text: trimmed, ts: Date.now() });
  const frage = (bezug ?? "").trim().slice(0, 200);
  // 07.10.2026: `false` = nicht angekommen (offline, Server lehnt ab) — die Liedkarte
  // merkt ihr Ja nur, wenn es wirklich ankam (frontend/lib/lied-karte.ts).
  return await sendChatHttp({
    message: frage ? `${trimmed} — auf deine Frage: „${frage}"` : trimmed,
    // 27.10.2026 (Mandat): nur dieser Weg trägt die Marke — ein Mandats-Knopf löst nur mit ihr ein (server/prozess-freigabe.ts).
    ...(vonKarte ? { vonKarte: true as const } : {}),
  });
}
(
  window as unknown as {
    __voiceV8SendFromCard?: (text: string, bezug?: string) => Promise<boolean>;
  }
).__voiceV8SendFromCard = sendUserMessageFromCard;

function showOrUpdateToolProgressBubble(p: {
  tool: string;
  phase: "started" | "running" | "done";
  elapsedMs?: number;
  lastLine?: string;
  fortschritt?: unknown;
}): void {
  // Ticket 3074: Musik zeigt ihre Stufen (Text prüfen → Komponieren → Prüfung) statt „läuft seit Ns“.
  if (p.tool === "generate_music" && liedFortschrittEreignis(p, (karte) => insertOrdered(karte, Date.now()))) {
    fuehreChatNach();
    return;
  }
  const label = TOOL_PROGRESS_LABELS[p.tool] ?? p.tool;
  let bubble = _toolProgressBubbles.get(p.tool);
  if (!bubble) {
    bubble = document.createElement("div");
    bubble.className = "bubble system tool-progress";
    insertOrdered(bubble, Date.now());
    _toolProgressBubbles.set(p.tool, bubble);
  }
  // Kinder safely via textContent setzen — keine innerHTML-XSS-Risiken.
  while (bubble.firstChild) bubble.removeChild(bubble.firstChild);
  const spinner = document.createElement("span");
  spinner.className = "tool-progress-spinner";
  spinner.setAttribute("aria-hidden", "true");
  bubble.appendChild(spinner);
  const elapsedSec = Math.round((p.elapsedMs ?? 0) / 1000);
  const textEl = document.createElement("span");
  textEl.className = "tool-progress-text";
  textEl.textContent =
    p.phase === "started"
      ? `${label} gestartet …`
      : `${label} läuft seit ${elapsedSec}s …`;
  bubble.appendChild(textEl);
  if (p.lastLine) {
    const tail = document.createElement("div");
    tail.className = "tool-progress-tail";
    tail.textContent = p.lastLine;
    bubble.appendChild(tail);
  }
  fuehreChatNach();
}

function closeToolProgressBubble(tool: string): void {
  // Ticket 3074: die Stufenkarte der Musik hängt nicht in `_toolProgressBubbles`, sie hat ihren eigenen Besitzer.
  if (tool === "generate_music") schliesseLiedFortschritt();
  const bubble = _toolProgressBubbles.get(tool);
  if (!bubble) return;
  bubble.remove();
  _toolProgressBubbles.delete(tool);
}

type ToolCardPayload = {
  tool: string;
  callId: string;
  isError: boolean;
  result: unknown;
  /** 15.10.2026: die Anzeige des Servers (server/werkzeug-anzeige.ts) — ungeprüft, gelesen über anzeigeVon. */
  anzeige?: unknown;
};

/** Fehlversuch halten, bis der Zug fertig ist — sonst bleibt die luegende Karte. */
let offeneOebbKarte: ToolCardPayload | null = null;

function entferneOebbKarten(): void {
  // 23.09.2026 (#2418): samt Faltung und leerem Behälter — sonst bleibt
  // „Zugverbindung · 2-mal“ als Karte ohne Inhalt stehen.
  entferneWerkzeugKarten(refs.chat, "oebb_journey");
}

function schliesseOffeneOebbKarte(): void {
  const card = offeneOebbKarte;
  if (!card) return;
  offeneOebbKarte = null;
  dispatchToolCard(card, { jetzt: true });
}

/**
 * Eine Rückfrage oben anheften. (Ticket 2660)
 *
 * Die Umgebung für lib/rueckfrage-oben.ts: der Vorlese-Knopf der Blasen
 * (derselbe Weg wie jede Antwort) und der sanfte Ton „rueckfrage".
 */
function heftRueckfrageAn(r: Rueckfrage): void {
  zeigeRueckfrage(r, {
    vorleseKnopf: makePlayButton,
    ton: () => playBeep("rueckfrage"),
  });
}

/**
 * Die offene Rückfrage unter Zeilen, die gerade von außen in den Verlauf
 * kamen, oben anheften. (Ticket 2660, Nachbesserung 28.09.2026)
 *
 * EIN Weg für den Chat-Strom UND den Abgleich mit dem Server
 * (hydrateFromServer). Der Prüfer fand die Lücke: Die Karte hing nur am
 * Strom, der Abgleich — nach der Rückkehr aus dem Hintergrund, nach einem
 * Verbindungsabriss, beim Kaltstart, nach dem Tipp auf die Push-Meldung —
 * brachte die Frage ohne Karte und ohne Ton. Mit gesperrtem Telefon im Auto
 * ist gerade das der wahrscheinliche Weg.
 *
 * @param karten Die dabei neu gezeichneten Werkzeugkarten (callId → Karte);
 *   deren Antwortknöpfe spiegelt die Karte oben.
 */
function heftOffeneRueckfrageAn(
  zeilen: readonly VerlaufsZeile[],
  karten: ReadonlyMap<string, HTMLElement>,
): void {
  // pre.3598: beantwortete Karten ruhig zeichnen, bevor die offene angeheftet wird.
  beantworteteKarten(zeilen, karten);
  const offen = offeneRueckfrage(zeilen);
  if (!offen) return;
  heftRueckfrageAn({
    ...offen,
    antworten: antwortKnoepfe(offen.callId ? karten.get(offen.callId) : undefined),
  });
}

function dispatchToolCard(
  card: ToolCardPayload,
  opts?: { jetzt?: boolean },
): void {
  // Ticket 2175: hier laufen ALLE Werkzeug-Ergebnisse zusammen — Chat,
  // Sprache und Anruf. Eine Stelle, ein Zaehler.
  zugStand.werkzeuge += 1;
  if (card.tool === "oebb_journey" && !opts?.jetzt && !oebbHatZuege(card.result)) {
    offeneOebbKarte = card;
    return;
  }
  if (card.tool === "oebb_journey") {
    offeneOebbKarte = null;
    entferneOebbKarten();
  }
  // 2026-07-05 (Martin): Browser-Suche als EINEN lebendigen Block buendeln —
  // die Mikroschritte weder als Karte noch in die History; ein Fehlschritt
  // ("Not Found") wird still verschluckt.
  if (BROWSER_STEP_TOOLS.has(card.tool)) {
    const r = parseToolResult(card.result);
    const failed = card.isError || (r?.ok === false);
    if (!failed) browserActivityStep(card.tool, card.result);
    if (card.callId) renderedToolCallIds.add(card.callId);
    return;
  }
  // Ein echtes Tool (oder Ergebnis) beendet den laufenden Aktivitaets-Block.
  settleBrowserActivity();
  // Card ZUERST rendern, dann Action — sonst landet die Card in einem
  // versteckten View nach goToView().
  const karte = renderToolCard(card);
  // Ticket 2660: fragt Vespera per ask_confirm, bleibt die Frage oben stehen
  // (mit Ton und Vorlesen). Kommt sie über den Verlauf, heftet sie
  // heftOffeneRueckfrageAn an — mit demselben Schlüssel, also nie doppelt.
  const rueckfrage = karte && rueckfrageAusKarte(card);
  if (rueckfrage) heftRueckfrageAn({ ...rueckfrage, antworten: antwortKnoepfe(karte) });
  // 27.09.2026 (Ticket 2671): hat das Gespraech die Antwort-Tiefe umgestellt,
  // holt der Abgleich den Serverstand, und der Schalter folgt ihm.
  void nachWerkzeug(card.tool, card.isError);
  // 27.09.2026 (Ticket 2701): hat er sich verabschiedet, bleibt das Mikro
  // nach diesem Zug zu — der Hörer am Vorlese-Ende löst es ein.
  merkeAbschied(card.tool, card.isError, card.result);
  // 27.09.2026 (Ticket 2698): „Essenzspiegel vorlesen“ — den fertigen Player
  // vormerken; er startet, wenn Vesperas eigener Satz verklungen ist
  // (abspielenNachStimme an beiden Zugenden). Nur hier, also nur live: der
  // Verlauf startet nichts.
  const abspielen = abspielAuftrag(card.tool, card.isError, card.result);
  if (abspielen) {
    // Ticket 3021: der Zuruf für den Spiegel überlebt ein Neuladen (Blatt öffnet wieder).
    if (abspielen.ziel === "spiegel") merkeSpiegelOffen();
    const abspieler = abspielerFuer(abspielen, card.callId);
    if (abspieler) merkeAbspielen(abspieler);
  }

  // 11.10.2026 (Ticket 2669): „Seite neu laden“ — nur live (hier), nie aus dem Verlauf. Geladen wird erst, wenn
  // Vesperas Bestätigung verklungen ist und nichts mehr läuft (lib/app-neu-laden.ts).
  if (neuLadenAuftrag(card.tool, card.isError, card.result)) merkeNeuLaden(NEU_LADEN_UMGEBUNG);

  if (card.tool === "open_view" && !card.isError) {
    const r = parseToolResult(card.result);
    const view = r && typeof r.view === "string" ? r.view : "";
    // #181: Optionaler Projekt-Bezug — bei view=projects + project direkt ins
    // Detail navigieren statt nur die Übersicht zu öffnen.
    const project = r && typeof r.project === "string" ? r.project.trim() : "";
    if (view === "human-design") {
      // 10.10.2026 (Ticket 3109): die Chart-Ansicht ist ein SHEET, kein Tab — kein goToView/switchToTab
      // (das kennt "human-design" nicht), also auch in keiner der beiden Init-Ketten. Wessen Chart,
      // hat der Server entschieden (userId nur für den Administrator; der Server prüft es beim Laden nochmal).
      const uid = r && typeof r.userId === "number" && Number.isInteger(r.userId) && r.userId > 0 ? r.userId : 0;
      const hdName = r && typeof r.name === "string" ? r.name : "";
      setTimeout(() => {
        void openHumanDesignSheet(uid ? { userId: uid, name: hdName } : undefined);
      }, 0);
    } else if (view) {
      setTimeout(() => {
        goToView(view);
        if (view === "projects" && project) {
          void ensureProjectsView().openProjectByNameOrId(project);
        }
      }, 0);
    }
  } else if (
    !card.isError &&
    (card.tool === "project_set_active" ||
      card.tool === "project_create" ||
      card.tool === "project_delete" ||
      card.tool === "project_update")
  ) {
    // Pre.193 Sprint B: Active-Project-Anzeige live updaten wenn der Bot
    // das aktive Projekt umstellt — sonst hängt der Subheader.
    void getActiveProject()
      .then((p) => renderActiveProjectSlot(p))
      .catch(() => {});
  } else if (card.tool === "contact_call" && !card.isError) {
    const r = parseToolResult(card.result);
    const phone = r && typeof r.phone === "string" ? r.phone : "";
    if (phone) {
      const tel = `tel:${phone.replace(/\s+/g, "")}`;
      window.location.href = tel;
    }
  } else if (card.tool === "open_app" && !card.isError) {
    // 2026-07-04 (Martin): App am iPhone direkt oeffnen — Scheme/Link nativ
    // ans System reichen. dispatchToolCard laeuft nur in Live-Pfaden.
    const r = parseToolResult(card.result);
    const u = r && typeof r.url === "string" ? r.url : "";
    if (u) openExternalUrl(u);
  }

  if (!SELF_PERSISTING_TOOLS.has(card.tool)) {
    pushHistory({ kind: "tool", card, ts: Date.now() });
  }
}

/**
 * Ticket 2698: wie ein Ziel startet — über denselben Weg wie sein eigener
 * Knopf. Ist der Ton gesperrt, bekommen Spiegel und Alltag ein Angebot im
 * Chat; die Meditation hat ihre Karte mit „Anhören“ schon da.
 */
function abspielerFuer(a: AbspielAuftrag, callId: string): Abspieler | null {
  if (KARTE_TRAEGT_PLAYER.has(a.ziel)) return nimmKartenStart(callId);
  const spielen =
    a.ziel === "spiegel"
      ? (): void => openEssenceSheet(a.tag ?? undefined, "werkzeug", { abspielen: true })
      : (): void => void import("./lib/alltag-view.js").then((m) => m.alltagNeuestenAbspielen());
  return {
    spielen,
    anbieten: () => {
      insertOrdered(baueAntippAngebot(a.ziel, spielen), Date.now());
      fuehreChatNach();
    },
  };
}

function extractImageUrl(result: unknown): string | null {
  if (typeof result === "string") {
    try {
      const parsed = JSON.parse(result);
      if (parsed && typeof parsed.url === "string") return parsed.url;
    } catch {
      /* not JSON — ignore */
    }
  }
  if (
    result &&
    typeof result === "object" &&
    "url" in result &&
    typeof (result).url === "string"
  ) {
    return (result as { url: string }).url;
  }
  return null;
}

function extractField(result: unknown, field: string): string | null {
  if (typeof result === "string") {
    try {
      const parsed = JSON.parse(result);
      if (parsed && typeof parsed[field] === "string") return parsed[field];
    } catch {
      /* not JSON */
    }
  }
  if (result && typeof result === "object" && field in result) {
    const v = (result as Record<string, unknown>)[field];
    if (typeof v === "string") return v;
  }
  return null;
}

function renderTruthWarning(warn: {
  reason?: string;
  correction?: string;
}): void {
  const node = document.createElement("div");
  node.className = "truth-warning";
  const head = document.createElement("div");
  head.className = "truth-warning-head";
  head.textContent = "Wahrheits-Wächter: Antwort markiert";
  node.appendChild(head);
  if (warn.reason) {
    const reason = document.createElement("div");
    reason.className = "truth-warning-reason";
    reason.textContent = warn.reason;
    node.appendChild(reason);
  }
  if (warn.correction) {
    const corr = document.createElement("div");
    corr.className = "truth-warning-correction";
    corr.textContent = `Besser: ${warn.correction}`;
    node.appendChild(corr);
  }
  insertOrdered(node, Date.now());
  fuehreChatNach();
}

/**
 * Pre.119: STT-Reject-Hint. Whisper hat Audio erkannt, der Filter hat
 * es aber als (vermeintliche) Halluzination geblockt. Zeigt einen
 * dezenten Hint im Chat — Source-Text + Reason — damit der User sieht
 * warum keine Bot-Antwort kam und korrigieren kann.
 */
function renderSttRejectHint(info: {
  text: string;
  reason: string;
  noSpeech: number | null;
  avgLogprob: number | null;
}): void {
  const node = document.createElement("div");
  node.className = "stt-reject-hint";
  const head = document.createElement("div");
  head.className = "stt-reject-hint-head";
  head.textContent = "Eingabe gefiltert (Halluzinations-Schutz)";
  node.appendChild(head);
  const body = document.createElement("div");
  body.className = "stt-reject-hint-body";
  const reasonLabel: Record<string, string> = {
    "confidence-extreme": "Audio zu undeutlich",
    "multi-lang": "Mehrsprachiger Wortsalat",
    boilerplate: "Sah aus wie Whisper-Boilerplate",
  };
  const reasonText = reasonLabel[info.reason] ?? info.reason;
  body.textContent = `${reasonText}: „${info.text}"`;
  node.appendChild(body);
  // Etappe 1.G: explizites Re-Try-Hint damit User weiß was zu tun ist
  // und nicht nur einen kryptischen Filter-Reason sieht.
  const action = document.createElement("div");
  action.className = "stt-reject-hint-action";
  action.textContent =
    "Ich hab dich nicht ganz verstanden — bitte sprich nochmal kurz und deutlich.";
  node.appendChild(action);
  if (info.noSpeech !== null && info.avgLogprob !== null) {
    const meta = document.createElement("div");
    meta.className = "stt-reject-hint-meta";
    meta.textContent = `noSpeech ${info.noSpeech.toFixed(2)} · avgLog ${info.avgLogprob.toFixed(2)}`;
    node.appendChild(meta);
  }
  insertOrdered(node, Date.now());
  fuehreChatNach();
}

// Pre.298: History-Render in Funktion gezogen damit bootstrapAuth nach
// dem Wipe das user-scoped History-Array re-rendern kann.
// Pre.507: PER-ITEM try/catch. Vorher konnte EIN corruptes Item (z.B.
// eine alte Image-Bubble mit url=null nach Format-Migration) den ganzen
// Render-Loop killen, wodurch alle nachfolgenden Bubbles unsichtbar
// blieben — Symptom auf Martin's iPhone: sah nur die letzten paar
// Messages, weil Loop in der Mitte abgebrochen war.
function renderHistoryToDom(entries: PersistedEntry[]): number {
  let skipped = 0;
  for (const e of entries) {
    try {
      // Pre.103: replayingTs setzen, damit insertOrdered den ORIGINAL-
      // Timestamp aus dem History-Eintrag nutzt statt Date.now() — sonst
      // landet alle History hinten und Reihenfolge kollabiert.
      replayingTs = e.ts ?? Date.now();
      if (e.kind === "user" || e.kind === "bot" || e.kind === "system") {
        appendBubble(e.kind, e.text ?? "", e.kanal, {
          fortsetzung: e.fortsetzung,
          abgebrochen: e.abgebrochen,
          entwickler: e.entwickler,
          frage: e.frage,
          ...(e.rueckfrage ? { rueckfrage: e.rueckfrage } : {}),
          // 20.10.2026: nur an Martins Zeile an den Schmied (sonst wäre eine Marke am falschen Eintrag nur Anzeige ohne Wirkung, aber nie versteckt).
          ...(e.kind === "user" && e.kartenantwort === true && e.an === "schmied" ? { kartenantwort: true as const } : {}),
          ...(e.karte ? { karte: e.karte } : {}),
          ...(e.karteAntwort ? { karteAntwort: e.karteAntwort } : {}),
          ...adressatenAus(e),
          // 16.10.2026 (Zimmermann P1): der Absender mit derselben Lesart wie Server und Verlauf — appendBubble zeigt dann nur Text.
          ...(e.entwickler ? entwicklerMerkmal({ entwickler: e.entwicklerId }) : {}),
          // 16.10.2026: Bilder des Schmieds aus dem lokalen Verlauf — erneut geprüft (der Speicher ist kein Beweis).
          ...schmiedBilderVom(e.entwickler === true && !e.entwicklerId, e),
        });
      } else if (e.kind === "tool" && e.card) {
        renderToolCard(e.card);
      } else if (e.kind === "warn" && e.warn) {
        renderTruthWarning(e.warn);
      } else if (e.kind === "image" && e.image?.url) {
        appendImageBubble(e.image.url, e.image.from, e.image.caption ?? null, e.kanal);
      } else if (e.kind === "music" && e.music) {
        // Ticket 2937: Verlauf bleibt stumm (Geistermusik) — Autoplay nur live.
        appendMusicBubble(e.music);
      } else if (e.kind === "url" && e.urlOpen?.url) {
        appendUrlBubble(e.urlOpen.url, e.urlOpen.title);
      } else if (e.kind === "file" && e.fileItem) {
        appendFileBubble(e.fileItem);
      }
    } catch (renderErr) {
      skipped++;
      console.warn(
        `[history-render] item übersprungen (kind=${e.kind ?? "?"}):`,
        renderErr,
      );
    } finally {
      replayingTs = null;
    }
  }
  return skipped;
}

// History beim Page-Load wiederherstellen — instant scroll an's Ende
// (wie WhatsApp/Telegram). Smooth-Behavior wird nach der Hydration wieder
// aktiv für Live-Updates.
// Pre.509: Server-First. KEIN initial renderHistoryToDom mehr — der
// Server-Pull (hydrateFromServer mit replaceAll) bestimmt was angezeigt
// wird. Cache wird nur als Offline-Fallback genutzt: wenn der Server-
// Pull failt UND lokal was im Cache liegt, rendert das Catch im
// hydrateFromServer-Fehlerpfad (siehe hydrateFromServer:catch unten).
refs.chat.classList.add("hydrating");
// Pre.208: Hydrate-Phase mit visibility:hidden (siehe CSS .chat.hydrating).
// Reihenfolge:
//  1. scrollChatToBottomReliably (instant, weil scroll-behavior:auto während hydrate)
//  2. setTimeout für Lazy-Bilder/Markdown (~120ms typischer Layout-Settle)
//  3. ERST DANACH visibility wieder anschalten — User sieht direkt das Ende
scrollChatToBottomReliably();
setTimeout(() => {
  // Final scroll bevor wir sichtbar machen — unsichtbar hat niemand gescrollt.
  scrollChatToBottomReliably();
  refs.chat.classList.remove("hydrating");
}, 120);
// Sicherheits-Re-Scroll für sehr lazy Bilder (200KB+ Audio etc.) — jetzt
// sichtbar, also nur, wenn er noch unten steht (Ticket 2730).
setTimeout(() => fuehreChatNach(), 600);

// Pre.111: Cross-Device-Sync. Nach localStorage-Hydration parallel
// Server-History pullen und die Messages mergen die wir noch nicht haben.
// Dedupe per Composite-Key (created_at-ms + role + erste 80 Zeichen).
// Pre.298: in Funktion gezogen damit bootstrapAuth nach Account-Switch
// erneut triggern kann (vorher lief der erste Run mit Default-Key vor
// setHistoryUid → Server-Messages landeten im falschen localStorage).
// Pre.366: Hydrate-Status im DOM sichtbar — User sah beim Reload "App
// hängt" weil die ganzen Cards rekonstruiert wurden. Jetzt unobtrusive
// Status-Pille die "Lade Verlauf…" zeigt während hydrateFromServer läuft.
/**
 * Die Statuspille oben.
 *
 * @param nachsatz Eine zweite, leisere Zeile — wofuer das Ergebnis gut ist.
 *   ⚠️ 09.09.2026: Ohne sie beantwortete die Pille nur „was ist passiert".
 *   Martin fragte nach dem „wozu", und der Nachsatz ist die Stelle dafuer.
 */
function setHydrateStatus(
  active: boolean,
  label?: string,
  nachsatz?: string,
  /** pre.3603: „fertig“ = Rückmeldung nach einer Handlung, ohne Ladekreis. */
  art: "laeuft" | "fertig" = "laeuft",
): void {
  let pill = document.getElementById("hydrate-pill") as HTMLDivElement | null;
  if (active) {
    if (!pill) {
      pill = document.createElement("div");
      pill.id = "hydrate-pill";
      pill.className = "hydrate-pill";
      pill.setAttribute("role", "status");
      pill.setAttribute("aria-live", "polite");
      document.body.appendChild(pill);
    }
    pill.replaceChildren();
    const haupt = document.createElement("span");
    haupt.className = "hydrate-pill-haupt";
    haupt.textContent = label ?? "Lade Verlauf…";
    pill.appendChild(haupt);
    if (nachsatz) {
      const leise = document.createElement("span");
      leise.className = "hydrate-pill-nachsatz";
      leise.textContent = nachsatz;
      pill.appendChild(leise);
    }
    pill.classList.toggle("hydrate-pill--zweizeilig", Boolean(nachsatz));
    pill.classList.toggle("hydrate-pill--fertig", art === "fertig");
    pill.classList.add("is-visible");
  } else if (pill) {
    pill.classList.remove("is-visible");
    // pre.3603: nur entfernen, wenn sie inzwischen nicht wieder gezeigt wird (die Rückmeldung von
    // „Verlauf kürzen“ folgt direkt auf das Ausblenden des Neuaufbaus und verschwand sonst nach 300 ms).
    setTimeout(() => {
      if (!pill?.classList.contains("is-visible")) pill?.remove();
    }, 300);
  }
}

/**
 * Pre.509: Server-First-Mode. Wenn `replaceAll=true`:
 *   - localStorage history-Cache komplett wegwerfen
 *   - DOM leeren
 *   - Alle Server-Items direkt rendern (kein Dedupe-Vergleich gegen lokal)
 *   - Server ist Source-of-Truth, lokaler Cache nur Offline-Fallback
 *
 * Wenn `replaceAll=false` (legacy / live-sync): traditioneller Merge —
 * Items mit Composite-Key gegen lokalen Cache abgleichen, nur fehlende
 * appenden.
 */
/**
 * Wie viele Nachrichten der Chat beim Oeffnen holt. (22.09.2026)
 *
 * ⚠️ Zusammen mit {@link VERLAUF_NACHSCHLAG} zu lesen: was hier fehlt, holt
 *    das Hochscrollen nach. Eine kleine erste Seite ist nur dann richtig,
 *    wenn der Nachschlag verlaesslich funktioniert.
 */
const VERLAUF_ERSTE_SEITE = 50;
/** Wie viele beim Hochscrollen nachkommen. */
const VERLAUF_NACHSCHLAG = 80;

// 2026-07-03 (Martin, #36 A): Aeltere Nachrichten nachladen (Hochscrollen).
let loadingOlder = false;
let noMoreOlder = false;

function appendOlderBubble(
  role: "user" | "bot" | "system",
  text: string,
  ts: number,
): void {
  const node = document.createElement("div");
  // Aus dem Verlauf: kein Einblenden (style.css, `.bubble.aus-verlauf`) — Altes beginnt nicht bei Deckkraft 0.
  node.className = `bubble ${role} aus-verlauf`;
  if (role !== "system") {
    const speaker =
      role === "user"
        ? (currentUser?.name?.split(" ")[0]?.toUpperCase() ?? "ICH")
        : (currentBotName?.toUpperCase() ?? "BÄRLI");
    node.dataset.speaker = speaker;
    const label = document.createElement("span");
    label.className = "bubble-speaker";
    label.textContent = speaker;
    node.appendChild(label);
  }
  node.dataset.text = text.slice(0, 80).trim();
  if (role === "bot") {
    fillBotBubble(node, text, "verlauf");
  } else {
    const span = document.createElement("span");
    span.className = "bubble-text";
    span.textContent = text;
    node.appendChild(span);
  }
  // replayingTs: unterdrueckt Autoplay im Renderer + insertOrdered nutzt ts.
  const prev = replayingTs;
  replayingTs = ts;
  try {
    insertOrdered(node, ts);
  } finally {
    replayingTs = prev;
  }
}

async function loadOlderHistory(): Promise<void> {
  if (loadingOlder || noMoreOlder) return;
  // Aeltester bereits gerenderter Bubble-ts.
  let oldestTs = Date.now();
  for (const c of Array.from(refs.chat.children)) {
    const t = Number((c as HTMLElement).dataset?.ts ?? 0);
    if (t > 0) {
      oldestTs = t;
      break;
    }
  }
  loadingOlder = true;
  setHydrateStatus(true, "Lade ältere Nachrichten …");
  try {
    const { fetchChatHistory } = await import("./lib/chat-history-api.js");
    const older = await fetchChatHistory({
      beforeIso: new Date(oldestTs - 1).toISOString(),
      limit: VERLAUF_NACHSCHLAG,
    });
    if (older.length === 0) {
      noMoreOlder = true;
      return;
    }
    /*
     * ⚠️ 02.10.2026 (pre.3607): Wer schnell an den Anfang wischt, löst das Nachladen aus — und die
     * Antwort kommt, während die Fläche oben noch nachläuft oder ausfedert. Einfügen + Ort halten
     * schrieb `scrollTop` mitten hinein (echtes WebKit, Fall g: zweimal); am iPhone bricht das das
     * Nachlaufen ab. Erst einfügen, wenn er die Fläche nicht mehr bewegt (lib/scroll-ruhe.ts).
     * ⚠️ pre.3612: Außer der Browser hält den Ort selbst (`browserHaeltOrt`, lib/scroll-ort.ts) — dann
     *    schreiben wir nichts, und das Warten ließ nur den Anfang leer stehen (Martin: „die Blasen
     *    tauchen erst auf, wenn der Verlauf stehen bleibt“; WebKit, Fall h: 1.157 ms oben gewartet).
     */
    if (!browserHaeltOrt(refs.chat)) await wartAufRuhe(refs.chat, "aelteres");
    // Dedup gegen bereits gerenderte Bubbles (Rolle+Textanfang).
    const roleOf = (el: HTMLElement): string =>
      el.classList.contains("bot")
        ? "bot"
        : el.classList.contains("user")
          ? "user"
          : "system";
    const seen = new Set(
      Array.from(refs.chat.children).map(
        (c) =>
          `${roleOf(c as HTMLElement)}|${((c as HTMLElement).dataset?.text ?? "").slice(0, 80)}`,
      ),
    );
    /*
     * ⚠️ 01.10.2026 (Ticket 2873): Die älteren Blasen kommen OBEN dazu — der Ort,
     * an dem er liest, muss stehen bleiben. Hier stand `scrollTop = prevTop +
     * added` auf einem Verlauf mit `scroll-behavior: smooth`: eine Animation,
     * die ein bereits überholtes Ziel ansteuert. Dazu klappten die langen
     * Blasen erst ein Bild später zu. Im Nachbau ohne Verankerung (WebKit):
     * 7.066 Punkte Sprung der Bezugsblase, danach ein Flug von 5.000 Punkten
     * in Schritten bis 360 — und weil `scrollTop` dabei unter 80 blieb, lud
     * der Verlauf gleich noch einmal nach. Jetzt: einfügen, zuklappen, Ort
     * halten — sofort, in EINEM Schritt (lib/scroll-ort.ts).
     */
    const kinderVorher = refs.chat.children.length;
    const verschoben = haltenOhneSprung(refs.chat, () => {
      for (const m of older) {
        const ts = parseServerTs(m.createdAt)?.getTime() ?? oldestTs;
        const role = (m.role === "assistant" ? "bot" : m.role);
        const text = ohneAudioMarke(m.content ?? "");
        if (!text.trim()) continue;
        const key = `${role}|${text.slice(0, 80).trim()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        try {
          appendOlderBubble(role, text, ts);
        } catch {
          /* einzelne Bubble ueberspringen */
        }
      }
      // Die Tagestrenner gehören zu dieser Änderung: im selben Schritt, vor dem Ort-Halten.
      spuelTagestrenner();
    });
    klapper.leeren();
    // Kam etwas dazu, weiß die Regel danach, ob dieser Browser den Ort selbst hält (lib/scroll-ort.ts).
    if (refs.chat.children.length > kinderVorher) merkeVerankerung(verschoben);
  } catch {
    /* still — kein Abbruch der App */
  } finally {
    loadingOlder = false;
    setHydrateStatus(false);
  }
}

// Hochscrollen an den Anfang → aeltere Nachrichten nachladen.
/*
 * pre.3612: schon zwei Bildschirmhöhen vor dem Anfang, nicht erst bei 80 Punkten. Ein Wisch am iPhone legt bei
 * 30 Bildern/s bis 2.000 Punkte je Bild zurück (Journal 02.10., 22:15) — wer erst am Anfang lädt, steht dort
 * vor leerer Fläche. Mit Verankerung des Browsers ist das Ältere da, bevor er oben ankommt.
 */
const nachladeSchwelle = (): number => Math.max(80, refs.chat.clientHeight * 2);
refs.chat.addEventListener(
  "scroll",
  gesteuert("wachen", () => {
    if (refs.chat.scrollTop < nachladeSchwelle() && !loadingOlder && !noMoreOlder) {
      void loadOlderHistory();
    }
  }),
  { passive: true },
);

async function hydrateFromServer(
  opts: { replaceAll?: boolean } = {},
): Promise<void> {
  const replaceAll = opts.replaceAll === true;
  setHydrateStatus(true, replaceAll ? "Lade vom Server…" : "Lade Verlauf…");
  /*
   * ⚠️ 21.09.2026 — DAS ERGEBNIS WAR NIE ZU SEHEN.
   *
   * Unten stand `return; // Skip finally setHydrateStatus(false)`. Das ist
   * ein Irrtum ueber JavaScript: ein `return` im try-Block ueberspringt das
   * `finally` NICHT, es laeuft trotzdem. Die Ergebnis-Pille („3 neu, 12
   * schon da") wurde also gesetzt und im selben Zug wieder versteckt; die
   * 5,2 Sekunden Anzeigezeit gab es nie.
   *
   * Genau diese Zeile hatte Martin am 09.09. verlangt: *„Man sieht kurz,
   * was gemacht wird. Aber keine Information dazu: Wird das Material
   * ueberhaupt genutzt?"*
   */
  let eigenerAusblender = false;
  try {
    const { fetchChatHistory } = await import("./lib/chat-history-api.js");
    /*
     * ⚠️ 22.09.2026 (Martin): *„nicht zu viele Elemente immer geladen werden
     *    muessen fuer den Chat."*
     *
     * Hier stand 150. Gemessen, was das an reinem Text kostet:
     *
     *     Martin  150 Nachrichten =  98,3 kB   (Schnitt   671 Zeichen)
     *     Albert  150 Nachrichten = 276,8 kB   (Schnitt 1.889 Zeichen,
     *                                           die laengste 12.332)
     *
     * Ein Viertelmegabyte Text bei jedem App-Start, dazu Bilder und
     * Werkzeugkarten. Mit 50 sind es 27,6 kB bzw. 61,7 kB — rund ein
     * Viertel.
     *
     * ⚠️ Nichts geht dabei verloren: Hochscrollen laedt 80 weitere nach
     *    (`loadOlderHistory`, geprueft). Und 50 ist im Haus schon die
     *    etablierte Zahl — genau so viele liest der Server fuer den
     *    Gespraechskontext (`getRecentMessagesForUser`).
     *
     * ⚠️ Das ist NICHT dasselbe wie das Auto-Archiv vom selben Tag. Jenes
     *    verschiebt, AB WANN gezaehlt wird; diese Zahl sagt, WIE VIELE
     *    geholt werden. Martins Anliegen haengt an dieser hier.
     */
    const items = await fetchChatHistory({ limit: VERLAUF_ERSTE_SEITE });
    // Pre.790 (#61): EIN Wipe-Helfer (vorher 2 identische Blöcke) — und er
    // leert ZWINGEND renderedToolCallIds: Der Boot macht IMMER zwei
    // replaceAll-Pulls (Modul-Init + bootstrapAuth). Pull A renderte die
    // Server-Tool-Karten und registrierte ihre callIds; Pull B wischte das
    // DOM, aber der callId-Dedupe hielt die Karten danach für „schon
    // gerendert" → ALLE Karten fehlten nach jedem Reload (Haupt-Wurzel #61).
    const wipeChatCacheAndDom = (): void => {
      history.length = 0;
      try {
        localStorage.removeItem(getHistoryKey());
      } catch {
        /* ignore */
      }
      while (refs.chat.firstChild) refs.chat.removeChild(refs.chat.firstChild);
      renderedToolCallIds.clear();
    };
    if (items.length === 0) {
      if (replaceAll) {
        // Server hat nichts → DOM leer lassen, kein Fallback auf alten Cache.
        wipeChatCacheAndDom();
      }
      return;
    }
    if (replaceAll) {
      wipeChatCacheAndDom();
    }
    // Pre.772: Dedupe-Schlüssel CONTENT-basiert (kind+Text), NICHT mehr
    // zeit-basiert. Root-Cause des Doppel-Bugs: live gerenderte Nachrichten
    // tragen die CLIENT-Zeit (Date.now()), die Server-Kopie die SERVER-Zeit —
    // mit ts im Schlüssel matchte nie etwas → der visibilitychange-Re-Pull
    // hängte beim Screen-an/App-Wechsel die GANZE Konversation erneut an.
    // Ohne ts im Schlüssel greift der Dedupe korrekt.
    const localKeys = replaceAll
      ? new Set<string>()
      : new Set(
          history.flatMap((e) => {
            const plain = `${e.kind}|${verlaufSchluessel(e.text)}`;
            return e.karteAntwort ? [plain, zeilenSchluessel(e.kind, e.text, e.karteAntwort)] : [plain];
          }),
        );
    // #118: Bild-Nachrichten liegen lokal als kind:"image" (Schlüssel
    // "image|…"), der Server liefert sie aber als role:"user"/"bot" mit der
    // Caption als Inhalt. Ohne passenden Zweit-Schlüssel matchte der Dedup nie
    // und das Foto wurde beim visibilitychange-Re-Pull ein zweites Mal
    // angehängt. Jede Bild-Nachricht zusätzlich unter ihrem Rollen-Key merken.
    if (!replaceAll) {
      for (const e of history) {
        if (e.kind === "image" && e.image) {
          const role = e.image.from === "bot" ? "bot" : "user";
          const cap = (e.image.caption ?? "").slice(0, 80).trim();
          localKeys.add(`${role}|${cap}`);
          /*
           * ⚠️ 05.09.2026 — zusätzlich unter der BILD-ADRESSE merken.
           *
           * Martin, mit Foto: *„der eingefügte Text steht zweimal da, einmal
           * unter dem Bild und ein zweites Mal im Chat selber … Wenn ich neu
           * lade, ist der Text verschwunden, weil er ja nur einmal in der
           * Datenbank steht."*
           *
           * Genau das war der Befund: In der Datenbank steht die Nachricht
           * EINMAL, mit korrekten Bild-Metadaten. Die zweite Blase entstand
           * rein lokal beim Nachlauf — der Abgleich lief über die ersten 80
           * Zeichen des Textes, und der ist eine schwache Kennung: Er wird
           * beschnitten, getrimmt, und Beschriftung (was Martin sieht) und
           * gespeicherter Inhalt (was zur KI ging) müssen nicht Zeichen für
           * Zeichen dasselbe sein.
           *
           * Die Bildadresse dagegen ist eindeutig. Steht sie schon im Chat,
           * ist die Nachricht da — egal, wie ihr Text aussieht.
           */
          if (e.image.url) localKeys.add(`bild|${e.image.url}`);
        }
      }
    }
    // #120: Gerade streamende Bubbles (liveBotBubble / liveUserBubble) sind
    // noch NICHT in history — ein visibilitychange-Re-Pull während des Streams
    // sieht sie nicht und hängt eine zweite Bubble an (Duplikat-Bug). Wir
    // ergänzen die Keys mit dem aktuellen Stream-Text als Schutz.
    if (!replaceAll) {
      if (liveBotBubble) {
        const t = (liveBotBubble.dataset.text ?? "").trim();
        if (t) localKeys.add(`bot|${t}`);
      }
      if (liveUserBubble) {
        const t = (liveUserBubble.textContent ?? "").trim().slice(0, 80);
        if (t) localKeys.add(`user|${t}`);
      }
      // 2312: HTTP-Stream setzte liveBotBubble früher nicht. Jede schon
      // gezeichnete Bot-Blase zählt — sonst hängt der Vordergrund-Pull
      // dieselbe Antwort noch einmal an.
      for (const b of refs.chat.querySelectorAll<HTMLElement>(".bubble.bot")) {
        const t = (b.dataset.text ?? "").trim();
        if (t) localKeys.add(`bot|${t}`);
        const reply = (bubbleReplyText(b) || "").slice(0, 80).trim();
        if (reply) localKeys.add(`bot|${reply}`);
      }
    }
    // 2026-07-19 (Traegheits-Jagd): Tool-Karten wurden bei JEDEM
    // Vordergrund-Pull erneut in history gepusht — Bubbles sind per
    // localKeys dedupliziert, Karten waren es NICHT (DOM-Dedupe in
    // renderToolCard verhindert nur das Anzeigen, nicht das Anwachsen).
    // ~62 schwere Duplikate (bis 24 KB je Karte) pro App-Wechsel, das
    // 500er-Fenster war nach wenigen Wechseln damit gesaettigt.
    const knownCardKeys = new Set<string>();
    for (const h of history) {
      if (h.kind === "tool" && h.card) {
        knownCardKeys.add(`${h.card.tool}|${h.card.callId}|${h.ts}`);
      }
    }
    let appended = 0;
    let failed = 0;
    // Ticket 2660: die hier neu gezeichneten Karten — für die Rückfrage oben.
    const neueKarten = new Map<string, HTMLElement>();
    // Pre.506: PER-ITEM try/catch. Vorher konnte EIN corrupted item
    // (z.B. altes Tool-Card-Format) den ganzen Loop killen — der äußere
    // catch hat geschluckt, und alle NACHFOLGENDEN (= neuesten!) Items
    // blieben unrendert. Symptom: Phone-PWA zeigte alten Stand und
    // ignorierte heutige Messages komplett (Bug-Report Martin 28.05.).
    for (const m of items) {
      try {
        // UTC-bewusst parsen — sonst zeigt die Bubble den SQLite-UTC-Stempel als
        // Lokalzeit (Martin: „2 Stunden falsch"). Fallback: jetzt.
        const ts = parseServerTs(m.createdAt)?.getTime() ?? Date.now();
        const role = m.role === "assistant" ? "bot" : m.role;
        // 15.10.2026: ohne die Audio-Marke — sie steht nur im Servertext, nie in der Blase (sonst doppelte Sprachnachricht).
        // 21.10.2026: Martins Tipp auf eine Karte gleicht NUR mit derselben Kennung ab — sonst schluckt ein früheres „Ja“ die Zeile mit Kartenbezug.
        const karteKennung = role === "user" ? merkmalAusMetadaten(m.metadata).karteAntwort : undefined;
        const key = zeilenSchluessel(role, m.content, karteKennung);
        const bubbleAlreadyLocal = localKeys.has(key);
        // Pre.177: Bot-Bubble nur appenden wenn nicht in localStorage.
        // Tool-Cards aus metadata.toolCards immer prüfen — selbst wenn
        // die Bubble lokal war, kann die Card fehlen.
        // Pre.531: Wenn User-Message ein Bild dabei hatte (z.B. Beleg-
        // Upload), zuerst die Image-Bubble rendern. Server speichert
        // imageUrl in metadata.imageUrl seit Pre.531.
        const meta = m.metadata as
          | undefined
          | { toolCards?: unknown; imageUrl?: string; imageUrls?: unknown };
        /*
         * 2026-08-28: ALLE Bilder wiederherstellen, nicht nur das erste.
         * `imageUrl` bleibt der Rueckfall fuer Eintraege von vor dieser
         * Aenderung — die tragen nur eine URL.
         */
        const userImageUrls: string[] =
          role !== "user"
            ? []
            : Array.isArray(meta?.imageUrls)
              ? meta.imageUrls.filter((u): u is string => typeof u === "string")
              : typeof meta?.imageUrl === "string"
                ? [meta.imageUrl]
                : [];
        // Steht eines dieser Bilder schon im Chat, ist die ganze Nachricht
        // schon da — dann darf weder eine zweite Bild- noch eine Textblase
        // entstehen (siehe die Begruendung oben bei `bild|`).
        const bildSchonDa = userImageUrls.some((u) =>
          localKeys.has(`bild|${u}`),
        );
        if (bildSchonDa) continue;
        /*
         * Ticket 2893: Ein Eintrag der Antwort ohne Text, aber mit Karten — die Dateien eines Telegram-Zuges
         * (Bild, PDF, Audio), nach der Antwort in denselben Verlauf geschrieben. Es entsteht KEINE leere
         * Blase; die Karten darunter zeichnen dieselben Bilder und Dateien wie bei einem Chat-Zug.
         */
        const nurKarten = istNurKarten(role, m.content, meta?.toolCards);
        // Ticket 2892: der Kanal der Zeile (metadata.source) — Fotos und Bilder aus Telegram tragen dieselbe Marke wie ihr Text.
        const zeilenKanal = kanalAusQuelle((m.metadata as { source?: unknown } | null | undefined)?.source);
        if (nurKarten) {
          /* nichts zu zeichnen außer den Karten (unten) */
        } else if (userImageUrls.length > 0 && !bubbleAlreadyLocal) {
          replayingTs = ts;
          // Der Begleittext gehoert an das LETZTE Bild — so steht er
          // darunter statt zwischen den Bildern.
          userImageUrls.forEach((u, i) => {
            const beschriftung =
              i === userImageUrls.length - 1 ? m.content || null : null;
            appendImageBubble(u, "user", beschriftung, zeilenKanal);
            pushHistory(
              {
                kind: "image",
                image: { url: u, caption: beschriftung, from: "user" },
                ...(zeilenKanal ? { kanal: zeilenKanal } : {}),
                ts,
              },
              { deferSave: true },
            );
          });
          appended++;
        } else if (bubbleAlreadyLocal && role === "user") {
          // 16.10.2026: die schon gezeichnete Blase bekommt „Martin → Schmied“, wenn die gespeicherte Zeile es belegt.
          const vermerk = adressatVermerkAus("martin", m.metadata as { an?: unknown } | null | undefined);
          if (vermerk) {
            const lokalKey = m.content.slice(0, 80).trim();
            const stehend = Array.from(refs.chat.querySelectorAll<HTMLElement>(".bubble.user")).find((b) => (b.dataset.text ?? "") === lokalKey);
            if (stehend) {
              setzeAdressatVermerk(stehend, vermerk);
              sperreBeantworteteFragen(refs.chat, meldeFrageGesperrt);
            }
          }
        } else if (bubbleAlreadyLocal && role === "bot") {
          // 12.10.2026: war die Antwort gestoppt, trägt die schon gezeichnete Blase den Vermerk „abgebrochen“ (die Teilantwort bleibt stehen).
          if (merkmalAusMetadaten(m.metadata).abgebrochen) {
            const lokalKey = m.content.slice(0, 80).trim();
            const stehend = Array.from(refs.chat.querySelectorAll<HTMLElement>(".bubble.bot")).find(
              (b) => (b.dataset.text ?? "") === lokalKey,
            );
            if (stehend) setzeFortsetzungsVermerk(stehend, { abgebrochen: true });
          }
          // 2026-07-21 (Martin, Sagres-Fall): riss der Stream ab, blieb die
          // HALBE Blase stehen — und die volle Server-Fassung wurde als
          // Duplikat verworfen (Dedupe-Key = erste 80 Zeichen, fuer beide
          // gleich). Jetzt: Blase + History-Eintrag auf die volle Fassung
          // anheben (Markdown + Abspiel-Knopf inklusive).
          const keyText = m.content.slice(0, 80).trim();
          const he = history.find(
            (h) =>
              h.kind === "bot" &&
              (h.text ?? "").slice(0, 80).trim() === keyText,
          );
          if (he?.text && m.content.length > he.text.length + 10) {
            he.text = m.content;
            const node = Array.from(
              refs.chat.querySelectorAll<HTMLElement>(".bubble.bot"),
            ).find(
              (b) =>
                (b.dataset.text ?? "") === keyText ||
                (b.textContent ?? "").trim().slice(0, 80).trim() === keyText,
            );
            if (node) fillBotBubble(node, m.content, "verlauf");
          }
        } else if (!bubbleAlreadyLocal) {
          replayingTs = ts;
          // Ticket 2864: der Kanal steht seit pre.3532 in metadata.source.
          const kanal = kanalAusQuelle((m.metadata as { source?: unknown } | null | undefined)?.source);
          // 12.10.2026: Fortsetzung / abgebrochen aus `metadata` (keine Datenmigration; alte Zeilen tragen nichts).
          const merkmal = merkmalAusMetadaten(m.metadata);
          appendBubble(role, m.content, kanal, merkmal);
          pushHistory(
            { kind: role, text: m.content, ts, ...(kanal ? { kanal } : {}), ...merkmal },
            { deferSave: true },
          );
          appended++;
        }
        const cards = m.metadata?.toolCards;
        if (Array.isArray(cards)) {
          for (const c of cards) {
            try {
              replayingTs = ts;
              kartenKanal = zeilenKanal;
              const gezeichnet = renderToolCard({
                tool: String(c.tool ?? ""),
                callId: String(c.callId ?? ""),
                isError: Boolean(c.isError),
                result: c.result,
                anzeige: c.anzeige,
              });
              if (gezeichnet) neueKarten.set(String(c.callId ?? ""), gezeichnet);
              // Eine Karte ohne Blase davor ist selbst die neue Nachricht (Scrollen, Zähler).
              if (nurKarten && gezeichnet) appended++;
              const cardKey = `${String(c.tool ?? "")}|${String(c.callId ?? "")}|${ts}`;
              if (!knownCardKeys.has(cardKey)) {
                knownCardKeys.add(cardKey);
                pushHistory(
                  {
                    kind: "tool",
                    card: {
                      tool: String(c.tool ?? ""),
                      callId: String(c.callId ?? ""),
                      isError: Boolean(c.isError),
                      result: c.result,
                      anzeige: c.anzeige,
                    },
                    ts,
                  },
                  { deferSave: true },
                );
              }
            } catch (cardErr) {
              failed++;
              console.warn(
                "[chat-history-sync] tool-card render skipped:",
                cardErr,
              );
            } finally {
              replayingTs = null;
              kartenKanal = undefined;
            }
          }
        }
      } catch (itemErr) {
        failed++;
        console.warn("[chat-history-sync] item skipped:", itemErr);
      } finally {
        replayingTs = null;
      }
    }
    // Aufgeschobene Sicherung: EINMAL fuer den ganzen Pull (s. pushHistory).
    saveHistory(history);
    /*
     * Ticket 2873: Die Tagestrenner entstehen sonst erst im Microtask NACH dem Ans-Ende-Stellen
     * und NACH dem Zuklappen — und schieben den Verlauf nachträglich (Nachbau: 56 bis 173 Punkte
     * über dem Ende, der Knopf zeigt „Zum Ende des Chats“, die nächste fremde Antwort wird nicht
     * nachgeführt). Hier, im selben Schritt: erst die Trenner, dann ans Ende, dann zuklappen.
     */
    spuelTagestrenner();
    /*
     * Ticket 2730: Nur der volle Neuaufbau stellt ans Ende — dabei entsteht
     * der Verlauf neu, eine Lesestelle gibt es danach nicht mehr. Der
     * Nachzug nach einem Verbindungsabriss hängt nur an; liest er gerade
     * weiter oben, bleibt er dort.
     */
    if (appended > 0) {
      if (replaceAll) scrollChatToBottomReliably();
      else fuehreChatNach();
    }
    /*
     * Ticket 2873: Was die langen Blasen an Höhe hergeben, ist HIER entschieden — im
     * selben Schritt wie das Zeichnen, mit gehaltenem Ort (am Ende des Verlaufs bleibt
     * der Abstand zum Ende), nicht je Blase ein Bild später (gemessen: erst 14.936,
     * dann 11.804 Punkte; im Journal −11.161 in einem Ereignis). Ist der Chat
     * versteckt (Kaltstart), bleibt alles vorgemerkt und kommt dran, sobald er eine
     * Größe hat.
     */
    klapper.leeren();
    /*
     * Ticket 2660 (Nachbesserung 28.09.2026): Eine Rückfrage, die ankam,
     * während der Strom nicht lief (Telefon gesperrt, Verbindung weg, App
     * geschlossen), kommt NUR hier an. Offen ist sie, solange Martin danach
     * nichts gesagt hat — das steht in genau diesen Zeilen vom Server.
     */
    heftOffeneRueckfrageAn(items, neueKarten);
    /*
     * Pre.508 hatte die Zahlen sichtbar gemacht — vorher verschwand die
     * Pille einfach und niemand wusste, ob 0, 5 oder 50 Eintraege kamen.
     *
     * ⚠️ 09.09.2026, Martin: *„Man sieht kurz, was gemacht wird. Aber keine
     *    Information dazu: Wird das Material ueberhaupt genutzt? Wie weit hat
     *    das eine Relevanz im System?"*
     *
     *    Die Zahlen beantworteten „was ist passiert", nicht „wozu". Und die
     *    ehrliche Antwort auf sein „wozu" ist ueberraschend: Dieser Knopf
     *    frischt auf, was ER sieht — nicht, was Vespera weiss. Der Server
     *    liest den Verlauf bei JEDEM Zug selbst aus der Datenbank
     *    (`getRecentMessagesForUser`, chat.ts, letzte 50). Was hier im
     *    Browser liegt, ist die Anzeige.
     *
     *    Das gehoert dazugesagt, sonst haelt er den Knopf fuer etwas, das er
     *    nicht ist — und drueckt ihn, wenn Vespera etwas vergessen zu haben
     *    scheint, wo er gar nicht hilft.
     */
    const totalPulled = items.length;
    const inCache = totalPulled - appended - failed;
    /*
     * ⚠️ 22.09.2026 (Martin, mit Foto vom Telefon): *„diese Information ist
     *    im Smartphone-Modus schrecklich. Da brauche ich ja die ganze
     *    Information gar nicht."*
     *
     *    Hier stand über den halben Bildschirm „Nichts Neues — 150 Einträge,
     *    alle schon da" plus ein erklärender Nachsatz. Zwei Zeilen, um zu
     *    sagen, dass nichts passiert ist. Der Nachsatz erklärte ausserdem
     *    den Knopf „Verlauf neu vom Server" — den es in der Symbol-Leiste
     *    seit heute nicht mehr gibt.
     *
     *    Was gezeigt wird, entscheidet jetzt `verlauf-meldung.ts`. Dort ist
     *    es geprüft, samt Gegenprobe auf den alten Satz.
     */
    const meldung = verlaufMeldung({
      neu: appended,
      schonDa: inCache,
      uebersprungen: failed,
    });
    if (meldung) {
      setHydrateStatus(true, meldung.text, meldung.nachsatz);
      eigenerAusblender = true;
      setTimeout(() => setHydrateStatus(false), 5200);
    } else {
      /*
       * Nichts Neues: die Pille verschwindet einfach. ⚠️ Hier KEIN
       * `eigenerAusblender` — das Flag haelt nur den Ausblender im
       * `finally` davon ab, eine gerade gesetzte Meldung sofort wieder zu
       * verstecken. Hier ist genau das erwuenscht.
       */
      setHydrateStatus(false);
    }
    return;
  } catch (err) {
    console.warn("[chat-history-sync] failed:", err);
    // Pre.509: Offline-Fallback. Wenn Server-Pull failt UND localStorage
    // hat noch was: aus Cache rendern damit User wenigstens den letzten
    // bekannten Stand sieht. NUR wenn DOM noch leer ist — sonst hätte
    // ein erfolgreicher Pre.509-Pull schon gerendert.
    if (refs.chat.children.length === 0 && history.length > 0) {
      renderHistoryToDom(history);
      setHydrateStatus(true, "Server nicht erreichbar — zeige lokalen Cache");
    } else {
      setHydrateStatus(true, "Verlauf-Sync fehlgeschlagen");
    }
    eigenerAusblender = true;
    setTimeout(() => setHydrateStatus(false), 3000);
    return;
  } finally {
    // Nur wenn kein eigener Ausblender laeuft — sonst loescht das finally
    // die Meldung, die gerade erst gesetzt wurde.
    if (!eigenerAusblender) setHydrateStatus(false);
  }
}

/**
 * Pre.508: Hard-Reset des lokalen Chat-Caches. Cleart die localStorage-
 * History UND DOM, dann neuer Pull vom Server. Für User-getriggerten
 * "Da fehlt was, hol's neu"-Pfad.
 */
async function resyncHistoryFromServer(): Promise<void> {
  await hydrateFromServer({ replaceAll: true });
}
// Globaler Hook damit andere Module (settings, debug menu) den Reset
// triggern können ohne app.ts-Import.
(
  window as unknown as { __voiceV8ResyncHistory?: () => Promise<void> }
).__voiceV8ResyncHistory = resyncHistoryFromServer;

// Pre.509: Server-First Boot-Hydrate. Default-Key für unauthenticated.
// Auth-Bootstrap re-pullt mit replaceAll:true sobald uid bekannt ist —
// das ersetzt dann den initialen Default-Key-Pull komplett.
void hydrateFromServer({ replaceAll: true });
window.addEventListener("voice-v8:hydrate-from-server", () => {
  void hydrateFromServer({ replaceAll: true });
});

// Pre.505: PWA-Reopen-Re-Hydrate. Auf iOS PWA bleibt der Tab oft tagelang
// im Hintergrund — beim Wieder-Öffnen war der Server-Pull bisher nur am
// initial Boot. Resultat: User sah einen alten Stand obwohl auf einem
// anderen Device weiter geschrieben wurde. Jetzt: jedes Mal wenn das
// Document wieder visible wird, einmal nachpullen. Mit 30s-Throttle damit
// schnelle Tab-Switches nicht spamen.
let lastHydrateAt = Date.now();
const REHYDRATE_THROTTLE_MS = 30_000;
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return;
  const now = Date.now();
  if (now - lastHydrateAt < REHYDRATE_THROTTLE_MS) return;
  lastHydrateAt = now;
  void hydrateFromServer();
});

// Cross-Device-Sync (Live, Sprint A erweitert): SSE-Stream für sofortigen
// Push wenn auf einem anderen Gerät eine Voice/Chat-Message läuft. Dedupe
// per Composite-Key wie beim Bootstrap-Pull (ts+role+first80). Der Stream
// wickelt seinen eigenen Auto-Reconnect mit exponential backoff ab.
void (async () => {
  try {
    // 2026-07-05 (Martin, #45): Chat-Badge — Zahl am Chat-Punkt der Bottom-
    // Nav, wenn Meldungen ankommen, waehrend der Chat nicht sichtbar ist
    // (wie das Widget-Label im Alltag).
    const chatIsVisible = (): boolean => {
      if (document.hidden) return false;
      const overlayOpen = document.querySelector(
        ".hd-overlay, .book-overlay, .archive-overlay, .usr-overlay, " +
          ".project-sheet-overlay, " +
          ".material-sheet:not([hidden])",
      );
      if (overlayOpen) return false;
      const chatView = document.getElementById("view-chat");
      return !chatView?.hidden;
    };
    const renderChatBadge = (): void => {
      const btn = document.getElementById("bnav-chat");
      if (!btn) return;
      let badge = btn.querySelector<HTMLElement>(".bnav-badge");
      if (unseenChatCount <= 0) {
        badge?.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "bnav-badge";
        btn.appendChild(badge);
      }
      badge.textContent = unseenChatCount > 9 ? "9+" : String(unseenChatCount);
    };
    document.getElementById("bnav-chat")?.addEventListener("click", () => {
      unseenChatCount = 0;
      renderChatBadge();
    });
    const { subscribeChatStream } = await import("./lib/chat-stream.js");
    const reaktionenAnzeige = baueReaktionenAnzeige(refs.chat);
    const auftragsAnzeige = baueAuftragsAnzeige(refs.chat);
    // 19.10.2026 (Hintergrund-Fach Etappe 2): Fortschrittskarte je Hintergrund-Auftrag im Chat und die Aktivitätszeile über dem Eingabefeld.
    const jobAnzeige = baueJobAnzeige(refs.chat);
    // B12a (28.10.2026): Lauf-Leiste oben im Chat — was der Autonomie-Lauf gerade tut, mit Stopp (lib/lauf-leiste.ts). Fragt selbst nach.
    const chatAnsicht = document.getElementById("view-chat");
    const laufLeiste = chatAnsicht ? baueLaufLeiste(chatAnsicht, refs.chat) : null;
    // 3.11.2026: Fortschrittskarte „Entwicklung“ oben im Chat (lib/entwicklung-karte.ts) — dieselbe Tafel wie die Marke, von selbst da, solange Arbeit läuft.
    const entwicklungKarte = chatAnsicht ? baueEntwicklungKarte(chatAnsicht, refs.chat) : null;
    const aktivitaetKnopf = document.getElementById("aktivitaet-zeile");
    const aktivitaet = aktivitaetKnopf ? baueAktivitaetsZeile(aktivitaetKnopf, () => goToView("tasks"), Date.now, (id) => {
      const wb = id === "schmied" ? schmiedWarteblase : zusatzWarteblasen.get(id);
      return wb?.sichtbar() === true || wb?.entfernt() === true;
    }) : null;
    aktivitaetNeuZeichnen = () => aktivitaet?.neuZeichnen();
    // 19.10.2026: Karte „Morgenreport“ (lib/morgenreport-karte.ts) — liest nie von selbst vor, öffnet nie das Mikro.
    const morgenAnzeige = baueMorgenAnzeige(refs.chat);
    void morgenAnzeige.aktualisiere();
    setInterval(() => void morgenAnzeige.aktualisiere(), NACHSEHEN_MS);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void morgenAnzeige.aktualisiere();
    });
    let chatStreamOpenedOnce = false;
    let unseenChatCount = 0;
    subscribeChatStream({
      // Ticket 2849: nur die iPhone-App (natives Plugin) nimmt „Alltag starten/beenden“ an.
      kann: () => (isAlwaysRecordSupported() ? [KANN_ALLTAG] : []),
      onAuftrag: (auftrag) => {
        void import("./lib/alltag-steuern.js").then((m) => m.behandleAuftrag(auftrag));
      },
      // Ticket 2882: eine Stimme je Konto — beginnt ein anderes Gerät zu sprechen, endet die ältere Stimme hier.
      onStimme: stimmeVomKonto,
      onStimmeVorn: (e) => vornVomKonto(e),
      // 17.10.2026: ein anderes Gerät nimmt auf — Wach-Wort und Auto-Mikro bleiben hier zu (zweitesGeraetHaeltZu).
      onStimmeAufnahme: (e) => {
        aufnahmeVomKonto(e);
        // 22.10.2026 (Sicherheitsprüfung, lib/frage-rueckkehr.ts): ein anderes Gerät nimmt auf — die wartende Frage wird dort beantwortet.
        if (aufnahmeAnderswo(e, EIGENES_GERAET) && frageRueckkehr.loesche()) meldeDiagnose("[ptt] frage-rueckkehr zu grund=anderswo");
      },
      onStimmeFertig: fertigVomKonto,
      // 28.10.2026: die Reihe eines anderen Geräts läuft — hier beginnt solange nichts von selbst (seiten-abgleich.ts „Besitz“).
      onStimmeBesitz: (e) => besitzVomKonto(e),
      // 16.10.2026: Statusanzeige „Schmied arbeitet“ — als Warteblase am Ende des Verlaufs, kein Chat-Beitrag.
      onSchmiedStatus: (stand) => {
        schmiedStatusMikroFrei = stand.mikroFrei === true;
        schmiedWarteblase.zeige(stand);
        aktivitaet?.setzeEntwickler("schmied", stand);
      },
      onZusatzStatus: (id, stand) => {
        zusatzWarteblasen.get(id)?.zeige(stand);
        aktivitaet?.setzeEntwickler(id, stand);
      },
      // 19.10.2026: Hintergrund-Auftrag dieses Kontos — Karte im Chat und Eintrag in der Aktivitätszeile.
      onJobFortschritt: (job) => {
        jobAnzeige.setze(job);
        aktivitaet?.setzeJob(job);
        // Ein Prozess hat sich bewegt oder endete: die Lauf-Leiste nicht erst in 5 s nachziehen.
        if (job.art === "prozess") void laufLeiste?.aktualisiere();
      },
      // 18.10.2026: Emoji-Reaktionen der Entwickler als kleine Chips an Martins Blasen — kein Chat-Beitrag (lib/reaktionen-chips.ts).
      onReaktionen: (stand) => reaktionenAnzeige.setze(stand.reaktionen),
      // 18.10.2026: Karte „Auftrag an Vespera“ mit Ja/Nein (lib/auftrags-karte.ts) — kein Chat-Beitrag, in P1 wird nichts ausgeführt.
      onAuftraege: (stand) => auftragsAnzeige.setze(stand.auftraege),
      // 16.10.2026: Marke „Entwicklung <Zahl>“ in der Kopfzeile — nur Zahl und Liste, kein Chat-Beitrag.
      onSchmiedArbeit: (stand) => {
        zeigeSchmiedArbeit(stand);
        entwicklungKarte?.zeige(stand);
      },
      onOpen: () => {
        // 16.10.2026: ein Schmied-Status, der während des Abrisses endete, bleibt nicht hängen — der Server schickt einen noch
        // lebenden Stand direkt nach „ready“ wieder (server/chat.ts).
        schmiedWarteblase.verbindungNeu();
        entwicklungKarte?.verbindungNeu();
        for (const b of zusatzWarteblasen.values()) b.verbindungNeu();
        jobAnzeige.verbindungNeu();
        aktivitaet?.verbindungNeu();
        // 2026-07-04 (Martin): nach JEDEM (Re-)Connect die Luecke nachziehen —
        // Nachrichten, die waehrend eines Abrisses auf einem anderen Geraet
        // geschrieben wurden, sendet der Bus nicht nach. Nur ein Pull holt sie.
        // Beim ERSTEN Connect nicht noetig (Boot hydratisiert bereits).
        if (!chatStreamOpenedOnce) {
          chatStreamOpenedOnce = true;
          return;
        }
        void hydrateFromServer();
      },
      onMessage: (m) => {
        // UTC-bewusst parsen — sonst zeigt die Bubble den SQLite-UTC-Stempel als
        // Lokalzeit (Martin: „2 Stunden falsch"). Fallback: jetzt.
        let ts = parseServerTs(m.createdAt)?.getTime() ?? Date.now();
        // 2026-07-04 (Martin): Uhren-Versatz Geraet<->Server — FRISCHE
        // Live-Echos (juenger als 2 Min) mit LOKALER Zeit einsortieren,
        // sonst rutscht die Antwort/Karte UEBER die eigene Frage (lokale
        // Bubbles tragen Geraetezeit). Alte Nachrichten (Backfill) behalten
        // den Original-Stempel.
        if (Math.abs(Date.now() - ts) < 120_000) ts = Date.now();
        const kind: "user" | "bot" | "system" =
          m.role === "assistant" ? "bot" : m.role;
        // 15.10.2026: ohne die Audio-Marke — die Blase und der Verlauf tragen nur das Gesagte.
        const contentSlice = verlaufSchluessel(m.content);
        // Pre.148: PTT-Echos droppen — die sendende Seite zeichnet selbst,
        // der Inhalts-Abgleich pre.146 fing das Doppel nicht zuverlässig.
        // 29.09.2026 (Ticket 2796): Das war das angekündigte Seiten-Kennzeichen.
        // Verworfen wird nur noch das EIGENE Echo; der Browser am Rechner
        // bekommt die Sprechtasten-Antwort samt Karten sofort, statt erst
        // beim nächsten Nachladen (gemessen 11,5 Minuten).
        // 22.10.2026 (Sicherheitsprüfung, lib/frage-rueckkehr.ts): Martin antwortete anderswo (Telefon, Kanal) — die Rückkehr öffnet nichts mehr.
        if (antwortAnderswo(m, EIGENES_GERAET) && frageRueckkehr.loesche()) meldeDiagnose("[ptt] frage-rueckkehr zu grund=anderswo");
        if (istEigenesPttEcho(m, EIGENES_GERAET)) return;
        // #45: Meldung kam an, waehrend der Chat nicht sichtbar ist -> Badge.
        if (kind === "bot" && !chatIsVisible()) {
          unseenChatCount++;
          renderChatBadge();
        }
        // Pre.221: Im Voice-Mode rendert die LiveKit-TranscriptionReceived-
        // Pipeline die Bot-Bubble selbst (über handleBotSegment-Akkumulator
        // pre.217). SSE-Echo aus persistChat würde eine ZWEITE Bubble erzeugen
        // — Race: SSE läuft NETZWERK-schnell, der Voice-TranscriptionSynchro-
        // nizer nimmt sich aber 1-2s Zeit (Wort-für-Wort sync mit Audio-Play-
        // back). SSE landet VOR der Voice-Bubble → alle Dedupes versagen
        // (liveBotBubble noch nicht da, history noch nicht gepusht, DOM
        // noch leer). Fix: voice-source SSE-Echos für Bots droppen wenn
        // wir gerade im Voice-Call sind. Cross-Device-Tab (anderes Gerät,
        // nicht im Call) sieht dann state=idle → Echo wird normal gerendert.
        const isInVoiceCall =
          currentState !== "idle" && currentState !== "error";
        // Pre.763: ECHTER Doppel-Fix. Im aktiven Voice-Call rendert der Realtime-
        // Pfad JEDE Nachricht (User UND Bot) selbst live. Der SSE-Echo (für
        // Cross-Device-Sync) ist für DIESEN Client dann immer redundant und kam
        // — je nach source-Tag — als zweite Bubble durch (die Content-Dedupes
        // race-failen, weil der Echo VOR/zeitgleich mit der Live-Bubble landet).
        // Vorher wurde nur source==="voice" + kind==="bot" gedroppt → User-
        // Bubbles + andere source-Tags doppelten. Jetzt: im Call ALLE User-/Bot-
        // Echos droppen, egal welche source. Andere Geräte (state=idle) bekommen
        // sie weiterhin normal (Cross-Device-Sync bleibt intakt).
        if (isInVoiceCall && (kind === "bot" || kind === "user")) return;
        // 16.10.2026 (Martin, 21:37): eine Nachricht MIT Bild ist EINE Karte. Die sendende Seite hat die Bildblase schon (Verlaufsart
        // „image“, ohne data-text) — die Text-Prüfungen unten sehen sie nicht und zeichneten den Text ein zweites Mal ohne Bild.
        // Kennung ist die Ablage-Adresse (lib/bild-echo.ts), nie der Text. Ein anderes Gerät zeichnet Bild(er) samt Text.
        const echoBilder = kind === "user" && bildEchoAn() ? bildAdressenAusEcho(m) : [];
        if (echoBilder.length) {
          if (bildSchonGezeichnet(echoBilder, refs.chat, history)) return;
          try {
            replayingTs = ts;
            const kanalBild = kanalAusQuelle(m.source);
            echoBilder.forEach((u, i) => {
              const beschriftung = i === echoBilder.length - 1 ? m.content || null : null;
              appendImageBubble(u, "user", beschriftung, kanalBild);
              pushHistory({ kind: "image", image: { url: u, caption: beschriftung, from: "user" }, ...(kanalBild ? { kanal: kanalBild } : {}), ts });
            });
          } finally {
            replayingTs = null;
          }
          fuehreChatNach();
          notifyNewMessage();
          return;
        }
        // Pre.141: Dedupe gegen live-Bot-Bubble + history.
        //
        // Pre.127 hat history-Dedupe gefixt (30s Toleranz + content-
        // match). Aber Race: Voice-Stream pushed Eintrag erst bei
        // isFinal. SSE-Echo kommt während des Streams an → history
        // hat den Eintrag NOCH NICHT → zweite Bubble entsteht.
        // Fix: gegen die laufende Live-Bubble prüfen.
        //
        // #120: Bot-Bubble über dataset.text prüfen statt textContent.
        // textContent schließt Avatar-Bild-Alt, Play-Button-Labels und
        // weitere Kinder-Nodes ein → Vergleich scheiterte bei finalisierten
        // Bubbles → SSE-Echo kam trotzdem durch → Duplikat sichtbar.
        if (kind === "bot" && liveBotBubble) {
          const liveText = (liveBotBubble.dataset.text ?? "").trim();
          if (liveText && liveText === contentSlice.trim()) return;
        }
        if (kind === "user" && liveUserBubble) {
          const liveText = (liveUserBubble.textContent ?? "")
            .trim()
            .slice(0, 80);
          if (liveText && liveText === contentSlice.trim()) return;
        }
        for (const e of history) {
          if (e.kind !== kind) continue;
          const eContent = verlaufSchluessel(e.text);
          if (eContent !== contentSlice) continue;
          if (Math.abs((e.ts ?? 0) - ts) <= 30_000) return;
        }
        // Pre.193: DOM-Dedupe via data-text-Attribut — robust gegen
        // Avatar/Time/Button-Subnodes die textContent verfälschen.
        // Bei LiveKit-Realtime malt onBotMessage eine Bubble mit
        // data-text = der reine Reply. SSE-Echo kommt später, ts kann
        // außerhalb 30s liegen → DOM-Check ist Last-Resort.
        if (kind === "bot" || kind === "user") {
          const sel = kind === "bot" ? ".bubble.bot" : ".bubble.user";
          const recentBubbles = Array.from(
            refs.chat.querySelectorAll<HTMLElement>(sel),
          ).slice(-8);
          const trimmedSlice = contentSlice.trim();
          for (const b of recentBubbles) {
            const dataTxt = (b.dataset.text ?? "").trim();
            if (dataTxt && dataTxt === trimmedSlice) return;
          }
        }
        // 2026-07-02 (Audit): try/finally wie an den Schwester-Replay-Stellen
        // (Pre.507) — eine Render-Exception liess replayingTs sonst dauerhaft
        // haengen: Auto-Vorlesen tot, neue Bubbles mit altem Timestamp.
        if (kind === "bot" && findePassendeBotBlase(m.content)) {
          return;
        }
        try {
        replayingTs = ts;
        // Ticket 2864: Telegram-Echos tragen ihren Kanal (m.source kommt mit jeder Bus-Nachricht).
        const kanal = kanalAusQuelle(m.source);
        // Ticket 2893: die Dateien eines Telegram-Zuges kommen als Eintrag ohne Text, nur mit Karten — keine leere Blase.
        const nurKarten = istNurKarten(kind, m.content, m.toolCards);
        if (!nurKarten) {
          // Dreier-Modus: der Beitrag des Entwicklers kommt als system mit der Quelle „entwickler“.
          const entwickler = istEntwicklerZeile(m.role, m.source);
          // 16.10.2026 (Zimmermann P1): wer schrieb — dieselbe Lesart wie Server und Fremd-Marke (unbekannt = nicht der Schmied).
          const absender = entwickler ? entwicklerMerkmal(m) : {};
          const vomSchmied = entwickler && absender.entwicklerId === undefined;
          // Frage, Adressat, „weiter“ und Mikro gelten NUR beim Schmied — Martins Gesprächszeilen tragen ihren Adressaten wie bisher.
          const frage = vomSchmied ? frageAus((m as { frage?: unknown }).frage) : null;
          // 16.10.2026: an wen (Bus-Feld `an`/`sekundaer`, nur gültige Werte) — für „wer an wen“ an der Blase.
          const adressaten = !entwickler || vomSchmied ? adressatenAus(m as { an?: unknown; sekundaer?: unknown }) : {};
          const liveMerkmal: BlasenMerkmal = { ...(entwickler ? { entwickler: true, ...(frage ? { frage } : {}), ...absender, ...(absender.entwicklerId === "zimmermann" && rueckfrageIdAus((m as { rueckfrage?: unknown }).rueckfrage) ? { rueckfrage: rueckfrageIdAus((m as { rueckfrage?: unknown }).rueckfrage)! } : {}) } : {}), ...adressaten, ...(kind === "user" && kartenantwortGilt((m as { kartenantwort?: unknown }).kartenantwort, adressaten.an, m.content) ? { kartenantwort: true as const } : {}), ...schmiedBilderVom(vomSchmied, m as { bilder?: unknown }), ...karteMerkmal(entwickler ? (absender.entwicklerId ?? "schmied") : null, m) };
          appendBubble(kind, m.content, kanal, liveMerkmal);
          pushHistory({ kind, text: m.content, ts, ...(kanal ? { kanal } : {}), ...liveMerkmal });
          // 15.10.2026 (Schmied): sein Beitrag kam LIVE (nicht aus dem Verlauf) — vorlesen, falls an; das Mikro erfährt es.
          // Nur der Schmied: ein Beitrag des Zimmermanns (oder eines unbekannten Absenders) beantwortet keine Anrede an den Schmied (P4/P5).
          const weiterLive = vomSchmied && weiterAus((m as { weiter?: unknown }).weiter, frage) === true;
          // 20.10.2026 (Martin 09:39, lib/offene-fragen.ts): eine Nachricht Martins von einem anderen Gerät, belegt an einen Entwickler
          // (Feld `an`), erledigt dessen offene Fragen bis zu ihrer Zeit — nie die eines anderen Absenders.
          if (kind === "user") offeneFragen.martinAntwortete(adressaten.an, ts);
          // 22.10.2026 (Martin 10:26): Zimmermann/Hüter fragten einen ANDEREN Entwickler — das Server-Feld `ziel` (nur Bus, nie aus dem Text)
          // nimmt dem Beitrag die Frage an Martin: kein Mikro, keine offene Frage. Beim Schmied gilt weiter sein `an` (oben).
          const zielLive = entwickler && !vomSchmied ? (m as { ziel?: unknown }).ziel : undefined;
          // Ein frischer Beitrag, der Martin fragt, steht als offene Frage in „Offen <Zahl>“ (nur Anzeige; das Mikro regelt das Warten).
          const fragtLive = entwickler
            ? frageVon({
                entwicklerId: vomSchmied ? "schmied" : absender.entwicklerId,
                frisch: schmiedBeitragFrisch(m.createdAt, Date.now()),
                text: m.content,
                frage,
                weiter: (m as { weiter?: unknown }).weiter,
                info: (m as { info?: unknown }).info,
                an: adressaten.an,
                erwartet: (m as { erwartet?: unknown }).erwartet,
                ziel: zielLive,
              })
            : null;
          if (fragtLive) offeneFragen.frageKam(fragtLive, m.content, ts, !!frage);
          // 22.10.2026: derselbe Entwickler arbeitet weiter oder meldet nur (`weiter`/`info`) — er wartet nicht mehr; seine älteren
          // offenen Fragen erledigen sich selbst (lib/offene-fragen.ts `absenderMachteWeiter`). Nur dieser Absender.
          // 28.10.2026: der Kurzstand vom SERVER (server/schmied-kurzstand.ts) ist kein „er macht weiter“ — seine offenen Fragen bleiben.
          else if (entwickler && (m as { kurzstand?: unknown }).kurzstand !== true) {
            const weiterHier = weiterAus((m as { weiter?: unknown }).weiter, frage);
            if (weiterHier === true || infoAus((m as { info?: unknown }).info, frage, weiterHier) === true)
              offeneFragen.absenderMachteWeiter(vomSchmied ? "schmied" : absender.entwicklerId, ts);
          }
          if (vomSchmied)
            schmiedBeitragKam(
              m.content,
              m.createdAt,
              nimmEntwicklerBlase(),
              weiterLive,
              schmiedGanzGilt((m as { stueckweise?: unknown }).stueckweise),
              haltAnVesperaGilt({ an: adressaten.an, frage, mikroWieBisher: (m as { mikroWieBisher?: unknown }).mikroWieBisher }),
              infoAus((m as { info?: unknown }).info, frage, weiterLive) === true,
              schmiedBeitragKennung(m.sessionId, m.createdAt),
              schmiedFragtMartin({
                text: m.content,
                frage,
                weiter: (m as { weiter?: unknown }).weiter,
                info: (m as { info?: unknown }).info,
                an: adressaten.an,
                erwartet: (m as { erwartet?: unknown }).erwartet,
              }),
              // 22.10.2026: an Zimmermann/Hüter (nicht Vespera — die hat ihren Halt oben; nicht mit Martin als zweitem Adressaten).
              // Eine Ja/Nein-Karte (`frage`) gilt immer Martin — dann wie bisher.
              frage === null && schmiedAnAndereEntwickler(adressaten.an, adressaten.sekundaer),
              kurzstandOhneWarten({ kurzstand: (m as { kurzstand?: unknown }).kurzstand, weiter: (m as { weiter?: unknown }).weiter }),
            );
          // 19.10.2026: Zimmermann/Hüter — im Gespräch (Server: `imGespraech`) mit eigener Stimme, sonst nur Blase und Knopf.
          const zusatzId = entwickler && istZusatzId(absender.entwicklerId) ? absender.entwicklerId : null;
          if (zusatzId) {
            const zWeiter = weiterAus((m as { weiter?: unknown }).weiter, null) === true;
            zusatzBeitragKam(zusatzId, m.content, m.createdAt, nimmEntwicklerBlase(), {
              weiter: zWeiter,
              info: infoAus((m as { info?: unknown }).info, null, zWeiter) === true,
              ganz: schmiedGanzGilt((m as { stueckweise?: unknown }).stueckweise),
              kennung: schmiedBeitragKennung(m.sessionId, m.createdAt),
              imGespraech: (m as { imGespraech?: unknown }).imGespraech === true,
              erwartet: (m as { erwartet?: unknown }).erwartet === true,
              ziel: zielLive,
            });
          }
        }
        // Ticket 2660: die hier neu gezeichneten Karten — für die Rückfrage oben.
        const neueKarten = new Map<string, HTMLElement>();
        // Pre.172: Tool-Cards aus dem Cross-Device-SSE-Stream rendern,
        // damit der zweite Tab die Bilder/Cards live ohne Reload sieht.
        if (Array.isArray(m.toolCards)) {
          for (const c of m.toolCards) {
            replayingTs = ts;
            kartenKanal = kanal;
            const gezeichnet = renderToolCard({
              tool: String(c.tool ?? ""),
              callId: String(c.callId ?? ""),
              isError: Boolean(c.isError),
              result: c.result,
              anzeige: c.anzeige,
            });
            kartenKanal = undefined;
            if (gezeichnet) neueKarten.set(String(c.callId ?? ""), gezeichnet);
            pushHistory({
              kind: "tool",
              card: {
                tool: String(c.tool ?? ""),
                callId: String(c.callId ?? ""),
                isError: Boolean(c.isError),
                result: c.result,
                anzeige: c.anzeige,
              },
              ts,
            });
            replayingTs = null;
          }
        }
        // Ticket 2660: fragt diese Zeile etwas (Hintergrundlauf oder
        // ask_confirm), bleibt die Frage oben stehen.
        heftOffeneRueckfrageAn([m], neueKarten);
        } finally {
          replayingTs = null;
          kartenKanal = undefined;
        }
        replayingTs = null;
        // Scroll nur autorollen wenn der User schon am unteren Ende war —
        // sonst stören wir gerade laufendes Hochscrollen / Re-read; dann
        // zählt der Knopf die neue Nachricht (Ticket 2730: ein Zustand).
        fuehreChatNach();
        notifyNewMessage();
      },
      onError: ({ willRetryInMs }) => {
        console.warn(`[chat-stream] disconnected, retry in ${willRetryInMs}ms`);
      },
    });
  } catch (err) {
    console.warn("[chat-stream] init failed:", err);
  }
})();

/*
 * 23.09.2026, Ticket 2511: Der Pre.113-Abgleich stand hier (fetch, dann
 * {...lokal, ...server}, mit einem Rennwächter, der bei jedem Start den
 * Gerätestand hochschob). Er ist in lib/einstellungen-abgleich.ts aufgegangen
 * und läuft nach dem Anmelden — mit Zeitstempeln statt Ankunftsreihenfolge.
 *
 * Wie jede Änderung ausging, meldet der Abgleich als Ereignis. Die
 * Einstellungsseite sagt es selbst in ihrer Statuszeile; überall sonst (Mikro-
 * Fenster, Modell-Chip, Plus-Menü) sagt es dieser Hinweis — sichtbar statt
 * still verschluckt.
 */
window.addEventListener("voice-v8:settings-sync", (ev) => {
  const d = (ev as CustomEvent<SpeicherErgebnis & { nachgereicht?: boolean }>).detail;
  if (!d) return;
  if (d.zustand === "gespeichert" && d.nachgereicht) {
    showToast("Einstellungen nachgereicht — jetzt auf allen Geräten.", "success");
    return;
  }
  if (!d.text || document.body.dataset.activeView === "settings") return;
  if (d.zustand === "wartet" || d.zustand === "fehler" || d.zustand === "ueberholt") {
    showToast(d.text, d.zustand === "ueberholt" ? "info" : "warning", 6000);
  }
});

// --- Audio-Meter + Visualizer --------------------------------------------

const userMeter = new AudioMeter();
const botMeter = new AudioMeter();

// Pre.123: Live-Speaking-Indikator. LiveKit-UserStateChanged feuerte in
// unserem Setup nicht zuverlässig (siehe pre.122). Pragmatik: aus dem
// Mic-Audio-Pegel direkt mit Hysterese-Schwellwerten erkennen ob der
// User aktiv spricht. KEIN Bubble (das war der pre.105-Bug), nur die
// Mic-Button-Klasse `is-recording` und der State-Pill-Hint.
const SPEAK_ON_THRESHOLD = 0.04;
const SPEAK_OFF_THRESHOLD = 0.018;
const SPEAK_OFF_GRACE_MS = 700;
// P1.8 (Audit): erst ≥1s ZUSAMMENHÄNGENDE Sprache zählt als echte
// User-Sprache für den SilenceWatcher — Huster/Klicks lösen nichts aus.
const SPEAK_CONFIRM_MS = 1000;
// P3.4 (Audit): Liefert das Mikro >5s AUSSCHLIESSLICH exakt Pegel 0, ist es
// physikalisch tot (suspendierter AudioContext / OS-Mute geben exakt 0 —
// echtes Raumrauschen nie). Dann wird der Banner-Punkt grau. KEIN Banner,
// kein Toast — Mathematik statt Heuristik (pre.831-Lektion).
const MIC_DEAD_AFTER_MS = 5000;
let zeroLevelSince = 0;
let micDeadShown = false;
let lastAboveOn = 0;
let aboveSpanStart = 0;
let speakingActive = false;

/**
 * P1.8 (Audit): Der Live-Banner ist im Anruf das EINZIGE auf jedem
 * Bildschirm sichtbare Element — sein Label sagt ehrlich, ob das Mikro
 * gerade Ton aufnimmt ("reagiert auf nichts, sehe nicht ob er recordet").
 */
function updateLiveBannerLabel(): void {
  const label = document.querySelector("#voice-live-banner .voice-live-label");
  if (!label) return;
  label.textContent = muted
    ? "Stumm"
    : micDeadShown
      ? "Mikro liefert nichts — einmal tippen"
      : speakingActive
        ? "Mikro nimmt auf …"
        : "Verbunden";
}

function setMicLevel(level: number): void {
  // P3.5 (Audit): --mic-level auf dem muteBtn hatte NULL CSS-Konsumenten —
  // der Live-Banner-Punkt ist die eine Pegel-Anzeige (P1.8).
  const liveBanner = document.getElementById("voice-live-banner");
  liveBanner?.style.setProperty("--mic-level", level.toFixed(3));
  const now = Date.now();
  // P3.4: Exakt-0-Detektor (nur im aktiven, nicht stummen Lauschen).
  if (level < 1e-7) {
    if (zeroLevelSince === 0) zeroLevelSince = now;
  } else {
    zeroLevelSince = 0;
  }
  const micDead =
    connected &&
    !muted &&
    currentState === "listening" &&
    zeroLevelSince > 0 &&
    now - zeroLevelSince > MIC_DEAD_AFTER_MS;
  if (micDead !== micDeadShown) {
    micDeadShown = micDead;
    liveBanner?.classList.toggle("voice-live--micdead", micDead);
    updateLiveBannerLabel();
  }
  if (level >= SPEAK_ON_THRESHOLD) {
    if (aboveSpanStart === 0) aboveSpanStart = now;
    if (now - aboveSpanStart >= SPEAK_CONFIRM_MS) client.noteLocalSpeech();
    lastAboveOn = now;
    if (!speakingActive) {
      speakingActive = true;
      refs.muteBtn.classList.add("is-recording");
      updateLiveBannerLabel();
      if (currentState === "listening") {
        // P3.5: ehrlich — der Pegel beweist nur, dass das Mikro AUFNIMMT,
        // nicht dass der Bot versteht. (data-user-speaking hatte keinen
        // CSS-Konsumenten und ist entfernt.)
        refs.state.textContent = "Mikro aktiv";
      }
    }
  } else {
    aboveSpanStart = 0;
    if (
      speakingActive &&
      level < SPEAK_OFF_THRESHOLD &&
      now - lastAboveOn > SPEAK_OFF_GRACE_MS
    ) {
      speakingActive = false;
      refs.muteBtn.classList.remove("is-recording");
      updateLiveBannerLabel();
      if (currentState === "listening") {
        refs.state.textContent = STATE_LABELS.listening;
      }
    }
  }
}

// Pre.105: ENDLÖSUNG für die Geister-Skeleton-Bubbles. Bot-Skeleton wird
// NICHT mehr aus Audio-Pegel erstellt. Der Bot streamt immer Text bevor
// Audio sinnvoll ist — onBotMessage erstellt die Bubble als erstes Token
// kommt. Dadurch entfällt die ganze Skeleton-Race-Logik. Falls eine
// Bot-Bubble noch is-recording-Klasse hat (legacy), wird sie ohne Wave
// gerendert (CSS macht das harmlos).
function clearBotSilenceTimer(): void {
  // No-op (pre.105) — Watchdog-Timer entfernt, weil keine Skeleton mehr.
}

function setBotLevel(level: number): void {
  // Wenn liveBotBubble existiert (durch onBotMessage), aktualisieren wir
  // nur den --wave-level für eine eventuelle CSS-Animation. Wir erstellen
  // KEINE neue Bubble aus Audio-Pegel.
  if (liveBotBubble) {
    liveBotBubble.style.setProperty("--wave-level", level.toFixed(3));
  }
}

/**
 * Erzeugt eine "Skeleton"-Bubble mit Wave-Visualizer. Wird angezeigt
 * solange noch kein Transkript-Text da ist. Sobald Text reinkommt,
 * wird der Visualizer gegen den Text ausgetauscht.
 */
function fillBubbleWithText(bubble: HTMLElement, text: string): void {
  bubble.classList.remove("is-recording");
  bubble.textContent = text;
  // Ticket #168: textContent= hat das von insertOrdered gesetzte Zeitlabel
  // gelöscht → wiederherstellen, damit die Uhrzeit auch live (vor Reload) steht.
  restoreBubbleTs(bubble);
}

// --- Voice-Client ---------------------------------------------------------

let liveUserBubble: HTMLElement | null = null;
let liveBotBubble: HTMLElement | null = null;

// Pre.834: iOS-Mikrofon-Sperre menschlich erklären — der rohe Browser-
// Text („not allowed by the user agent…") ist unverständlich.
const MIC_BLOCKED_TEXT =
  "Mikrofon ist von iOS für diese App GESPERRT (einmal Nicht-erlauben getippt). " +
  "So hebst du es auf: iPhone-Einstellungen → App-Liste → Eintrag dieser Web-App → Mikrofon erlauben — danach App neu öffnen.";
function humanizeMediaError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/MIC_BLOCKED|not allowed|denied|NotAllowed/i.test(msg)) {
    return MIC_BLOCKED_TEXT;
  }
  return msg;
}

// Pre.825: Sicherungs-Aufnahme — hält unbestätigte Sprache lokal vor und
// reicht sie bei Verbindungsverlust nach (nichts geht mehr verloren).
const voiceGuard = new VoiceGuard();
// Pre.831: Der pre.827-„Stall-Wächter" ist ERSATZLOS entfernt — Gemini
// liefert Transkripte in ~30s-PAKETEN, der 12s-Alarm feuerte deshalb
// mitten in funktionierende Sätze (Martins „wie oft soll das noch
// kommen?!"). Feedback leisten jetzt: Live-Mitschrift (pre.830),
// Sicherungs-Punkt (pre.825) und die ehrliche Nachreichung bei echtem
// Verlust. KEINE Heuristik-Banner mehr auf diesem Pfad.
// Ticket #182: Der Punkt unterscheidet jetzt ehrlich „nimmt auf" (grün) von
// „aufgenommen, aber noch nicht vom Server bestätigt" (amber) — der grüne
// Punkt allein hatte Sicherheit vorgegaukelt, obwohl Sprache hängen blieb.
const GUARD_DOT_TITLES: Record<string, string> = {
  live: "Sicherungs-Aufnahme aktiv — nichts geht verloren",
  pending: "Aufgenommen — warte noch auf Bestätigung vom Server",
};
voiceGuard.onState = (state) => {
  const dot = document.getElementById("guard-dot");
  if (!dot) return;
  dot.hidden = state === "off";
  if (state !== "off") dot.dataset.state = state;
  dot.title = GUARD_DOT_TITLES[state] ?? "";
};
// Ticket #178/#182: aus einem toten Tab (iOS-Hintergrund, Crash, Reload) liegen
// gebliebene Sicherung beim Start nachreichen — sonst ginge der letzte Satz weg.
void replayPendingGuard().then((ok) => {
  if (ok)
    showToast(
      "Eine zuvor unterbrochene Aufnahme wurde nachgereicht.",
      "success",
      4000,
    );
});

/*
 * Vespera legt selbst auf. (23.09.2026, Ticket 2521)
 *
 * Das Werkzeug `anruf_beenden` im Anruf gibt nur das Signal (seine Karte).
 * Aufgelegt wird hier, über denselben Weg wie der Knopf — aber erst, wenn
 * die Verabschiedung zu Ende gesprochen ist. Sagt Martin danach noch etwas,
 * bleibt die Verbindung. Die Regel steht in shared/anruf-beenden.ts.
 *
 * ⚠️ 28.09.2026 (Ticket 2718): das Signal kommt jetzt beim AUFRUF
 *    (agent/anruf-beenden.ts), nicht mehr nach Vesperas Antwort. Das
 *    Schluss-Transkript seiner eigenen Abschiedsworte reist langsamer (der
 *    Agent schickt es erst nach einem Textstrom) und kommt darum oft NACH
 *    dem Signal an. Es ist kein Nachsatz: `laufend` merkt sich die Blase,
 *    die beim Signal noch offen war, und nur eine andere zählt.
 */
let auflegenPlan: {
  seit: number;
  stillSeit: number;
  nutzerSprach: boolean;
  takt: number;
  laufend: HTMLElement | null;
} | null = null;

function planeAuflegen(): void {
  if (auflegenPlan || !connected) return;
  const jetzt = Date.now();
  const plan = {
    seit: jetzt,
    stillSeit: currentState === "speaking" ? 0 : jetzt,
    nutzerSprach: false,
    takt: 0,
    laufend: liveUserBubble,
  };
  plan.takt = window.setInterval(() => {
    const t = Date.now();
    if (currentState === "speaking") plan.stillSeit = 0;
    else if (!plan.stillSeit) plan.stillSeit = t;
    const urteil = jetztAuflegen(
      plan.stillSeit ? t - plan.stillSeit : 0,
      t - plan.seit,
      plan.nutzerSprach,
    );
    if (urteil === "warten" && connected) return;
    window.clearInterval(plan.takt);
    auflegenPlan = null;
    meldeDiagnose(`[anruf] vespera-legt-auf ${urteil} nach=${t - plan.seit}ms`);
    if (urteil === "auflegen" && connected) void handleHangup();
  }, 200);
  auflegenPlan = plan;
}

const client = createVoiceClient({
  onState: setState,
  onUserTranscript: (text, isFinal) => {
    // Pre.825: angekommenes Transkript = Sprache ist erfasst → Puffer leeren.
    if (isFinal) voiceGuard.noteTranscript();
    if (!liveUserBubble) {
      liveUserBubble = document.createElement("div");
      liveUserBubble.className = "bubble user";
      insertOrdered(liveUserBubble, Date.now());
    }
    fillBubbleWithText(liveUserBubble, text);
    // #129: nur nachführen, wenn der User schon unten ist — sonst reißt es
    // ihn beim Lesen älterer Nachrichten nach unten (Martins Beschwerde:
    // „springt immer wieder runter"). Seit Ticket 2730 ein Zustand, den nur
    // sein Scrollen ändert, nicht mehr ein Maß im Moment des Einfügens.
    fuehreChatNach();
    if (isFinal) {
      // Pre.280: STT-Junk-Filter. Gemini Live transkribiert Stille/Atmer
      // oft als sinnlose Fragmente ("." / "I" / "で 、 それ" / "요거 요거")
      // die als eigene User-Bubbles erschienen und den Chat zumüllten.
      // Wenn das Final-Transcript Junk ist → Bubble entfernen, NICHT in
      // History pushen, Bot bekommt es eh schon gar nicht weil zu kurz.
      if (isJunkTranscript(text)) {
        liveUserBubble.remove();
        liveUserBubble = null;
        return;
      }
      // Pre.762: Doppel-User-Bubble-Dedupe. Die Bot-Seite hat das schon
      // (Pre.353/447), die User-Seite fehlte → Gemini-Live-Doppel-Finals
      // erzeugten doppelte User-Zeilen. Vergleich gegen die History-Daten
      // (kein Zeitstempel-Müll wie bei textContent).
      const lastH = history[history.length - 1];
      if (
        lastH?.kind === "user" &&
        (lastH.text ?? "").trim() === text.trim()
      ) {
        liveUserBubble.remove();
        liveUserBubble = null;
        return;
      }
      // Ticket 2521: wer nach dem Abschied noch spricht, hat nicht aufgelegt.
      // Ticket 2718: erst hier, nach Rauschen und Doppel — und nicht der
      // Schluss der Äußerung, die beim Signal noch lief (siehe auflegenPlan).
      if (auflegenPlan && liveUserBubble !== auflegenPlan.laufend) auflegenPlan.nutzerSprach = true;
      pushHistory({ kind: "user", text, ts: Date.now() });
      liveUserBubble = null;
    }
  },
  onBotMessage: (text, isFinal) => {
    // Pre.353: Doppel-Bubble-Dedupe (vor jeder neuen Bot-Bubble).
    // Manche Voice-Pipelines (Realtime + Pipeline-Mix) emittieren EIN Reply
    // ZWEIMAL als isFinal — erst eine kürzere Stub, dann der lange voll.
    // Effekt im Chat: "Bot: Sie hat einen Titel..." gefolgt von
    // "Bot: Sie hat einen Titel ... Einschätzung?". Pre.217-Akkumulator
    // fängt das nicht ab weil die Final-Events zu weit auseinander liegen.
    //
    // Fix: wenn isFinal kommt und es eine vorherige Bot-Bubble (nicht
    // die aktuelle liveBotBubble) gibt deren Text Präfix des neuen ist
    // → alte entfernen. Wenn neue Bubble Präfix der alten ist
    // (kommt sehr selten, Stub-nach-Voll) → neue skippen.
    if (isFinal && text.trim().length > 0) {
      // Pre.784: Markdown-agnostische Normalisierung. Der Vergleich lief
      // vorher ROH (mit **fett**/Listen-Sternen) gegen GERENDERTES
      // textContent (ohne) → Präfix-Match scheiterte, Doppel-Bubbles
      // blieben stehen ("steht doppelt da, verschwindet nicht immer").
      const mdNorm = (s: string): string =>
        s
          .replace(/[*_~`#>]/g, "")
          .replace(/\s+/g, " ")
          .trim();
      const newT = text.trim();
      const newNorm = mdNorm(newT);
      const allBot = Array.from(
        refs.chat.querySelectorAll<HTMLElement>(".bubble.bot"),
      );
      // Pre.447: Suche die letzten BIS-ZU-3 Bot-Bubbles statt nur die
      // letzte — bei Gemini-Live kommen manchmal mehrere isFinal-Events
      // schnell hintereinander, der ECHTE Duplicate kann ein paar Schritte
      // zurück liegen.
      const candidates = allBot.filter((b) => b !== liveBotBubble).slice(-3);
      let dedupedNew = false;
      for (const prev of candidates) {
        const prevT = mdNorm(bubbleReplyText(prev));
        if (prevT.length <= 8) continue;
        // Pre.447 Ticket #3 Fix: IDENTISCHE Texte werden auch dedupliziert.
        // Vorher griff nur Präfix-Match → identische Bubbles blieben beide.
        if (newNorm === prevT) {
          // Identisch → neue komplett skippen
          if (liveBotBubble) {
            liveBotBubble.remove();
            liveBotBubble = null;
          }
          return;
        }
        if (newNorm.startsWith(prevT) && newNorm.length > prevT.length) {
          // Alte Stub-Bubble ist Präfix der neuen → entfernen.
          prev.remove();
          try {
            const lastH = history[history.length - 1];
            if (
              lastH?.kind === "bot" &&
              mdNorm(lastH.text ?? "") === prevT
            ) {
              history.pop();
              saveHistory(history);
            }
          } catch {
            /* fail-quiet */
          }
          break;
        } else if (prevT.startsWith(newNorm) && prevT.length > newNorm.length) {
          // Neue Bubble ist verkürzter Stub einer bereits gerenderten
          // langen → komplett skip.
          if (liveBotBubble) {
            liveBotBubble.remove();
            liveBotBubble = null;
          }
          dedupedNew = true;
          break;
        }
      }
      if (dedupedNew) return;
    }
    if (!liveBotBubble) {
      // Race-Fix vereinfacht: Wir brauchen keinen User-Skeleton mehr
      // einfügen, weil insertOrdered die Reihenfolge per Timestamp regelt.
      // Wenn STT-final später kommt, rutscht die User-Bubble automatisch
      // an die richtige Stelle (vor dem Bot-Bubble, sofern sie früher
      // begonnen hat).
      liveBotBubble = document.createElement("div");
      liveBotBubble.className = "bubble bot";
      insertOrdered(liveBotBubble, Date.now());
      // Pre.162: Avatar SOFORT prepend — vorher erst bei isFinal über
      // fillBotBubble. Während des Streams kein Avatar = visuelles Flackern.
      prependBotAvatar(liveBotBubble);
      // Ticket 2730: liest er weiter oben, zeigt der Knopf die neue Antwort.
      notifyNewMessage();
    }
    // Pre.193: data-text-Key fortlaufend updaten, damit SSE-Echo-Dedupe
    // die Bubble auch während des Streams identifizieren kann.
    if (liveBotBubble) liveBotBubble.dataset.text = text.slice(0, 80).trim();
    // Während des Streams: textContent (schnell). Bei isFinal: Markdown
    // + Play-Button rendern (langsamer aber schöner — wird nur einmal pro
    // Reply ausgeführt).
    if (isFinal) {
      fillBotBubble(liveBotBubble, text);
      if (autoPlayEnabled && mode !== "voice") void playTts(text);
    } else {
      fillBubbleWithText(liveBotBubble, text);
    }
    // #129: Folge-Scroll nur wenn der User unten ist — so bleibt
    // Hochscrollen während der Bot spricht möglich (Martins Beschwerde: Chat
    // springt beim Sprechen runter). Die Dedup-Logik oben entfernt und hängt
    // Blasen an; seit Ticket 2730 ändert das den Zustand nicht mehr.
    fuehreChatNach();
    if (isFinal) {
      // Ticket 2397: der Stempel der Blase, nicht ein neuer — sonst kippt
      // die Reihenfolge gegen die Werkzeug-Karten beim Neuladen.
      pushHistory({ kind: "bot", text, ts: blasenStempel(liveBotBubble) });
      liveBotBubble = null;
      /* botFrameStreak removed pre.105 */
      clearBotSilenceTimer();
    }
  },
  onError: (err) => {
    // Pre.644: echter Fehler → kein nahtloser Refresh mehr, normal/laut.
    preemptiveRefreshActive = false;
    // Ticket 2728: ein verbrauchter/abgelaufener Anruf von Vespera ist kein
    // Fehler, sondern ein ruhiger Hinweis (native Huelle meldet ihn hier).
    if (err.message === ANRUF_VORBEI) {
      mode = "chat";
      setState("idle");
      void resumeRecorderIfPausedForCall();
      showToast(ANRUF_NICHT_MEHR_GUELTIG, "info", 6000);
      return;
    }
    // Hoechstdauer erreicht: der Anruf ist zu Ende, ruhig sagen warum.
    if (err.message === ANRUF_HOECHSTDAUER) {
      connected = false;
      syncVoiceModeExclusivity();
      mode = "chat";
      renderDock(currentState);
      void resumeRecorderIfPausedForCall();
      showToast(ANRUF_HOECHSTDAUER_TEXT, "info", 8000);
      return;
    }
    // P1.8 (Audit): Fehler in Menschensprache — die iOS-Mikro-Sperre
    // bekommt ihre Anleitung statt "not allowed by the user agent".
    const human = humanizeMediaError(err);
    appendBubble("system", `Fehler: ${human}`);
    pushHistory({
      kind: "system",
      text: `Fehler: ${human}`,
      ts: Date.now(),
    });
    if (/MIC_BLOCKED|not allowed|NotAllowed/i.test(err.message)) {
      showStatusBanner({
        kind: "error",
        message: MIC_BLOCKED_TEXT,
        owner: "update",
      });
      return;
    }
    if (/Verbindung zum Bot verloren/.test(err.message)) {
      // P2.4 (Audit): Reconnect endgültig aufgegeben → Dock ehrlich in den
      // Chat-Modus zurück (vorher blieben inerte Voice-Knöpfe stehen).
      connected = false;
      syncVoiceModeExclusivity();
      mode = "chat";
      renderDock(currentState);
    }
    // Pre.275: Bei Session-Crashes (code 1011, Internal error, Operation
    // not implemented, Deadline expired) AUCH den Crash-Banner zeigen —
    // System-Bubble allein ist zu leise.
    const msg = err.message;
    const isCrash =
      /1011|Internal error|Operation is not implemented|Deadline expired|Requested entity was not found|session-close|google_realtime/i.test(
        msg,
      );
    // Pre.377/381: Gemini-Provider-Drops sind bekanntes Anbieter-Verhalten,
    // nicht unser Bug. Statt "Bot abgestürzt — Reconnect probieren?"
    // automatisch reconnecten nach kurzer Wartezeit.
    // Pre.381: Pattern erweitert um "session-close" / "Session beendet" —
    // User-Report 2026-05-18 zeigte dass diese Texte vorher nicht matchten.
    const isProviderDrop =
      /google_realtime|invalid argument|1007|Deadline expired|disconnection soon|sessionResumption|session-close|Session beendet|session beendet/i.test(
        msg,
      );
    if (isProviderDrop) {
      // Soft-Notice statt Crash, dann auto-reconnect.
      showCrashBanner(
        "Verbindung kurz unterbrochen — versuche neu zu verbinden …",
        { silent: true, autoReconnect: true },
      );
      setTimeout(() => {
        try {
          // P1.6 (Audit): In-place-Reconnect statt connectBtn.click() —
          // der Klick lief durch den kompletten Connect-Pfad (Klingelton,
          // doppelte Rooms); forceReconnect nutzt die Reconnect-Maschine.
          client.forceReconnect("provider-drop");
          refs.banner.hidden = true;
        } catch {
          /* user can click manually if auto fails */
        }
      }, 1200);
    } else if (isCrash) {
      showCrashBanner(`Bot abgestürzt: ${msg}. Reconnect probieren?`);
    }
  },
  onToolCard: (card) => {
    // Pre.339: Wenn eine Tool-Card ankommt, ist der zugehörige Tool-Lauf
    // fertig — entferne ggf. die Live-Progress-Bubble.
    closeToolProgressBubble(card.tool);
    // Ticket 2521: das Auflege-Signal ist keine Karte für den Verlauf.
    if (card.tool === ANRUF_BEENDEN) {
      planeAuflegen();
      return;
    }
    dispatchToolCard(card);
  },
  onToolProgress: (p) => {
    // Pre.339: Live-Status während lang laufender Tools (claude_task_server,
    // book_compile, video-Analyse). Statt 30s+ schwarzes Loch zeigen wir
    // eine pulsierende System-Bubble "🤖 claude_task_server läuft seit 15s …".
    if (p.phase === "done") {
      closeToolProgressBubble(p.tool);
      return;
    }
    showOrUpdateToolProgressBubble(p);
  },
  onTruthWarning: (warn) => {
    renderTruthWarning(warn);
    pushHistory({ kind: "warn", warn, ts: Date.now() });
  },
  onSttReject: (info) => {
    renderSttRejectHint(info);
  },
  // Pre.123: onUserState aus pre.122 entfernt — LiveKit-UserStateChanged
  // feuerte nicht zuverlässig in unserem Setup. Live-Indikator läuft
  // jetzt direkt aus dem Mic-Audio-Pegel in setMicLevel (Hysterese).
  onReconnectAttempt: (attempt, max) => {
    // Echter Reconnect-Versuch (nicht der geplante Refresh) → laut, und den
    // Seamless-Modus abschalten falls er noch lief (jetzt ist's echte Not).
    preemptiveRefreshActive = false;
    // Pre.825: was bis zum Abriss gesprochen wurde, sofort sichern.
    void voiceGuard.flushIfLost(`reconnect-${attempt}`).then((ok) => {
      if (ok)
        showToast(
          "Deine letzte Aussage wurde gesichert und nachgereicht.",
          "success",
          3500,
        );
    });
    showToast(
      `Verbindung wackelt — verbinde neu (${attempt}/${max})…`,
      "warning",
    );
  },
  onReconnected: () => {
    document
      .getElementById("voice-live-timer")
      ?.classList.remove("timer-refresh-soon");
    showToast("Verbindung wieder da", "success", 2000);
  },
  // Pre.644: GEPLANTER Sitzungs-Refresh läuft gleich (vor dem ~10min-
  // Provider-Cut). Nahtlos — nur eine ruhige Pille als Heads-up, damit man
  // sich auf die kurze Pause einstellen kann, KEIN Crash-/Wackel-Banner.
  onPreemptiveRefresh: (forced) => {
    preemptiveRefreshActive = true;
    // Pre.769: Auffrischung läuft jetzt → Amber-Puls am Timer entfernen.
    document
      .getElementById("voice-live-timer")
      ?.classList.remove("timer-refresh-soon");
    showToast(
      forced
        ? "Sitzung wird aufgefrischt — einen Moment, alles bleibt erhalten"
        : "Sitzung kurz aufgefrischt — alles bleibt erhalten",
      "info",
      2600,
    );
  },
  // Pre.769: ~1 Min VOR der Auffrischung — dezenter Amber-Puls am Timer
  // (lautlos, kein Toast). Heads-up, ohne den Gesprächsfluss zu stören.
  onPreemptiveWarn: () => {
    document
      .getElementById("voice-live-timer")
      ?.classList.add("timer-refresh-soon");
  },
  // Pre.444: Connection-Quality. UI-Indikator (Balken-Symbol via
  // CSS-Class auf data-quality) + 1×-Toast bei dauerhaft schwacher
  // Verbindung.
  onConnectionQuality: (snapshot) => {
    document.body.dataset.voiceQuality = snapshot.local;
  },
  // Pre.448: prominenter Status-Banner statt nur Toast — User-Forderung
  // "Brauche IMMER Feedback wenn was nicht stimmt".
  onPoorConnection: () => {
    showStatusBanner({
      kind: "warning",
      message:
        "Schwache Verbindung — vielleicht ins WLAN wechseln. (Versuche mich selbst zu retten…)",
      autoDismissMs: 8000,
    });
  },
  onAudioStuck: () => {
    // Banner + Toast + globaler Tap-Listener für Recovery
    showStatusBanner({
      kind: "warning",
      message:
        "Bot spricht — aber kein Ton bei dir? Tippe einmal auf den Bildschirm, Audio wird freigegeben.",
      actionLabel: "Audio aktivieren",
      actionHandler: () => {
        const ctx = getAudioCtx();
        if (ctx?.state === "suspended") {
          void ctx.resume().catch(() => undefined);
        }
        document.querySelectorAll("audio").forEach((el) => {
          const a = el;
          if (a.paused) void a.play().catch(() => undefined);
        });
      },
    });
    const unblockOnce = (): void => {
      document.removeEventListener("click", unblockOnce);
      document.removeEventListener("touchend", unblockOnce);
      const ctx = getAudioCtx();
      if (ctx?.state === "suspended") {
        void ctx.resume().catch(() => undefined);
      }
      document.querySelectorAll("audio").forEach((el) => {
        const audio = el;
        if (audio.paused) {
          void audio.play().catch(() => undefined);
        }
      });
    };
    document.addEventListener("click", unblockOnce, { once: false });
    document.addEventListener("touchend", unblockOnce, { once: false });
  },
  onModulesList: () => {
    // SettingsView re-render damit die neue Liste sichtbar wird,
    // ohne dass der User den Tab schließen und neu öffnen muss.
    if (settingsView) {
      settingsView?.destroy();
      settingsView = new SettingsView(settingsViewContainer);
    }
    // Pre.344: ExamplesView re-render damit die Module-Statusfarben
    // (aktiv/inaktiv) sofort stimmen.
    window.dispatchEvent(new CustomEvent("voice-v8:modules-updated"));
  },
  onLocalAudio: (track) => {
    if (track) {
      userMeter.attach(track, setMicLevel);
      // Pre.825: Sicherungs-Aufnahme läuft, solange das Mikro offen ist.
      voiceGuard.start(track);
    } else {
      userMeter.detach();
      setMicLevel(0);
      // Mikro weg (Auflegen) → letzten unbestätigten Rest retten, dann aus.
      void voiceGuard.flushIfLost("auflegen").then((ok) => {
        if (ok)
          showToast(
            "Nicht übertragene Sprache wurde gesichert und nachgereicht.",
            "success",
            3500,
          );
      });
      voiceGuard.stop();
    }
  },
  onBotAudio: (track) => {
    if (track) {
      // Skeleton wird LAZY in setBotLevel angelegt sobald echter Audio-Pegel
      // ankommt — Track-Publish allein reicht nicht (siehe pre.90).
      botMeter.attach(track, setBotLevel);
    } else {
      botMeter.detach();
      /* botFrameStreak removed pre.105 */
      setBotLevel(0);
      if (liveBotBubble?.classList.contains("is-recording")) {
        liveBotBubble.remove();
        liveBotBubble = null;
      }
    }
  },
});

let connected = false;
let muted = false;
/**
 * Konzentrations-Modus: wenn aktiv, wird das Mic während Bot-speech
 * automatisch stumm geschaltet (lokal), damit Hintergrundgeräusche
 * (Auto, Café, Verkehr) den Bot nicht versehentlich unterbrechen.
 * Beim Wechsel zurück zu listening wird das Mic wieder eingeschaltet.
 */
let focusMode = false;
/** Tracking ob das Mic durch focusMode gerade auto-stumm ist. */
let autoMutedByFocus = false;
/**
 * App-Modus: "chat" (HTTP, kein LiveKit, rein textbasiert) oder
 * "voice" (LiveKit aktiv, Mic + TTS). Klare Trennung — nie beides
 * gleichzeitig. Chat ist Default, Voice wird durch den Anruf-Button
 * gestartet, Hangup geht zurück zu Chat.
 */
type AppMode = "chat" | "voice";
let mode: AppMode = "chat";

function isConnected(): boolean {
  return connected;
}

// --- Dock: drei Telefon-Buttons + Verbinden-Button -----------------------

let connecting = false;
/**
 * Startet einen Anruf (Voice-Mode). Baut die LiveKit-Verbindung auf,
 * Mic + Bot-TTS sind aktiv. Aus Chat-Mode kommend wechseln wir in den
 * Voice-Mode.
 */
// P1.6.3 (Audit): Der Pre.384-Timer (4-Min-Disconnect+connectBtn.click)
// ist ERSATZLOS entfernt — der einzige legitime Treiber für den geplanten
// Sitzungs-Refresh ist checkMicHealth (Pre.644) in webrtc.ts. Zwei
// konkurrierende Reconnect-Maschinen waren eine Kaskaden-Quelle.

// 05.09.2026 — EIN Besitzer der Pause. Bis heute hielt app.ts die
// Tagesaufnahme für Anruf und PTT selbst an (toggleRecorderPause, ohne
// Grund), parallel zum Gründe-Set in native-recorder.ts. Zwei Besitzer, ein
// kippender Schalter, gemerkter Zustand: Hatte der eine schon fortgesetzt,
// „setzte" der andere mit altem Wissen fort — und pausierte die laufende
// Aufnahme (Martin: „geht immer noch auf Pause, obwohl nichts rennt").
// Jetzt nennt jeder seinen Grund; wer fortsetzt, kippt nichts mehr. Die
// Nachprüfung vom 12.07. (dreimal, dann ehrlich warnen) lebt jetzt dort.
async function resumeRecorderIfPausedForCall(): Promise<void> {
  try {
    const rec = await import("./lib/native-recorder.js");
    await rec.resumeDayRecorderAfterMic("anruf");
  } catch {
    /* Recorder nicht verfuegbar — egal */
  }
}

// Ticket 271: NIE mitten in einem aktiven PTT-Zug fortsetzen (iOS-Mikro-
// Konflikt 4010 killte den nativen Recorder). Audit [19]: nie im Anruf.
// Das Ende des Zugs (ptt.ts cleanup) setzt ohnehin fort — dies ist das Netz.
async function resumeRecorderAfterPttIfNeeded(): Promise<void> {
  if (pttState !== "idle" || connected) return;
  try {
    const rec = await import("./lib/native-recorder.js");
    await rec.resumeDayRecorderAfterMic("ptt");
  } catch {
    /* Recorder nicht verfuegbar — egal */
  }
}

/**
 * 2026-07-18 (Ticket 405): haengendes connected/mode-Paar selbst heilen.
 * Ist der Anruf real schon weg (Client getrennt), das Flag aber noch auf
 * true, dann zuruecksetzen statt taub abzubrechen. Stand vorher zweimal
 * kopiert da (Anruf-Knopf, PTT) — und fehlte im Tipp-Pfad, wo jede getippte
 * Nachricht dann an "nicht verbunden" scheiterte.
 * isConnected ist optional. Fehlt die Methode, gilt der Zustand als
 * UNBEKANNT — nicht als getrennt. Nur `isConnected?.() === false` raeumt
 * das Flag. `=== true` macht undefined zum Idle, und PTT stiehlt das
 * Mikro waehrend eines nativen Anrufs.
 */
function healStaleCallFlags(): void {
  const known = client.isConnected?.();
  const clientActive = known === true;
  // 2026-07-24 (Martin: "PTT tot nach abgebrochenem Anruf, muss neu
  // starten"): Ein gestarteter und gleich gestoppter Anruf kann einen
  // Room in connecting/reconnecting-LIMBO hinterlassen — isConnected()
  // ist dann true, obwohl kein echter Anruf laeuft, und der Room haelt
  // das Mikro (PTT bekommt keins). Will die App KEINEN Anruf (nicht
  // connected, nicht gerade am Verbinden), der Client haelt aber noch
  // einen Room: HART aufraeumen. Der User-initiierte disconnect stoppt
  // auch den Auto-Reconnect.
  if (!connected && !connecting && clientActive) {
    void client.disconnect().catch(() => undefined);
    mode = "chat";
    currentState = "idle";
    return;
  }
  if (connected && known === false) {
    connected = false;
    syncVoiceModeExclusivity();
    mode = "chat";
    // 2026-07-18 (Ticket 405): currentState MUSS mit zurueck. Bleibt er
    // nach einem Anruf-Abriss auf einem Nicht-idle-Wert, gilt
    // isInVoiceCall weiter als wahr — Bot-Antworten aus dem SSE-Kanal
    // wuerden STILL verworfen (Echo-Drop weiter unten).
    currentState = "idle";
  }
}

async function handleConnect(): Promise<void> {
  // Pre.139: Wenn der User nicht auf der Chat-View ist, springt der
  // Klick auf den Anruf-Button zuerst zurück zum Chat-View. Das war
  // der Wunsch: "Schließen-Element" um zu Voice zurückzukehren.
  if (
    document.body.dataset.activeView &&
    document.body.dataset.activeView !== "chat"
  ) {
    switchToTab("chat");
    return;
  }
  // 2026-06-30 (Martin): Selbstheilung — s. healStaleCallFlags().
  healStaleCallFlags();
  // Reentrancy-Guard
  if (connecting || connected) return;
  // 2026-07-02 (Martin): laufende Alltag-Aufnahme und LiveKit-Anruf kaempfen
  // ums Mikrofon (Audio-Engine-Fehler 4010). Aufnahme fuer die Dauer des
  // Anrufs pausieren, nach dem Auflegen automatisch fortsetzen.
  try {
    const rec = await import("./lib/native-recorder.js");
    await rec.pauseDayRecorderForMic("anruf");
  } catch {
    /* Recorder nicht verfuegbar — egal */
  }
  connecting = true;
  refs.connectBtn.disabled = true;
  mode = "voice";
  client.setBotAudioMuted(false);
  // 2026-07-12 (Martin, Anruf stumm "kein User-Audio-Track"): das PTT-Warm-
  // Mikro (haelt den getUserMedia-Stream bis zu 90s nach jeder Sprachausgabe)
  // BLOCKIERT den Anruf-Mikro-Zugriff -> LiveKit bekommt ein stummes Mikro.
  // Vor dem Verbindungsaufbau freigeben, damit der Anruf den Mikro allein hat.
  releaseWarmMic("anruf-start");
  /*
   * ⚠️ 22.09.2026 — die Freigabe allein genuegt nicht mehr. Sie ist ein
   *    einmaliger Griff; seit pre.3151 waermt der Browser nach jeder
   *    Antwort und bei jedem Zurueckkommen erneut vor. Der Riegel haelt,
   *    solange der Anruf laeuft.
   */
  setzeAnrufAktiv(true);
  try {
    // P2.5 (Audit): Mute-Zustand eines früheren Calls nicht vererben —
    // sonst bleibt die Sicherungs-Aufnahme für ALLE Folge-Calls tot.
    muted = false;
    voiceGuard.setMuted(false);

    // #115: iOS-PWA — Mikrofon-Erlaubnisdialog wiederholt sich nach App-Updates.
    // Ursache: setMicrophoneEnabled(true) läuft NACH room.connect() (~2–5s) und
    // damit außerhalb des iOS-User-Gesture-Fensters → iOS zeigt bei JEDEM Anruf
    // einen Bestätigungsdialog, obwohl das Mikrofon in den Einstellungen erlaubt ist.
    // Fix: getUserMedia() HIER vorab holen — der Anruf-Button-Tap ist noch frisch
    // im iOS-Gesture-Kontext. Browser merkt sich „granted" für diese Seiten-Session,
    // LiveKits setMicrophoneEnabled() findet die Berechtigung danach sofort vor.
    // Auch Frühfehler (dauerhaft gesperrt) werden jetzt SOFORT erkannt statt erst
    // nach dem 15s room.connect()-Timeout.
    // Native Capacitor: ein Mikro — das native LiveKit. Kein WebView-
    // getUserMedia, das den Track sofort stoppt (stiehlt den Anruf-Mic).
    if (!isNativePlatform() && navigator.mediaDevices?.getUserMedia) {
      try {
        const micPrewarm = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        micPrewarm.getTracks().forEach((t) => t.stop());
      } catch (micErr) {
        const micMsg =
          micErr instanceof Error ? micErr.message : String(micErr);
        if (/not allowed|denied|NotAllowed/i.test(micMsg)) {
          // Gesperrt → sofort Fehler werfen, room.connect() überspringen.
          throw new Error("MIC_BLOCKED");
        }
        // Anderer Fehler (kein Mic-Gerät) → weitermachen; LiveKit
        // meldet ihn dann mit eigenem Fehlertext.
      }
    }

    await client.connect();
    // Pre.490: Footer-Modell-Anzeige updaten — Voice-Anruf aktiv, jetzt
    // ist das Realtime-Modell wirklich in Use.
    // Pre.923: connected=true MUSS vor renderStatusBar stehen, sonst zeigt der
    // Badge (jetzt connection-aware) noch das Chat-Modell statt des Realtime.
    connected = true;
    syncVoiceModeExclusivity();
    renderStatusBar();
    muted = false;
    // Pre.471: Kein "Verbunden. Sprich los."-Bubble mehr — User-Beschwerde
    // 2026-05-23 "verbunden, aufgelegt, sprich los, das braucht kein Mensch".
    // Der prominente Voice-Live-Banner oben rechts (pulsierend + Timer)
    // ist die einzige nötige Anzeige.
    renderDock(currentState);
  } catch (err) {
    // Eine nicht verbrauchte Anruf-Kennung (z. B. MIC_BLOCKED vor dem Token-Abruf)
    // darf nicht auf Martins naechsten eigenen Anruf warten — der bekaeme 410.
    client.setzeAnrufId(null);
    if (err instanceof Error && err.message === "aborted") {
      // P2.1 (Audit): User hat während des Aufbaus aufgelegt — kein Fehler.
      mode = "chat";
      setState("idle");
      return;
    }
    // P2.4 (Audit): mode VOR setState — setState ruft renderDock, das den
    // neuen Modus sehen muss (sonst bleibt ein inertes Voice-Dock stehen).
    mode = "chat";
    // Ticket 2728: ein verbrauchter/abgelaufener Anruf von Vespera ist kein
    // Fehler, sondern ein ruhiger Hinweis.
    if (err instanceof Error && err.message === ANRUF_VORBEI) {
      setState("idle");
      void resumeRecorderIfPausedForCall();
      showToast(ANRUF_NICHT_MEHR_GUELTIG, "info", 6000);
      return;
    }
    setState("error");
    appendBubble("system", `Fehler: ${humanizeMediaError(err)}`);
    if (/MIC_BLOCKED|not allowed|NotAllowed/i.test(String(err))) {
      showStatusBanner({
        kind: "error",
        message: MIC_BLOCKED_TEXT,
        owner: "update",
      });
    }
  } finally {
    connecting = false;
    refs.connectBtn.disabled = false;
  }
}

async function handleMute(): Promise<void> {
  if (!connected) return;
  try {
    muted = !muted;
    // Pre.825: stumm = keine Sicherungs-Aufnahme (Privatsphäre).
    voiceGuard.setMuted(muted);
    await client.setMicEnabled(!muted);
    renderDock(currentState);
    updateLiveBannerLabel();
  } catch (err) {
    setState("error");
    appendBubble(
      "system",
      `Mute-Fehler: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

async function handleHangup(): Promise<void> {
  if (!connected) {
    // P2.1 (Audit): Auflegen WÄHREND des Aufbaus bricht den Aufbau ab —
    // vorher war der Knopf in "verbinde…" komplett tot.
    if (connecting) {
      connecting = false;
      mode = "chat";
      void client.disconnect();
      setState("idle");
      renderDock("idle");
      void resumeRecorderIfPausedForCall();
    }
    return;
  }
  // Hard hangup — wenn der Bot gerade redet, erst interrupten damit er
  // nicht in den ChatCtx hineinredet, dann disconnect. Beides ohne
  // Server-Roundtrip-Wartezeit für die UI: wir setzen lokal sofort um.
  try {
    if (currentState === "speaking" || currentState === "thinking") {
      await client.interruptBot();
    }
    await client.disconnect();
    connected = false;
    syncVoiceModeExclusivity();
    muted = false;
    mode = "chat";
    void resumeRecorderIfPausedForCall();
    // Pre.937 (#151-Fix): Anruf vorbei, aber der Nutzer liest typischerweise
    // weiter → Display NICHT schlafen schicken. Das nosleep-Video läuft weiter
    // (solange die App sichtbar ist); freigegeben wird erst beim Hintergrund-
    // Wechsel (visibilitychange → hidden). Kein releaseWakeLock() mehr hier.
    client.setBotAudioMuted(true);
    // Pre.490: Footer zeigt jetzt wieder das Chat-Modell (Haiku etc.)
    renderStatusBar();
    // Live-Wave-Bubbles wegräumen — sonst bleibt der Visualizer nach
    // dem Auflegen sichtbar obwohl niemand mehr spricht.
    liveUserBubble?.remove();
    liveBotBubble?.remove();
    liveUserBubble = null;
    liveBotBubble = null;
    // Pre.471: Kein "Aufgelegt."-Bubble mehr — User-Beschwerde 2026-05-23.
    // Der Voice-Live-Banner verschwindet automatisch + State-Indikatoren
    // (Hangup-Button weg, Connect-Button da) zeigen es eh.
    renderDock("idle");
  } catch (err) {
    setState("error");
    appendBubble(
      "system",
      `Fehler beim Auflegen: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

function handleFocusToggle(): void {
  focusMode = !focusMode;
  // Wenn der Bot gerade redet: sofort auto-mute anwenden.
  applyFocusMode(currentState);
  renderDock(currentState);
}

/**
 * Wendet den Focus-Mode an: wenn der Bot redet/denkt UND Focus aktiv ist,
 * Mic auto-stummschalten. Sobald Bot fertig ist, Mic wieder anschalten
 * (außer User hat manuell stumm gestellt — manuelles muted hat Vorrang).
 */
function applyFocusMode(state: VoiceState): void {
  if (!connected) return;
  const botActive = state === "speaking" || state === "thinking";
  const shouldAutoMute = focusMode && botActive;

  if (shouldAutoMute && !muted && !autoMutedByFocus) {
    autoMutedByFocus = true;
    void client.setMicEnabled(false);
  } else if (!shouldAutoMute && autoMutedByFocus) {
    autoMutedByFocus = false;
    if (!muted) {
      void client.setMicEnabled(true);
    }
  }
}

refs.connectBtn.addEventListener("click", () => {
  // 15.09.2026: Ein verborgener Knopf ist keine Grenze — es gibt Wege, die
  // ihn klicken, ohne dass man ihn sieht (Widget-Verknuepfung, Banner nach
  // einem Abbruch). Der Weg endet hier, nicht erst am 403 des Servers.
  if (!darfAnrufen()) return;
  hapticTap(35); // Martin 14.07.: sofort fuehlbare Quittung (Anrufen)
  // Pre.835: Video-Wachhalter IN der Geste starten (iOS-play()-Regel).
  startNoSleep();
  // Pre.329: AudioContext IM Click-Tick "warm anwerfen" — iOS Safari und
  // Chrome erlauben Audio nur nach echter User-Geste IM SELBEN Sync-Tick.
  primeAudio();
  // Pre.478: Zusätzlich HTMLAudioElement-Pipeline entsperren — LiveKit
  // attach() erzeugt ein <audio>-Element, dessen play() iOS sonst silent
  // ablehnt. User-Beschwerde 2026-05-25 "Bot-Text kommt aber kein Audio".
  unlockAudio();
  // Pre.336: Telefon-Ringing-Tone — läuft bis Connection steht.
  startRingingTone();
  void handleConnect();
});

// Pre.344: Anwendungsbeispiele-Klick — Beispiel-Prompt landet im Text-
// Input-Feld, User kann anpassen + abschicken. Wechselt automatisch zur
// Chat-View und schließt den Drawer auf Mobile.
// pre.848: zentrale Toast-Brücke für Module ohne direkten showToast-Zugriff
// (z.B. tickets-view → „KI-Behebung gestartet").
window.addEventListener("voice-v8:toast", (ev) => {
  const d = (
    ev as CustomEvent<{
      message?: string;
      kind?: ToastKind;
      ms?: number;
    }>
  ).detail;
  if (d?.message) showToast(d.message, d.kind ?? "info", d.ms ?? 4000);
});

/*
 * Der Wunsch-Knopf: Vespera fragt, der Mensch erzählt, daraus wird ein Ticket.
 *
 * ⚠️ Martin: *„Vespera wird ihn direkt fragen, was er für Wünsche hat, um dann
 *    mit an der Entwicklung teilzuhaben."* Also darf hier KEIN Formular
 *    aufgehen — es soll ein Gespräch sein.
 *
 *    Der Auftrag geht mit dem Datei-Marker hinaus: er erreicht das Modell
 *    vollständig, wird aber nicht als Blase gezeichnet. Von außen sieht es
 *    aus, als hätte Vespera angefangen. Dasselbe Mittel wie bei einer
 *    angehängten Datei (siehe DATEI_MARKER).
 *
 * ⚠️ `ticket_create` steht JEDEM Nutzer offen (tools/tickets.ts) und Martin
 *    sieht als Hauptnutzer alle Tickets — der Wunsch landet also ohne
 *    weiteres Zutun bei ihm.
 */
{
  // Das Zeichen kommt aus dem gemeinsamen Vorrat, nicht als zweite Zeichnung
  // ins Markup — dieselbe Regel wie beim Mikrofon.
  const b = document.getElementById("dock-wunsch");
  const lbl = b?.querySelector(".dock-btn-label");
  if (b && lbl) {
    void import("./lib/zeichen.js").then((m) => {
      lbl.insertAdjacentHTML("beforebegin", m.zeichen("wunsch", 24));
    });
  }
}
document.getElementById("dock-wunsch")?.addEventListener("click", () => {
  hapticTap(20);
  goToView("chat");
  void sendChatHttp({
    message:
      DATEI_MARKER +
      "Ich habe gerade den Wunsch-Knopf gedrückt. Frag mich jetzt von dir aus, " +
      "was ich mir für Vespera wünsche — beginne mit EINER offenen, freundlichen " +
      "Frage und warte meine Antwort ab. Frag danach höchstens zweimal nach: was " +
      "genau, und an welcher Stelle im Programm. Lege den Wunsch dann mit " +
      "ticket_create an (das ist hier ausdrücklich erwünscht, ich habe selbst " +
      "darum gebeten) und schreibe in den Titel voran, dass es ein Wunsch ist. " +
      "Sag mir am Schluss in einem Satz, dass Martin ihn bekommt.",
  });
});

window.addEventListener("voice-v8:example-prompt", (ev) => {
  const detail = (ev as CustomEvent<{ prompt?: string }>).detail;
  const prompt = detail?.prompt?.trim();
  if (!prompt) return;
  closeDrawerForNavigation();
  goToView("chat");
  const field = document.getElementById(
    "text-input-field",
  ) as HTMLTextAreaElement | null;
  if (field) {
    field.value = prompt;
    field.focus();
    // Resize-trigger falls auto-grow vorhanden
    field.dispatchEvent(new Event("input", { bubbles: true }));
  }
});

window.addEventListener("voice-v8:open-project", (ev) => {
  const id = (ev as CustomEvent<{ projectId?: string }>).detail?.projectId;
  if (!id) return;
  goToView("projects");
  void ensureProjectsView().openProjectById(id);
});

// Pre.336: Profil-Onboarding-Wizard — Settings-Button triggert hier.
// Wechselt zur Chat-View, baut Voice-Verbindung auf und sendet dem Bot
// einen klaren Onboarding-Prompt mit profile_update Hinweis.
window.addEventListener("voice-v8:profile-onboarding-start", () => {
  closeDrawerForNavigation();
  goToView("chat");
  // Kleinen Moment warten damit die View gewechselt hat und Audio sich
  // im echten User-Gesten-Tick befindet.
  primeAudio();
  startRingingTone();
  void (async () => {
    try {
      await handleConnect();
      // Kurz warten bis client.state === "listening" — sonst geht der
      // Text als sendChatHttp und nicht als Voice-Prompt durch.
      let attempts = 0;
      while (currentState !== "listening" && attempts < 30) {
        await new Promise((r) => setTimeout(r, 200));
        attempts++;
      }
      await sendUserMessage(
        "Bitte hilf mir mein Profil komplett auszufüllen. Frag mich bitte konversationell durch die fehlenden Stammdaten — Name, Telefon, Adresse, Geburtstag — und speichere meine Antworten mit dem profile_update Tool.",
      );
    } catch (e) {
      showToast(
        "Onboarding-Start fehlgeschlagen: " +
          (e instanceof Error ? e.message : String(e)),
        "error",
        4000,
      );
    }
  })();
});
refs.muteBtn.addEventListener("click", () => {
  hapticTap(20); // Martin 14.07.: fuehlbare Quittung (Stumm)
  void handleMute();
});
refs.focusBtn.addEventListener("click", handleFocusToggle);
refs.hangupBtn.addEventListener("click", () => {
  hapticTap(35); // Martin 14.07.: fuehlbare Quittung (Auflegen)
  void handleHangup();
});

// Pre.138: Long-Press (≥500ms) auf den zentralen Mic/Call-Button öffnet
// das Quick-Access-Sheet — Schnellsprung zu Kontakten, Dateien etc.
// ohne Umweg über den Drawer.
attachLongPress(refs.connectBtn, openQuickAccessSheet);
attachLongPress(refs.muteBtn, openQuickAccessSheet);

function attachLongPress(el: HTMLElement, fire: () => void): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let triggered = false;
  const start = (): void => {
    triggered = false;
    timer = setTimeout(() => {
      triggered = true;
      // Vibrations-Feedback wenn verfügbar.
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        navigator.vibrate(30);
      }
      fire();
    }, 500);
  };
  const cancel = (): void => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  // Click verhindern wenn long-press getriggert hat — sonst öffnet sich
  // sowohl das Sheet als auch z.B. der Voice-Call.
  el.addEventListener("click", (ev) => {
    if (triggered) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      triggered = false;
    }
  });
  el.addEventListener("pointerdown", start);
  el.addEventListener("pointerup", cancel);
  el.addEventListener("pointerleave", cancel);
  el.addEventListener("pointercancel", cancel);
  // Pre.139: iOS-Kontext-Menü beim Long-Press unterdrücken — sonst
  // erscheint die Text-Selektions-UI über den Buttons.
  el.addEventListener("contextmenu", (ev) => ev.preventDefault());
}

// --- Push-to-Talk (Pre.412 Redesign — OpenAI-Style Tap-Toggle) --------
// Vorher: Hold/Tap/Lock mit pointerdown/up/leave — verwirrend, fragil,
// User-Beschwerde "geht gar nicht, kann nicht stoppen".
// Jetzt: einfacher Tap-Toggle. Tap auf Mic-Button startet Aufnahme,
// Tap auf denselben (jetzt animierten) Button stoppt + sendet automatisch.
// Bubble zeigt Live-Timer + großen Cancel-X-Button. Kein Lock-Schloss,
// kein Hold-Druck-Halten. Einfacher Mental-Model.
// Setting `pttMode: "call"` überspringt PTT komplett — Tap startet sofort
// einen Voice-Call statt Recording.
/**
 * Eine Diagnosezeile in die Browser-Konsole UND ins Server-Protokoll legen.
 *
 * 2026-08-23: Dieses fetch stand viermal wortgleich im File. Wer eine neue
 * Sonde einbaut, kopiert sonst den Block ein fuenftes Mal — und wer nur
 * console.log schreibt, misst ins Leere: Browser-Konsolen sieht hier niemand.
 * Ein Aufruf, ein Ort.
 */
/** Journalzeile beim Sperren eines Ja/Nein-Paars: nur Grund und Zahl (lib/frage-knoepfe.ts), nie Text. */
const meldeRueckfrageGesperrt: RueckfrageMelder = (grund, anzahl) => meldeDiagnose(`[rueckfrage] gesperrt grund=${grund} n=${anzahl}`);
// 20.10.2026: gebündelt — beim Neuzeichnen des Verlaufs EINE Zeile je Grund statt einer je Karte (lib/frage-knoepfe.ts).
const meldeFrageGesperrt: FrageMelder = buendleFrageMelder((grund, anzahl) => meldeDiagnose(`[frage] gesperrt grund=${grund} n=${anzahl}`));

function meldeDiagnose(roh: string): void {
  // Welle 0 (lib/aufnahme-kennung.ts): die Jev-Zeilen tragen die Kennung der Aufnahme und die Regelfassung.
  const line = mitAufnahmeKennung(roh);
  console.log(line);
  void fetch("/api/debug/ptt-timing", {
    method: "POST",
    body: JSON.stringify({ line }),
    headers: { "Content-Type": "text/plain" },
    keepalive: true,
    credentials: "include",
  }).catch(() => undefined);
}

const pttRecorder = new PttRecorder();

/**
 * Ticket 2246 (13.09.2026): "Vespera hat etwas bemerkt" — >99% der
 * gezählten App-"Sitzungen" dauern seit 05.09. unter einer Minute
 * (server/bildschirmzeit.ts). Das ist kein Nutzungsverhalten, sondern
 * ein visibilitychange-Flackern; die Ursache war bisher nur Vermutung
 * ("das warme Mikrofon haelt eine Audio-Session offen"), NICHT verifiziert.
 *
 * Live-Instrumentierung, die der bestehenden Meldung an /api/app-state
 * (meldeWennGeaendert(), Zeile ~1703) bewusst NICHT beigemischt wird —
 * die laeuft dort bereits einmal SYNCHRON beim Laden, bevor pttRecorder
 * hier existiert, und sie ist gedrosselt (nur bei echtem Zustandswechsel).
 * Für die Diagnose zählt genau das Gegenteil: JEDES Ereignis, ungedrosselt,
 * mit dem zeitlichen Abstand zum letzten — die Häufung IST der Befund.
 *
 * Kein Verhalten geändert — reine Diagnose über denselben Weg wie Ticket
 * 2232/2257 (meldeDiagnose, erreicht den Server, nicht nur die Konsole).
 */
let letzterSichtbarkeitswechselAt = 0;
document.addEventListener("visibilitychange", () => {
  const jetzt = Date.now();
  const deltaMs = letzterSichtbarkeitswechselAt
    ? jetzt - letzterSichtbarkeitswechselAt
    : -1;
  letzterSichtbarkeitswechselAt = jetzt;
  const { tagesaufnahme, wiedergabe } = mikroSitzungsStatus();
  meldeDiagnose(
    `[sichtbar-d] ${document.visibilityState} deltaMs=${deltaMs} aufnahme=${pttRecorder.isRecording()} tagesaufnahme=${tagesaufnahme} wiedergabe=${wiedergabe}`,
  );
});

// 2026-07-09 (Martin, #317/#303 — Freihand v2): EIGENER Dock-Schalter statt
// verstecktem Tipp auf dem Push-Knopf. Tipp an -> Dauer-Zuhoeren: VAD erkennt
// das Sprechende -> sendet selbst -> nach der Antwort wird weitergehoert.
// Der Push-Knopf (halten/loslassen) bleibt komplett unberuehrt.
const handsFreeVad = new HandsFreeVad();
// Dauer-Stille sichtbar machen (falsches Eingangsgeraet etc.).
handsFreeVad.onSilentInput = () => {
  const dev = handsFreeVad.getStats().deviceLabel;
  window.dispatchEvent(
    new CustomEvent("voice-v8:toast", {
      detail: {
        message: dev && dev !== "?"
          ? `Freihand hört nur Stille — Chrome nutzt gerade „${dev}". Wähle im Schloss-Menü (Adressleiste) ein anderes Mikrofon.`
          : "Freihand hört nur Stille — liefert dein Mikrofon Ton? (Eingangsgerät im System/Browser prüfen)",
        kind: "info",
      },
    }),
  );
};
let freihandActive = false;
// 2026-07-22 (Martin, Echo-Doppel der Tiefen Frage): Selbsthoer-Sperre —
// solange die Bot-Stimme spielt, pausiert die Freihand-Erkennung; nach dem
// letzten Ton kurzer Nachlauf gegen Raum-Hall, dann frische Aeusserung.
// Ticket 570 (2026-07-30): Mikrofon-Spur einschalten. Sie meldet, WANN
// etwas nach dem Mikrofon greift — Vorlesen, Push-to-Talk. Bei 64
// gemessenen Abrissen liess sich die Ursache nicht klaeren, weil genau
// diese Ereignisse nur in der Browser-Konsole standen.
starteMikroSpur();
// 17.10.2026 (lange-spur.ts): beginnt eine lange Spur (Meditation, Spiegel, Biografie), schweigt die sprechende Stimme.
starteLangeSpur((art) => {
  meldeDiagnose(`[lange-spur] stimme gestoppt art=${art}`);
  stopCurrentTts("lange-spur");
  // Ein kurz vorher VON SELBST geöffnetes Mikro (nach Frage/Antwort/Freihand) würde die Spur aufnehmen: verwerfen, nicht senden.
  // Von Hand geöffnete Aufnahmen bleiben.
  // Nur eine AUFNAHME, die von selbst aufging — nie den laufenden Zug („sending“), der die Spur vielleicht gestartet hat.
  if (sollMikroVerwerfen(pttState, pttTiming.extra.via)) {
    meldeDiagnose(`[lange-spur] mikro von selbst verworfen via=${String(pttTiming.extra.via)}`);
    pttVerwerfen("lange-spur");
  }
});

// 21.10.2026: je Stück und je Zugende die Vorlese-Karten nachführen (lib/vorlese-mitlauf.ts).
window.addEventListener("voice-v8:tts-audio-start", () => vorleseMitlauf.anstossen());
window.addEventListener("voice-v8:tts-audio-end", () => vorleseMitlauf.anstossen());
window.addEventListener("voice-v8:tts-audio-start", () => {
  if (freihandActive) handsFreeVad.pause();
});
window.addEventListener("voice-v8:tts-audio-end", (ende) => {
  if (!freihandActive) return;
  // Welle A (lib/zug-ende.ts): ein Vorlese-Ende mitten im Zug (Hinweis, Vorab-Satz) weckt die Freihand-Erkennung nicht.
  if ((ende as CustomEvent<{ zugFertig?: boolean }>).detail?.zugFertig === false) return;
  window.setTimeout(() => {
    if (freihandActive && pttState === "recording") handsFreeVad.resume();
  }, 450);
});
/*
 * Ticket 2403 — das Mikro nach einer FRAGE von selbst oeffnen.
 *
 * Martin, 19.09.: *„Wenn Vespera eine Antwort vorliest und diese mit einer
 * Frage endet, soll das Mikro automatisch freigeschaltet werden — ohne dass
 * ich jedes Mal den PTT-Knopf druecken muss."*
 *
 * ⚠️ VIER WAECHTER, und jeder hat einen Grund:
 *   1. Der Schalter ist AUS, solange Martin ihn nicht einschaltet. Es ist
 *      die Vorstufe zur Freihand-Konversation, und die ist geparkt.
 *   2. Nur wenn die Antwort wirklich mit einer Frage SCHLIESST — sonst
 *      waere es Dauer-Zuhoeren, das der Ticket-Text ausdruecklich
 *      ausschliesst. 23.09.2026: außer Martin wählt selbst die Stufe
 *      „nach jeder Antwort" (sein Wunsch vom 20.09., 21:25).
 *   3. Nur aus dem Ruhezustand. Laeuft schon eine Aufnahme oder ein Senden,
 *      wird nichts angefasst.
 *   4. Nur wenn die App im Vordergrund ist. Ein Mikro, das im Hintergrund
 *      aufgeht, ist etwas anderes als eine Antwort auf eine Frage.
 *
 * ⚠️ Es ruft pttToggle() — denselben Weg wie der Knopf. Ein zweiter
 *    Startweg waere ein zweiter Besitzer, und der Rekorder hat genau damit
 *    eine Historie (Dauer-Aufnahme, Runaway-Watcher pre.1935).
 */
/*
 * 16.10.2026, 18:19 (lib/schmied-warten.ts, Halt an Vespera): ging ein Beitrag des Schmieds an Vespera, ist das Ende IHRES
 * fertigen Zuges ihre Antwort — der Halt endet ohne Öffnen, und über das Mikro entscheidet der Hörer darunter nach den
 * normalen Regeln. Dieselben Wächter wie dort: nicht die Stimme des Schmieds, nicht das Ende der Musik, nicht mitten im Zug.
 * Ein eigener Hörer (vor dem großen), damit dessen Regeln unberührt bleiben; die Reihenfolge ist gleichgültig (`beginne`
 * beendet den Halt ebenso).
 */
window.addEventListener("voice-v8:tts-audio-end", (haltEnde) => {
  const d = (haltEnde as CustomEvent<{ musikEnde?: boolean; zugFertig?: boolean; sprecher?: string } | undefined>).detail;
  // 17.10.2026: jeder andere Sprecher (Schmied, Hörprobe „probe“) ist keine Antwort Vesperas.
  if (d?.sprecher || d?.musikEnde === true || d?.zugFertig === false) return;
  if (alleWarten().map((w) => w.vesperaAntwortet(zugKennung())).some(Boolean)) meldeDiagnose("[ptt] mikro halt-ende vespera-antwortet");
});

/** 22.10.2026 (Martin 08:12, lib/frage-rueckkehr.ts): eine Frage wartet, während die Seite im Hintergrund ist — Titel und EIN Versuch bei der Rückkehr. */
const frageRueckkehr = new FrageRueckkehr(
  { lies: () => document.title, setze: (t) => void (document.title = t) },
  (zeile) => meldeDiagnose(zeile),
);
// Synchron aus dem Hintergrund-Zweig unten: der Zug ist der, dessen Frage gerade entfiel (die Seite ist unsichtbar, nichts Neues begann).
window.addEventListener(FRAGE_IM_HINTERGRUND, () => {
  if (zugEndeStand().zugFertig) frageRueckkehr.merke(zugKennung());
});

window.addEventListener("voice-v8:tts-audio-end", (ev) => {
  const frage = (ev as CustomEvent<{ endetMitFrage?: boolean }>).detail
    ?.endetMitFrage;
  // 13.10.2026 (Ticket 3230): dasselbe Ereignis, wenn die MUSIK endet (lib/wiedergabe-stand.ts) - kein Zug, nichts einzulösen.
  const musikEnde = (ev as CustomEvent<{ musikEnde?: boolean }>).detail?.musikEnde === true;
  /*
   * 23.09.2026 (Ticket 2403): Wächter 1 und 2 sind jetzt EINE Frage an die
   * Einstellung — aus, nur nach einer Frage oder (seit 07.10.) Jev
   * entscheidet (mikroNachAntwort in shared/mikro-nach-antwort.ts). Die
   * Wächter 3 und 4 darunter und der Abbruch-Weg (`tts-audio-abbruch` löst
   * dieses Ereignis nie aus) bleiben, wie sie sind.
   */
  const einst = loadSettings();
  const stufe = autoMikroGeltend(einst.autoMikroNachFrage);
  /*
   * 15.10.2026 (Plan „Verlässliche Abarbeitung“, Welle A — lib/zug-ende.ts): Ist der Zug noch nicht zu Ende (kein `done`),
   * entscheidet dieses Ende nichts — es war ein Hinweis oder Vorab-Satz während der Werkzeuge. Martin: das Mikro ging mitten
   * im Arbeitszug auf und schickte Raumton als neuen Auftrag ab. Die Merker darunter bleiben für das echte Ende liegen.
   */
  const zugEnde = (
    ev as CustomEvent<{
      zugFertig?: boolean;
      zug?: { art?: string; rueckfrage?: boolean; schmiedAbschied?: boolean; erwartetAntwort?: boolean; aktionErledigt?: boolean; nurKnopf?: boolean } | null;
      sprecher?: string;
      schmiedAngesprochen?: boolean;
      schmiedFolgt?: boolean;
      /** 19.10.2026: Vesperas Antwort endet mit der Anrede an Zimmermann oder Hüter (Übergabe). */
      zusatzAngesprochen?: string;
      fertig?: boolean;
    }>
  ).detail;
  // 15.10.2026 (Schmied): das Ende SEINER Stimme entscheidet nichts — das wartende Mikro erfährt es über schmiedWarten.
  // 17.10.2026: ebenso das Ende eines Hörprobe-Satzes (Sprecher „probe“, lib/vespera-satz.ts) — sonst öffnete „Jev entscheidet“
  // nach jedem Probesatz das Freisprech-Mikro mitten in den Einstellungen (Prüfbefund).
  if (zugEnde?.sprecher) return;
  if (!musikEnde && zugEnde?.zugFertig === false) {
    if (stufe === true || stufe === "jev") meldeDiagnose("[ptt] mikro übersprungen zug-laeuft");
    return;
  }
  /*
   * 27.09.2026 (Ticket 2701): Hat er sich in diesem Zug verabschiedet
   * (gespraech_beenden, vom Modell gerufen), bleibt es zu — das entscheidet
   * mikroNachAntwort. Wäre es sonst aufgegangen, sagt das Protokoll, warum
   * nicht.
   */
  const abschiedArt = musikEnde ? { verabschiedet: false, abgeleitet: false } : abschiedEinloesenArt();
  const verabschiedet = abschiedArt.verabschiedet;
  /*
   * 29.09.2026 (Ticket 2790): Auf ihn wartet die Antwort auch, wenn eine
   * Fragen-Runde in DIESEM Zug ihre Frage gestellt hat — der Server meldet
   * das am Zugende (frage-runde.ts). Um 06:58 endete Vespera mitten in der
   * Runde mit einer Feststellung; in der Stufe „nach Frage“ ging nichts auf.
   * Die Fensterlänge bleibt die eingestellte (Ticket 2668).
   */
  const runde = musikEnde ? false : rundeEinloesen();
  /*
   * 07.10.2026 (Ticket 3048): In der Stufe „Jev entscheidet“ (früher „immer“)
   * sagt Jevs Meldung vom Zugende, ob es nach einer Antwort OHNE Frage zu
   * bleibt, kurz zuhört oder wie bisher aufgeht (shared/mikro-nach-antwort.ts).
   * Die Frage-Regel bleibt die Basis; die Fensterlänge kommt aus derselben
   * Entscheidung. Je Entscheidung in dieser Stufe eine Zeile, nur Wörter und Zahlen.
   */
  const jev = musikEnde ? null : jevMikroEinloesen();
  const wartet = frage === true || runde;
  // 17.10.2026 (Sicherheitsprüfung M2): wartet eine Entscheidung (Rückfrage-Karte, Ja-Tor, Runde) oder spricht Vespera den Schmied an
  // (seine Frage kann eine Entscheidung sein)? Dann kein 400-ms-Ja (`PttModalCallbacks.entscheidungOffen`).
  // (Die Kennung kommt aus lib/tts-queue.ts `zusatzAmEnde`; hier nur die zwei gültigen Werte, ohne Hilfsfunktion.)
  const zusatzDanach: ZusatzId | null =
    !musikEnde && (zugEnde?.zusatzAngesprochen === "zimmermann" || zugEnde?.zusatzAngesprochen === "hueter") ? zugEnde.zusatzAngesprochen : null;
  mikroEntscheidungOffen = zugEnde?.zug?.rueckfrage === true || runde || zugEnde?.schmiedAngesprochen === true || zugEnde?.schmiedFolgt === true || zusatzDanach !== null;
  // Welle A, 16.10. geändert: nach einem Arbeitszug öffnet eine Entscheidung (Karte, Ja-Tor, Runde) ODER eine Frage am Ende (`wartet`).
  const arbeitszug = !musikEnde && zugEnde?.zug?.art === "arbeit" ? { entscheidungOffen: zugEnde.zug.rueckfrage === true || runde } : undefined;
  // 17.10.2026: endet der Zug mit dem Notsatz („Frag mich bitte noch einmal“), meldet der Server `erwartetAntwort` — eine Bitte, die öffnet.
  const notsatzBitte = !musikEnde && zugEnde?.zug?.erwartetAntwort === true;
  // 28.10.2026 (Autonomie-Plan B5a): wartet nur eine Knopf-Karte (Mandat, Prozess, Zugangswerte), antwortet Martin per Knopf — das
  // Mikro öffnet nicht von selbst (`grund=knopf-karte`). `mikroEntscheidungOffen` oben bleibt gesetzt (kein 400-ms-Ja).
  const nurKnopf = !musikEnde && zugEnde?.zug?.nurKnopf === true;
  const lage = { stufe, wartetAufMartin: wartet, jev: jev?.stufe ?? null, fensterMs: nachlauschFenster(einst.nachlauschMs), wiedergabe: wiedergabeJetzt(), langeSpur: langeSpurLaeuft(), musikEnde, arbeitszug, notsatzBitte, nurKnopf, aktionErledigt: !musikEnde && zugEnde?.zug?.aktionErledigt === true && zugEnde?.schmiedAngesprochen !== true && zugEnde?.schmiedFolgt !== true && zusatzDanach === null };
  // 17.10.2026 (lib/fertig.ts): „Fertig“ in diesem Zug — das Mikro bleibt zu, in jeder Stufe.
  const fertig = !musikEnde && zugEnde?.fertig === true;
  const entscheid = mikroNachAntwort({ ...lage, verabschiedet, abschiedAbgeleitet: abschiedArt.abgeleitet, fertig });
  if (fertig) meldeDiagnose(`[ptt] mikro übersprungen fertig-gedrueckt`);
  // Welle 0: `frage` = endetMitFrage der Vorlese-Schlange (0/1), die Kennung die der Aufnahme, auf die dieser Zug antwortet.
  if (stufe === "jev") meldeDiagnose(mitAufnahmeKennung(mikroNachAntwortZeile(entscheid, stufe, jev, frage === true, abschiedArt.abgeleitet), zugAufnahme()));
  const via = entscheid.via;
  if (verabschiedet && !via) {
    // Wie vor dem 07.10.: „wäre ohne den Abschied aufgegangen“ — Jevs Meldung kam mit dem Abschied, sie zählt hier nicht.
    const sonst = mikroNachAntwort({ ...lage, jev: null, verabschiedet: false }).via;
    if (sonst) meldeDiagnose(`[ptt] ${sonst} übersprungen verabschiedet`);
  }
  if (nurKnopf && !via) {
    // B5a: wäre es ohne die Knopf-Karte aufgegangen, sagt das Protokoll, warum nicht (nur Wörter, kein Text).
    const sonst = mikroNachAntwort({ ...lage, nurKnopf: false, verabschiedet, abschiedAbgeleitet: abschiedArt.abgeleitet, fertig }).via;
    if (sonst) meldeDiagnose(`[ptt] ${sonst} übersprungen knopf-karte`);
  }
  if (!via) return;
  // 22.10.2026 (Martin 08:12, lib/frage-rueckkehr.ts): nur eine schlichte Frage Vesperas (keine Karte/Ja-Tor/Runde/Entwickler) darf
  // bei der Rückkehr in die Seite noch einmal versuchen — gemerkt wird erst unten, wenn sie wegen des Hintergrunds entfiel.
  const rueckkehrMoeglich = !musikEnde && via === "nach-frage" && entscheid.grund === "frage" && !mikroEntscheidungOffen;
  if (runde && frage !== true) meldeDiagnose(`[ptt] ${via} wartet=runde`);
  // Ab hier sollte es aufgehen — wer jetzt NICHT öffnet, sagt warum. Sonst
  // sieht „ging nicht auf" im Protokoll aus wie „keine Frage".
  // 27.09.2026 (Ticket 2698): Gleich startet der Player, um den er gebeten
  // hat — er will zuhören, nicht sprechen. Ein offenes Mikro nähme den
  // Spiegel auf und schickte ihn als Nachricht.
  if (abspielenWartet()) {
    meldeDiagnose(`[ptt] ${via} übersprungen abspielen`);
    return;
  }
  // 11.10.2026 (Ticket 2669): Gleich lädt die Seite neu, um das er gebeten hat — ein Mikro, das jetzt aufgeht,
  // risse sie nicht ab, hielte aber das Neuladen auf (Aufnahme = Hindernis).
  if (neuLadenWartet()) {
    meldeDiagnose(`[ptt] ${via} übersprungen neu-laden`);
    return;
  }
  /*
   * 29.09.2026 (Ticket 2764): erst nach dem Ausklang (lib/ausklang.ts).
   * Martin hörte „fet“ statt „fertig“: die Sprechtaste schaltete die
   * Tonsitzung 37 ms nach dem letzten `ended` um, und über Bluetooth warf
   * das den Rest weg, der noch zum Ohr unterwegs war. Ruhezustand und
   * Vordergrund zählen im Moment des Öffnens — darum stehen sie im Rückruf.
   * Der Player (oben) gehört zu diesem Zug und wird sofort gefragt.
   */
  const oeffne = (): void =>
  nachAusklang(
    () => {
      if (pttState !== "idle") {
        meldeDiagnose(`[ptt] ${via} übersprungen zustand=${pttState}`);
        return;
      }
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        meldeDiagnose(`[ptt] ${via} übersprungen hintergrund`);
        // 22.10.2026: Titel „Vespera wartet“, und bei der Rückkehr EIN Versuch über denselben Weg (frageRueckkehrPruefen).
        if (rueckkehrMoeglich) window.dispatchEvent(new CustomEvent("voice-v8:frage-im-hintergrund"));
        return;
      }
      /*
       * ⚠️⚠️ 22.09.2026, TICKET 2502 — HIER STAND `void pttToggle()`, MIT DEM
       * ##    KOMMENTAR „denselben Weg wie der Knopf". DAS WAR FALSCH.
       *
       * Martin: *„Das Mikro ging durchs Ansprechen automatisch auf. Aber das
       * zugehoerige Modal hat sich NICHT geoeffnet — ich musste danach manuell
       * extra auf Push-to-Talk klicken."*
       *
       * `pttToggle()` ist nur der LETZTE Schritt des Knopf-Wegs. Der Knopf
       * durchlaeuft `startPttRecording()`: in den Chat wechseln, Ton
       * freischalten, `openPttModal()`, Status setzen — und ERST DANN
       * `pttToggle()`. Wer nur den letzten Schritt ruft, bekommt ein offenes
       * Mikro ohne Fenster: man sieht nicht, dass aufgenommen wird, sieht den
       * Text nicht mitlaufen und hat keinen Stopp-Knopf.
       *
       * ⚠️ Der Kommentar hat die Luecke verdeckt. Er behauptete die Gleichheit,
       *    statt sie herzustellen — dasselbe Muster wie „Kommentar ist kein
       *    Beweis" vom 21.09.
       *
       * ⚠️ HANDSFREE, nicht der normale Start: Hier haelt niemand einen Knopf.
       *    `startPttHandsFree` sperrt die Aufnahme offen, sodass sie nicht beim
       *    ersten Loslassen endet; beendet wird sie vom Sende-Wort oder der
       *    Stille (Ticket 2420) oder vom Stopp-Knopf im Fenster.
       */
      startPttHandsFree(via, entscheid.fensterMs);
    },
    (grund) => mikroNachSchmiedVorlesen(via, grund, oeffne, zusatzDanach ?? "schmied"),
  );
  /*
   * 15.10.2026 (Schmied, shared/schmied.ts): Spricht Vespera am Ende den Schmied an (und ist er im Chat), bleibt das Mikro
   * zu, bis sein Beitrag da ist — und, bei „Entwickler vorlesen“, fertig gesprochen — oder SCHMIED_WARTEN_MS ohne Beitrag
   * vergangen sind (wartetAufSchmied, lib/schmied-warten.ts). Ob es überhaupt aufgeht, hat mikroNachAntwort oben schon
   * entschieden; hier ändert sich nur das WANN. Ohne Anrede an ihn: wie immer, sofort.
   */
  // 16.10.2026 (server/adressat-reihe.ts): sprach Martin beide an und antwortet der Schmied NACH Vespera, wartet es genauso auf ihn.
  // 16.10.2026 (server/dreier-modus.ts): verabschiedet sie ihn dabei („Danke, Schmied, bis später!“), kommt von ihm nichts mehr —
  // das Mikro wartet NICHT auf ihn (der Server meldet es am Zug, Feld `schmiedAbschied`; Notschalter SCHMIED_ABSCHIED=0).
  const schmiedAbschied = zugEnde?.schmiedAngesprochen === true && zugEnde.zug?.schmiedAbschied === true;
  if (!musikEnde && schmiedAbschied) meldeDiagnose(`[ptt] ${via} schmied-abschied von=vespera`);
  const schmiedDanach = (zugEnde?.schmiedAngesprochen === true && !schmiedAbschied) || zugEnde?.schmiedFolgt === true;
  // 28.10.2026: die Frage geht an Martin, wenn Vespera am Ende keinen Entwickler anspricht (Schmied folgt nur danach).
  const frageAnMartin = zugEnde?.schmiedAngesprochen !== true && zusatzDanach === null;
  if (!musikEnde && schmiedDanach && wartetAufSchmied(via, oeffne, true, "schmied", frageAnMartin)) return;
  // 19.10.2026: Übergabe/Anrede an Zimmermann oder Hüter am Ende: dasselbe Warten, mit SEINER Warteblase und seiner Antwort.
  if (zusatzDanach && wartetAufSchmied(via, oeffne, true, zusatzDanach)) return;
  oeffne();
});

/**
 * 19.10.2026 (Mikro-Vorstart, lib/mikro-vorstart.ts): geht das Mikro nach der Wiedergabe, die gleich endet, sowieso von selbst auf?
 * `null` = ja (dann holt ptt.ts das Mikro ~1,2 s vor dem Ende); sonst EIN Wort, warum nicht. Dieselben Regeln wie das Ende unten
 * (tts-audio-end -> mikroNachAntwort, Wächter in startPttHandsFree) — aber nur ANSEHEN: nichts wird eingelöst, nichts gemeldet.
 * Im Zweifel nein: ein Mikro, das umsonst vorgezogen wurde, kostet Klang; ein ausgelassener Vorstart nur den alten kalten Start.
 */
function vorstartGrund(lage: VorstartLage): string | null {
  const einst = loadSettings();
  if (einst.mikroVorstart === false) return "aus";
  // In der App nie (WARMES_MIKRO_IN_DER_APP, 25.09.2026): ein vorgezogener Strom wäre dort ein warmer.
  const plattform = vorstartPlattformGrund();
  if (plattform) return plattform;
  if (lage.sprecher === "probe") return "probe";
  const autoStufe = autoMikroGeltend(einst.autoMikroNachFrage);
  if (autoStufe !== true && autoStufe !== "jev") return "stufe-aus";
  const stand = zugEndeStand();
  if (!stand.zugFertig) return "zug-laeuft";
  if (stand.fertig) return "fertig";
  if (pttState !== "idle") return "zustand";
  if (document.visibilityState !== "visible") return "hintergrund";
  if (abspielenWartet()) return "abspielen";
  if (neuLadenWartet()) return "neu-laden";
  if (martinTipptGerade()) return "tippt";
  if (liegtEtwasUeberDemChat()) return "seite-offen";
  if (langeSpurLaeuft()) return "lange-spur";
  if (autoMikroNichtVorn()) return "nicht-vorn";
  if (aufnahmeStartSperre()) return "aufnahme";
  if (einst.pttMode === "call") return "anruf";
  if (zweitesGeraetSperrt("nach-frage")) return "aufnahme-anderes-geraet";
  // Sein Vorlesen: das Mikro öffnet, wenn das Ende seines letzten Beitrags etwas einlöst (lib/schmied-warten.ts).
  // 21.10.2026: steht danach noch ein Beitrag in der Reihe, öffnet erst deren Ende (lib/vorlese-reihe.ts) — kein Vorstart jetzt.
  if (lage.sprecher === "schmied" && vorleseReihe.offen() > 1) return "reihe-laeuft";
  if (lage.sprecher === "schmied") return alleWarten().some((w) => w.oeffnetNachVorlesen()) ? null : "kein-oeffnen";
  // Vesperas Stimme: spricht sie am Ende einen Entwickler an, wartet das Mikro auf SEINEN Beitrag, nicht auf das Ende hier.
  if (lage.schmiedAngesprochen || lage.zusatzAngesprochen || stand.schmiedFolgt) return "wartet-auf-entwickler";
  const runde = rundeAnsehen();
  const zug = stand.zug;
  const entscheid = mikroNachAntwort({
    stufe: autoStufe,
    wartetAufMartin: lage.endetMitFrage || runde,
    verabschiedet: abschiedAnsehen(),
    abschiedAbgeleitet: abschiedAbgeleitetAnsehen(),
    jev: jevMikroAnsehen()?.stufe ?? null,
    fensterMs: nachlauschFenster(einst.nachlauschMs),
    wiedergabe: wiedergabeJetzt(),
    langeSpur: langeSpurLaeuft(),
    arbeitszug: zug?.art === "arbeit" ? { entscheidungOffen: zug.rueckfrage === true || runde } : undefined,
    notsatzBitte: zug?.erwartetAntwort === true,
    aktionErledigt: zug?.aktionErledigt === true,
  });
  return entscheid.via ? null : `zu-${entscheid.grund}`;
}

setzeVorstart({
  erlaubt: vorstartGrund,
  starte: ({ restMs }) => {
    void mikroVorstarten(restMs).then((nein) => {
      if (nein) meldeDiagnose(`[ptt] vorstart nein grund=${nein} restMs=${restMs}`);
    });
  },
  gibFrei: (grund) => gibVorstartFrei(grund),
  melde: (zeile) => meldeDiagnose(zeile),
});

/**
 * Das Mikro wartet auf den Schmied (15.10.2026, shared/schmied.ts) — `true`, wenn es wartet; `false` (Dreier-Modus aus):
 * wie immer. Geöffnet wird später über `oeffne` (Ausklang, Ruhezustand, Vordergrund prüft es selbst) — aber nur, wenn
 * seitdem kein neuer Zug begann und kein Abspielen oder Neuladen wartet.
 */
function wartetAufSchmied(via: string, oeffne: () => void, zugVonVespera = true, id: EntwicklerId = "schmied", frageAnMartin = false): boolean {
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  const einstellungen = loadSettings() as unknown as Record<string, unknown>;
  // 18.10.2026: Hauptschalter „Entwickler“ UND „Entwickler im Chat“ (eine Stelle: shared/entwickler-im-chat.ts).
  // 19.10.2026: Zimmermann/Hüter zusätzlich ihr eigener Schalter („<Name> im Chat“, `entwicklerKontoAn`) — sonst wartet nichts auf sie.
  if (id === "schmied" ? !entwicklerHauptAn(einstellungen, uid) : !entwicklerKontoAn(id, einstellungen, uid)) return false;
  const zug = zugKennung();
  // Prüfauflage A3: Beginn des Wartens — eine alte, beantwortete Karte von davor hält das Mikro nicht (lib/ja-karte-offen.ts).
  // 28.10.2026 (Prüfbefund): Schwelle = BEGINN des Zugs, nicht das Ende der Wiedergabe — eine Karte, die WÄHREND Vesperas Antwort kam, ist frisch.
  const wartenSeit = Math.min(Date.now(), zugEndeStand().zugStart ?? Date.now());
  meldeDiagnose(`[ptt] ${via} wartet=${id}`);
  const versuch = (weil: SchmiedOeffnetWeil): void => {
    if (!schmiedOeffnenGilt(via, zug, weil)) return;
    // 17.10.2026: Frist/Statusende/Obergrenze ohne offene Frage an Martin — kein Mikro, nur der leise Hinweis. Eine offene Frage
    // (Vesperas Antwort endete mit Frage/Entscheidung: via nach-frage) öffnet weiter wie bisher. Hinweis nur am vorderen Gerät,
    // im Vordergrund, nicht beim Tippen.
    // 28.10.2026: nur eine Frage AN MARTIN zählt — „Schmied, passt das?“ (Anrede an einen Entwickler) nicht (`frageAnMartin`, Aufrufer).
    const frageOffen = zugVonVespera && via === "nach-frage" && frageAnMartin;
    if (nurHinweisStattMikro(weil, frageOffen)) {
      meldeDiagnose(`[ptt] ${via} nur-hinweis weil=${weil}`);
      if (document.visibilityState === "visible" && autoMikroNichtVorn() === null && !martinTipptGerade()) {
        window.dispatchEvent(new CustomEvent("voice-v8:toast", { detail: { message: MIKRO_BEREIT_HINWEIS, kind: "info" } }));
      }
      return;
    }
    // 21.10.2026 (Martin 07:13): steht noch ein Beitrag (auch eines anderen Entwicklers) in der Vorlese-Reihe, erst nach ihrem Ende.
    if (mikroErstNachReihe(via, id, weil, () => versuch(weil))) return;
    // 22.10.2026 (Martin 07:55, Journal `nach-frage wartet=zimmermann`): antwortete er mit einer Ja/Nein-Karte (oder steht eine Karte
    // offen), antwortet Martin per Knopf — das Mikro öffnet NICHT von selbst (nur sperren; Martins Druck bleibt möglich).
    const karte = jaKarteHaeltMikro(refs.chat, id, null, Date.now(), wartenSeit, meldeDiagnose, frageOffen);
    if (karte) {
      meldeDiagnose(`[ptt] ${via} ${id} zu grund=${karte} weil=${weil}`);
      return;
    }
    meldeDiagnose(`[ptt] ${via} ${id} weil=${weil}`);
    oeffne();
  };
  wartenVon(id).beginne(versuch, zugVonVespera ? zug : null, zugVonVespera && via === "nach-frage" && frageAnMartin);
  return true;
}

/** Darf ein SPÄTERES Öffnen für den Zug `zug` noch geschehen (kein neuer Zug, kein Abspielen/Neuladen)? Sonst sagt das Protokoll, warum nicht. */
function schmiedOeffnenGilt(via: string, zug: number, weil: string): boolean {
  if (zugKennung() !== zug || !zugEndeStand().zugFertig) {
    meldeDiagnose(`[ptt] ${via} übersprungen neuer-zug weil=${weil}`);
    return false;
  }
  // 17.10.2026 (lib/fertig.ts): Martin hat in diesem Zug „Fertig“ gedrückt — nichts öffnet mehr.
  if (zugEndeStand().fertig) {
    meldeDiagnose(`[ptt] ${via} übersprungen fertig weil=${weil}`);
    return false;
  }
  if (abspielenWartet() || neuLadenWartet()) {
    meldeDiagnose(`[ptt] ${via} übersprungen abspielen-oder-neu-laden weil=${weil}`);
    return false;
  }
  return true;
}

/**
 * 21.10.2026: der letzte ECHTE Tastendruck in einem Eingabefeld (performance.now(), null = noch keiner). Nur vertrauenswürdige
 * Ereignisse (`isTrusted`): Text, den die Seite selbst ins Feld legt (Vollständigkeits-Bremse, „ins Textfeld“, Diktat), löst
 * keines aus und zählt darum nie als Tippen. Reine Umschalt-/Befehlstasten zählen nicht.
 */
let letzteTippTaste: number | null = null;
const NUR_UMSCHALTER = new Set(["Shift", "Control", "Alt", "Meta", "CapsLock", "Fn", "OS"]);
function merkeTippTaste(ev: Event): void {
  if (!ev.isTrusted || !imEingabefeld(ev.target as HTMLElement | null)) return;
  if (ev instanceof KeyboardEvent && NUR_UMSCHALTER.has(ev.key)) return;
  letzteTippTaste = performance.now();
  // Auflage Sicherheitsprüfer (21.10.): wer tippt, will schreiben — ein VON SELBST geöffnetes Mikro schließt still, solange es noch keine Sprache hörte.
  if (tasteSchliesstMikro({ aufnahme: pttState === "recording", vonSelbst: mikroVonSelbstOffen(), ohneSprache: pttModal?.nachlauschOhneSprache() === true })) {
    meldeDiagnose("[ptt] von-selbst zu grund=tippt");
    pttVerwerfen("tippt", "taste");
  }
}
document.addEventListener("keydown", merkeTippTaste, { capture: true, passive: true });
document.addEventListener("input", merkeTippTaste, { capture: true, passive: true });

/**
 * 21.10.2026 (Martin 19:58; Auflagen Sicherheitsprüfer): der Text, den die Vollständigkeits-Bremse ins Feld gelegt hat — mit Zeit
 * und Zug (lib/bremse-feld.ts). Er zählt nie als Tippen; der nächste gesprochene Satz gehört ≤ 60 s im selben Zug dazu (pttFinishAndSend).
 */
let bremseMerker: BremseMerker | null = null;
/** Nach dem Einfügen durch die Bremse (oder einen Folgesatz): merken — nur, wenn vorher nichts Getipptes im Feld stand. */
function merkeBremseText(vorher: string): void {
  bremseMerker = merkeBremse({ vorher, jetzt: textField.value, alt: bremseMerker, um: Date.now(), zug: zugKennung() });
  meldeDiagnose(`[ptt-ende] bremse-text ${bremseMerker ? "gemerkt" : "nicht-gemerkt"}`);
}

/** Tippt Martin gerade (Text im Chatfeld, der nicht von der Bremse stammt — oder Fokus im Feld UND Tastendruck in den letzten 4 s)? */
function martinTipptGerade(): boolean {
  return martinTippt({
    aktiv: document.activeElement,
    letzteTasteVorMs: letzteTippTaste === null ? null : performance.now() - letzteTippTaste,
    feldText: textField.value,
    feldAusBremse: ausBremse(bremseMerker, textField.value),
  });
}

/**
 * 21.10.2026 (Martin 19:58): eine Frage wartete, das Mikro blieb wegen Tippens zu. Nach `TIPP_HINWEIS_NACH_MS` eine leise Zeile —
 * nur, wenn er dann nicht mehr tippt und noch derselbe Zug gilt (lib/schmied-warten.ts `tippHinweisZeigen`). Einmal je Zug; öffnet nichts.
 */
let tippHinweisZug = -1;
function tippSperreHinweisPlanen(zug: number): void {
  if (tippHinweisZug === zug) return;
  tippHinweisZug = zug;
  window.setTimeout(() => {
    const zeigen = tippHinweisZeigen({
      gleicherZug: zugKennung() === zug && !zugEndeStand().fertig,
      ruhe: pttState === "idle",
      sichtbar: document.visibilityState === "visible",
      vorn: autoMikroNichtVorn() === null,
      tipptNoch: martinTipptGerade(),
    });
    meldeDiagnose(`[ptt] tipp-hinweis ${zeigen ? "gezeigt" : "nicht"}`);
    if (zeigen) showToast(TIPP_SPERRE_HINWEIS, "info", 6000);
  }, TIPP_HINWEIS_NACH_MS);
}

/**
 * 17.10.2026 (Martin 11:15/11:19, lib/schmied-warten.ts `frageKam`): der Schmied fragt Martin, und nichts wartete auf ihn — das Mikro
 * öffnet nach seinem Vorlesen bzw. der Lesefrist wie nach einer Frage Vesperas: dieselbe Regel (mikroNachAntwort, „aus“ bleibt aus),
 * nur im selben, fertigen Zug (`schmiedOeffnenGilt`: kein neuer Zug, kein „Fertig“, kein Abspielen/Neuladen), nicht während eine
 * Stimme spricht, erst nach dem Ausklang aus dem Ruhezustand im Vordergrund — und, über startPttHandsFree, nur am vorderen Gerät,
 * nicht bei Anruf, langer Spur oder Aufnahme auf einem anderen Gerät. Tippt Martin (Fokus im Feld UND Tastendruck in den letzten 4 s), bleibt es zu
 * — dann kommt nach einer Wartezeit ein leiser Hinweis (tippSperreHinweisPlanen).
 */
// 22.10.2026: `blase` = der fragende Beitrag — trägt er eine Ja/Nein-Karte, antwortet Martin per Knopf (lib/ja-karte-offen.ts).
function schmiedFrageOeffnet(zug: number, id: EntwicklerId = "schmied", blase: HTMLElement | null = null): (weil: SchmiedOeffnetWeil) => void {
  const versuch = (weil: SchmiedOeffnetWeil): void => {
    mikroEntscheidungOffen = true; // M2: auch beim Öffnen selbst (die Frist kann Vespera-Fragen dazwischen gehabt haben)
    // 22.10.2026 (Martin 07:55): eine Ja/Nein-Karte beantwortet nur der Knopf — fragt dieser Beitrag per Karte (offen oder schon getippt)
    // oder steht irgendeine Karte noch offen, öffnet das Mikro NICHT von selbst. Nur sperren, nie öffnen; Martins Druck bleibt möglich.
    const karte = jaKarteHaeltMikro(refs.chat, id, blase, Date.now(), null, meldeDiagnose);
    if (karte) {
      meldeDiagnose(`[ptt] mikro ${id}-frage zu grund=${karte} weil=${weil}`);
      return;
    }
    const einst = loadSettings();
    const entscheid = mikroNachAntwort({
      stufe: autoMikroGeltend(einst.autoMikroNachFrage),
      wartetAufMartin: true,
      verabschiedet: false,
      jev: null,
      fensterMs: nachlauschFenster(einst.nachlauschMs),
      wiedergabe: wiedergabeJetzt(),
      langeSpur: langeSpurLaeuft(),
    });
    const via = entscheid.via;
    if (!via) {
      meldeDiagnose(`[ptt] mikro schmied-frage zu grund=${entscheid.grund} weil=${weil}`);
      return;
    }
    if (!schmiedOeffnenGilt(via, zug, weil)) return;
    if (ttsWiedergabeOffen()) {
      meldeDiagnose(`[ptt] ${via} übersprungen stimme-laeuft weil=${weil}`);
      return;
    }
    if (martinTipptGerade()) {
      meldeDiagnose(`[ptt] ${via} übersprungen tippt weil=${weil}`);
      tippSperreHinweisPlanen(zug);
      return;
    }
    // 21.10.2026 (Martin 07:12–07:14): steht noch ein Beitrag in der Vorlese-Reihe, öffnet es erst nach ihrem Ende (das neueste gewinnt).
    if (mikroErstNachReihe(via, id, weil, () => versuch(weil))) return;
    meldeDiagnose(`[ptt] ${via} ${id} weil=${weil}`);
    nachAusklang(
      () => {
        if (pttState !== "idle") {
          meldeDiagnose(`[ptt] ${via} übersprungen zustand=${pttState}`);
          return;
        }
        if (document.visibilityState !== "visible") {
          meldeDiagnose(`[ptt] ${via} übersprungen hintergrund`);
          return;
        }
        // Auch im Ausklang kann er zu tippen begonnen haben.
        if (martinTipptGerade()) {
          meldeDiagnose(`[ptt] ${via} übersprungen tippt weil=${weil}`);
          tippSperreHinweisPlanen(zug);
          return;
        }
        startPttHandsFree(via, entscheid.fensterMs);
      },
      (grund) => {
        meldeDiagnose(`[ptt] ${via} übersprungen ${grund} weil=${weil}`);
        // 19.10.2026 (Martin 13:37): eine Stimme (z. B. eine Info des Schmieds) begann im Ausklang — die offene Frage vergisst das Mikro nicht:
        // nach dem Ende dieses Vorlesens versucht es genau einmal noch einmal (derselbe Zug, dieselben Wächter).
        if (weil === "nach-wiedergabe") return; // nur EIN Wiederholversuch
        const gemerkt = alleWarten().some((w) => w.frageMikroNachWiedergabe(() => versuch("nach-wiedergabe")));
        if (gemerkt) meldeDiagnose(`[ptt] ${via} wartet=wiedergabe-ende`);
        // 21.10.2026: kein Warten zählt diese Stimme mehr (z. B. ein Hintergrund-Beitrag) — dann trägt die Vorlese-Reihe den Versuch.
        else if (vorleseReihe.mikroNachReihe(() => versuch("nach-wiedergabe"))) meldeDiagnose(`[ptt] ${via} ${id} wartet=reihe-ende weil=${weil}`);
      },
    );
  };
  return versuch;
}

/**
 * 21.10.2026 (Martin 07:12–07:14, lib/vorlese-reihe.ts): ein Auto-Mikro eines Entwicklers öffnet nicht mitten in der Vorlese-Reihe —
 * steht noch ein Beitrag an, versucht `versuch` es nach ihrem Ende genau einmal (`true`). Sonst `false`: jetzt öffnen; ein älterer
 * verschobener Versuch verfällt (das neueste gewinnt, keine Doppelöffnung).
 */
function mikroErstNachReihe(via: string, id: EntwicklerId, weil: string, versuch: () => void): boolean {
  if (!vorleseReihe.mikroNachReihe(versuch)) return false;
  meldeDiagnose(`[ptt] ${via} ${id} wartet=reihe-ende weil=${weil}`);
  return true;
}

/**
 * Das Auto-Mikro entfiel im Ausklang, weil eine Stimme begann (`grund`, lib/ausklang.ts). Stehen Beiträge des Schmieds zum
 * Vorlesen an, versucht es GENAU EINMAL nach dem letzten noch einmal (lib/schmied-warten.ts `mikroNachVorlesen`) — mit denselben
 * Wächtern wie das Warten auf ihn, und nur, wenn die Stufe nicht inzwischen „aus“ ist. `oeffne` prüft Ausklang, Ruhezustand
 * (Martin spricht/nimmt auf → zu) und Vordergrund selbst. Ein Stopp oder die Sprechtaste verwirft es (stopCurrentTts).
 * ⚠️ 16.10.2026, 07:21–07:24: ohne das blieb das Mikro nach Vesperas Frage zu, weil der Schmied im Ausklang zu sprechen begann.
 */
function mikroNachSchmiedVorlesen(via: string, grund: string, oeffne: () => void, id: EntwicklerId = "schmied"): void {
  meldeDiagnose(`[ptt] ${via} übersprungen ${grund}`);
  const zug = zugKennung();
  const nochmal = (): void => {
    if (!loadSettings().autoMikroNachFrage) {
      meldeDiagnose(`[ptt] ${via} übersprungen stufe-aus weil=nach-schmied`);
      return;
    }
    if (!schmiedOeffnenGilt(via, zug, "nach-schmied")) return;
    // 21.10.2026 (Prüfauflage): steht nach seinem Ende noch ein Beitrag in der Reihe, erst nach deren Ende.
    if (mikroErstNachReihe(via, id, "nach-schmied", nochmal)) return;
    meldeDiagnose(`[ptt] ${via} schmied weil=nach-schmied`);
    oeffne();
  };
  if (wartenVon(id).mikroNachVorlesen(nochmal)) meldeDiagnose(`[ptt] ${via} wartet=schmied-vorlesen`);
  // 21.10.2026 (Martin 07:13:36, `nach-frage übersprungen wiedergabe`): sein eigenes Vorlesen war schon zu Ende, die Stimme gehörte einem
  // anderen Beitrag der Reihe — der Versuch geht nicht verloren, sondern wartet auf das Ende der ganzen Reihe.
  else if (vorleseReihe.mikroNachReihe(nochmal)) meldeDiagnose(`[ptt] ${via} ${id} wartet=reihe-ende weil=${grund}`);
}

/**
 * 22.10.2026 (Martin 08:12, lib/frage-rueckkehr.ts): die Seite ist wieder sichtbar und vorn, und Vesperas Frage wartet noch (gemerkt im
 * Hintergrund-Zweig des Frage-Mikros). GENAU EIN Versuch, derselbe Weg wie das Frage-Mikro: mikroNachAntwort (Stufe „aus“, Musik, lange
 * Spur, „Fertig“ → zu), schmiedOeffnenGilt (neuer Zug, „Fertig“, Abspielen/Neuladen), nach dem Ausklang aus der Ruhe im Vordergrund,
 * nicht beim Tippen — und startPttHandsFree mit allen eigenen Wächtern (vorn, anderes Gerät, Seite offen, Doppelstart). Journal ohne Text.
 */
function frageRueckkehrPruefen(): void {
  if (!frageRueckkehr.wartet()) return;
  const stand = zugEndeStand();
  const erg = frageRueckkehr.rueckkehr({
    sichtbar: document.visibilityState === "visible",
    fokus: document.hasFocus(),
    zug: zugKennung(),
    zugFertig: stand.zugFertig,
    fertig: stand.fertig === true,
    ruhe: pttState === "idle",
    stimme: ttsWiedergabeOffen(),
    anruf: connected || loadSettings().pttMode === "call",
    karteOffen: jaKarteOffen(refs.chat, Date.now()),
    entscheidungOffen: mikroEntscheidungOffen,
    tippt: martinTipptGerade(),
  });
  if (erg.tun === "nichts") return;
  if (erg.tun === "zu") {
    meldeDiagnose(`[ptt] frage-rueckkehr zu grund=${erg.grund}`);
    return;
  }
  const einst = loadSettings();
  const entscheid = mikroNachAntwort({
    stufe: autoMikroGeltend(einst.autoMikroNachFrage),
    wartetAufMartin: true,
    verabschiedet: false,
    jev: null,
    fensterMs: nachlauschFenster(einst.nachlauschMs),
    wiedergabe: wiedergabeJetzt(),
    langeSpur: langeSpurLaeuft(),
    fertig: stand.fertig,
  });
  const via = entscheid.via;
  if (via !== "nach-frage") {
    meldeDiagnose(`[ptt] frage-rueckkehr zu grund=${entscheid.grund}`);
    return;
  }
  if (!schmiedOeffnenGilt(via, erg.zug, "rueckkehr")) {
    meldeDiagnose("[ptt] frage-rueckkehr zu grund=gilt-nicht");
    return;
  }
  meldeDiagnose("[ptt] frage-rueckkehr offen grund=frage");
  nachAusklang(
    () => {
      if (pttState !== "idle") return meldeDiagnose(`[ptt] ${via} übersprungen zustand=${pttState} weil=rueckkehr`);
      if (document.visibilityState !== "visible") return meldeDiagnose(`[ptt] ${via} übersprungen hintergrund weil=rueckkehr`);
      if (martinTipptGerade()) return meldeDiagnose(`[ptt] ${via} übersprungen tippt weil=rueckkehr`);
      // M2 (wie schmiedFrageOeffnet): direkt vor dem Öffnen — ein alter Wert (auch einer, der im Ausklang kam) schaltet kein schnelles
      // nacktes Ja frei (lib/ptt-modal.ts `entscheidungOffen`); bei der Rückkehr gilt die sichere Regel.
      mikroEntscheidungOffen = true;
      startPttHandsFree(via, entscheid.fensterMs);
    },
    (grund) => meldeDiagnose(`[ptt] ${via} übersprungen ${grund} weil=rueckkehr`),
  );
}
document.addEventListener("visibilitychange", frageRueckkehrPruefen);
window.addEventListener("focus", frageRueckkehrPruefen);

/**
 * „Martin → Schmied“ an Martins jüngster Blase und ihrem Eintrag im lokalen Verlauf (16.10.2026, lib/adressat-vermerk.ts). Belegt
 * ist es, weil der Server `schmied` gemeldet hat: diese Nachricht ging an ihn (gespeichert mit `metadata.an = "schmied"`).
 */
function markiereLetzteNachrichtAnSchmied(an: "schmied" | ZusatzId = "schmied"): void {
  const vermerk = adressatVermerkAus("martin", { an });
  const blasen = refs.chat.querySelectorAll<HTMLElement>(".bubble.user");
  const blase = blasen[blasen.length - 1];
  if (blase) {
    setzeAdressatVermerk(blase, vermerk);
    sperreBeantworteteFragen(refs.chat, meldeFrageGesperrt);
  }
  for (let i = history.length - 1; i >= 0; i--) {
    const e = history[i];
    if (e?.kind !== "user") continue;
    e.an = an;
    saveHistory(history);
    break;
  }
}

/**
 * 17.10.2026 (Martin 15:08): die Nachricht ging an den Zimmermann (der Server hat es entschieden) — „Martin → Zimmermann“; kein Mikro-Warten.
 * 17.10.2026 (Martin 19:46): ebenso an den Hüter — `detail.id` sagt, an wen.
 */
window.addEventListener(ZUSATZ_ANGESPROCHEN, (ev) => martinSprachEntwicklerAn(zusatzAusEreignis(ev), false));

/*
 * 20.10.2026 (Martin 09:39): „Offen <Zahl>“ — Fragen der Entwickler an Martin ohne Antwort (lib/offene-fragen.ts). Nur Anzeige:
 * erfasst im Live-Weg oben, erledigt durch eine belegte Nachricht an genau diesen Absender (`martinSprachEntwicklerAn`, Bus).
 * Ein Tipp auf einen Eintrag führt zur Blase und stellt „An:“ auf den Absender — das Mikro öffnet NICHT, kein Ja, kein Serverweg.
 */
let offenAnzeige: OffenAnzeige | null = null;
// Nur Konto 1 (wie „Entwicklung“), gespeichert je Konto; beim Kontowechsel löscht der Wipe unten auch diese Schlüssel.
const offeneFragen = new OffeneFragen({
  melde: meldeDiagnose,
  geaendert: () => offenAnzeige?.zeichne(),
  konto: () => (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid,
});
offenAnzeige = verdrahteOffeneFragen(offeneFragen, { waehle: zeigeOffeneFrage });

/**
 * „Antworten“ an einem Eintrag von „Offen“ (22.10.2026, Martin: „ich kann nur auf ‚Erledigt‘ klicken, aber nichts anstoßen“): in den
 * Chat — auch von einer Vollseite aus (Training, Morgenreport …: `goToView` schließt sie; vorher wechselte nur der Reiter darunter und
 * die Seite blieb obenauf) —, „An:“ auf den Absender, zur Blase, und das Antwortfeld bekommt den Fokus. Das Mikro öffnet NICHT.
 */
function zeigeOffeneFrage(f: OffeneFrage): void {
  if (document.body.dataset.activeView !== "chat" || liegtEtwasUeberDemChat()) goToView("chat");
  empfaengerWahl.waehle(f.von);
  oeffneAntwortfeld();
  const blase = refs.chat.querySelector<HTMLElement>(`.bubble[data-ts="${f.ts}"]`);
  meldeDiagnose(`[offen] gewählt von=${f.von} an=${empfaengerWahl.empfaenger()} blase=${blase ? 1 : 0}`);
  if (!blase) {
    showToast("Die Frage steht nicht mehr im Chat — „An:“ ist gesetzt.", "info");
    return;
  }
  let ruhig = false;
  try {
    ruhig = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    /* ohne matchMedia: sanft */
  }
  blase.scrollIntoView({ block: "center", behavior: ruhig ? "auto" : "smooth" });
  blase.classList.add("offene-frage-ziel");
  setTimeout(() => blase.classList.remove("offene-frage-ziel"), 2500);
}

/*
 * 15.10.2026 (Martin, 21:29): MARTIN hat den Schmied angesprochen („Hey Schmied, …“) — der Server hat Vespera nicht gefragt
 * (server/dreier-modus.ts, `nachrichtAnSchmied`), es gibt also kein Vorlese-Ende. Das Mikro entscheidet wie nach einer
 * Frage (die Antwort des Schmieds wartet auf Martin) — dieselbe Regel mikroNachAntwort, Stufe „aus“ bleibt aus — und
 * wartet dann auf seinen Beitrag oder SCHMIED_WARTEN_MS.
 */
window.addEventListener(SCHMIED_ANGESPROCHEN, (ev) => {
  // 16.10.2026 (server/dreier-modus.ts): Martin hat ihn verabschiedet — es kommt nichts mehr, das Mikro wartet nicht auf ihn.
  martinSprachEntwicklerAn("schmied", (ev as CustomEvent<SchmiedAngesprochenDetail | undefined>).detail?.abschied === true);
});

/**
 * Martin hat diesen Entwickler angesprochen (Schmied: Ereignis `schmied`; 19.10.2026 auch Zimmermann und Hüter: `zimmermann`/`hueter`):
 * „Martin → <Name>“ an der Blase, und das Mikro wartet auf SEINE Antwort (sein eigenes Warten, `wartenVon`) — dieselbe Regel wie nach einer Frage.
 */
function martinSprachEntwicklerAn(id: EntwicklerId, abschied: boolean): void {
  // 17.10.2026 (M2): Martin sprach ihn an — was er fragt, kann eine Entscheidung sein; kein 400-ms-Ja.
  mikroEntscheidungOffen = true;
  // Prüfauflage A3: ab jetzt wartet das Mikro auf SEINE Antwort — nur eine Karte danach (oder eine noch offene) hält es zu.
  const angesprochenSeit = Date.now();
  // 16.10.2026: die Nachricht ging an ihn (der Server hat es entschieden) — „Martin → <Name>“ an ihrer Blase.
  markiereLetzteNachrichtAnSchmied(id === "schmied" ? "schmied" : (id));
  // 20.10.2026 (lib/offene-fragen.ts): die Nachricht ging belegt an ihn — seine offenen Fragen sind beantwortet (nur seine).
  offeneFragen.martinAntwortete(id);
  const warten = wartenVon(id);
  if (abschied) warten.abbrechen();
  const einst = loadSettings();
  const stufe = autoMikroGeltend(einst.autoMikroNachFrage);
  const entscheid = mikroNachAntwort({
    stufe,
    wartetAufMartin: true,
    verabschiedet: false,
    jev: null,
    fensterMs: nachlauschFenster(einst.nachlauschMs),
    wiedergabe: wiedergabeJetzt(),
    langeSpur: langeSpurLaeuft(),
  });
  const via = entscheid.via;
  if (!via) return;
  const oeffne = (): void => {
    // 22.10.2026 (Martin 07:55): antwortete er mit einer Ja/Nein-Karte (oder steht eine Karte offen), antwortet Martin per Knopf —
    // das Mikro öffnet NICHT von selbst (nur sperren, nie öffnen; Martins Druck bleibt möglich). Lib/ja-karte-offen.ts.
    const karte = jaKarteHaeltMikro(refs.chat, id, null, Date.now(), angesprochenSeit, meldeDiagnose);
    if (karte) {
      meldeDiagnose(`[ptt] ${via} ${id} zu grund=${karte}`);
      return;
    }
    nachAusklang(
      () => {
        if (pttState !== "idle") {
          meldeDiagnose(`[ptt] ${via} übersprungen zustand=${pttState}`);
          return;
        }
        if (document.visibilityState !== "visible") {
          meldeDiagnose(`[ptt] ${via} übersprungen hintergrund`);
          return;
        }
        startPttHandsFree(via, entscheid.fensterMs);
      },
      (grund) => mikroNachSchmiedVorlesen(via, grund, oeffne, id),
    );
  };
  if (abschied) {
    meldeDiagnose(`[ptt] ${via} ${id}-abschied von=martin`);
    oeffne();
    return;
  }
  wartetAufSchmied(via, oeffne,
    // Martin hat ihn angesprochen: nur ein NEUER Beitrag zählt (lib/schmied-warten.ts), keiner aus Vesperas letztem Zug.
    false,
    id,
  );
}

/*
 * „Hey Vespera" — das Wach-Wort. (22.09.2026, Ticket 2503)
 *
 * Martin: *„Ich moechte ein Wach-Wort, das im Hintergrund lauscht, das Mikro
 * selbst oeffnet und die Eingabe startet — ohne Push-to-Talk zu druecken."*
 *
 * ⚠️ Das Lauschen selbst liegt im Nativen (PawbotWachWort.swift): nur dort
 *    gibt es im Hintergrund ein Mikrofon, und nur dort laeuft die Erkennung
 *    auf dem GERAET. Hier steht bloss, was danach geschieht.
 *
 * ⚠️ Und was danach geschieht, ist DERSELBE Weg wie beim Knopf —
 *    startPttHandsFree. Ein eigener Startweg waere ein zweiter Besitzer;
 *    genau daran hing heute schon Ticket 2502 (offenes Mikro ohne Fenster),
 *    weil dort nur der letzte Schritt gerufen wurde.
 */
/**
 * Wann ist das Wach-Wort zuletzt im HINTERGRUND gefallen? (23.09.2026)
 *
 * ⚠️ Martin, 09:16, unterwegs: *„Hey Vespera soll im Hintergrund das Mikro
 *    wieder öffnen und das Modal zeigen."* Im Hintergrund gibt es fuer die
 *    Seite weder ein Mikrofon noch ein Fenster — iOS gibt beides nur der
 *    sichtbaren App. Bisher lief der Start trotzdem los und scheiterte
 *    wortlos. Jetzt wird das Wort VORGEMERKT: wer die App innerhalb von
 *    WACHWORT_NACHHOLEN_MS oeffnet, landet direkt in der offenen Aufnahme.
 *
 * Nacharbeit 24.09.2026 (Ticket 2536): Vormerkung, Frist und Satz stehen in
 * lib/wachwort-satz.ts (`WachwortVormerkung`) — dort mit Verhaltenstests.
 * JEDES gefallene Wort geht durch sie, auch im Vordergrund: dann gilt die
 * Frist auch für ein Ereignis, das erst beim Öffnen ankommt.
 */
const wachwortVormerkung = new WachwortVormerkung();

/**
 * Das vorgemerkte Wort einlösen — sofort (Vordergrund) oder beim Öffnen.
 * (Nacharbeit 24.09.2026, Ticket 2536)
 *
 * ⚠️ Der EINZIGE Aufruf von `wachwortWeiter`: erst nach der Frist, erst aus
 *    der Ruhe. Ein verfallenes Wort bekommt nur eine Zeile ins Protokoll.
 */
function wachwortEinloesen(nachgeholt: boolean): void {
  const e = wachwortVormerkung.nimm(Date.now());
  if (!e) return;
  const nachS = Math.round(e.nachMs / 1000);
  /*
   * Prüfauflage H1 (20.10.2026, Vollständigkeits-Bremse): ein offener Satz geht nie an die KI und verfällt nie still —
   * er kommt ins Eingabefeld, VOR Frist, Ruhe und Wiedergabe (das Feld stört keine Antwort und keine Aufnahme).
   */
  const feld = wachwortFeldText(e);
  if (feld !== null) {
    meldeDiagnose(`[wachwort] satz nicht gesendet grund=unvollstaendig nach=${nachS}s — ins Eingabefeld`);
    wachwortZumChat();
    wachwortInsFeld(feld);
    return;
  }
  if (e.art === "verfallen") {
    meldeDiagnose(`[wachwort] verfallen nach=${nachS}s`);
    return;
  }
  if (pttState !== "idle") return;
  /*
   * 13.10.2026 (ios-mikro-offen): Die Wiedergabe bricht NUR `startPttRecording` ab, hinter allen Sperren (Gespräch,
   * Aufnahme anderswo, Anrufmodus) — also nur, wenn wirklich zugehört wird. Ein nachgeholtes Wort darf keine
   * inzwischen gestartete neue Antwort abwürgen.
   */
  if (wachwortNachholenStoertWiedergabe(nachgeholt, ttsWiedergabeOffen())) {
    meldeDiagnose(`[wachwort] nachgeholt verworfen nach=${nachS}s (neue Antwort läuft)`);
    return;
  }
  if (nachgeholt) {
    meldeDiagnose(`[wachwort] nachgeholt nach=${nachS}s ${satzMerkmale(e.satz)}`);
  }
  wachwortWeiter(e.satz);
}

/**
 * „Hey Vespera“ auf einer anderen Seite: zuerst in den Chat. (06.10.2026)
 *
 * Martin: „Wenn ich auf der Seite im Essenz-Spiegel oder auch woanders bin und dann ‚Hey Vespera‘ sage, dann soll das
 * System auf Chat gehen, sonst ist das Modal im Hintergrund!“ Vollseiten (Essenz-Spiegel, Human Design, Tiefe Fragen,
 * Mein Alltag …) liegen ÜBER der Aufnahmekarte, und die Antwort erscheint im Chat darunter. goToView schließt alle
 * Overlays und Sheets und wechselt den Reiter — dieselbe Stelle wie „per Sprache zum Chat“ (Ticket 195).
 * Im Chat ohne offene Seite ist es ein Nichts.
 */
function wachwortZumChat(): void {
  const aktiv = document.body.dataset.activeView;
  const wareAnderswo = !!aktiv && aktiv !== "chat";
  goToView("chat");
  if (wareAnderswo) meldeDiagnose(`[wachwort] zum Chat von=${aktiv}`);
}

/**
 * Was nach dem Wach-Wort geschieht — der Satz geht ab, oder das Mikro öffnet
 * sich wie bisher. (24.09.2026, Ticket 2536)
 *
 * Entschieden wird in lib/wachwort-satz.ts; hier stehen nur die zwei Wege,
 * die es schon gab. ⚠️ Das Mikro kommt über DENSELBEN Startweg wie der Knopf
 * (Ticket 2502) — `startPttHandsFree`, kein eigener.
 */
function wachwortWeiter(satz: WachwortSatz | null): void {
  // 17.10.2026: läuft eine lange Spur, hörte der Lauscher vermutlich sie selbst — weder Mikro noch Satz (lange-spur.ts).
  if (mikroVonSelbstGesperrt("wachwort")) return;
  // 17.10.2026: nimmt ein anderes Gerät gerade auf, gehört der Satz dorthin — weder Mikro noch Satz hier (seiten-abgleich.ts).
  if (zweitesGeraetHaeltZu("wachwort")) return;
  wachwortAusfuehren(
    satz,
    {
      anrufModus: loadSettings().pttMode === "call",
      bildWartet: pendingComposerImages.length > 0,
    },
    {
      sendeSatz: pttSendeWachwortSatz,
      oeffneMikro: () => startPttHandsFree("wachwort"),
      insFeld: wachwortInsFeld,
      zumChat: wachwortZumChat,
      melde: meldeDiagnose,
    },
  );
}

/** 20.10.2026 (Vollständigkeits-Bremse): ein offener Satz nach dem Wach-Wort geht nicht ab — er steht im Eingabefeld. */
function wachwortInsFeld(text: string): void {
  const vorher = textField.value;
  pttLegeInsTextfeld(text);
  merkeBremseText(vorher);
  showToast(BREMSE_ZURUECK_HINWEIS, "warning", 8000);
}

/** Höchstens alle 30 s ein Hinweis, damit ein Lauscher, der die Meditation immer wieder hört, nicht klappert. */
let wachwortHinweisAm = 0;
function wachwortGesperrtHinweis(): void {
  const jetzt = Date.now();
  if (jetzt - wachwortHinweisAm < 30_000) return;
  wachwortHinweisAm = jetzt;
  window.dispatchEvent(
    new CustomEvent("voice-v8:toast", { detail: { message: "Es läuft eine lange Wiedergabe – bitte die Sprechtaste benutzen.", kind: "info" } }),
  );
}

async function wachwortEinrichten(): Promise<void> {
  const nv = await import("./lib/native-voice.js");
  if (!nv.isNativePlatform()) return;
  const wunsch = loadSettings().wachwort === true;
  // 23.09.2026: Der Lauscher meldet seine Wechsel (lauscht, wartet, Fehler)
  // ins Protokoll — ab Build 67. Vorher sah der Server davon NICHTS, und
  // „funktioniert nicht" liess sich keiner Stelle zuordnen.
  const wz = await import("./lib/wachwort-zustand.js");
  // Ab Build 68 kommt JEDER Wechsel des Lauschers hier an (vorher drosselte
  // das Native auf fünf Minuten, und die Zustandszeile blieb stehen). Die
  // Drossel gehört nur vor das Server-Protokoll, und sie lässt jeden
  // Zustandswechsel durch (Ticket 2544, lib/wachwort-zustand.ts).
  const melden = wz.wachwortMelder();
  // 20.10.2026 (Martin: „geht oftmals nur jedes zweite Mal“): Fehlversuche vor dem Treffer zählen, nur Zahlen.
  const fehlversuche = wz.wachwortFehlversuche();
  await nv.aufWachwortSpurHoeren((zeile) => {
    // Ticket 2822: der Mikrofon-Motor kommt über denselben Weg. Seine Zeile
    // trägt ihr eigenes Präfix „[mikro-motor]", geht immer ins Protokoll und
    // ist kein Zustand des Lauschers (der Punkt bleibt, wie er ist).
    if (wz.istMotorZeile(zeile)) {
      meldeDiagnose(zeile.trim());
      // Prüfung 06.10.2026: den Stand der Tonsitzung für die Zeile `[tts-neu]` merken (wachwort-zustand.ts).
      wz.merkeMotorZeile(zeile);
      return;
    }
    if (melden(zeile, Date.now())) meldeDiagnose(`[wachwort] ${zeile}`);
    const fehlversuchZeile = fehlversuche(zeile, Date.now());
    if (fehlversuchZeile) meldeDiagnose(`[wachwort] ${fehlversuchZeile}`);
    // 23.09.2026: sichtbar machen (Martin: „Ich sehe nichts.") — die
    // Einstellungen zeigen den letzten Zustand unter dem Schalter.
    wz.merkeWachwortZustand(zeile);
  });
  // Zustand der App (mikroBeimVorlesen) einmal beim Start holen, damit schon die erste Antwort ihn kennt.
  // 14.10.2026: einmal pro Start an die native Kippe — ohne Wahl AUS (AN bricht das Vorlesen ab), mit Wahl die Wahl.
  void mikroBeimVorlesenAnwenden(mikroOffenGilt());
  mikroBeimVorlesenAuffrischen();
  const an = await nv.wachwortSetzen(wunsch);
  meldeDiagnose(`[wachwort] eingerichtet wunsch=${wunsch ? "an" : "aus"} an=${an ? "ja" : "nein"}`);
  // Das ruhende Mikro gehoert dem Lauscher, nicht dem Vorwaermen (ptt.ts).
  setzeWachwortVorrang(an);
  /*
   * ⚠️⚠️ 23.09.2026 — HIER STAND `if (!an) return;`.
   *
   *    Martin, 12:19: *„Hier bei Vespera geht immer noch nichts."* Das
   *    Protokoll: Start um 12:17:47 mit `wunsch=aus`, um 12:18:19 in den
   *    Einstellungen angeschaltet, um 12:18:28 `[wachwort] gehoert` — und
   *    danach nichts. Die Einrichtung war vor dem Hoerer ausgestiegen; das
   *    Wort fiel in eine Seite, die nicht zuhoerte. Dieselbe Rueckkehr liess
   *    auch die Bruecke fuer das Seiten-Mikro weg: der Lauscher trat nicht
   *    beiseite, iOS stellte die Spur der Sprechtaste stumm, und das Fenster
   *    zeigte eine flache Linie ohne Text.
   *
   *    Hoerer und Bruecke gehoeren deshalb IMMER eingerichtet. Wer den
   *    Schalter spaeter umlegt, aendert nur, ob das Native lauscht — ist er
   *    aus, kommt einfach kein Ereignis.
   */
  // Der Lauscher tritt beiseite, solange die Seite ein Mikrofon haelt
  // (Sprechtaste, Vorwaermen) — er sieht getUserMedia nicht. Ab Build 67.
  window.addEventListener("voice-v8:web-mikro", (e) => {
    const offen = (e as CustomEvent<{ offen?: boolean }>).detail?.offen === true;
    void nv.wachwortWebMikro(offen);
  });
  void nv.wachwortWebMikro(webMikroOffen());
  /*
   * Ein Takt als Netz: Leitungen, die jemand anderes schliesst (das Mikro der
   * Tiefen Frage), melden ihr Ende nicht — meldeWebMikro leitet es aus dem
   * echten Zustand der Spuren ab und meldet nur einen WECHSEL.
   */
  window.setInterval(meldeWebMikro, 1500);
  await nv.aufWachwortHoeren((ereignis) => {
    const sichtbar = document.visibilityState === "visible";
    // Ticket 2536: ob ein Satz mitkam — Länge und Zeichenzahl, nie der Text.
    meldeDiagnose(
      `[wachwort] gefallen sichtbar=${sichtbar ? "ja" : "nein"} zustand=${pttState} ${satzMerkmale(ereignis.satz)}`,
    );
    wz.merkeWachwortZustand("gehoert");
    /*
     * ⚠️ Dieselben Waechter wie beim Auto-Mikro: nichts anfassen, wenn schon
     *    etwas laeuft. Der Lauscher tritt zwar nativ beiseite, aber das
     *    Ereignis kommt aus einer anderen Welt — verlassen wird sich auf den
     *    eigenen Zustand.
     */
    if (pttState !== "idle") return;
    /*
     * 17.10.2026 (lange-spur.ts, Prüfung): läuft eine lange Spur, hörte der Lauscher vermutlich sie selbst. Weder Mikro noch
     * Satz noch Vormerken — im Vordergrund wie im Hintergrund (auch bei gesperrtem Telefon, auch der Nachtlauf). Im
     * Vordergrund sagt ein leiser Hinweis, was stattdessen geht.
     */
    if (mikroVonSelbstGesperrt("wachwort")) {
      if (sichtbar) wachwortGesperrtHinweis();
      return;
    }
    /*
     * 17.10.2026 (Journal 08:16): Martin nimmt an einem ANDEREN Gerät auf (Mac, Taste) — das „Vespera“ in seinem Satz ist
     * kein Ruf an dieses Gerät. Weder Mikro noch Satz noch Vormerken (im Vorder- wie im Hintergrund). Die Sprechtaste hier
     * bleibt frei (seiten-abgleich.ts „Aufnahme über Geräte“).
     */
    if (zweitesGeraetHaeltZu("wachwort")) return;
    /*
     * Ticket 2536 (Nacharbeit): vormerken, immer — im Hintergrund löst das
     * Öffnen ein, im Vordergrund sofort. Beide Male mit derselben Frist.
     */
    if (!sichtbar) {
      /*
       * 13.10.2026 (Martin im Auto, Navigation vorn): ein mitgebrachter Satz braucht kein Mikro — er geht auch
       * bei verdeckter App ab (wachwortImHintergrund). Ohne brauchbaren Satz bleibt es beim Vormerken.
       */
      wachwortImHintergrund(
        ereignis.satz,
        { anrufModus: loadSettings().pttMode === "call", bildWartet: pendingComposerImages.length > 0 },
        wachwortVormerkung,
        { sende: pttSendeWachwortSatz, melde: meldeDiagnose, insFeld: wachwortInsFeld },
        Date.now(),
      );
      return;
    }
    wachwortVormerkung.merke(ereignis.satz, Date.now());
    wachwortEinloesen(false);
  });
}
void wachwortEinrichten();

// 23.09.2026: Abstürze vom letzten Lauf ins Protokoll — nativ ab Build 71,
// dazu ein Seiteninhalt, der ohne Abschied endete. Siehe lib/absturz-melden.ts.
void (async () => {
  const nv = await import("./lib/native-voice.js");
  const { absturzMelden } = await import("./lib/absturz-melden.js");
  let speicher: Storage | null = null;
  try {
    speicher = window.sessionStorage;
  } catch {
    speicher = null;
  }
  await absturzMelden({
    speicher,
    melde: meldeDiagnose,
    holeBerichte: nv.isNativePlatform() ? nv.absturzBerichte : null,
    beimAbschied: (f) => window.addEventListener("pagehide", f),
  });
})();

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible" || !wachwortVormerkung.offen) return;
  wachwortEinloesen(true);
});

const freihandBtn = document.getElementById("dock-freihand");
// 2026-07-10 (Martin): Freihand GEPARKT — die Aufnehmen-Schneiden-Neustarten-
// Architektur verliert Satzanfaenge (Neustart-Totzeit) und schneidet bei
// Denkpausen ab. Wird als kontinuierlicher Puffer neu gebaut und erst nach
// selbst durchgefuehrtem E2E-Test (Fake-Audio) wieder aktiviert. Bis dahin:
// Anruf-Knopf = Freisprechen.
freihandBtn?.setAttribute("hidden", "true");

function freihandSyncButton(): void {
  freihandBtn?.setAttribute("aria-pressed", String(freihandActive));
  freihandBtn?.classList.toggle("is-active", freihandActive);
  const lbl = freihandBtn?.querySelector(".dock-btn-label");
  if (lbl) lbl.textContent = freihandActive ? "Höre zu" : "Freihand";
  syncVoiceModeExclusivity();
  // 2026-08-10: Der Selbstheilungs-Puls laeuft nur, solange Freihand an ist.
  // Diese Funktion laeuft bei JEDEM Wechsel — darum steht die Kopplung hier
  // und nicht an den einzelnen Umschalt-Stellen, wo sie eine vergessen
  // koennte.
  if (freihandActive) fhWatchdogStart();
  else fhWatchdogStop();
}

// 2026-07-10 (Martin, "Architektur zu fragil"): MODUS-EXKLUSIVITAET —
// waehrend Freihand ist Anrufen gesperrt, waehrend eines Anrufs Freihand.
// EIN Sync-Punkt, aufgerufen beim Umschalten + im 2s-Puls (faengt auch
// Anruf-Enden ohne Event).
function syncVoiceModeExclusivity(): void {
  /*
   * ⚠️ 22.09.2026 — DER ANRUF-RIEGEL HAENGT HIER, NICHT AN SECHS STELLEN.
   *
   * Ein Anruf endet an sechs verschiedenen Orten (Auflegen, Abriss,
   * Aufraeumen, Moduswechsel). Den Riegel an jeder einzeln zu loesen waere
   * nur eine Frage der Zeit, bis eine vergessen wird — diese Funktion laeuft
   * bei JEDEM Wechsel und leitet ihn aus dem echten Zustand ab.
   *
   * Warum es ihn braucht: das Warm-Mikro blockiert den Anruf-Mikro-Zugriff,
   * LiveKit bekaeme ein stummes Mikro (Martin, 12.07.). Die Freigabe vor dem
   * Anruf ist ein einmaliger Griff; seit pre.3151 waermt der Browser nach
   * jeder Antwort erneut vor.
   */
  setzeAnrufAktiv(connected || connecting);
  // Der Anruf ist vorbei, bevor die Musik „fertig“ meldete: die Stufenkarte geht mit ihm (lib/lied-fortschritt.ts).
  anrufZustandFuerLied(connected);
  const callBtn = document.querySelector<HTMLButtonElement>(".dock-btn--call");
  if (callBtn) {
    callBtn.disabled = freihandActive;
    callBtn.title = freihandActive
      ? "Während Freihand nicht verfügbar — Freihand zuerst ausschalten"
      : "Anrufen";
  }
  if (freihandBtn instanceof HTMLButtonElement) {
    freihandBtn.disabled = connected;
    if (connected) freihandBtn.title = "Während eines Anrufs nicht verfügbar";
  }
}

/** 20.10.2026 (Vollständigkeits-Bremse): die Uhr, die eine gebremste Freihand-Pause nach der verlängerten Frist entscheidet. */
let freihandBremseUhr = 0;

/**
 * Freihand-Pause bei offenem Ende der Mitschrift (shared/satz-vollstaendig.ts): nicht sofort senden. Kommt bis zur
 * verlängerten Frist (`bremsFristMs`) kein neues Wort und keine neue Äußerung, geht ein „unsicherer“ Satz hinaus, ein
 * „offener“ ins Eingabefeld. Spricht Martin weiter, entscheidet die nächste Pause neu. Kam nur neuer Text nach (die
 * Erkennung liefert verspätet), prüft die Uhr mit DIESEM Text neu (Prüfauflage N2). `true`: jetzt nicht senden.
 */
function freihandGebremst(): boolean {
  window.clearTimeout(freihandBremseUhr);
  const text = pttLiveText;
  const grad = satzOffen(text);
  if (grad === null) return false;
  meldeDiagnose(`[freihand] bremse wartet grad=${grad}`);
  freihandBremseUhr = window.setTimeout(() => {
    if (!freihandActive || pttState !== "recording" || handsFreeVad.sprichtGerade()) return;
    if (pttLiveText !== text) {
      if (!freihandGebremst()) freihandSenden();
      return;
    }
    if (grad === "unsicher") {
      freihandSenden();
      return;
    }
    handsFreeVad.pause();
    pttModal?.setStatus("sending");
    pttUnvollstaendigInsFeld("freihand-unvollstaendig");
  }, bremsFristMs(FREIHAND_STILLE_MS) - FREIHAND_STILLE_MS);
  return true;
}

/** Die Freihand-Pause schickt ab — EIN Aufruf für die Pause und die gebremste Pause. */
function freihandSenden(): void {
  handsFreeVad.pause();
  pttModal?.setStatus("sending");
  void pttFinishAndSend("freihand-pause");
}

function freihandAttachVadWhenRecording(): void {
  // Aufnahme startet asynchron (Mikro) — pollen bis sie wirklich laeuft.
  let tries = 0;
  const tick = (): void => {
    if (!freihandActive) return;
    // WICHTIG: pttState wird sofort "recording" — der TRACK existiert aber
    // erst nach dem (async) Mikro-Aufbau. Beide Bedingungen abwarten, sonst
    // haengt die Erkennung an null und scheitert still ([vad-t] frames=0).
    const vadTrack = pttRecorder.getAudioTrack();
    if (pttState === "recording" && vadTrack) {
      handsFreeVad.start(vadTrack, {
        onSpeechEnd: () => {
          if (!freihandActive || pttState !== "recording") return;
          // 20.10.2026 (Vollständigkeits-Bremse): offener Satz — nicht in der Pause senden.
          if (freihandGebremst()) return;
          freihandSenden();
        },
      });
      // Diagnose (wie [ptt-t]): 4s nach dem Start EINE Messzeile mit den
      // echten Pegeln ins Server-Log — beweist, ob/warum die Erkennung
      // (nicht) anspringt, statt an Schwellen zu raten.
      window.setTimeout(() => {
        try {
          const st = handsFreeVad.getStats();
          const line =
            `[vad-t] calibrated=${st.calibrated} floor=${st.floor.toFixed(3)} ` +
            `thresh=${st.speechThresh.toFixed(3)} peak=${st.peak.toFixed(3)} ` +
            `speech=${st.speechDetected ? "yes" : "no"} frames=${st.frames} ` +
            `err=${st.attachError || "-"} dev="${st.deviceLabel}" ` +
            `muted=${st.deviceMuted} state=${pttState}`;
          meldeDiagnose(line);
        } catch {
          /* Diagnose best-effort */
        }
      }, 4000);
      return;
    }
    tries += 1;
    if (tries < 25) window.setTimeout(tick, 200);
  };
  tick();
}
/*
 * 24.09.2026 (Ticket 2518): Tauscht die Sprechtaste mitten im Druck eine
 * tote Leitung, hing die Pausen-Erkennung noch an der Kopie der alten Spur —
 * sie hätte nie eine Pause gehört. Neu anhängen; start() stoppt die alte
 * Kopie selbst.
 */
hoereAufLeitungsWechsel(() => {
  if (freihandActive && handsFreeVad.isRunning()) freihandAttachVadWhenRecording();
});

function freihandListen(): void {
  if (!freihandActive || pttState !== "idle" || connected) return;
  // Nie zuhoeren, solange Vespera noch spricht (Neustart stoppt sonst die TTS).
  if (activeTtsQueues.size > 0) return;
  if (mikroVonSelbstGesperrt("freihand")) return;
  startPttHandsFree();
  freihandAttachVadWhenRecording();
}

function freihandRelisten(): void {
  if (!freihandActive) return;
  window.setTimeout(() => freihandListen(), 600);
}

function toggleFreihand(): void {
  // Diagnose: JEDER Klick meldet sich — zuletzt kam bei 'Hoere zu'-Klicks
  // gar nichts im Log an (Handler tot? alter Stand?).
  try {
    const line = `[fh-t] toggle ${freihandActive ? "off" : "on"} state=${pttState} tts=${activeTtsQueues.size}`;
    meldeDiagnose(line);
  } catch {
    /* Diagnose best-effort */
  }
  if (!freihandActive) {
    if (connected) return; // im Anruf kein Freihand
    if (loadSettings().pttMode === "call") {
      window.dispatchEvent(
        new CustomEvent("voice-v8:toast", {
          detail: {
            message:
              "Freihand geht nicht im Anruf-Modus — stell Push-to-Talk in den Einstellungen um.",
            kind: "info",
          },
        }),
      );
      return;
    }
    freihandActive = true;
    freihandSyncButton();
    window.dispatchEvent(
      new CustomEvent("voice-v8:toast", {
        detail: {
          message:
            "Freihand an — sprich einfach, ich sende bei einer Pause von selbst. Nochmal tippen schaltet aus.",
          kind: "info",
        },
      }),
    );
    freihandListen();
  } else {
    freihandActive = false;
    handsFreeVad.stop();
    if (pttState === "recording") {
      pttAbort("freihand-aus");
      pttModal?.close();
      pttModal = null;
      pttResetButton();
    }
    freihandSyncButton();
    void resumeRecorderAfterPttIfNeeded();
    window.dispatchEvent(
      new CustomEvent("voice-v8:toast", {
        detail: { message: "Freihand aus.", kind: "info" },
      }),
    );
  }
}
freihandBtn?.addEventListener("click", () => toggleFreihand());
// WACHHUND (2026-07-09, Martin "leuchtet, hoert aber nichts"): erzwingt die
// Invariante "Freihand an => zuhoeren oder senden". Der einzelne Relisten-
// Hook im Turn-Ende griff nicht in allen Pfaden — dieser Puls heilt JEDES
// verpasste Weiterhoeren binnen 2s (freihandListen prueft selbst alle Guards).
let fhBlockedTicks = 0;
let fhSilentTicks = 0;
// 2026-08-10 (Martin, "das Handy wird warm"): Dieser Puls lief seit dem
// 09.07. ALLE ZWEI SEKUNDEN — die ganze Lebenszeit der App, im Vordergrund
// wie im Hintergrund, ohne Abschalter.
//
// Und er bewachte nichts: Freihand ist seit dem 10.07. geparkt (der Knopf
// ist versteckt, `freihandActive` kann gar nicht mehr wahr werden). Jeder
// Tick weckte also die JS-Maschine, rief syncVoiceModeExclusivity() mit
// zwei querySelector samt DOM-Schreibzugriffen — und stieg sofort wieder
// aus. 43 200 Weckrufe am Tag fuer nichts.
//
// Auf einem Telefon ist das nicht gratis: Ein Zwei-Sekunden-Takt haelt die
// WebView wach und verhindert den tieferen Schlaf, in dem iOS Strom spart.
//
// Jetzt laeuft er NUR, solange Freihand wirklich an ist — und wird beim
// Ausschalten wieder abgeraeumt. Die Selbstheilung bleibt damit
// unveraendert: Sie greift genau dann, wenn es etwas zu heilen gibt.
//
// Den zweiten Zweck (Anruf-Enden ohne Event abfangen) uebernimmt der
// Sync-Aufruf an den vier Stellen, an denen sich `connected` aendert —
// dort, wo es TATSAECHLICH passiert, statt es alle zwei Sekunden zu suchen.
let fhWatchdogHandle: number | null = null;
function fhWatchdogStop(): void {
  if (fhWatchdogHandle !== null) {
    clearInterval(fhWatchdogHandle);
    fhWatchdogHandle = null;
  }
  fhBlockedTicks = 0;
  fhSilentTicks = 0;
}
function fhWatchdogStart(): void {
  if (fhWatchdogHandle !== null) return;
  fhWatchdogHandle = window.setInterval(() => {
  if (!freihandActive) {
    fhWatchdogStop();
    return;
  }
  // WICHTIG: pttState ist schon "idle", waehrend die Antwort NOCH SPRICHT
  // (bewusstes Design) — erst weiterhoeren, wenn keine TTS mehr aktiv ist,
  // sonst wuergt der Neustart die laufende Antwort ab (stopCurrentTts).
  if (!connected && pttState === "idle" && activeTtsQueues.size === 0) {
    fhBlockedTicks = 0;
    fhSilentTicks = 0;
    freihandListen();
    return;
  }
  // Gesunde Aktivitaet (Aufnahme laeuft / sendet) ist KEINE Blockade.
  if (pttState !== "idle") {
    fhBlockedTicks = 0;
    fhSilentTicks = 0;
    return;
  }
  fhBlockedTicks += 1;
  // SELBSTHEILUNG (Martin, "nach der 2. Runde tot"): blockiert NUR die
  // TTS-Queue-Menge und alle Queues sind still+fertig, haengt eine Queue
  // (z.B. Abort-waehrend-Fetch) — nach 2 Pruef-Ticks wegraeumen.
  if (
    pttState === "idle" &&
    !connected &&
    activeTtsQueues.size > 0 &&
    // AUDIT-Fund 4 (20.07.): eine zwischen Saetzen PAUSIERTE Queue ist von
    // einer Zombie-Queue per isSilentIdle nicht unterscheidbar — der
    // Sweeper hat sie nach ~4s kommentarlos gecancelt. Pausiert = lebendig.
    [...activeTtsQueues].every((q) => q.isSilentIdle() && !q.isPaused())
  ) {
    fhSilentTicks += 1;
    if (fhSilentTicks >= 2) {
      const n = activeTtsQueues.size;
      for (const q of [...activeTtsQueues]) {
        try {
          q.cancel();
        } catch {
          /* ignore */
        }
        activeTtsQueues.delete(q);
      }
      fhSilentTicks = 0;
      console.log(`[fh-t] ${n} haengende TTS-Queue(s) weggeraeumt`);
      meldeDiagnose(`[fh-t] force-cleared ${n} stuck queues`);
    }
  } else {
    fhSilentTicks = 0;
  }
  // Diagnose: nach 3 blockierten Ticks den Grund melden (max alle 5 Ticks).
  if (fhBlockedTicks >= 3 && fhBlockedTicks % 5 === 3) {
    const line =
      `[fh-t] blocked ticks=${fhBlockedTicks} state=${pttState} ` +
      `connected=${connected} ttsQueues=${activeTtsQueues.size}`;
    meldeDiagnose(line);
  }
  }, 2000);
}
// Pre.489: Auto-Stop nach PTT_MAX_MS (Ticket 2263: 15min, war 5min) → Modal
// sofort auf "sending" und automatisch absenden. Vorher lief der Modal-Timer blind weiter
// obwohl der Recorder schon tot war.
pttRecorder.onAutoStop = (): void => {
  if (pttState === "recording") {
    if (pttModal) pttModal.setStatus("sending");
    /*
     * ⚠️ 18.09.2026 — HOERBAR, nicht nur fuehlbar.
     *
     * Hier standen nur die zwei `hapticTap`. Die brauchen das
     * Capacitor-Plugin (nur nativ) oder `navigator.vibrate` — und das gibt
     * es im iPhone-Browser NICHT. Auf Alberts Geraet war der Stopp damit
     * vollstaendig stumm; er sprach sechs Minuten weiter.
     *
     * Die Vibration bleibt: wo es sie gibt, ist sie die diskretere
     * Quittung. Der Ton ist der Rueckhalt fuer alle anderen.
     */
    playBeep("achtung");
    hapticTap(20);
    hapticTap(15);
    /*
     * ⚠️⚠️ 16.09.2026 — HIER WURDE JEMAND MITTEN IM SATZ GESTOPPT, OHNE ES
     *    ZU ERFAHREN.
     *
     * Albert hat am 16.09. um 07:23 zu sprechen begonnen. Um 07:38:24 war
     * die Aufnahme exakt 15:00,02 lang — das harte Limit (PTT_MAX_MS) — und
     * wurde automatisch abgeschickt. Danach vergingen SECHS MINUTEN, in
     * denen er weiterredete und nichts aufnahm. Um 07:48 schrieb er: *„Jetzt
     * habe ich eine halbe Stunde gesprochen und ich gemerkt, dass das Handy
     * gar nicht aufnimmt."*
     *
     * Die einzige Meldung waren diese zwei `hapticTap`. Die brauchen das
     * Capacitor-Plugin (nur nativ) oder `navigator.vibrate` — und das gibt
     * es auf dem iPhone nicht. Auf seinem Geraet war der Stopp also
     * VOLLSTAENDIG STUMM. Die einzige Vorwarnung ist ein Pulsieren der
     * Timer-Ziffern, das man nur sieht, wenn man hinschaut.
     *
     * ⚠️ Bewusst die hauseigene Meldung und KEIN Ton und kein Alarm — die
     *    Sprechtaste ist an dieser Stelle absichtlich ruhig gehalten
     *    (ptt-modal.ts). Aber ruhig heisst nicht unsichtbar.
     *
     * ⚠️ Zwoelf Sekunden statt der ueblichen dreieinhalb: Wer gerade
     *    gesprochen hat, schaut nicht in dem Moment aufs Display, in dem die
     *    Meldung erscheint.
     */
    // 20.10.2026 (Vollständigkeits-Bremse): bricht die Höchstdauer mitten im Satz ab, geht er nicht an die KI — ins Eingabefeld.
    if (notausgangZuruecklegen(pttLiveText)) {
      pttUnvollstaendigInsFeld("hoechstdauer-unvollstaendig", `${Math.round(PTT_MAX_MS / 60_000)} Minuten erreicht — `, 12_000);
      return;
    }
    showToast(
      `${Math.round(PTT_MAX_MS / 60_000)} Minuten erreicht — die Aufnahme ist gesendet. Zum Weitersprechen bitte neu starten.`,
      "warning",
      12_000,
    );
    void pttFinishAndSend("hoechstdauer");
  }
};
/*
 * 30.10.2026 (Aufnahme-Wächter, lib/ptt-wachter.ts): Steht die Aufnahme, ohne
 * dass jemand sie beendet hat, sagt die Karte es laut (rot, Ton, Vibration),
 * und das bisher Aufgenommene geht an die Erkennung statt verloren. Kein Wächter
 * ohne Ausgang: gesendet wird immer, auch wenn die Karte nichts zeigen kann.
 */
const AUSFALL_TON_ABSTAND_MS = 900;
pttRecorder.onAusfall = (a): void => {
  if (pttState !== "recording") return;
  const satz = ausfallSatz(a.stehtSeitMs);
  pttModal?.zeigeAusfall(satz, "ausfall");
  for (let i = 0; i < 3; i++) window.setTimeout(() => playBeep("achtung"), i * AUSFALL_TON_ABSTAND_MS);
  try {
    navigator.vibrate?.([200, 100, 200, 100, 400]);
  } catch {
    /* nicht überall vorhanden (iOS-Browser) */
  }
  hapticTap(20);
  showToast(satz, "warning", 12_000);
  void pttFinishAndSend("aufnahme-ausfall");
};
pttRecorder.onRueckkehr = (r): void => {
  if (pttState !== "recording") return;
  const satz = rueckkehrSatz(r.fehltMs);
  pttModal?.zeigeAusfall(satz, "rueckkehr");
  playBeep("achtung");
  showToast(satz, "warning", 12_000);
  void pttFinishAndSend("aufnahme-rueckkehr");
};
/*
 * Ticket 2618 (29.09.2026): Lange Diktate gehen an der Höchstdauer in einem
 * neuen Teil weiter, statt zu enden (lib/diktat-teile.ts). Jeder fertige
 * Teil wird über DIESELBE Erkennung wie „ins Textfeld" erkannt — ohne
 * Modellzug, ohne Endwort (das spricht erst der letzte Teil); der Server
 * legt dabei seinen Ton ab (`ablegen`). Die Karte sagt, dass der neue Teil
 * läuft. onAutoStop oben bleibt das Ende, wo kein Teil geht (nativer Weg,
 * oder kein neuer Rekorder lieferte).
 */
pttRecorder.erkenneTeil = (ton, mime, ablegen) =>
  erkenneGanzeAufnahme(ton, mime, null, loadSettings().sendeWort ?? "", {}, "", ablegen);
pttRecorder.onTeil = (nr) => {
  pttModal?.teilBegonnen(nr);
};
let pttBubble: PttBubble | null = null;
type PttState = "idle" | "recording" | "sending";
/*
 * 2026-08-28 (Martin: "ein Mikrofonzeichen") — MITLESEN WAEHREND DES SPRECHENS
 *
 * Bisher passierte waehrend der Aufnahme nichts: Der Ton wurde gesammelt, und
 * erst NACH dem Loslassen ging die Datei zur Erkennung. Gemessen am laufenden
 * Server dauert dieser Schritt im Mittel 380 ms (schlimmster Fall 3,6 s bei
 * einer langen Aufnahme) — er ist nicht der Flaschenhals, aber er ist die
 * Stelle, an der man NICHTS sieht.
 *
 * Jetzt liest die Erkennung mit, waehrend gesprochen wird. Sie leiht sich
 * DENSELBEN Mikrofon-Strom (PttRecorder.getStream) — ein zweites getUserMedia
 * waere auf iOS die bekannte Abriss-Ursache.
 *
 * Scheitert sie (kein Guthaben, Netz weg, alte Huelle), bleibt alles beim
 * Alten: die Datei geht wie bisher zur Erkennung. Es kann also nichts
 * kaputtgehen, nur etwas fehlen.
 */
let pttLive: import("./lib/live-dictation.js").LiveDictation | null = null;
let pttLiveText = "";

/**
 * Beendet die Mitschrift und liefert, was sie verstanden hat — dazu ihren Stand (12.10.2026, Bahn
 * live-primaer): kam der Abschluss, riss die Leitung, wie viel Ton kam an. Der Server entscheidet damit, ob
 * er die Datei-Erkennung abwarten muss. Ohne Mitschrift oder bei einem Fehler: kein Stand (der alte Weg).
 */
async function pttLiveAbschlussMitStand(): Promise<{ satz: string; stand: LiveStand | null }> {
  pttNachhoerenBeenden();
  const live = pttLive;
  pttLive = null;
  if (!live) return { satz: "", stand: null };
  try {
    const voll = await live.stopUndAbschluss();
    return { satz: (voll || "").trim(), stand: live.stand() };
  } catch {
    return { satz: pttLiveText.trim(), stand: null };
  }
}

/** Beendet die Mitschrift und liefert, was sie verstanden hat. */
async function pttLiveAbschluss(): Promise<string> {
  return (await pttLiveAbschlussMitStand()).satz;
}

/** Bricht die Mitschrift ab, ohne ihr Ergebnis zu verwenden. */
function pttLiveVerwerfen(): void {
  pttNachhoerenBeenden();
  const live = pttLive;
  pttLive = null;
  pttLiveText = "";
  void live?.stop().catch(() => undefined);
}

/*
 * ── Nachhören (26.09.2026, Ticket 2672) ─────────────────────────────────
 *
 * Martin, auf der Autobahn: „Senden" löste nicht aus — *„zum hundertsten
 * Mal"*. Wort und Stille hängen an der Web-Audio-Rechnung der Seite; steht
 * sie (Ticket 2634), läuft die Aufnahme weiter, aber nichts schickt ab.
 *
 * Die Wache (lib/nachhoeren.ts) fragt alle 500 ms `mitschriftSteht` — die
 * EINE Stelle, die das entscheidet. Sagt sie ja, schickt der Nachhörer die
 * aufgenommenen Teile an den Server, und die Karte entscheidet mit dem
 * Nachgehörten nach DERSELBEN Regel; abgeschickt wird über onSelbstSenden →
 * pttFinishAndSend wie immer. Hier stehen nur die Signale.
 *
 * ⚠️ Nur der Web-Weg (auch in der App-Hülle). Die rein native Aufnahme hat
 *    keine Rekorder-Teile und bleibt unberührt.
 * ⚠️ Im Normalfall liefert die Mitschrift — dann geht nichts hinaus.
 */
let pttNachhoerWache: MitschriftWache | null = null;

/** Was am Live-Diktat DIESER Aufnahme ankommt — der letzte Rahmen, oder aus. */
interface PttDiktatTon {
  letzterMs: number | null;
  aus: boolean;
  /** 26.09.2026 abends: wie oft die Mitschrift neuen Text brachte (0 = nie). */
  textStand: number;
}

function pttNachhoerenStarten(ton: PttDiktatTon): void {
  pttNachhoerenBeenden();
  if (pttRecorder.warNativ()) return;
  pttNachhoerWache = wacheUeberMitschrift({
    lage: () =>
      pttState === "recording" && pttRecorder.isRecording() && pttSelbstSendenEinstellung()
        ? {
            aufnahmeMs: pttRecorder.getElapsedMs(),
            diktatAus: ton.aus,
            diktatRuhtMs: ton.letzterMs === null ? null : performance.now() - ton.letzterMs,
            pegelTot: pttModal?.pegelTot() ?? false,
            // 26.09.2026 abends: Sprache laut Pegel, aber kein Wort in der
            // Mitschrift — und kommt wieder Text, gibt das Nachhören zurück.
            sprichtSeitMs: pttModal?.sprichtSeitMs() ?? null,
            textStand: ton.textStand,
          }
        : null,
    nativ: isNativePlatform(),
    teile: () => pttRecorder.teileBisher(),
    laeuft: () => pttState === "recording" && pttRecorder.isRecording(),
    // Welle 0: die Kennung der Aufnahme reist mit (Abfrage `az`) — die Kennung des Nachhörens selbst bleibt, wie sie war.
    az: aufnahmeKennung() ?? undefined,
    onProbe: (p) => pttModal?.nachgehoert(p),
    // 08.10.2026: der Pegel der Aufnahme vom Server — die Anzeige nimmt ihn nur, solange sie selbst nicht misst.
    onPegel: (db) => pttModal?.pegelNachgereicht(db),
    onZurueck: (proben) => {
      meldeSpur("ptt-nachhoeren", `zurueck grund=live-text proben=${proben}`);
      pttModal?.mitschriftZurueck();
      if (pttLiveText) pttModal?.setLiveText(pttLiveText);
    },
  });
}

/** Wache und Nachhörer abmelden — beim Ende der Mitschrift, auf jedem Weg. */
function pttNachhoerenBeenden(): void {
  pttNachhoerWache?.stop();
  pttNachhoerWache = null;
}

let pttState: PttState = "idle";

/* ═══════════════════════════════════════════════════════════════════════════
   EIN DRUCK — EINE WAHRHEIT — HOECHSTENS EINE MELDUNG (2026-08-31)

   Martin, mit Foto zweier gestapelter Meldungen: *„Siehst Du das passiert oft,
   wenn ich draufdruecke, dann kommt diese Meldung oben, diese zwei
   eigenartigen Informationen. Erstens einmal kommt es auch manchmal sogar
   viermal."*

   Auf dem Foto stehen woertlich uebereinander:
       „Zu kurz — Knopf halten und sprechen."
       „Zum Aufnehmen den Knopf gedrueckt HALTEN."
   Zwei Meldungen, die einander widersprechen, nach EINEM Druck, bei dem er
   gehalten UND gesprochen hat.

   ── DIE URSACHE, gemessen und im Code belegt

   `pttToggle()` setzt `pttState = "recording"` SYNCHRON (Zeile ~13510) und
   wartet erst DANACH auf `pttStartRecording()`. Der Zustand behauptet also
   „es laeuft", bevor ein einziges Byte Ton faellt.

   Wie lange dauert dieses Fenster wirklich? Aus der eigenen Telemetrie
   (`[ptt-t]` im journalctl, 53 echte Druecke Martins):

       Median bis der Recorder laeuft      479 ms
       p90                                1610 ms
       schlechtester Fall                 1897 ms
       Druecke ueber 300 ms Verlust          75 %
       ueber den langsamen "kalten" Weg       96 %

   Laesst Martin in diesem Fenster los (also nach 0,5–1,9 s — genau seine
   „zu kurz gedrueckt"-Beschwerde), passiert Folgendes:

     1. `pointerup` sieht `pttState === "recording"` und glaubt, es laufe.
     2. `pttFinishAndSend()` ruft `stopAndGet()`.
     3. Dort ist `this.recorder` noch `null` -> `return null` (ptt.ts:662).
     4. Der Null-Zweig meldet „Zu kurz — Knopf halten und sprechen."
        — das GEGENTEIL dessen, was er getan hat.  ← MELDUNG 1
     5. Der verspaetete Start wird fertig, findet `pttState === "idle"`,
        verwirft und meldet „Zum Aufnehmen den Knopf gedrueckt HALTEN."
                                                              ← MELDUNG 2

   Zwei Druecke hintereinander ergeben vier Meldungen. Genau sein Befund.

   ⚠️ Ein Schutz gegen „losgelassen vor dem Start" EXISTIERT bereits
   (`if (pttReleased) pttResolveRelease()` im `.then`) — er verliert nur das
   Rennen, weil der optimistische `pttState` `pointerup` frueher feuern
   laesst. Es fehlt also keine Wache, es fehlt die REIHENFOLGE.

   ── WAS DIESE DREI HELFER TUN

   `pttStartLauf`  haelt das Start-Versprechen fest, damit das Loslassen
                   darauf WARTEN kann, statt den Druck wegzuwerfen.
   `pttMelde`      laesst pro Druck hoechstens EINE Meldung durch.
   `pttMeldungenFreigeben`  oeffnet das Fenster bei jedem neuen Druck.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Das laufende Start-Versprechen — `null`, sobald der Start durch ist. */
let pttStartLauf: Promise<unknown> | null = null;

/**
 * Läuft schon die NÄCHSTE Aufnahme — auch wenn sie erst startet?
 *
 * ⚠️⚠️ 23.09.2026, TICKET 2502 — der eigentliche Grund, warum das Mikro nach
 * einer Frage „kurz aufging" und das Fenster zu blieb. Im Protokoll, zweimal
 * wörtlich gleich (08:44:17 und 09:17:52):
 *
 *   [ptt-t]    … via=nach-frage path=cold … rec=+321ms
 *   [ptt-ende] start-verworfen via=nach-frage dauer=4ms zustand=idle rekorder=laeuft
 *
 * Der Auto-Start feuert beim Ende der Vorlese-Wiedergabe. Genau darauf wartet
 * der ALTE Zug (`await ttsQueue.idle()` in pttSendBlob) — und sein finally
 * setzte `pttState = "idle"`, während der Kaltstart der neuen Aufnahme noch
 * lief. Die Wache dort (`seq === pttSendSeq`) schützt nur vor einem neueren
 * SENDEN, nicht vor einer neueren AUFNAHME: die Sequenz wächst erst beim
 * Absenden. Der fertige Start fand dann „idle" und verwarf sich selbst.
 *
 * Gefragt wird die harte Wahrheit, nicht `pttState` (der kann veralten): ein
 * laufendes Start-Versprechen oder ein laufender Rekorder.
 */
function naechsteAufnahmeLaeuft(): boolean {
  return pttStartLauf !== null || pttRecorder.isRecording();
}
// Ticket 2882: der Takt des Seiten-Abgleichs fragt dieselbe harte Wahrheit —
// ein EINZIGES Ende für jeden Ausgang einer Aufnahme (siehe seiten-abgleich.ts).
setzeAufnahmeQuelle(naechsteAufnahmeLaeuft);

/**
 * Darf jetzt eine Aufnahme beginnen? Der Grund, wenn nicht. (01.10.2026, Ticket 2882)
 *
 * EINE Frage für ALLE Startwege (Knopf, Taste, Feld-Mikro, Aktionstaste,
 * Freihand, Auto-Mikro nach Frage/Antwort, Wach-Wort) — gestellt im ersten
 * Schritt eines Starts, nicht erst, wenn das Mikrofon da ist:
 *
 *   `aufnahme`      der Rekorder dieser Seite läuft,
 *   `start`         ein Start dieser Seite wartet noch aufs Mikrofon
 *                   (gemessen bis 1,9 s; `pttState` allein sagt es nicht, wenn
 *                   ein alter Zug ihn zurückgesetzt hat),
 *   `andere-seite`  eine andere Seite desselben Browsers nimmt auf
 *                   (Ticket 2882: der Vorfall vom 01.10., 08:47).
 */
function aufnahmeStartSperre(): "aufnahme" | "start" | "andere-seite" | null {
  if (pttRecorder.isRecording()) return "aufnahme";
  if (pttStartLauf !== null) return "start";
  return fremdeAufnahme() ? "andere-seite" : null;
}

/**
 * Ein abgewiesener Doppelstart: eine ruhige Messzeile (höchstens eine je
 * Sekunde) und — nur wenn ein Mensch gedrückt hat — ein Satz, warum nichts
 * aufgeht. Automatische Wege (Auto-Mikro, Wach-Wort, Freihand) bleiben stumm.
 */
function pttDoppelstartAbgewiesen(via: PttAusloeser, grund: "aufnahme" | "start" | "andere-seite"): void {
  const fremd = grund === "andere-seite" ? fremdeAufnahme() : null;
  const zeile = doppelstartZeile(
    via,
    grund,
    performance.now(),
    fremd ? `andere=${fremd.via} seit=${Math.round(fremd.seitMs)}ms` : "",
  );
  if (zeile) meldeDiagnose(zeile);
  const mensch = via === "knopf" || via === "aktionstaste";
  if (grund === "aufnahme" && mensch) {
    // Der Druck bei laufender Aufnahme bleibt ein SENDEN-Wunsch (23.08.2026).
    // Zustand heilen: Der Recorder laeuft, also IST aufgenommen worden — nur
    // der Druck eines Menschen, und nur von „idle“: ein laufendes Senden bleibt.
    if (pttState === "idle") pttState = "recording";
    if (pttLocked) pttLockSendArmed = true;
  }
  if (grund === "andere-seite" && mensch) {
    pttMelde("Die Aufnahme läuft schon in einem anderen Fenster — dort sprechen und senden.");
  }
  /*
   * Ein Druck gleich nach „Verwerfen" im Mikro-Anlauf (Nachprüfung 01.10.): der
   * abgebrochene Start wartet noch aufs Mikrofon (bis 1,9 s), `pttState` steht
   * schon auf „idle" — dieser Start verwirft sich selbst, sobald das Mikro da
   * ist. Der neue Druck geht dabei verloren. Vorher blieb es still, und danach
   * kam „Zum Aufnehmen den Knopf gedrückt HALTEN." aus dem alten Start — obwohl
   * gerade gedrückt wurde. Jetzt sagt DIESER Druck, was los ist; `pttMelde` lässt
   * je Druck nur eine Meldung durch, die alte Zeile verfällt also.
   *
   * ⚠️ Der Druck wird bewusst NICHT an den alten Start gehängt und neu gestartet:
   *    wann der alte Start endet, weiß der Mensch nicht (er hat längst losgelassen
   *    oder aufgegeben), und ein Mikrofon, das Sekunden später von selbst aufgeht,
   *    ist schlimmer als ein Satz, der sagt „gleich noch einmal".
   */
  if (grund === "start" && mensch && pttState === "idle") {
    pttMelde("Mikrofon wird noch freigegeben … gleich noch einmal drücken.");
  }
}
/** Wurde fuer den AKTUELLEN Druck schon etwas gemeldet? */
let pttMeldungGezeigt = false;

/** Bei jedem frischen Druck darf wieder genau eine Meldung kommen. */
function pttMeldungenFreigeben(): void {
  pttMeldungGezeigt = false;
}

/**
 * Eine Meldung je Druck — die erste gewinnt, alle weiteren verfallen.
 *
 * ⚠️ Bewusst die ERSTE und nicht die letzte: sie entsteht dort, wo der
 * Nutzer gerade gehandelt hat. Die spaeteren stammen aus Aufraeumpfaden,
 * die seinen Druck nicht mehr kennen — genau die widersprachen ihm.
 */
function pttMelde(message: string, kind: "info" | "error" = "info"): void {
  if (pttMeldungGezeigt) return;
  pttMeldungGezeigt = true;
  window.dispatchEvent(
    new CustomEvent("voice-v8:toast", { detail: { message, kind } }),
  );
}

/**
 * Auf einen noch laufenden Aufnahme-Start warten.
 *
 * ⚠️ MIT Deckel. Ohne ihn haengt das Senden an einem Start, der selbst
 * haengt — und der Knopf bliebe tot. 4500 ms decken Pause (1200) plus
 * Kaltstart (gemessen bis 1897) mit Luft ab.
 */
async function pttWarteAufStart(): Promise<void> {
  const t0 = performance.now();
  while (performance.now() - t0 < 4500) {
    if (pttRecorder.isRecording()) return;
    if (!pttStartLauf) return;
    await new Promise<void>((r) => window.setTimeout(r, 40));
  }
}

function pttResetButton(): void {
  refs.pttBtn.classList.remove("is-recording");
  composerPttEl()?.classList.remove("is-recording");
  refs.pttBtn.classList.remove("is-canceling");
  setPttDockSend(false);
  pttLockSendArmed = false;
  refs.pttBtn.disabled = false;
  refs.pttBtn.setAttribute(
    "aria-label",
    "Sprachnachricht: kurz tippen startet Freihand — nochmal tippen oder Enter sendet. Gedrückt halten und sprechen, loslassen sendet. Nach links wischen oder Escape bricht ab.",
  );
}

/**
 * 2026-08-28: Das wandernde Mikrofon im Eingabefeld — es zeigt denselben
 * Zustand wie das Fuss-Mikro. Beide sind nie gleichzeitig sichtbar (CSS:
 * nur bei `body.text-focus`), aber beide muessen wissen, dass aufgenommen
 * wird: sonst tippt man bei offener Tastatur ins Leere.
 */
function composerPttEl(): HTMLElement | null {
  return document.getElementById("composer-ptt");
}

/**
 * Ging das Mikro VON SELBST auf (nach Vesperas Frage oder Antwort) — ohne dass ein Mensch es geöffnet hat?
 * (06.10.2026, Ticket 3024)
 */
function mikroVonSelbstOffen(): boolean {
  const via = pttTiming.extra.via;
  return via === "nach-frage" || via === "nach-antwort";
}

/**
 * 12.10.2026 (Fortsetzung, Prüfung): hat dieser Start die laufende Antwort schon abgebrochen (`fortsetzungBeiAufnahmestart`)? Dann ruft
 * `pttSetButtonRecording` (das Mikro ist da) den Abbruch NICHT ein zweites Mal — ein zweiter Ruf träfe sonst, 0,3–1,9 s später, eine Antwort,
 * die inzwischen zu einer anderen Nachricht gehört. Ein Abbruch je Start.
 */
let pttAbbruchGesendet = false;

function pttSetButtonRecording(): void {
  refs.pttBtn.classList.add("is-recording");
  composerPttEl()?.classList.add("is-recording");
  // 2026-07-05 (#30): Reinsprechen bricht eine noch laufende Antwort ab.
  /*
   * ⚠️ 06.10.2026 (Ticket 3024): NUR, wenn ein Mensch das Mikro geöffnet hat. Um 08:28:10 ging es nach
   *    Vesperas Frage von selbst auf, während Martins Nachricht mit Bild schon lief: der Abbruch traf DIESEN
   *    Zug, die Aufnahme wurde danach ohne ein Wort verworfen — und das Bild kam nie an. Ein von selbst
   *    geöffnetes Mikro bricht erst ab, wenn wirklich gesendet wird (pttFinishAndSend).
   */
  if (!mikroVonSelbstOffen() && !pttAbbruchGesendet) void fetch("/api/chat/abort", { method: "POST" }).catch(() => {});
  pttAbbruchGesendet = false;
  refs.pttBtn.setAttribute(
    "aria-label",
    "Aufnahme läuft — loslassen sendet, hochwischen verriegelt für Freihand, nach links wischen bricht ab",
  );
}

async function pttStartRecording(): Promise<boolean> {
  // 2026-07-06 (Martin: '5 Sekunden gedrueckt, nichts passiert'): Die Kette
  // hatte ZWEI Warteschritte OHNE Timeout — ein haengender Bridge-Call oder
  // eine gestoerte iOS-Audio-Session blockierte den Start unbegrenzt.
  // Die Tagesaufnahme pausiert der Recorder selbst, unmittelbar vor dem
  // Mikro-Zugriff (ptt.ts, Grund „ptt", auf 400 ms gedeckelt).
  try {
    await Promise.race([
      pttRecorder.start(),
      new Promise<never>((_res, rej) =>
        window.setTimeout(
          () => rej(new Error("Mikrofon reagiert nicht (Timeout)")),
          4000,
        ),
      ),
    ]);
  } catch (err) {
    void resumeRecorderAfterPttIfNeeded();
    const msg = err instanceof Error ? err.message : String(err);
    const hint = `Mikrofon-Zugriff nicht möglich (${msg}). Bitte im Browser die Mikrofon-Freigabe für diese Seite erlauben (Schloss-Symbol in der Adressleiste → Mikrofon → Zulassen) und erneut versuchen.`;
    appendBubble("system", hint);
    pushHistory({ kind: "system", text: hint, ts: Date.now() });
    return false;
  }
  // Pre.472: PttBubble komplett raus bei Hold-Mode — Visual nur am
  // Dock-PTT-Button selbst (is-recording CSS-class). User-Beschwerde
  // 2026-05-24 "doppelt angezeigt, nicht absendbar, voller Schrott".
  // WhatsApp-Style: nichts erscheint im Chat während Aufnahme, nur
  // beim Senden landet User-Bubble mit Audio+Transcript.
  // 2026-07-04 (Race-Fix): Hat der kurze Tipp den Vorgang schon abgebrochen,
  // waehrend das Mikro startete? Dann NICHT auf Rot schalten und die
  // (verspaetet gestartete) Aufnahme sofort verwerfen.
  // 2026-09-01 (Martin: PTT sporadisch „zu kurz“ / „Mikro nicht warm“):
  // Das Loslassen setzt pttState auf „sending“, BEVOR der Start fertig ist.
  // Dieser Block hielt das fuer Abbruch und warf die frische Aufnahme weg.
  // Senden wartet — die Aufnahme muss stehen bleiben.
  if (pttState === "sending") {
    return true;
  }
  if (pttState !== "recording" || !pttRecorder.isRecording()) {
    meldePttEnde(
      "start-verworfen",
      `rekorder=${pttRecorder.isRecording() ? "laeuft" : "steht"}`,
    );
    pttRecorder.cancel();
    void resumeRecorderAfterPttIfNeeded();
    pttResetButton();
    /*
     * ⚠️ 2026-08-31 — DIES war die ZWEITE Meldung auf Martins Foto.
     *
     * Sie stammt aus dem Aufraeumpfad des verspaetet fertig gewordenen
     * Starts. Der weiss nicht mehr, was der Nutzer getan hat — er sieht nur
     * einen Zustand, der nicht mehr „recording" ist, und rief deshalb
     * „Zum Aufnehmen den Knopf gedrueckt HALTEN." hinterher, direkt unter
     * die erste, gegenteilige Meldung.
     *
     * `pttMelde` laesst sie jetzt nur noch durch, wenn fuer diesen Druck
     * noch NICHTS gesagt wurde. Hat der Sende-Weg schon geantwortet, bleibt
     * sie stumm — eine Handlung, eine Rueckmeldung.
     *
     * Der Satz selbst bleibt: fuer den echten kurzen Tipp ist er richtig.
     */
    pttMelde("Zum Aufnehmen den Knopf gedrückt HALTEN.");
    return false;
  }
  pttBubble = null;
  pttSetButtonRecording();
  // Mitschrift auf DEMSELBEN Strom starten — best effort, nie blockierend.
  // Schlaegt sie fehl, laeuft die Aufnahme unveraendert weiter.
  // ⚠️ 2026-08-28: Hier stand nur `pttLiveText = ""`. Blieb aus einem frueheren
  // Durchgang eine Instanz stehen, wurde ihr Griff bei der Zuweisung unten
  // ueberschrieben — sie lief weiter und schrieb in DIESELBE Variable. Erst
  // sauber beenden, dann neu anfangen.
  pttLiveVerwerfen();
  /**
   * Auf den Tonstrom warten — höchstens vier Sekunden.
   *
   * ⚠️ Die Grenze ist wichtig: ohne sie hinge die Schleife an einem Mikro,
   * das nie kommt. Und die Zustandsprüfung ebenso — wer loslässt, bevor der
   * Strom da ist, soll keine Mitschrift mehr starten.
   */
  const warteAufStrom = async (): Promise<MediaStream | null> => {
    const bis = Date.now() + 4000;
    for (;;) {
      if (pttState !== "recording") return null;
      const s = pttRecorder.getStream();
      if (s) return s;
      if (Date.now() > bis) return null;
      await new Promise((r) => setTimeout(r, 120));
    }
  };
  // Ticket 2672: was am Live-Diktat ankommt — gelesen von der Nachhör-Wache.
  const diktatTon: PttDiktatTon = { letzterMs: null, aus: false, textStand: 0 };
  pttNachhoerenStarten(diktatTon);
  void (async () => {
    /*
     * ⚠️ 2026-09-04 (Martin: *„Ich sehe nicht, wie der Text entsteht. Liegt
     * es daran, dass manchmal im Hintergrund der Recorder läuft?"* — ja.)
     *
     * Hier stand `const strom = pttRecorder.getStream(); if (!strom) return;`
     * — EIN Versuch, dann stillschweigend aufgegeben. Der Pegelbalken
     * daneben versucht es in einer Schleife weiter (`requestAnimationFrame`),
     * die Mitschrift nicht.
     *
     * Das Mikro startet asynchron. Läuft die TAGES-Aufnahme, startet es sogar
     * ausdrücklich KALT — der Kommentar in ptt.ts sagt es selbst: „PTT
     * während der Tages-Aufnahme hat seinen eigenen Pause/Resume-Tanz und
     * startet dann eben kalt." In diesem Fenster war der Strom noch nicht da,
     * die Mitschrift gab auf, und die Aufnahme lief weiter — genau Martins
     * Bild: es funktioniert, aber man sieht nichts entstehen.
     *
     * Jetzt wird gewartet, aber nicht ewig: höchstens vier Sekunden, und nur
     * solange wirklich aufgenommen wird. Bricht er vorher ab, ist nichts mehr
     * zu schreiben.
     */
    const strom = await warteAufStrom();
    if (!strom) return;
    try {
      const { LiveDictation } = await import("./lib/live-dictation.js");
      const live = new LiveDictation({
        onText: (_final, _interim, woerter, alle, ganz) => {
          const vorher = pttLiveText;
          // pre.3625: `ganz` statt `final + interim` — die getrimmten Teile klebten („Weltwie“).
          pttLiveText = ganz;
          // Ticket 2672 (26.09.2026 abends): neuer Text heißt „die Mitschrift liefert".
          if (pttLiveText && pttLiveText !== vorher) diktatTon.textStand++;
          // Sichtbar machen, was verstanden wird — in der Aufnahmekarte,
          // weil die oben sitzt und der Daumen unten auf dem Knopf liegt.
          // 26.09.2026 spät: mit den Zeitmarken der Wörter (Sprechpause).
          // 03.10.2026 (Fantasiewörter): dazu alle Wörter mit Uhrzeit — fürs Urteil (rausch-woerter.ts).
          pttModal?.setLiveText(pttLiveText, { woerter, alleWoerter: alle });
        },
        // Ticket 2867: das Satzende-Signal der Erkennung — nur gemessen (ende-token.ts).
        onEnde: (e) => pttModal?.setEndeToken(e),
        onError: () => {
          /* Stille: die Datei-Erkennung faengt es auf. */
          // Ticket 2672: … und das Nachhören übernimmt Wort und Stille.
          diktatTon.aus = true;
        },
        // Ticket 2672: jeder Rahmen, der hier ankommt, heißt „die Rechnung
        // läuft". Bleiben sie aus, steht die Mitschrift.
        onLevel: () => {
          diktatTon.letzterMs = performance.now();
        },
      });
      if (pttState !== "recording") return;
      await live.start(strom);
      if (pttState !== "recording") {
        void live.stop().catch(() => undefined);
        return;
      }
      pttLive = live;
    } catch {
      /* kein Guthaben, kein Netz — der alte Weg traegt. */
      // Ticket 2672: ohne Mitschrift hört der Server nach.
      diktatTon.aus = true;
    }
  })();
  // Pre.478: vibrate ist jetzt im pointerdown-handler — hier nicht mehr
  // nötig (war 200-500ms verzögert).
  return true;
}

async function pttToggle(): Promise<void> {
  // Sending-State: ignorieren, läuft schon
  if (pttState === "sending") return;
  // Modus-Lookup
  const settings = loadSettings();
  if (settings.pttMode === "call") {
    void handleConnect();
    return;
  }
  if (pttState === "idle") {
    pttState = "recording";
    const ok = await pttStartRecording();
    if (!ok) {
      pttState = "idle";
    } else if (pttRecorder.warNativ()) {
      // 05.09.2026 (Martin: „keinen Visualizer und keinen Text"): Die native
      // Engine kann nicht live streamen — der Text kommt nach dem Loslassen.
      // Das steht jetzt dort, wo sonst die Mitschrift wächst.
      // ⚠️ 24.09.2026 (Ticket 2202): als HINWEIS — als Text gewertet, galt die
      //    Aufnahme sofort als gesprochen, und „Absenden nach Stille" schickte
      //    sie ab, bevor ein Wort fiel (siehe ptt-modal.ts, setLiveText).
      // Ticket 2607: der Satz nennt den Grund (ptt-weg.ts) und steht leise da.
      pttModal?.setLiveText(NATIV_OHNE_MITSCHRIFT, { hinweis: true });
    }
    return;
  }
  if (pttState === "recording") {
    void pttFinishAndSend("umschalter");
    return;
  }
}

/**
 * Wodurch endete diese Aufnahme? EINE Zeile im Server-Protokoll. (23.09.2026)
 *
 * ⚠️ Anlass: Das Mikro ging nach Vesperas Gegenfrage zweimal von selbst auf
 *    (`via=nach-frage`, 06:49 und 06:52) — und beide Aufnahmen endeten, ohne
 *    je beim Server anzukommen. Das Protokoll zeigte den Start auf die
 *    Millisekunde, das Ende gar nicht: Abbruch, verworfener Start und leerer
 *    Puffer waren stumm. Drei Erklärungen (Wach-Wort, Hi-Fi-Umschaltung,
 *    Absenden nach Stille) sahen plausibel aus und scheiterten jede an einer
 *    Zeile der Messung. Statt einer vierten Vermutung sagt jetzt jedes Ende
 *    seinen Grund.
 */
function meldePttEnde(art: string, zusatz = ""): void {
  const via = pttTiming.extra.via ?? "?";
  const ms = Math.round(pttRecorder.getElapsedMs());
  // 24.09.2026 (Ticket 2202): welcher Weg — damit leere und abgebrochene
  // Aufnahmen je Weg zählbar sind (Probewoche nativ gegen Web).
  const weg = pttTiming.wegZeile();
  meldeDiagnose(
    `[ptt-ende] ${art} via=${via} dauer=${ms}ms zustand=${pttState}${zusatz ? " " + zusatz : ""}${absendenZusatz(art)}${weg ? " " + weg : ""}`,
  );
}

/** 20.10.2026: beim Senden, welcher Modus von „Automatisch absenden“ galt — nur das Wort (jev/fest/aus), keine Inhalte. */
function absendenZusatz(art: string): string {
  return art === "senden" ? ` absenden=${absendenModus(Number(loadSettings().sendeStilleMs ?? 0))}` : "";
}

function pttAbort(grund: string, quelle?: string): void {
  stopRingingTone();
  // 12.10.2026: die verworfene Aufnahme nimmt ihre Fortsetzungs-Angabe mit (die Vorrunde bleibt, wie sie ist).
  fortsetzungSpeicher.verwerfeAktive();
  // 21.10.2026 (Prüfauflage 5): ein verworfenes Feld-Diktat nimmt seine Cursor-Stelle mit — kein späterer Weg erbt sie.
  feldDiktat = null;
  if (pttState === "idle") return;
  meldePttEnde("abbruch", `grund=${grund}${quelle ? ` quelle=${quelle}` : ""}`);
  pttLiveVerwerfen();
  void resumeRecorderAfterPttIfNeeded();
  pttRecorder.cancel();
  // Ticket 2882: die Aufnahme ist weg — andere Seiten sofort freigeben (läuft noch ein Start, wartet die Freigabe auf dessen Ende).
  aufnahmeFreigeben();
  pttBubble?.close();
  pttBubble = null;
  pttState = "idle";
  pttResetButton();
}

/**
 * Die Aufnahme wegwerfen und die Karte schließen — der Knopf „Verwerfen",
 * das gesprochene „Verwerfen" (Ticket 2674) und das Nachlausch-Fenster
 * (Ticket 2668) nehmen DIESEN Weg; nur der Anlass im Protokoll unterscheidet
 * sie.
 */
function pttVerwerfen(grund: string, quelle?: string): void {
  hapticTap(45); // war Beep
  hapticTap(40);
  // Ticket 2643: VOR dem Abbruch fragen — er beendet die Messung der Leitung.
  pttStummHinweisAmEnde();
  pttAbort(grund, quelle);
  pttModal?.close();
  pttModal = null;
}

/**
 * 22.10.2026 (Martin 07:55, lib/ja-karte-offen.ts): ein bloßes gesprochenes „Ja“/„Nein“ bei offener Ja/Nein-Karte — die Aufnahme ist
 * schon gestoppt; nichts geht hinaus, nichts wird gespeichert. Ruhezustand wie nach einer leeren Aufnahme, dazu der Hinweis auf den
 * Knopf. Die Journalzeile trägt keinen Text.
 */
function pttJaKarteVerwerfen(): void {
  meldeDiagnose("[ptt] ja-karte-offen gesprochen-verworfen");
  window.clearTimeout(pttWatchdogTimer);
  pttBubble = null;
  pttCloseModal();
  pttState = "idle";
  pttResetButton();
  void resumeRecorderAfterPttIfNeeded();
  pttMelde(JA_KARTE_HINWEIS);
}

/*
 * ── Stumme Quelle (Ticket 2643, 26.09.2026 abends) ──────────────────────
 *
 * Konto 23 lieferte auf Web- UND nativem Weg reine digitale Stille (−91 dB).
 * Meldet die Sprechtaste, dass auch die getauschte Leitung stumm bleibt,
 * sagt es die Karte (ptt-modal.ts).
 *
 * ⚠️ 26.09.2026 spät (dritte Runde): Bis pre.3347 wurde jeder Ausgang hier
 *    zum Verwerfen — nichts ging mehr zur Erkennung. Kam der Ton nach der
 *    Prüfung doch (sie misst nur die ersten 1,8 s der Leitung), war das
 *    Diktat verloren. Jetzt wird NICHTS verworfen: die Aufnahme geht wie
 *    immer zur Erkennung, und der Server wirft echte digitale Stille selbst
 *    weg („Ich habe nichts gehört"). Der Satz, wo das Mikrofon-Recht liegt,
 *    kommt ZUSÄTZLICH in den Verlauf — nur, wenn bis zum Ende wirklich
 *    kein Ton kam.
 */
let pttStummeQuelle = false;

/**
 * Am Ende einer Aufnahme, auf jedem Ausgang, VOR dem Stopp des Rekorders:
 * blieb die gemeldete stumme Quelle bis zum Schluss stumm? Dann steht der
 * Satz im Verlauf. Gezählt wird ein späterer Laut am Pegel der Karte
 * (`stummBisher`) und jede Spitze der Leitungs-Wache (`quelleGabTon`).
 * Genau einmal je Aufnahme.
 */
function pttStummHinweisAmEnde(): void {
  if (!pttStummeQuelle) return;
  pttStummeQuelle = false;
  const ohneTon = (pttModal?.stummBisher() ?? true) && !pttRecorder.quelleGabTon();
  meldeSpur("ptt", `stumm-am-ende ton=${ohneTon ? "nein" : "ja"}`);
  // Ticket 2643 (29.09.2026): derselbe Satz wie auf der Karte — mit dem
  // Recht, das die Karte gerade gelesen hat (mikro-recht.ts).
  if (ohneTon) appendBubble("system", stummHinweisFuer(isNativePlatform(), letztesMikroRecht()));
}

/*
 * Ticket 2882 (01.10.2026): Zwei Seiten beanspruchten im selben Augenblick —
 * diese war die jüngere und weicht. Ihre Aufnahme ist höchstens zwei Sekunden
 * alt (seiten-abgleich.ts, RENNEN_MS); die andere Seite hat denselben Ton und
 * läuft weiter. Still: kein Piepton, kein Satz — nur die Messzeile.
 */
beiRennenVerloren((fremd) => {
  if (pttState === "idle" && !naechsteAufnahmeLaeuft()) return;
  meldeDiagnose(
    `[ptt-t] doppelstart abgewiesen via=${pttTiming.extra.via ?? "?"} läuft=andere-seite andere=${fremd.via} rennen=ja`,
  );
  pttAbort("andere-seite");
  pttModal?.close();
  pttModal = null;
});

/**
 * Ticket 2668 (27.09.2026): endet ein Druck, der das Nachlausch-Fenster
 * öffnete, ohne erkannten Text, schließt er still — keine Meldung „nichts
 * verstanden", kein Mikro-Wechsel. Nach einer Antwort nichts zu sagen ist
 * kein Fehler; im Fall 07:58:52 stand die Meldung nach einer Aufnahme, deren
 * Pegel nie über der Sprachgrenze lag. true = still geschlossen.
 * ⚠️ Nur ohne belegte Sprache (27.09.2026, Nachprüfung): war im Fenster
 *    Sprache belegt und die Erkennung liefert nichts, ist das ein echter
 *    Erkennungsausfall — dann bleibt die normale Rückmeldung.
 */
function pttLeerStill(messung: TextMessung | undefined): boolean {
  if (messung?.nachlauschOhneSprache !== true) return false;
  meldeSpur("ptt", "nachlauschen-leer-still");
  return true;
}

/*
 * ── Zurückholen (Ticket 2674, 26.09.2026 abends) ────────────────────────
 *
 * „Den Plan würde ich, ehrlich gesagt, verwerfen." warf das ganze Diktat
 * weg. Ein gesprochenes „Verwerfen" ist jetzt umkehrbar: es liegt eine
 * Minute im Speicher dieser Seite (lib/verworfen.ts). Zurück kommt es per
 * Tipp auf „Zurückholen" oder per Diktat, das nur „zurückholen" bzw. „doch
 * nicht" ist — immer ins Eingabefeld.
 *
 * 26.09.2026 spät (dritte Runde): Es liegt die AUFNAHME dort, nicht der
 * Text der Mitschrift, und zurückgeholt wird die ganze Aufnahme erkannt —
 * höchstens zwei, je mit eigener Notiz.
 *
 * 26.09.2026 (vierte Runde): Ob sie dort liegt, entscheidet der TON, nicht
 * die Mitschrift; sie verlässt die Ablage erst nach der gelungenen
 * Erkennung, und scheitert die, erscheint „Erneut versuchen" wie beim
 * Senden (lib/feld-auftraege.ts). Ein drittes Verwerfen sagt, dass das
 * älteste nicht mehr zurückzuholen ist.
 */
const pttVerworfen = neueVerworfenAblage();
/** Die Notiz je verworfener Aufnahme — sie geht mit ihr. */
const pttVerworfenKarten = new Map<Verworfenes, HTMLElement>();

/** Notizen, deren Aufnahme nicht mehr bereitliegt (zurückgeholt, verdrängt, abgelaufen), gehen. */
function pttVerworfenKartenAufraeumen(): void {
  for (const [v, karte] of pttVerworfenKarten) {
    if (pttVerworfen.enthaelt(v)) continue;
    karte.remove();
    pttVerworfenKarten.delete(v);
  }
}

/** Das gesprochene „Verwerfen": die Aufnahme weglegen und das Zurückholen anbieten. */
function pttLegeVerworfenWeg(v: {
  ton: Blob | null;
  hatteTon: boolean;
  mime: string;
  text: string;
  fuerSich: boolean;
  /** 22.10.2026: vom Server als Silbensalat verworfen — nur per Tipp zurückzuholen (lib/verworfen.ts). */
  salat?: boolean;
}): void {
  const gelegt = pttVerworfen.lege(v);
  if (!gelegt) return;
  const { neu, verdraengt } = gelegt;
  meldeSpur(
    "ptt",
    `verworfen zeichen=${neu.text.length} ton=ja${neu.salat ? " salat=ja" : ""}${verdraengt.length ? ` verdraengt=${verdraengt.length}` : ""}`,
  );
  const karte = document.createElement("div");
  karte.className = "ptt-retry ptt-retry--verworfen";
  const satz = document.createElement("p");
  satz.className = "ptt-retry-text";
  satz.textContent = "Verworfen";
  karte.appendChild(satz);
  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className = "btn btn--ghost btn--sm";
  knopf.textContent = "Zurückholen";
  knopf.setAttribute("aria-label", "Verworfen – Zurückholen ins Eingabefeld");
  knopf.addEventListener("click", () => {
    // Ein Tipp je Auftrag — ein zweiter während der Erkennung täte nichts.
    knopf.disabled = true;
    void pttZurueckholen("tipp", neu).finally(() => {
      knopf.disabled = false;
    });
  });
  karte.appendChild(knopf);
  pttVerworfenKarten.set(neu, karte);
  // Ein drittes Verwerfen verdrängt das älteste — dessen Notiz geht mit,
  // und es wird gesagt (vierte Runde): sonst verschwand es still.
  pttVerworfenKartenAufraeumen();
  if (verdraengt.length > 0) showToast(VERDRAENGT_HINWEIS, "info", 6000);
  insertOrdered(karte, Date.now());
  fuehreChatNach();
  // Nach der Frist gibt es nichts mehr zurückzuholen — dann auch keinen Knopf.
  window.setTimeout(pttVerworfenKartenAufraeumen, ZURUECKHOLEN_MS + 100);
}

/**
 * Das Verworfene ins Eingabefeld — per Tipp auf SEINE Notiz oder per
 * gesprochenem „zurückholen" (dann das jüngste). Die ganze Aufnahme wird
 * erkannt, ohne den Befehl „Verwerfen" — über die Aufträge ans Feld.
 *
 * ⚠️ Vierte Runde: nur NACHGESEHEN (`nimm`); aus der Ablage geht es erst,
 *    wenn die Erkennung gelang (`gelungen` → `hole`). Vorher war es schon
 *    heraus, und scheiterte die Erkennung, war es weg.
 */
function pttZurueckholen(weg: "tipp" | "wort", welches?: Verworfenes): Promise<void> {
  const v = pttVerworfen.nimm(welches);
  if (!v) {
    pttVerworfenKartenAufraeumen();
    showToast("Nichts mehr zum Zurückholen — nach einer Minute ist es weg.", "info");
    return Promise.resolve();
  }
  meldeSpur("ptt", `zurueckholen weg=${weg} zeichen=${v.text.length}`);
  return pttFeld.auftrag({
    ziel: "zurueckholen",
    ton: v.ton,
    mime: v.mime,
    endwort: { handlung: "verwerfen", fuerSich: v.fuerSich },
    gilt: () => !v.zurueckgeholt,
    gelungen: () => {
      pttVerworfen.hole(v);
      pttVerworfenKartenAufraeumen();
      meldeSpur("ptt", `zurueckgeholt weg=${weg} zeichen=${v.text.length}`);
    },
  });
}

/**
 * Das gesprochene „Verwerfen" (Ticket 2674): wie der Knopf Verwerfen — nur
 * bleibt die AUFNAHME eine Minute zurückzuholen. (26.09.2026 spät)
 *
 * ⚠️ Der Befund zur stummen Quelle zuerst: der Stopp beendet ihre Messung.
 *    Ebenso der TON (vierte Runde): die Leitungs-Wache endet mit dem Stopp.
 * ⚠️ „sending", solange der Rekorder den Rest liefert — sonst handelte ein
 *    zweiter Weg (Knopf, Stille) noch auf dieselbe Aufnahme.
 * ⚠️ Vierte Runde: Aufgehoben wird, was TON hatte — nicht, was die
 *    Mitschrift zeigte. Stand sie, lag genau das nie in der Ablage.
 */
async function pttVerwerfenUmkehrbar(wort: { fuerSich: boolean; text: string }): Promise<void> {
  feldDiktat = null; // Prüfauflage 5: verworfen — die Cursor-Stelle geht mit
  pttStummHinweisAmEnde();
  const hatteTon = pttRecorder.quelleGabTon();
  pttState = "sending";
  const mime = pttRecorder.getMimeType();
  const ton = await pttRecorder.stopAndGet().catch(() => null);
  aufnahmeFreigeben(); // Ticket 2882: gestoppt — andere Seiten sofort freigeben
  pttVerwerfen("selbst-verwerfen");
  pttLegeVerworfenWeg({
    ton: ton && ton.size >= LEERE_AUFNAHME_BYTES ? ton : null,
    hatteTon,
    mime: ton?.type || mime,
    text: wort.text,
    fuerSich: wort.fuerSich,
  });
}

/**
 * Das gesprochene „zurückholen" / „doch nicht" — diese Aufnahme ist der
 * Befehl. (26.09.2026, vierte Runde)
 *
 * Bis zur dritten Runde wurde sie verworfen, sobald die Karte auslöste
 * (2 s Ruhe nach „Doch nicht"). Eine Denkpause MITTEN in „Doch nicht …
 * so schlimm" — oder eine Mitschrift, die hinterherhinkt — sieht genauso
 * aus, und dann war die laufende Aufnahme weg. Jetzt wird sie ganz erkannt
 * (derselbe Weg wie beim Senden, ohne Modellzug): ist sie NUR der Befehl,
 * kommt das Verworfene zurück; steht mehr darin, geht sie als Nachricht
 * hinaus — im Zweifel senden, nie verwerfen. Scheitert die Erkennung,
 * bleibt sie mit „Erneut senden".
 */
async function pttZurueckholBefehl(): Promise<void> {
  if (pttState === "idle" || pttState === "sending") return;
  // 21.10.2026 (Prüfauflage 1): ein Feld-Diktat sendet nie — auch „doch nicht …“ mit mehr darin geht ins Feld (ganz, an die Cursor-Stelle).
  if (feldDiktat) {
    await pttFeldDiktatEnde("selbst-zurueckholen", null, false); // der Stopp-Ton klang schon (onSelbstSenden)
    return;
  }
  pttStummHinweisAmEnde();
  meldePttEnde("zurueckholen", "anlass=selbst-zurueckholen");
  const seq = pttSendenBeginnen();
  const ausloeser = wachwortAusloeser(pttTiming.extra.via); // Ticket 2624, wie in pttFinishAndSend
  const mime = pttRecorder.getMimeType();
  const blob = await pttRecorder.stopAndGet().catch(() => null);
  aufnahmeFreigeben(); // Ticket 2882: gestoppt — andere Seiten sofort freigeben
  const satz = await pttLiveAbschluss();
  const ruhe = (): void => {
    window.clearTimeout(pttWatchdogTimer);
    pttCloseModal();
    pttState = "idle";
    pttResetButton();
    void resumeRecorderAfterPttIfNeeded();
  };
  if (!blob || blob.size < LEERE_AUFNAHME_BYTES) {
    // Keine Aufnahme, nichts anzuzweifeln: die Mitschrift war das ganze Diktat.
    ruhe();
    await pttZurueckholen("wort");
    return;
  }
  const typ = blob.type || mime;
  const erkannt = await erkenneGanzeAufnahme(blob, typ, null, loadSettings().sendeWort ?? "", {}, satz);
  const was = zurueckholBefehl(erkannt);
  meldeSpur("ptt", `zurueckholen-befehl ${was}${erkannt.ok ? "" : ` grund=${erkannt.grund}`}`);
  if (was === "senden") {
    await pttSendBlob(blob, typ, seq, "", undefined, undefined, undefined, null, false, ausloeser);
    return;
  }
  ruhe();
  if (was === "zurueckholen") await pttZurueckholen("wort");
  else if (was === "wiederholen") offerPttRetry(blob, typ, null);
  else pttMelde("Nichts verstanden.");
}

/** Pre.480: Modal-Cleanup nach Send/Abort. */
function pttCloseModal(): void {
  if (pttModal) {
    pttModal.close();
    pttModal = null;
  }
}

let pttWatchdogTimer = 0;
let pttSendSeq = 0;

/*
 * ── Aufträge ans Eingabefeld (26.09.2026, vierte Runde) ────────────────
 *
 * „ins Textfeld" (Wort und Knopf), „Umschreiben" und „Zurückholen" nehmen
 * EINEN Weg — denselben Hochlade- und Erkennungsweg wie das Senden, mit
 * derselben Karte „Erneut versuchen" (offerPttRetry), der Reihe nach, das
 * Feld solange gesperrt. Begründung in lib/feld-auftraege.ts.
 */
/** Ist das Eingabefeld gerade für einen Auftrag gesperrt? (pttFeldSperren) */
let pttFeldGesperrt = false;
const pttFeld = neueFeldAuftraege({
  einstellung: () => loadSettings().sendeWort ?? "",
  feld: {
    lese: () => textField.value,
    haengeAn: (text, beimDruck) => pttLegeInsTextfeld(text, beimDruck),
    ersetze: (text) => {
      textField.value = text;
      autoResizeTextarea();
      updateTextInputState();
      textField.focus();
      hapticTap(12);
    },
    sperre: (hinweis) => pttFeldSperren(hinweis),
  },
  wiederholen: (a, satz) => offerPttRetry(a.ton, a.mime, a.endwort, { auftrag: a, satz }),
  melde: (text, art) => showToast(text, art),
  spur: (zeile) => meldeSpur("ptt", zeile),
});

/**
 * 2026-07-29 (Martin: "Wenn ich mit Push-to-Talk mache, hätte ich gerne oben
 * einen dritten Button 'Nur ins Textfeld übertragen', sodass ich noch den
 * Text anpassen kann, falls ich ihn nicht gleich wegschicken möchte oder
 * Attachments hinzufügen will"):
 *
 * Dieser Weg nimmt dieselbe Aufnahme, lässt sie erkennen und legt den Text
 * ins Eingabefeld — mehr nicht. Kein Modell, keine Werkzeuge, keine
 * Antwort. Was dann damit passiert, entscheidet Martin.
 *
 * Ticket 781 (2026-07-30): „Umschreiben" nimmt denselben Ausgang — der
 * Entwurf im Feld wird nach dem Gesprochenen umgeschrieben (NUR ungesendete
 * Texte, Martins Entscheidung). Ohne Entwurf wird es „ins Textfeld".
 *
 * ⚠️ 2026-08-28, Fund der Gegenpruefung zu pre.2352: Dieser Ausgang holte
 *    weder den Abschluss noch verwarf er die Mitschrift — die LiveDictation
 *    blieb mit offener WebSocket auf einem beendeten Strom stehen und
 *    schrieb die Worte der vorigen Aufnahme über die laufende. Darum hier
 *    immer pttLiveAbschluss().
 * ⚠️ 26.09.2026 (vierte Runde): Der Platz in der Reihe wird JETZT genommen
 *    (pttFeld.auftrag, bevor der Rekorder den Rest geliefert hat) — so
 *    bleibt die Reihenfolge der Befehle, und das Feld ist sofort gesperrt.
 *    Die Mitschrift reist als Rückfall zum Server, wie beim Senden; ins
 *    Feld legt die Seite sie nie selbst.
 */
function pttBeendeInsFeld(
  ziel: "textfeld" | "umschreiben",
  endwort: EndwortAngabe | null,
  /** 20.10.2026: nach dem Gelingen (der Text steht im Feld) — lib/feld-auftraege.ts `gelungen`. */
  gelungen?: () => void,
): Promise<void> {
  if (pttState === "idle" || pttState === "sending") return Promise.resolve();
  // 21.10.2026: Diktat aus dem Feld-Mikro — an die Cursor-Stelle (beim Umschreiben zählt sie nicht). Einmal genommen.
  const beimDruck = ziel === "textfeld" ? feldDiktat : null;
  feldDiktat = null;
  // Ticket 2643 (26.09.2026 spät): nichts verwerfen — nur der Satz, wenn
  // bis zum Ende kein Ton kam. VOR dem Stopp gefragt.
  pttStummHinweisAmEnde();
  pttState = "sending";
  refs.pttBtn.disabled = true;
  const mime = pttRecorder.getMimeType();
  const erkannt = (async (): Promise<FeldAuftrag | null> => {
    const blob = await pttRecorder.stopAndGet().catch(() => null);
    aufnahmeFreigeben(); // Ticket 2882: gestoppt — andere Seiten sofort freigeben
    const live = await pttLiveAbschluss();
    pttCloseModal();
    pttState = "idle";
    pttResetButton();
    void resumeRecorderAfterPttIfNeeded();
    // Ticket 2618: dieselbe Schwelle, unter der ein Diktat in Teilen seinen
    // letzten Teil nicht als Ende nimmt (lib/diktat-teile.ts).
    if (!blob || blob.size < KLEINSTER_TEIL_BYTES) {
      // 2026-08-31: ueber den Riegel, nicht direkt — sonst kann dieser Satz
      // wieder unter einer anderen Meldung stehen (Martins Foto: zwei
      // widersprechende Meldungen nach EINEM Druck).
      pttMelde("Zu kurz — Knopf halten und sprechen.");
      return null;
    }
    return { ziel, ton: blob, mime: blob.type || mime, endwort, live };
  })();
  // 21.10.2026: das Diktat aus dem Feld-Mikro reist mit seiner Cursor-Stelle (`beimDruck`).
  const roh = beimDruck ? erkannt.then((a) => (a ? { ...a, beimDruck } : a)) : erkannt;
  // 20.10.2026 (Vollständigkeits-Bremse, N3): wer es wissen muss, erfährt erst nach dem Gelingen, dass der Text im Feld steht.
  const aufnahme = gelungen ? roh.then((a) => (a ? { ...a, gelungen } : a)) : roh;
  return pttFeld.auftrag(aufnahme);
}

async function pttFinishToRewrite(): Promise<void> {
  await pttBeendeInsFeld("umschreiben", null);
}

/**
 * Text ins Eingabefeld — der EINE Ort, der das Feld beschreibt (26.09.2026
 * abends): an bestehenden Text anhängen, nie überschreiben — wer schon
 * etwas getippt hat, verliert es nicht.
 *
 * 21.10.2026 (Martin: „weil der Cursor ja schon positioniert ist“): kommt der Text aus dem Mikro IM Feld (`beimDruck`,
 * siehe feldDiktat), steht er an der Cursor-Stelle — eine Markierung wird ersetzt (lib/diktat-cursor.ts). Danach steht
 * der Cursor hinter dem Text, der Fokus im Feld; gesendet wird nie. Das `input`-Ereignis rechnet Höhe und
 * `data-senden-bereit` neu (es ist nicht `isTrusted` und zählt darum nicht als Tippen, merkeTippTaste).
 */
function pttLegeInsTextfeld(text: string, beimDruck?: FeldStand): void {
  let cursor: number;
  if (beimDruck) {
    const stelle = stelleImFeld(beimDruck, { ...leseFeldStand(), fokus: document.activeElement === textField });
    const r = fuegeEin(textField.value, stelle, text);
    textField.value = r.wert;
    cursor = r.cursor;
  } else {
    const bisher = textField.value.trim();
    textField.value = bisher ? bisher + " " + text : text;
    // Cursor ans Ende, damit man direkt weiterschreiben kann.
    cursor = textField.value.length;
  }
  textField.focus();
  textField.dispatchEvent(new Event("input", { bubbles: true }));
  // Nach der Höhe (sie rollt ans Ende): die Markierung zuletzt — der Browser rollt dann zum Cursor.
  textField.setSelectionRange(cursor, cursor);
}

/** Wert und Markierung des Eingabefelds jetzt. */
function leseFeldStand(): FeldStand {
  return { wert: textField.value, start: textField.selectionStart ?? textField.value.length, ende: textField.selectionEnd ?? textField.value.length };
}

/**
 * 21.10.2026: Diktat aus dem Mikro IM Feld (#composer-ptt) — Wert und Cursor beim Druck. Gesetzt bei JEDEM Aufnahmestart
 * (startPttRecording: Feld-Mikro → Stand, jeder andere Weg → null), genommen beim Ende (pttBeendeInsFeld). Solange es
 * steht, endet die Aufnahme immer im Feld (pttFinishAndSend), nie als Nachricht.
 */
let feldDiktat: FeldStand | null = null;

const FELD_DIKTAT_HINWEIS = "Steht im Feld – nicht gesendet.";

/**
 * Das Ende eines Feld-Diktats (Prüfauflage 3): hörbar der Stopp-Ton (keine Sende-Vibration), die Karte sagt „Ins Feld …“,
 * und erst wenn der Text steht (`gelungen` des Feld-Auftrags) der Hinweis, dass NICHT gesendet wurde. Journal ohne Text.
 */
async function pttFeldDiktatEnde(
  anlass: string,
  endwort: EndwortAngabe | null,
  /** false: der Aufrufer hat den Stopp-Ton schon gespielt (onSelbstSenden „zurueckholen“) — nie zweimal. */
  stoppTon = true,
): Promise<void> {
  if (stoppTon) playBeep("stop");
  pttModal?.setInsFeld();
  meldePttEnde("textfeld", `anlass=feld-diktat ${anlass}`);
  await pttBeendeInsFeld("textfeld", endwort, () => showToast(FELD_DIKTAT_HINWEIS, "info", 4000));
}

/** Das Mikro IM Feld übernimmt die Aufnahme (Start oder Tipp während einer laufenden): Stand merken, Karte umstellen. */
function feldDiktatSetzen(anlass: string): void {
  feldDiktat = leseFeldStand();
  pttModal?.setInsFeld();
  meldeDiagnose(`[ptt] feld-diktat ${anlass}`);
}

/**
 * 20.10.2026 (Vollständigkeits-Bremse, shared/satz-vollstaendig.ts; Martin: „Es kann nie abgeschickt werden, wenn der Satz
 * unvollständig ist“): die Karte wollte von selbst senden, der Satz ist aber offen. Er geht NICHT an die KI — die ganze
 * Aufnahme wird erkannt und ins Eingabefeld gelegt (derselbe Weg wie „ins Textfeld“, mit „Erneut versuchen“ bei Fehlern:
 * nichts geht verloren). Senden oder Weitersprechen entscheidet Martin.
 */
function pttUnvollstaendigInsFeld(anlass: string, vorsatz = "", ttlMs = 8000): void {
  playBeep("stop");
  meldePttEnde("textfeld", `anlass=${anlass}`);
  // Prüfauflage N3: „steht im Eingabefeld“ erst, wenn es dort steht — scheitert die Erkennung, kommt „Erneut versuchen“.
  // Das Feld VOR dem Einfügen (es ist bis dahin gesperrt): stand dort ein getippter Entwurf, wird nichts als Bremse-Text gemerkt.
  const vorher = textField.value;
  void pttFinishToTextField(false, () => {
    merkeBremseText(vorher);
    showToast(`${vorsatz}${BREMSE_ZURUECK_HINWEIS}`, "warning", ttlMs);
  });
}

const BREMSE_ANGEHAENGT_HINWEIS = "Angehängt – der ganze Satz steht im Eingabefeld. Senden oder weitersprechen.";

async function pttFinishToTextField(
  /**
   * 26.09.2026 abends (Ticket 2659): hat das gesprochene „ins Textfeld"
   * ausgelöst? Nur dann fällt es aus dem Text — per Knopf bleibt der Text,
   * wie er gesagt wurde.
   */
  durchWort = false,
  /** 20.10.2026 (Vollständigkeits-Bremse, N3): erst nachdem der Text im Feld steht. */
  gelungen?: () => void,
): Promise<void> {
  await pttBeendeInsFeld("textfeld", durchWort ? { handlung: "textfeld", fuerSich: true } : null, gelungen);
}

/**
 * Ein Zug geht ab: Zustand, Knopf, Wachhund — gibt die Sequenz des Zugs
 * zurück. (24.09.2026, Ticket 2536: aus pttFinishAndSend herausgelöst, damit
 * der Satz nach dem Wach-Wort DENSELBEN Wachhund bekommt statt eines zweiten.)
 */
function pttSendenBeginnen(): number {
  pttState = "sending";
  refs.pttBtn.classList.remove("is-recording");
  refs.pttBtn.disabled = true;
  // 2026-07-03 (Martin): Wachhund — haengt der Server-Stream, bleibt der
  // Button sonst dauerhaft ausgegraut. Audit 2026-07-04: Handle + Sequenz —
  // der alte anonyme Timer ueberlebte seinen Sendevorgang und schoss einen
  // SPAETEREN Send ab (heisses Mikro, verlorene Nachricht).
  const seq = ++pttSendSeq;
  window.clearTimeout(pttWatchdogTimer);
  pttWatchdogTimer = window.setTimeout(() => {
    if (pttState === "sending" && seq === pttSendSeq) {
      // Ticket 2634: auch die Karte. Sie blieb bisher stehen, obwohl der Knopf
      // freigegeben war — „Sende …“ für immer.
      pttCloseModal();
      pttState = "idle";
      pttResetButton();
      void resumeRecorderAfterPttIfNeeded();
    }
  }, 90_000);
  return seq;
}

/*
 * ── Fortsetzung (12.10.2026, Bahn „fortsetzung-anhaengen“; shared/fortsetzung.ts, docs/fortsetzung-2026-10-12.md) ──────────────────────────
 *
 * Martin spricht oft in Denkpausen; „Absenden nach Stille“ schneidet dann mitten in der Äußerung ab. Bisher: kein Anschließen — die Antwort auf
 * den ersten Teil lief, der Knopf war bis zu ihrem Ende gesperrt (`pttState = "sending"`, `pttBtn.disabled`), der Rest ging als neue,
 * getrennte Nachricht hinaus. Jetzt: beginnt er binnen 12 s nach einem Auto-Senden eine NEUE Aufnahme (Knopf oder Aktionstaste), ist der Knopf
 * frei (`fortsetzungUebernehmen`), die Antwort der Vorrunde wird SOFORT abgebrochen (derselbe Weg wie der Abbruch-Knopf, POST /api/chat/abort),
 * die Wiedergabe gestoppt, und die Aufnahme geht mit dem Feld `fortsetzung` hinaus. Der Server prüft es; erst sein Ja (Ereignis `fortsetzung`)
 * lässt die Seite das Etikett zeichnen.
 */
const fortsetzungSpeicher = neuerFortsetzungSpeicher();
/** Die Zug-Nummer (`pttSendSeq`), deren Auto-Senden im Speicher steht; −1: keine. Nur dieser Zug darf den Knopf freigeben. */
let fortsetzungSendungSeq = -1;
/** Die Blase der Antwort des letzten Zuges (für das Etikett „abgebrochen“, sobald der Server die Fortsetzung bestätigt). */
let fortsetzungVorigeBlase: HTMLElement | null = null;

/** Ist eine Vorlese-Schlange offen oder klingt ein Element? (Für `tts=` der Fortsetzung — vor dem Stopp lesen.) */
function ttsWiedergabeOffen(): boolean {
  if (currentAudio !== null && !currentAudio.paused) return true;
  for (const q of activeTtsQueues) if (q.isAudioActive()) return true;
  return false;
}

/**
 * Der Druck auf den Knopf kommt, während die Antwort auf ein Auto-Senden noch erzeugt wird (`pttState === "sending"`) — und das Fenster der
 * Fortsetzung ist offen: der Knopf wird frei. Die Aufräumschritte des alten Zuges prüfen `seq === pttSendSeq` und fassen den Zustand danach nicht
 * mehr an; die Karte „Sende …“ geht zu. Den Abbruch der Antwort und die Angabe der Fortsetzung erledigt `startPttRecording` (gleich danach).
 * Liefert `true`, wenn der Knopf frei wurde.
 */
function fortsetzungUebernehmen(): boolean {
  /*
   * 12.10.2026 (Prüfung): die Freigabe gehört dem Zug, dessen Auto-Senden im Speicher steht (`fortsetzungSendungSeq`) — nicht irgendeinem
   * „sending“. Im Vorlauf eines Zuges (Rekorder anhalten, Mitschrift abschließen) steht im Speicher noch die Sendung des VORIGEN: sie zu
   * übernehmen ließe die Fortsetzung am falschen Zug hängen. Und: ist der Start ohnehin gesperrt (läuft schon eine Aufnahme), ändert
   * sich NICHTS — sonst bliebe die Antwort abgelöst, ohne dass eine neue Aufnahme beginnt.
   */
  if (pttState !== "sending" || fortsetzungSendungSeq !== pttSendSeq || !fortsetzungSpeicher.fensterOffen()) return false;
  if (aufnahmeStartSperre() !== null) return false;
  meldeDiagnose(`[fortsetzung-app] knopf-frei restMs=${fortsetzungSpeicher.restMs() ?? "-"}`);
  pttSendSeq++;
  window.clearTimeout(pttWatchdogTimer);
  pttCloseModal();
  pttState = "idle";
  pttResetButton();
  return true;
}

/**
 * Nach einem Auto-Senden bleibt der Knopf für die Frist der Fortsetzung frei (`pttSendenBeginnen` hat ihn gesperrt). Danach gilt wieder das Alte:
 * gesperrt, solange die Antwort erzeugt wird — es sei denn, der Zug ist zu Ende oder eine neue Aufnahme hat die Zügel (`seq` veraltet).
 */
function fortsetzungKnopfFrei(seq: number): void {
  refs.pttBtn.disabled = false;
  window.setTimeout(() => {
    if (pttState === "sending" && seq === pttSendSeq) refs.pttBtn.disabled = true;
  }, FORTSETZUNG_FENSTER_MS + 100);
}

/**
 * Läuft gerade eine Aufnahme der Sprechtaste — auch im Anlauf? (26.09.2026)
 * Dann klingt die Quittung eines älteren Zugs nicht (quittungFuerVersand):
 * der Ton landete in der neuen Aufnahme.
 */
function pttNimmtAuf(): boolean {
  return pttState === "recording" || pttRecorder.isRecording();
}

/**
 * Der Satz nach „Hey Vespera" geht ab — ohne Mikrofon. (24.09.2026, Ticket 2536)
 *
 * Martin: „Hey Vespera, wie wird das Wetter morgen in Hollenthon" in EINEM
 * Atemzug, wie „Hey Siri, …". Der Lauscher hat den Satz schon gehört (Ton ab
 * dem Ende des Wach-Worts, Text auf dem Gerät erkannt); hier wird er nur
 * noch abgeschickt.
 *
 * ⚠️ DERSELBE Sendeweg wie die Sprechtaste (pttSendBlob): Blase, Vorlesen,
 *    Werkzeugkarten, „Erneut senden", Messung. Es fehlt nur der Anfang — kein
 *    Mikrofon, kein Fenster, keine Mitschrift. Ein eigener Sendeweg wäre ein
 *    zweiter Besitzer.
 * ⚠️ Der Server erkennt den Ton trotzdem selbst (seit 04.09., siehe
 *    voice-command.ts „Live-Text als Rueckfall") — der Text vom Gerät ist
 *    dort der Rückfall, wie der Live-Text. Die Schnellspur greift auf dem
 *    Server-Text.
 */
function pttSendeWachwortSatz(s: {
  ton: Blob;
  mime: string;
  text: string;
  dauerMs: number;
}): boolean {
  if (connected || pttState !== "idle") return false;
  // 22.10.2026 (lib/ja-karte-offen.ts): nur „Ja“/„Nein“ bei offener Ja/Nein-Karte — nicht senden, nur der Hinweis auf den Knopf.
  if (gesprochenesJaVerwerfen(s.text, refs.chat, Date.now(), meldeDiagnose)) {
    meldeDiagnose("[ptt] ja-karte-offen gesprochen-verworfen via=wachwort");
    showToast(JA_KARTE_HINWEIS, "info", 5000);
    return true;
  }
  // Wie beim Druck: eine laufende Wiedergabe endet, der Chat ist zu sehen.
  stopCurrentTts("wachwort-satz");
  if (
    document.body.dataset.activeView &&
    document.body.dataset.activeView !== "chat"
  ) {
    switchToTab("chat");
  }
  unlockAudio();
  pttMeldungenFreigeben();
  const seq = pttSendenBeginnen();
  /*
   * Dieselbe Zeile wie beim Loslassen: an ihr beginnt die Antwortzeit-Messung
   * (Ticket 2537, server/antwort-zug.ts). `via=wachwort` ordnet den Zug dem
   * Wach-Wort zu; `dauer` ist die Länge des Satzes, nicht einer Aufnahme.
   */
  meldeDiagnose(
    `[ptt-ende] senden via=wachwort anlass=wachwort-satz dauer=${s.dauerMs}ms zustand=${pttState}`,
  );
  // 26.09.2026: hörbar „weg", sobald der Server den Satz angenommen hat.
  const quittung = quittungFuerVersand("wachwort-satz", s.ton.size, pttNimmtAuf);
  // 12.10.2026: der Satz nach „Hey Vespera“ ist keine Fortsetzung und keine Vorrunde — ein offenes Fenster schließt sich.
  fortsetzungSpeicher.vergiss();
  void pttSendBlob(s.ton, s.mime, seq, s.text, "geraet", undefined, quittung);
  return true;
}

/**
 * 21.10.2026 (Martin 19:58; Auflagen Sicherheitsprüfer, lib/bremse-feld.ts): steht noch der angefangene Satz der
 * Vollständigkeits-Bremse im Feld (≤ 60 s, selber Zug, unverändert), gehört das Gesprochene DAZU — nie geht die zweite
 * Hälfte allein an die KI:
 *   „zusammen“  das Mikro ging von selbst auf (nach einer Frage) → Feld + Gesprochenes als EINE Nachricht, das Feld wird leer;
 *   „anhaengen“ Martin drückte selbst → ans Feld angehängt, nichts gesendet (er entscheidet).
 * Hörbar (Stopp-Ton) und im Journal (ohne Text). Nichts verloren (Fehler → „Erneut versuchen“), nichts doppelt.
 * Ein von selbst geöffnetes Mikro ohne belegte Sprache schließt weiter still (pttLeerStill) — kein „Nichts verstanden“ am Feld.
 * Der Satz nach dem Wach-Wort (pttSendeWachwortSatz) nimmt diesen Weg bewusst NICHT: „Hey Vespera …“ beginnt etwas Neues.
 * true = erledigt (ins Feld oder zusammen gesendet), false = normal senden.
 */
async function pttBremseFortsetzung(): Promise<boolean> {
  const bremse = bremseWeg({ m: bremseMerker, feld: textField.value, jetzt: Date.now(), zug: zugKennung(), vonSelbstNachFrage: mikroVonSelbstOffen() });
  if (bremse === "normal" || pttModal?.nachlauschOhneSprache() === true) return false;
  playBeep("stop");
  meldeDiagnose(`[ptt-ende] bremse-fortsetzung weg=${bremse}`);
  const vorher = textField.value;
  const ergebnis = { steht: null as string | null };
  await pttFinishToTextField(false, () => {
    ergebnis.steht = textField.value;
  });
  if (ergebnis.steht === null) return true; // nichts erkannt / Fehler: „Erneut versuchen“ trägt es, das Feld bleibt
  // Nachprüfung: hat inzwischen ein neuer Druck begonnen (Mikro, Karte) oder sperrt ein Auftrag das Feld, wird NICHT gesendet —
  // dann wie „anhaengen“ (sonst beendete das Senden die neue Aufnahme bzw. der Text läge ohne Hinweis im Feld).
  if (zusammenJetztSenden({ weg: bremse, feldUnveraendert: textField.value === ergebnis.steht, ruhe: pttState === "idle", karteOffen: !!pttModal, feldGesperrt: pttFeldGesperrt })) {
    bremseMerker = null;
    meldeDiagnose("[ptt-ende] bremse-fortsetzung gesendet=zusammen");
    await handleTextSubmit("angetippt");
    return true;
  }
  merkeBremseText(vorher);
  showToast(BREMSE_ANGEHAENGT_HINWEIS, "info", 6000);
  return true;
}

async function pttFinishAndSend(
  anlass = "unbenannt",
  /**
   * Ticket 2659: schickte ein Auslösewort ab — und stand es für sich
   * (Sprechpause davor)? `null`: nicht das Wort (Knopf, Stille …); dann
   * bleibt der Text, wie er gesagt wurde (siehe pttSendBlob).
   */
  endwort: EndwortAngabe | null = null,
  /**
   * 21.10.2026 (Vorfall 17:25:05): die Vollständigkeits-Bremse der Karte hielt an, weil die LIVE-Mitschrift offen endete. Die
   * Aufnahme geht trotzdem zum Server (nur mit „selbst-stille“); ER entscheidet am endgültigen Text: fertig → hinaus, sonst ins Feld.
   */
  liveOffen = false,
): Promise<void> {
  if (pttState === "idle" || pttState === "sending") return;
  const endeStart = performance.now(); // 28.10.2026: Aufnahme-Ende — für die Zeile „modal-zu“ (nur Zahlen)
  /*
   * 21.10.2026 (Martin: „mit dem Mikrofonzeichen automatisch in das Textfeld einbinden“): kam die Aufnahme aus dem Mikro
   * IM Feld, endet sie im Feld an der Cursor-Stelle — egal wodurch (zweiter Tipp, Senden-Knopf der Karte, Stille, Wort).
   * Nie gesendet; ein auslösendes Wort fällt wie sonst aus dem Text. Senden entscheidet Martin.
   */
  if (feldDiktat) {
    await pttFeldDiktatEnde(anlass, endwort);
    return;
  }
  // Ticket 2643 (26.09.2026 spät): nichts verwerfen — nur der Satz, wenn
  // bis zum Ende kein Ton kam. VOR dem Stopp gefragt.
  pttStummHinweisAmEnde();
  /*
   * 12.10.2026 (Messfühler): endet die Aufnahme NICHT durch Auto (Knopf, Wort …), steht in der Zeile, wo Auto
   * gerade stand — Stille, Jevs Urteil, Satzende, und wie lange es noch gebraucht hätte. Nur Zahlen.
   */
  const autoStand = anlass === "selbst-stille" ? "" : (pttModal?.autoStand() ?? "");
  // 15.10.2026 (Bahn „wortstille“): bei JEDEM Senden — wie lange der Text ruhte und wie lange der Pegel davon nicht still war.
  const ruheStand = pttModal?.ruheStand() ?? "";
  meldePttEnde("senden", `anlass=${anlass}${liveOffen ? " bremse=live-offen" : ""}${autoStand ? " " + autoStand : ""}${ruheStand ? " " + ruheStand : ""}`);
  /*
   * Wartet ein Bild im Eingabefeld, gehört das Gesprochene DAZU. (05.09.2026)
   *
   * Martin: *„Wenn ich ein Bild mit dem Plus in das Textfeld einfüge und dann
   * auf Aufnahme klicke, dann soll die Aufnahme sich primär einbetten ins
   * Textfeld, sodass das beides in Kombination dann geschickt wird. … Da
   * brauch ich nicht das kleine Feld anklicken, sondern dann soll es
   * automatisch eingefügt werden, weil das die logische Konsequenz ist."*
   *
   * Er hat recht: Wer ein Bild anhängt und dann spricht, sagt etwas ÜBER das
   * Bild. Sofort zu senden hieße, den Anhang stehen zu lassen und den Satz
   * allein loszuschicken — zwei Nachrichten, wo eine gemeint war.
   */
  if (pendingComposerImages.length > 0) {
    await pttFinishToTextField();
    return;
  }
  // 21.10.2026 (Martin 19:58): steht noch der angefangene Satz der Bremse im Feld, gehört das Gesprochene dazu (pttBremseFortsetzung).
  if (await pttBremseFortsetzung()) return;
  const seq = pttSendenBeginnen();
  // 12.10.2026 (Fortsetzung, docs/fortsetzung-2026-10-12.md): endet die Aufnahme durch Stille, merkt die Seite sich Jevs Urteil, das Satzende und die Stille (nur Zahlen) — VOR dem Stopp gelesen.
  const autoSendet = darfAmServerVerwerfen(anlass);
  const autoZahlen = autoSendet ? (pttModal?.autoZahlen() ?? null) : null;
  const autoSendeStart = performance.now();
  // Ticket 3024: ein von selbst geöffnetes Mikro hat beim Öffnen nichts abgebrochen — jetzt wird gesendet,
  // die neue Nachricht löst die laufende Antwort ab (wie Reinsprechen per Knopf und wie Senden mit Text).
  // Prüfbefund: nur, wenn wirklich eine Antwort läuft (chatTurnActive) — sonst ist der Abbruch ein leerer Ruf.
  if (mikroVonSelbstOffen() && chatTurnActive) void fetch("/api/chat/abort", { method: "POST" }).catch(() => {});
  // Ticket 2624: gleich hier gelesen — bis der Ton gestoppt ist, kann schon der nächste Druck begonnen haben.
  const ausloeser = wachwortAusloeser(pttTiming.extra.via);
  /*
   * 24.09.2026 (Ticket 2202): Ab hier wartet Martin auf seinen Text. Ob das
   * auf dem nativen Weg (ohne Mitschrift) länger dauert, ist die offene
   * Hälfte der Probewoche — `[ptt-text]` misst es (siehe pttSendBlob). Die
   * Uhr läuft ab JETZT, dem Loslassen.
   *
   * ⚠️ Den Weg liest erst die Zeile nach `pttWarteAufStart` unten: Wer
   *    loslässt, während der native Start noch über die Brücke läuft, hätte
   *    hier einen Druck ohne Weg gezählt. Später darf es nicht sein — bis
   *    der Text kommt, kann schon der nächste Druck begonnen haben.
   */
  const messung = neueTextMessung(performance.now());
  // Ticket 2668: gefragt, solange DIESE Karte offen ist — bis der Text kommt,
  // kann schon der nächste Druck eine neue haben.
  messung.nachlauschOhneSprache = pttModal?.nachlauschOhneSprache() === true;
  /*
   * ⚠️ 2026-08-31 — DIE eigentliche Reparatur von Martins Doppelmeldung.
   *
   * Vorher wurde hier sofort gestoppt. Steckte der Start noch im Mikro-Aufbau
   * (gemessener Median 479 ms, p90 1610 ms), war `this.recorder` schlicht
   * noch `null`: `stopAndGet()` lieferte `null`, der Zweig darunter meldete
   * „Zu kurz" — und der verspaetet fertige Start warf gleich die zweite,
   * widersprechende Meldung hinterher.
   *
   * Jetzt wartet das Senden den Start ab. Danach existiert der Recorder
   * wirklich, es wird echter Ton gestoppt, und der spaete Aufraeumpfad
   * findet einen stimmigen Zustand vor, statt eine Leiche zu melden.
   */
  let blob: Blob | null = null;
  let hatteRecorder = false;
  try {
    await pttWarteAufStart();
    // Ticket 2202: jetzt steht fest, welchen Weg dieser Druck nahm.
    messung.weg = pttTiming.wegZeile();
    if (pttRecorder.isRecording()) {
      const fehl = 400 - pttRecorder.getElapsedMs();
      if (fehl > 0) {
        await new Promise<void>((r) => window.setTimeout(r, fehl));
      }
    }
    hatteRecorder =
      pttRecorder.isRecording() || pttRecorder.getElapsedMs() > 0;
    blob = await pttRecorder.stopAndGet();
    aufnahmeFreigeben(); // Ticket 2882: gestoppt — andere Seiten sofort freigeben
  } catch (err) {
    window.clearTimeout(pttWatchdogTimer);
    pttLiveVerwerfen();
    pttCloseModal();
    pttState = "idle";
    pttResetButton();
    void resumeRecorderAfterPttIfNeeded();
    pttMelde(
      err instanceof Error
        ? `Senden fehlgeschlagen (${err.message}) — bitte noch einmal.`
        : "Senden fehlgeschlagen — bitte noch einmal.",
    );
    return;
  }
  if (!blob) {
    meldeDiagnose(
      `[ptt-ende] puffer-leer hatteRekorder=${hatteRecorder ? "ja" : "nein"}${messung.weg ? " " + messung.weg : ""}`,
    );
    // ⚠️ 2026-08-28: Dieser Zweig kehrte zurueck, BEVOR unten der Abschluss
    // geholt wird — die Mitschrift blieb mit offener Verbindung stehen.
    pttLiveVerwerfen();
    window.clearTimeout(pttWatchdogTimer);
    pttBubble = null;
    pttCloseModal();
    pttState = "idle";
    pttResetButton();
    void resumeRecorderAfterPttIfNeeded();
    /*
     * ⚠️ 2026-08-31 — hier stand pauschal „Zu kurz — Knopf halten und
     * sprechen.", auch wenn Martin eine Sekunde gehalten und gesprochen
     * hatte. Der Satz war schlicht falsch: nicht ER war zu kurz, das MIKRO
     * war zu spaet (gemessen bis 1897 ms bis zum Recorder-Start).
     *
     * Jetzt entscheidet die tatsaechliche HALTEDAUER am Finger, welcher
     * Satz stimmt — nicht mehr der Umstand, dass kein Blob herauskam.
     * `pttReleaseHeld` wird beim Loslassen aus `pttPressStart` gerechnet,
     * misst also den Druck und nicht die Aufnahme (die beiden Uhren
     * auseinanderzuhalten ist der Kern dieses Fehlers).
     */
    if (hatteRecorder && pttReleaseHeld > 0 && pttReleaseHeld < 700) {
      pttMelde("Zu kurz — Knopf halten und sprechen.");
    } else {
      pttMelde("Mikrofon war noch nicht bereit — bitte noch einmal.");
    }
    return;
  }
  // Mitschrift abschliessen, BEVOR gesendet wird — die letzten Tokens
  // kommen mit kurzer Verzoegerung.
  const { satz, stand: liveStand } = await pttLiveAbschlussMitStand();
  // 22.10.2026 (Martin 07:55): nur ein bloßes „Ja“/„Nein“ gesagt, während eine Ja/Nein-Karte offen ist — das zählt dort nie (nur der
  // Knopf). Nichts senden, nichts speichern; ein Hinweis, eine Journalzeile ohne Text (lib/ja-karte-offen.ts).
  if (gesprochenesJaVerwerfen(satz, refs.chat, Date.now(), meldeDiagnose)) {
    pttJaKarteVerwerfen();
    return;
  }
  /*
   * 26.09.2026 — Ging der Zug OHNE Knopfdruck ab (Stille, Auslösewort,
   * Freihand), klingt „gesendet" und es vibriert. Martin: *„damit ich
   * höre …, dass die Absendung erfolgt, wenn ich das Handy nicht ansehe."*
   *
   * ⚠️ Hier wird die Quittung nur GEBAUT. Klingen darf sie erst, wenn der
   *    Server die Aufnahme angenommen hat (streamPtt, onAngenommen) — bis
   *    pre.3318 klang sie hier, auch bei leerer Aufnahme oder ohne Netz.
   */
  const quittung = quittungFuerVersand(anlass, blob.size, pttNimmtAuf);
  /*
   * 28.10.2026 (Martin: „das Fenster ist länger offen als nötig, obwohl hinten schon etwas passiert“): ab hier ist alles Nötige
   * gelesen (Karten-Stände oben, Aufnahme gestoppt, Mitschrift abgeschlossen) — die Karte „Sende …“ hat nichts mehr zu tun. Bisher
   * blieb sie bis zur Antwort des Servers auf das Transkript offen (Hochladen + Erkennung, oft 1–3 s). Jetzt geht sie zu; die
   * ruhige Anzeige ist die Blase „Sprachnachricht wird transkribiert …“ im Chat (pttSendBlob). Nichts davon ändert, wann das Mikro
   * stoppt oder was gesendet wird. Ein Spätkommender Rückruf schließt nur die EIGENE Karte (schliesseEigeneKarte), nie eine neue.
   */
  meldeDiagnose(`[ptt-timing] modal-zu anlass=${anlass} ms=${Math.round(performance.now() - endeStart)}`);
  pttCloseModal();
  // Ticket 2780: endete das Diktat nach Stille, darf der Server verwerfen.
  const vonSelbst = darfAmServerVerwerfen(anlass);
  // 12.10.2026: die Kennung dieses Zuges (die nächste Aufnahme nennt sie, wenn sie die Fortsetzung ist) und die Angabe, falls DIESE Aufnahme eine war.
  const fort = fortsetzungSpeicher.beiSenden(vonSelbst, autoZahlen, fortsetzungErlaubt(), autoSendeStart);
  fortsetzungSendungSeq = vonSelbst && fortsetzungErlaubt() ? seq : -1;
  if (fortsetzungSendungSeq === seq) fortsetzungKnopfFrei(seq);
  await pttSendBlob(blob, pttRecorder.getMimeType(), seq, satz, undefined, messung, quittung, endwort, vonSelbst, ausloeser, liveStand, fort, vonSelbst && liveOffen);
}

/**
 * #123: „Erneut senden"-Angebot nach endgültigem Sende-Fehlschlag. Die
 * Aufnahme bleibt im Speicher — ein Klick versucht die Übertragung erneut,
 * NICHTS geht verloren. Die System-Bubble erklärt was passiert ist.
 */
function offerPttRetry(
  blob: Blob,
  mime: string,
  /** 26.09.2026 spät: das Endwort reist mit — auch der zweite Versuch nimmt es aus dem Text. */
  endwort: EndwortAngabe | null = null,
  /**
   * 26.09.2026 (vierte Runde): ein Auftrag ans Eingabefeld (ins Textfeld,
   * Umschreiben, Zurückholen) — DIESELBE Karte, nur „Erneut versuchen"
   * statt „Erneut senden", und der zweite Versuch geht wieder in die Reihe
   * der Aufträge (pttFeld), nicht zum Modell. Die Mitschrift reist dann
   * nicht mehr mit, wie beim „Erneut senden".
   */
  feld: { auftrag: FeldAuftrag; satz: string } | null = null,
): void {
  // 2026-07-01 (Martin): lesbare Notiz-Karte statt blasser grauer Zeile.
  const bubble = document.createElement("div");
  bubble.className = "ptt-retry";
  const msg = document.createElement("p");
  msg.className = "ptt-retry-text";
  msg.textContent = feld
    ? feld.satz
    : "Verbindung unterbrochen — deine Aufnahme ist gesichert, nichts geht verloren.";
  bubble.appendChild(msg);
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn btn--primary btn--sm ptt-retry-btn";
  btn.textContent = feld ? "Erneut versuchen" : "Erneut senden";
  btn.addEventListener("click", () => {
    bubble.remove();
    if (feld) {
      void pttFeld.auftrag({ ...feld.auftrag, ton: blob, mime, endwort, live: "" });
      return;
    }
    // Zustand wie bei einem frischen Send — Guard in pttSendBlob übernimmt.
    pttState = "sending";
    refs.pttBtn.disabled = true;
    void pttSendBlob(blob, mime, ++pttSendSeq, "", undefined, undefined, undefined, endwort);
  });
  bubble.appendChild(btn);
  insertOrdered(bubble, Date.now());
  fuehreChatNach();
}

/**
 * #123: Eigentlicher Sende-Pfad, vom Stop-Pfad getrennt — damit eine
 * gescheiterte Übertragung mit demselben (gemerkten) Blob wiederholt
 * werden kann, ohne dass der User neu aufsprechen muss.
 */
// 2026-07-14 (Review CRITICAL): seq als PARAMETER — vorher referenzierte
// pttSendBlob ein freies `seq` (deklariert nur in pttFinishAndSend) ->
// ReferenceError zur Laufzeit (esbuild prueft keine Typen, tsc deckt
// frontend nicht ab). Betrifft Stille-Waechter UND finally-Cleanup.
async function pttSendBlob(
  blob: Blob,
  mimeType: string,
  seq: number,
  /**
   * 2026-08-28: Was die Mitschrift waehrend des Sprechens verstanden hat.
   * Leer heisst: der Server erkennt wie bisher aus der Tondatei — genau das
   * ist auch der Fall beim "Erneut senden", wo die Mitschrift laengst
   * beendet ist.
   */
  liveText = "",
  /**
   * 24.09.2026 (Ticket 2536): „geraet" = der Text kam vom Wach-Wort-Lauscher
   * der App, nicht aus der Soniox-Echtzeit — der Server bucht ihn nicht so.
   */
  textQuelle?: "geraet",
  /**
   * 24.09.2026 (Ticket 2202): Loslassen und Weg dieses Drucks — nur beim
   * Senden von der Sprechtaste. „Erneut senden" und der Satz nach dem
   * Wach-Wort haben kein Loslassen; eine Wartezeit wäre dort erfunden.
   */
  messung?: TextMessung,
  /**
   * 26.09.2026: „gesendet" für einen Zug ohne Knopfdruck — klingt, sobald
   * der Server die Aufnahme angenommen hat (quittungFuerVersand).
   */
  quittung?: () => void,
  /**
   * 26.09.2026 (Ticket 2659): ging der Zug per Auslösewort ab — und stand
   * es laut Karte für sich (Sprechpause davor)? Dann fällt auch ein
   * mehrdeutiges Wort wie „Weiter" aus dem Text.
   * 26.09.2026 abends: `null` (Knopf, Stille) — dann fällt gar nichts weg:
   * per Knopf geht „Gut. Weiter." unverändert hinaus.
   * 26.09.2026 spät: die Angabe geht an den Server; ER nimmt das Wort aus
   * seinem Text — Blase, Modell und Verlauf sind derselbe Text.
   */
  endwort: EndwortAngabe | null = null,
  /**
   * 29.09.2026 (Ticket 2780): endete das Diktat nach Stille? Dann darf der
   * Server es per „Verwerfen"/„Stopp" am Ende SEINER Erkennung verwerfen
   * (lib/audio-feedback.ts, darfAmServerVerwerfen) — nach einem Druck, in
   * Freihand und nach dem Wach-Wort nie.
   */
  vonSelbst = false,
  /**
   * 30.09.2026 (Ticket 2624): „wachwort“, wenn das Wach-Wort das Mikro dieser
   * Aufnahme geöffnet hat — dann gilt am Server „Hey Vespera nur mit meiner
   * Stimme“ auch hier. Sonst `undefined`; „Erneut senden“ trägt es nicht.
   */
  ausloeser?: "wachwort",
  /**
   * 12.10.2026 (Bahn live-primaer): der Stand der Live-Mitschrift beim Absenden (LiveDictation.stand). Der
   * Server prüft damit, ob der Live-Text allein reichen darf; ohne ihn (Erneut senden, Gerätetext, ältere
   * Hülle) erkennt er die Tondatei wie immer.
   */
  liveStand: LiveStand | null = null,
  /**
   * 12.10.2026 (Bahn „fortsetzung-anhaengen“): die Kennung dieses Zuges (sie wird die `vorherId` der nächsten Aufnahme, wenn dieser Zug
   * durch Stille endete) und — war DIESE Aufnahme die Fortsetzung einer vorigen — deren Angabe. Ohne: wie bisher (nur „Erneut senden“,
   * der Satz nach dem Wach-Wort).
   */
  fort?: { kennung: string; fortsetzung: FortsetzungAngabe | null },
  /** 21.10.2026: die Bremse der Karte hielt bei offener LIVE-Mitschrift an — der Server prüft den Endtext (pttFinishAndSend). */
  liveOffen = false,
): Promise<void> {
  /*
   * 24.09.2026 (Ticket 2202): Wie lange wartet man nach dem Loslassen auf
   * den eigenen Text? EINE Zeile je Druck, genau einmal — mit Text, leer
   * („nichts verstanden") oder Fehler. `live=` sagt, ob die Mitschrift
   * mitging (nativ nie). Erkannt wird auf beiden Wegen die Tondatei; der
   * Live-Text ist beim Server nur der Rückfall (voice-command.ts).
   * „Genau einmal" trägt der Druck selbst (ptt-weg.ts, TextMessung).
   */
  const meldeTextWartezeit = (ergebnis: "text" | "leer" | "fehler"): void => {
    const zeile = textMessungAbschliessen(messung, {
      ergebnis,
      live: liveText.trim() !== "",
      jetzt: performance.now(),
    });
    if (zeile) meldeDiagnose(zeile);
  };
  // 2026-07-24 (Martin): leere Aufnahme (Mikro hat nichts eingefangen, z.B.
  // weil gerade eine Vorhoerprobe lief) gar nicht erst senden — sonst haengt
  // eine "wird transkribiert"-Blase. Schwelle = server MIN_AUDIO_BYTES (512).
  if (blob.size < LEERE_AUFNAHME_BYTES) {
    meldeDiagnose(
      `[ptt-ende] zu-klein bytes=${blob.size}${messung?.weg ? " " + messung.weg : ""}`,
    );
    pttCloseModal();
    pttState = "idle";
    pttResetButton();
    appendBubble(
      "system",
      "Ich habe nichts gehört — die Aufnahme war leer. Halt die Taste etwas " +
        "länger gedrückt und sprich nach dem Ton.",
    );
    return;
  }
  // Pre.483: SOFORT User-Placeholder + leere Bot-Bubble vorbereiten —
  // Server-Stream füllt beide inkrementell.
  const placeholder = document.createElement("div");
  placeholder.className = "bubble user is-placeholder";
  placeholder.textContent = "Sprachnachricht wird transkribiert …";
  // Der Anker DIESES Zuges — er ordnet die Blase ein und haelt zugleich die
  // Werkzeug-Schritte zusammen, die gleich folgen. Siehe eroeffneZug.
  const zugTs = Date.now();
  const schliesseZug = eroeffneZug(zugTs);
  insertOrdered(placeholder, zugTs);
  scrollChatToBottomReliably();

  // Bot-Bubble lazy erzeugen (erst wenn erster Text-Chunk kommt)
  let botBubble: HTMLElement | null = null;
  // #43: Zwischenhinweis nur EINMAL pro Anfrage sprechen.
  let spokeToolHint = false;
  // 2026-07-10 (Martin): gesprochener Zwischenhinweis NUR bei echter Wartezeit.
  let sawToolResult = false;
  let toolHintTimer: number | null = null;
  let pttWorkingEl: HTMLElement | null = null;
  // Beim Start gelesen: die Wahl ist beim Transkript womöglich schon verbraucht (lib/ptt.ts `verbraucht`).
  const pttWartName = wartenderName(empfaengerWahl, empfaengerWahl.anFeld(), currentBotName);
  const clearPttWorking = (): void => {
    entferneWarteblase(pttWorkingEl);
    pttWorkingEl = null;
  };
  // 2026-07-24 (Martin): verzoegerter Werkzeug-Hinweis — nur zeigen, wenn
  // das Werkzeug wirklich dauert; sonst kein Fueller.
  let hintTimer = 0;
  const clearHintTimer = (): void => {
    if (hintTimer) {
      window.clearTimeout(hintTimer);
      hintTimer = 0;
    }
  };
  // Ticket 2257: kurzer Warte-TON (kein Satz) fuer eine Werkzeug-Pause, die
  // lange genug dauert, dass sie wie Stillstand wirkt — siehe WERKZEUG_TON_MS.
  // Einmal pro Zug, wie beim gesprochenen Hinweis oben.
  let spokeWaitTone = false;
  let waitToneTimer = 0;
  const clearWaitToneTimer = (): void => {
    if (waitToneTimer) {
      window.clearTimeout(waitToneTimer);
      waitToneTimer = 0;
    }
  };
  // 2026-07-03 (Martin): „arbeitet"-Puls zeigen (idempotent) — sichtbar
  // machen, dass der Prozess laeuft, auch zwischen Tool-Schritten.
  const ensurePttWorking = (label?: string): void => {
    if (!pttWorkingEl) {
      pttWorkingEl = buildPendingBubble(label, pttWartName);
      insertOrdered(pttWorkingEl, Date.now());
      fuehreChatNach();
    } else if (label) {
      setzeWartetext(pttWorkingEl, label);
    }
  };
  let botText = "";
  /*
   * 06.10.2026 (Ticket 2738): bis hierher steht in botText nur, was Vespera selbst
   * vorangestellt hat (Warte-Brücke, Vorantwort; Ereignis `vorsatz`). Die Meldung nach
   * dreißig Sekunden fragt nach der ANTWORT — ein „Einen Moment.“ ist keine.
   */
  let vorsatzBis = 0;
  /* Zeiger in die SPRECHQUELLE — siehe sprechNachschub. */
  let gesprochenBis = 0;
  const collectedToolCards: PttToolCall[] = [];
  /* Ticket 2444: Ergebnisse dieses Zugs fuer die Abschluss-Karte. */
  const zugErgebnisse: Ergebnis[] = [];
  let zugWerkzeuge = 0;
  const mailZug = beginneMailZug();
  // #117: Merkmal ob onTranscript mit echtem Text aufgerufen wurde. Wenn ja,
  // darf onDone(false) den Placeholder NICHT mehr entfernen — das Transkript
  // ist bereits im Chat sichtbar. Vorher verschwand der STT-Text immer dann,
  // wenn der anschließende LLM-Call scheiterte (Quota, Netz, …).
  let transcriptShown = false;
  // Ticket 3085: die Auskunft „Text gespeichert, nicht noch einmal sprechen“ (onGespeichert), leer = der Server hat nichts abgelegt.
  let gespeichertSatz = "";
  // 12.10.2026: der lokale Verlaufseintrag der Nachricht dieses Zuges — er trägt das Merkmal „Fortsetzung“, sobald der Server es bestätigt.
  let verlaufNutzer: PersistedEntry | null = null;
  /*
   * 12.10.2026 (Fortsetzung, Prüfung): ABGELÖST heißt: ein neuerer Zug hat die Zügel (`pttSendSeq` ist weitergezählt — die Fortsetzung hat den
   * Knopf freigegeben, eine neue Aufnahme läuft oder wurde gesendet). Spätes Eintreffendes dieses Zuges (Text, Werkzeugschritte, `done`) darf
   * nichts mehr hörbar oder sichtbar neu beginnen: sonst spräche die alte Antwort in die neue Aufnahme. Was der Server schon abgelegt hat,
   * holt der Verlaufsabgleich.
   */
  const abgeloest = (): boolean => seq !== pttSendSeq;

  // TTS-Queue: parallel TTS-fetches, sequentielles play. Spart 1-2s
  // gefühlte Wartezeit weil erster Satz schon spielt während LLM noch tippt.
  const settings = loadSettings();
  // #305: EINE answerId je gesprochener Antwort -> Server pinnt Provider.
  const ttsAnswerId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : String(Date.now()) + Math.random().toString(36).slice(2);
  const ttsQueue = new TtsQueue({
    fetchTts: (text, signal) =>
      fetchTtsChunk(text, settings.voiceId, signal, ttsAnswerId),
    direktBeta: () => loadSettings().ttsDirektBeta === true,
    // Pre.1111: waehrend die PTT-Antwort gesprochen wird, die klickbare
    // Stop-Pille zeigen — so kann auch eine lange Sprach-Antwort unterbrochen
    // werden (Pille -> stopCurrentTts -> currentTtsQueue.cancel()).
    onAudioStart: () => {
      currentTtsQueue = ttsQueue;
      setVoicePrep("playing");
    },
  });
  // 2026-07-03 (Martin): currentTtsQueue SOFORT registrieren — sonst zielt
  // ein Ton-Aus VOR dem Audio-Start (oder im Satz-Uebergang) ins Leere und
  // die Antwort spricht weiter. Das finally unten raeumt es wieder auf.
  verdrahteLangeSpurHinweise(ttsQueue);
  currentTtsQueue = ttsQueue;
  activeTtsQueues.add(ttsQueue);
  // 23.09.2026 (Ticket 2551): ab jetzt hat diese Antwort Vorrang vor
  // automatischem Vorlesen — auch solange sie noch auf ihren Text wartet.
  sprechtastenQueue = ttsQueue;

  try {
  /*
   * ⚠️ Nachprüfung 01.10.2026 (Ticket 2634): Die Rückrufe dieses Zugs schließen nur DIE Karte, die ihn
   * gesendet hat. Wurde sie ausgeblendet und hat Martin inzwischen eine neue Aufnahme begonnen, stünde
   * sonst die späte Antwort des alten Zugs (onDone, onAusVerlauf) und schlösse die NEUE Karte.
   */
  const eigeneKarte = pttModal;
  const schliesseEigeneKarte = (): void => {
    if (pttModal === null || pttModal === eigeneKarte) pttCloseModal();
  };
    await streamPtt(blob, mimeType, {
      // 26.09.2026: „gesendet" erst, wenn die Aufnahme beim Server ist.
      onAngenommen: quittung,
      /*
       * 30.09.2026 (Ticket 2634): Der Strom ist verstummt (Rückkehr aus dem
       * Hintergrund, 60 s Stille), die Antwort steht aber schon im Verlauf des
       * Servers (ptt.ts → lib/ptt-haenger.ts). Die Karte geht zu, der Verlauf
       * wird geholt — dieselbe Nachführung wie nach jeder Rückkehr; eine
       * angefangene Bot-Blase hebt sie auf die ganze Fassung. NICHTS wird neu
       * gesendet. Danach kommt `onDone(true, "")`.
       *
       * ⚠️ „wird transkribiert …“ nur dann wegnehmen, wenn die Mitschrift nie
       *    ankam: kam sie, IST der Platzhalter die Blase der Nachricht.
       */
      onAusVerlauf: () => {
        clearHintTimer();
        clearWaitToneTimer();
        clearPttWorking();
        if (!transcriptShown) placeholder.remove();
        schliesseEigeneKarte();
        void hydrateFromServer();
      },
      onTranscript: (rohesTranskript, audioUrl, info) => {
        /*
         * ⚠️ 20.09.2026 (Ticket 2420) — DAS AUSLOESEWORT GEHOERT NICHT IN
         *    DIE NACHRICHT. Punkt 4 des Tickets.
         *
         * 26.09.2026 spät (dritte Runde): Bis pre.3347 fiel es nur HIER weg
         * — aus der Blase. Der Server gab dem Modell und dem Verlauf
         * weiter „…, senden." Jetzt nimmt der Server es aus SEINEM Text
         * (`endwort` reist mit, shared/sende-wort.ts
         * ohneAusgeloestesEndwort), und die Blase zeigt, was er schickt.
         * Nur ein Server ohne diese Angabe (vor dem Neustart) lässt es
         * hier tun — mit derselben Funktion.
         */
        const transcript = info?.endwortGeprueft
          ? rohesTranskript
          : ohneAusgeloestesEndwort(rohesTranskript, loadSettings().sendeWort ?? "", endwort);
        meldeTextWartezeit(rohesTranskript.trim() ? "text" : "leer");
        // Placeholder → echtes Transkript
        if (transcript.trim()) {
          transcriptShown = true;
          zugNutzerFrage = transcript.trim();
          appendUserAudioBubble(transcript.trim(), audioUrl, placeholder);
          // 2026-07-02 (Martin): sichtbarer Arbeits-Puls — bei PTT war nach
          // dem Transkript NICHTS zu sehen, bis der erste Text kam.
          if (!abgeloest()) ensurePttWorking();
          // 12.10.2026: der Eintrag bleibt in der Hand — das Etikett der Fortsetzung kommt erst mit dem Ja des Servers (onFortsetzung).
          verlaufNutzer = { kind: "user", text: transcript.trim(), ts: Date.now() };
          pushHistory(verlaufNutzer);
        } else {
          placeholder.remove();
        }
        schliesseEigeneKarte();
        // 2026-07-14 (#316 Fix A): Stille-Waechter ab TURN-BEGINN statt
        // erst beim ersten Tool-Call — auf der Warm-CLI-Spur vergingen
        // real 8-34s bis zum ersten Ton. Nach ~4s ohne Antwort-Text
        // EIN kurzer gesprochener Hinweis (einmal pro Turn).
        /*
         * ⚠️ 2026-09-03 (Martin: *„keine Zwischenmeldungen mehr, ausser nach
         * dreissig Sekunden vielleicht ... und dann bitte individualisieren,
         * dass es nicht immer das Gleiche ist."*)
         *
         * Hier standen DREI feste Saetze, zufaellig gewaehlt — „Kleinen
         * Augenblick noch" war einer davon. Genau der Satz, den er zitiert.
         *
         * Eine Zufallsauswahl aus drei Saetzen ist keine Individualisierung:
         * sie sagt jedes Mal dasselbe, nur in anderer Reihenfolge, und faellt
         * nach dem dritten Mal auf. Vor allem aber weiss sie NICHTS — sie
         * entsteht, bevor irgendetwas geschehen ist.
         *
         * Die Staffel unten (`sprichToolHint`) sagt statt dessen, WAS laeuft,
         * und steigert sich, wenn es dauert. Sie braucht aber ein laufendes
         * Werkzeug. Solange keines da ist, gibt es nichts zu melden — und
         * dann ist Stille ehrlicher als ein Fuellsatz.
         *
         * Fuer den einen Fall, den er offengelassen hat („nach dreissig
         * Sekunden vielleicht"): erst nach `ZWISCHENMELDUNG_MS`, und nur
         * wenn wirklich noch KEIN Werkzeug gemeldet hat — dann eine einzige
         * Zeile aus derselben Staffel, damit sie sich nicht wiederholt.
         */
        if (autoPlayEnabled && !spokeToolHint && toolHintTimer === null) {
          toolHintTimer = window.setTimeout(() => {
            toolHintTimer = null;
            if (spokeToolHint || botText.slice(vorsatzBis).trim()) return;
            if (pttState !== "sending" || seq !== pttSendSeq) return;
            spokeToolHint = true;
            sprichToolHint("Ich bin noch dran", (t) => ttsQueue.push(t));
          }, ZWISCHENMELDUNG_MS);
        }
      },
      onTextChunk: (chunk) => {
        clearHintTimer();
        clearWaitToneTimer();
        clearPttWorking();
        if (abgeloest()) return;
        botText += chunk;
        if (!botBubble) {
          botBubble = document.createElement("div");
          botBubble.className = "bubble bot has-avatar";
          insertOrdered(botBubble, Date.now());
          // Ticket 2730: liest er weiter oben, sagt der Knopf, dass unten
          // eine Antwort beginnt — statt ihn hinunterzuziehen.
          notifyNewMessage();
          fortsetzungVorigeBlase = botBubble;
        }
        // Ticket 2175: dieselbe Marke wie im Chat — das erste Wort.
        zugErstesWort();
        // Inkrementell den Text rendern. fillBotBubble macht Markdown +
        // setzt Avatar — wir rufen es bei jedem Chunk, das ist ok für
        // moderate Längen.
        fillBotBubble(botBubble, botText, "strom");
        // Ticket 2730: hier stand ein bedingungsloser Sprung ans Ende — bei
        // JEDEM Stück. Wer waehrend der Antwort nachlas, wurde zurueckgerissen.
        fuehreChatNach();
        // An TTS-Queue weitergeben — schneidet selbst an Satzgrenzen.
        // 2026-07-01 (Martin): NUR wenn Vorlesen an ist — sonst sprach die
        // PTT-Antwort trotz "Ton aus" weiter (eigene Queue, ungated).
        // 2026-08-30 (Martin: "die Ausgabe soll nicht immer das sein, was
        // gelesen wird"): Traegt die Antwort eine Sprechfassung, wird NUR
        // die gesprochen — die Tabelle bleibt zum Ansehen. Ohne Marke gilt
        // unveraendert das Verhalten von vorher.
        if (autoPlayEnabled) {
          const nach = sprechNachschub(botText, gesprochenBis);
          gesprochenBis = nach.neu;
          if (nach.text) ttsQueue.push(nach.text);
        }
      },
      onVorsatz: () => {
        /*
         * 06.10.2026 (Ticket 2738, Prüfbefund zur Warte-Brücke): der Satz davor war
         * Vesperas eigener Vorsatz („Einen Moment.“). Die Antwort beginnt neu — mit
         * dem schnellen ersten Stück —, der Arbeits-Puls kommt zurück (der Text hatte
         * ihn weggenommen, das Modell arbeitet aber noch), und die Meldung nach
         * dreißig Sekunden zählt den Satz nicht als Antwort.
         */
        if (abgeloest()) return;
        if (autoPlayEnabled) ttsQueue.neuerAnfang();
        vorsatzBis = botText.length;
        ensurePttWorking();
      },
      onFlush: () => {
        if (abgeloest()) return;
        // 2026-07-13 (Martin): Vor einem Tool-Aufruf den schon gestreamten,
        // aber noch nicht gesprochenen Satz zu Ende sprechen (sonst wird er
        // waehrend des Tool-Laufs verschluckt).
        if (autoPlayEnabled) ttsQueue.flushPending();
        // Ticket 2257: ab hier kann die Stille beginnen (Werkzeug laeuft
        // gleich) — wenn sie WERKZEUG_TON_MS ueberdauert, kurz Bescheid geben.
        clearWaitToneTimer();
        if (autoPlayEnabled && !spokeWaitTone) {
          waitToneTimer = window.setTimeout(() => {
            waitToneTimer = 0;
            if (spokeWaitTone) return;
            if (pttState !== "sending" || seq !== pttSendSeq) return;
            if (ttsQueue.isAudioActive()) return;
            spokeWaitTone = true;
            playWerkzeugWarteTon();
          }, WERKZEUG_TON_MS);
        }
      },
      onToolCall: (tool, callId, input) => {
        if (abgeloest()) return;
        /* Ticket 2701: der Abschied ist kein Arbeitsschritt (abschluss.ts). */
        if (zaehltAlsArbeit(tool)) zugWerkzeuge++;
        // Ticket 2257 Teil a: Sicherheitsnetz. onFlush (oben) deckt nur den
        // Anthropic-Streaming-Pfad ab — der CLI-Bridge-Pfad im Server sendet
        // vor tool_call kein "flush"-Ereignis. flushPending() ist idempotent
        // (leerer Puffer = no-op), darum kostet der zusaetzliche Aufruf hier
        // nichts, verhindert aber verschluckte Saetze auf dem anderen Pfad.
        if (autoPlayEnabled) ttsQueue.flushPending();
        // 2026-07-03 (Martin): Wort-Feedback — sag, was gerade laeuft.
        // 2026-07-24 (Martin): ERST nach 1.2s zeigen/sprechen — bei
        // schnellen Antworten war der Hinweis nur Rauschen. onTextChunk /
        // onToolResult / onDone raeumen den Timer, dann entfaellt er.
        const hintPhrase = pttProgressPhrase(tool, input);
        clearHintTimer();
        hintTimer = window.setTimeout(() => {
          hintTimer = 0;
          ensurePttWorking(hintPhrase);
          // dieselbe Phrase HOERBAR machen — nur in Stille, nie in
          // laufende Ausgabe, und gedrosselt.
          if (autoPlayEnabled && !ttsQueue.isAudioActive()) {
            sprichToolHint(hintPhrase, (t) => ttsQueue.push(t));
          }
        }, ZWISCHENMELDUNG_MS);
        console.log("[ptt-stream] tool_call:", tool, callId, input);
      },
      onToolResult: (callId, name, result, isError, anzeige) => {
        // Ticket 3074: das Ergebnis löst die Stufenkarte der Musik ab, auch wenn „fertig“ nie ankam.
        if (name === "generate_music") closeToolProgressBubble(name);
        if (!isError) {
          const ergebnis = ergebnisAus(name, result);
          if (ergebnis) zugErgebnisse.push(ergebnis);
        }
        clearHintTimer();
        sawToolResult = true;
        const tc: PttToolCall = {
          tool: name,
          callId,
          input: {},
          result,
          isError,
        };
        collectedToolCards.push(tc);
        dispatchToolCard({ tool: name, callId, isError, result, anzeige });
        // 20.10.2026 (Lesemodus): erscheint die Mail-Karte erst mitten im Zug, bleibt keine halb gesprochene Antwort liegen.
        merkeMailAntwort(mailZug, name, abgeloest(), () => ttsQueue.cancel("lesemodus"));
        // 2026-07-03 (Martin): nach dem Tool-Ergebnis denkt der Bot weiter —
        // Puls neu unter der Karte, mit „verarbeite"-Hinweis.
        clearPttWorking();
        // Die Karte der Handlung bleibt (sie IST geschehen); ein Puls „arbeitet“ gäbe es für eine abgelöste Antwort nicht mehr.
        if (abgeloest()) return;
        // 2026-07-29 (Martin, zum dritten Mal: "Wenn diese komischen Floskeln
        // endlich enden mit dem 'Dauert ein Moment, bitte, Moment, Moment' —
        // dieser Müll"):
        //
        // Hier stand nach JEDEM Werkzeug-Ergebnis "Einen Moment, ich
        // verarbeite das". Der Satz sagt nichts: Dass gearbeitet wird, sieht
        // man am Puls; WAS gearbeitet wird, verrät er nicht. Er war ein
        // Ritual, kein Hinweis — und weil nach einer Werkzeug-Kette mehrere
        // Ergebnisse hintereinander kommen, hörte Martin ihn wieder und
        // wieder.
        //
        // Der Puls allein zeigt jetzt, dass es weitergeht. Etwas GESAGT wird
        // nur noch, wenn es etwas zu sagen gibt — die werkzeugspezifischen
        // Ansagen ("Ich suche Flüge nach Faro", "Ich baue das PDF") bleiben,
        // denn die tragen eine Information.
        ensurePttWorking();
        // 2026-07-19: auch hier hoerbar — faengt die lange Denkphase NACH
        // den Werkzeugen ab (gemessen: 25 s Stille zwischen letztem
        // Ergebnis und erstem Text). Dieselbe Drossel wie beim Start.
        // 2026-07-29: Auch NICHT mehr aussprechen. Ein Satz, der keine
        // Information trägt, ist beim Zuhören schlimmer als Stille — man
        // wartet darauf, dass etwas kommt, und es kommt nichts.
        // Die Durchhalte-Staffel greift weiterhin, wenn eine ECHTE Ansage
        // (mit Objekt) sich wiederholen würde.
      },
      // Ticket 281: Modell live anzeigen, sobald der Pfad feststeht.
      onModel: (m) => updateFooterEffectiveModel(m),
      // Ticket 2790: wartet die Fragen-Runde, hört der Hörer am Vorlese-Ende es.
      // 12.10.2026: eine Fragen-Runde des abgelösten Zuges öffnete das Mikro in die neue Aufnahme.
      onRunde: (wartet) => void (!abgeloest() && merkeRunde(wartet)),
      /*
       * Ticket 3048: Jevs Meldung über das Mikro — gilt nur in der Stufe „Jev entscheidet“.
       * ⚠️ Nur, solange DIESER Zug der laufende ist (dieselbe Prüfung wie beim Schließen in
       *    eroeffneZug): kommt die Meldung eines alten, hängenden Zugs erst nach dem Beginn
       *    des neuen, entschiede sie sonst über dessen Mikro.
       */
      onMikro: (roh) => {
        if (laufenderZug === zugTs) merkeJevMikro(roh);
      },
      onProjectSuggest: (sug) => showProjectSuggestBanner(sug),
      /*
       * 29.09.2026 (Ticket 2780): Der Server hat verworfen — „Verwerfen"
       * oder „Stopp" stand für sich am Ende seiner Erkennung. Wie das
       * gesprochene Verwerfen der Seite: nichts geht hinaus, die Aufnahme
       * liegt eine Minute zum Zurückholen bereit, hörbar und mit Notiz.
       */
      onVerworfen: (v) => {
        meldeTextWartezeit("text");
        placeholder.remove();
        schliesseEigeneKarte();
        if (!abgeloest()) playBeep("cancel");
        pttLegeVerworfenWeg({ ton: blob, hatteTon: true, mime: mimeType, text: v.text, fuerSich: v.fuerSich });
      },
      /*
       * 21.10.2026 (Vorfall 15:44:53): die Bremse am ENDGÜLTIGEN Text des Servers — die Stille sandte, der Satz endet offen
       * („… Und“). Nichts ging hinaus: der Text steht im Eingabefeld, Senden oder Weitersprechen entscheidet Martin.
       */
      onUnvollstaendig: (text) => {
        meldeTextWartezeit("text");
        placeholder.remove();
        schliesseEigeneKarte();
        // Nichts geht verloren — auch ein abgelöster Zug (neue Aufnahme läuft) legt den Text ins Feld, nur still.
        if (text) {
          const vorher = textField.value;
          pttLegeInsTextfeld(text);
          merkeBremseText(vorher);
        }
        if (abgeloest()) return;
        playBeep("cancel");
        showToast(BREMSE_ZURUECK_HINWEIS, "warning", 8000);
      },
      /*
       * 19.10.2026: der Rauschfilter des Servers hat verworfen — nie still. Karte mit dem erkannten Text, „Trotzdem senden“
       * (Martins Tipp = Ja; Empfänger Schmied wie beim Ja-Knopf) und „Verwerfen“; dazu der Hinweis-Ton (abschaltbar).
       */
      onRauschen: (r) => {
        meldeTextWartezeit("text");
        placeholder.remove();
        schliesseEigeneKarte();
        // Ein abgelöster Zug (neuere Aufnahme läuft) zeigt weder Karte noch Ton.
        if (abgeloest()) return;
        zeigeRauschKarte(refs.chat, r, {
          sende: (text) => sendUserMessage(text, undefined, undefined, "schmied"),
          melde: (knopf, zeichen) => meldeSpur("ptt", `rauschen-karte zeichen=${zeichen} knopf=${knopf}`),
          ton: () => playBeep("rueckfrage"),
        });
        fuehreChatNach();
      },
      /*
       * 30.09.2026 (Ticket 2624): „Hey Vespera nur mit meiner Stimme“ — der
       * Server hat den Satz einer fremden Stimme verworfen. Kein Fehler und
       * kein „Erneut senden“ (dieselbe Stimme würde wieder verworfen): die
       * Blase „wird transkribiert“ weicht einer stillen Zeile mit der
       * ehrlichen Auskunft des Servers. Es kommt danach `onDone(true, "")`.
       */
      onFremdeStimme: (hinweis) => {
        meldeTextWartezeit("text");
        placeholder.remove();
        schliesseEigeneKarte();
        if (hinweis) appendBubble("system", hinweis);
      },
      /*
       * 22.10.2026 (Martin, 07:52): Silbensalat aus Geräuschen, am Server verworfen (server/ptt-salat.ts). Kein Text im Chat —
       * die Blase „wird transkribiert“ verschwindet, ein kurzer Hinweis ohne Wort der Rede (kein Ton, kein „Erneut senden“).
       * Prüfauflage 22.10.: ein Fehlgriff muss rettbar sein — die Aufnahme liegt eine Minute in der Verworfen-Ablage, die Notiz
       * „Verworfen · Zurückholen“ erkennt sie neu ins Eingabefeld (derselbe Weg wie das gesprochene „Verwerfen“, ohne Text).
       */
      onSalat: (hinweis) => {
        meldeTextWartezeit("leer");
        placeholder.remove();
        schliesseEigeneKarte();
        meldeSpur("ptt", "salat-verworfen hinweis=toast");
        if (abgeloest()) return;
        if (hinweis) showToast(hinweis, "info", 3500);
        // salat: öffnet kein gesprochenes Befehlsfenster, verdrängt nie ein echtes Verworfenes (lib/verworfen.ts).
        pttLegeVerworfenWeg({ ton: blob, hatteTon: true, mime: mimeType, text: "", fuerSich: false, salat: true });
      },
      /*
       * 01.10.2026 (Ticket 2882): Dieselbe Aufnahme war schon im Zug — der
       * Server beantwortet sie nur einmal. Die Antwort der ersten steht im
       * Verlauf (und kommt über den Kanal). Hier verschwindet nur die Blase
       * „wird transkribiert“, ruhig und ohne „Erneut senden“.
       */
      onDublette: (hinweis) => {
        meldeTextWartezeit("text");
        placeholder.remove();
        schliesseEigeneKarte();
        if (hinweis) appendBubble("system", hinweis);
      },
      /*
       * 11.10.2026 (Ticket 3085): Der Text eines langen Diktats liegt schon am Server, der Zug ist aber gescheitert oder der
       * Strom gerissen. Statt „sag es bitte noch einmal“ (ein zweites Diktat legte eine zweite Zeile an) zeigt `onDone` unten
       * diesen Satz als stille Zeile.
       */
      onFortsetzung: (abgebrochen) => {
        // Der Server hat die Angabe geprüft und angenommen: erst jetzt das Etikett (Blase und lokaler Verlauf).
        setzeFortsetzungsVermerk(placeholder, { fortsetzung: true });
        // Und hat er die Antwort der Vorrunde WIRKLICH abgebrochen, trägt deren Blase sofort den Vermerk (nicht erst nach dem Nachladen).
        if (abgebrochen && fortsetzungVorigeBlase && fortsetzungVorigeBlase !== botBubble) {
          setzeFortsetzungsVermerk(fortsetzungVorigeBlase, { abgebrochen: true });
        }
        if (verlaufNutzer) {
          verlaufNutzer.fortsetzung = true;
          saveHistory(history);
        }
      },
      onGespeichert: (satz) => {
        gespeichertSatz = satz;
      },
      onDone: (ok, finalText, error, effectiveModel, gestoppt) => {
        glockeNeuFragen();
        schliesseOffeneOebbKarte();
        clearHintTimer();
        clearWaitToneTimer();
        clearPttWorking();
        /*
         * Nach dem Zug rotiert der Server die Sitzung, wenn sie zu schwer
         * wurde (pre.2784), und waermt 1,5 s spaeter nach. Erst danach steht
         * der Zustand fest — vorher gefragt zeigte die Anzeige den alten.
         */
        setTimeout(() => void holeWaerme(), 4000);
        if (ok && effectiveModel) updateFooterEffectiveModel(effectiveModel);
        // Ticket 2782: erst jetzt weiß der Server, ob in diesem Zug etwas zu
        // ruhen begann — Grund und „ruht bis“ für Hinweis und Blatt.
        void frageGrund();
        /*
         * 12.10.2026 (Prüfung): das Ende eines ABGELÖSTEN Zuges (die Fortsetzung hat ihn abgebrochen): keine Blase, kein Vorlesen, keine
         * Fehlermeldung und nie „Erneut senden“ (dieselbe Aufnahme ginge ein zweites Mal hinaus). Eine schon gezeichnete Teilantwort bleibt
         * stehen, als abgebrochen gekennzeichnet; alles Weitere liefert der Verlaufsabgleich.
         */
        if (abgeloest()) {
          ttsQueue.cancel();
          schliesseEigeneKarte();
          if (!transcriptShown) placeholder.remove();
          if (botBubble) {
            setzeFortsetzungsVermerk(botBubble, { abgebrochen: true });
            if (botText.trim()) {
              pushHistory({ kind: "bot", text: botText.trim(), ts: blasenStempel(botBubble), abgebrochen: true });
            }
          }
          return;
        }
        if (!ok) {
          schliesseEigeneKarte();
          ttsQueue.cancel();
          // Ticket 3085: das lange Diktat liegt am Server — keine Fehlermeldung, kein „sag es noch einmal“, kein „Erneut senden“.
          if (gespeichertSatz) {
            if (!transcriptShown) {
              placeholder.remove();
              meldeTextWartezeit("fehler");
            }
            appendBubble("system", gespeichertSatz);
            return;
          }
          if (!transcriptShown) {
            placeholder.remove();
            // 2026-07-24 (Martin): War die Aufnahme leer (Mikro hat nichts
            // eingefangen), waere "Erneut senden" mit demselben leeren Blob
            // sinnlos — stattdessen den Server-Hinweis zeigen.
            const errStr = String(error ?? "");
            const leer = PTT_LEER_MUSTER.test(errStr);
            // Ticket 2202: auch ein leeres Ergebnis ist eine Wartezeit — und
            // genau die Zahl, die für den nativen Weg zählt.
            meldeTextWartezeit(leer ? "leer" : "fehler");
            if (leer) {
              if (pttLeerStill(messung)) return;
              // 05.09.2026 (Martin im Auto, Navi im Transkript): Kam die
              // Aufnahme aus der nativen Engine (iPhone-Mikro), nehmen die
              // nächsten Aufnahmen das Headset-/Automikro — näher am Mund.
              if (pttRecorder.warNativ()) {
                nativMeiden(10 * 60_000);
                appendBubble(
                  "system",
                  errStr + " Die nächsten zehn Minuten nehme ich über das Headset- oder Automikro auf.",
                );
              } else {
                appendBubble("system", errStr);
              }
              return;
            }
            /*
             * 30.09.2026 (Ticket 2634): Die Wache hat einen verstummten Strom
             * beendet (ptt.ts, „Verbindung unterbrochen“). Die Aufnahme IST beim
             * Server — seine Kopfzeilen kommen erst nach dem ganzen Upload — und
             * die Antwort stand im Verlauf nicht (sonst käme `onAusVerlauf`).
             * „Erneut senden“ wäre hier falsch: jeder Versand bekommt eine eigene
             * Kennung, der Server erkennt ihn nicht als denselben, und dieselbe
             * Arbeit und Handlung liefen ein zweites Mal. Darum nur die ehrliche
             * Auskunft; was Martin dann tut, entscheidet er.
             */
            if (istAbgerissenerStrom(errStr)) {
              appendBubble("system", UNTERBROCHEN_OHNE_ANTWORT);
              return;
            }
            // #123: Upload kam nie durch (auch nach Auto-Retries) — Aufnahme
            // ist noch komplett im Speicher → „Erneut senden" anbieten statt
            // sie wegzuwerfen. Placeholder weg, das Angebot ersetzt ihn.
            offerPttRetry(blob, mimeType, endwort);
            return;
          }
          // #117: Transkript steht schon im Chat → stehen lassen, nur den
          // Fehler der Antwort-Phase melden.
          // 2026-07-03 (Martin): Netz-/Verbindungsabbruch ist NICHT das Ende —
          // die Antwort wird serverseitig (persistChat -> Bus) nachgeliefert.
          const errStr = String(error ?? "");
          const isDrop =
            /*
             * ⚠️ 2026-08-30 — die DEUTSCHE Formulierung fehlte hier, und das
             * war der ganze Fehler.
             *
             * Martin, mit Foto: *„Die Fehlermeldung, die weiß ich nicht,
             * warum die immer wieder kommt. Ich glaub, das hat damit zu tun,
             * wenn ich wegklicke und der grad noch im Prozess ist [...]
             * sollte ja trotzdem laufen, auch wenn ich aus dem Vespera
             * rausgehe, oder?"*
             *
             * Er hat recht, und im Protokoll steht es: der Zug von 07:51 lief
             * VOLLSTAENDIG durch — 466 kB hochgeladen, 495 Zeichen live
             * erkannt, Antwort mit 503 Zeichen fertig. Kein Serverfehler.
             * Nur der Browser verlor die Leitung.
             *
             * Genau dafuer gibt es den sanften Zweig darunter („wird im
             * Hintergrund fertig geschrieben"). Er kam nie zum Zug, weil
             * dieser Erkenner auf englische Brocken prueft — `abort`,
             * `network`, `connection lost` — waehrend `ptt.ts` den Abbruch
             * als „Verbindung unterbrochen" meldet. Kein einziges der
             * gesuchten Woerter steckt darin.
             *
             * Folge: die haeufigste Ursache landete im Fehler-Zweig, rot, mit
             * „bitte erneut senden" — und ein zweites Senden haette dieselbe
             * Arbeit ein zweites Mal gemacht.
             */
            /load failed|failed to fetch|networkerror|network|http-5\d\d|timeout|timed out|abort|econn|connection (was )?lost|lost|verbindung.*unterbroch|leitung.*(weg|verloren)/i.test(
              errStr,
            );
          appendBubble(
            "system",
            isDrop
              ? "Verbindung war kurz unterbrochen — die Antwort wird im Hintergrund fertig geschrieben und erscheint hier normalerweise von selbst. Falls sie ausbleibt, frag einfach nochmal."
              : `Sprachnachricht-Fehler: ${errStr || "unbekannt"}`,
          );
          return;
        }
        // Falls onTextChunk nie gefeuert hat aber finalText doch was hat
        // (sollte nicht passieren bei korrekt streamendem LLM, aber safety)
        if (!botBubble && finalText.trim()) {
          // Ein Zeitpunkt fuer beide — currentTs() achtet zudem auf den
          // Replay-Override, den Date.now() hier uebergangen haette.
          const ts = currentTs();
          /*
           * 23.09.2026 (Ticket 2551): Die Antwort gehört in die EIGENE
           * Schlange — der Nachschub unten nimmt sie von hier. Das Vorlesen
           * von appendBubble weicht dieser noch offenen Schlange (sonst
           * blieb die Antwort stumm, etwa bei „Wiederholung beantwortet aus
           * dem Register"), darum klingt sie genau einmal.
           */
          botText = finalText.trim();
          appendBubble("bot", finalText.trim(), undefined, gestoppt ? { abgebrochen: true } : undefined);
          pushHistory({
            kind: "bot",
            text: finalText.trim(),
            ts,
            ...(gestoppt ? { abgebrochen: true } : {}),
          });
        } else if (botBubble && botText.trim()) {
          // Ticket 2397: derselbe Stempel wie die Live-Blase, wie im Chat-Weg.
          // 12.10.2026: hat Martin die Antwort gestoppt (Stopp-Knopf), trägt die Blase gleich den Vermerk „abgebrochen“.
          if (gestoppt) setzeFortsetzungsVermerk(botBubble, { abgebrochen: true });
          pushHistory({
            kind: "bot",
            text: botText.trim(),
            ts: blasenStempel(botBubble),
            ...(gestoppt ? { abgebrochen: true } : {}),
          });
        }
        /*
         * 14.10.2026 (Build 110): Antwort auf einen Wach-Wort-Satz (textQuelle "geraet") bei verdeckter App → Mitteilung
         * „Vespera hat geantwortet“. Nicht bei Abbruch; ohne Plugin/älteren Build still (wachwortAntwortMelden wirft nie).
         */
        try {
          if (textQuelle === "geraet" && !gestoppt && (botText.trim() || finalText.trim())) {
            void wachwortAntwortMelden(botText.trim() || finalText.trim(), {
              sichtbar: document.visibilityState === "visible",
              vorschauAn: loadSettings().antwortVorschauSperrbildschirm === true,
            });
          }
        } catch {
          /* Die Mitteilung ist eine Zugabe: nie darf sie das Zugende stören. */
        }
        // Rest in TTS-Queue freigeben (kein Satzende war am Ende).
        // Noch einmal Nachschub: der letzte Absatz nach ::/stimme:: kam
        // oft erst mit dem Schluss der Marke — ohne diesen Schritt blieb
        // er in der Blase und nicht in der Spur.
        /* Ticket 2444: die Summe am Ende — auch im Sprachweg. */
        if (brauchtAbschluss(zugWerkzeuge, zugErgebnisse)) {
          const karte = baueAbschlussKarte(zugErgebnisse);
          if (botBubble) botBubble.insertAdjacentElement("afterend", karte);
          else insertOrdered(karte, Date.now());
        }
        // Lesemodus: erschien im Zug eine Mail-Karte, hat merkeMailAntwort die Schlange geschlossen (cancel) — push und flush
        // sind dann wirkungslos und idle() löst sofort auf; kein Hängen im finally, keine halb gesprochene Antwort.
        if (autoPlayEnabled) {
          const nach = sprechNachschub(botText, gesprochenBis, true);
          gesprochenBis = nach.neu;
          /*
           * Ticket 2232 (13.09.2026): Schlussbilanz je Zug. Bleibt
           * `gesprochenBis` hier spuerbar unter der Laenge der Quelle
           * zurueck, ist das Enqueue-Ende die Ursache; stimmt es ueberein,
           * liegt ein spaeterer Vorlese-Abbruch an der Wiedergabe
           * (tts-queue.ts), nicht am Nachschub.
           */
          meldeDiagnose(
            `[sprech-d] zug-ende bisher=${gesprochenBis} botText=${botText.length}`,
          );
          if (nach.text) ttsQueue.push(nach.text);
          ttsQueue.flush();
          // Ticket 2232: die Wiedergabe-Bilanz kommt erst, wenn wirklich
          // alles gespielt ist — deshalb nicht hier, sondern nach idle().
          // Bewusst ohne await: der Zug soll darauf nicht warten.
          void ttsQueue
            .idle()
            .then(() => meldeSprechBilanz(ttsQueue, "ptt"))
            .catch(() => undefined);
        } else if (sprechtastenQueue === ttsQueue) {
          /*
           * 23.09.2026 (Ticket 2551): Ohne Lautsprecher wird diese Schlange
           * nie geflusht und spricht nie — ihr Vorrang endet hier. Sonst
           * hielte er, bis irgendwer stopCurrentTts ruft, und jedes spätere
           * Vorlesen bliebe still (das finally unten wartet dann vergeblich
           * auf idle()).
           */
          sprechtastenQueue = null;
        }
        // Ticket 2698: ein vorgemerkter Player startet nach diesem Vorlesen.
        // Ohne Lautsprecher endet idle() nie — dann gibt es nichts abzuwarten.
        abspielenNachStimme(autoPlayEnabled ? ttsQueue.idle() : null);
      },
    }, liveText, textQuelle, {
      endwort,
      vonSelbst,
      ausloeser,
      liveStand,
      ...(fort ? { anfrageKennung: fort.kennung } : {}),
      ...(fort?.fortsetzung ? { fortsetzung: fort.fortsetzung } : {}),
      ...(liveOffen ? { liveOffen: true } : {}),
    });
    // 2026-07-05 (Martin): Knopf gehoert zur ANTWORT, nicht zum Audio —
    // sofort freigeben, sobald der Stream fertig ist. Haengt ein Audio-
    // Element (iOS nach Session-Stoerung: weder "ended" noch "error"),
    // blieb der Knopf sonst fuer immer grau, obwohl der Text laengst da war.
    //
    // 2026-08-23 (Martin: "Wenn ich zweimal auf den Record-Button druecke,
    // loescht er die alte Aufnahme und faengt von null an"): DIESE Zeile war
    // die Wurzel. Sie gehoert zum ALTEN Zug, feuert aber erst, wenn dessen
    // Stream endet — und da laeuft laengst die naechste Aufnahme. Sie stellte
    // den Zustand auf 'idle' zurueck, waehrend der Recorder lief; der
    // naechste Druck sah 'idle' und begann von vorn statt abzusenden.
    //
    // Im Protokoll vom 23.08.: 12:51:22 alter Zug fertig, 12:51:24 neue
    // Aufnahme, 12:51:33 zweiter Druck -> Neustart, neun Sekunden verloren.
    //
    // Derselbe Fehler wurde am 07.07. (Ticket 271) elf Zeilen tiefer im
    // finally-Block bereits behoben — diese Stelle blieb uebrig. Gleiche
    // Loesung, gleiche Bedingung: Nur der NEUESTE Zug darf den Zustand
    // anfassen.
    // 23.09.2026 (Ticket 2502): …und nicht, wenn schon die nächste Aufnahme
    // läuft — siehe naechsteAufnahmeLaeuft().
    // 12.10.2026: der Strom dieser Antwort ist zu Ende — eine Fortsetzung muss sie nicht mehr abbrechen.
    if (fort) fortsetzungSpeicher.antwortEnde(fort.kennung);
    if (seq === pttSendSeq && !naechsteAufnahmeLaeuft()) {
      pttState = "idle";
      pttResetButton();
    }
    // Auf Audio-Ende warten (best-effort — kein Timeout, User kann
    // dazwischen einen neuen PTT-Tap machen, das canceled die Queue)
    await ttsQueue.idle();
  } finally {
    schliesseZug();
    beendeMailZug(mailZug);
    clearWaitToneTimer();
    // 12.10.2026: auch bei einem Fehler ist der Strom dieser Antwort vorbei.
    if (fort) fortsetzungSpeicher.antwortEnde(fort.kennung);
    // 2026-07-07 (Ticket 271): nur der NEUESTE Turn darf Zustand/Watchdog
    // anfassen — ein alter Turn (haengendes Audio-Ende) clobberte sonst den
    // gerade laufenden und resumte den Alltag-Recorder MITTEN im neuen PTT.
    if (seq === pttSendSeq) {
      window.clearTimeout(pttWatchdogTimer);
      setVoicePrep("hidden");
      // 23.09.2026 (Ticket 2502): Das Ende der Wiedergabe ist genau der
      // Moment, in dem das Mikro nach einer Frage von selbst aufgeht. Den
      // Zustand der NEUEN Aufnahme fasst dieser alte Zug nicht an.
      if (!naechsteAufnahmeLaeuft()) {
        pttState = "idle";
        pttResetButton();
      }
    }
    // 2026-07-09 (Audit Wurzel #4): Resume des Alltag-Recorders leicht
    // verzoegern — er racte sonst mit der frischen Mikro-Vorwaermung und
    // killte den warmen Track. Im Freihand-Modus GAR NICHT resumen (der
    // naechste Zuhoer-Zyklus braucht das Mikro gleich wieder; das Resume
    // macht erst das Ausschalten des Freihand-Modus).
    window.setTimeout(() => {
      if (!freihandActive) void resumeRecorderAfterPttIfNeeded();
    }, 1500);
    // Zwischenhinweis-Timer aufraeumen — nie nach dem Turn sprechen.
    if (toolHintTimer !== null) {
      window.clearTimeout(toolHintTimer);
      toolHintTimer = null;
    }
    // Freihand: nach der Antwort (ttsQueue.idle oben abgewartet) weiterhoeren.
    freihandRelisten();
    activeTtsQueues.delete(ttsQueue);
    if (currentTtsQueue === ttsQueue) currentTtsQueue = null;
    // Ticket 2551: nur die eigene Schlange freigeben — ein neuerer Zug hat
    // seine schon eingetragen.
    if (sprechtastenQueue === ttsQueue) sprechtastenQueue = null;
  }
}

/**
 * User-Bubble mit Transkript-Text. Audio-Player wurde Pre.477 entfernt —
 * User-Beschwerde: bei PTT braucht man die Audio-Datei nicht zum Nachhören,
 * Text genügt (ChatGPT-Verhalten). Audio-URL wird trotzdem persistiert,
 * sodass man sie über das Bubble-Action-Menü teilen kann falls gewünscht.
 *
 * Optional: existing-node update (statt neu insert) — wenn ein Placeholder
 * schon im Chat hängt (Pre.477 Live-Placeholder beim Stop), wird der
 * Inhalt darin ersetzt statt eine zweite Bubble einzufügen.
 */
function appendUserAudioBubble(
  transcript: string,
  audioUrl: string | null,
  existingNode?: HTMLElement | null,
): void {
  if (existingNode) {
    existingNode.classList.remove("is-placeholder");
    existingNode.textContent = transcript;
    if (audioUrl) {
      existingNode.dataset.audioUrl = audioUrl;
    }
    return;
  }
  const node = document.createElement("div");
  node.className = "bubble user";
  if (audioUrl) {
    node.dataset.audioUrl = audioUrl;
  }
  node.textContent = transcript;
  insertOrdered(node, Date.now());
  scrollChatToBottomReliably();
}

// Pre.480: PTT Tap-Toggle mit Modal — ChatGPT-Style. Hold-To-Record raus.
// User-Beschwerde 2026-05-25: "weiß nicht ob halten oder tappen, Vibrate
// kommt nicht durch, keine visuelle Rückmeldung". Tap ist eindeutig:
// 1× Tap → Modal auf + Recording an. Stop-Button im Modal → senden.
// X im Modal oder ESC → abbrechen.
refs.pttBtn.hidden = false;

let pttModal: PttModalHandle | null = null;

// Pre.934 (Martin): Push-to-Talk HALTEN-vs-TIPPEN — wie Claude/WhatsApp.
//  • Gedrückt HALTEN → nimmt auf, solange gehalten; Loslassen sendet.
//  • Kurz TIPPEN     → Aufnahme „rastet ein" (läuft weiter); nächster Tipp sendet.
//  • HOCHWISCHEN → verriegeln (Freihand); LINKS wischen → Abbrechen.
// Pointer-Capture sorgt dafür, dass pointerup/-move auch ankommen, wenn der
// Finger vom Knopf rutscht. Race: lässt der User VOR dem (async) Aufnahmestart
// los, entscheidet der .then-Callback (pttReleased).
/**
 * 2026-07-28 (Ticket 771): Grenze zwischen "kurz getippt" und "bewusst
 * gehalten". Martins Vorschlag waren zwei Sekunden — das waere zu lang: Wer
 * "Wie spaet ist es?" in anderthalb Sekunden sagt und loslaesst, erwartet
 * Senden und stuende sonst mit einer laufenden Aufnahme da. Eine halbe
 * Sekunde trennt zuverlaessig: darunter ist es erkennbar ein Tipp, darueber
 * ein Halten. Dieselbe Groessenordnung nutzen WhatsApp, Signal und Telegram.
 */
const PTT_TIPP_MS = 500;
const PTT_CANCEL_DY = 100;
// 2026-07-04 (Martin, Telegram-Verhalten): LINKS wischen bricht ab,
// HOCHWISCHEN verriegelt (Freihand).
const PTT_CANCEL_DX = 110;
let pttPressStart = 0;
let pttPressY = 0;
let pttPressX = 0;
let pttLastY = 0;
let pttLastX = 0;
let pttReleased = false;
let pttReleaseHeld = 0;
/**
 * Wie spät das Loslassen im Handler ankam: performance.now() minus
 * `ev.timeStamp` (23.09.2026, Ticket 2494). Nur für die Messzeile
 * `[ptt-einrasten]` — sie soll zeigen, ob der Finger wirklich so lange lag
 * oder ob das pointerup während des Kaltstarts verspätet zugestellt wurde.
 */
let pttReleaseVerzug = 0;
let pttLocked = false;
// Nach dem Verriegelungs-Wisch darf das folgende Loslassen NICHT senden.
let pttLockReleasePending = false;
// 2026-07-07 (Ticket 278): frischer Druck im eingerasteten Modus "armed" das
// Absenden — der Push-Knopf mutiert sichtbar zum Senden-Knopf.
let pttLockSendArmed = false;
let pttCancelIntent = false;
/** Wann die Aufnahme zuletzt einrastete (performance.now). Ticket 2494. */
let pttLockedAt = 0;
/**
 * So lange nach dem Einrasten sendet ein Druck NICHT. (23.09.2026, Ticket 2494)
 *
 * Martin: *„Beim Klick auf Push-to-Talk geht die Aufnahme kurz an und sofort
 * wieder weg."* Vier Fälle im Protokoll, alle `anlass=loslassen gesperrt=ja`
 * mit der Haltezeit des ERSTEN Drucks: 09:14:07 (Aufnahme 67 ms, zweiter Druck
 * rund 57 ms nach dem Einrasten), 11:10:20 (44 ms), 12:08:47 (387 ms), 12:53:44
 * (35 ms). Ein zweites Paar aus Druck und Loslassen kam 15–330 ms nach dem
 * Einrasten und schickte ab — schneller, als jemand abschickt, der erst noch
 * sprechen will. Woher es kommt (doppeltes Ereignis von iOS oder ein
 * Nachtippen), sagt die `[ptt-prell]`-Zeile beim nächsten Mal.
 */
const PTT_PRELL_MS = 700;

// 2026-07-07 (Ticket 270): PTT per iPhone-Aktionstaste — der native App
// Intent oeffnet die App mit pawbot://ptt, wir starten die Aufnahme DIREKT
// im eingerasteten Freihand-Modus (wie Hochwischen). Erneuter Druck auf den
// (mutierten) Senden-Knopf beendet + sendet.
// 2026-07-22 (Martin, "verschiebt sich eigenartig"): Push->Senden-Mutation
// zentral — Klasse UND Label wechseln zusammen, das Dock-Layout steht still.
function setPttDockSend(active: boolean): void {
  refs.pttBtn.classList.toggle("is-lock-send", active);
  const lbl = refs.pttBtn.querySelector(".dock-btn-label");
  if (lbl) lbl.textContent = active ? "Senden" : "Push";
  updateTextInputState();
}

/**
 * Wer hat das Mikrofon angefordert? (10.09.2026, Ticket 2202)
 *
 * ⚠️ Drei Wege fuehren in dieselbe Aufnahme und schreiben dieselbe
 *    Telemetrie-Zeile: der Knopf, die Aktionstaste des Telefons (Deep-Link)
 *    und der Freihand-Modus. In der Statistik waren sie bisher nicht zu
 *    trennen — 138 kalte Starts, und niemand konnte sagen, wie viele davon
 *    ueberhaupt einen Fingerdruck hatten. Genau daran ist die Untersuchung
 *    vom 10.09. gescheitert.
 */
export type PttAusloeser =
  | "knopf"
  | "aktionstaste"
  | "freihand"
  /** Ticket 2403/2502: von selbst nach einer Frage — kein Mensch am Knopf. */
  | "nach-frage"
  /** Ticket 2403 (23.09.2026): von selbst nach einer Antwort ohne Frage (seit 07.10. nur, wenn Jev es offen lässt). */
  | "nach-antwort"
  /** Ticket 2503: „Hey Vespera" ist gefallen. */
  | "wachwort"
  /** Ticket 2695: Tipp auf die sprechende Pille („Unterbrechen – ich spreche“). */
  | "pille"
  | "pille-taste";

/**
 * 17.10.2026 (M2): wartete beim letzten Öffnen „nach Frage“ eine Entscheidung (Rückfrage-Karte, Ja-Tor, Runde) oder der Schmied?
 * Vorgabe `true`: ein Weg, der es nicht setzt, bekommt die sichere Regel (kein 400-ms-Ja).
 */
let mikroEntscheidungOffen = true;

/** Die Anlass-Zeile im Fenster — nur fuer Starts ohne eigenen Druck. */
const ANLASS_TEXT: Partial<Record<PttAusloeser, string>> = {
  // 23.09.2026: „Umstellen" statt „Abschalten" — der Knopf hat drei Stufen,
  // ein Tipp führt von „nach Frage" auf „Jev entscheidet", nicht auf aus.
  "nach-frage":
    "Von selbst geöffnet — Vespera hat dich etwas gefragt. Umstellen: das Mikro-Zeichen oben rechts.",
  "nach-antwort":
    "Von selbst geöffnet — nach Vesperas Antwort. Umstellen: das Mikro-Zeichen oben rechts.",
  wachwort: "Von selbst geöffnet — du hast „Hey Vespera“ gesagt.",
  aktionstaste: "Über die Aktionstaste geöffnet.",
};

/**
 * „Von selbst absenden" — Wort und Stille aus den Einstellungen, bei jedem
 * Aufruf frisch gelesen (Ticket 2420). `null`, wenn beides aus ist.
 *
 * ⚠️ 26.09.2026 (Ticket 2672): EINE Lesung für die Karte und das Nachhören —
 *    nachgehört wird nur, wenn es etwas abzuschicken gibt.
 */
function pttSelbstSendenEinstellung(): { wort: string; stilleMs: number; zurueckholen: boolean; satzende: boolean } | null {
  const s = loadSettings();
  const wort = (s.sendeWort ?? "").trim();
  const stille = Number(s.sendeStilleMs ?? 0);
  if (!wort && !stille) return null;
  // Ticket 2674 (26.09.2026 abends): liegt Verworfenes bereit, ist ein
  // Diktat aus nur „zurückholen" ein Befehl — seit der dritten Runde auch
  // kurz danach, damit es dann nicht als Nachricht hinausgeht.
  // 20.10.2026 (Regler „Automatisch absenden“): „Feste Stille“ nur nach abgeschlossenem Satz (lib/stille-wahl.ts).
  return { wort, stilleMs: stille, zurueckholen: pttVerworfen.befehlGilt(), satzende: festNurNachSatz() };
}

/** Die Anlässe, bei denen niemand gerufen hat: das Mikro geht von selbst auf, wenn eine Vorlesung endet. */
const MIKRO_VON_SELBST: ReadonlySet<PttAusloeser> = new Set<PttAusloeser>(["nach-frage", "nach-antwort"]);

/**
 * Nimmt ein ANDERES Gerät des Kontos gerade auf (17.10.2026, lib/seiten-abgleich.ts `zweitesGeraetSperrt`)? Dann bleibt ein
 * Wach-Wort- oder Auto-Mikro-Start zu, und das Journal sagt es. Jeder Weg einer Hand (Knopf, Aktionstaste, Pille) nie.
 */
function zweitesGeraetHaeltZu(via: string): boolean {
  const zeile = zweitesGeraetSperrt(via);
  if (zeile) meldeDiagnose(zeile);
  return zeile !== null;
}

function startPttHandsFree(
  via: PttAusloeser = "freihand",
  /** Ticket 3048: das Nachlausch-Fenster aus mikroNachAntwort (nur von selbst geöffnet). */
  nachlauschMs?: number,
  /** Ticket 2695: klang die Wiedergabe, bevor die Pille sie stoppte? Ohne Angabe liest startPttRecording es selbst. */
  ttsLief?: boolean,
): void {
  if (connected) return;
  // 12.10.2026: die Aktionstaste ist ein Mensch am Knopf — in der Frist der Fortsetzung gibt sie ihn frei (wie der Druck auf den Knopf).
  if (pttState === "sending" && via === "aktionstaste") fortsetzungUebernehmen();
  if (pttState !== "idle") return;
  /*
   * 11.10.2026 (Ticket 2945, Prüfbefund): Von selbst geöffnet heißt: niemand hat gerufen. Arbeitet Martin gerade
   * auf einer Seite (Kontakt-Formular, Rechnung, „Frage des Tages“, Mein Alltag), würde der Sprung in den Chat
   * sie schließen und seine Eingabe wegwerfen. Darum bleibt das Mikro zu, und das Protokoll sagt es (wie die
   * anderen Zeilen „übersprungen …“ der Auto-Mikro-Entscheidung). Springen darf nur ein ausdrücklicher Aufruf —
   * Knopf, Aktionstaste, Wach-Wort (startPttRecording). ⚠️ Vor jeder Zustandsänderung unten (wie die Sperre).
   */
  // 17.10.2026 (Martin: „sonst nimmt er die Meditation auf“): läuft eine lange Spur, geht nichts von selbst auf (lange-spur.ts).
  if (mikroVonSelbstGesperrt(via)) return;
  // 19.10.2026 (Martin: „Mikro Auto“ aus): von selbst geht nichts auf, wenn die Einstellung aus ist — Knopf, Wach-Wort & Co. nie betroffen.
  if (MIKRO_VON_SELBST.has(via) && !autoMikroAn(loadSettings().autoMikroNachFrage)) {
    meldeDiagnose(`[ptt] ${via} übersprungen stufe-aus weil=chip`);
    return;
  }
  // 17.10.2026 (Martin, 07:35): von selbst nur am VORDEREN Gerät — dort spricht die Stimme (seiten-abgleich.ts `autoMikroNichtVorn`).
  const vornAnderswo = MIKRO_VON_SELBST.has(via) ? autoMikroNichtVorn() : null;
  if (vornAnderswo) {
    meldeDiagnose(`[ptt] ${via} übersprungen nicht-vorn vorn=${vornAnderswo}`);
    return;
  }
  // 17.10.2026: nimmt ein anderes Gerät des Kontos auf, gehen Wach-Wort und Auto-Mikro hier nicht auf (Knopf & Co. immer).
  if (zweitesGeraetHaeltZu(via)) return;
  if (MIKRO_VON_SELBST.has(via) && liegtEtwasUeberDemChat()) {
    meldeDiagnose(`[ptt] ${via} übersprungen seite-offen`);
    return;
  }
  // Ticket 2882: VOR jeder Zustandsänderung unten (Verriegeln, Dock „Senden“) —
  // ein abgewiesener Start darf nichts davon verstellen.
  const sperre = aufnahmeStartSperre();
  if (sperre) {
    pttDoppelstartAbgewiesen(via, sperre);
    return;
  }
  if (loadSettings().pttMode === "call") {
    void handleConnect();
    return;
  }
  pttReleased = false;
  pttCancelIntent = false;
  pttLocked = true;
  pttLockedAt = performance.now();
  pttLockReleasePending = false;
  pttLockSendArmed = false;
  setPttDockSend(true);
  startPttRecording(
    via,
    undefined,
    ttsLief === undefined ? undefined : { quelle: via, seitSichtbar: "?", ttsLief },
    nachlauschMs,
  );
  pttModal?.setLocked(true);
}

function startPttRecording(
  via: PttAusloeser = "knopf",
  /** performance.now() beim Eintritt in den Handler — siehe pttTiming.begin. */
  ausloeserZeit?: number,
  /** Ticket 2522: Finger, Feld, Leertaste … — nur beim Knopf-Weg bekannt. `ttsLief` (12.10.2026): klang die Wiedergabe, bevor der Druck sie stoppte? */
  druck?: { quelle: string; seitSichtbar: string; ttsLief?: boolean },
  /** Ticket 3048: das Fenster aus mikroNachAntwort; ohne Angabe das eingestellte. */
  nachlauschMs?: number,
): void {
  // 27.09.2026 (Ticket 2698): Er will sprechen — ein vorgemerkter Player-
  // Start entfällt, sonst spielte er in diese Aufnahme. Vor allem anderen:
  // der Knopf-Handler bricht das Vorlesen schon vorher ab, und dessen Ende
  // löst den Start erst danach (als Mikrotask) ein.
  zugOhneAbspielen();
  // Ticket 2669: ebenso ein vorgemerktes Neuladen — er will sprechen, die Seite lädt nicht in seine Aufnahme.
  zugOhneNeuLaden();
  // 22.10.2026 (lib/frage-rueckkehr.ts): eine Aufnahme beginnt — eine im Hintergrund wartende Frage öffnet bei der Rückkehr nichts mehr.
  if (frageRueckkehr.loesche()) meldeDiagnose("[ptt] frage-rueckkehr zu grund=aufnahme");
  // 2026-08-23 (Martin: "Wenn ich zweimal auf den Record-Button druecke,
  // loescht er die alte Aufnahme und faengt von null an. Manchmal spreche
  // ich, druecke drauf, weil ich glaube, es sendet ab, aber in Wahrheit
  // loescht es und startet neu.")
  //
  // Im Protokoll bewiesen (12:51:24 und 12:51:33 je ein rec=, kein Senden
  // dazwischen, danach transkript=14ch): Neun Sekunden Sprache verloren.
  //
  // Bisher hing der Schutz allein an pttState, geprueft NUR im Knopf-
  // Handler. Geraet der Zustand aus dem Tritt — verspaeteter Mikro-Start,
  // abgebrochener Zug, zweiter Aufrufer — beginnt diese Funktion
  // bedingungslos von vorn und verwirft dabei die laufende Aufnahme.
  //
  // Der Recorder ist die harte Wahrheit; pttState kann veralten. Laeuft er,
  // ist ein Druck ein SENDEN-Wunsch, nie ein Neustart. Der Riegel sitzt
  // deshalb HIER statt bei jedem Aufrufer — das erschlaegt die Klasse.
  // Was dann geschieht, entscheidet wie bisher das Loslassen
  // (pttResolveRelease): kurz = verriegeln, nochmal = senden.
  /*
   * ⚠️⚠️ 01.10.2026, Ticket 2882 — DER RIEGEL GILT FÜR JEDEN START, IM ERSTEN SCHRITT.
   *
   * Hier stand nur `if (pttRecorder.isRecording())`. Das ist erst wahr, wenn
   * das Mikrofon da ist — gemessen 0,3 bis 1,9 s nach dem Start. Ein zweiter
   * Start in diesem Fenster (`pttStartLauf` gesetzt, `isRecording()` noch
   * false) fand alles frei und öffnete eine zweite Aufnahme. Und eine
   * Aufnahme auf einer ANDEREN Seite desselben Browsers sah er nie: am
   * 01.10., 08:47 lief die erste über das Auto-Mikro nach einer Frage, Martin
   * klickte auf der anderen Seite die Sprechtaste — zwei Aufnahmen, zwei
   * Uploads, zwei Antworten, zwei Stimmen.
   *
   * Der Druck bei laufender Aufnahme bleibt ein SENDEN-Wunsch (2026-08-23,
   * Neun Sekunden Sprache verloren): nichts beginnt neu, das Loslassen
   * entscheidet wie bisher. Den Zustand heilt nur der Druck eines Menschen,
   * und nur, wenn er auf „idle" stand — ein laufendes Senden bleibt unberührt.
   */
  const sperre = aufnahmeStartSperre();
  if (sperre) {
    pttDoppelstartAbgewiesen(via, sperre);
    return;
  }
  // Ab hier gehört die Aufnahme dieser Seite — die anderen sehen es sofort.
  aufnahmeBeanspruchen(via);
  // 21.10.2026: vom Mikro IM Feld gestartet → Diktat an die Cursor-Stelle; jeder andere Start löscht den alten Stand.
  // Gelesen im selben Druck (pointerdown mit preventDefault): der Fokus und die Markierung stehen noch im Feld.
  feldDiktat = druck?.quelle === "feld" ? leseFeldStand() : null;
  if (feldDiktat) meldeDiagnose("[ptt] feld-diktat start");
  // 2026-07-09 (Audit): Telemetrie-Nullpunkt — misst jede Startphase.
  pttTiming.begin(ausloeserZeit);
  beginneAufnahme(); // Welle 0: die Kennung DIESER Aufnahme (Messung, kein Verhalten)
  pttTiming.set("via", via);
  if (druck) {
    pttTiming.set("quelle", druck.quelle);
    pttTiming.set("seitSichtbar", druck.seitSichtbar);
  }
  // 2026-07-06 (Martin: "will schon sprechen waehrend er noch vorliest"):
  // PTT-Druck bricht die laufende Vorlese-Wiedergabe SOFORT ab — kein Warten,
  // kein "Ton unterbrechen" noetig. Stoppt Audio + alle TTS-Queues.
  /*
   * 12.10.2026 (Bahn „fortsetzung-anhaengen“): Beginnt diese Aufnahme binnen 12 s nach einem Auto-Senden und startet ein Mensch sie (Knopf,
   * Aktionstaste), ist sie die Fortsetzung der vorigen Äußerung: die Antwort der Vorrunde wird JETZT abgebrochen — nicht erst, wenn das Mikro da
   * ist (0,3–1,9 s) und nicht erst beim Senden. Derselbe Weg wie der Abbruch-Knopf; die Wiedergabe ist oben schon gestoppt, und eine abgebrochene
   * Schlange nimmt kein spätes Textstück der Vorrunde mehr an (tts-queue.ts: `finished`). Die Angabe bleibt an dieser Aufnahme, bis sie gesendet
   * oder verworfen wird (`fortsetzungSpeicher.beiSenden`).
   */
  const ttsLief = druck?.ttsLief ?? ttsWiedergabeOffen();
  // 17.10.2026: Martins Hand am Mikro pausiert eine laufende lange Spur — sonst nähme die Aufnahme sie mit auf (lange-spur.ts).
  if (via === "knopf" || via === "aktionstaste" || via === "pille" || via === "pille-taste") pausiereFuerHand(`sprechtaste-${via}`);
  pttAbbruchGesendet = false;
  fortsetzungBeiAufnahmestart({
    speicher: fortsetzungSpeicher,
    mensch: via === "knopf" || via === "aktionstaste" || via === "pille" || via === "pille-taste",
    ttsLief,
    stoppeWiedergabe: () => stopCurrentTts("ptt"),
    abbrechen: () => {
      pttAbbruchGesendet = true;
      void fetch("/api/chat/abort", { method: "POST" }).catch(() => {});
    },
    melde: (zeile) => meldeDiagnose(`${zeile} via=${via}`),
  });
  pttTiming.mark("stopTts");

  // 2026-07-06 (Martin): SOFORT sichtbares Feedback beim Druck — der Knopf
  // pulsiert rot, BEVOR Mikro/Modal stehen. Scheitert der Start, raeumt
  // pttResetButton() wieder auf.
  refs.pttBtn.classList.add("is-recording");
  // 2026-07-03 (Martin): egal wo man ist — PTT springt in den Chat, damit
  // Verlauf + Antwort sichtbar sind.
  // 11.10.2026 (Ticket 2945): auch wenn die Grundansicht schon „Chat“ heißt und
  // eine Vollseite wie „Mein Alltag“ darüber liegt — dort ging das Aufnahme-Fenster
  // unter der Seite auf (Aktionstaste, Wach-Wort). Der Wechsel läuft über goToView
  // wie beim Wach-Wort: es schließt, was darüber liegt. Das Auto-Mikro kommt gar
  // nicht bis hierher, wenn etwas offen liegt (siehe `seite-offen` oben).
  // Im Chat ohne offene Seite bleibt es ein Nichts (kein Sprung ans Ende).
  const aktiveAnsicht = document.body.dataset.activeView;
  if (aktiveAnsicht && (aktiveAnsicht !== "chat" || liegtEtwasUeberDemChat())) goToView("chat");
  hapticTap(15);
  unlockAudio();
  // Ticket 2643: jeder Druck beginnt ohne Befund über die Quelle.
  pttStummeQuelle = false;
  pttModal = openPttModal({
    // 21.10.2026 (Martin): KEIN Ja/Nein mehr im Fenster — die offene Frage steht als Karte „Offene Frage“ im Verlauf (lib/frage-knoepfe.ts).
    /*
     * 23.09.2026 (Ticket 2522): ging das Fenster OHNE eigenen Druck auf, sagt
     * es warum. Martin: „Mikro und Modal gehen von selbst an … das muss
     * differenziert werden." Seit pre.3184 oeffnet das Auto-Mikro nach einer
     * Frage wirklich (vorher verwarf es sich) — das soll man erkennen.
     */
    ...(ANLASS_TEXT[via] ? { anlass: ANLASS_TEXT[via] } : {}),
    // 17.10.2026: nach einer Frage geöffnet — ein kurzes Ja geht sofort hinaus.
    ...(via === "nach-frage" ? { nachFrage: true, entscheidungOffen: mikroEntscheidungOffen } : {}),
    // Wartet ein Bild, legt der Hauptknopf den Text dorthin statt zu senden
    // (siehe die Weiche in pttFinishAndSend) — dann muss er das auch sagen.
    ...(pendingComposerImages.length > 0 ? { hauptLabel: "Zum Bild" } : {}),
    // 2026-07-22 (Martin): Stumm-Schalter in der Karte — gleiche Quelle wie
    // der Plus-Menue-Schalter, beide bleiben synchron.
    mutedInitial: !autoPlayEnabled,
    /*
     * Ticket 2420 (20.09.2026): von selbst absenden. Die Einstellung wird
     * bei JEDEM Takt frisch gelesen — wer sie waehrend einer Aufnahme
     * abschaltet, soll nicht doch noch abgeschickt bekommen.
     */
    selbstSenden: pttSelbstSendenEinstellung,
    // 03.10.2026: die Messung der Endwörter (Fantasiewörter) geht mit, wenn die Karte sie hat.
    stilleAutoFrage: (text, stillMs, rausch, folge) => frageStilleAuto(text, stillMs, undefined, rausch, folge),
    onSelbstSenden: (grund, wort) => {
      // Derselbe Weg wie der Knopf — ein zweiter Sendeweg waere ein
      // zweiter Besitzer, und der Rekorder hat damit eine Historie.
      // 26.09.2026: auch das Fenster wie beim Knopf auf „Sende …“ — sonst
      // blieb „Verwerfen“ klickbar, hielt den Versand aber nicht an.
      // Nachprüfung: nur wie der Knopf — bei veraltetem pttState hing das
      // Fenster sonst auf „Sende …“, während pttFinishAndSend nichts tat.
      if (pttState !== "recording") {
        meldeDiagnose(`[ptt-knopf] selbst-senden ohne wirkung zustand=${pttState} grund=${grund}`);
        return;
      }
      // Ticket 2672: entschied die Karte mit Nachgehörtem, steht es im Protokoll.
      pttNachhoerWache?.ausgeloest(grund);
      /*
       * Ticket 2674: die beiden Befehle nehmen die Wege ihrer Knöpfe —
       * „Verwerfen" wie der Knopf Verwerfen, „ins Textfeld" wie der Knopf
       * Ins Textfeld. Hörbar, weil Martin dabei oft nicht hinschaut.
       */
      if (grund === "verwerfen") {
        playBeep("cancel");
        // 26.09.2026 spät: umkehrbar — die AUFNAHME liegt eine Minute bereit.
        void pttVerwerfenUmkehrbar(wort ?? { fuerSich: false, text: "" });
        return;
      }
      pttModal?.setStatus("sending");
      /*
       * „zurückholen"/„doch nicht": das Verworfene kommt ins Eingabefeld
       * (oder der Hinweis, dass nichts mehr daliegt). Vierte Runde: DIESE
       * Aufnahme wird nicht mehr verworfen, sondern ganz erkannt — ist sie
       * mehr als der Befehl („Doch nicht … so schlimm"), geht sie hinaus.
       */
      if (grund === "zurueckholen") {
        playBeep("stop");
        void pttZurueckholBefehl();
        return;
      }
      if (grund === "textfeld") {
        playBeep("stop");
        meldePttEnde("textfeld", "anlass=selbst-textfeld");
        void pttFinishToTextField(true);
        return;
      }
      /*
       * 20.10.2026 (Vollständigkeits-Bremse, shared/satz-vollstaendig.ts): die LIVE-Mitschrift endet offen.
       * 21.10.2026 (Vorfall 17:25:05, Martin: „warum trägt er das ins Inputfeld ein?“): die Live-Mitschrift hatte zwei Wörter weniger
       * als die Datei-Erkennung — die Seite legte einen fertigen Satz ins Feld. Jetzt geht die Aufnahme wie jede Stille-Sendung zum
       * Server; der ENDGÜLTIGE Text entscheidet (voice-command.ts `stilleZuruecklegen(…, liveOffen)`): fertig → hinaus, nicht fertig
       * → `done.unvollstaendig` → ins Eingabefeld (onUnvollstaendig). Nie beides.
       */
      if (grund === "unvollstaendig") {
        console.log(`[ptt-timing] uid=selbst senden grund=stille bremse=live-offen`);
        void pttFinishAndSend("selbst-stille", null, true);
        return;
      }
      console.log(`[ptt-timing] uid=selbst senden grund=${grund}`);
      void pttFinishAndSend(
        `selbst-${grund}`,
        grund === "wort" ? { handlung: "senden", fuerSich: wort?.fuerSich === true } : null,
      );
    },
    // Ticket 2643: der Satz bei stummer Quelle nennt den richtigen Ort.
    inApp: isNativePlatform(),
    onStummeQuelle: () => {
      pttStummeQuelle = true;
    },
    // Ticket 2643 (29.09.2026): nur die App kennt das Mikrofon-Recht und
    // kann ihre Einstellungen öffnen (ab Build 93; ältere liefern nichts).
    ...(isNativePlatform() ? { mikroRecht: holeMikroRecht, oeffneEinstellungen: oeffneAppEinstellungen } : {}),
    // #533 (2026-07-24): echter Mikro-Pegel fuer den Visualizer (nur lesend).
    getLevelTrack: () => pttRecorder.getAudioTrack(),
    pegelNativ: () => pttRecorder.pegelNativ(),
    // Ticket 2230: die "kein Pegel"-Warnung gegen die ECHTE Aufnahme pruefen.
    hatEchtenTon: () => pttRecorder.hatEchtenTon(),
    // Ticket 2618: neuer Teil statt Ende — die Karte kündigt ihn an und
    // warnt an der Uhr DIESES Teils, wenn er nicht zustande kommt.
    teilLage: () => pttRecorder.teilLage(),
    onToggleMute: () => {
      const enabled = !autoPlayEnabled;
      setAutoPlay(enabled);
      return !enabled;
    },
    /*
     * Ticket 2403 — derselbe Schalter wie in den Einstellungen, nur hier
     * erreichbar. Martin: „nicht nur in den Einstellungen, sondern auch
     * direkt."
     *
     * ⚠️ Ueber loadSettings/saveSettings, nicht ueber eine eigene Variable.
     *    Zwei Orte, die denselben Zustand halten, sind das haeufigste Muster
     *    in diesem Haus — und hier waere es besonders aergerlich: der
     *    Schalter im Fenster und der in den Einstellungen wuerden sich
     *    gegenseitig ueberschreiben.
     */
    // 23.09.2026: drei Stufen statt an/aus — ein Tipp schaltet weiter
    // (aus → nach Frage → Jev entscheidet → aus), wie bei der Stille.
    autoMikroInitial: autoMikroGeltend(loadSettings().autoMikroNachFrage) ?? false,
    onToggleAutoMikro: () => {
      const jetzt = loadSettings();
      // Konto noch unbekannt und „jev“ gespeichert: nichts überschreiben (lib/jev-konto.ts).
      if (jevStufeGeschuetzt(jetzt.autoMikroNachFrage)) return "jev";
      const stufe = naechsteAutoMikro(jetzt.autoMikroNachFrage, jevMoeglich());
      saveSettings({ ...jetzt, autoMikroNachFrage: stufe });
      return stufe;
    },
    // Ticket 2741: nur die Anzeige — erkannt wird weiter (siehe ptt-modal.ts).
    mitschriftZeigen: loadSettings().mitschriftZeigen !== false,
    // Ticket 2509/2842: dieselbe Einstellung wie in den Einstellungen, kein
    // zweiter Besitzer — die Karte zeigt sie nur noch an (umgestellt wird in
    // der Fußzeile und im Blatt der Antwortlänge).
    stilleInitial: Number(loadSettings().sendeStilleMs ?? 0),
    onStop: () => {
      if (pttState === "recording") {
        if (!feldDiktat) sendeHaptik(); // war Beep — Feld-Diktat: Stopp-Ton statt Sende-Vibration (pttFeldDiktatEnde)
        pttModal?.setStatus("sending");
        void pttFinishAndSend("fenster-senden");
        return;
      }
      // Ticket 2634: ein Druck, der nichts bewirkt, war bisher unsichtbar.
      meldeDiagnose(`[ptt-knopf] senden ohne wirkung zustand=${pttState}`);
    },
    // Ticket 2668: schließt die Karte selbst (Nachlausch-Fenster), nennt sie
    // den Anlass; ein Druck bleibt „fenster-verwerfen".
    onCancel: (grund, quelle) => pttVerwerfen(grund ?? "fenster-verwerfen", quelle),
    /*
     * Ticket 2634 (30.09.2026): „Sende …“ ausgeblendet (Knopf oder Escape). Die
     * Karte hat sich selbst geschlossen; hier wird nur der Verweis
     * losgelassen. KEIN pttAbort und kein Neu-Senden: die Aufnahme ist beim
     * Server, der Zug läuft weiter, die Antwort kommt im Verlauf (Bus, Nachzug
     * beim Zurückkommen, die Nachfrage von streamPtt).
     */
    onAusblenden: () => {
      meldeDiagnose(`[ptt-ausblenden] zustand=${pttState}`);
      pttModal = null;
    },
    // 2026-07-29: Dritter Weg — Text übernehmen statt senden.
    onToText: () => {
      if (pttState === "recording") {
        hapticTap(15);
        pttModal?.setStatus("sending");
        void pttFinishToTextField();
      }
    },
    /*
     * Ticket 2668: ging das Mikro von selbst auf, lauscht es nur kurz —
     * kommt in dieser Zeit ab dem offenen Mikro keine Sprache, schließt es.
     */
    ...(via === "nach-antwort" || via === "nach-frage"
      ? { nachlauschMs: nachlauschMs ?? nachlauschFenster(loadSettings().nachlauschMs), bereitTon: loadSettings().bereitTon !== false }
      : {}),
    // 2026-07-30 (Ticket 781): Vierter Weg — den vorhandenen Entwurf nach dem
    // Gesprochenen UMSCHREIBEN. Nur angeboten, wenn im Feld schon etwas
    // steht: ohne Entwurf gibt es nichts umzuschreiben, und ein Knopf ohne
    // Gegenstand verwirrt mehr, als er hilft.
    ...(textField.value.trim()
      ? {
          onRewrite: () => {
            if (pttState === "recording") {
              hapticTap(15);
              pttModal?.setStatus("sending");
              void pttFinishToRewrite();
            }
          },
        }
      : {}),
  });
  pttModal.setStatus("starting");
  updateTextInputState();
  void import("./lib/geo-watch.js")
    .then((m) => m.holeStandortEinmal(true))
    .catch(() => undefined);
  // 2026-08-31: Das Versprechen wird FESTGEHALTEN, nicht mehr nur `void`-
  // gestartet. Erst dadurch kann das Loslassen darauf warten, statt den
  // Druck wegzuwerfen und „Zu kurz" zu behaupten (siehe Block bei pttState).
  pttStartLauf = pttToggle().then(() => {
    if (!pttModal) return;
    if (pttState === "recording") {
      // 2026-07-04 (Martin): Piep ERST JETZT — das Mikro startet seit dem
      // warmStream-Fix kalt; piepst es frueher, gehen die ersten Worte
      // verloren ("nur ein Bruchteil transkribiert").
      hapticTap(35); // war Beep — jetzt kraeftiger Impuls
      const unterbrechenZeile = unterbrechenMikroOffen(performance.now()); // Ticket 2695
      if (unterbrechenZeile) meldeDiagnose(unterbrechenZeile);
      pttModal.setStatus("recording");
      // Hat der User VOR dem (asynchronen) Aufnahmestart schon losgelassen?
      if (pttReleased) pttResolveRelease();
      return;
    }
    // 2026-09-01 (Martin: „Öffne Mikro, dann verschwindet das Bild“):
    // Loslassen setzt sending, während der Start noch läuft. Das war kein
    // Fehlschlag — das Senden wartet. Karte bleibt auf „Sende …“.
    if (pttState === "sending") {
      pttModal.setStatus("sending");
      return;
    }
    // Aufnahme startete NICHT (z.B. Mikrofon nicht freigegeben) — Modal nicht
    // hängen lassen; der rote Mikrofon-Hinweis wird im Chat sichtbar.
    pttModal.close();
    pttModal = null;
  });
  // Der Start ist durch — ab hier gibt es nichts mehr zu erwarten.
  void pttStartLauf.finally(() => {
    pttStartLauf = null;
    unterbrechenVerwerfen(); // Ticket 2695: ein Start ohne Mikro hinterlässt keine Messung
    // Ticket 2882: auch ein Start, der nie zur Aufnahme wurde (Mikrofon verweigert,
    // verworfen), gibt die Anmeldung sofort frei — nicht erst im nächsten Takt.
    aufnahmeFreigeben();
  });
  // 21.10.2026: Diktat aus dem Feld-Mikro — die Karte sagt „Ins Feld“ statt „Senden“.
  if (feldDiktat) pttModal?.setInsFeld();
}

function pttResolveRelease(): void {
  if (pttState !== "recording") return;
  if (pttCancelIntent) {
    hapticTap(45); // war Beep
    hapticTap(40);
    refs.pttBtn.classList.remove("is-canceling");
    pttAbort("wisch-oder-escape");
    pttModal?.close();
    pttModal = null;
    return;
  }
  if (!pttRecorder.isRecording()) {
    // 2026-09-01 (Martin: „dann steht Mikrofon nicht bereit“): Loslassen
    // während „Öffne Mikro …“ hat sofort gesendet — 0 ms Ton, Meldung.
    // Stattdessen Freihand, sobald das Mikro wirklich da ist.
    pttLocked = true;
    pttLockedAt = performance.now();
    pttLockReleasePending = false;
    pttLockSendArmed = false;
    pttReleased = false;
    setPttDockSend(true);
    pttModal?.setLocked(true);
    hapticTap(25);
    return;
  }
  // 23.09.2026 (Ticket 2494): Tipp oder Halten zählt ab dem Aufnahmebeginn,
  // nicht ab dem Druck — Begründung bei halteZeitAbAufnahme().
  const aufnahmeMs = Math.round(pttRecorder.getElapsedMs());
  if (!pttLocked && halteZeitAbAufnahme(pttReleaseHeld, aufnahmeMs) < PTT_TIPP_MS) {
    // Nur der neue Fall schreibt eine Zeile: die Uhr am Finger sagt Halten,
    // die Aufnahme ist aber kürzer als ein Tipp. Daran ist zu sehen, ob die
    // Regel greift. „aufnahme-kurz", nicht „kaltstart": warm knapp an der
    // Grenze (rec=+58ms, gehalten 530 ms) greift sie auch. `verzug` trennt
    // die zwei Deutungen: klein = der Finger lag wirklich so lange, groß =
    // das Loslassen kam früher und wurde erst nach dem Kaltstart zugestellt.
    if (pttReleaseHeld >= PTT_TIPP_MS) {
      meldeDiagnose(
        `[ptt-einrasten] aufnahme-kurz gehalten=${Math.round(pttReleaseHeld)}ms aufnahme=${aufnahmeMs}ms verzug=${Math.round(pttReleaseVerzug)}ms`,
      );
    }
    // 2026-07-28 (Martin, nach eigener Beobachtung: "fast 98 % lasse ich den
    // Knopf nicht los, sondern verriegle"): Der kurze Tipp VERRIEGELT jetzt,
    // statt abzubrechen.
    //
    // Vorher (04.07.) brach er ab und zeigte einen Hinweis, wie man es
    // "richtig" macht — Halten plus Hochwischen. Damit brauchte der HAEUFIGSTE
    // Fall zwei Gesten, und der intuitive kurze Tipp kostete die Aufnahme.
    // Genau das kritisiert das Audit zu #771: die Maschine erzieht den Nutzer,
    // statt ihm zu folgen.
    //
    // Jetzt gilt dasselbe Muster wie in WhatsApp, Signal und Telegram und wie
    // beim Eingabefeld-Mikro: kurz tippen laesst laufen, nochmal tippen sendet,
    // gedrueckt halten sendet beim Loslassen. Das Hochwischen bleibt als
    // zweiter Weg erhalten — es wird niemandem etwas weggenommen.
    //
    // Die Aufnahme LAEUFT hier bereits (pointerdown hat sie gestartet); es
    // wird also nur der Zustand umgestellt, nichts neu begonnen.
    pttLocked = true;
    pttLockedAt = performance.now();
    pttLockReleasePending = false;
    pttLockSendArmed = false;
    // Wichtig: Ohne dies haelt der .then-Callback von pttToggle den Zug fuer
    // "losgelassen" und wuerde sofort senden.
    pttReleased = false;
    setPttDockSend(true);
    pttModal?.setLocked(true);
    hapticTap(25);
    return;
  }
  // Halten losgelassen ODER eingerasteter Tipp → senden. (Feld-Diktat: keine Sende-Vibration, der Stopp-Ton kommt in pttFeldDiktatEnde.)
  if (!feldDiktat) sendeHaptik(); // war Beep
  pttModal?.setStatus("sending");
  void pttFinishAndSend(
    `loslassen gehalten=${Math.round(pttReleaseHeld)}ms aufnahme=${aufnahmeMs}ms gesperrt=${pttLocked ? "ja" : "nein"}`,
  );
}

/**
 * Wann die App zuletzt sichtbar wurde — der Bezugspunkt fuer
 * `istNachgereichterDruck()`. `performance.now()`, damit er denselben
 * Nullpunkt hat wie `ev.timeStamp`.
 */
let pttSichtbarSeit: number | null = null;
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    pttSichtbarSeit = performance.now();
  }
});

const onPttPointerDown = (ev: PointerEvent): void => {
  /*
   * ⚠️⚠️ 19.09.2026 — GANZ VORNE, VOR ALLEM ANDEREN.
   *
   * Martin: *„Immer wenn ich hier auf Aufnahme gedrueckt habe und absenden
   * und dann zu Vespera wechsle, dann passiert das, dass dann der
   * Push-to-Talk auch automatisch geklickt ist. Das ist total bloed."*
   *
   * Am Protokoll belegt: `via=knopf` wird NUR hier gesetzt — es war ein
   * echtes Zeigerereignis auf dem Knopf, kein programmatischer Start. Das
   * Ereignis entstand nur nicht jetzt, sondern lag in der Schlange und wurde
   * bei der Rueckkehr nachgereicht. Begruendung in lib/ptt-geste.ts.
   *
   * Steht das weiter unten, sind healStaleCallFlags(), das Stoppen der
   * Sprachausgabe und das Oeffnen des Mikros schon gelaufen — der Schaden
   * waere angerichtet.
   */
  if (istNachgereichterDruck(ev.timeStamp, pttSichtbarSeit)) {
    meldeDiagnose(
      `[ptt] nachgereichter Druck verworfen (t=${Math.round(ev.timeStamp)}, sichtbar seit ${Math.round(pttSichtbarSeit ?? 0)})`,
    );
    return;
  }
  /*
   * ⚠️ 10.09.2026 (Ticket 2202) — die erste Zeile des Handlers, vor allem
   *    anderen. Zwischen hier und `pttTiming.begin()` liegen das Heilen der
   *    Anruf-Flags, das Lesen der Einstellungen und das Stoppen der
   *    Sprachausgabe. Bisher war das eine ungemessene Luecke; jetzt steht sie
   *    als `vor=+Nms` in der Zeile.
   */
  const handlerEintritt = performance.now();
  /*
   * 23.09.2026 (Ticket 2522): WER hat gedrueckt? 11:07:51 startete eine
   * Aufnahme 2 s nach dem Neuladen, Martin hatte nicht getippt — und die
   * Zeile sagte nur `via=knopf`. Jetzt: Finger (mit Zeigerart), Feld,
   * Leertaste oder kuenstlich, und wie lange die Seite da schon sichtbar war.
   */
  const druckQuelle =
    pttDruckQuelle ??
    (ev.isTrusted ? `finger-${ev.pointerType || "unbekannt"}` : "kuenstlich");
  const seitSichtbar =
    pttSichtbarSeit !== null && pttSichtbarSeit !== undefined
      ? `${Math.round(ev.timeStamp - pttSichtbarSeit)}ms`
      : "?";
  // 2026-06-30 (Martin): haengendes connected-Flag selbst heilen — sonst
  // reagiert PTT nach einem Anruf-Drop nicht mehr. S. healStaleCallFlags().
  healStaleCallFlags();
  if (connected) return;
  if (loadSettings().pttMode === "call") return; // Anruf-Modus → auf pointerup
  // 12.10.2026: ob die Wiedergabe klang, wird VOR dem Stopp unten gelesen (`tts=` der Fortsetzung).
  const ttsLiefBeimDruck = ttsWiedergabeOffen();
  if (pttState === "sending" && !fortsetzungUebernehmen()) return;
  if (pttState === "recording") {
    // 2026-07-07 (Ticket 278): im eingerasteten Modus ist der Knopf der
    // ABSENDEN-Knopf — frischer Druck armed das Senden (pointerup sendet).
    if (pttLocked) {
      const seitEinrasten = performance.now() - pttLockedAt;
      if (seitEinrasten < PTT_PRELL_MS) {
        meldeDiagnose(
          `[ptt-prell] Druck ${Math.round(seitEinrasten)}ms nach dem Einrasten verworfen ` +
            `typ=${ev.pointerType || "?"} echt=${ev.isTrusted ? "ja" : "nein"} zeiger=${ev.pointerId}`,
        );
        return;
      }
      pttLockSendArmed = true;
      try {
        refs.pttBtn.setPointerCapture(ev.pointerId);
      } catch {
        /* Capture ist optional */
      }
    }
    return;
  }
  // 2026-07-04 (Martin: "Brauche die Moeglichkeit, dass er stoppt"):
  // Dazwischenreden — der PTT-Druck stoppt SOFORT die laufende Sprachausgabe.
  // Verhindert nebenbei, dass die Bot-Stimme ins Mikro blutet und die
  // Transkription versaut.
  stopCurrentTts("ptt-zeiger");
  ev.preventDefault();
  try {
    refs.pttBtn.setPointerCapture(ev.pointerId);
  } catch {
    /* Capture ist optional */
  }
  // 2026-08-31: Neuer Druck, neues Recht auf genau EINE Rueckmeldung.
  // ⚠️ Ohne diese Zeile bliebe nach der ersten Meldung fuer den Rest der
  // Sitzung alles stumm — das waere schlimmer als zwei Meldungen.
  pttMeldungenFreigeben();
  pttPressStart = performance.now();
  pttPressY = ev.clientY;
  pttPressX = ev.clientX;
  pttLastY = ev.clientY;
  pttLastX = ev.clientX;
  pttReleased = false;
  pttLocked = false;
  pttLockReleasePending = false;
  pttCancelIntent = false;
  startPttRecording("knopf", handlerEintritt, { quelle: druckQuelle, seitSichtbar, ttsLief: ttsLiefBeimDruck });
};

const onPttPointerMove = (ev: PointerEvent): void => {
  if (pttReleased || pttLocked || pttState !== "recording") return;
  pttLastY = ev.clientY;
  pttLastX = ev.clientX;
  const dy = ev.clientY - pttPressY;
  const dx = ev.clientX - pttPressX;
  // Telegram: HOCHWISCHEN verriegelt — Aufnahme laeuft freihaendig weiter,
  // gesendet wird per Tipp auf den Knopf oder den Senden-Button im Modal.
  // 2026-07-24 (Martin): Verriegeln nur bei DEUTLICH nach oben — der
  // vertikale Weg muss den seitlichen klar dominieren, sonst verriegelt
  // ein Rutscher.
  if (dy < -PTT_CANCEL_DY && Math.abs(dy) > Math.abs(dx) * 1.5) {
    pttLocked = true;
    pttLockedAt = performance.now();
    pttLockReleasePending = true;
    pttCancelIntent = false;
    refs.pttBtn.classList.remove("is-canceling");
    // Ticket 278: Knopf mutiert sichtbar zum Absenden-Knopf.
    setPttDockSend(true);
    hapticTap(15);
    pttModal?.setStatus("recording");
    pttModal?.setLocked(true);
    return;
  }
  // LINKS wischen = Abbrechen (Telegram). 2026-07-24 (Martin): nur wenn
  // die Links-Bewegung den vertikalen Weg klar dominiert — ein
  // diagonaler/vertikaler Rutscher bricht nicht mehr ab.
  const cancel = dx < -PTT_CANCEL_DX && Math.abs(dx) > Math.abs(dy) * 1.5;
  if (cancel !== pttCancelIntent) {
    pttCancelIntent = cancel;
    refs.pttBtn.classList.toggle("is-canceling", cancel);
  }
};

/**
 * Finger weg — Loslassen ODER pointercancel (iOS bricht den Zeiger oft ab
 * sobald das Overlay da ist oder der Daumen rutscht).
 *
 * 2463: pointercancel darf die Aufnahme NICHT wegwerfen. Kurz = verriegeln,
 * Halten = senden, Hochwischen = verriegeln. Abbrechen bleibt Links-Wisch.
 */
const onPttPointerUp = (ev: PointerEvent, art: "up" | "cancel" = "up"): void => {
  try {
    refs.pttBtn.releasePointerCapture(ev.pointerId);
  } catch {
    /* ignore */
  }
  if (connected) return;
  if (loadSettings().pttMode === "call") {
    if (art === "up") void handleConnect();
    return;
  }
  if (pttLocked) {
    // 2015: iOS schickt beim zweiten Tipp (Senden) oft pointercancel statt
    // pointerup — der erste Tipp hat verriegelt, der zweite kam nie an.
    if (pttLockSendArmed && (art === "up" || art === "cancel")) {
      pttLockSendArmed = false;
      hapticTap(20);
      if (pttState === "recording") pttResolveRelease();
      return;
    }
    pttLockReleasePending = false;
    pttReleased = false;
    return;
  }
  if (art === "cancel" && pttState === "recording") {
    const dy = pttLastY - pttPressY;
    const dx = pttLastX - pttPressX;
    if (dy < -PTT_CANCEL_DY && Math.abs(dy) > Math.abs(dx) * 1.5) {
      pttLocked = true;
      pttLockedAt = performance.now();
      pttLockReleasePending = true;
      pttCancelIntent = false;
      refs.pttBtn.classList.remove("is-canceling");
      setPttDockSend(true);
      hapticTap(15);
      pttModal?.setStatus("recording");
      pttModal?.setLocked(true);
      return;
    }
  }
  pttReleased = true;
  pttReleaseHeld = performance.now() - pttPressStart;
  pttReleaseVerzug = performance.now() - ev.timeStamp;
  if (pttState === "recording") pttResolveRelease();
};

/**
 * 2026-07-29 (Martin: "Wenn ich Push to Talk klicke, dann soll ich auch mit
 * Enter absenden koennen."): Im Freihand-Modus laeuft die Aufnahme weiter,
 * und bisher gab es nur einen Weg zum Absenden — den Knopf noch einmal
 * antippen. Wer am Schreibtisch sitzt und die Hand ohnehin auf der Tastatur
 * hat, greift dafuer zur Maus. Enter ist der Griff, den man erwartet: Es ist
 * dieselbe Taste, die im Eingabefeld sendet.
 *
 * Escape bricht ab — spiegelbildlich zum Wisch nach links.
 *
 * Gilt in JEDEM Aufnahme-Zustand — gehalten wie eingerastet. Nur wenn gerade
 * in ein Textfeld getippt wird, bleibt Enter dort; sonst wuerde man beim
 * Schreiben unabsichtlich eine Sprachnachricht abfeuern.
 */
document.addEventListener(
  "keydown",
  (e: KeyboardEvent) => {
  // 2026-07-29 (Martin: "Das Absenden bei Push to Talk mit Enter geht
  // immer noch nicht, wenn ich es loslassen moechte ueber den Desktop"):
  // Die Bedingung verlangte den VERRIEGELTEN Zustand. Wer am Schreibtisch
  // den Knopf mit der Maus gedrueckt haelt, ist aber nicht verriegelt — fuer
  // ihn tat sich nichts. Dabei ist gerade das der Fall, in dem die zweite
  // Hand ohnehin auf der Tastatur liegt.
  // Jetzt genuegt: Es wird aufgenommen. Ob gehalten oder eingerastet, ist
  // fuer die Absicht egal — Enter heisst senden.
  // 2026-07-29: Nach drei erfolglosen Anlaeufen eine ehrliche Diagnose statt
  // der naechsten Vermutung. Diese Zeile sagt beim Enter-Druck, WARUM es
  // nicht sendet — sichtbar in der Konsole des Browsers.
  if (e.key === "Enter" && !e.shiftKey) {
    const z = e.target as HTMLElement | null;
    console.log(
      `[ptt-enter] pttState=${pttState} pttLocked=${pttLocked} ` +
        `ziel=${z?.tagName ?? "-"} fokus=${(document.activeElement as HTMLElement | null)?.tagName ?? "-"}`,
    );
  }
  // 2026-07-29, dritter Anlauf: Nicht nur auf pttState schauen. Ist das
  // Aufnahme-Fenster offen, WILL der Nutzer senden — egal, in welchem
  // inneren Zustand die Aufnahme gerade steckt. Der Zustand war bisher die
  // einzige Bedingung, und offenbar stimmte er im entscheidenden Moment
  // nicht mit dem ueberein, was Martin auf dem Bildschirm sieht.
  const nimmtAuf = pttState === "recording" || pttModal !== null;
  if (!nimmtAuf) return;
  const ziel = e.target as HTMLElement | null;
  // 2026-07-29: BUTTON gehoerte NICHT in diese Liste — aber der Fokus liegt
  // im Aufnahme-Fenster auf einem Knopf, und der Browser macht aus Enter auf
  // einem fokussierten Knopf einen Klick. Traf der Fokus "Verwerfen", warf
  // Enter die Aufnahme weg statt sie zu senden. Deshalb hier ABFANGEN und
  // selbst entscheiden, statt dem Browser den Vortritt zu lassen.
  const tippfeld =
    !!ziel &&
    (ziel.tagName === "INPUT" ||
      ziel.tagName === "TEXTAREA" ||
      ziel.isContentEditable);
  if (tippfeld) return;
  // Ticket 2634: „Ausblenden“ (Karte in „Sende …“) ist der eine Knopf, den Enter
  // klicken DARF — sonst schluckte diese Weiche die Taste, und die Tastatur
  // käme nicht aus der hängenden Karte.
  if (ziel?.closest?.(".pttm-ausweg")) return;

  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    hapticTap(20);
    // Haelt der Nutzer den Knopf gerade mit der Maus, kommt gleich noch ein
    // pointerup. Ohne diese Markierung wuerde es ein zweites Mal senden.
    pttReleased = true;
    pttResolveRelease();
    return;
  }
  if (e.key === "Escape") {
    e.preventDefault();
    pttCancelIntent = true;
    hapticTap(12);
    pttResolveRelease();
  }
  },
  // ERFASSUNGSPHASE: Wir wollen VOR dem fokussierten Knopf drankommen. Sonst
  // macht der Browser aus Enter einen Klick auf ihn — und wenn das gerade
  // "Verwerfen" ist, ist die Aufnahme weg statt gesendet.
  true,
);

refs.pttBtn.addEventListener("pointerdown", onPttPointerDown);
refs.pttBtn.addEventListener("pointermove", onPttPointerMove);
refs.pttBtn.addEventListener("pointerup", onPttPointerUp);
refs.pttBtn.addEventListener("pointercancel", (ev: PointerEvent) => {
  onPttPointerUp(ev, "cancel");
});
refs.pttBtn.addEventListener("contextmenu", (ev) => ev.preventDefault());

/*
 * 2026-08-28 (Martin: "wenn die Tastatur aufgeklappt ist, aufsprechen"):
 *
 *   Unter 768px blendet `body.text-focus` die ganze Fusszeile aus — und damit
 *   das eine Mikrofon. Genau beim Tippen fehlte also der Weg zum Sprechen.
 *
 *   Dieser Knopf erscheint nur dann (siehe CSS) und kann bewusst NUR tippen:
 *   `pttToggle()` startet Freihand, ein zweiter Tipp sendet. Halten,
 *   Wischen und Verriegeln bleiben dem Fuss-Mikro — bei offener Tastatur
 *   will niemand den Finger auf dem Schirm lassen.
 *
 *   ⚠️ Bewusst NICHT die Zeiger-Handler des Fuss-Mikros angehaengt: die
 *   fassen `refs.pttBtn` an 31 Stellen fest an (Zeiger-Einfang, Zustaende,
 *   Wisch-Erkennung). Sie zu verallgemeinern hiesse, die heikelste Geste der
 *   App umzubauen — fuer eine Geste, die hier gar nicht gebraucht wird.
 */
/*
 * ⚠️ Am Dokument, nicht am Knopf: Beim ersten Anlauf hing der Zuhoerer direkt
 * an `#composer-ptt` — und `getElementById` lieferte an dieser Stelle NULL.
 * Der Knopf existiert zum Zeitpunkt der Modul-Auswertung noch nicht. Gemessen
 * war die Folge, dass ein Tipp sichtbar gar nichts tat: keine Aufnahme, keine
 * Karte, nicht einmal ein Fehler. Ein `?.` schluckt genau so etwas.
 *
 * Die Zustellung ueber das Dokument ist gegen jede Reihenfolge immun.
 */


// 2026-07-25 (Martin: "kann man bei der Tastatur den Push-Knopf belegen, so
// lange ich die Taste halte?"): LEERTASTE HALTEN = sprechen, loslassen =
// senden. Die Taste loest dieselben Ereignisse am Sprech-Knopf aus wie ein
// Finger — es gibt also KEINE zweite Aufnahme-Logik, und Warm-Mikro,
// Verriegeln, Abbrechen und alle Sicherheitsnetze gelten unveraendert.
//
// Warum die Leertaste und nicht Martins Vorschlaege: Die Feststelltaste
// melden Browser als Umschalter, nicht als gedrueckte Taste — "halten" laesst
// sich damit nicht erkennen. Der Tabulator ist der Weg, die App ohne Maus zu
// bedienen; ihn zu kapern nimmt genau das weg.
let tastenPttAktiv = false;
/**
 * Woher kam der letzte kuenstliche Druck? (23.09.2026, Ticket 2522)
 * Leertaste und Mikro im Feld schicken DASSELBE Zeigerereignis wie der
 * Finger — im Protokoll stand bisher fuer alle drei `via=knopf`.
 */
let pttDruckQuelle: "leertaste" | "feld" | null = null;
function tastenPttZeige(art: "pointerdown" | "pointerup"): void {
  refs.pttBtn.dispatchEvent(
    new PointerEvent(art, { pointerId: 1, bubbles: true, cancelable: true }),
  );
}
/*
 * 2026-08-28 (Martin: "wenn die Tastatur aufgeklappt ist, aufsprechen"):
 *
 *   Unter 768px blendet `body.text-focus` die ganze Fusszeile aus — und damit
 *   das eine Mikrofon. Genau beim Tippen fehlte der Weg zum Sprechen.
 *
 *   Der Knopf im Feld erscheint NUR dann (siehe CSS) und schickt dieselben
 *   Zeiger-Ereignisse an das Fuss-Mikro wie die Leertaste darunter. Damit
 *   gibt es KEINE zweite Aufnahme-Logik: Warm-Mikro, kurzer Tipp fuer
 *   Freihand, Halten, Verriegeln, Abbrechen und alle Sicherheitsnetze gelten
 *   unveraendert.
 *
 *   ⚠️ Zwei Anlaeufe davor gingen daneben, beide still:
 *     - `click` statt `pointerdown`: Der Klick nahm dem Feld den Fokus,
 *       `text-focus` fiel weg, und der Knopf verschwand MITTEN im eigenen
 *       Tipp. Auf dem Telefon waere zusaetzlich die Tastatur zugeklappt.
 *     - `startPttRecording()` direkt gerufen: Das ist nur die halbe Geste —
 *       die Zustandsmaschine wartet auf ein zugehoeriges Loslassen. Es
 *       passierte sichtbar gar nichts.
 */
document.addEventListener(
  "pointerdown",
  (ev) => {
    const ziel = (ev.target as HTMLElement | null)?.closest?.("#composer-ptt");
    if (!ziel) return;
    // Haelt den Fokus im Feld: Tastatur bleibt offen, Knopf bleibt stehen.
    ev.preventDefault();
    // 21.10.2026 (Prüfauflage 2): läuft schon eine Aufnahme (z. B. von selbst geöffnet), beendet dieser Tipp sie — und
    // wie seine Beschriftung sagt: ins Feld an die Cursor-Stelle, nie als Nachricht.
    if (!feldDiktat && (pttState === "recording" || pttRecorder.isRecording())) feldDiktatSetzen("uebernommen");
    pttDruckQuelle = "feld";
    tastenPttZeige("pointerdown");
    pttDruckQuelle = null;
  },
  true,
);
document.addEventListener("pointerup", (ev) => {
  const ziel = (ev.target as HTMLElement | null)?.closest?.("#composer-ptt");
  if (!ziel) return;
  ev.preventDefault();
  tastenPttZeige("pointerup");
});
document.addEventListener("pointercancel", (ev) => {
  const ziel = (ev.target as HTMLElement | null)?.closest?.("#composer-ptt");
  if (!ziel) return;
  ev.preventDefault();
  tastenPttZeige("pointerup");
});

window.addEventListener("keydown", (ev) => {
  // Wiederholungen einer gehaltenen Taste ignorieren — sonst startet die
  // Aufnahme im Dauerfeuer neu.
  if (ev.code !== "Space" || ev.repeat || tastenPttAktiv) return;
  if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
  const aktiv = document.activeElement as HTMLElement | null;
  // Wird getippt oder haelt ein Bedienelement den Fokus, gehoert die
  // Leertaste dorthin — dann NICHT kapern.
  if (aktiv?.isContentEditable) return;
  if (aktiv && /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(aktiv.tagName)) return;
  if (refs.pttBtn.hidden || refs.pttBtn.disabled) return;
  tastenPttAktiv = true;
  ev.preventDefault(); // sonst scrollt die Seite
  pttDruckQuelle = "leertaste";
  tastenPttZeige("pointerdown");
  pttDruckQuelle = null;
});
window.addEventListener("keyup", (ev) => {
  if (ev.code !== "Space" || !tastenPttAktiv) return;
  tastenPttAktiv = false;
  ev.preventDefault();
  tastenPttZeige("pointerup");
});
// Sicherheitsnetz: wechselt das Fenster den Fokus, waehrend die Taste unten
// ist, kommt kein keyup mehr — die Aufnahme liefe sonst endlos weiter.
window.addEventListener("blur", () => {
  if (!tastenPttAktiv) return;
  tastenPttAktiv = false;
  tastenPttZeige("pointerup");
});

// 2026-07-05 (Martin: "haengt / laeuft weiter"): Sicherheitsnetz. Rutscht der
// Finger vom Knopf und das button-pointerup geht verloren (iOS-WKWebView),
// faengt das window-pointerup das Loslassen trotzdem — nur bei aktiver, NICHT
// verriegelter Aufnahme und nur, wenn der Knopf es nicht schon hatte.
window.addEventListener("pointermove", (ev: PointerEvent) => {
  if (pttState !== "recording" || pttLocked || pttReleased) return;
  onPttPointerMove(ev);
});
window.addEventListener("pointerup", (ev: PointerEvent) => {
  if (pttState !== "recording" || pttLocked || pttReleased) return;
  onPttPointerUp(ev);
});
window.addEventListener("pointercancel", (ev: PointerEvent) => {
  if (pttState !== "recording" || pttReleased) return;
  onPttPointerUp(ev, "cancel");
});

// Composer-Mic bleibt hidden (Pre.461 — User-Wunsch: Telegram-Style im Dock).

interface QuickAccessItem {
  view: string;
  label: string;
  iconPath: string;
}
const QUICK_ACCESS_ITEMS: QuickAccessItem[] = [
  {
    view: "contacts",
    label: "Kontakte",
    iconPath:
      "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
  },
  {
    view: "library",
    label: "Mediathek",
    iconPath:
      "M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zm-5.04-6.71l-2.75 3.54-1.96-2.36L8.5 17h11l-3.54-4.71z",
  },
  {
    view: "memory",
    label: "Erinnerungen",
    iconPath:
      "M12 2A10 10 0 1 0 22 12 10 10 0 0 0 12 2zm1 15h-2v-2h2v2zm2.07-7.75-.9.92A3.5 3.5 0 0 0 13 13h-2v-.5a4.5 4.5 0 0 1 1.31-3.18l1.24-1.26A2 2 0 1 0 10 7H8a4 4 0 1 1 7.07 2.25z",
  },
  {
    view: "projects",
    label: "Projekte",
    iconPath:
      "M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z",
  },
  {
    view: "settings",
    label: "Einstellungen",
    iconPath:
      "M19.43 12.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65A.488.488 0 0 0 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z",
  },
];

function openQuickAccessSheet(): void {
  // Falls schon offen: schließen.
  const existing = document.querySelector(".quick-access-overlay");
  if (existing) {
    existing.remove();
    return;
  }
  const overlay = document.createElement("div");
  overlay.className = "quick-access-overlay";
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) overlay.remove();
  });

  const sheet = document.createElement("div");
  sheet.className = "quick-access-sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Schnellzugriff");

  const grid = document.createElement("div");
  grid.className = "quick-access-grid";
  for (const item of QUICK_ACCESS_ITEMS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "quick-access-btn";
    btn.setAttribute("aria-label", item.label);
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "26");
    svg.setAttribute("height", "26");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", item.iconPath);
    path.setAttribute("fill", "currentColor");
    svg.appendChild(path);
    btn.appendChild(svg);
    const label = document.createElement("span");
    label.textContent = item.label;
    btn.appendChild(label);
    btn.addEventListener("click", () => {
      overlay.remove();
      goToView(item.view);
    });
    grid.appendChild(btn);
  }
  sheet.appendChild(grid);
  overlay.appendChild(sheet);
  document.body.appendChild(overlay);
}

// --- HTTP-Chat-Helper (Chat-Mode, kein LiveKit) -----------------------

// 2026-07-05 (#30): Waehrend ein Turn laeuft, wird der Senden-Knopf zum
// STOP-Knopf — ein Klick bricht die Generierung serverseitig ab (der warme
// Turn rechnete sonst weiter und verbrannte Zeit + Konto-Kontingent).
let chatTurnActive = false;
/*
 * 2026-08-24 (Martin, zwei Beschwerden, die derselbe Fehler waren):
 *
 *   "Der Scheiß Absendebutton geht auch noch immer nirgendwohin!"
 *   "Ich habe gerade über ein Medikament gesprochen. Er hat gesagt, er
 *    recherchiert. Dann kam es zu einem Abbruch und keine Fertigstellung."
 *
 *   Im Protokoll um 09:09:16 stand beides nebeneinander:
 *     [chat-abort] uid=1 interrupted=true
 *     [warm-chat] Konto 1 abgebrochen (Stopp vom Nutzer)
 *
 *   Solange eine Antwort lief, war dieser Knopf KEIN Senden-Knopf mehr,
 *   sondern ein Stopp-Knopf — jeder Klick wurde abgefangen (capture +
 *   stopImmediatePropagation) und brach die laufende Antwort ab. Wer einen
 *   Text getippt hatte, sah: nichts ging raus, und die Recherche war tot.
 *
 *   Ein Knopf, zwei Bedeutungen, und der Nutzer sieht nur eine davon.
 *
 * NEU: Steht Text im Feld, wird GESENDET — das ist unmissverstaendlich die
 * Absicht. Der Stopp bleibt, aber nur fuer den Fall, dass das Feld LEER ist:
 * dann kann ein Klick nichts anderes bedeuten als "hoer auf".
 */
/*
 * Ticket 2329 (19.09.2026): Ton beim ersten Antippen freischalten.
 *
 * Am Betrieb gemessen: Albert, im Browser unterwegs, bekam bei 63 % seiner
 * Wiedergaben keinen Ton (142 von 224 in sieben Tagen, am 17.09. 57 von 62).
 * Martin in der nativen App: 1302 Wiedergaben, kein einziger Fehler.
 */
freischaltenBeiErsterGeste();

document.getElementById("text-input-send")?.addEventListener(
  "click",
  (ev) => {
    if (!chatTurnActive) return;
    /*
     * Text im Feld → der Klick meint Senden, nicht Stoppen.
     *
     * 2026-08-24, NACHTRAG: Im ersten Anlauf stand hier, der laufende Zug werde
     * "im Sende-Weg ordentlich beendet". Das war falsch — nachgeprueft:
     * sendChatHttp setzt nur chatTurnActive und sendet los, und weder Frontend
     * noch Server haben einen Riegel dagegen. Es liefen ZWEI Antworten
     * gleichzeitig, und danach stand der Stopp-Knopf im falschen Zustand (das
     * finally des ersten Zugs raeumt auf, waehrend der zweite noch laeuft).
     *
     * Also hier abbrechen, bevor der neue Zug beginnt. Der Abbruch ist
     * absichtlich NICHT abgewartet: Die alte Antwort ist ueberholt, sobald
     * Martin etwas Neues schickt — auf ihr Ende zu warten hiesse, sein Senden
     * zu verzoegern.
     */
    if (textField.value.trim().length > 0) {
      void fetch("/api/chat/abort", { method: "POST" }).catch(() => {});
      return;
    }
    ev.preventDefault();
    ev.stopImmediatePropagation();
    // 27.10.2026 (Etappen-Lauf): leeres Feld = AUSDRÜCKLICHER Stopp — er beendet auch einen Prozess im Hintergrund (der Ruf oben, beim
    // Senden mit Text, tut das nicht: eine neue Nachricht lässt den Prozess weiterlaufen).
    void sendeProzessStopp();
  },
  { capture: true },
);

/*
 * ⚠️ 16.09.2026 — Martin: *„wenn Vespera einen Auftrag gerade abarbeitet,
 * moechte ich, so wie es bei Claude Code ueblich ist, einen Prozess
 * abbrechen koennen."*
 *
 * Das KONNTE er schon. Der Senden-Knopf ist bei leerem Feld ein Stopp-Knopf
 * (#30, pre.1430), `/api/chat/abort` haengt daran, und es funktioniert.
 *
 * Man sah es nur nicht:
 *   - Das SYMBOL blieb der Sende-Pfeil. Nur der Hintergrund wurde rot.
 *   - `title` wechselte auf „Antwort stoppen" — ein Tooltip, den es AM
 *     TELEFON NICHT GIBT.
 *   - `aria-label` blieb „Senden". Ein Screenreader sagte also weiter
 *     „Senden", waehrend der Knopf abbrach.
 *
 * Auf dem Telefon war es ein ROTER SENDE-PFEIL. Farbe allein ist kein
 * Signal — nicht bei Rot-Gruen-Schwaeche, und nicht fuer jemanden, der die
 * Farbe fuer eine Betonung haelt. Jetzt sagt das Symbol, was der Knopf tut.
 */
const SENDE_SYMBOL =
  '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">' +
  '<path fill="currentColor" d="M3.4 20.4 21.85 12 3.4 3.6 3.39 10.2 17 12 3.39 13.8z"/>' +
  "</svg>";
/** Ein gefuelltes Quadrat — dasselbe Zeichen, das jeder Player fuers Stoppen nutzt. */
const STOPP_SYMBOL =
  '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">' +
  '<rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor"/>' +
  "</svg>";

function zeigeStoppSymbol(btn: HTMLElement | null, stopp: boolean): void {
  if (!btn) return;
  btn.innerHTML = stopp ? STOPP_SYMBOL : SENDE_SYMBOL;
  btn.setAttribute("aria-label", stopp ? "Antwort stoppen" : "Senden");
}

async function sendChatHttp(payload: {
  message: string;
  /** Altbestand, Einzahl — bleibt fuer aeltere Aufrufer. */
  imageUrl?: string;
  /** 2026-08-28: bis zu fuenf Bilder in EINEM Zug. */
  imageUrls?: string[];
  caption?: string;
  /** 23.09.2026, Ticket 2498: Datei-Vorgang — nie der Beleg-Weg (chat.ts). */
  vorgang?: boolean;
  /** 28.09.2026, Ticket 2740: entscheidet mit darfVorlesen; ohne Angabe getippt. */
  herkunft?: FrageHerkunft;
  /** 11.10.2026, Ticket 2845: der Server hat die Anfrage mit 200 angenommen (lib/chat-http.ts). */
  onAngenommen?: () => void;
  /** 16.10.2026 (Dreier-Modus Stufe 1): Martins Wahl „An: Schmied“/„Vespera“ (lib/empfaenger-wahl.ts) — nur vom Eingabefeld gesetzt. */
  an?: MartinsEmpfaenger;
  /** 20.10.2026: Martins Tipp auf Ja/Nein einer Karte (lib/chat-http.ts). */
  kartenantwort?: true;
  /** 27.10.2026 (Mandat): vom Knopf einer Karte (`sendUserMessageFromCard`) — nur damit löst der Server einen Mandats-Knopf ein. */
  vonKarte?: true;
}): Promise<boolean> {
  chatTurnActive = true;
  // 12.10.2026: eine getippte Nachricht ist dazwischen — an die letzte Sprechtasten-Nachricht schließt nichts mehr an.
  fortsetzungSpeicher.vergiss();
  const sendBtnEl = document.getElementById("text-input-send");
  sendBtnEl?.classList.add("is-stop");
  sendBtnEl?.setAttribute("title", "Antwort stoppen");
  zeigeStoppSymbol(sendBtnEl, true);
  // Zustand neu berechnen — sonst bliebe der Knopf deaktiviert (das Feld ist
  // nach dem Senden leer) und der Stopp waere nicht anklickbar.
  updateTextInputState();
  /*
   * 11.10.2026, Ticket 2845 (Prüfbefund): Wirft die Anzeige NACH der Annahme (Schluss-Render, Abschluss-Karte), ist die
   * Nachricht trotzdem beim Server. Ein Wurf hieße für jeden Aufrufer „fehlgeschlagen“ — die Liedkarte bot „bitte noch
   * einmal tippen“ an und bestellte das Lied beim zweiten Tipp doppelt, das Eingabefeld meldete „Senden fehlgeschlagen“.
   * Darum gibt dieser Weg nach der Annahme `true` zurück, statt zu werfen; der Grund steht im Protokoll.
   */
  let angenommen = false;
  try {
    return await sendChatHttpInner({
      ...payload,
      onAngenommen: () => {
        angenommen = true;
        payload.onAngenommen?.();
      },
    });
  } catch (err) {
    if (!angenommen) throw err;
    meldeDiagnose(`[chat] Anzeige nach der Annahme gescheitert: ${err instanceof Error ? err.message : String(err)}`);
    return true;
  } finally {
    chatTurnActive = false;
    sendBtnEl?.classList.remove("is-stop");
    sendBtnEl?.removeAttribute("title");
    zeigeStoppSymbol(sendBtnEl, false);
    updateTextInputState();
  }
}

async function sendChatHttpInner(payload: {
  message: string;
  imageUrl?: string;
  imageUrls?: string[];
  caption?: string;
  vorgang?: boolean;
  herkunft?: FrageHerkunft;
  onAngenommen?: () => void;
  an?: MartinsEmpfaenger;
  kartenantwort?: true;
  vonKarte?: true;
}): Promise<boolean> {
  zugNutzerFrage = payload.message ?? "";
  /*
   * 07.10.2026: Kam die Nachricht beim Server an? Nein, wenn der Abruf scheiterte (offline) oder der
   * Server sie mit einem HTTP-Fehler abwies. Alles andere (Antwort, Fehler-Ereignis, Watchdog) heißt:
   * angekommen. Bisher fing dieser Weg jeden Fehler still ab — eine Karte konnte es nicht erfahren.
   *
   * 11.10.2026, Ticket 2845: „nicht angekommen“ gilt NUR für einen Fehler VOR der Antwort des Servers.
   * Hat er mit 200 geantwortet (`angenommen`), rechnet er weiter — auch wenn danach die Leitung reißt
   * oder eine Anzeige wirft (server/chat.ts, Ticket 2256). Wer dann erneut sendet, löst Mail oder
   * Aktion ein zweites Mal aus. Der 60-s-Abriss ohne jede Antwort zählt weiter als angekommen.
   */
  let angekommen = true;
  let angenommen = false;
  try {
    const { brauchtFrischenStandort, holeStandortEinmal } = await import(
      "./lib/geo-watch.js"
    );
    if (brauchtFrischenStandort(payload.message)) {
      await Promise.race([
        holeStandortEinmal(true),
        new Promise<void>((r) => window.setTimeout(r, 4000)),
      ]);
    }
  } catch {
    /* ohne Fix gilt der gespeicherte Ort */
  }
  // Bot-Live-Bubble erst erzeugen, wenn wirklich Text kommt — bei reinen
  // Tool-Calls (Bild generieren) bleibt sie unsichtbar.
  // Pre.743: Zeitanker = Sendezeitpunkt. Antwort-Blase + Platzhalter docken
  // hier an, NICHT an der Eintreffzeit — sonst rutscht eine spät eintreffende
  // Antwort (langes Opus) unter Nachrichten, die der User in der Wartezeit
  // geschrieben hat. So bleibt sie direkt unter ihrer Frage.
  const anchorTs = Date.now();
  let bubble: HTMLElement | null = null;
  // 2026-07-29: Welches Modell die Antwort erzeugt hat — wird
  // NACH fillBotBubble an die Zeitzeile geschrieben.
  let gemeldetesModell: string | null = null;
  let acc = "";
  /* Zeiger in die SPRECHQUELLE — siehe sprechNachschub. */
  let chatGesprochenBis = 0;
  // Ref-Objekt statt let — TS narrowt Closure-Zuweisungen sonst auf never.
  const chatTts = { stream: null as TtsStream | null };
  // Ticket 2257: derselbe kurze Warte-Ton wie bei PTT — der Chat-Stream
  // kennt keine "flush"-Meldung, darum wird hier ab onToolCall gezaehlt.
  let spokeWaitTone = false;
  let waitToneTimer = 0;
  const clearWaitToneTimer = (): void => {
    if (waitToneTimer) {
      window.clearTimeout(waitToneTimer);
      waitToneTimer = 0;
    }
  };
  // Pre.473: TruthGuard-Warnings akkumulieren, NACH fillBotBubble anhängen
  const truthWarnings: Array<{
    severity: "warn" | "info" | "none";
    matches: string[];
  }> = [];
  // Pre.743: SOFORT sichtbarer „wird bearbeitet"-Platzhalter (an anchorTs) — der
  // User sieht ohne Verzögerung, dass gearbeitet wird (wichtig bei langsamem
  // Opus); die echte Antwort/Tools ersetzen ihn an exakt dieser Stelle.
  const schliesseZug = eroeffneZug(anchorTs);
  let pending: HTMLElement | null = buildPendingBubble(undefined, wartenderName(empfaengerWahl, payload.an, currentBotName));
  insertOrdered(pending, anchorTs);
  // Er hat eben gesendet: ans Ende, und ab hier folgt der Chat der Antwort.
  scrollChatToBottomReliably();
  /*
   * ⚠️ 07.09.2026 — der Text sagt jetzt, was wirklich passiert.
   *
   * Martin: *„Dann ist gestanden: Suche läuft noch. Die Frage ist, Suche
   * nach was?"* Genau. An dieser Stelle weiss niemand, ob gesucht wird —
   * der Platzhalter zaehlt nur Sekunden. Meistens denkt Vespera einfach
   * nach. Ein Wort, das mehr behauptet, als der Code weiss, ist eine
   * kleine Unwahrheit auf dem Schirm.
   */
  const pendingHinweis = window.setTimeout(() => {
    setzeWartetext(pending, "Ich denke noch nach …");
  }, 2500);
  const clearPending = (): void => {
    window.clearTimeout(pendingHinweis);
    entferneWarteblase(pending);
    pending = null;
  };
  const ensureBubble = (): HTMLElement => {
    if (!bubble) {
      // 2312: persist/SSE kann die Blase schon gemalt haben (ohne Modell).
      // Dieselbe Antwort wiederverwenden, nicht eine zweite zeichnen.
      const schon = acc ? findePassendeBotBlase(acc) : null;
      if (schon) {
        bubble = schon;
        liveBotBubble = schon;
        return schon;
      }
      clearPending();
      bubble = document.createElement("div");
      // 2026-07-24 (Martin): ruhiger Schreib-Cursor waehrend des Streams —
      // ueberbrueckt die Pause zwischen kurzer Vorab-Reaktion und langer
      // Antwort, statt verunsichernder Warte-Punkte.
      bubble.className = "bubble bot is-streaming";
      insertOrdered(bubble, anchorTs);
      // Pre.162: Avatar sofort beim Erstellen, nicht erst bei fillBotBubble.
      prependBotAvatar(bubble);
      liveBotBubble = bubble;
      fuehreChatNach();
      // Ticket 2730: liest er weiter oben, zeigt der Knopf die neue Antwort.
      notifyNewMessage();
    }
    return bubble;
  };
  // Während Bild-Tools laufen (5-15s), zeigen wir ein Pulse-Skeleton-
  // Tile pro tool_call. Sobald das passende tool_result reinkommt,
  // ersetzen wir es durch die echte Image-Bubble (oder eine Tool-Card
  // bei anderen Tools).
  const pendingTiles = new Map<string, HTMLElement>();
  /* Ticket 2444: was dieser Zug hervorgebracht hat — fuer die Abschluss-Karte. */
  const zugErgebnisse: Ergebnis[] = [];
  let zugWerkzeuge = 0;
  const mailZug = beginneMailZug();
  chatMailZug = mailZug; // darfVorlesen fragt den Zug dieser Antwort; er bleibt bis zum nächsten Chat-Zug stehen (auch am Zugende nach dem finally)
  try {
    await streamChat({
      // Ticket 3074: endet der Strom ohne „fertig“ (Stopp, Netz weg, Stille), geht die Stufenkarte der Musik mit ihm.
      onEnde: () => schliesseLiedFortschritt(),
      message: payload.message,
      imageUrl: payload.imageUrl,
      imageUrls: payload.imageUrls,
      caption: payload.caption,
      vorgang: payload.vorgang,
      ...(payload.an ? { an: payload.an } : {}),
      ...(payload.kartenantwort ? { kartenantwort: true as const } : {}),
      ...(payload.vonKarte ? { vonKarte: true as const } : {}),
      onAngenommen: () => {
        angenommen = true;
        payload.onAngenommen?.();
      },
      onChunk: (chunk) => {
        // Ticket 2175: das erste Stueck beendet die Stille — ab hier steht
        // die Wartezeit fest.
        zugErstesWort();
        clearWaitToneTimer();
        acc += chunk;
        const el = ensureBubble();
        // Voice setzt dataset.text + liveBotBubble fortlaufend. HTTP tat das
        // nicht — SSE-Echo und Vordergrund-Pull malten dieselbe Antwort noch
        // einmal (Blase ohne Modell-Vermerk).
        el.dataset.text = acc.slice(0, 80).trim();
        liveBotBubble = el;
        // Die Sprechfassung gehoert nicht in die Blase — hier steht roher
        // Text (kein renderMarkdown), also muss die Marke hier weg.
        const sicht = teileAntwort(acc).anzeige;
        const schon = bubbleReplyText(el);
        if (!schon || sicht.length >= schon.length) {
          el.textContent = sicht;
        }
        fuehreChatNach();
        // 2026-07-21 (Martin): getippt spricht wie PTT — satzweise ab dem
        // ersten Satz, nicht erst nach der fertigen Antwort.
        // 15.09.2026 (Kunde ueber Martin): … aber nur, wenn man das will.
        // Ein Schalter im Plus-Menue (Vorgabe seit 15.10.2026 an).
        // 28.09.2026 (Ticket 2740): eine angetippte Karte spricht wie PTT.
        if (darfVorlesen(payload.herkunft)) {
          if (!chatTts.stream) {
            // 17.10.2026: die getippte Frage ist Martins Hand — laufende Musik wird angehalten, damit die Antwort sprechen darf (lange-spur.ts).
            pausiereFuerHand("getippt", true);
            chatTts.stream = beginTtsStream(undefined, undefined, bubble);
          }
          const nach = sprechNachschub(acc, chatGesprochenBis);
          chatGesprochenBis = nach.neu;
          if (nach.text) chatTts.stream.push(nach.text);
        }
      },
      onToolProgress: (ev) => {
        clearPending(); // Tool-Fortschritt übernimmt den „arbeitet"-Hinweis
        // Pre.355: Live-Spinner-Bubble auch im HTTP-Chat (Pre.339-System
        // bisher nur im LiveKit-Voice-Pfad aktiv gewesen).
        if (ev.phase === "done") {
          closeToolProgressBubble(ev.tool);
          return;
        }
        showOrUpdateToolProgressBubble(ev);
      },
      onToolCall: (ev) => {
        /* Ticket 2701: der Abschied ist kein Arbeitsschritt (abschluss.ts). */
        if (zaehltAlsArbeit(ev.name)) zugWerkzeuge++;
        // Ticket 2257: der Chat-Stream kennt kein "flush"-Ereignis wie PTT —
        // der Werkzeug-Aufruf selbst ist hier das früheste verfügbare Signal,
        // dass gleich eine Pause kommt. Vor JEDEM Aufruf neu ansetzen (nicht
        // aufaddieren), damit eine Kette mehrerer Werkzeuge nur EINEN Timer
        // offen hat, getaktet auf den letzten Aufruf.
        // Ticket 2257 Teil a: erst den schon gestreamten, aber noch nicht
        // gesprochenen Satzrest aussprechen — sonst verstummt die Ausgabe
        // waehrend des Werkzeug-Laufs, obwohl im Display mehr Text steht als
        // gesprochen wurde (analog zum PTT-Pfad in onFlush). ERST flushen,
        // DANN den Warte-Ton-Timer setzen.
        // 15.09.2026: Rest aussprechen und Warte-Ton nur, wenn der getippte
        // Weg ueberhaupt spricht — sonst toente es in eine stille Antwort.
        if (darfVorlesen(payload.herkunft)) chatTts.stream?.flushPending();
        clearWaitToneTimer();
        if (darfVorlesen(payload.herkunft) && !spokeWaitTone) {
          waitToneTimer = window.setTimeout(() => {
            waitToneTimer = 0;
            if (spokeWaitTone) return;
            if (!chatTts.stream || chatTts.stream.isAudioActive()) return;
            spokeWaitTone = true;
            playWerkzeugWarteTon();
          }, WERKZEUG_TON_MS);
        }
        // 2026-07-05 (#43): Warm-CLI sendet jetzt generische tool_calls —
        // dabei den "arbeitet"-Hinweis STEHEN lassen (keine Karte folgt);
        // nur die Skeleton-Faelle uebernehmen den Platz selbst.
        setzeWartetext(
          pending,
          ev.name === "web_search"
            ? "Suche läuft …"
            : ev.name === "route_directions"
              ? "Route wird geholt …"
              : "Arbeitet noch …",
        );
        if (ev.name === "generate_image" || ev.name === "edit_image") {
          clearPending();
          const skeleton = appendImageSkeleton(
            ev.name === "generate_image"
              ? typeof ev.input.prompt === "string"
                ? ev.input.prompt
                : "Bild wird erzeugt…"
              : typeof ev.input.instruction === "string"
                ? ev.input.instruction
                : "Bild wird bearbeitet…",
          );
          pendingTiles.set(ev.id, skeleton);
        } else if (ev.name === "play_music") {
          clearPending();
          const q =
            typeof ev.input.query === "string" ? ev.input.query : "Suche…";
          const skeleton = appendMusicSkeleton(q);
          pendingTiles.set(ev.id, skeleton);
        }
      },
      onToolResult: (ev) => {
        // Ticket 3074: das Ergebnis löst die Stufenkarte der Musik ab, auch wenn „fertig“ nie ankam.
        if (ev.name === "generate_music") closeToolProgressBubble(ev.name);
        const ergebnis = ergebnisAus(ev.name, ev.result);
        if (ergebnis) zugErgebnisse.push(ergebnis);
        merkeMailAntwort(mailZug, ev.name, false, () => chatTts.stream?.stumm());
        const skeleton = pendingTiles.get(ev.id);
        if (skeleton) {
          skeleton.remove();
          pendingTiles.delete(ev.id);
        }
        // Bild-Tools rendern wir als grosse Image-Bubble.
        if (ev.name === "generate_image" || ev.name === "edit_image") {
          const url = extractImageUrl(ev.result);
          if (url) {
            const cap =
              ev.name === "generate_image"
                ? extractField(ev.result, "prompt")
                : extractField(ev.result, "instruction");
            appendImageBubble(url, "bot", cap);
            pushHistory({
              kind: "image",
              image: { url, caption: cap ?? null, from: "bot" },
              ts: Date.now(),
            });
            return;
          }
        }
        // open_url: Tab öffnen + Card im Chat.
        if (ev.name === "open_url") {
          const data = parseUrlResult(ev.result);
          if (data?.ok && data.url) {
            // 2026-06-30 (Martin): Card zuerst, Tab-Öffnen best-effort —
            // WKWebView kann window.open werfen, sonst keine Karte.
            appendUrlBubble(data.url, data.title ?? null);
            openExternalUrl(data.url);
            pushHistory({
              kind: "url",
              urlOpen: { url: data.url, title: data.title ?? null },
              ts: Date.now(),
            });
            return;
          }
        }
        // Music-Player als Player-Card.
        if (ev.name === "play_music") {
          const data = parseMusicResult(ev.result);
          if (data) {
            // Ticket 2937: der Player bleibt in der Karte, kein Absprung nach draussen.
            appendMusicBubble(data, { autoplay: true });
            pushHistory({
              kind: "music",
              music: data,
              ts: Date.now(),
            });
            return;
          }
        }
        // Andere Tools: kompakte Tool-Card im Chat (gleicher Renderer
        // wie im Voice-Modus).
        // Pre.402: dispatchToolCard statt renderToolCard direkt — sonst
        // feuern Side-Effects nicht (Topbar-Refresh bei project_create/
        // _update/_set_active, contact_call → tel:, open_view → goToView).
        const isErr =
          ev.result !== null &&
          typeof ev.result === "object" &&
          (ev.result as { ok?: boolean }).ok === false;
        dispatchToolCard({
          tool: ev.name,
          callId: ev.id,
          isError: isErr,
          result: ev.result,
          anzeige: ev.anzeige,
        });
      },
      onError: (err) => {
        // Ticket 405: Watchdog-Abbruch ist KEIN verlorener Turn — der Server
        // rechnet weiter und liefert die Antwort ueber den SSE-Kanal nach.
        // Als eigene System-Blase, damit bereits gestreamter Text stehen
        // bleibt (die Fehler-Zeile unten ueberschreibt die Bot-Blase).
        if (err === CHAT_STREAM_STALLED) {
          appendBubble(
            "system",
            "Verbindung war kurz unterbrochen — die Antwort wird im Hintergrund fertig geschrieben und erscheint hier normalerweise von selbst. Falls sie ausbleibt, frag einfach nochmal.",
          );
          return;
        }
        if (err.startsWith("http-") && !angenommen) angekommen = false;
        ensureBubble().textContent = fehlerSatz(err);
        ensureBubble().title = err; // der technische Grund, zum Nachsehen
        ensureBubble().classList.add("error");
        ensureBubble().classList.remove("is-streaming");
      },
      onModel: (m) => updateFooterEffectiveModel(m),
      // Ticket 2790: wie an der Sprechtaste.
      onRunde: merkeRunde,
      onProjectSuggest: (sug) => showProjectSuggestBanner(sug),
      onDone: (info) => {
        glockeNeuFragen();
        // Pre.421: Footer dynamisch updaten wenn Server auf Fallback-Modell
        // gewechselt hat (Anthropic credit-leer → Gemini Lite / OpenAI).
        if (info.effectiveModel) {
          updateFooterEffectiveModel(info.effectiveModel);
          // 2026-07-29 (Martin: "Ich sehe immer noch nicht, was für ein
          // Modell wir für welche Frage bekommen"): Hier NUR merken, nicht
          // schon schreiben. Der Vermerk wurde vorher an dieser Stelle
          // gesetzt — und Sekundenbruchteile später von fillBotBubble
          // wieder entfernt, das die Blase zum Final-Render komplett leert.
          // Genau davor warnt der Kommentar bei den Wahrheits-Abzeichen
          // (Pre.473): Was bleiben soll, muss NACH dem Render dran.
          gemeldetesModell = info.effectiveModel;
        }
        // Ticket 2782: wie an der Sprechtaste — Grund und „ruht bis“ neu.
        void frageGrund();
      },
      onTruthWarning: (warn) => {
        // Pre.473: Warnings AKKUMULIEREN, NICHT direkt anhängen.
        // fillBotBubble (im finally-Block unten) ersetzt sonst innerHTML
        // und wischt unser Badge weg. Wir hängen sie NACH fillBotBubble
        // ans bubble-Element.
        truthWarnings.push(warn);
      },
    });
  } catch (err) {
    if (!angenommen) angekommen = false;
    ensureBubble().textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
    ensureBubble().classList.add("error");
        ensureBubble().classList.remove("is-streaming");
  } finally {
    schliesseZug();
    clearWaitToneTimer();
    clearPending(); // falls keine Text-Antwort kam (reine Tool-/Bild-Antwort)
    // Übrige Skeleton-Tiles aufräumen (z.B. bei Abbruch ohne tool_result).
    for (const tile of pendingTiles.values()) tile.remove();
    pendingTiles.clear();
    schliesseOffeneOebbKarte();
  }
  const finalBubble: HTMLElement | null = bubble;
  /*
   * Ticket 2444 (Martin): „Zum Schluss sollte immer die Summe von allem
   * kommen, damit der User nur mehr das Ergebnis sieht." Die Karte kommt
   * NACH der Antwort — sie ist das Ende, nicht ein weiterer Schritt.
   */
  if (brauchtAbschluss(zugWerkzeuge, zugErgebnisse)) {
    const karte = baueAbschlussKarte(zugErgebnisse);
    /* `bubble` wird nur in Rueckrufen gesetzt — TypeScript haelt es hier
       fuer null. Der Wert ist zur Laufzeit da; deshalb die Weitung. */
    const anker = finalBubble as HTMLElement | null;
    if (anker) anker.insertAdjacentElement("afterend", karte);
    else insertOrdered(karte, Date.now());
    fuehreChatNach();
  }
  // Ticket 2698: endet mit dem Vorlesen dieser Antwort (null: keins).
  let stimme: Promise<void> | null = null;
  if (acc && finalBubble) {
    // Pre.158: Final-Render der Bot-Bubble — Markdown + Play-Button.
    // (fillBotBubble entfernt den Schreib-Cursor is-streaming.)
    fillBotBubble(finalBubble, acc);
    if (chatTts.stream) {
      const nach = sprechNachschub(acc, chatGesprochenBis, true);
      chatGesprochenBis = nach.neu;
      // Ticket 2232 (13.09.2026): dieselbe Schlussbilanz wie im PTT-Zug.
      meldeDiagnose(
        `[sprech-d] zug-ende(chat) bisher=${chatGesprochenBis} acc=${acc.length}`,
      );
      if (nach.text) chatTts.stream.push(nach.text);
      stimme = chatTts.stream.finish();
      /*
       * 24.09.2026 (Ticket 2551): Der Rückfall fragte `autoPlayEnabled`
       * statt der Vorlese-Regel wie der Satz-Nachschub oben — seit dem
       * 15.09. wurden getippte Antworten am Zugende vorgelesen, auch wenn
       * „Getipptes vorlesen" aus war. Martin überließ die Wahl mir: Der
       * Schalter gilt. Ein Schalter, der nicht tut, was er sagt, ist ein
       * Fehler. Den Schaden vom 24.09. (die wartende Sprechtasten-Antwort
       * wurde abgewürgt) verhindert zusätzlich der Vorrang der Sprechtaste
       * in beginTtsStream (lib/sprechtasten-vorrang.ts).
       */
    } else if (darfVorlesen(payload.herkunft)) stimme = playTts(acc);
    // 2026-07-29: Modell-Vermerk an die Zeitzeile — aus demselben Grund
    // hier unten wie die Abzeichen: fillBotBubble hat die Blase eben geleert.
    if (gemeldetesModell) setzeModellVermerk(finalBubble, gemeldetesModell);
    liveBotBubble = finalBubble;
    // Pre.473: TruthGuard-Badges JETZT anhängen — nach fillBotBubble damit
    // sie nicht durch innerHTML-Replace verloren gehen.
    const bb: HTMLElement = finalBubble;
    for (const warn of truthWarnings) {
      if (bb.querySelector(`.bubble-truth-warn.truth-${warn.severity}`))
        continue;
      const badge = document.createElement("div");
      badge.className = `bubble-truth-warn truth-${warn.severity}`;
      const ns = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(ns, "svg");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("width", "14");
      svg.setAttribute("height", "14");
      svg.setAttribute("aria-hidden", "true");
      const path = document.createElementNS(ns, "path");
      path.setAttribute("fill", "currentColor");
      path.setAttribute(
        "d",
        warn.severity === "warn"
          ? "M12 2 1 21h22L12 2zm-1 6h2v8h-2zm0 10h2v2h-2z"
          : "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1 5h2v6h-2zm0 8h2v2h-2z",
      );
      svg.appendChild(path);
      badge.appendChild(svg);
      const label = document.createElement("span");
      label.textContent =
        warn.severity === "warn" ? "Möglicherweise erfunden" : "Vage Antwort";
      badge.appendChild(label);
      badge.title = `TruthGuard: ${warn.matches.join(", ")}`;
      bb.appendChild(badge);
    }
    /*
     * pre.3603: Der Schluss-Render (Markdown, Abspielknopf, Modell-Vermerk) macht die Blase höher — im
     * Nachbau um 207 Punkte. Nachgezogen wurde das nur, wenn das Festhalten vom letzten Textstück noch lief;
     * hatte es schon vier ruhige Bilder gezählt, stand die Antwort danach 207 Punkte unter dem Rand (in 1
     * von 3 Läufen, vor und nach dieser Version). `folgen` zieht nur nach, wenn er ohnehin unten war.
     */
    fuehreChatNach();
  }
  // Ticket 2698: ein vorgemerkter Player startet nach diesem Vorlesen.
  abspielenNachStimme(stimme);
  if (acc) {
    // 2026-07-04 (Martin): SELBEN Stempel wie die Live-Bubble (anchorTs) —
    // sonst kippt Text-vs-Tool-Karte zwischen Live und Reload.
    pushHistory({ kind: "bot", text: acc, ts: anchorTs });
  }
  // 2312: SSE-Echo kann eine Task später landen als pushHistory. Identität
  // noch einen Tick halten, dann freigeben — der nächste Zug braucht sie nicht.
  const gehalten = finalBubble;
  queueMicrotask(() => {
    if (liveBotBubble === gehalten) liveBotBubble = null;
  });
  return angekommen;
}

function makePlayIcon(): SVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "22");
  svg.setAttribute("height", "22");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute("d", "M8 5v14l11-7z");
  svg.appendChild(path);
  return svg;
}

/**
 * Pre.450 Ticket #13: Tap-to-Play-Card für phone_play-Tool.
 * Persona sagt "Tippe den roten Button um abzuspielen" — aber im
 * Frontend gab's bisher keinen Renderer. User sah nur den Bot-Text,
 * keine Card → Play geht nicht. Jetzt: prominente Card mit Provider-
 * Logo + Song-Label + dickem roten Play-Button der die URL öffnet.
 */
function appendPhonePlayBubble(
  url: string,
  label: string,
  provider: string,
): HTMLElement {
  const node = document.createElement("div");
  node.className = "bubble bot phone-play-card chat-karte";
  node.dataset.phonePlayUrl = url;

  const row = document.createElement("div");
  row.className = "phone-play-row";

  const meta = document.createElement("div");
  meta.className = "phone-play-meta";
  const providerLabel = document.createElement("div");
  providerLabel.className = "phone-play-provider";
  providerLabel.textContent =
    provider === "spotify"
      ? "Spotify"
      : provider === "apple" || provider === "apple_music"
        ? "Apple Music"
        : "YouTube Music";
  const songLabel = document.createElement("div");
  songLabel.className = "phone-play-label";
  songLabel.textContent = label;
  meta.appendChild(providerLabel);
  meta.appendChild(songLabel);
  row.appendChild(meta);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "phone-play-btn";
  btn.setAttribute("aria-label", `${label} abspielen`);
  const playIcon = makePlayIcon();
  playIcon.setAttribute("width", "32");
  playIcon.setAttribute("height", "32");
  btn.appendChild(playIcon);
  btn.addEventListener("click", () => {
    window.open(url, "_blank", "noopener,noreferrer");
  });
  row.appendChild(btn);

  node.appendChild(row);
  insertOrdered(node, Date.now());
  fuehreChatNach();
  return node;
}

/**
 * Die Musik-Karte in den Chat setzen (Ticket 2937). Gebaut wird sie in
 * lib/musik-karte.ts; sie öffnet nichts von selbst, der Player läuft in der Karte.
 */
function appendMusicBubble(
  data: MusicResult,
  opts?: { autoplay?: boolean },
): HTMLElement {
  const node = baueMusikKarte(data, opts);
  insertOrdered(node, Date.now());
  fuehreChatNach();
  return node;
}

interface UrlResult {
  ok: boolean;
  url?: string;
  title?: string | null;
  error?: string;
}

function parseUrlResult(result: unknown): UrlResult | null {
  if (typeof result === "string") {
    try {
      return JSON.parse(result) as UrlResult;
    } catch {
      return null;
    }
  }
  if (result && typeof result === "object") return result as UrlResult;
  return null;
}

function appendFileBubble(item: FileDto): void {
  const node = document.createElement("div");
  node.className = "bubble file-card user chat-karte";

  const icon = document.createElement("span");
  icon.className = "file-card-icon";
  icon.textContent = fileBadge(item.mime);
  node.appendChild(icon);

  const meta = document.createElement("div");
  meta.className = "file-card-meta";
  const name = document.createElement("a");
  name.className = "file-card-name";
  name.href = item.url;
  name.target = "_blank";
  name.rel = "noopener";
  // 2026-07-29 (Martin: "Verbesserung der Cards, weil da ein Attachment
  // angehängt worden ist, sodass es in einem guten Zusammenhang steht"):
  //
  // Hier stand die ANWEISUNG des Nutzers als Dateiname — bei einem langen
  // Satz füllte sie zwei Zeilen und verdrängte, worum es geht: welche Datei
  // das ist. Der echte Dateiname wird beim Hochladen leider nicht mit
  // gespeichert (FileDto kennt nur die UUID-Adresse), deshalb steht jetzt
  // die ART der Datei in der Kopfzeile — "PDF-Dokument", "Bild", "Audio" —
  // und die Anweisung darunter als das, was sie ist: der Auftrag dazu.
  const art = fileArtLabel(item.mime);
  name.textContent = art;
  meta.appendChild(name);
  const sub = document.createElement("div");
  sub.className = "file-card-sub";
  sub.textContent = `${humanFileSize(item.size)} · ${item.mime.split("/")[1] ?? item.mime}`;
  meta.appendChild(sub);
  // Der Auftrag zur Datei — eigene Zeile, gedämpft, auf zwei Zeilen begrenzt.
  const auftrag = (item.caption ?? "").trim();
  if (auftrag) {
    const a = document.createElement("div");
    a.className = "file-card-auftrag";
    a.textContent = auftrag;
    a.title = auftrag;
    meta.appendChild(a);
  }
  node.appendChild(meta);

  insertOrdered(node, Date.now());
  fuehreChatNach();
}

function fileBadge(mime: string): string {
  if (mime === "application/pdf") return "PDF";
  if (mime.startsWith("audio/mp")) return "MP3";
  if (mime.startsWith("audio/")) return "AUD";
  if (mime === "text/plain") return "TXT";
  if (mime === "text/markdown") return "MD";
  if (mime === "text/csv") return "CSV";
  if (mime === "application/json") return "JSON";
  if (mime.includes("word")) return "DOC";
  if (mime.includes("sheet")) return "XLS";
  if (mime === "application/zip") return "ZIP";
  return "?";
}

function humanFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function makeExternalLinkIcon(): SVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "18");
  svg.setAttribute("height", "18");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute(
    "d",
    "M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3zM19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2z",
  );
  svg.appendChild(path);
  return svg;
}

// openExternalUrl: seit 26.09.2026 in lib/app-browser.ts (Ticket 2679).

function appendUrlBubble(url: string, title: string | null): HTMLElement {
  const node = document.createElement("div");
  node.className = "bubble url-card bot chat-karte";

  // Pre.177: Maps-URLs bekommen einen expliziten "Route öffnen"-Button —
  // vorher musste der User die unauffällige URL-Zeile antippen, die wie
  // reiner Text aussieht. Bei normalen Links ein generischer "Öffnen"-Button.
  const isMaps =
    /(?:^|\.)google\.[^/]+\/maps|maps\.google\.|maps\.apple\./i.test(url);
  // Pre.790 (#60): Suche vs. Route unterscheiden — vorher hieß auch eine
  // reine Marker-Suche "Route öffnen".
  const isMapsSearch = isMaps && /\/maps\/search\//i.test(url);
  const actionLabel = isMapsSearch
    ? "In Google Maps suchen"
    : isMaps
      ? "Route in Google Maps öffnen"
      : "Link öffnen";

  const head = document.createElement("div");
  head.className = "url-card-head";
  const ico = document.createElement("span");
  ico.className = "url-card-icon";
  // Pre.790 (#60): Karten-Links sofort als Karte erkennbar (map-pin).
  ico.appendChild(
    isMaps ? icon("map-pin", { size: 18 }) : makeExternalLinkIcon(),
  );
  head.appendChild(ico);

  const body = document.createElement("div");
  body.className = "url-card-body";
  const t = document.createElement("div");
  t.className = "url-card-title";
  t.textContent = title ?? friendlyHost(url);
  body.appendChild(t);
  const u = document.createElement("a");
  u.className = "url-card-url";
  u.href = url;
  u.target = "_blank";
  u.rel = "noopener noreferrer";
  u.textContent = friendlyHost(url);
  body.appendChild(u);
  head.appendChild(body);
  node.appendChild(head);

  const actions = document.createElement("div");
  actions.className = "url-card-actions";
  const openBtn = document.createElement("a");
  openBtn.className = "url-card-action-btn";
  openBtn.href = url;
  openBtn.target = "_blank";
  openBtn.rel = "noopener noreferrer";
  openBtn.textContent = actionLabel;
  actions.appendChild(openBtn);
  actions.appendChild(
    makeShareButton({ url, title: title ?? friendlyHost(url) }),
  );
  node.appendChild(actions);

  insertOrdered(node, Date.now());
  fuehreChatNach();
  return node;
}

function friendlyHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function appendMusicSkeleton(query: string): HTMLElement {
  const node = document.createElement("div");
  node.className = "bubble music music-skeleton bot";
  const label = document.createElement("div");
  label.className = "music-skeleton-label";
  label.textContent = `Suche „${query}" auf YouTube …`;
  node.appendChild(label);
  insertOrdered(node, Date.now());
  fuehreChatNach();
  return node;
}

function appendImageSkeleton(label: string): HTMLElement {
  const node = document.createElement("div");
  node.className = "bubble image-skeleton bot";
  const shimmer = document.createElement("div");
  shimmer.className = "image-skeleton-shimmer";
  node.appendChild(shimmer);
  const labelEl = document.createElement("div");
  labelEl.className = "image-skeleton-label";
  labelEl.textContent = label.slice(0, 80);
  node.appendChild(labelEl);
  insertOrdered(node, Date.now());
  fuehreChatNach();
  return node;
}

// --- Image-Bubble + Upload-Flow ---------------------------------------

// Ticket 2892: Bild, Beschriftung (darunter), Uhrzeit und — bei Nachrichten aus Telegram — die Kanalmarke stehen in
// EINER Blase; der Teilen-Knopf hängt als runder Knopf daneben (style.css, „Teilen neben der Medienblase“).
// `kanal` kommt aus demselben Feld wie bei Textblasen (metadata.source, siehe kanal-vermerk.ts).
function appendImageBubble(
  url: string,
  from: "user" | "bot",
  caption?: string | null,
  kanal?: string | null,
): void {
  // 23.09.2026, Ticket 2498: Ein Datei-Vorgang schickt seine Bilder mit
  // einem Auftrag, der den DATEI_MARKER traegt. Beim Abgleich (Neuladen,
  // zweites Geraet) kommt dieser Auftrag als Bildunterschrift zurueck —
  // ganz, samt Dateiliste. Er geht ans Modell, nicht unter das Bild;
  // dieselbe Regel wie in appendBubble, an derselben Art Stelle.
  if (caption?.startsWith(DATEI_MARKER)) caption = null;
  const node = baueBildBlase({ url, from, caption, vergroessern: openLightboxStandalone });
  insertOrdered(node, Date.now());
  setzeKanalVermerk(node, kanal);
  /*
   * ⚠️ HIER war Martins Fehler zu Hause. Diese Zeile stand bedingungslos —
   *    und weil Hochscrollen das Nachladen aelterer Nachrichten ausloest,
   *    riss ihn jede nachgeladene Bild-Blase ans Ende zurueck. Bei 89
   *    Bildern in 177 Blasen war das ein Dauerzucken.
   */
  fuehreChatNach();
}

/** 16.10.2026: die Bilder am Merkmal (live vom Bus-Feld `bilder`, aus dem lokalen Verlauf) — nur beim Schmied und nur, wenn eine gültige Adresse dabei ist (shared/schmied-bilder.ts). */
function schmiedBilderVom(vomSchmied: boolean, traeger: { bilder?: unknown }): Pick<BlasenMerkmal, "bilder"> {
  const bilder = vomSchmied ? bilderAus(traeger.bilder) : [];
  return bilder.length ? { bilder } : {};
}

/** 16.10.2026: die Bilder eines Schmied-Beitrags groß — Lightbox mit Blättern, beginnend beim getippten (lib/schmied-bilder.ts). */
function oeffneSchmiedBilder(bilder: SchmiedBild[], index: number): void {
  void import("./lib/lightbox.js").then(({ openGallery }) => {
    openGallery(
      bilder.map((b, i) => ({
        id: `schmied-${i}`,
        url: b.url,
        mime: "image/*",
        caption: b.beschriftung ?? SCHMIED_BILD_ALT,
        source: "generated",
        createdAt: new Date().toISOString(),
      })),
      index,
    );
  });
}

function openLightboxStandalone(url: string, caption?: string | null): void {
  // Single-Bild-Lightbox via Galerie-Modul. Items-Liste enthält nur
  // dieses eine Bild → keine Prev/Next-Buttons, nur Close + Caption.
  void import("./lib/lightbox.js").then(({ openGallery }) => {
    openGallery(
      [
        {
          id: "single",
          url,
          mime: "image/*",
          caption: caption ?? null,
          source: "user",
          createdAt: new Date().toISOString(),
        },
      ],
      0,
    );
  });
}

async function attachImageToChat(
  /**
   * 2026-08-28: Eine LISTE. Bis dahin ein einzelnes Bild — und wer mehrere
   * anhaengte, verlor alle bis auf eines.
   */
  items: UploadDto[],
  caption?: string,
  // Pre.537: Beleg-Modus — die User-Bubble zeigt die kurze, menschliche
  // `caption` (z.B. „Beleg: Geschäftsessen"), an den Bot geht aber die
  // ausführliche `botMessage` mit dem Konto-Hint. Ohne botMessage = wie bisher.
  botMessage?: string,
): Promise<void> {
  if (items.length === 0) return;
  switchToTab("chat");
  const effectiveCaption = caption ?? items[0].caption ?? null;
  // Je Bild eine Blase — so sieht man beim Zurueckscrollen, was man geschickt
  // hat. Der Begleittext haengt an der LETZTEN, damit er unter den Bildern
  // steht und nicht zwischen ihnen.
  items.forEach((it, i) => {
    const beschriftung = i === items.length - 1 ? effectiveCaption : null;
    appendImageBubble(it.url, "user", beschriftung);
    pushHistory({
      kind: "image",
      image: { url: it.url, caption: beschriftung, from: "user" },
      ts: Date.now(),
    });
  });
  const auftrag =
    botMessage ??
    effectiveCaption ??
    (items.length === 1
      ? "Was siehst du auf diesem Bild?"
      : `Was siehst du auf diesen ${items.length} Bildern?`);
  if (mode === "voice") {
    // ⚠️ Im Anruf sieht das Modell heute GAR KEIN Bild: der Datenkanal traegt
    // nur den Text "[Bild: …]", und der Adapter plaettet jede Nachricht auf
    // reinen Text (agent/llm-anthropic.ts, agent/llm-gemini.ts). Solange das
    // so ist, waere "schau es dir an" eine Behauptung. Wir schicken die
    // Bilder trotzdem einzeln weiter, damit der Weg vollstaendig bleibt.
    for (const it of items) {
      try {
        await client.sendImageInput(it.url, auftrag);
      } catch (err) {
        appendBubble(
          "system",
          `Bild konnte nicht an den Bot gesendet werden: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
  } else {
    // EIN Zug mit allen Bildern — nicht N Zuege. Sonst beantwortet Vespera
    // jedes Bild einzeln, ohne sie miteinander in Beziehung zu setzen.
    // „An: Schmied“ gilt auch für Bilder (Beleg-Weg mit botMessage bleibt bei Vespera).
    const wahl = botMessage === undefined ? empfaengerWahl : { anFeld: () => undefined, verbraucht: () => undefined };
    await sendeMitWahl(wahl, (an, angenommen) =>
      sendChatHttp({
        message: auftrag,
        imageUrls: items.map((i) => i.url),
        caption: effectiveCaption ?? undefined,
        onAngenommen: angenommen,
        ...(an ? { an } : {}),
      }),
    );
  }
}

// --- Pre.538: Deterministische Beleg-Schnellbuchung (Foto → Konto → Fertig) -
// Kein Bot. Analysiert den Beleg, zeigt eine kompakte Bestätigungskarte mit
// dem vorgewählten Konto, ein Klick „Buchen" → gebucht. Das Ergebnis läuft
// über eine persistierte receipt_booked-Card (callId-dedupliziert) → auf allen
// Geräten identisch.

interface AnalyzedReceiptDto {
  ok: boolean;
  error?: string;
  upload_id?: string;
  upload_url?: string;
  vendor?: string | null;
  gross_amount?: number | null;
  net_amount?: number | null;
  tax_amount?: number | null;
  tax_rate?: number | null;
  date?: string | null;
  invoice_number?: string | null;
}

function fmtEurLabel(v?: number | null): string {
  return typeof v === "number" ? `${v.toFixed(2)} €` : "—";
}

async function quickBookFlow(
  item: UploadDto,
  category: ReceiptCategory,
  note: string,
): Promise<void> {
  switchToTab("chat");
  const cardEl = document.createElement("div");
  cardEl.className = "bubble tool-card tool-receipt-confirm";
  const status = document.createElement("div");
  status.className = "qbc-status";
  status.textContent = "Beleg wird gelesen …";
  cardEl.appendChild(status);
  refs.chat.appendChild(cardEl);
  fuehreChatNach();

  let analyzed: AnalyzedReceiptDto;
  try {
    const res = await fetch("/api/expense/analyze", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ upload_id: item.id }),
    });
    analyzed = (await res.json()) as AnalyzedReceiptDto;
  } catch (err) {
    status.textContent = `Beleg-Analyse fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`;
    return;
  }
  if (!analyzed.ok) {
    status.textContent = `Beleg-Analyse fehlgeschlagen: ${analyzed.error ?? "unbekannt"}`;
    return;
  }
  renderQuickConfirm(cardEl, analyzed, category, note, item);
}

function renderQuickConfirm(
  cardEl: HTMLElement,
  a: AnalyzedReceiptDto,
  category: ReceiptCategory,
  note: string,
  item: UploadDto,
): void {
  cardEl.innerHTML = "";

  const thumbUrl = a.upload_url ?? item.url;
  if (thumbUrl) {
    const tw = document.createElement("a");
    tw.className = "receipt-card-thumb receipt-booked-thumb";
    tw.href = thumbUrl;
    tw.target = "_blank";
    tw.rel = "noopener";
    const img = document.createElement("img");
    img.src = thumbUrl;
    img.alt = "Beleg";
    img.loading = "lazy";
    tw.appendChild(img);
    cardEl.appendChild(tw);
  }

  const head = document.createElement("div");
  head.className = "receipt-booked-head";
  const vendor = document.createElement("div");
  vendor.className = "receipt-card-vendor";
  vendor.textContent = a.vendor ?? "Beleg";
  head.appendChild(vendor);
  const amount = document.createElement("div");
  amount.className = "receipt-card-amount";
  amount.textContent = fmtEurLabel(a.gross_amount);
  head.appendChild(amount);
  const acct = document.createElement("div");
  acct.className = "receipt-booked-account";
  acct.textContent = `→ ${category.account_number} ${category.account_name}`;
  head.appendChild(acct);
  const meta = document.createElement("div");
  meta.className = "receipt-card-meta";
  const mp: string[] = [];
  if (a.date) mp.push(a.date);
  if (note) mp.push(note);
  if (mp.length) meta.textContent = mp.join(" · ");
  head.appendChild(meta);
  cardEl.appendChild(head);

  const statusLine = document.createElement("div");
  statusLine.className = "qbc-status";
  cardEl.appendChild(statusLine);

  const actions = document.createElement("div");
  actions.className = "qbc-actions";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "icm-btn icm-btn--ghost";
  cancelBtn.textContent = "Abbrechen";
  const bookBtn = document.createElement("button");
  bookBtn.type = "button";
  bookBtn.className = "icm-btn icm-btn--primary";
  bookBtn.textContent = "Buchen";
  actions.appendChild(cancelBtn);
  actions.appendChild(bookBtn);
  cardEl.appendChild(actions);

  cancelBtn.addEventListener("click", () => {
    cardEl.remove();
  });

  bookBtn.addEventListener("click", () => {
    void (async () => {
      bookBtn.disabled = true;
      cancelBtn.disabled = true;
      statusLine.textContent = "wird gebucht …";
      let out: {
        ok: boolean;
        error?: string;
        expense_id?: number | null;
        card?: unknown;
      };
      try {
        const res = await fetch("/api/expense/quick-book", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            upload_id: a.upload_id ?? item.id,
            account_id: category.account_id,
            account_number: category.account_number,
            account_name: category.account_name,
            gross_amount: a.gross_amount,
            net_amount: a.net_amount,
            tax_amount: a.tax_amount,
            tax_rate: a.tax_rate,
            expense_date: a.date,
            vendor: a.vendor,
            invoice_number_ext: a.invoice_number,
            note: note || undefined,
            upload_url: a.upload_url ?? item.url,
          }),
        });
        out = await res.json();
      } catch (err) {
        statusLine.textContent = `Buchung fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`;
        bookBtn.disabled = false;
        cancelBtn.disabled = false;
        return;
      }
      if (!out.ok) {
        statusLine.textContent = `Buchung fehlgeschlagen: ${out.error ?? "unbekannt"}`;
        bookBtn.disabled = false;
        cancelBtn.disabled = false;
        return;
      }
      // Bestätigungskarte entfernen. Die finale „Verbucht"-Zeile + receipt_
      // booked-Card kommt über den Cross-Device-SSE-Stream (der Server
      // persistiert + pusht sie in persistBookedReceipt) — so rendert sie auf
      // ALLEN Geräten identisch (Handy + Desktop), kein Doppel-Render.
      cardEl.remove();
    })();
  });
}

// Pre.773: Foto vor dem Upload verkleinern. Grund (per nginx-Log belegt): große
// iPhone-Fotos (5-12 MB) brechen bei schwachem Mobilfunk (z.B. am Berg) ab →
// HTTP 499 ("client closed connection"). Max 1600px Kante, JPEG ~0.82. Decodet
// das Bild nicht (exotisches Format): Original zurück (kein Risiko).
async function compressImageForUpload(file: File): Promise<Blob> {
  if (!file.type.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const maxEdge = 1600;
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.82),
    );
    return blob && blob.size > 0 && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

// Ticket 221 (Web): Bildschirm auf Freigabe teilen -> ein Frame als Bild in
// den Chat, damit Vespera das UI per Vision selbst sehen/pruefen kann.
// Ticket 221: „Bildschirm zeigen"-Knopf ins Menue (Drawer) haengen.
// 2026-07-10 (Martin, Aufraeumen): Knopf ENTFERNT — nie genutzt, verunsichert
// nur. Die Funktion bleibt per Sprache erreichbar (screen-Tools/Desktop-
// Daemon). Early-return statt Loeschung, falls er je zurueckkommen soll.
// (Code am 14.07. entfernt — war seit 10.07. hinter return; unerreichbar
// und fiel im neuen Frontend-Typecheck auf. Bei Bedarf: git-Historie.)

async function uploadAndAttachFile(file: File): Promise<void> {
  if (!file.type.startsWith("image/")) {
    appendBubble("system", "Nur Bilder werden gerade unterstützt.");
    return;
  }
  if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
    appendBubble("system", "Bild ist größer als 10 MB.");
    return;
  }
  // Pre.526/537: Kommt der Upload über „Beleg erfassen", läuft der Beleg-
  // Modus: statt generischer Bild-Chips zeigt die Modal Kategorie-Schnell-
  // buttons (nach Häufigkeit sortiert), das Textfeld ist nur Zusatzinfo.
  const isReceiptCapture = consumeReceiptCapturePending();
  // Ticket 1614: Kam der Upload ueber "Ankermoment festhalten", bekommt das
  // Bild einen Begleittext mit — daran erkennt Vespera die Absicht und fragt
  // nach Gefuehl und Situation, statt das Bild nur zu beschreiben.
  const isAnkerCapture = consumeAnkerCapturePending();

  // 2026-07-24 (Martin, Claude-App-Vorlage): normaler Bild-Anhang OHNE Modal —
  // Bild hochladen, dann als Thumbnail in den Composer legen; der Nutzer
  // schreibt/diktiert seinen Text direkt dort und sendet wie gewohnt.
  // (Beleg-Erfassung behaelt ihr Modal mit den Kategorie-Schnellbuttons.)
  if (!isReceiptCapture) {
    const localUrl = URL.createObjectURL(file);
    // Platzhalter DAZU, nicht statt der anderen — sonst verschwinden beim
    // zweiten Bild die schon fertigen aus der Reihe.
    ladendeAnhaenge.add(localUrl);
    renderComposerAttach(); // sofortiges Feedback (laedt)
    try {
      const blob = await compressImageForUpload(file);
      const name =
        blob === file
          ? file.name
          : `${file.name.replace(/\.[^.]+$/, "") || "bild"}.jpg`;
      const fd = new FormData();
      fd.append("file", blob, name);
      const ac = new AbortController();
      const t = setTimeout(() => ac.abort(), 60_000);
      let item: UploadDto;
      try {
        const res = await fetch("/api/upload", {
          method: "POST",
          credentials: "same-origin",
          body: fd,
          signal: ac.signal,
        });
        if (!res.ok) throw new Error(`upload failed: ${res.status}`);
        item = (await res.json()) as UploadDto;
      } finally {
        clearTimeout(t);
      }
        ladendeAnhaenge.delete(localUrl);
        fuegePendingBildHinzu(item);
      // Ticket 1614: Kam das Bild ueber "Ankermoment festhalten", steht die
      // Absicht schon im Eingabefeld. Martin ergaenzt Gefuehl und Situation
      // in eigenen Worten und sendet — Vespera legt daraus den Anker an.
      //
      // BEWUSST NUR VORGESCHLAGEN, nicht abgeschickt: Was den Moment
      // ausmacht, weiss nur er. Ein Formular mit Pflichtfeldern haette
      // dasselbe abgefragt und dabei das Erzaehlen verhindert.
      if (isAnkerCapture) {
        const feld = document.getElementById(
          "text-input-field",
        ) as HTMLTextAreaElement | null;
        if (feld && !feld.value.trim()) {
          feld.value =
            "Das ist ein Ankermoment. Gefühl: ";
          feld.focus();
          // Cursor ans Ende, damit er sofort weiterschreiben kann.
          feld.setSelectionRange(feld.value.length, feld.value.length);
          feld.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    } catch (err) {
      clearPendingComposerImage();
      appendBubble(
        "system",
        `Bild-Upload fehlgeschlagen: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    } finally {
      URL.revokeObjectURL(localUrl);
    }
    return;
  }

  // Vor dem Upload: Modal mit Preview anzeigen.
  const previewUrl = URL.createObjectURL(file);
  let captionResult: ImageCaptionResult;
  try {
    captionResult = await askImageCaption({
      previewUrl,
      filename: file.name,
      receiptCategories: isReceiptCapture
        ? sortedReceiptCategories()
        : undefined,
    });
  } finally {
    URL.revokeObjectURL(previewUrl);
  }
  if (captionResult.cancelled) return;

  // Beleg-Modus: kurze menschliche Bubble-Caption + ausführliche Bot-Message
  // mit dem Konto-Hint zusammenbauen; Kategorie-Häufigkeit hochzählen.
  let bubbleCaption: string | undefined = captionResult.caption || undefined;
  let botMessage: string | undefined;
  const receiptCategory =
    isReceiptCapture && captionResult.category ? captionResult.category : null;
  const receiptNote = isReceiptCapture ? captionResult.caption.trim() : "";
  if (isReceiptCapture) {
    const cat = receiptCategory;
    const note = receiptNote;
    if (cat) bumpReceiptCategory(cat.key);
    // Ohne Kategorie (nur „Senden"): konversationeller Bot-Fallback.
    const parts: string[] = [
      "Beleg verbuchen — analysieren und in die Buchhaltung eintragen.",
    ];
    if (note) parts.push(`Zusatzinfo: ${note}`);
    botMessage = parts.join(" ");
    bubbleCaption = note ? `Beleg — ${note}` : "Beleg";
  }

  // Pre.773: vor dem Upload verkleinern (gegen Abbruch/499 bei großen Fotos +
  // schwachem Netz). Bei Decode-Fehler bleibt das Original (kein Risiko).
  const uploadBlob = await compressImageForUpload(file);
  const uploadName =
    uploadBlob === file
      ? file.name
      : `${file.name.replace(/\.[^.]+$/, "") || "beleg"}.jpg`;
  const fd = new FormData();
  fd.append("file", uploadBlob, uploadName);
  if (bubbleCaption) fd.append("caption", bubbleCaption);
  let item: UploadDto;
  // Pre.772: SOFORTIGES Feedback + Timeout. Vorher kam bis zum Ende der fetch()
  // keine Rückmeldung — bei hängendem Upload wartete der User endlos („wartet
  // auf etwas, das vielleicht nie passiert"). Jetzt: sofort eine „wird
  // hochgeladen…"-Zeile + 60s-Abbruch mit klarer Fehlermeldung.
  appendBubble("system", "Beleg wird hochgeladen…");
  const pendingBubble = refs.chat.lastElementChild;
  const ac = new AbortController();
  const uploadTimer = setTimeout(() => ac.abort(), 60_000);
  try {
    const res = await fetch("/api/upload", {
      method: "POST",
      credentials: "same-origin",
      body: fd,
      signal: ac.signal,
    });
    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`upload failed: ${res.status} ${detail.slice(0, 200)}`);
    }
    item = (await res.json()) as UploadDto;
  } catch (err) {
    const aborted = err instanceof Error && err.name === "AbortError";
    appendBubble(
      "system",
      aborted
        ? `Upload-Timeout — bitte nochmal versuchen (Netz langsam oder Datei zu groß? Max ${uploadGrenzeMb()} MB).`
        : `Upload fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
    );
    return;
  } finally {
    clearTimeout(uploadTimer);
    pendingBubble?.remove();
  }

  // Pre.538: Kategorie gewählt → deterministischer Foto→Konto→Fertig-Flow
  // (kein Bot). Sonst (nur „Senden" ohne Chip) der konversationelle Pfad.
  if (receiptCategory) {
    await quickBookFlow(item, receiptCategory, receiptNote);
    return;
  }
  await attachImageToChat([item], bubbleCaption, botMessage);
}

async function attachFileToChat(item: FileDto): Promise<void> {
  switchToTab("chat");
  appendFileBubble(item);
  pushHistory({
    kind: "file",
    fileItem: item,
    ts: Date.now(),
  });
  // Im Voice-Mode könnten wir später ein file_input über Datenkanal
  // schicken; jetzt minimal: nur die Card zeigen, der Bot sieht sie nicht.
  // Im Chat-Mode senden wir einen Hinweis-Text mit der URL.
  if (mode !== "voice") {
    // 2026-07-29: Diese Nachricht wird GESPEICHERT und erscheint über den
    // Abgleich als Blase im Verlauf — vorher stand dort der komplette
    // technische Text samt rohem /uploads/-Pfad, mitten im Gespräch.
    // Jetzt: der Auftrag des Nutzers zuerst und in seinen Worten, die
    // Adresse hinten dran, wo das Modell sie findet und das Auge sie
    // überliest.
    const auftrag = (item.caption ?? "").trim();
    // 2026-07-29 (Martin: "Hier noch die Darstellung, das ist etwas
    // bughaft"): Die Karte zeigt Datei, Größe UND den Auftrag bereits an —
    // die Nachricht daneben wiederholte alles ein zweites Mal, samt rohem
    // /uploads/-Pfad mitten im Gespräch. Das Modell BRAUCHT den Pfad, das
    // Auge nicht.
    // Deshalb trägt diese Nachricht jetzt einen Marker: Sie geht vollständig
    // ans Modell, wird aber nicht als Blase gezeichnet. Die Karte IST die
    // Darstellung.
    // „An: Schmied“ gilt auch für Dateien (Martins Fall: PDF an den Schmied).
    await sendeMitWahl(empfaengerWahl, (an, angenommen) =>
      sendChatHttp({
        message:
          DATEI_MARKER +
          (auftrag
            ? `${auftrag}\n\n(${fileArtLabel(item.mime)}: ${item.url})`
            : `${fileArtLabel(item.mime)} angehängt: ${item.url}\nSag mir, was ich damit tun soll.`),
        onAngenommen: angenommen,
        ...(an ? { an } : {}),
      }),
    );
  }
}

/** Lesbare Art einer Datei — steht in der Karte, wo sonst der Name stuende. */
function fileArtLabel(mime: string): string {
  const m = (mime || "").toLowerCase();
  if (m.includes("pdf")) return "PDF-Dokument";
  if (m.startsWith("image/")) return "Bild";
  if (m.startsWith("audio/")) return "Audio-Datei";
  if (m.startsWith("video/")) return "Video";
  if (m.includes("word") || m.includes("document")) return "Dokument";
  if (m.includes("sheet") || m.includes("excel")) return "Tabelle";
  if (m.includes("zip") || m.includes("compressed")) return "Archiv";
  if (m.startsWith("text/")) return "Textdatei";
  return "Datei";
}

async function uploadAndAttachAnyFile(file: File): Promise<void> {

  if (file.size > MAX_GENERIC_UPLOAD_BYTES) {
    appendBubble("system", `Datei ist größer als ${uploadGrenzeMb()} MB.`);
    return;
  }
  // Bilder gehen über den Bild-Flow mit Preview-Modal.
  if (file.type.startsWith("image/")) {
    return uploadAndAttachFile(file);
  }
  // Pre.488: Statt window.prompt() (hässlich, langer Filename wraps) ein
  // echtes Modal mit Datei-Icon, Größe, MIME-Type + Quick-Suggestions.
  const { askFileCaption } = await import("./lib/file-caption-modal.js");
  const result = await askFileCaption({
    filename: file.name,
    sizeBytes: file.size,
    mimeType: file.type || "application/octet-stream",
    // 23.09.2026, Ticket 2498: am iPhone kommt eine Auswahl nur aus EINER
    // Quelle — „1 PDF + Fotos" wird erst hier, im zweiten Schritt, EIN Vorgang.
    weitereWaehlen: () => waehleWeitere(vorgangsUmgebung.annehmen()),
  });
  if (result.cancelled) return;
  if (result.weitere) {
    await starteDateiVorgang([file, ...result.weitere], vorgangsUmgebung, result.caption);
    return;
  }
  const caption = result.caption;
  const fd = new FormData();
  fd.append("file", file);
  if (caption.trim()) fd.append("caption", caption.trim());
  let item: FileDto;
  try {
    const res = await fetch("/api/upload", {
      method: "POST",
      credentials: "same-origin",
      body: fd,
    });
    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`upload failed: ${res.status} ${detail.slice(0, 200)}`);
    }
    item = (await res.json()) as FileDto;
  } catch (err) {
    appendBubble(
      "system",
      `Upload fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
    );
    return;
  }
  await attachFileToChat(item);
}

/*
 * Ticket 2680 (28.09.2026): der sichtbare Bildschirm als Bild an die
 * Nachricht — über denselben Eingang wie „Bild hochladen“ (nimmBilder →
 * uploadAndAttachFile → /api/upload), also mit Platz-Grenze, Verkleinern und
 * Vorschau im Eingabefeld. Nur die eigene Seite (lib/bildschirmfoto.ts).
 *
 * Nachbesserung 28.09.2026: auch aus dem Menü, also von jeder Seite aus —
 * das Plus-Menü sitzt im Gespräch, sein Bild zeigte darum immer nur das
 * Gespräch. Von woanders geht es NACH der Aufnahme ins Gespräch
 * (`insGespraech`): dort steht die Vorschau im Eingabefeld, und was Martin
 * dazu sagt, geht mit dem Bild hinaus.
 */
async function bildschirmfotoAnhaengen(o: { insGespraech?: boolean } = {}): Promise<void> {
  // Erst prüfen, dann aufnehmen — sonst hieße es „aufgenommen“ und gleich
  // danach „geht nicht“.
  if (reiheVollGemeldet()) return;
  try {
    const abbild = baueAbbild();
    if (o.insGespraech) goToView("chat");
    // Erst NACH dem Klonen melden — sonst stünde die Meldung selbst im Bild.
    showToast("Bildschirmfoto aufgenommen", "info", 2500);
    await nimmBilder([await abbild.alsDatei()]);
  } catch (err) {
    console.warn("[bildschirmfoto]", err);
    showToast("Bildschirmfoto ging gerade nicht.", "warning");
  }
}

// --- Plus-Sheet (Bottom-Sheet mit Aktionen) ----------------------------
const plusBtn = requireEl<HTMLButtonElement>("#plus-btn");
const plusSheet = requireEl<HTMLElement>("#plus-sheet");
const hiddenFile = requireEl<HTMLInputElement>("#hidden-file-input");
const hiddenCamera = requireEl<HTMLInputElement>("#hidden-camera-input");
const hiddenFileAny = requireEl<HTMLInputElement>("#hidden-file-input-any");

/**
 * Alles im Plus-Menü, was die Tastatur erreichen kann — in Reihenfolge.
 * 21.10.2026: ohne Verborgenes (zugeklapptes „Mehr“, nicht freigeschaltete
 * Einträge) — sonst wäre der letzte Knopf der Reihe einer, den es nicht gibt,
 * und Tab fiele aus dem Blatt.
 */
function plusSheetFokussierbare(): HTMLElement[] {
  return [...plusSheet.querySelectorAll<HTMLElement>("button:not([disabled])")].filter(
    (b) => !b.closest("[hidden]"),
  );
}

/** 21.10.2026: „Mehr“ im Plus-Menü auf- oder zuklappen (Seltenes: Beispiele, Ankermoment). */
function zeigePlusMehr(auf: boolean): void {
  const knopf = plusSheet.querySelector<HTMLButtonElement>("#plus-mehr-knopf");
  const liste = plusSheet.querySelector<HTMLElement>("#plus-mehr");
  if (!knopf || !liste) return;
  knopf.setAttribute("aria-expanded", String(auf));
  liste.hidden = !auf;
}

/**
 * `ueberTastatur`: das Plus kam über Enter/Leertaste (Klick mit `detail === 0`). 21.10.2026 (Martin, Bild): Öffnete ein Finger
 * das Blatt, stand die erste Kachel mit Fokusring da und sah „ausgewählt“ aus — dann geht der Fokus unsichtbar auf den
 * Titel (tabindex -1), Tab führt von dort zur ersten Aktion.
 */
function openPlusSheet(ueberTastatur = true): void {
  zeigePlusMehr(false);
  plusSheet.hidden = false;
  // 15.10.2026: Der Fokus geht ins Blatt, damit Tastatur und Vorleser nicht hinter dem Blatt weiterlaufen; Escape und Tab unten.
  if (ueberTastatur) plusSheetFokussierbare()[0]?.focus();
  else plusSheet.querySelector<HTMLElement>("#plus-sheet-title")?.focus();
  // 2026-07-20 (Martin, Rueckmeldung am Abend): die Statuszeile bleibt
  // PERMANENT im Fussbereich sichtbar — "ob verbunden und welches Modell"
  // ist Kern-Info, kein Menue-Detail. Der fruehere DOM-Umzug ins
  // Plus-Menue ist zurueckgenommen; hier wohnt nur die Mikrofon-Wahrheit.
  // Mikrofon-Wahrheit (Martin, "Was ist die Wahrheit?"): der orange
  // iOS-Punkt bedeutet nur "Mikrofon-Session offen" — hier steht
  // unmissverstaendlich, ob wirklich aufgenommen wird.
  const truth = document.getElementById("plus-mic-truth");
  if (truth) {
    truth.textContent = "";
    void import("./lib/native-recorder.js")
      .then(async (rec) => {
        await rec.syncFromNative().catch(() => {});
        const s = rec.getRecorderState();
        if (s === "recording" || s === "paused") {
          const startedAt = rec.getRecordingStartedAt();
          const seit = startedAt
            ? new Date(startedAt).toLocaleTimeString("de-AT", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "";
          truth.textContent =
            s === "paused"
              ? `Alltags-Aufnahme pausiert${seit ? ` (Start ${seit})` : ""}.`
              : `Alltags-Aufnahme läuft${seit ? ` seit ${seit}` : ""}.`;
          truth.classList.add("is-recording");
        } else {
          // Zwei Zeilen am Telefon, nicht drei — sonst rutscht „Schließen“
          // unter den Rand (15.10.2026, Schnell-Block darüber).
          truth.textContent =
            "Keine Aufnahme aktiv. Der orange Punkt oben heißt nur: " +
            "Mikrofon warm für Sofort-Sprechen.";
          truth.classList.remove("is-recording");
        }
      })
      .catch(() => {
        truth.textContent = "";
      });
  }
}
function closePlusSheet(): void {
  // Stand der Fokus im Blatt, geht er zurück auf das Plus — sonst landet er
  // auf <body> und die Tastatur fängt oben auf der Seite neu an.
  const drin = plusSheet.contains(document.activeElement);
  plusSheet.hidden = true;
  if (drin) plusBtn.focus();
}

plusBtn.addEventListener("click", (ev) => openPlusSheet(ev.detail === 0));
plusSheet.querySelectorAll("[data-sheet-close]").forEach((el) => {
  el.addEventListener("click", () => closePlusSheet());
});
// 15.10.2026: Escape schließt; Tab läuft im Blatt im Kreis (aria-modal), statt
// hinter dem Blatt ins Eingabefeld zu fallen. Der Lauscher sitzt am Blatt
// selbst — er hört nur, solange der Fokus darin ist, und braucht kein An/Ab.
plusSheet.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape") {
    ev.preventDefault();
    closePlusSheet();
    return;
  }
  if (ev.key !== "Tab") return;
  const reihe = plusSheetFokussierbare();
  if (reihe.length === 0) return;
  const erster = reihe[0];
  const letzter = reihe[reihe.length - 1];
  // Steht der Fokus auf dem Titel (nach einem Tipp aufs Plus), führt Tab hinein, Umschalt+Tab ans Ende — nie hinaus.
  if (!reihe.includes(document.activeElement as HTMLElement)) {
    ev.preventDefault();
    (ev.shiftKey ? letzter : erster).focus();
  } else if (ev.shiftKey && document.activeElement === erster) {
    ev.preventDefault();
    letzter.focus();
  } else if (!ev.shiftKey && document.activeElement === letzter) {
    ev.preventDefault();
    erster.focus();
  }
});
plusSheet.querySelector("#plus-mehr-knopf")?.addEventListener("click", (ev) => {
  const knopf = ev.currentTarget as HTMLElement;
  zeigePlusMehr(knopf.getAttribute("aria-expanded") !== "true");
});

plusSheet
  .querySelectorAll<HTMLButtonElement>(".sheet-action")
  .forEach((btn) => {
    btn.addEventListener("click", () => {
      const action = btn.dataset.action;
      closePlusSheet();
      if (action === "upload-image") {
        hiddenFile.click();
      } else if (action === "take-photo") {
        hiddenCamera.click();
      } else if (action === "bildschirmfoto") {
        // Das Menü ist oben schon zu (hidden) — im Bild steht der Bildschirm
        // darunter, nicht das Menü.
        void bildschirmfotoAnhaengen();
      } else if (action === "open-library") {
        switchToTab("library");
        void ensureLibraryView().refresh();
      } else if (action === "upload-file") {
        hiddenFileAny.click();
      } else if (action === "generate-image") {
        // 2026-07-24 (Martin): statt des winzigen Browser-Pop-ups schaltet
        // "Bild erzeugen" den Composer um — tippen ODER diktieren, dann
        // normal senden.
        setGenerateImageMode(true);
      } else if (action === "open-examples") {
        // Pre.360: Anwendungsbeispiele-Tab direkt aus dem Plus-Sheet
        // erreichbar — User kann ein Beispiel wählen und der Prompt
        // landet im Eingabefeld.
        goToView("examples");
      } else if (action === "open-settings") {
        // 15.10.2026: Martin suchte „Getipptes vorlesen“ in den Einstellungen
        // — von „Schnell einstellen“ führt darum ein Weg zu allen.
        goToView("settings");
      } else if (action === "anker-capture") {
        // Ticket 1614: Ankermoment. Derselbe Weg wie die Beleg-Erfassung —
        // Kamera auf, und der folgende Upload traegt die Absicht mit. Kein
        // zweiter Bild-Weg: Martins Wunsch war ausdruecklich, den vorhandenen
        // Plus-Knopf mitzubenutzen.
        void triggerAnkerCapture();
      }
      // 21.10.2026: „Material einspeisen“ ist aus dem Plus-Menü raus — derselbe
      // Weg (openMaterialSheet) steht im Menü unter „Material“ (#open-material-btn).
    });
  });

// --- Bildschirmfoto aus dem Menü (Ticket 2680, Nachbesserung 28.09.2026) ---
// Das Menü ist auf jeder Seite zu erreichen, das Plus-Menü nur im Gespräch.
{
  const knopf = document.getElementById("nav-bildschirmfoto");
  // Das Zeichen aus dem Plus-Menü — eine Zeichnung, nicht zwei.
  const zeichnung = plusSheet.querySelector('[data-action="bildschirmfoto"] svg');
  if (knopf && zeichnung) {
    const kopie = zeichnung.cloneNode(true) as Element;
    // Im Plus-Menü trägt die Hülle das aria-hidden, hier steht es allein.
    kopie.setAttribute("aria-hidden", "true");
    knopf.prepend(kopie);
  }
  knopf?.addEventListener("click", () => {
    // Am Telefon deckt das Menü die Seite: es geht zu, bevor aufgenommen
    // wird. Am Schreibtisch ist es die Leiste daneben und gehört ins Bild.
    if (!isDesktop()) closeDrawer();
    void bildschirmfotoAnhaengen({ insGespraech: true });
  });
}

// Pre.537: Beleg-Schnellkategorien → echte Panel-Konten. Ein Tipp im Beleg-
// Modus gibt das Konto vor, Bärli muss nur noch Betrag/Vendor bestätigen.
// Reihenfolge sortiert sich automatisch nach Nutzungshäufigkeit (localStorage).
// (Cross-Device-Sync der Reihenfolge ist ein möglicher Folgeschritt — aktuell
// pro Gerät; der Beleg-Flow läuft ohnehin meist am Handy.)
const RECEIPT_CATEGORIES: ReadonlyArray<ReceiptCategory> = [
  {
    key: "essen",
    label: "Geschäftsessen",
    account_number: "7260",
    account_name: "Bewirtung / Geschäftsessen",
    account_id: 415,
  },
  {
    key: "post",
    label: "Post / Versand",
    account_number: "7250",
    account_name: "Verpackung und Versand",
    account_id: 406,
  },
  {
    key: "kfz",
    label: "Tanken / KFZ",
    account_number: "7050",
    account_name: "Fahrzeugkosten",
    account_id: 362,
  },
  {
    key: "reise",
    label: "Reise",
    account_number: "7150",
    account_name: "Reisekosten",
    account_id: 382,
  },
  {
    key: "hostel",
    label: "Hostel",
    account_number: "7120",
    account_name: "Hostel Hollenthon",
    account_id: 376,
  },
  {
    key: "ki",
    label: "KI / Software",
    account_number: "7235",
    account_name: "KI / AI-Services",
    account_id: 412,
  },
  {
    key: "buero",
    label: "Büro / Werbung",
    account_number: "7110",
    account_name: "Büro- und Werbeaufwand",
    account_id: 374,
  },
  {
    key: "fortbildung",
    label: "Fortbildung",
    account_number: "7160",
    account_name: "Fortbildung",
    account_id: 384,
  },
  {
    key: "literatur",
    label: "Fachliteratur",
    account_number: "7240",
    account_name: "Fachliteratur und Medien",
    account_id: 404,
  },
  {
    key: "survival",
    label: "Survival",
    account_number: "7170",
    account_name: "Survival",
    account_id: 386,
  },
  {
    key: "gesundheit",
    label: "Gesundheit",
    account_number: "7180",
    account_name: "Außergewöhnliche Belastungen",
    account_id: 388,
  },
  {
    key: "internet",
    label: "Internet / Telefon",
    account_number: "7020",
    account_name: "Internet und Telefon",
    account_id: 356,
  },
];

const RECEIPT_CAT_FREQ_KEY = "receiptCatFreq.v1";

function readReceiptCatFreq(): Record<string, number> {
  try {
    const raw = localStorage.getItem(RECEIPT_CAT_FREQ_KEY);
    const obj = raw ? (JSON.parse(raw) as unknown) : {};
    return obj && typeof obj === "object"
      ? (obj as Record<string, number>)
      : {};
  } catch {
    return {};
  }
}

function bumpReceiptCategory(key: string): void {
  try {
    const freq = readReceiptCatFreq();
    freq[key] = (freq[key] ?? 0) + 1;
    localStorage.setItem(RECEIPT_CAT_FREQ_KEY, JSON.stringify(freq));
  } catch {
    /* localStorage voll/blockiert — Reihenfolge bleibt halt statisch */
  }
}

/** Kategorien nach Häufigkeit absteigend; bei Gleichstand stabile Default-Reihenfolge. */
function sortedReceiptCategories(): ReceiptCategory[] {
  const freq = readReceiptCatFreq();
  return RECEIPT_CATEGORIES.map((c, i) => ({ c, i }))
    .sort((a, b) => {
      const fa = freq[a.c.key] ?? 0;
      const fb = freq[b.c.key] ?? 0;
      return fb - fa || a.i - b.i;
    })
    .map((x) => x.c);
}

/*
 * ⚠️ 09.09.2026 — die Beleg-Erfassung (Pre.526) ist aus der Oberflaeche
 *    heraus. Martin: „Ich sehe immer noch Rechnungen im System."
 *    Sie war nie in Gebrauch: null Zeilen in `receipt_cache`, im Journal von
 *    sieben Tagen nur Startmeldungen. `triggerReceiptCapture` und der
 *    Serverweg /api/receipt/process stehen noch — nur fuehrt kein Knopf mehr
 *    dorthin. Wer sie ganz ausbauen will, faengt dort an.
 */

// Chat-Dock-Knopf: zurück in den Chat-Verlauf (schließt offene Overlays via
// goToView → closeAllOverlays, #195). Schneller Daumen-Weg aus jeder Unterseite.
// 2026-07-22 (Martin, "Menue muss leichter erreichbar sein"): der Slot
// oeffnet jetzt die Uebersicht — zurueck zum Chat fuehrt der schwebende
// Pfeil (in Views wie auf Vollseiten).
// 29.09.2026 (Ticket 2803): in den Einstellungen am Rechner fuehrt „Menü"
// denselben Weg wie „← Menü" in der Einstellungsliste (lib/einstellungen-
// rueckweg.ts) — dort steht die App-Leiste nicht, die openDrawer zeigen koennte.
// 28.10.2026: „Menü" öffnet überall die Seitenleiste (Zuhause, Chat, alle Bereiche) — auch in den
// Einstellungen, wo sie am Rechner als schmale Leiste daneben steht (dann geht der Fokus hinein).
refs.chatBtn.addEventListener("click", () =>
  menueAusDerFusszeile(document, openDrawer),
);
// 2026-09-01 (Martin): Dock „Aufnahme" zeigt die Alltagsaufzeichnungen.
// Widget-Stop hatte das Overlay geöffnet, Dock startete neu — Uhr lief,
// Header-Mikro nicht. Overlay nur, wenn wirklich etwas läuft.
document.getElementById("dock-record")?.addEventListener("click", () => {
  void import("./lib/alltag-view.js").then((m) => m.openAlltagAufnahmen());
});
/*
 * ⚠️⚠️ 22.09.2026 — GENAU HIER WAR DER KNOPF WEG.
 *
 * Martin: *„Uebrigens habe ich noch immer nicht entdeckt, wo man im Browser
 * auf der Webseite auf ,Rekorden' druecken kann."* Er hat nichts uebersehen:
 * diese Zeilen haben den Dock-Knopf im Browser ausgeblendet.
 *
 * Der Kommentar, der hier stand — „nativ-only … fuehrte nur zur Liste" —
 * war beim Schreiben richtig und ist es seit dem 17.09. nicht mehr. Seither
 * zeichnet der Browser auf. Die Begruendung ueberlebte ihren Grund, die
 * Regel blieb stehen.
 *
 * Versteckt wird jetzt nur noch, wo gar kein Mikrofon zu holen ist.
 */
void import("./lib/native-recorder.js").then((m) => {
  if (!m.kannAufzeichnen()) {
    const btn = document.getElementById("dock-record");
    btn?.classList.add("is-hidden");
  }
});

/**
 * Pre.526: Beleg-Erfassung. Öffnet den Camera-Input mit voreingestellter
 * Caption „verbuchen" — der Bot soll dann automatisch expense_analyze_upload
 * aufrufen und die Vorschläge präsentieren.
 *
 * Implementation: setzt einen Flag, der beim nächsten Upload die Caption
 * automatisch befüllt + sendet sie als User-Text-Hinweis an den Bot mit.
 */
let _receiptCapturePending = false;
let _ankerCapturePending = false;

/**
 * Ticket 1672 (2026-08-10): Wie lange eine gemerkte Absicht gilt.
 *
 * Vorher standen hier 30 Sekunden — und die reichten nur, wenn man das Foto
 * frisch aufnimmt. Wer aus der Mediathek eines aussucht, braucht laenger:
 * scrollen, ansehen, entscheiden. Dann verfiel die Absicht STILL. Das Bild
 * landete als gewoehnlicher Anhang, die Rueckfrage nach Gefuehl und Situation
 * blieb aus, und der Moment wurde kein Anker. Man merkte es nur daran, dass
 * nichts passierte — es sah nicht nach einem Fehler aus.
 *
 * Die Wurzel war nicht die Zahl, sondern die Bauform: Eine Absicht hing an
 * einer Stoppuhr statt am naechsten Bild. Sie gilt jetzt fuer den NAECHSTEN
 * Upload, so lange er auch dauert; verbraucht wird sie beim Einloesen
 * (consume…Pending). Die Frist ist nur noch ein grober Rueckfall fuer den
 * Fall, dass jemand die Kamera abbricht und Stunden spaeter ein ganz anderes
 * Bild schickt.
 */
const ABSICHT_VERFAELLT_MS = 10 * 60_000;
let _absichtVerfall: number | null = null;
function absichtVerfallLoeschen(): void {
  if (_absichtVerfall !== null) {
    clearTimeout(_absichtVerfall);
    _absichtVerfall = null;
  }
}
function absichtVerfallLegen(loeschen: () => void): void {
  if (_absichtVerfall !== null) clearTimeout(_absichtVerfall);
  _absichtVerfall = window.setTimeout(() => {
    _absichtVerfall = null;
    loeschen();
  }, ABSICHT_VERFAELLT_MS);
}

/**
 * Ticket 1614: Ankermoment festhalten — Kamera auf, Absicht merken.
 *
 * Wie die Beleg-Erfassung, mit anderem Ziel: Der Beleg geht in die
 * Buchhaltung, der Anker an Vespera mit der Bitte, Gefuehl und Situation zu
 * erfragen. Deshalb eine eigene Absicht statt eines gemeinsamen Schalters
 * mit Sonderfaellen.
 */
async function triggerAnkerCapture(): Promise<void> {
  _ankerCapturePending = true;
  absichtVerfallLegen(() => {
    _ankerCapturePending = false;
  });
  hiddenCamera.click();
}

function consumeAnkerCapturePending(): boolean {
  if (_ankerCapturePending) {
    _ankerCapturePending = false;
    absichtVerfallLoeschen();
    return true;
  }
  return false;
}

/** Pre.526: vom Upload-Handler abgefragt — soll die nächste Caption gesetzt sein? */
function consumeReceiptCapturePending(): boolean {
  if (_receiptCapturePending) {
    _receiptCapturePending = false;
    absichtVerfallLoeschen();
    return true;
  }
  return false;
}
(
  window as unknown as { __consumeReceiptCapturePending?: () => boolean }
).__consumeReceiptCapturePending = consumeReceiptCapturePending;

async function handleGenerateImagePrompt(prompt: string): Promise<void> {
  const userMsg = `Erzeuge ein Bild: ${prompt}`;
  appendBubble("user", userMsg);
  pushHistory({ kind: "user", text: userMsg, ts: Date.now() });
  // Bild-Generierung geht in beiden Modi: im Chat über /api/chat
  // (Tool-Use), im Voice ueber LiveKit-Datenkanal.
  if (mode === "voice" && connected) {
    try {
      await client.sendTextInput(userMsg);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      appendBubble("system", `Senden fehlgeschlagen: ${msg}`);
      // Pre.273: Wenn Senden fehlschlägt (z.B. WS disconnected), den
      // großen Crash-Banner anzeigen damit User es nicht übersieht.
      showCrashBanner(
        `Senden fehlgeschlagen — ${msg}. Verbindung neu aufbauen?`,
      );
    }
  } else {
    await sendChatHttp({ message: userMsg });
  }
}

/*
 * 2026-08-28: EIN Eingang fuer mehrere Bilder — Knopf, Einfuegen und Ablegen
 * sollen sich gleich verhalten.
 *
 * ⚠️ Die Falle, die beim Kartieren gefunden wurde: uploadAndAttachFile
 * verbraucht die gemerkte Absicht JE DATEI (Beleg, Ankermoment). Schickte man
 * fuenf Bilder durch die Schleife, fraesse das ERSTE die Absicht und die
 * uebrigen vier waeren gewoehnliche Anhaenge. Ein Beleg und ein Ankermoment
 * sind aber immer genau EIN Bild — also nimmt dieser Eingang in dem Fall auch
 * nur das erste und sagt es.
 */
async function nimmBilder(dateien: File[]): Promise<void> {
  if (dateien.length === 0) return;
  const mitAbsicht = _receiptCapturePending || _ankerCapturePending;
  if (mitAbsicht) {
    if (dateien.length > 1) {
      showToast("Dafür nehme ich nur das erste Bild.", "info");
    }
    await uploadAndAttachFile(dateien[0]);
    return;
  }
  if (reiheVollGemeldet()) return;
  const platz = freiePlaetze();
  const nehmen = dateien.slice(0, platz);
  if (dateien.length > platz) {
    showToast(
      `Ich nehme ${nehmen.length} von ${dateien.length} — mehr als ${MAX_ANHAENGE} gehen nicht auf einmal.`,
      "info",
    );
  }
  // Nacheinander, nicht parallel: der Server nimmt eine Datei je Anfrage,
  // und so bleibt die Reihenfolge der Anhaenge die des Auswaehlens.
  for (const d of nehmen) await uploadAndAttachFile(d);
}

hiddenFile.addEventListener("change", () => {
  void nimmBilder(Array.from(hiddenFile.files ?? []));
  hiddenFile.value = "";
});
hiddenCamera.addEventListener("change", () => {
  const file = hiddenCamera.files?.[0];
  if (file) void uploadAndAttachFile(file);
  hiddenCamera.value = "";
});
hiddenFileAny.addEventListener("change", () => {
  // 23.09.2026, Ticket 2498: ALLE gewaehlten Dateien, nicht nur die erste —
  // und derselbe Eingang wie Einfuegen und Ziehen (nimmEingefuegteDateien).
  const dateien = Array.from(hiddenFileAny.files ?? []);
  hiddenFileAny.value = "";
  void nimmEingefuegteDateien(dateien);
});

// --- Text-Input (Chat-Eingabe als Alternative zu Voice) ---------------
const textForm = requireEl<HTMLFormElement>("#text-input");
const textField = requireEl<HTMLTextAreaElement>("#text-input-field");
const textSendBtn = requireEl<HTMLButtonElement>("#text-input-send");

// 2026-07-24 (Martin, Claude-App-Vorlage): Bild-Anhang INLINE — kein Modal
// mehr. Das gewaehlte Bild erscheint als Thumbnail oben im Eingabe-Pill; der
// Text (oder das Diktat) wird direkt hier geschrieben und beim normalen
// Senden mitgeschickt.
/*
 * 2026-08-28 (Martin: "damit man auch mehrere Bilder hochladen kann"):
 * Der Composer haelt jetzt eine LISTE.
 *
 * ⚠️ Vorher war es ein einzelner Platz — und das war schon kaputt, nicht erst
 * unvollstaendig: Wer drei Bilder einfuegte, lud drei hoch, legte drei
 * Datensaetze an und schickte EINES. Die anderen zwei ueberschrieb es
 * lautlos (renderComposerAttach mit replaceChildren, dann Zuweisung).
 *
 * Die Obergrenze ist bewusst niedrig. Sie ist keine Byte-Grenze, sondern eine
 * Kosten- und Kontextgrenze: Ein auf 1600px verkleinertes Foto kostet das
 * Modell rund 2500 Token, und es reist in JEDEM weiteren Zug der warmen
 * Sitzung wieder mit. Fuenf Bilder sind rund 12.500 Token je Zug.
 */
// 23.09.2026, Ticket 2498: die Zahl wohnt in shared/limits.ts — Server
// (MAX_BILDER in chat.ts) und Datei-Vorgang lesen dieselbe.
const MAX_ANHAENGE = MAX_BILDER_JE_ZUG;
let pendingComposerImages: UploadDto[] = [];
const composerAttachTray = document.createElement("div");
composerAttachTray.id = "composer-attach-tray";
composerAttachTray.hidden = true;
{
  const pill = textField.parentElement;
  if (pill) pill.insertBefore(composerAttachTray, pill.firstChild);
}
/** Gerade hochladende Bilder — sie stehen als blasser Platzhalter in der Reihe. */
const ladendeAnhaenge = new Set<string>();

/**
 * Zeichnet ALLE Anhaenge. Vorher zeichnete diese Funktion genau einen und
 * loeschte dabei die uebrigen — das war der eigentliche Fehler.
 */
function renderComposerAttach(): void {
  composerAttachTray.replaceChildren();
  const eintraege: Array<{ url: string; laedt: boolean }> = [
    ...pendingComposerImages.map((i) => ({ url: i.url, laedt: false })),
    ...[...ladendeAnhaenge].map((u) => ({ url: u, laedt: true })),
  ];
  for (const e of eintraege) {
    const chip = document.createElement("div");
    chip.className = "composer-attach-chip";
    if (e.laedt) chip.classList.add("is-loading");
    const im = document.createElement("img");
    im.src = e.url;
    im.alt = "Anhang";
    chip.appendChild(im);
    if (!e.laedt) {
      const x = document.createElement("button");
      x.type = "button";
      x.className = "composer-attach-x";
      // Der Knopf nennt jetzt, WELCHES Bild er entfernt — bei fuenf gleich
      // aussehenden Kreuzen ist "Bild entfernen" fuer Screenreader wertlos.
      const nr = eintraege.indexOf(e) + 1;
      x.setAttribute("aria-label", `Bild ${nr} von ${eintraege.length} entfernen`);
      x.textContent = "\u2715";
      // ⚠️ Genau EINEN Eintrag entfernen. Vorher rief dieses X
      // clearPendingComposerImage() und raeumte alles ab.
      x.addEventListener("click", () => entfernePendingBild(e.url));
      chip.appendChild(x);
    }
    composerAttachTray.appendChild(chip);
  }
  composerAttachTray.hidden = eintraege.length === 0;
}

/** Haengt ein fertig hochgeladenes Bild an, statt das vorige zu ersetzen. */
function fuegePendingBildHinzu(item: UploadDto): void {
  pendingGenerateImage = false;
  if (!pendingComposerImages.some((i) => i.url === item.url)) {
    pendingComposerImages.push(item);
  }
  renderComposerAttach();
  updateTextInputState();
  if (window.matchMedia("(pointer: fine)").matches) textField.focus();
}

/** Nimmt genau einen Anhang wieder heraus. */
function entfernePendingBild(url: string): void {
  pendingComposerImages = pendingComposerImages.filter((i) => i.url !== url);
  renderComposerAttach();
  updateTextInputState();
}

function clearPendingComposerImage(): void {
  pendingComposerImages = [];
  ladendeAnhaenge.clear();
  composerAttachTray.replaceChildren();
  composerAttachTray.hidden = true;
  updateTextInputState();
}

/** Wie viele Bilder noch hineinpassen. */
function freiePlaetze(): number {
  return Math.max(
    0,
    MAX_ANHAENGE - pendingComposerImages.length - ladendeAnhaenge.size,
  );
}

/** Ist die Anhang-Reihe voll? Dann sagt es das — ein Satz für alle Bild-Wege. */
function reiheVollGemeldet(): boolean {
  if (freiePlaetze() > 0) return false;
  showToast(`Mehr als ${MAX_ANHAENGE} Bilder gehen nicht auf einmal.`, "info");
  return true;
}

// 2026-07-24 (Martin: "Loeschen-Button wieder"): kleiner Verwerfen-Knopf im
// Pill. Erscheint, sobald Text (oder ein Anhang) da ist, und leert die
// Eingabe, wenn Martin sie doch nicht abschicken will.
const textClearBtn = document.createElement("button");
textClearBtn.type = "button";
textClearBtn.id = "text-clear-btn";
textClearBtn.className = "text-clear-btn";
textClearBtn.hidden = true;
textClearBtn.setAttribute("aria-label", "Eingabe verwerfen");
textClearBtn.title = "Eingabe verwerfen";
textClearBtn.innerHTML =
  '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">' +
  '<path fill="currentColor" d="M18.3 5.71 12 12.01 5.7 5.7 4.29 7.12l6.3 6.3' +
  '-6.3 6.29 1.41 1.41 6.3-6.3 6.29 6.3 1.41-1.41-6.3-6.29 6.3-6.3z"/></svg>';
textClearBtn.addEventListener("click", () => {
  // Vierte Runde: solange ein Auftrag das Feld sperrt, wird nichts geleert.
  if (pttFeldGesperrt) return;
  textField.value = "";
  if (pendingGenerateImage) setGenerateImageMode(false);
  clearPendingComposerImage();
  autoResizeTextarea();
  updateTextInputState();
  // 2026-07-25 (Martin: "es soll einfach nur den Inhalt loeschen und fertig!
  // Cursor und Tastatur nur, wenn ich direkt ins Textfeld klicke"): KEIN
  // Fokus mehr — auch nicht mit echter Tastatur. Loeschen ist Loeschen.
});
textSendBtn.insertAdjacentElement("beforebegin", textClearBtn);

// Waehrend des Diktats bekommt das Pill eine sichtbare Wellenform (wie in
// der Claude-App). Die Klasse steuert das CSS.
const textInputPill = textField.parentElement;

/*
 * 26.09.2026 (vierte Runde): „Erkenne …" über dem Feld, solange ein Auftrag
 * (ins Textfeld, Umschreiben, Zurückholen) läuft — das Feld ist dann
 * gesperrt (pttFeldSperren). Als Statuszeile, damit auch ein Vorleser es
 * ansagt; Aussehen in style.css (.text-input-erkennt).
 */
const textFeldHinweis = document.createElement("span");
textFeldHinweis.className = "text-input-erkennt";
textFeldHinweis.setAttribute("role", "status");
textFeldHinweis.hidden = true;
textField.insertAdjacentElement("beforebegin", textFeldHinweis);

/*
 * 16.10.2026 (Dreier-Modus Stufe 1, lib/empfaenger-wahl.ts): „An: Vespera | Schmied“ über dem Feld — nur bei „Entwickler im
 * Chat“. Bewusst NICHT in der Knopfzeile: deren Breiten sind ausgerechnet („Wer nachgibt“, style.css), ein weiteres Element
 * hätte den Modell-Chip auf Martins Telefon verdrängt. Gilt für Tippen UND Sprechtaste (das Feld steht im Chat immer).
 */
empfaengerAnzeige = baueEmpfaengerWahl(empfaengerWahl);
textField.insertAdjacentElement("beforebegin", empfaengerAnzeige.el);
window.addEventListener(EMPFAENGER_GEAENDERT, () => empfaengerAnzeige?.zeichne());
// 19.10.2026: „Ziel mit Schmied besprechen“ (Seite Morgenreport) legt nur Text ins Feld und wählt „An: Schmied“ — Martin sendet selbst.
window.addEventListener(ZIEL_BESPRECHEN_EREIGNIS, (ev) => {
  const text = (ev as CustomEvent<{ text?: unknown }>).detail?.text;
  if (typeof text !== "string" || !text.trim()) return;
  empfaengerWahl.waehle("schmied");
  textField.value = entwurfMitZiel(textField.value, text); // ein angefangener Entwurf bleibt stehen
  textField.dispatchEvent(new Event("input", { bubbles: true }));
  textField.focus();
});

/**
 * Das Feld sperren (mit sichtbarem Hinweis) oder freigeben — nur die
 * Aufträge ans Feld (pttFeld) rufen das. Gesperrt heißt: nicht tippen,
 * nicht leeren, nicht abschicken; die Sprechtaste bleibt frei.
 */
function pttFeldSperren(hinweis: string | null): void {
  pttFeldGesperrt = hinweis !== null;
  textField.readOnly = pttFeldGesperrt;
  textField.setAttribute("aria-busy", pttFeldGesperrt ? "true" : "false");
  textInputPill?.classList.toggle("is-erkennt", pttFeldGesperrt);
  textFeldHinweis.textContent = hinweis ?? "";
  textFeldHinweis.hidden = !pttFeldGesperrt;
  updateTextInputState();
}

// Ticket 559 (2026-07-25, Martin mit drei Referenzbildern): DREI ZONEN statt
// eines gequetschten Blocks — Anhang oben, Textfeld ueber die volle Breite,
// darunter eine feste Aktionsleiste. Dafuer werden die vorhandenen Knoepfe in
// eine eigene Zeile umgehaengt; neue Elemente entstehen nicht, und alle
// Zugriffe im Code laufen ueber IDs statt ueber Positionen.
// 21.10.2026 (Entwurf A): sichtbar ist es wieder EINE Zeile (Plus | Feld | Antwort | Mikro ⇄ Senden) — die
// Knopfzeile ist dafür in style.css `display: contents`. DOM und Reihenfolge hier bleiben, wie sie sind.
const composerActions = document.createElement("div");
composerActions.className = "composer-actions";
if (textInputPill) {
  textInputPill.appendChild(composerActions);
  // Reihenfolge wie in der Referenz: Plus ganz links, dann die Wellenform,
  // rechts die Bedien-Knoepfe. Der Diktat-Knopf bringt seine Wellenform
  // selbst mit (sie wird weiter unten direkt vor ihm eingehaengt).
  for (const el of [
    document.getElementById("plus-btn"),
    // 2026-08-27 (Martin: "muss nicht unbedingt in der Fusszeile diese
    // Information stehen"): Das aktive Modell zieht aus dem Fuss-Streifen in
    // die Knopfzeile — dorthin, wo man es liest, bevor man etwas abschickt.
    //
    // Bewusst DASSELBE Element, nicht ein zweites: #status-llm wird an vier
    // Stellen gepflegt (Einstellungen, chat-done vom Server, Klick oeffnet die
    // Modell-Einstellungen). Ein Umhaengen aendert daran nichts; eine Kopie
    // haette angefangen auseinanderzulaufen.
    document.getElementById("status-llm"),
    // 27.09.2026 (Tickets 2673/2678): gleich daneben die Werkzeug-Zahl und
    // der Schalter fuer die Antwort-Tiefe — dieselbe Zeile, dieselbe Regel:
    // was hier fehlt, landet nie in der Knopfzeile.
    document.getElementById("status-werkzeuge"),
    document.getElementById("status-tiefe"),
    document.getElementById("status-automikro"),
    document.getElementById("composer-mic"),
    // 2026-08-28: Das wandernde Mikrofon. ⚠️ Es MUSS hier stehen — die
    // Knopfzeile wird im Programm zusammengesetzt, und was nicht in dieser
    // Liste steht, landet nie in `.composer-actions`. Beim ersten Anlauf
    // fehlte es: die CSS-Regeln passten dadurch auf gar nichts, und der
    // Knopf stand mit `display: block` sichtbar daneben.
    document.getElementById("composer-ptt"),
    textClearBtn,
    textSendBtn,
  ]) {
    if (el) composerActions.appendChild(el);
  }
  document.getElementById("status-llm")?.classList.add("composer-modell");

  /**
   * Der schwebende Knopf muss wissen, wie hoch der Verfasser gerade ist.
   * (09.09.2026)
   *
   * ## Warum
   *
   * Seine Hoehe stand in `style.css` als feste 124 Punkte — gerechnet aus
   * dem einzeiligen Verfasser (108) plus 16 Punkte Luft. Der Verfasser ist
   * aber nicht fest hoch: ein Bild-Anhang bringt 60 Punkte mit, jede weitere
   * Textzeile rund 26. Mit Bild und zwei Zeilen misst er 239 Punkte, und der
   * Knopf lag 115 Punkte tief in ihm — genau ueber dem Satz, den Martin
   * gerade tippte.
   *
   * ⚠️ Es gibt keine reine CSS-Loesung dafuer: der Knopf ist absolut
   *    positioniert und kennt seine Geschwister nicht. Die Hoehe muss
   *    gemessen und weitergereicht werden.
   *
   * ⚠️ Ist der Verfasser verborgen (Sprach-Modus), wird die Eigenschaft
   *    ENTFERNT statt auf 0 gesetzt. Sonst rutschte der Knopf an den unteren
   *    Rand; ohne sie greift der Rueckfallwert aus dem Blatt.
   */
  /*
   * 22.10.2026 (Martin 08:48: „beim Tippen … zuckelt das immer wieder mal“): die Höhe steht an den Elementen, die sie lesen
   * (Pfeil im Chat, Wiedergabe-Pille; style.css), NICHT mehr an <html>. Gemessen: jede neue Eingabezeile änderte dort die
   * Eigenschaft, und weil Eigenschaften vererbt werden, rechnete der Browser ALLE rund 3100 Elemente der Seite neu — beim
   * Tippen bis zu einer Sekunde (4× gedrosselt). Ohne gemessenen Wert gilt die Vorgabe aus `:root` (108px).
   */
  const verfasserHoeheMelden = (): void => {
    const v = document.getElementById("text-input");
    const sichtbar = v && !v.hidden && v.getBoundingClientRect().height > 0;
    let wert: string | null = null;
    if (sichtbar) {
      // 19.10.2026: die Aktivitätszeile liegt über dem Formular und gehört zur Höhe — sonst überdeckt das Häuschen sie.
      const z = document.getElementById("aktivitaet-zeile");
      const zeileH = z && !z.hidden ? z.getBoundingClientRect().height : 0;
      wert = Math.round(v.getBoundingClientRect().height + zeileH) + "px";
    }
    for (const leser of [document.getElementById("scroll-to-bottom"), voicePrepTeile?.pille]) {
      if (!leser) continue;
      if (wert !== null) leser.style.setProperty("--verfasser-h", wert);
      else leser.style.removeProperty("--verfasser-h");
    }
  };
  verfasserHoeheNachmessen = verfasserHoeheMelden;
  verfasserHoeheMelden();
  const textInputEl = document.getElementById("text-input");
  if (textInputEl && typeof ResizeObserver !== "undefined") {
    const beobachter = new ResizeObserver(gesteuert("wachen", verfasserHoeheMelden, true));
    beobachter.observe(textInputEl);
    const aktivitaetEl = document.getElementById("aktivitaet-zeile");
    if (aktivitaetEl) beobachter.observe(aktivitaetEl);
  }
}

// 2026-07-24 (Martin, "Bild erzeugen"): kein window.prompt mehr. Der Composer
// bekommt einen Erzeugen-Modus — ein Chip zeigt ihn an, der Wunsch wird im
// gewohnten grossen Feld getippt oder diktiert. Bild-Anhang und Erzeugen-Modus
// schliessen einander aus (man haengt kein Bild an, waehrend man eines
// erzeugen laesst), deshalb teilen sie sich dasselbe Tablett.
let pendingGenerateImage = false;
function setGenerateImageMode(on: boolean): void {
  pendingGenerateImage = on;
  if (on) {
      pendingComposerImages = [];
      ladendeAnhaenge.clear();
      composerAttachTray.replaceChildren();
    const chip = document.createElement("div");
    chip.className = "composer-mode-chip";
    const label = document.createElement("span");
    label.textContent = "Bild erzeugen";
    chip.appendChild(label);
    const x = document.createElement("button");
    x.type = "button";
    x.className = "composer-attach-x composer-mode-x";
    x.setAttribute("aria-label", "Bild erzeugen abbrechen");
    x.textContent = "\u2715";
    x.addEventListener("click", () => setGenerateImageMode(false));
    chip.appendChild(x);
    composerAttachTray.appendChild(chip);
    composerAttachTray.hidden = false;
    textField.focus();
  } else {
    composerAttachTray.replaceChildren();
    composerAttachTray.hidden = true;
  }
  updateTextInputState();
}

/** 2026-07-25 (Martin): Hoehe des Eingabefelds, in Zeilen. Danach wird
 *  innen gescrollt statt weiter zu wachsen. */
const MAX_ZEILEN = 5;

/** Textlänge und Feldbreite beim letzten Messen (autoResizeTextarea) — wächst der Text nur, muss das Feld nicht erst zusammenfallen. */
let gemesseneLaenge = 0;
let gemesseneBreite = 0;
let gemesseneUmbrueche = 0;
/** Eingabearten, bei denen der kurze Weg gilt: Zeichen kommen dazu, nichts wird ersetzt. */
const TIPP_ARTEN = new Set(["insertText", "insertLineBreak", "insertCompositionText"]);

/**
 * @param getippt aus dem `input`-Hörer: Martin tippt gerade.
 *
 * 22.10.2026 (Martin 08:48: „beim Tippen … zuckelt das immer wieder mal“): gemessen (Chromium, 300 Blasen) setzte jeder
 * Tastendruck das Feld auf `height: auto` und las danach zweimal die Höhe — DREI Layouts je Zeichen statt einem, und das
 * zusammenfallende Feld ließ jedes Mal den ganzen Chat neu setzen. Beim Tippen gilt darum der kurze Weg: Kann das Feld nicht
 * schrumpfen (Text nur länger, oder es ist schon einzeilig), genügt EINE Messung bei stehender Höhe; geschrieben wird nur,
 * wenn eine Zeile dazukommt. Alles andere (Löschen, Diktat, Leeren, Mikro ⇄ Senden) geht den vollen Weg wie bisher.
 */
function autoResizeTextarea(ev?: Event): void {
  const getippt = ev instanceof InputEvent && TIPP_ARTEN.has(ev.inputType);
  // 2026-07-25 (Martin: "es soll nur maximal vier oder fuenf Zeilen wachsen,
  // dann ein Scrollfeld werden — damit fuellt es nicht die ganze Seite"):
  // Deckel bei FUENF Zeilen. Er wird aus der tatsaechlichen Zeilenhoehe und
  // dem Innenabstand gerechnet, nicht als feste Pixelzahl — das Feld hat je
  // nach Ansicht 14px oder 16px Schrift, eine feste Zahl waere dort falsch.
  const stil = window.getComputedStyle(textField);
  const zeile = parseFloat(stil.lineHeight) || 25;
  const innen =
    (parseFloat(stil.paddingTop) || 0) + (parseFloat(stil.paddingBottom) || 0);
  const max = Math.round(zeile * MAX_ZEILEN + innen);
  const laenge = textField.value.length;
  const jetzt = parseFloat(textField.style.height);
  const umbrueche = textField.value.split("\n").length - 1;
  // Kurzer Weg nur, wenn der Text wirklich länger wird und keine Zeilenumbrüche wegfallen (Markieren + Einfügen,
  // Undo, Autokorrektur, programmatische Ereignisse ersetzen Text: dort kann das Feld schrumpfen → voller Weg).
  const kannSchrumpfen = (laenge <= gemesseneLaenge || umbrueche < gemesseneUmbrueche) && !(jetzt <= zeile + innen + 1);
  gemesseneLaenge = laenge;
  const vorUmbrueche = gemesseneUmbrueche;
  gemesseneUmbrueche = umbrueche;
  if (getippt && laenge > 0 && jetzt > 0 && !kannSchrumpfen && umbrueche >= vorUmbrueche) {
    const inhalt = textField.scrollHeight; // eine Messung, das Feld behält seine Höhe
    const mehrzeiligBleibt = textForm.hasAttribute("data-mehrzeilig") || inhalt <= zeile + innen + 2;
    // Andere Breite (Fenster, Knopf): der volle Weg — sonst bliebe ein breiter gewordenes Feld zu hoch.
    if (mehrzeiligBleibt && Math.min(inhalt, max) <= jetzt && textField.clientWidth === gemesseneBreite) {
      if (inhalt > textField.clientHeight) textField.scrollTop = inhalt; // wie unten: der Text schiebt sich mit
      return;
    }
  }
  // Ticket 718/628 (2026-07-25, Martins Bild): Das Feld darf bis fast zur
  // ganzen Bildschirmhoehe wachsen — vorher waren 140 Pixel (rund vier
  // Zeilen) das Ende, und alles darueber verschwand aus dem Blick.
  // Gedeckelt bleibt es dennoch, damit die Nachrichten darueber nicht
  // vollstaendig verdraengt werden; ab dem Deckel wird INNEN gescrollt.
  textField.style.height = "auto";
  // 21.10.2026 (Eingabe in einer Zeile, Entwurf A): bricht der Text neben den Knöpfen um, bekommt er die volle Breite
  // über ihnen (style.css, `data-mehrzeilig`). Zurück erst mit leerem Feld — sonst spränge die Zeile beim Tippen hin
  // und her. Nur das Aussehen; gemessen wird danach in der neuen Lage.
  const mehrzeilig =
    textField.value.length > 0 &&
    (textForm.hasAttribute("data-mehrzeilig") || textField.scrollHeight > zeile + innen + 2);
  textForm.toggleAttribute("data-mehrzeilig", mehrzeilig);
  const hoehe = Math.min(textField.scrollHeight, max);
  textField.style.height = `${hoehe}px`;
  // Und der Kern von Martins Wunsch: ist der Deckel erreicht, SCHIEBT SICH
  // DER TEXT NACH OBEN mit, damit die gerade geschriebene bzw. diktierte
  // Stelle immer sichtbar bleibt.
  // 07.10.2026: gemessen an der GEZEIGTEN Höhe — quer mit Tastatur deckelt
  // style.css das Feld auf zwei Zeilen, unter dem Deckel von hier.
  gemesseneBreite = textField.clientWidth;
  if (textField.scrollHeight > textField.clientHeight) {
    textField.scrollTop = textField.scrollHeight;
  }
}

function updateTextInputState(): void {
  const hasText = textField.value.trim().length > 0;
    const hasImg = pendingComposerImages.length > 0;
  // Im Erzeugen-Modus braucht es zwingend eine Beschreibung — ein leeres
  // "erzeuge ein Bild" waere sinnlos.
  // 26.09.2026 (vierte Runde): Solange ein Sprachauftrag das Feld sperrt,
  // geht nichts ab — der erkannte Text gehört noch dazu. Die Sprechtaste
  // (pttLaeuft) und der Stopp-Knopf (chatTurnActive) bleiben frei.
  const canSend = (pendingGenerateImage ? hasText : hasText || hasImg) && !pttFeldGesperrt;
  // 2026-07-25 (Martin: "Absende-Button nach dem Absenden wirkt nicht
  // schoen"): Waehrend die Antwort laeuft, IST dieser Knopf der Stopp-Knopf.
  // Ihn dann zu deaktivieren, weil das Feld leer ist, machte ihn blass —
  // und vor allem klick-tot, also war "Antwort stoppen" genau dann
  // unbenutzbar, wenn man es braucht.
  const pttLaeuft = pttState === "recording" || pttModal !== null;
  textSendBtn.disabled = !canSend && !chatTurnActive && !pttLaeuft;
  textClearBtn.hidden = pttFeldGesperrt || (!hasText && !hasImg && !pendingGenerateImage);
  textField.disabled = false;
  textField.placeholder = pendingGenerateImage
    ? "Was soll ich für dich erzeugen?"
    : hasImg
      ? // 22.10.2026 (Martin: der Platzhalter brach dreizeilig um): kurz, eine Zeile. Dass Text freiwillig ist, sagt der
        // Senden-Knopf — er steht schon mit dem Bild allein bereit.
        pendingComposerImages.length > 1
        ? "Was soll ich mit den Bildern tun?"
        : "Was soll ich damit tun?"
      : connected
        ? "Tippen statt sprechen…"
        : "Schreib oder sprich…";
  // 21.10.2026 (Eingabe in einer Zeile): nur ein Merkmal fürs Aussehen — ist etwas zu senden, tritt das Feld-Mikro
  // für den Senden-Knopf zurück (style.css, „EINE RUHIGE ZEILE“). Welche Wege senden, ändert das nicht.
  // Am Ende, wenn Verwerfen und Platzhalter stehen: wechselt Mikro ⇄ Senden, ändert sich die Breite des Feldes — seine Höhe (auch die des Platzhalters) neu rechnen.
  const warBereit = textForm.hasAttribute("data-senden-bereit");
  textForm.toggleAttribute("data-senden-bereit", canSend);
  if (warBereit !== canSend || (!textField.value && textForm.hasAttribute("data-mehrzeilig"))) autoResizeTextarea();
}

/*
 * 2026-08-28 (Martin: "ein Mikrofonzeichen") — HIER STAND DAS ZWEITE MIKROFON.
 *
 *   Das Live-Diktat (#302) schrieb per Soniox-Echtzeit-Verbindung in das
 *   Eingabefeld. Gemessen sass sein Knopf 66 Punkte ueber dem Dock-Mikro,
 *   in derselben Farbe mit demselben Zeichen — und tat etwas anderes:
 *   schreiben statt fragen.
 *
 *   Was es konnte, kann jetzt der EINE Knopf: die Erkennung laeuft waehrend
 *   des Sprechens mit, der Satz ist in der Aufnahmekarte zu lesen, und
 *   "Ins Textfeld" uebernimmt ihn ohne erneute Erkennung.
 *
 *   Entfernt: setDictating, stopDictation, dictateCleanup,
 *   fokussiereNurMitTastatur, der Klick-Handler und die sieben
 *   _dictate*-Variablen. Die Klasse LiveDictation BLEIBT — sie traegt jetzt
 *   die Mitschrift und wird von den Tiefen Fragen und der
 *   Datei-Bildunterschrift weiter benutzt.
 */
// 2026-07-21 (Verfassung-Seite): "Was heisst das fuer heute?" traegt die
// Frage direkt ins Gespraech — gleiche Bahn wie eine getippte Nachricht.
// 28.09.2026 (Ticket 2740): dieselbe Bahn, aber als ANGETIPPT — siehe unten.
/*
 * 06.09.2026 — Martin: „für die Zuhauseseite wäre gut, dass ich das Heute
 * vorlesen lassen kann, weil das sehr viel Text ist." Der Knopf ist
 * DERSELBE wie an jeder Bot-Blase (makePlayButton: laden, spielen,
 * pausieren, weiter — Pille stoppt). Ein Modul, das Text zeigt, meldet nur
 * Text und Platz; der Knopf und die Stimme bleiben hier. So klingt das
 * Tagesblatt wie jede Antwort, mit derselben Stimme und demselben Stopp.
 */
window.addEventListener("voice-v8:vorlese-knopf", (ev) => {
  const d = (ev as CustomEvent<{ text?: string; platz?: HTMLElement }>).detail;
  const text = d?.text?.trim();
  if (!text || !d?.platz) return;
  d.platz.replaceChildren(makePlayButton(text));
});
/** Solange gesetzt, rollt der Fokus im Feld den Chat NICHT ans Ende (die Blase der Frage soll im Blick bleiben). */
let fokusBeiDerFrage = false;

/** Das Antwortfeld bekommt den Fokus, ohne den Chat zu rollen (die Blase rollt der Aufrufer). Nie das Mikro. */
function oeffneAntwortfeld(): void {
  if (textForm.hidden || textField.disabled) return;
  fokusBeiDerFrage = true;
  try {
    textField.focus({ preventScroll: true });
  } catch {
    textField.focus();
  } finally {
    fokusBeiDerFrage = false;
  }
}

/**
 * Ein Vorschlag fürs Eingabefeld, NICHT gesendet (22.10.2026, Seite „Training“: „Plan anlegen“). In den Chat, „An:“ auf Vespera,
 * der Text ins Feld (steht dort schon etwas, kommt er dahinter — nichts Getipptes geht verloren), Fokus ins Feld. Martin ergänzt
 * und schickt selbst ab. Kein Mikro, kein Senden. Der Text kommt nur von festen Knöpfen der App (Klartext, ≤ 200 Zeichen).
 */
window.addEventListener("voice-v8:ins-feld", (ev) => {
  const text = (ev as CustomEvent<{ text?: unknown }>).detail?.text;
  if (typeof text !== "string" || !text.trim() || text.length > 200) return;
  goToView("chat");
  if (textForm.hidden || textField.disabled) return;
  empfaengerWahl.waehle("vespera");
  pttLegeInsTextfeld(text.trimStart());
});

window.addEventListener("voice-v8:ask-vespera", (ev) => {
  const text = (ev as CustomEvent<{ text?: string }>).detail?.text?.trim();
  if (!text) return;
  // 2026-08-20 (Martin: "Ich habe geklickt: Was heisst das fuer heute? Dann
  // hat er zu Hause gewechselt, aber nicht in den Chat, wo eigentlich die
  // Information kommen sollte."):
  //
  //   Der Knopf schloss die Verfassung — und landete damit dort, wo Martin
  //   hergekommen war, meist Zuhause. Die Frage ging trotzdem raus, und die
  //   Antwort lief in den Chat, den niemand aufschlug. Aus seiner Sicht
  //   passierte nichts.
  //
  //   Wer eine Frage stellt, will die Antwort sehen. Der Wechsel gehoert
  //   HIERHER und nicht in jeden einzelnen Knopf: Dieses Ereignis ist der
  //   gemeinsame Weg fuer alle "frag Vespera"-Knoepfe, egal von welcher
  //   Seite sie kommen.
  //
  // 28.09.2026 (Ticket 2740, Martin: Tugend-Karte angetippt, die Antwort
  // blieb stumm bis zum Play-Knopf): Aus demselben Grund gehoert auch das
  // Vorlesen hierher. Niemand hat getippt — die Antwort spricht wie nach der
  // Sprechtaste, sofern der Lautsprecher an ist (darfVorlesen).
  goToView("chat");
  void sendUserMessage(text, "angetippt");
});


/**
 * Schickt einen getippten oder angetippten Text ab. Ticket 2845: sagt, ob der
 * Server die Nachricht angenommen hat — `false` = offline oder abgewiesen, das
 * Eingabefeld legt den Text dann zurück (handleTextSubmit). Der Anruf-Weg wirft
 * bei einem Fehler.
 */
async function sendUserMessage(
  text: string,
  herkunft?: FrageHerkunft,
  /** Der Server hat die Anfrage mit 200 angenommen — ein späterer Fehler widerruft das nicht (Ticket 2845). */
  angenommen?: () => void,
  /** 16.10.2026 (Dreier-Modus Stufe 1): Martins Wahl „An: Schmied“/„Vespera“ — nur das Eingabefeld (handleTextSubmit) reicht sie herein. */
  an?: MartinsEmpfaenger,
  /** 20.10.2026: echter Tipp auf Ja/Nein einer Karte — die Zeile geht wie immer an den Server, steht aber ohne eigene Blase da (nur „Ja“/„Nein“ an den Schmied). */
  kartenantwort?: boolean,
): Promise<boolean> {
  const trimmed = text.trim();
  if (!trimmed) return true;
  const still = kartenantwort === true && an === "schmied" && (trimmed === "Ja" || trimmed === "Nein");
  // Im Anruf gibt es keinen Empfänger (der Datenkanal geht an Vespera): die Kartenantwort wird gar nicht gesendet, die Karte öffnet wieder.
  healStaleCallFlags();
  if (still && mode === "voice" && connected) {
    showToast("Im Anruf nicht möglich — bitte nach dem Anruf im Chat antworten.", "info", 5000);
    return false;
  }
  appendBubble("user", trimmed, undefined, still ? { kartenantwort: true } : undefined);
  pushHistory({ kind: "user", text: trimmed, ts: Date.now(), ...(still ? { kartenantwort: true as const } : {}) });
  // Ticket 405: erst heilen, dann entscheiden. Ohne das lief eine getippte
  // Nachricht nach einem Anruf-Drop in "Senden fehlgeschlagen: nicht
  // verbunden", waehrend PTT und Anruf-Knopf sich laengst selbst geheilt
  // hatten.
  healStaleCallFlags();
  if (mode === "voice" && connected) {
    // Anruf läuft → Text geht via LiveKit-Datenkanal, Bot antwortet
    // via TTS + Text-Stream.
    await client.sendTextInput(trimmed);
    return true;
  }
  // Chat-Modus → reiner HTTP-Pfad ohne LiveKit, Bot antwortet als
  // Text-Stream; ob er vorliest, entscheidet darfVorlesen(herkunft).
  return await sendChatHttp({
    message: trimmed,
    herkunft,
    ...(angenommen ? { onAngenommen: angenommen } : {}),
    ...(an ? { an } : {}),
    ...(still ? { kartenantwort: true as const } : {}),
  });
}

/**
 * Befehlskanal P1: Martins Antwortzeile auf eine Rückfrage des Zimmermanns (lib/rueckfrage-karte.ts). Im Anruf geht sie NICHT: dort fällt der
 * Empfänger weg (sendUserMessage → Datenkanal an Vespera) — also gar nicht senden, nur Hinweis. Scheitert das Senden, wird die Blase als
 * „Nicht angekommen“ markiert und zählt nicht als Antwort (sperreBeantworteteRueckfragen).
 */
async function sendeRueckfrageAntwort(zeile: string): Promise<boolean> {
  healStaleCallFlags();
  if (mode === "voice" && connected) {
    showToast("Im Anruf nicht möglich — bitte nach dem Anruf im Chat antworten.", "info", 5000);
    return false;
  }
  const angekommen = await sendUserMessage(zeile, undefined, undefined, "zimmermann");
  if (!angekommen) markiereNichtAngekommen(zeile);
  return angekommen;
}

/**
 * 20.10.2026 (Martin 12:38: „Ich kann den Knopf nicht drücken … das Ja muss Bezug nehmen auf die jeweilige Frage“): Martins Tipp auf eine Ja/Nein-
 * Karte des Schmieds MIT Kennung. Geht an die Karten-Route (lib/karte-anfrage.ts) — nie über den Chat- oder Anruf-Weg, also auch im Anruf nie an
 * Vespera. Erst wenn der Server die Antwort gespeichert hat, steht Martins Zeile „Ja“/„Nein“ (mit Kennung) im Verlauf — als Blase nur versteckt, die Karte zeigt die Wahl —, und das Mikro wartet wie bei
 * jeder Nachricht an den Schmied. Notschalter am Server aus („aus“): der alte Weg ohne Bezug. Schon beantwortet/abgelaufen/unbekannt: die Karte
 * wird mit Wort gesperrt (`FrageGesperrt`); Netz/Grenze: sie geht wieder auf (`false`) und sagt warum.
 */
function schmiedFrageSender(kennung: string | undefined): FrageSender {
  return kennung ? (antwort) => sendeKarteAntwort(kennung, antwort) : (antwort, an, echt) => sendUserMessage(antwort, undefined, undefined, an, echt);
}

/**
 * 20.10.2026 (Martin 22:07): der Tipp auf die Karte von Zimmermann oder Hüter — nur über die Karten-Route (der Server stellt das Wort NUR dem
 * Absender der Karte zu). Nie über den Chat- oder Anruf-Weg, auch nicht als Rückfall: ohne Karten-Route gibt es für sie kein Knopf-Ja.
 */
function zusatzKarteSender(kennung: string, an: "zimmermann" | "hueter"): FrageSender {
  return (antwort) => sendeKarteAntwort(kennung, antwort, an);
}

async function sendeKarteAntwort(kennung: string, antwort: "Ja" | "Nein", an: "schmied" | "zimmermann" | "hueter" = "schmied"): Promise<boolean | FrageGesperrt> {
  const r = await sendeKarte(kennung, antwort === "Ja" ? "ja" : "nein");
  if (r?.ok) {
    // 22.10.2026 (Martin 07:55): kam das Echo des Servers schon (live, vor dieser Antwort), steht die Zeile bereits — keine zweite Blase.
    if (!karteAntwortSteht(refs.chat, kennung)) {
      appendBubble("user", antwort, undefined, { an, karteAntwort: kennung });
      pushHistory({ kind: "user", text: antwort, ts: Date.now(), an, karteAntwort: kennung });
    }
    if (an === "schmied") meldeSchmiedAngesprochen({});
    return true;
  }
  // Notschalter SCHMIED_KARTE=0: der alte Weg — aber als ECHTER Kartentipp (fünfter Parameter), damit er im Anruf mit Hinweis abbricht statt
  // über den Datenkanal an Vespera zu gehen (Prüfer-Auflage 20.10.2026), wie im Knopf-Weg ohne Kennung. NUR beim Schmied.
  if (r && !r.ok && r.error === "aus" && an === "schmied") return sendUserMessage(antwort, undefined, undefined, "schmied", true);
  if (r && !r.ok && (r.error === "beantwortet" || r.error === "abgelaufen" || r.error === "unbekannt" || r.error === "ersetzt")) {
    const vorher = r.antwort ? ` (${r.antwort === "ja" ? "Ja" : "Nein"})` : "";
    return { gesperrt: r.error === "beantwortet" ? `schon beantwortet${vorher}` : karteFehlerText(r.error) };
  }
  showToast(karteFehlerText(r?.error ?? "nicht-bereit"), "info", 5000);
  return false;
}

/**
 * Ticket 2845: die Blase der fehlgeschlagenen Nachricht sagt es selbst — mit Wort, nicht nur mit Farbe
 * (Aussehen: .bubble.user.is-nicht-angekommen in style.css). Gesucht wird von unten die letzte Nutzer-Blase mit
 * diesem Text; ein zweites Mal wird nichts angehängt.
 */
function markiereNichtAngekommen(gesendet: string): void {
  const schluessel = gesendet.trim().slice(0, 80).trim();
  const blasen = document.querySelectorAll<HTMLElement>(".bubble.user");
  for (let i = blasen.length - 1; i >= 0; i--) {
    const blase = blasen[i];
    if (blase?.dataset.text !== schluessel) continue;
    if (blase.classList.contains("is-nicht-angekommen")) return;
    blase.classList.add("is-nicht-angekommen");
    const vermerk = document.createElement("span");
    vermerk.className = "bubble-nicht-angekommen";
    vermerk.textContent = "Nicht angekommen";
    blase.appendChild(vermerk);
    return;
  }
}

/**
 * Ticket 2845: der Text kommt ins Feld zurück, wenn die Nachricht nicht beim
 * Server ankam — nie überschreiben, nie verdoppeln (lib/entwurf-halten.ts).
 * Kein Fokus: Cursor und Tastatur gehören Martin (25.07.2026).
 */
function legeEntwurfZurueck(gesendet: string): void {
  markiereNichtAngekommen(gesendet);
  const neu = entwurfZurueck(textField.value, gesendet, false);
  if (neu === null) return;
  textField.value = neu;
  autoResizeTextarea();
  updateTextInputState();
  showToast("Nicht angekommen — dein Text steht wieder im Feld.", "info", 5000);
}

async function handleTextSubmit(
  /** 21.10.2026: „angetippt“ = Feld + gesprochener Folgesatz der Bremse (pttFinishAndSend) — spricht die Antwort wie die Sprechtaste. */
  herkunft?: FrageHerkunft,
): Promise<void> {
  // 2026-09-01 (Martin: "PTT laesst sich nicht absenden — unten der
  // Absende-Button, oben im Modal geht es"): Der Pfeil im Feld ist derselbe
  // Senden-Knopf wie immer. Bei laufender Aufnahme war er DEAKTIVIERT, weil
  // das Feld leer ist — der Tipp ging ins Leere. Das Modal oben sendete.
  if (pttState === "recording" || pttModal) {
    hapticTap(20);
    pttReleased = true;
    if (pttState === "recording") pttResolveRelease();
    return;
  }
  // 26.09.2026 (vierte Runde): Solange ein Auftrag das Feld sperrt, geht
  // nichts ab — sein Text gehört noch dazu (auch Enter).
  if (pttFeldGesperrt) {
    hapticTap(20);
    return;
  }
  // Review 23.07. (MITTEL): laufendes Diktat beenden, BEVOR das Feld
  const text = textField.value.trim();
    const bilder = pendingComposerImages.slice();
  const wantsGenerated = pendingGenerateImage;
    if (!text && bilder.length === 0) return;
  if (wantsGenerated && !text) return;
  textField.value = "";
  autoResizeTextarea();
  textSendBtn.disabled = true;
  let reinerText = false;
  let angenommen = false;
  try {
    if (wantsGenerated) {
      setGenerateImageMode(false);
      await handleGenerateImagePrompt(text);
    } else if (bilder.length > 0) {
      clearPendingComposerImage();
      await attachImageToChat(bilder, text || undefined);
    } else {
      // Ticket 2845: das Feld war schon geleert — kam die Nachricht nicht an
      // (kein 200 vom Server), steht der Text wieder da, statt nur in einer
      // Blase zu liegen. Nach dem 200 nie: der Server rechnet weiter, ein
      // zweites Senden liefe doppelt (Mail, Aktion).
      reinerText = true;
      /*
       * 16.10.2026 (Dreier-Modus Stufe 1, lib/empfaenger-wahl.ts): „An: Schmied“ gilt für diese eine Nachricht — angenommen,
       * fällt die Wahl auf Vespera zurück; kam sie nicht an, bleibt sie für das erneute Senden stehen. (Bilder gehen denselben Weg; Anruf: Vespera.)
       */
      const ok = await sendeMitWahl(empfaengerWahl, (an, bestaetigt) =>
        sendUserMessage(
          text,
          herkunft,
          () => {
            angenommen = true;
            bestaetigt();
          },
          an,
        ),
      );
      if (ok === false) legeEntwurfZurueck(text);
    }
  } catch (err) {
    appendBubble(
      "system",
      `Senden fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
    );
    // Auch ein Fehler NACH der Annahme (die Anzeige wirft) legt nichts zurück.
    if (reinerText && !angenommen) legeEntwurfZurueck(text);
  } finally {
    updateTextInputState();
  }
}

/**
 * 2026-07-29 (Martin: "Einen PDF oder eine Datei kopieren kann ich es noch
 * nicht mit Steuerung einfügen. Bitte das auch noch machen"):
 *
 * Bis hierher führte nur der Weg über das Plus-Menü zu einer Datei. Wer
 * unterwegs etwas in der Zwischenablage hatte — einen Screenshot, ein PDF
 * aus der Mail, ein Bild aus dem Browser — musste es erst irgendwo
 * abspeichern und dann heraussuchen.
 *
 * Jetzt nimmt das Eingabefeld an, was eingefügt wird, und das Fenster
 * nimmt an, was hineingezogen wird. Beides landet im BESTEHENDEN Upload-Weg
 * (uploadAndAttachAnyFile) — also mit demselben Dialog, derselben
 * Größenprüfung und derselben Karte wie über das Plus-Menü. Ein zweiter
 * Weg, kein zweites Verfahren.
 */
function dateienAusUebertragung(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const raus: File[] = [];
  // items zuerst: Nur so kommt man an Screenshots, die als "image/png"
  // ohne Dateinamen in der Zwischenablage liegen.
  if (dt.items && dt.items.length > 0) {
    for (const it of Array.from(dt.items)) {
      if (it.kind !== "file") continue;
      const f = it.getAsFile();
      if (f) raus.push(f);
    }
  }
  if (raus.length === 0 && dt.files && dt.files.length > 0) {
    raus.push(...Array.from(dt.files));
  }
  return raus;
}

/*
 * 23.09.2026, Ticket 2498 — mehrere Dateien sind EIN Vorgang.
 *
 * Hier stand „Bewusst nacheinander: Jede Datei bekommt ihren eigenen
 * Dialog". Damit bekam jede Datei auch ihren eigenen Zug und ihre eigene
 * Antwort: Martins zwei Sprachnachrichten von Albert (22.09.) wurden zweimal
 * einzeln ausgewertet, den Zusammenhang musste er selbst erfragen.
 *
 * Jetzt: eine Datei wie bisher (Dialog, Karte, Zug). Nur Bilder wie bisher
 * ins Tablett — dort gehen sie schon gemeinsam in EINEN Zug. Sonst ein
 * Vorgang: ein Dialog, alle Dateien, eine gemeinsame Antwort
 * (lib/datei-vorgang.ts, Regel in istDateiVorgang).
 */
async function nimmEingefuegteDateien(dateien: File[]): Promise<void> {
  if (dateien.length === 0) return;
  if (istDateiVorgang(dateien)) {
    await starteDateiVorgang(dateien, vorgangsUmgebung);
  } else if (dateien.length > 1) {
    await nimmBilder(dateien);
  } else {
    await uploadAndAttachAnyFile(dateien[0]);
  }
}

/** Was der Vorgang von hier braucht — gereicht, nicht nachgebaut. */
const vorgangsUmgebung: VorgangsUmgebung = {
  marker: DATEI_MARKER,
  artLabel: fileArtLabel,
  verkleinern: compressImageForUpload,
  karteZeigen: (el) => {
    switchToTab("chat");
    insertOrdered(el, Date.now());
    fuehreChatNach();
  },
  imAnruf: () => mode === "voice",
  // Der Zug traegt `vorgang: true` — so faellt er am Server nie in den
  // Beleg-Weg (istBelegUpload, chat.ts).
  senden: (zug) => sendChatHttp(zug),
  systemZeile: (text) => appendBubble("system", text),
  // Wie ein einzelner Anhang: Bilder als Bild, sonst als Datei. So erkennt
  // der Abgleich die Bilder des Zuges an ihrer Adresse wieder (`bild|…`) und
  // zeichnet sie nicht ein zweites Mal.
  merken: (hochgeladen) => {
    const ts = Date.now();
    for (const d of hochgeladen) {
      pushHistory(
        d.mime.startsWith("image/")
          ? { kind: "image", image: { url: d.url, caption: null, from: "user" }, ts }
          : { kind: "file", fileItem: d, ts },
        { deferSave: true },
      );
    }
    saveHistory(history);
  },
  annehmen: () => hiddenFileAny.accept,
};

// Einfügen ins Eingabefeld (Strg+V / Cmd+V).
textField.addEventListener("paste", (ev: ClipboardEvent) => {
  const dateien = dateienAusUebertragung(ev.clipboardData);
  if (dateien.length === 0) return; // reiner Text — normal einfügen lassen
  ev.preventDefault();
  void nimmEingefuegteDateien(dateien);
});

// Einfügen, ohne dass das Feld den Fokus hat — z.B. direkt nach dem Kopieren.
document.addEventListener("paste", (ev: ClipboardEvent) => {
  const ziel = ev.target as HTMLElement | null;
  // Im Feld selbst hat der Zuhörer oben schon zugegriffen.
  if (ziel === textField) return;
  if (
    ziel &&
    (ziel.tagName === "INPUT" ||
      ziel.tagName === "TEXTAREA" ||
      ziel.isContentEditable)
  ) {
    return; // ein anderes Eingabefeld — nicht dazwischenfunken
  }
  const dateien = dateienAusUebertragung(ev.clipboardData);
  if (dateien.length === 0) return;
  ev.preventDefault();
  void nimmEingefuegteDateien(dateien);
});

// Hineinziehen (Drag & Drop) — derselbe Weg.
document.addEventListener("dragover", (ev: DragEvent) => {
  if (!ev.dataTransfer) return;
  const hatDatei = Array.from(ev.dataTransfer.items ?? []).some(
    (i) => i.kind === "file",
  );
  if (!hatDatei) return;
  ev.preventDefault();
  ev.dataTransfer.dropEffect = "copy";
  document.body.classList.add("is-drop-target");
});
document.addEventListener("dragleave", (ev: DragEvent) => {
  // Nur beim Verlassen des Fensters aufräumen, nicht bei jedem Kindwechsel.
  if (ev.relatedTarget === null) {
    document.body.classList.remove("is-drop-target");
  }
});
document.addEventListener("drop", (ev: DragEvent) => {
  document.body.classList.remove("is-drop-target");
  const dateien = dateienAusUebertragung(ev.dataTransfer);
  if (dateien.length === 0) return;
  ev.preventDefault();
  void nimmEingefuegteDateien(dateien);
});

textField.addEventListener("input", (ev) => {
  // 2026-07-29 (Martin: "Das soll man auch wieder loeschen koennen, wenn er
  // so einen Bloedsinn schreibt."): Ein `input`-Ereignis entsteht NUR durch
  // echte Eingabe — das Diktat setzt den Wert programmatisch und loest
  // keines aus. Hier laesst sich also sauber unterscheiden, wer schreibt.
  //
  autoResizeTextarea(ev);
  updateTextInputState();
  empfaengerWahl.frische(); // 27.10.2026: wer tippt, dem verfällt der gewählte Chip „An:“ nicht (lib/empfaenger-wahl.ts)
});
// 2026-07-25 (Martin: "ich muss noch den Screen verschieben, damit ich zum
// Textfeld komme"): Die Hoehenanpassung greift erst, wenn die Tastatur
// tatsaechlich oben ist — das dauert auf iOS einen Moment. Deshalb danach ans
// Ende scrollen, damit die zuletzt gelesene Stelle und die Leiste zusammen im
// Blick sind. Zwei Anlaeufe, weil die Tastatur je nach Geraet unterschiedlich
// schnell erscheint.
textField.addEventListener("focus", () => {
  // 22.10.2026: „Antworten“ aus „Offen“ rollt zur Frage — dort bleibt der Blick, das Feld bekommt nur den Fokus.
  if (fokusBeiDerFrage) return;
  window.setTimeout(() => scrollChatToBottomReliably(), 120);
  window.setTimeout(() => scrollChatToBottomReliably(), 420);
});
textField.addEventListener("keydown", (ev) => {
  // Enter senden, Shift+Enter neue Zeile (wie Telegram/WhatsApp Desktop).
  if (ev.key === "Enter" && !ev.shiftKey) {
    ev.preventDefault();
    void handleTextSubmit();
  }
});
textForm.addEventListener("submit", (ev) => {
  ev.preventDefault();
  void handleTextSubmit();
});
// 2026-07-25 (Martin: "bitte den Enter-Button als Absendebutton nehmen, wenn
// ich eine Nachricht aufgenommen habe oder auch Push-to-Talk"): Enter sendet
// oben laengst — aber nur mit Fokus IM Feld. Nach Push-to-Talk sitzt der Fokus
// nirgends; die Taste lief also ins Leere. Dieser Weg faengt das ab und geht
// durch dieselbe Tuer wie der Senden-Knopf. Bewusst eng gefasst, damit Enter
// nirgends gekapert wird.
//
// 2026-08-28: Der zweite Fall — "nach dem Diktieren sitzt der Fokus auf dem
// Mikro-Knopf" — ist mit dem Feld-Diktat entfallen. Die Bedingung darauf
// (`diktiert`) war danach konstant falsch und ist weg; uebrig bleibt die
// Textlaenge, und `handleTextSubmit` kehrt bei leerem Feld ohnehin zurueck.
document.addEventListener("keydown", (ev) => {
  if (ev.key !== "Enter" || ev.shiftKey || ev.isComposing) return;
  if (textField.value.trim().length === 0) return;
  const aktiv = document.activeElement as HTMLElement | null;
  if (aktiv === textField) return; // dort greift der eigene Handler oben
  const imComposer = !!aktiv && !!textInputPill && textInputPill.contains(aktiv);
  const nichtsFokussiert = !aktiv || aktiv === document.body;
  if (!imComposer && !nichtsFokussiert) return;
  ev.preventDefault();
  void handleTextSubmit();
});
// Pre.1015: Mobil-Fix — der ERSTE Tipp auf „Senden" schloss nur die Tastatur
// (Textfeld verlor Fokus, Composer rutschte weg, der Klick verfehlte den Button),
// erst der zweite sendete. pointerdown abfangen hält den Fokus → Klick/Submit
// feuert beim ersten Tipp. (preventDefault auf pointerdown verhindert die
// Fokus-/Selektionsdefaults, NICHT den nachfolgenden Klick.)
textSendBtn.addEventListener("pointerdown", (ev) => {
  ev.preventDefault();
});

// --- Tab-Switch -----------------------------------------------------------

const memoryViewContainer = requireEl("#view-memory");
const memoryView = new MemoryView(memoryViewContainer);

const settingsViewContainer = requireEl("#view-settings");
let settingsView: SettingsView | null = null;

// Pre.344: Anwendungsbeispiele-View (Modul-Vitrine).
const examplesViewContainer = requireEl("#view-examples");
let examplesView: import("./lib/examples-view.js").ExamplesView | null = null;

const projectsViewContainer = requireEl("#view-projects");
let projectsView: ProjectsView | null = null;

// Pre.974: Vorlese-Player in den geteilten Datei-Viewer (Material + Projekte)
// einklinken — derselbe buildNarrationPlayer wie Mein Leben/HD/Essenz/Projekte.
// Der Viewer zerlegt den Inhalt selbst in Abschnitte (setSections) und steuert
// setReady/prefetch; hier nur den Player bauen (googleVoice + Stimm-Persistenz).
setViewerPlayerBuilder((o) =>
  buildNarrationPlayer({
    // Der Viewer kennt den Dateinamen — die Leiste soll ihn zeigen.
    titel: (o as { titel?: string }).titel ?? "Datei",
    initialVoice: vorleseStimme(),
    endpoint: o.endpoint,
    getBody: (voiceId) => {
      const b = o.getBody(voiceId);
      return b
        ? { ...b, googleVoice: googleHdFallbackForVoice(voiceId) }
        : null;
    },
    sectionable: o.sectionable ?? false,
    resumeId: o.resumeId,
    resumeVersion: o.resumeVersion,
    onVoiceChange: (id) => {
      try {
        saveSettings({ ...loadSettings(), narratorVoiceId: id });
      } catch {
        /* sync-Fehler ignorieren — Stimme gilt trotzdem für jetzt */
      }
    },
  }),
);

// Pre.968: aktives Projekt für den Topbar-Chip-Klick merken — Klick öffnet
// DIREKT das Projekt-Detail (nicht die Projektliste).
let chipActiveProjectId: string | null = null;
// Ticket #458 (2026-07-23): Projekt-Wechsel-VORSCHLAG — schlanke Leiste
// ueber dem Composer, nie ein stiller Wechsel. "Wechseln" setzt das
// aktive Projekt (PUT /api/projects/active), "Nein" merkt sich die
// Ablehnung serverseitig fuer ein paar Stunden.
let letzterVorschlagId: string | null = null;
function showProjectSuggestBanner(sug: {
  projectId: string;
  name: string;
  score: number;
}): void {
  if (letzterVorschlagId === sug.projectId) return;
  letzterVorschlagId = sug.projectId;
  document.getElementById("project-suggest-banner")?.remove();
  const bar = document.createElement("div");
  bar.id = "project-suggest-banner";
  bar.className = "project-suggest-banner";
  bar.setAttribute("role", "status");
  const txt = document.createElement("span");
  txt.className = "psb-text";
  txt.textContent = `Klingt nach \u201E${sug.name}\u201C — Projekt wechseln?`;
  bar.appendChild(txt);
  const ja = document.createElement("button");
  ja.type = "button";
  ja.className = "psb-btn psb-btn--ja";
  ja.textContent = "Wechseln";
  const nein = document.createElement("button");
  nein.type = "button";
  nein.className = "psb-btn";
  nein.textContent = "Nein";
  const weg = (): void => {
    // Review 23.07.: Sperre freigeben — sonst blockte ein unbeachtet
    // abgelaufener Banner denselben Vorschlag fuer die ganze Session
    // (Server-Semantik: 10min-Cooldown, 6h nur bei explizitem Nein).
    if (letzterVorschlagId === sug.projectId) letzterVorschlagId = null;
    bar.classList.remove("is-visible");
    window.setTimeout(() => bar.remove(), 250);
  };
  ja.addEventListener("click", () => {
    ja.disabled = true;
    nein.disabled = true;
    void fetch("/api/projects/active", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ project_id: sug.projectId }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("status"))))
      .then((j: { active?: ProjectDto | null }) => {
        renderActiveProjectSlot(j.active ?? null);
        txt.textContent = `Projekt \u201E${sug.name}\u201C ist jetzt aktiv.`;
        ja.remove();
        nein.remove();
        window.setTimeout(weg, 2500);
      })
      .catch(() => {
        txt.textContent = "Wechsel gerade nicht m\u00f6glich.";
        window.setTimeout(weg, 2500);
      });
  });
  nein.addEventListener("click", () => {
    void fetch("/api/projects/suggest-dismiss", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ project_id: sug.projectId }),
    }).catch(() => {});
    weg();
  });
  bar.append(ja, nein);
  // Review 23.07. (MITTEL): im Layout-Fluss VOR dem Composer statt fixed —
  // das fixe Banner lag ab zweizeiligem Entwurf AUF dem Eingabefeld und
  // fing Taps ab. Im Fluss waechst es mit der Textarea mit.
  const form = document.getElementById("text-input");
  if (form?.parentNode) form.parentNode.insertBefore(bar, form);
  else document.body.appendChild(bar);
  requestAnimationFrame(() => bar.classList.add("is-visible"));
  window.setTimeout(() => {
    if (bar.isConnected) weg();
  }, 30_000);
}

function renderActiveProjectSlot(p: ProjectDto | null): void {
  chipActiveProjectId = p?.id ?? null;
  // Pre.375: Status-Bar (Footer) zeigt NICHT mehr das aktive Projekt —
  // das steht schon als Zeile oben auf Zuhause. Pre.373 hatte das
  // HTML auf hidden gesetzt, aber diese Funktion hat es bei jedem Update
  // wieder eingeblendet. Jetzt bleibt es permanent hidden.
  const sep = document.getElementById("status-bar-project-sep");
  const slot = document.getElementById("status-bar-project");
  if (sep) sep.hidden = true;
  if (slot) {
    slot.textContent = "";
    slot.hidden = true;
  }

  // 11.10.2026: das Projekt steht nicht mehr als Chip im Kopf, sondern als leise Zeile oben auf
  // Zuhause (lib/projekt-zeile.ts). Dort liegen auch Name, Beschriftung und `hidden`.
  zeichneProjektZeile(p);
}

function ensureProjectsView(): ProjectsView {
  if (!projectsView) {
    projectsView = new ProjectsView(projectsViewContainer, {
      onActiveChange: (p) => renderActiveProjectSlot(p),
      // Player-Builder injizieren (buildNarrationPlayer lebt hier in app.ts —
      // direkter Import in projects-view.ts wäre zirkulär). googleVoice wird
      // hier ergänzt (Fallback-Stimme lebt ebenfalls in app.ts).
      buildPlayer: (o) => {
        const player = buildNarrationPlayer({
          titel: (o as { titel?: string }).titel ?? "Projekt",
          initialVoice:
            vorleseStimme(),
          endpoint: o.endpoint,
          getBody: (voiceId) => {
            const b = o.getBody(voiceId);
            return b
              ? { ...b, googleVoice: googleHdFallbackForVoice(voiceId) }
              : null;
          },
          sectionable: o.sectionable ?? false,
          resumeId: o.resumeId,
          resumeVersion: o.resumeVersion,
          onVoiceChange: (id) => {
            try {
              saveSettings({ ...loadSettings(), narratorVoiceId: id });
            } catch {
              /* sync-Fehler ignorieren — Stimme gilt trotzdem fuer jetzt */
            }
          },
        });
        // Pre.961: Knopf FREISCHALTEN. Der Play-Button ist gesperrt, solange der
        // Player nicht „ready" ist (buildPlayerBar). Buch/HD/Essenz rufen
        // setReady(true) — der Projekt-Player tat das nie → „Vorlesen" blieb
        // ausgegraut. buildPlayer wird nur mit vorhandenem Überblickstext
        // aufgerufen, also ist er hier bereit.
        player.setReady(true);
        // Pre.960: GENAU WIE Buch/HD/Essenz vorwärmen. Ohne das resolved der
        // erste Klick erst async (TTS holen) und ruft play() DANACH auf — auf
        // iOS ist die Nutzer-Geste dann weg → Wiedergabe blockiert. Mit
        // Vor-Wärmen liegt die URL beim Klick bereit → synchroner Sofort-Play.
        player.prefetch();
        return player;
      },
    });
  }
  return projectsView;
}

// Beim Bootstrap: aktuelles aktives Projekt einmal laden für die Status-Bar.
void getActiveProject()
  .then((p) => renderActiveProjectSlot(p))
  .catch(() => {
    // ignore — Endpoint möglicherweise nicht erreichbar im Single-User-Mode
  });

// Pre.156: Bot-Avatar laden + in Topbar/Bot-Bubbles rendern.
void loadBotAvatar();

// Pre.297: Settings-View dispatched dieses Event nach Auto-Save —
// Topbar-Avatar in Echtzeit aktualisieren.
window.addEventListener("voice-v8:persona-changed", () => {
  void loadBotAvatar();
});

// Die Projekt-Zeile auf Zuhause wurde angetippt (lib/projekt-zeile.ts).
document.addEventListener(PROJEKT_OEFFNEN, () => {
  // Pre.968: Klick aufs aktive-Projekt-Label öffnet DIREKT das Projekt-Detail
  // (kein zweiter Klick über die Projektliste mehr nötig).
  if (chipActiveProjectId) {
    void ensureProjectsView().openProjectById(chipActiveProjectId);
  } else {
    goToView("projects");
  }
});

const toolsViewContainer = requireEl("#view-tools");
let toolsView: ToolsView | null = null;

const libraryViewContainer = requireEl("#view-library");
let libraryView: LibraryView | null = null;

function ensureLibraryView(): LibraryView {
  if (!libraryView) {
    libraryView = new LibraryView(libraryViewContainer, {
      onAttach: (item) => attachImageToChat([item]),
    });
  }
  return libraryView;
}

// Pre.157: FilesView entfernt — Library ist jetzt die Mediathek und
// zeigt PDFs/Audio/Andere mit. Der attachFileToChat-Pfad bleibt für
// das Quick-Access-Sheet erhalten.

const contactsViewContainer = requireEl("#view-contacts");
let contactsView: ContactsView | null = null;

function ensureContactsView(): ContactsView {
  if (!contactsView) {
    contactsView = new ContactsView(contactsViewContainer);
  }
  return contactsView;
}

// pre.903 (Martin): „Blättern" — beim Wechsel der Ansicht ALLE offenen
// Bereichs-Overlays schließen, damit nichts im Hintergrund gestapelt offen
// bleibt. HD/Essenz/Verbindung/Rechnungen teilen .hd-overlay; der Rest hat
// eigene Klassen. Material ist ein persistentes Sheet → ausblenden statt entfernen.
const OVERLAYS_ZUM_SCHLIESSEN =
  ".hd-overlay, .essence-overlay, .conn-overlay, " +
  ".archive-overlay, .book-overlay, .cust-overlay, " +
  ".usr-overlay, .project-sheet-overlay, .contact-sheet-overlay, " +
  "#deep-questions-sheet, #verfassung-sheet, " +
  ".tickets-backdrop, .changelog-sheet";

/**
 * Liegt etwas ÜBER dem Chat — ein Overlay, ein Sheet (Mein Alltag, Mediathek) oder eine Seite des Routers?
 * Dieselbe Liste wie beim Schließen (closeAllOverlays), keine zweite. (Ticket 2945)
 */
function liegtEtwasUeberDemChat(): boolean {
  return (
    eineSeiteOffen() ||
    document.querySelector(OVERLAYS_ZUM_SCHLIESSEN) !== null ||
    document.querySelector(".material-sheet:not([hidden])") !== null
  );
}

function closeAllOverlays(): void {
  document.querySelectorAll(OVERLAYS_ZUM_SCHLIESSEN).forEach((el) => el.remove());
  // Modal-Sheets verstecken: Mediathek (#material-sheet) UND „Mein Alltag"
  // (#alltag-sheet) teilen die Klasse .material-sheet, haben aber verschiedene
  // IDs — die alte ID-Abfrage übersah das Alltag-Sheet (#195: per Sprache nicht
  // schließbar). Klasse fängt beide. Alltag-Hash mitziehen, damit der Page-
  // Router es nicht wieder aufmacht.
  document
    .querySelectorAll<HTMLElement>(".material-sheet")
    .forEach((el) => (el.hidden = true));
  // 2026-07-21 (Martin, "Markierung-Chaos"): beim Sammel-Schliessen ALLE
  // Seiten-Hashes raeumen — sonst klebt der alte Hash (und die Sidebar-
  // Markierung) beim Wechsel in eine View.
  // 27.09.2026 (Ticket 2637): hier stand eine eigene Liste der Seiten, und
  // „Ankermomente" fehlte darin — nach „Chat" lag die Seite weiter obenauf.
  // Jetzt schliesst der Router jede angemeldete Seite, die oben noch nicht
  // entfernt wurde, und raeumt jeden Seiten-Hash. Eine neue Seite braucht
  // nur ihre registerPageRoute.
  schliesseAlleSeiten();
  clearHdHash();
}

/**
 * Wo Martin zuletzt war — Chat oder Zuhause. (05.09.2026)
 *
 * Martin: *„Wenn ich den Chat offen habe, dass der Chat wieder geladen
 * wird."* Unterseiten (#alltag, #essence) kommen über `openPagesFromHash`
 * zurück; die Grundansicht landete bisher immer im Zuhause.
 *
 * ⚠️ Gelesen wird das an der EINEN Stelle, die beim Start die Ansicht
 *    setzt (`if (!location.hash) goToView(...)`). Ein erster Anlauf setzte
 *    die Ansicht daneben — und wurde zwei Zeilen später von genau dieser
 *    Stelle überschrieben. Im Browser gemessen, nicht vermutet.
 */
/**
 * Beim Start dorthin zurueck, wo Martin war. (08.09.2026)
 *
 * Martin: *„Können wir es so machen, dass, wenn ich auf einer Seite bin
 * (Essenzspiegel oder wo auch immer) und ich einen Reload mache, die Seite
 * genau dort wieder geladen wird, wo ich gerade bin?"*
 *
 * Zwei Ebenen, in dieser Reihenfolge:
 *
 *   1. die GRUNDANSICHT (Zuhause, Chat, Mediathek, Projekte, …) — gemerkt
 *      in `localStorage`, siehe {@link ANSICHTEN};
 *   2. die UNTERSEITE darueber (Spiegel, Buch, Alltag, Offen, …) — sie
 *      traegt ihren Hash und kommt ueber `openPagesFromHash` zurueck.
 *
 * ⚠️ Die Reihenfolge ist die eigentliche Arbeit. `goToView` schliesst alle
 *    Overlays und LOESCHT dabei die Seiten-Hashes. Wer die Unterseite zuerst
 *    oeffnet, sieht sie eine Zehntelsekunde spaeter wieder verschwinden —
 *    darum wird der Hash vorher gerettet und danach wieder gesetzt.
 *
 * ⚠️ Ein fremder Hash (Human Design hat seine eigene, aeltere Logik mit
 *    Admin-Parametern) wird NICHT angefasst: dann bleibt alles, wie es war.
 */
function stelleOrtHerWieder(): void {
  const hash = location.hash;
  if (hash && !istSeitenHash(hash)) return;
  goToView(gemerkteAnsicht());
  if (!hash) return;
  window.history.replaceState(
    null,
    "",
    location.pathname + location.search + hash,
  );
  openPagesFromHash();
}

function gemerkteAnsicht(): string {
  try {
    const gemerkt = localStorage.getItem(ANSICHT_SCHLUESSEL) ?? "";
    return ANSICHTEN.has(gemerkt) ? gemerkt : "zuhause";
  } catch {
    return "zuhause";
  }
}

/** Wo Martin zuletzt war. Unterseiten regelt der Hash. */
const ANSICHT_SCHLUESSEL = "vespera.ansicht";

/**
 * Die Grundansichten, die ein Neuladen ueberleben duerfen.
 *
 * 08.09.2026 — Martin: *„Können wir es so machen, dass, wenn ich auf einer
 * Seite bin (Essenzspiegel oder wo auch immer) und ich einen Reload mache,
 * die Seite genau dort wieder geladen wird, wo ich gerade bin?"*
 *
 * Seit dem 05.09. merkte sich das Haus GENAU ZWEI Ansichten, Chat und
 * Zuhause. Wer in der Mediathek, in den Projekten, bei den Kontakten, im
 * Gedaechtnis, in den Werkzeugen, den Auftraegen, der Automatik oder den
 * Einstellungen neu lud, landete im Zuhause — acht Seiten, die dieselbe
 * Bitte schon einmal gestellt bekommen hatten.
 *
 * ⚠️ „login" gehoert NICHT dazu. Eine wiederhergestellte Anmeldemaske ueber
 *    einer angemeldeten Sitzung waere kein Ort, sondern eine Sackgasse.
 */
const ANSICHTEN = new Set([
  "zuhause",
  "chat",
  "library",
  "projects",
  "contacts",
  "memory",
  "tools",
  "tasks",
  "automation",
  "examples",
  "settings",
  // 06.10.2026: die Morse-Lernseite (frontend/lib/morse-view.ts)
  "morse",
]);

/**
 * „← Menü" aus den Einstellungen führt in die Grundansicht davor.
 * (24.09.2026, Ticket 2570; Verhalten in lib/einstellungen-rueckweg.ts)
 */
const rueckweg = rueckwegAusEinstellungen(ANSICHTEN);

function switchToTab(target: string): void {
  for (const b of tabButtons) {
    b.setAttribute("aria-selected", String(b.dataset.tab === target));
  }
  document.querySelectorAll<HTMLElement>(".view").forEach((v) => {
    v.hidden = v.id !== `view-${target}`;
  });
  // Pre.139: Wenn nicht im Chat-View, soll der orange Anruf-Button im
  // Dock zum "Zurück zu Voice"-Button werden — User kommt aus
  // Kontakte/Dateien/etc. mit einem Tap zurück, ohne Drawer.
  rueckweg.wechsel(document.body.dataset.activeView, target);
  document.body.dataset.activeView = target;
  /*
   * 05.09.2026 — die Ansicht überlebt das Neuladen.
   *
   * Martin: *„Super wäre, wenn ich bei den Reloads, wenn ich die
   * Alltag-Seite offen habe, dass der Alltag wieder geladen wird, und wenn
   * ich den Chat offen habe, dass der Chat wieder geladen wird."*
   *
   * Unterseiten (Alltag, Spiegel, …) tragen ihren Hash und kommen über
   * `openPagesFromHash` von selbst zurück. Die beiden Grundansichten — Chat
   * und Zuhause — hatten nichts dergleichen: jeder Neustart landete im
   * Zuhause. Das hier merkt sich die letzte; wiederhergestellt wird sie beim
   * Start, aber nur, wenn KEIN Seiten-Hash da ist (die Unterseite gewinnt).
   */
  try {
    // 08.09.2026: jede Grundansicht, nicht nur die zwei (siehe ANSICHTEN).
    if (ANSICHTEN.has(target)) localStorage.setItem(ANSICHT_SCHLUESSEL, target);
  } catch {
    /* Privatmodus oder voll — dann eben wie bisher */
  }
  // 2026-07-05 (Martin): WKWebView zeichnet den frisch sichtbaren View sonst
  // erst beim Screen-Touch (leerer Chat nach Reload). Repaint erzwingen.
  scheduleAppRepaints();
  /*
   * 2026-08-23 (Martin: "Wenn ich auf Chat klicke, dann springt er nicht ganz
   * runter, d.h. ich muss dann erst scrollen."):
   *
   *   Der Chat gehoert ans ENDE — dort steht das Neueste. goToView("chat") hat
   *   dafuer seit dem 20.07. einen pinChatToBottom(1200). Der Dock-Knopf ruft
   *   aber switchToTab("chat"), und die macht den Verlauf bloss sichtbar. Der
   *   Kommentar in goToView beschreibt das Symptom sogar wortgleich —
   *   "switchToTab macht ihn bloss sichtbar; wo er zuletzt stand, blieb er
   *   stehen" —, nur wurde der Fix eine Ebene zu hoch eingebaut.
   *
   *   Hier statt am Knopf, weil FUENF weitere Stellen switchToTab("chat")
   *   aufrufen. Wer den Chat sichtbar macht, stellt ihn auch ans Ende.
   *
   *   Der Pin haelt nach: Beim Sichtbarwerden nehmen die Blasen erst ihre echte
   *   Hoehe an (content-visibility), ein einmaliges scrollTop landet dann zu
   *   hoch. Eine Nutzer-Geste bricht ihn sofort ab — nie gegenscrollen.
   */
  if (target === "chat") pinChatToBottom(1200);
  /*
   * 29.09.2026 (Ticket 2803): Die Einstellungen behalten die Fusszeile. Im
   * Anruf steht dort der Einstellungs-Knopf (Ticket #1926) — in den
   * Einstellungen selbst zeigt er „du bist hier" und tut beim Tippen nichts.
   * Sonst baute goToView("settings") die offene Seite neu auf: wer gerade in
   * „Stimme und Vorlesen" stand, landete ungefragt wieder in der Uebersicht.
   * Hier, weil jeder Ansichtswechsel hier durchgeht (auch die Sprechtaste,
   * die in den Chat springt) — EIN Besitzer.
   */
  setzeOrtMarke(
    document.getElementById("dock-settings") as HTMLButtonElement | null,
    target === "settings",
  );
  /*
   * 28.10.2026: Die Fußzeile ist auf jeder Ansicht gleich (kein Tausch von
   * Vorlesen/Anrufen mehr). Dafür richtet jeder Ansichtswechsel die
   * Seitenleiste ein: in den Einstellungen am Rechner bleibt sie als
   * schmale Symbolleiste neben der Einstellungsliste stehen.
   */
  // Als Ereignis, nicht als Aufruf: die Leiste (`drawer`) wird weiter unten deklariert und steht beim
  // allerersten Wechsel noch nicht (zeitliche Totzone). Der Hörer sitzt bei der Leiste.
  window.dispatchEvent(new Event("voice-v8:ansicht-gewechselt"));
}

// Pre.140: goToView ist switchToTab PLUS view-spezifische Init. Wird von
// Quick-Access (pre.138) und open_view-Tool (pre.137) genutzt — vorher
// haben beide nur switchToTab gerufen, der View war dann sichtbar aber
// leer (Live-Bug Martin: "Kontakte/Erinnerungen/Projekte/Einstellungen
// sind alle leer"). Tab-Click hat die Init schon mitgemacht.
/**
 * Ein einzelner GENAUER Standort-Fix beim Öffnen von Zuhause (2026-08-29).
 *
 * Martin: *„auf der Zuhauseseite steht immer noch Landsee als Standort, aber
 * ich bin gerade schon in Hollenthon"* — und danach: *„Baue das bitte ein mit
 * dem GPS. Das hat ja schon mal funktioniert."*
 *
 * Gemessen war der gespeicherte Punkt frisch (15:17), aber 5000 m unscharf.
 * Hollenthon lag 5,52 km entfernt, also IM Unschärfekreis. Der Name wurde in
 * pre.2394 ehrlich gemacht; der Punkt braucht einen echten Fix.
 *
 * ⚠️ Kein Rückbau von Ticket 777. Dort ging es um die DAUERBEOBACHTUNG
 * (`watchPosition` mit GPS, solange die App offen war — 29 % Akku an einem
 * Vormittag). Hier läuft das Funkgerät für EINEN Fix und geht wieder aus.
 *
 * Zwei Bremsen, damit daraus keine Dauerbeobachtung durch die Hintertür wird:
 *
 *   1. Höchstens alle 3 Minuten. Zwischen Zuhause und Chat wird oft hin und
 *      her gewechselt — seit heute mit einem Knopf, der genau dazu einlädt.
 *      Ohne die Bremse wäre jeder Tipp ein GPS-Start.
 *   2. Nur wenn die Seite sichtbar ist. Ein Wechsel im Hintergrund (Push,
 *      Wiederherstellung) soll das Funkgerät nicht wecken.
 */
let letzterGenauerFix = 0;
const GENAUER_FIX_ABSTAND_MS = 180_000;

async function frischenStandortHolen(): Promise<void> {
  if (typeof document !== "undefined" && document.hidden) return;
  const jetzt = Date.now();
  if (jetzt - letzterGenauerFix < GENAUER_FIX_ABSTAND_MS) return;
  letzterGenauerFix = jetzt;
  try {
    const { holeStandortEinmal } = await import("./lib/geo-watch.js");
    await holeStandortEinmal(true);
  } catch {
    return; // ohne Fix bleibt der alte Ort stehen — besser als keiner
  }
  /*
   * ⚠️ Erst NACH dem Speichern erneuern. Der Fix braucht Sekunden; die
   * Anzeige oben stand längst. Ohne diese Zeile sähe Martin bis zum
   * nächsten Öffnen weiter den alten Ort — und genau das war die Meldung.
   */
  if (document.body.dataset.activeView !== "zuhause") return;
  try {
    const v = await ensureZuhauseView();
    v.refresh();
  } catch {
    /* still */
  }
}

function goToView(target: string): void {
  // #195: Beim Wechsel in einen Haupt-View zuerst alle offenen Overlays/Sheets
  // schließen. Sonst blieb ein Sheet (z.B. „Mein Alltag") über dem neuen View
  // liegen → „per Sprache schließen" wirkte nicht (open_view('chat') wechselte
  // nur den Tab darunter). Idempotent zu den Nav-Handlern, die es schon rufen.
  closeAllOverlays();
  // 2026-07-14 (Martin): Desktop behaelt die Sidebar beim Seitenwechsel.
  closeDrawerForNavigation();
  switchToTab(target);
  if (target === "chat") {
    /*
     * ⚠️ 2026-08-29: Der schwebende Knopf wurde bisher NUR beim Scrollen
     * aktualisiert. Seit er das Haus traegt, muss er schon beim Betreten des
     * Chats stehen — wer nie scrollt, saehe ihn sonst nie, und genau darum
     * ging Martins Bitte („damit man weiß, wo man da hinkommt").
     */
    updateScrollBtnVisibility();
  }
  if (target === "zuhause") {
    void ensureZuhauseView().then((v) => v.refresh());
    void frischenStandortHolen();
  } else if (target === "memory") {
    void memoryView.ensureLoaded();
  } else if (target === "settings") {
    settingsView?.destroy();
    settingsView = new SettingsView(settingsViewContainer);
  } else if (target === "examples") {
    // Pre.344: ExamplesView lazy initialisieren — kein Side-Effect bevor
    // sichtbar.
    void (async () => {
      if (!examplesView) {
        const { ExamplesView } = await import("./lib/examples-view.js");
        examplesView = new ExamplesView(examplesViewContainer);
      }
    })();
  } else if (target === "library" || target === "files") {
    // Pre.157: "files" ist Alias für die unified Mediathek (Library).
    void ensureLibraryView().refresh();
  } else if (target === "contacts") {
    void ensureContactsView().refresh();
  } else if (target === "projects") {
    void ensureProjectsView().refresh();
  } else if (target === "tasks") {
    // Pre.381: Background-Task-Manager lazy initialisieren.
    void (async () => {
      if (!tasksView) {
        const tasksContainer = document.getElementById("view-tasks");
        if (!tasksContainer) return;
        const { TasksView } = await import("./lib/tasks-view.js");
        tasksView = new TasksView(tasksContainer);
      }
    })();
  } else if (target === "automation") {
    // 2026-07-04 (Martin): Automatik-View — geplante Auftraege + System-Jobs.
    void (async () => {
      const el = document.getElementById("view-automation");
      if (!el) return;
      if (!automationView) {
        const { AutomationView } = await import("./lib/automation-view.js");
        automationView = new AutomationView(el);
      } else {
        void automationView.refresh();
      }
    })();
  } else if (target === "morse") {
    // 06.10.2026: Morse-Lernseite — dieselbe Init wie in der Klick-Kette (Pre.386-Falle: zwei Init-Ketten).
    void oeffneMorse();
  }
}

/**
 * Die Morse-Lernseite (06.10.2026, frontend/lib/morse-view.ts): beim ersten Öffnen bauen, danach den
 * Stand frisch holen. EINE Stelle für beide Init-Ketten (Tab-Klick und goToView).
 */
let morseView: import("./lib/morse-view.js").MorseView | null = null;
async function oeffneMorse(): Promise<void> {
  const el = document.getElementById("view-morse");
  if (!el) return;
  if (!morseView) {
    const { MorseView } = await import("./lib/morse-view.js");
    morseView = new MorseView(el);
  } else {
    void morseView.refresh();
  }
}

// „Morse“ im Menü „Mehr“ (frontend/lib/morse-menue.ts): nach jeder Änderung der Einstellungen neu entscheiden.
window.addEventListener("voice-v8:settings-changed", () => zeigeMorseImMenue());
window.addEventListener("voice-v8:settings-sync", () => zeigeMorseImMenue());

let zuhauseView: import("./lib/zuhause-view.js").ZuhauseView | null = null;
async function ensureZuhauseView(): Promise<import("./lib/zuhause-view.js").ZuhauseView> {
  if (!zuhauseView) {
    const el = document.getElementById("view-zuhause");
    if (!el) throw new Error("view-zuhause fehlt");
    const { ZuhauseView } = await import("./lib/zuhause-view.js");
    zuhauseView = new ZuhauseView(el);
  }
  return zuhauseView;
}
let tasksView: import("./lib/tasks-view.js").TasksView | null = null;
let automationView:
  | import("./lib/automation-view.js").AutomationView
  | null = null;
// Pre.381: globales Polling für die Nav-Badge — User sieht in der Sidebar
// "(2)" wenn Tasks laufen, auch ohne den Tasks-Tab offen zu haben.
// Pre.382: Topbar-Pulse synct sich darüber mit.
void (async () => {
  const { startGlobalTasksPolling } = await import("./lib/tasks-view.js");
  startGlobalTasksPolling();
})();
document
  .getElementById("topbar-tasks-pulse")
  ?.addEventListener("click", () => goToView("tasks"));

// Aufnahme-Indikator im Header: zeigt eine laufende (native) Aufnahme global an
// und öffnet bei Tippen den Recorder (Pause/Stopp). Nur nativ relevant; im
// Browser/PWA bleibt er aus (kein nativer Recorder).
void (async () => {
  const ind = document.getElementById("topbar-rec-indicator");
  if (!ind) return;
  ind.addEventListener("click", () => {
    void import("./lib/alltag-view.js").then((m) => m.openRecorderOverlay());
  });
  const nr = await import("./lib/native-recorder.js");
  // ⚠️ 22.09.2026 — sonst bleibt die Aufnahme-Anzeige im Kopf im Browser
  //    stumm, obwohl dort eine Aufnahme laufen kann.
  if (!nr.kannAufzeichnen()) return;
  const lbl = ind.querySelector(".topbar-rec-label");
  const navBtn = document.getElementById("open-alltag-btn");
  // 2026-07-24 (Martin: "Symbol soll aufleuchten, dass verarbeitet wird"):
  // nach dem Stopp laeuft die Finalisierung serverseitig noch ~40s+ weiter.
  // processingActive kommt aus dem busy-Poll unten (server-getrieben,
  // ueberlebt App-Neustart).
  let processingActive = false;
  const update = (st: string): void => {
    const recActive = st === "recording" || st === "paused";
    // Verarbeitung nur zeigen, wenn NICHT gerade aufgenommen wird.
    const showProc = processingActive && !recActive;
    ind.hidden = !(recActive || showProc);
    ind.dataset.state = showProc ? "processing" : st;
    // Sidebar "Mein Alltag": roter Punkt bei Aufnahme, bernstein bei Verarbeitung.
    navBtn?.classList.toggle("is-recording", recActive);
    navBtn?.classList.toggle("is-processing", showProc);
    if (lbl) {
      /*
       * 2026-07-28 (Martin: "Das Button-Feld 'verarbeitet' mit den Punkten
       * verbraucht ein bisschen zu viel Platz in der Headerline"): Recht hat
       * er — "Verarbeitet …" war mit Abstand das breiteste Label (13 Zeichen
       * gegen 8 bei "Aufnahme"), und es erscheint ausgerechnet dann, wenn die
       * Kopfzeile ohnehin voll ist.
       *
       * ⚠️ 2026-09-14 (Ticket 2229) — die Antwort darauf war zu radikal.
       *
       * Das Label wurde daraufhin auf "" gesetzt: der Punkt pulsierte, und
       * was er bedeutete, stand NUR noch in title/aria-label. Auf dem
       * Telefon gibt es kein Hover — fuer Martin war das unsichtbar. Genau
       * das ist sein Ticket 2229: "Manchmal erscheint das Mikrofon-Zeichen,
       * aber ich sehe keine zugehoerige Information ... ich weiss in dem
       * Moment nicht, ob wirklich aufgenommen wird." Seine Bedingung dort:
       * "Kein Zeichen ohne Text."
       *
       * Beides ist erfuellbar. Der Platz war nie das Problem des Textes an
       * sich, sondern seiner Breite: "Speichert" ist 9 Zeichen und damit
       * schmaler als das breiteste Label, das die Kopfzeile bei laufender
       * Aufnahme ohnehin traegt. Der ausfuehrliche Satz bleibt im Titel.
       */
      if (showProc) lbl.textContent = "Speichert";
      // 2026-07-24 (Martin): Timer statt "Aufnahme" — kleiner, informativer.
      else if (st === "paused") lbl.textContent = "Pausiert";
      else if (st === "recording")
        lbl.textContent = nr.formatRecordingElapsed() || "Aufnahme";
      else lbl.textContent = "Aufnahme";
    }
    if (showProc) {
      ind.setAttribute("aria-label", "Aufnahme wird verarbeitet");
      ind.title = "Aufnahme wird verarbeitet";
    } else if (st === "paused") {
      ind.setAttribute("aria-label", "Aufnahme pausiert — antippen für Pause oder Stopp");
      ind.title = "Aufnahme pausiert";
    } else {
      ind.setAttribute(
        "aria-label",
        "Aufnahme läuft — antippen für Pause oder Stopp",
      );
      ind.title = "Aufnahme läuft — antippen für Pause oder Stopp";
    }
  };
  /**
   * 2026-07-31 (Martin: "mein Akku ist immer extrem schnell leer"):
   *
   * Hier stand ein starrer 6-Sekunden-Takt. Gemessen ergab das 426 Anfragen
   * pro Stunde allein fuer diese eine Frage — ob gerade eine Aufnahme
   * finalisiert wird. Das aendert sich vielleicht dreimal am Tag.
   *
   * Jeder dieser Kontakte weckt das Funkmodul, und DAS kostet Strom, nicht
   * die Rechenarbeit. Zum Vergleich: Das Buendeln der Aufnahme-Haeppchen am
   * 27.07. senkte 180 Weckungen pro Stunde auf sechs und war damit der
   * groesste Energiegewinn bisher.
   *
   * Jetzt passt sich der Takt an: schnell, solange sich etwas aendert,
   * danach in Schritten langsamer bis zu einer Minute. Sobald sich etwas
   * tut, springt er sofort zurueck — die Anzeige bleibt flink, wo es
   * darauf ankommt.
   */
  const pollProcessing = async (): Promise<boolean> => {
    const r = await fetch("/api/system/busy", { credentials: "include" });
    if (!r.ok) return false;
    const j = (await r.json()) as { reasons?: string[] };
    const proc = (j.reasons ?? []).some((x) => x.startsWith("recorder-finish"));
    if (proc !== processingActive) {
      processingActive = proc;
      update(nr.getRecorderState());
      return true; // Aenderung -> wieder schnell fragen
    }
    return false;
  };
  starteAnpassendenTakt({
    abfrage: pollProcessing,
    schnellMs: 6000,
    langsamMs: 60_000,
    name: "recorder-finish",
  });
  // Sekundentakt: Timer im Topbar-Label mitlaufen lassen.
  window.setInterval(
    gesteuert(
      "live",
      () => {
        if (document.visibilityState !== "visible") return;
        if (nr.getRecorderState() === "recording" && lbl) {
          const t = nr.formatRecordingElapsed();
          if (t) lbl.textContent = t;
        }
      },
      true,
    ),
    1000,
  );
  // 2026-07-02 (Martin, #35): beim Zurueckkehren in den Vordergrund den
  // echten Zustand neu abgleichen — sonst bleibt der Indikator nach einem
  // Widget-/Hintergrund-Stopp faelschlich auf „Aufnahme" stehen.
  // 2026-07-14 (Martin, "weiss nicht was Sache ist"): zusaetzlich zur
  // Sichtbarkeits-Synchronisation ALLE 15s gegen den echten nativen
  // Recorder abgleichen — der Indikator kann damit nie mehr laenger als
  // 15s luegen (egal welcher Event verpasst wurde).
  window.setInterval(
    gesteuert(
      "live",
      () => {
        if (document.visibilityState !== "visible") return;
        void nr
          .syncFromNative()
          .then(() => update(nr.getRecorderState()))
          .catch(() => {});
      },
      true,
    ),
    15_000,
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    void (async () => {
      try {
        await nr.syncFromNative();
        update(nr.getRecorderState());
        void pollProcessing();
      } catch {
        /* nicht kritisch */
      }
    })();
  });
  try {
    await nr.syncFromNative();
    update(nr.getRecorderState());
    // 2026-07-13 (Martin, "Icon oben zeigt Aufnahme, Ansicht zeigt 0"): nach
    // App-Neustart kann der native Zustand stale auf "recording" stehen,
    // obwohl serverseitig laengst beendet. Beim Start gegen die SERVER-Wahrheit
    // pruefen — sagt der Server "finished" (kein aktiver Chunk), Icon verbergen.
    const st0 = nr.getRecorderState();
    if (st0 === "recording" || st0 === "paused") {
      try {
        const r = await fetch("/api/recorder/session-status", {
          credentials: "include",
        });
        if (r.ok) {
          const j = (await r.json()) as {
            state?: string;
            lastChunkTs?: number | null;
          };
          const stale =
            j.state === "finished" &&
            (j.lastChunkTs == null || Date.now() - j.lastChunkTs > 90_000);
          if (stale && nr.getRecorderState() === st0) {
            ind.hidden = true;
            ind.dataset.state = "idle";
          }
        }
      } catch {
        /* Server-Gegenprobe best-effort */
      }
    }
    // #35: Global-Kanal — vorher verdraengte das Aufnahme-Overlay diesen
    // Callback und der Header blieb nach dem Stopp auf "Aufnahme" stehen.
    nr.observeRecorderGlobal(update);
    await nr.observeRecorder({ onState: update, onError: () => {} });
  } catch {
    /* nicht kritisch */
  }

  // P1 ZOMBIE-DETEKTOR (11.07.: Server schloss die Session um 10:00, App nahm
  // bis 18:00 'auf' — niemand sah es): waehrend einer Aufnahme jede Minute den
  // Server fragen. Session serverseitig zu ODER >3 min kein Empfang -> Punkt
  // amber/rot + EINE klare Warnung.
  // 2026-07-29 (Audit C): Drei Minuten stammten aus der Zeit vor dem
  // Buendeln. Seit die Haeppchen nur noch alle zehn Minuten hochgeladen
  // werden, schlug dieser Waechter in JEDEM gesunden Zyklus an — der
  // Indikator kippte auf Amber, und es kam die Meldung "Netz/Empfang
  // pruefen", obwohl alles in Ordnung war. Ein Waechter, der staendig
  // grundlos warnt, ist schlimmer als keiner: Man gewoehnt sich an sein
  // Signal und uebersieht den echten Fall.
  // 15 Minuten = Sende-Fenster plus Luft (siehe server/aufnahme-fristen.ts;
  // die Zahl steht hier bewusst als Literal, weil das Frontend nicht aus
  // server/ importieren kann — der Kommentar haelt die Kopplung fest).
  const TRUST_WARN_MS = 15 * 60_000;
  let trustWarned = false;
  window.setInterval(gesteuert("live", () => {
    void (async () => {
      const st = nr.getRecorderState();
      if (st !== "recording" && st !== "paused") {
        delete ind.dataset.trust;
        trustWarned = false;
        return;
      }
      // 2026-07-28 (Akku, #744): Diese Wache fragte JEDE MINUTE beim Server
      // nach — 1.440 Funk-Weckungen an einem Tag mit durchgehender Aufnahme.
      // Damit machte sie genau das zunichte, wofuer das Buendeln der
      // Haeppchen gebaut wurde: "Nicht die Datenmenge kostet, sondern die
      // HAEUFIGKEIT der Funk-Weckungen." Die Haeppchen gehen bewusst nur alle
      // zehn Minuten raus; der Funk wurde trotzdem minuetlich geweckt, fuer
      // eine winzige Statusabfrage.
      //
      // Sie faerbt einen INDIKATOR ein. Schaut niemand hin, gibt es nichts
      // anzuzeigen — im Hintergrund also gar nicht erst funken. Beim
      // Zurueckkehren laeuft sie sofort wieder (der Takt tickt weiter, die
      // erste sichtbare Runde fragt frisch nach).
      if (document.visibilityState !== "visible") return;
      try {
        const r = await fetch("/api/recorder/session-status", {
          credentials: "include",
        });
        const j = (await r.json()) as {
          ok?: boolean;
          state?: string;
          lastChunkTs?: string | null;
        };
        if (!j?.ok) return;
        const closed =
          j.state === "finished" || j.state === "quarantined";
        const last = j.lastChunkTs ? Date.parse(j.lastChunkTs) : 0;
        const oldEnough =
          Date.now() - (nr.getRecordingStartedAt() || Date.now()) >
          TRUST_WARN_MS;
        const stale =
          st === "recording" &&
          oldEnough &&
          (!last || Date.now() - last > TRUST_WARN_MS);
        ind.dataset.trust = closed ? "err" : stale ? "warn" : "ok";
        if ((closed || stale) && !trustWarned) {
          trustWarned = true;
          window.dispatchEvent(
            new CustomEvent("voice-v8:toast", {
              detail: {
                message: closed
                  ? "Achtung: Der Server hat diese Aufnahme-Session geschlossen — bitte Aufnahme stoppen und neu starten (nichts geht verloren, alles ist archiviert)."
                  /*
                   * ⚠️ 21.09.2026 — hier stand „seit über 3 Minuten". Die
                   *    Schwelle ist seit dem 28.07. TRUST_WARN_MS, also 15
                   *    Minuten. Eine Meldung, die eine andere Zahl nennt als
                   *    die Regel darüber, macht aus einem Befund ein Rätsel.
                   */
                  : "Aufnahme läuft, aber der Server hat seit über 15 Minuten nichts empfangen — Netz/Empfang prüfen.",
                kind: "info",
              },
            }),
          );
        }
        if (!closed && !stale) trustWarned = false;
      } catch {
        /* Netzfehler beim Poll: keine Trust-Aenderung */
      }
    })();
  // Takt von 60 s auf 5 Minuten: Die Haeppchen kommen gebuendelt alle zehn
  // Minuten, ein minuetlicher Blick darauf kann also gar nichts Neues sehen.
  // Fuenf Minuten liegen sicher unter dem Fenster und kosten ein Fuenftel.
  }, true), 5 * 60_000);
})();

const tabButtons = document.querySelectorAll<HTMLButtonElement>(
  ".nav-item[data-tab]",
);

// --- Drawer-Logik (Hamburger-Menü) ----------------------------------
//
// Mobile (< 768px): Drawer überlagert die App von links, Tab-Klick schließt ihn.
// Desktop (>= 768px): Drawer ist permanente Sidebar links, App-Content shiftet
// per padding-left auf #app. Tab-Klick schließt NICHT. Hamburger-Btn blendet
// optional auf Desktop weg, ist aber als Toggle weiterhin nutzbar.
const drawer = document.getElementById("nav-drawer");
// 2026-07-08 (Martin, Menue hinter Essenz-Spiegel): Drawer als PORTAL direkt
// unter <body>. In <main> gefangen erzeugten Repaint-Transforms auf main einen
// Stacking-Context — der Drawer malte trotz z-index 3000 UNTER body-Level-
// Overlays (Essenz/HD/Buch haengen an document.body).
if (drawer && drawer.parentElement !== document.body) {
  document.body.appendChild(drawer);
}
const menuBtn = document.getElementById("menu-btn") as HTMLButtonElement | null;
const desktopMq = window.matchMedia("(min-width: 768px)");
const isDesktop = (): boolean => desktopMq.matches;

// Pre.745: Desktop-Menü hat zwei sichtbare Layouts — voll (--sidebar-w) und
// Icon-Rail (--rail-w, nur Symbole). Mobile bleibt Off-Canvas (offen/zu).
// Der gewählte Zustand wird in localStorage gemerkt (Muster wie das Theme).
const NAV_MODE_KEY = "voice-v8.nav-mode";
function loadNavMode(): "full" | "rail" {
  try {
    return localStorage.getItem(NAV_MODE_KEY) === "rail" ? "rail" : "full";
  } catch {
    return "full";
  }
}
/**
 * In den Einstellungen am Rechner (ab 1024) steht die Leiste immer als schmale
 * Symbolleiste (Zuhause, Chat, Bereiche); rechts daneben die Einstellungsliste.
 * Das ist ein Zustand der ANSICHT, nicht die Wahl der Nutzerin — gespeichert wird
 * sie deshalb nie (setNavMode schreibt nur ihre eigene Wahl).
 */
const einstMq = window.matchMedia("(min-width: 1024px)");
function leisteSchmalInEinstellungen(): boolean {
  return einstMq.matches && document.body.dataset.activeView === "settings";
}
function applyNavMode(mode: "full" | "rail"): void {
  // Rail gibt es NUR auf Desktop; auf Mobile nie (dort Off-Canvas).
  const rail = (mode === "rail" || leisteSchmalInEinstellungen()) && isDesktop();
  document.body.classList.toggle("nav-rail", rail);
  menuBtn?.setAttribute("aria-expanded", rail ? "false" : "true");
  // 23.09.2026, Ticket 2487: Die volle Leiste zeigt den Verbrauch, also hier
  // nachfragen (höchstens einmal je Minute). Hier laufen Öffnen, Ausklappen
  // aus dem Symbolmodus und am Desktop jeder Seitenwechsel durch. Scheitert
  // der Abruf beim Start, kommt die Kachel so beim nächsten Wechsel zurück.
  if (!rail) void ermittleZugang().finally(() => aktualisiereVerbrauch());
}
function setNavMode(mode: "full" | "rail"): void {
  try {
    localStorage.setItem(NAV_MODE_KEY, mode);
  } catch {
    /* localStorage evtl. gesperrt — Zustand gilt dann nur für die Sitzung */
  }
  applyNavMode(mode);
}
// Pre.1047: Puls am „Mein Alltag"-Menüeintrag, solange eine Aufnahme verarbeitet
// wird (Martins Wunsch: in der Sidebar sehen, dass etwas läuft). Aktualisiert
// beim Öffnen des Drawers. Fail-safe: jede Panne → kein Puls.
// 2026-07-14 (Martin): frischer Essenzspiegel -> Punkt am Menue-Eintrag,
// sichtbar bis 23:00 des Tages (Erinnerung an den Abend-Spiegel). Klick
// auf Essenz gilt als gesehen (localStorage-Zeitstempel).
function refreshEssenceNavHint(): void {
  const btn = document.getElementById("open-essence-btn");
  if (!btn) return;
  // 24.09.2026, Stufe 0a: ohne Spiegel im Paket nicht fragen (siehe essenzFrei).
  if (!essenzFrei) {
    btn.classList.remove("has-fresh");
    return;
  }
  void fetch("/api/essence-mirror?peek=1", { credentials: "same-origin" })
    .then((r) => r.json())
    .then((d: { ok?: boolean; generatedAt?: string }) => {
      let fresh = false;
      if (d.ok && d.generatedAt) {
        const gen = new Date(d.generatedAt);
        const nowV = new Date();
        const sameDay =
          gen.toLocaleDateString("sv-SE", { timeZone: "Europe/Vienna" }) ===
          nowV.toLocaleDateString("sv-SE", { timeZone: "Europe/Vienna" });
        const hour = Number(
          nowV.toLocaleString("en-GB", {
            timeZone: "Europe/Vienna",
            hour: "2-digit",
            hour12: false,
          }),
        );
        let seen = 0;
        try {
          seen = Number(localStorage.getItem("voice-v8.essence-seen") || 0);
        } catch {
          /* localStorage gesperrt */
        }
        fresh = sameDay && hour < 23 && gen.getTime() > seen;
      }
      btn.classList.toggle("has-fresh", fresh);
      if (fresh) btn.title = "Neuer Essenzspiegel ist fertig";
    })
    .catch(() => btn.classList.remove("has-fresh"));
}
document.getElementById("open-essence-btn")?.addEventListener("click", () => {
  try {
    localStorage.setItem("voice-v8.essence-seen", String(Date.now()));
  } catch {
    /* localStorage gesperrt */
  }
  document.getElementById("open-essence-btn")?.classList.remove("has-fresh");
});
// Ticket 2752: ein Hinweis am Knopf — im Hintergrund sieht ihn niemand.
starteSichtbarenTakt(refreshEssenceNavHint, 10 * 60_000);
window.setTimeout(refreshEssenceNavHint, 8_000);

function refreshAlltagNavPulse(): void {
  const btn = document.getElementById("open-alltag-btn");
  if (!btn) return;
  // 2026-07-18 (Martin, Verschwendung): nur die ZAHL der laufenden
  // Verarbeitungen wird gebraucht. Vorher holte das bei JEDEM Oeffnen der
  // Sidebar die komplette Alltag-Liste (~120 kB, ~180 Notiz-Dateien).
  void import("./lib/alltag-api.js")
    .then((m) => m.fetchAlltagStatus(1))
    .then((st) => {
      btn.classList.toggle("is-processing", (st?.now?.processing ?? 0) > 0);
    })
    .catch(() => {
      btn.classList.remove("is-processing");
    });
}

function openDrawer(): void {
  if (!drawer) return;
  drawer.hidden = false;
  drawer.classList.toggle("is-desktop", isDesktop());
  document.body.classList.add("drawer-open");
  // Desktop: gemerkten voll/rail-Zustand anwenden. Mobile: immer voll.
  applyNavMode(isDesktop() ? loadNavMode() : "full");
  refreshAlltagNavPulse();
  refreshEssenceNavHint();
}
// 23.09.2026, Ticket 2487: zurück im Vordergrund den Verbrauch nachfragen,
// wenn die volle Leiste zu sehen ist (nicht zu, nicht im Symbolmodus).
nachfragenBeiRueckkehr(
  () => !!drawer && !drawer.hidden && !document.body.classList.contains("nav-rail"),
);
// 2026-07-14 (Martin, MIT NACHDRUCK): beim Seitenwechsel bleibt die
// Sidebar am DESKTOP SICHTBAR (>=1200px voll, darunter Icon-Rail), damit
// man zwischen Mediathek & Co. hin- und herwechseln kann. Ersetzt die
// 05.07.-Regel "schliesst auch am Desktop" an allen Navigations-Stellen.
// Nur Mobile schliesst weiter (Off-Canvas braucht die volle Breite).
// 2026-07-20 (Martin, "Sidebar verschwindet"): Jede Vollseiten-Seite
// meldet ihr Oeffnen; zentral halten wir am Desktop die Sidebar sichtbar
// (wie Human Design/Essenz). EIN Hook fuer alle Seiten, kein Duplikat.
window.addEventListener("voice-v8:page-open", () =>
  closeDrawerForNavigation(),
);
/** Nach jedem Ansichtswechsel: am Rechner steht die Leiste (voll oder schmal), in den Einstellungen schmal. */
function richteLeisteFuerAnsichtEin(): void {
  if (!drawer || !isDesktop()) return;
  if (drawer.hidden) openDrawer();
  else applyNavMode(loadNavMode());
}
einstMq.addEventListener("change", richteLeisteFuerAnsichtEin);
window.addEventListener("voice-v8:ansicht-gewechselt", richteLeisteFuerAnsichtEin);
function closeDrawerForNavigation(): void {
  if (isDesktop()) {
    if (drawer?.hidden) openDrawer();
    applyWidthNavMode();
    return;
  }
  closeDrawer();
}

// 2026-07-29 (Audit Paket F): Die Nutzerverwaltung liegt in
// lib/users-admin-view.ts. Was sie frueher still aus dem gemeinsamen
// Namensraum nahm, wird ihr hier ausdruecklich uebergeben — dadurch steht
// schwarz auf weiss, wovon dieser Teil wirklich abhaengt.
//
// 2026-08-26: Dieser Aufruf lag seit dem 29.07. IM Rumpf des
// wake-lock-release-Handlers (ein Patch hatte ihn dort hineingeschnitten und
// dabei dessen eigenen Rumpf ueberschrieben). Er lief damit praktisch nie —
// die Seite "Nutzer" warf vier Wochen lang "AdminUmgebung nicht gesetzt" und
// oeffnete gar nicht. Er steht jetzt hier, weil `drawer` erst weiter oben in
// dieser Datei deklariert wird: an der alten Stelle waere er beim Start in die
// zeitliche Totzone gelaufen.
setzeAdminUmgebung({
  refs: refs as unknown as Record<string, HTMLElement | null> &
    Record<string, unknown>,
  drawer,
  scrollToBottomBtn,
  connecting: () => connecting,
  unreadCount: () => unreadCount,
  closeDrawerForNavigation,
  openHumanDesignSheet,
  pinChatToBottom,
  prewarmChatSession,
  updateScrollBtnVisibility,
  updateUnreadBadge,
});

function closeDrawer(): void {
  if (!drawer) return;
  // Pre.94: closeDrawer schließt IMMER — auch im Desktop. User entscheidet
  // selbst per X-Button oder Hamburger ob die Sidebar offen sein soll.
  // Tab-Klick im Desktop schließt NICHT (Logik in tabButtons.click handler).
  drawer.hidden = true;
  drawer.classList.remove("is-desktop");
  document.body.classList.remove("drawer-open");
  document.body.classList.remove("nav-rail");
  menuBtn?.setAttribute("aria-expanded", "false");
}
// Pre.746: EIN Toggle-Verhalten, geteilt vom Topbar-Hamburger (nur Mobile) und
// dem Sidebar-internen Toggle (Desktop — der Hamburger gehört optisch zum Menü).
function toggleNav(): void {
  if (isDesktop()) {
    // Desktop: voll <-> Icon-Rail umschalten (Sidebar bleibt sichtbar).
    if (drawer?.hidden) {
      openDrawer();
      return;
    }
    setNavMode(loadNavMode() === "rail" ? "full" : "rail");
  } else if (drawer?.hidden) {
    openDrawer();
  } else {
    closeDrawer();
  }
}
menuBtn?.addEventListener("click", toggleNav);
// 2026-07-05 (Martin): Zurueck-Pfeil einer Vollseite -> Menue wieder
// ausklappen, damit direkt das naechste Element waehlbar ist.
document.addEventListener("vespera:menu-reopen", () => openDrawer());
document.addEventListener("vespera:go-chat", () => goToView("chat"));
document.addEventListener("vespera:einstellungen-verlassen", () => {
  const ziel = rueckweg.ziel();
  goToView(ziel);
  fokusNachEinstellungen(document, ziel);
});
// 13.10.2026 (Zuhause → Neu): drei weitere Wege, damit eine Karte etwas Echtes öffnet. Dieselbe Weise wie go-chat:
// Ereignis statt Aufruf, zuhause-view.ts greift nicht in app.ts hinein.
document.addEventListener("vespera:go-view", ((ev: Event) => {
  const view = (ev as CustomEvent<{ view?: string }>).detail?.view;
  if (view && ANSICHTEN.has(view)) goToView(view);
}));
document.addEventListener("vespera:go-seite", ((ev: Event) => {
  const key = (ev as CustomEvent<{ key?: string }>).detail?.key;
  if (key) oeffneSeite(key);
}));
document.addEventListener("vespera:was-ist-neu-oeffnen", () => void wasIstNeu.oeffne(null));
document.addEventListener("vespera:go-settings", ((ev: Event) => {
  // 22.10.2026: `fokus` (Überschrift eines Abschnitts) reist mit — die Seite setzt den Fokus dorthin (settings-view.ts).
  const { sub, fokus } = (ev as CustomEvent<{ sub?: string; fokus?: string }>).detail ?? {};
  goToView("settings");
  if (!sub) return;
  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent("voice-v8:settings-subtab", { detail: { sub, fokus } }),
    );
  }, 50);
}));
/*
 * 2026-08-29: Zuhause kann jetzt einen BESTIMMTEN Spiegel oeffnen.
 * Ein Ereignis statt eines Aufrufs, weil zuhause-view.ts nicht in app.ts
 * hineingreift — alle anderen Wege von dort laufen genauso.
 */
document.addEventListener("vespera:spiegel-tag", (ev: Event) => {
  const tag = (ev as CustomEvent<string>).detail;
  void openEssenceSheet(typeof tag === "string" ? tag : undefined, "zuhause");
});
// 23.09.2026: zwei weitere Wege aus dem Zuhause (Drei-Reiter-Entwurf) —
// „Spiegel anhören" (Heute) spielt gleich ab, „Alle Spiegel im Verlauf"
// (Rückblick) öffnet gleich die Liste der Tage.
document.addEventListener("vespera:spiegel-anhoeren", () => {
  void openEssenceSheet(undefined, "zuhause-anhoeren", { abspielen: true });
});
document.addEventListener("vespera:spiegel-verlauf", () => {
  void openEssenceSheet(undefined, "zuhause-verlauf", { verlauf: true });
});
/*
 * 23.09.2026 (Ticket 2549): die Morgenmeditation aus dem Zuhause abspielen.
 * Der Player entsteht HIER (buildNarrationPlayer lebt hier); was mit ihm
 * geschieht — Endpunkt der fertigen Datei, Vermerk erst beim „gehört" —
 * steht seit 27.09. (Ticket 2636) in lib/meditation-anhoeren.ts, wo der Test
 * den echten Hörer fährt.
 */
document.addEventListener("vespera:meditation-anhoeren", (ev: Event) =>
  meditationAnhoeren(ev, buildNarrationPlayer, vorleseStimme),
);

document.addEventListener("vespera:zuhause-sprechen", () => {
  // Ins Gespräch. Mikro erst, wenn Push gedrückt wird — nicht schon hier.
  goToView("chat");
});
document.addEventListener("vespera:zuhause-aufnehmen", () => {
  void import("./lib/alltag-view.js").then((m) =>
    m.openRecorderOverlay({ start: true }),
  );
});
document.addEventListener("vespera:zuhause-frage", (ev: Event) => {
  const frage = (ev as CustomEvent<string>).detail;
  if (typeof frage !== "string" || !frage.trim()) return;
  goToView("chat");
  // Prüfbefund 27.10.2026 (Kann 3): eine Frage der Zuhause-Seite ist kein Knopf einer Karte — ohne die Marke `vonKarte` (sonst löste ein
  // Text, der wortgleich einem Mandats-Knopf ist, ein Mandat ein).
  void sendUserMessageFromCard(frage.trim(), undefined, false);
});
// 2026-07-20 (Martin, "Sidebar verschwindet"): desktop-bewusst — auf dem
// Desktop bleibt die Sidebar (closeDrawerForNavigation haelt sie), nur mobil
// wird der Drawer geschlossen. Fixt Material/Alltag/Tiefe-Fragen einheitlich.
document.addEventListener("vespera:menu-close", () => closeDrawerForNavigation());
document
  .getElementById("nav-drawer-toggle")
  ?.addEventListener("click", toggleNav);
document.querySelectorAll<HTMLElement>("[data-drawer-close]").forEach((el) =>
  el.addEventListener("click", () => {
    // Desktop: „Schließen" klappt zur Icon-Rail ein (Menü verschwindet NICHT).
    // Mobile: schließt das Off-Canvas-Menü wie bisher.
    if (isDesktop()) setNavMode("rail");
    else closeDrawer();
  }),
);
document.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape" && drawer && !drawer.hidden && !isDesktop())
    closeDrawer();
});
// 2026-07-14 (Martin, "Seitenbar bei Inhalte-Wahl sichtbar lassen"):
// Die Fensterbreite entscheidet den Menue-Modus — ab 1200px bleibt die
// Sidebar VOLL sichtbar (genug Platz), zwischen 768 und 1199px klappt
// sie automatisch zur Icon-Rail ein. Manuelles Umschalten bleibt
// moeglich; beim Ueberschreiten der 1200px-Grenze wird neu gesetzt.
const wideMq = window.matchMedia("(min-width: 1200px)");
function applyWidthNavMode(): void {
  if (!isDesktop() || drawer?.hidden) return;
  setNavMode(wideMq.matches ? "full" : "rail");
}
wideMq.addEventListener("change", applyWidthNavMode);
desktopMq.addEventListener("change", () => {
  if (!drawer) return;
  if (isDesktop()) {
    // Wechsel auf Desktop: Sidebar automatisch öffnen (sofern nicht im Login).
    if (menuBtn && getComputedStyle(menuBtn).display !== "none") {
      openDrawer();
      applyWidthNavMode();
    }
  } else {
    // Wechsel auf Mobile: Sidebar schließen damit der Content full-width geht.
    drawer.hidden = true;
    drawer.classList.remove("is-desktop");
    document.body.classList.remove("drawer-open");
    document.body.classList.remove("nav-rail");
  }
});

// Pre.928: Bottom-Nav (mobil) — Daumen-Navigation statt oben-Hamburger.
// Nur mobil aktiv (has-bottom-nav); Desktop nutzt die Sidebar. "Mehr" öffnet
// das bestehende Menü (Hamburger bleibt vorerst parallel als Sicherheitsnetz).
function syncBottomNav(): void {
  // 2026-07-09 (Martin, "Leiste kaum genutzt, redundant zum Hamburger"): untere
  // Tab-Leiste ausgeblendet. Die bnav-* Buttons bleiben (versteckt) im DOM —
  // sie dienen intern weiter als Navigations-API (bnav-chat?.click() ->
  // switchToTab("chat") u.a.); .click() wirkt auch bei display:none.
  document.body.classList.remove("has-bottom-nav");
}
syncBottomNav();
desktopMq.addEventListener("change", syncBottomNav);

// Untere Leiste ein-/ausblenden (mehr Platz auf kleinen Geräten). Gemerkt.
const BOTTOM_NAV_COLLAPSED_KEY = "voice-v8.bottomNavCollapsed";
const bottomNavToggle = document.getElementById("bottom-nav-toggle");
function applyBottomNavCollapsed(collapsed: boolean): void {
  document.body.classList.toggle("bottom-nav-collapsed", collapsed);
  bottomNavToggle?.setAttribute("aria-expanded", collapsed ? "false" : "true");
  bottomNavToggle?.setAttribute(
    "aria-label",
    collapsed ? "Untere Leiste einblenden" : "Untere Leiste ausblenden",
  );
}
try {
  applyBottomNavCollapsed(
    localStorage.getItem(BOTTOM_NAV_COLLAPSED_KEY) === "1",
  );
} catch {
  /* localStorage kann werfen — dann bleibt die Leiste sichtbar (Default) */
}
bottomNavToggle?.addEventListener("click", () => {
  const next = !document.body.classList.contains("bottom-nav-collapsed");
  applyBottomNavCollapsed(next);
  try {
    localStorage.setItem(BOTTOM_NAV_COLLAPSED_KEY, next ? "1" : "0");
  } catch {
    /* ignore */
  }
});

document.getElementById("bnav-zuhause")?.addEventListener("click", () => {
  goToView("zuhause");
});
document.getElementById("bnav-chat")?.addEventListener("click", () => {
  closeAllOverlays();
  switchToTab("chat");
});
document
  .getElementById("bnav-leben")
  ?.addEventListener("click", () => void openBookSheet());
document
  .getElementById("bnav-projekte")
  ?.addEventListener("click", () => goToView("projects"));
document
  .getElementById("bnav-mehr")
  ?.addEventListener("click", () => openDrawer());

// Pre.745: Tooltips für die Icon-Rail — der Titel kommt aus dem Label, damit
// man im eingeklappten Menü beim Hover sieht, was das Symbol bedeutet.
drawer?.querySelectorAll<HTMLElement>(".nav-item").forEach((item) => {
  const label = item.querySelector("span")?.textContent?.trim();
  if (label && !item.title) item.title = label;
});

// pre.903 (Martin): „Blättern" — jeder Klick auf einen Menü-Eintrag schließt
// ZUERST alle offenen Overlays (Capture-Phase, vor dem Button-eigenen Handler),
// sodass nur die gewählte Ansicht offen ist.
// Ticket 2680 (28.09.2026): ausgenommen sind Handlungen ohne Seitenwechsel
// (`data-bleibt`, das Bildschirmfoto) — die offene Seite gehört ins Bild.
// 22.10.2026: Ein Zeichen der Symbolreihe, das eine Seite öffnet (`data-seite`,
// das Archiv), blättert genauso — es stand bis dahin als Eintrag in der Liste.
drawer?.addEventListener(
  "click",
  (ev) => {
    const t = ev.target as HTMLElement | null;
    if (t?.closest?.(":is(.nav-item, .nav-action[data-seite]):not([data-bleibt])")) closeAllOverlays();
  },
  true,
);

for (const btn of tabButtons) {
  btn.addEventListener("click", async () => {
    const target = btn.dataset.tab;
    if (!target) return;
    if (target === "zuhause") {
      goToView("zuhause");
      return;
    }
    switchToTab(target);
    // Mobile: Drawer schließen. Desktop: User entscheidet selbst.
    closeDrawerForNavigation();
    if (target === "memory") {
      await memoryView.ensureLoaded();
    } else if (target === "settings") {
      // Bei jedem Öffnen frisch rendern, damit Module-Liste aktuell ist.
      settingsView?.destroy();
      settingsView = new SettingsView(settingsViewContainer);
    } else if (target === "tools") {
      // Pre.288 Bug-Fix: Auf Mobile NICHT chat sichtbar lassen (sah aus
      // als wären "Sprechen" und "Module" das gleiche). Nur im Desktop
      // bleibt Chat als Speedbar-Hintergrund.
      const chatView = document.getElementById("view-chat");
      if (isDesktop()) {
        if (chatView) chatView.hidden = false;
      } else {
        if (chatView) chatView.hidden = true;
      }
      toolsViewContainer.hidden = false;
      document.body.classList.add("tools-open");
      if (!toolsView) {
        toolsView = new ToolsView(toolsViewContainer, client, isConnected, {
          onSendText: async (text) => {
            await sendUserMessage(text);
          },
          // Kein onAfterRun mehr — Drawer bleibt persistent offen.
          onClose: () => {
            toolsViewContainer.hidden = true;
            document.body.classList.remove("tools-open");
            switchToTab("chat");
          },
        });
      }
    } else if (target === "library" || target === "files") {
      void ensureLibraryView().refresh();
    } else if (target === "contacts") {
      void ensureContactsView().refresh();
    } else if (target === "projects") {
      void ensureProjectsView().refresh();
    } else if (target === "examples") {
      // Pre.345 Fix: Examples-View lazy initialisieren beim Tab-Klick.
      if (!examplesView) {
        const { ExamplesView } = await import("./lib/examples-view.js");
        examplesView = new ExamplesView(examplesViewContainer);
      }
    } else if (target === "tasks") {
      // Pre.386 Fix: gleicher Bug wie examples (Pre.345) — Tab-Click ohne
      // Init. Hintergrund-Tasks-View war leer trotz Badge "1".
      if (!tasksView) {
        const tasksContainer = document.getElementById("view-tasks");
        if (tasksContainer) {
          const { TasksView } = await import("./lib/tasks-view.js");
          tasksView = new TasksView(tasksContainer);
        }
      }
    } else if (target === "automation") {
      // 2026-07-04: Automatik-View — Klick-Kette braucht die Init genauso
      // wie goToView (Pre.386-Falle: zwei Init-Ketten).
      const el = document.getElementById("view-automation");
      if (el) {
        if (!automationView) {
          const { AutomationView } = await import("./lib/automation-view.js");
          automationView = new AutomationView(el);
        } else {
          void automationView.refresh();
        }
      }
    } else if (target === "morse") {
      // 06.10.2026: Morse-Lernseite — dieselbe Init wie in goToView (Pre.386-Falle: zwei Init-Ketten).
      await oeffneMorse();
    }
  });
}

// --- Verlauf-Lösch-Geste auf der Chat-Liste -------------------------------
// Doppelklick auf den state-Indikator oben löscht Verlauf — kleiner Notausgang
// während wir noch keine UI-Aktion dafür haben.
refs.state.addEventListener("dblclick", () => {
  if (confirm("Chatverlauf wirklich löschen?")) clearHistory();
});

// --- Auth-Bootstrap (Phase C) --------------------------------------------
//
// Beim Page-Load /api/auth/me checken:
//   - authenticated=true → normales UI, User-Info im Settings-Tab
//   - authenticated=false + defaultUserId gesetzt → Auth ist aus, Single-User
//   - authenticated=false + kein defaultUserId → Auth ist an, Login zeigen
const loginContainer = requireEl("#view-login");
/**
 * Darf dieses Konto die ausführliche HD-Deutung abrufen? Aus /api/auth/me
 * (features.humanDesign). Ohne Wissen `true` — dann entscheidet der Server,
 * und ein 402 zeigt ebenfalls die Kurzfassung. (24.09.2026, Ticket 2567)
 */
let hdDeutungFrei = true;
/**
 * Hat das Konto den Essenz-Spiegel? Aus /api/auth/me (features.essenz, ab
 * Probe). Ohne Wissen `true` — wie bisher. (24.09.2026, Ticket 2567, 0a)
 *
 * ⚠️ Die Seitenleiste fragt /api/essence-mirror von selbst ab
 *    (refreshEssenceNavHint). Seit der Spiegel am Paket-Tor hängt, wäre
 *    das für Frei eine 402 — und der globale 402-Abfang öffnete bei jedem
 *    Öffnen der Leiste das Paket-Blatt. Darum fragt sie nur mit Spiegel.
 */
let essenzFrei = true;
let currentUser: { id: number; email: string; name: string | null; picture?: string | null } | null =
  null;

function showLoginView(hinweis?: string): void {
  // Alle anderen Views verstecken, Login einblenden, Tabs + Topbar
  // ausblenden (im Login-Modus soll nur die Card sichtbar sein).
  document.querySelectorAll<HTMLElement>(".view").forEach((v) => {
    v.hidden = v.id !== "view-login";
  });
  const topbar = document.getElementById("topbar");
  if (topbar) topbar.hidden = true;
  // Menü-Button ausblenden bevor eingeloggt.
  if (menuBtn) menuBtn.hidden = true;
  refs.connectBtn.hidden = true;
  // Pre.374: Body-Klasse für CSS-Hooks. Status-Bar (Footer) und Dock
  // werden im Login-Modus per CSS ausgeblendet — nur die Login-Card
  // soll sichtbar sein.
  document.body.classList.add("is-login");
  new LoginView(loginContainer, undefined, hinweis);
}

function showAuthenticatedUI(): void {
  loginContainer.hidden = true;
  // Pre.374: Login-Klasse wieder weg, sobald eingeloggt.
  document.body.classList.remove("is-login");
  // Pre.109: Login-Route hat data-mode=dark erzwungen — beim App-Bootstrap
  // wieder löschen damit OS-Theme greift.
  delete document.body.dataset.mode;
  const topbar = document.getElementById("topbar");
  if (topbar) topbar.hidden = false;
  if (menuBtn) menuBtn.hidden = false;
  // 15.09.2026: NICHT mehr selbst entscheiden — sonst ueberschreibt diese
  // Zeile nach dem Anmelden die Nutzer-Pruefung (Martin sah den Hoerer bei
  // Albert). Siehe zeichneAnrufKnopf().
  zeichneDockPlatz(connected);
  // Pre.290: User-Block kompakt — Avatar-Initial + Name groß + Email klein.
  const navUser = document.getElementById("nav-user");
  const navUserName = document.getElementById("nav-user-name");
  const navUserEmail = document.getElementById("nav-user-email");
  const navUserAvatar = document.getElementById("nav-user-avatar");
  if (currentUser && navUser && navUserName) {
    const display =
      currentUser.name?.trim() || currentUser.email.split("@")[0] || "User";
    navUserName.textContent = display;
    if (navUserEmail) navUserEmail.textContent = currentUser.email;
    if (navUserAvatar) {
      const pic = currentUser.picture;
      if (pic) {
        navUserAvatar.textContent = "";
        navUserAvatar.classList.add("has-photo");
        const img = document.createElement("img");
        img.src = pic;
        img.alt = "";
        img.referrerPolicy = "no-referrer";
        img.addEventListener("error", () => {
          navUserAvatar.classList.remove("has-photo");
          img.remove();
          navUserAvatar.textContent = display.charAt(0).toUpperCase();
        });
        navUserAvatar.appendChild(img);
      } else {
        navUserAvatar.classList.remove("has-photo");
        navUserAvatar.textContent = display.charAt(0).toUpperCase();
      }
    }
    navUser.hidden = false;
  }
  // Pre.91: im Desktop ist die Sidebar permanent — beim Bootstrap automatisch
  // öffnen. Im Mobile bleibt sie zu, User triggert sie über den Hamburger.
  // 2026-07-14 (Martin): Modus nach Fensterbreite — >=1200px voll,
  // darunter eingeklappte Icon-Rail.
  if (isDesktop()) {
    openDrawer();
    applyWidthNavMode();
  }

  // 15.09.2026: Der Aufgaben-Knopf in der Fusszeile ist weg (Martin). Der
  // Zaehler laeuft weiter — er fuettert die Kachel im Home-Widget.
  if (currentUser?.id === 1) {
    void (async () => {
      const { startTicketsBadgePolling } = await import("./lib/tickets-view.js");
      startTicketsBadgePolling();
    })();
    // 29.09.2026 (Ticket 2733): der Entwicklungs-Ticker im Kopf — nur Konto 1,
    // höchstens jede Minute und nur bei sichtbarer App (lib/entwicklungs-ticker.ts).
    void import("./lib/entwicklungs-ticker.js").then((m) => m.starteEntwicklungsTicker());
  }
  // 29.09.2026 (Ticket 2823): das Zeichen „Mac“ im Kopf. Für jedes Konto
  // gestartet — sichtbar wird es nur, wenn das Konto je Vespera Desktop
  // gekoppelt hat; ohne Mac fragt es nach der ersten Antwort nicht mehr
  // (lib/mac-zeichen.ts, höchstens jede Minute, nur bei sichtbarer App).
  void import("./lib/mac-zeichen.js").then((m) => m.starteMacZeichen());

  /*
   * 16.09.2026 — EINE MARKE AM BLATT, damit auch Bausteine ohne Nutzerwissen
   * fragen koennen, ob sie fuer den Hauptnutzer zeichnen.
   *
   * Anlass: Martin sieht auf Alberts Seite die Statuszeile *„Sonnet · CLI an
   * · Extension aus"*. Er dazu: *„Bi kann er gar nicht nutzen."* Modellname
   * und CLI-Zustand sind Innereien — ein Kunde kann sie weder verstehen noch
   * beeinflussen. Die Bausteine, die das zeichnen, liegen aber in lib/ und
   * kennen `currentUser` nicht.
   *
   * ⚠️ Der sparsame Fall ist die VORGABE: fehlt die Marke, gilt „kein
   *    Hauptnutzer". Wer sie vergisst, zeigt zu wenig — nie zu viel.
   */
  if (currentUser?.id === 1) document.body.dataset.hauptnutzer = "ja";
  else delete document.body.dataset.hauptnutzer;

  // Liegt ein Spiegel bereit, den noch niemand angesehen hat? (16.09.2026)
  pruefeSpiegelHinweis();
  /*
   * Einmal fragen, ob etwas hinterlegt ist. Kein Intervall — Martin hat den
   * Kanal ausdruecklich als den STILLEN bestellt („kein Ton, kein Push, kein
   * Aufwecken"); ein Puls waere genau die Unruhe, die er ausgeschlossen hat.
   */
  glockeNeuFragen();

  // Pre.526: Superuser-Only-UI-Elemente einblenden (data-superuser-only="1").
  // Beleg-Erfassung ist exklusiv für Martin (Panel-API-Zugriff).
  if (currentUser?.id === 1) {
    document
      .querySelectorAll<HTMLElement>('[data-superuser-only="1"]')
      .forEach((el) => {
        el.hidden = false;
      });
  }

  // Pre.577: Nutzer-Verwaltung (Admin-Tool) — nur für den Haupt-Admin (id=1).
  // /api/auth/me wurde in bootstrapAuth bereits geladen; currentUser hält das
  // Ergebnis. Im Single-User-Modus (kein currentUser) wird der Knopf nicht
  // eingeblendet — die Admin-Endpunkte sind dort ohnehin 403/503.
  // Modul-Freigaben haengen an derselben Bedingung: wer keine Nutzer
  // verwalten darf, vergibt auch keine Module.
  const adminLink = document.getElementById("open-admin-link");
  if (adminLink) adminLink.hidden = currentUser?.id !== 1;
  // 06.10.2026: die Morse-Lernseite im Menü unter „Täglich“ (nur Konto 1, Schalter in den Einstellungen).
  zeigeMorseImMenue();
  // 29.10.2026: „Vespera 9.0 testen“ — nur Konto 1, nur wenn der Server /neun freigibt.
  void zeigeNeunEintrag(document, currentUser?.id);
}

// ── Tiefe Frage des Tages als Aufgabe (V2, 2026-07-20) ─────────────────────
// Martins Auftrag: nicht nur im Chat-Verlauf, sondern als sichtbare Aufgabe
// mit dediziertem Antworten-Fluss; die Antwort wird gespeichert (Tabelle
// deep_questions) und formt als Resonanz-Spur die naechsten Fragen.

initDeepQuestions({
  showToast: (m, k) => showToast(m, (k as ToastKind) ?? "info"),
  showStatusBanner,
  markDeepqBanner: () => refs.banner.classList.add("banner--deepq"),
});

async function bootstrapAuth(): Promise<void> {
  // Rückkehr aus dem Panel (Ticket 2713 V2, lib/panel-kopplung-rueckkehr.ts).
  const kopplung = kopplungAusAdresse();
  let me: MeResponse;
  try {
    me = await fetchMe();
  } catch {
    // Server nicht erreichbar — single-user mode wird angenommen
    /*
     * 23.09.2026, Ticket 2511: Die Einstellungen gleichen sich trotzdem ab,
     * sobald der Server wieder antwortet — sonst lief die ganze Sitzung auf
     * dem Zwischenspeicher, und keine Änderung erreichte die anderen Geräte.
     * Dieselbe Regel wie unten: angemeldet → seine Kennung, Einzelbetrieb →
     * die vorgegebene; ein anderes Konto als zuletzt → der Speicher ist fremd.
     */
    starteAbgleichSpaeter(async () => {
      const spaet = await fetchMe();
      if (!spaet.authenticated) return spaet.defaultUserId ?? null;
      try {
        const lastUid = localStorage.getItem(CURRENT_UID_KEY);
        if (lastUid !== String(spaet.user.id)) kontoGewechselt(lastUid);
      } catch {
        /* localStorage unavailable */
      }
      return spaet.user.id;
    });
    document.body.classList.remove("auth-pending");
    (window as unknown as { __bootOk?: () => void }).__bootOk?.();
    return;
  }
  try {
    if (me.authenticated) {
      /*
       * 28.09.2026 (Ticket 2713 V2): Kam die Rückkehr aus dem Panel ohne
       * Sitzung an, geht es nach dem Anmelden zurück zum Server — er
       * vergleicht JETZT das Konto und holt nur bei Gleichheit ab
       * (lib/panel-kopplung-rueckkehr.ts, kopplungBeimStart).
       */
      const kopplungsSchritt = kopplungBeimStart(true, kopplung, weiterGemerkt());
      vergissWeiter();
      if (kopplungsSchritt.art === "weiter") {
        location.replace(KOPPLUNG_WEITER);
        return;
      }
      // Pre.285 Bug #200: SICHERHEITS-Fix Multi-Tenant Chat-Leak.
      // Wenn aktueller User != localStorage-current-uid: ALLE
      // voice-v8.* localStorage-Keys clearen, dann history-Key
      // user-scopen. Verhindert dass User B den Chat von User A sieht.
      try {
        const lastUid = localStorage.getItem(CURRENT_UID_KEY);
        const curUid = String(me.user.id);
        if (lastUid !== curUid) {
          // 23.09.2026, Ticket 2511: festhalten, wem die Einstellungen im
          // Speicher gehören — sie gehen nie an das neue Konto.
          kontoGewechselt(lastUid);
          // Account-Wechsel erkannt — wipe voice-v8.* cache
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            // 20.10.2026: auch die offenen Fragen (lib/offene-fragen.ts, Kurztexte) und die aufgeklappten Mails (lib/mail-text.ts) gehen nie an ein anderes Konto.
            if (k && (k.startsWith("voice-v8.chat-history") || k.startsWith(OFFEN_SCHLUESSEL) || k === MAIL_OFFEN_SCHLUESSEL))
              keysToRemove.push(k);
          }
          for (const k of keysToRemove) localStorage.removeItem(k);
          localStorage.setItem(CURRENT_UID_KEY, curUid);
        }
        setHistoryUid(me.user.id);
        // Pre.509: Server-First. KEIN localStorage-Vorabrender mehr —
        // Hydrate mit replaceAll holt direkt vom Server und füllt DOM.
        // localStorage wird im hydrate als Side-Effect mit-aktualisiert
        // für den Offline-Fallback-Pfad.
        history.length = 0;
        window.dispatchEvent(new CustomEvent("voice-v8:hydrate-from-server"));
      } catch {
        /* localStorage unavailable */
      }
      currentUser = me.user;
      setzeAngemeldetesKonto(me.user?.name);
      hdDeutungFrei =
        (me as unknown as { features?: { humanDesign?: boolean } }).features
          ?.humanDesign !== false;
      essenzFrei =
        (me as unknown as { features?: { essenz?: boolean } }).features
          ?.essenz !== false;
      // 25.09.2026: „Sprache live“ in der Verbrauchskachel nur mit Anruf im
      // Paket — den hat bis auf Weiteres nur Martins Konto (entitlements.ts).
      setzeAnrufSichtbar(
        (me as unknown as { features?: { call?: boolean } }).features?.call === true,
      );
      // 19.10.2026: dieselbe Auskunft für alle Einstellungen und den Anruf-Knopf (lib/konto-lage.ts).
      setzeAnrufImPaket(
        (me as unknown as { features?: { call?: boolean } }).features?.call === true,
      );
      // Ticket 1614 (2026-08-08): Ankermoment-Eintrag im Plus-Sheet nur, wenn
      // freigeschaltet. Ueber das BERECHTIGUNGS-SYSTEM, nicht ueber die
      // Nutzer-ID — Martins Wunsch war "plugin-maessig, sodass sich fuer
      // andere Kunden gewisse Parameter freischalten lassen". Eine
      // uid===1-Abfrage haette genau das verhindert.
      {
        const frei = Boolean(
          (me as unknown as { features?: { ankermomente?: boolean } }).features
            ?.ankermomente,
        );
        document
          .getElementById("sheet-anker")
          ?.toggleAttribute("hidden", !frei);
      }
      // Pre.296: globales uid-Flag für SettingsView (Premium-Lock)
      (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid =
        me.user.id;
      kontoSperrenNachfuehren();
      zeichneEntwicklerSchalter();
      entwicklerHoerer.merke();
      offenAnzeige?.zeichne(); // das Konto ist bekannt: „Offen“ nur für Konto 1 (lib/offene-fragen.ts)
      uebernimmLesemodus(); // das Konto ist bekannt: sein Lesemodus gilt (lib/lesemodus.ts)
      // 13.10.2026 (Zuhause → Neu): die Freischaltungen des Kontos, damit „Neu“ nur Karten zeigt, die es für dieses Konto gibt.
      (window as unknown as { __voiceV8Features?: Record<string, boolean> }).__voiceV8Features = (
        me as unknown as { features?: Record<string, boolean> }
      ).features;
      showAuthenticatedUI();
      // 23.09.2026, Ticket 2511: Einstellungen vom Server — im Hintergrund,
      // die Oberfläche steht schon und wartet nicht darauf.
      void starteAbgleich(me.user.id);
      // Neue Registrierung: Willkommens-Onboarding — NICHT fuer den bereits
      // bekannten/eingeloggten Hauptnutzer (Superuser id=1, Martin).
      if (me.user?.id !== 1) void maybeStartOnboarding();
      // Deep-Link/Reload: HD-Analyse erst JETZT öffnen (currentUser steht → die
      // Admin-Steuerungen rendern korrekt). Pre.750: auch Essenz/Buch (page-routes).
      openHdFromHash();
      oeffneSpiegelNachNeuladen();
      // 2452: Zuhause steht, bevor der Splash weicht — sonst ist das Bild
      // nachtraeglich da, und die Karten springen. Szene wird im Aufbau
      // sofort gezeichnet, nicht erst nach dem Wetter.
      await ensureZuhauseView();
      stelleOrtHerWieder();
      // Ticket 2713 V2: das Ergebnis der Kopplung zeigt die Karte „Panel“.
      if (kopplung) entferneKopplungAusAdresse();
      if (kopplungsSchritt.art === "zeigen") {
        merkeKopplungsErgebnis(kopplungsSchritt.ergebnis, currentUser?.name);
        document.dispatchEvent(
          new CustomEvent("vespera:go-settings", { detail: { sub: "verbindungen" } }),
        );
      }
      // Pre.760: Standort automatisch aktuell halten (nur wenn Schalter an UND
      // Permission bereits erteilt → kein ungefragter Prompt beim Laden).
      void import("./lib/geo-watch.js").then((m) => m.maybeStartGeoWatch());
      // V2 Tiefe Fragen: offene Tagesfrage als Aufgabe zeigen.
      void checkDeepQuestion();
    } else if (kopplungBeimStart(false, kopplung, weiterGemerkt()).art === "anmelden") {
      // Ticket 2713 V2: die Rückkehr aus dem Panel braucht eine ECHTE
      // Anmeldung — auch dort, wo sonst der Einzelbetrieb ohne Anmeldung
      // gilt. Danach geht es zurück zum Server (oben).
      merkeWeiter();
      showLoginView(KOPPLUNG_ANMELDEN_HINWEIS);
    } else if (me.defaultUserId !== undefined) {
      // Auth-Endpunkte sind aus → Single-User-Modus, kein Login zeigen.
      (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid =
        me.defaultUserId;
      kontoSperrenNachfuehren();
      zeichneEntwicklerSchalter();
      entwicklerHoerer.merke();
      offenAnzeige?.zeichne(); // das Konto ist bekannt: „Offen“ nur für Konto 1 (lib/offene-fragen.ts)
      uebernimmLesemodus(); // das Konto ist bekannt: sein Lesemodus gilt (lib/lesemodus.ts)
      showAuthenticatedUI();
      void starteAbgleich(me.defaultUserId);
      openHdFromHash();
      oeffneSpiegelNachNeuladen();
      await ensureZuhauseView();
      stelleOrtHerWieder();
      // Pre.760: Standort automatisch aktuell halten (nur wenn Schalter an UND
      // Permission bereits erteilt → kein ungefragter Prompt beim Laden).
      void import("./lib/geo-watch.js").then((m) => m.maybeStartGeoWatch());
      // V2 Tiefe Fragen: offene Tagesfrage als Aufgabe zeigen.
      void checkDeepQuestion();
    } else {
      // Auth aktiv, aber kein gültiger Cookie → Login-View.
      showLoginView();
    }
  } finally {
    // FOUC-Schutz: erst jetzt #app + Login einblenden. Splash blendet weg.
    document.body.classList.remove("auth-pending");
    (window as unknown as { __bootOk?: () => void }).__bootOk?.();
  }
}

// iOS-Tastatur/Safe-Area systemweit: visualViewport spiegeln, damit bei
// offener Tastatur nichts unter den Notch oder hinter die Tastatur rutscht.
// Setzt --app-vh (sichtbare Hoehe) + --kb (Tastatur-Hoehe) + body.keyboard-open.
function installViewportGuard(): void {
  const vv = window.visualViewport;
  const root = document.documentElement;
  let ohneFeldGemeldet = false;
  let warOffen = false;
  const apply = (): void => {
    const h = vv ? vv.height : window.innerHeight;
    // 07.10.2026: verdeckt = oben weggeschoben + unten unter der Tastatur; `kb` ist nur der untere Teil,
    // `oben` der begrenzte Versatz. Martins iPhone meldet innerHeight = sichtbare Höhe — lib/tastatur.ts.
    const lage = vv ? sichtLage(window.innerHeight, root.clientHeight, vv.height, vv.offsetTop, vv.scale) : null;
    const verdeckt = lage ? lage.verdeckt : 0;
    const oben = lage ? lage.oben : 0;
    const kb = lage ? lage.unten : 0;
    root.style.setProperty("--app-vh", h + "px");
    root.style.setProperty("--kb", kb + "px");
    /*
     * ⚠️ 2026-08-29 — `offsetTop` fehlte hier, und das kostete die
     * Aufnahmekarte bei offener Tastatur.
     *
     * Martin: *„Zum Text nachbearbeiten kommt zwar das Mikrofonzeichen.
     * Wenn man dann draufklickt, sollte da nicht wieder ein Visualisierer
     * kommen. Also das funktioniert noch nicht so ganz."*
     *
     * Im Browser nachgestellt erscheint die Karte und der Pegel laeuft
     * (`flex`, 18 Punkte hoch) — die Mechanik stimmt. Auf dem Telefon
     * schiebt iOS beim Oeffnen der Tastatur aber den SICHTBAREN Ausschnitt
     * nach unten, damit die Schreibmarke frei bleibt. `position: fixed`
     * haengt am LAYOUT-Ausschnitt; die Karte sitzt dann um genau diesen
     * Versatz oberhalb des Sichtbaren — vorhanden, aber nicht zu sehen.
     *
     * Auf Martins Foto sieht man den Versatz: die Statusleiste liegt ueber
     * einer Sprechblase, die Seite ist also nach oben gerueckt.
     *
     * Der Versatz war bisher nirgends greifbar. Er wird hier mitgemessen —
     * dieselbe Beobachtung, dieselbe Frage („wo ist das Sichtbare?").
     * Ohne Tastatur ist er 0, dann aendert sich nichts.
     */
    root.style.setProperty("--vv-top", oben + "px");
    /*
     * ⚠️ 30.09.2026 (Ticket 2873) — die Tastatur gilt nur als offen, wenn
     * auch ein Feld den Fokus hat (lib/tastatur.ts). Allein aus der Rechnung
     * kippte die Klasse auf dem iPhone beim Scrollen kurz an und wieder aus
     * (innerHeight zieht hinter visualViewport her); mit ihr verschwindet die
     * Fußzeile und #app bekommt eine andere Höhe — die Ansicht sprang.
     */
    const offen = tastaturOffen(verdeckt, document.activeElement);
    document.body.classList.toggle("keyboard-open", offen);
    // Mit der Klasse kommen und gehen Kopf- und Fusszeile (quer): ihre Höhen gleich nachmessen.
    if (offen !== warOffen) {
      warOffen = offen;
      moebelMessen();
    }
    // Der Fall, den die Rechnung allein früher als „Tastatur“ gewertet hätte: ins Protokoll, einmal je Fall.
    const ohneFeld = verdeckt > TASTATUR_AB_PX && !offen;
    if (ohneFeld && !ohneFeldGemeldet) {
      const a = document.activeElement;
      meldeTastaturOhneFeld(a instanceof HTMLElement ? a.tagName.toLowerCase() : "keins");
    }
    ohneFeldGemeldet = ohneFeld;
    // Bei offener Tastatur: das Feld mit dem Fokus in seiner Fläche ins Bild holen (lib/tastatur.ts).
    feldImBlick(offen, h);
  };
  if (vv) {
    vv.addEventListener("resize", apply);
    /*
     * 07.10.2026: Bei offener Tastatur hängt die Übernahme des Versatzes NICHT am Schalter
     * „Scroll-Messfühler aus“ — #app und die Vollseiten stehen an `--vv-top`; ein Schub ohne `resize`
     * käme sonst erst beim Ausschalten an. Ohne Tastatur bleibt es beim Schalter (Ticket 2912).
     */
    const beiSchubGesteuert = gesteuert("wachen", apply, true);
    vv.addEventListener("scroll", () => {
      if (document.body.classList.contains("keyboard-open")) apply();
      else beiSchubGesteuert();
    });
  }
  window.addEventListener("orientationchange", apply);
  // Fokus kommt und geht: die Klasse folgt sofort, nicht erst beim nächsten Resize.
  document.addEventListener("focusin", apply);
  document.addEventListener("focusout", () => window.setTimeout(apply, 0));
  apply();
}
installViewportGuard();
starteScrollWache();
starteScrollTakt();
starteBildrateHinweis();
// pre.3606: wächst die Seite über 6.000 Knoten, sagt eine Journalzeile, wo (lib/dom-wachstum.ts).
starteDomWachstum();

function showUpgradeSheet(need: string | null): void {
  if (document.querySelector(".upg-overlay")) return;
  const ov = document.createElement("div");
  ov.className = "upg-overlay";
  ov.addEventListener("click", (e) => { if (e.target === ov) ov.remove(); });
  const sheet = document.createElement("div");
  sheet.className = "upg-sheet";
  const h = document.createElement("h3");
  h.className = "upg-title";
  const lbl = need === "lebensbuch" ? "„Lebensbuch“" : need === "begleiter" ? "„Begleiter“" : "einem höheren Paket";
  h.textContent = `Diese Funktion ist in ${lbl} enthalten.`;
  const p = document.createElement("p");
  p.className = "upg-text";
  p.textContent = "Schalte sie frei und hol das Beste aus Vespera heraus.";
  const actions = document.createElement("div");
  actions.className = "upg-actions";
  const a = document.createElement("a");
  a.className = "upg-btn upg-btn--primary";
  // 24.09.2026, Ticket 2567: der Anker auf vespera.at heißt „preis" —
  // „#preise" landete oben auf der Seite statt bei den Preisen.
  a.href = "https://vespera.at/#preis";
  a.target = "_blank";
  a.rel = "noopener";
  a.textContent = "Pakete ansehen";
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "upg-btn upg-btn--ghost";
  closeBtn.textContent = "Später";
  closeBtn.addEventListener("click", () => ov.remove());
  actions.append(a, closeBtn);
  sheet.append(h, p, actions);
  ov.appendChild(sheet);
  document.body.appendChild(ov);
}
{
  const __origFetch: typeof fetch = window.fetch.bind(window);
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const res = await __origFetch(input, init);
    if (res.status === 402) {
      try {
        const url = typeof input === "string" ? input : input instanceof URL ? input.href : (input).url;
        if (url.includes("/api/")) {
          const j = (await res.clone().json().catch(() => null)) as { need?: string } | null;
          showUpgradeSheet(j?.need ?? null);
        }
      } catch { /* nie blockieren */ }
    }
    return res;
  });
}

void bootstrapAuth();

// --- Logout-Handler ------------------------------------------------------
async function handleLogout(): Promise<void> {
  if (!confirm("Wirklich abmelden?")) return;
  // 21.09.2026 (Martin, Firefox: „Das Logout geht nicht"): kein fetch mehr,
  // das ein Neuladen abbrechen kann — die Seite GEHT zum Abmelde-Endpunkt,
  // der das Cookie loescht und auf / zurueckleitet. Kein Rennen, kein 499.
  currentUser = null;
  setzeAngemeldetesKonto(null);
  location.assign("/api/auth/logout");
}

// Abmelden sitzt in Einstellungen (Modelle und Konto) und seit 29.09.2026
// (Ticket 2797) im Konto-Menü hinter dem Namen — nie als offener Knopf
// direkt neben dem Namen. Beide rufen handleLogout.
document.addEventListener("click", (e) => {
  const t = (e.target as Element | null)?.closest?.("#settings-logout");
  if (t) void handleLogout();
});

// Brand-Klick = zurück zur Startseite (Sprechen)
document.getElementById("brand-home")?.addEventListener("click", () => {
  goToView("zuhause");
});

// --- Service-Worker -------------------------------------------------------
//
// Update-Strategie:
//  - Wenn der User idle ist (nicht verbunden) → automatisch reloaden.
//  - Wenn der User gerade verbunden ist → KEIN Hard-Reload, sondern
//    Banner "Update verfügbar — beim nächsten Auflegen aktiv". Sonst
//    würde der Tab mitten im Gespräch wegspringen + Doppelt-Grüßen.
// Pre.163: PWA-Update-Banner. Wird gezeigt wenn ein neuer Service Worker
// installiert wurde aber noch wartet. Klick → skipWaiting → controllerchange
// → existing reload-Handler.
let updateBannerShown = false;
// Ticket 2653: EIN Besitzer fuer „wartenden Worker aktivieren und neu laden" —
// Knopf und stilles Nachladen rufen ihn, siehe lib/worker-wechsel.ts.
const workerWechsel = erzeugeWorkerWechsel({
  container: "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
  neuLaden: () => reloadJetzt(),
  ohneWartendenNeuLaden: () => reloadForUpdate(),
  notladenErlaubt: () => !connected && neuLadenDarfJetzt(),
});
/**
 * 11.10.2026 (Ticket 2669): „Seite neu laden“ auf Zuruf — das Gerät für lib/app-neu-laden.ts. Das Neuladen ist der
 * Weg des Knopfs „Neue Version“ (workerWechsel); die Lage ist dieselbe Frage wie beim stillen Nachladen, nur dass
 * hier die Seite sichtbar ist und der Mensch darum gebeten hat.
 */
const NEU_LADEN_UMGEBUNG: NeuLadenUmgebung = {
  lage: () => ({
    anruf: connected,
    sprechtaste: pttState !== "idle",
    aufnahme: aufnahmeLaeuft(),
    // Das Diktat der Tiefen Frage — wie beim stillen Nachladen; das warme Mikro der Sprechtaste zählt nicht (45 s im Web).
    mikro: mikroOffenOhneWarmes(),
    // Auch Vesperas Antwortstimme: sie steht nicht im Ton-Leitstand, sondern in den Warteschlangen (klingtEtwas).
    ton: klingtEtwas(aktuellerTon(), activeTtsQueues),
    antwort: laufenderZug !== null,
    entwurf: textField.value.trim().length > 0,
  }),
  neuLaden: () => void workerWechsel.aktivierenUndNeuLaden(),
  hinweis: (message) =>
    window.dispatchEvent(new CustomEvent("voice-v8:toast", { detail: { message, kind: "info", ms: 6000 } })),
  melde: meldeDiagnose,
};
/**
 * 2026-07-27 (Martin: „es stehen immer zwei verschiedene Buttons da"):
 * Der Service-Worker oeffnet KEIN eigenes Banner mehr. Er meldet nur, dass
 * eine neue Fassung wartet — angezeigt wird sie ueber denselben einen Knopf
 * wie der Versions-Vergleich. Vorher waren es zwei Melder, die sich
 * gegenseitig ausblenden sollten; der Abgleich lief aber nur an einer Stelle
 * und nur einmal, also standen beide da.
 */
function showUpdateBanner(): void {
  if (updateBannerShown) return;
  updateBannerShown = true;
  // Welcher Worker wartet, fragt worker-wechsel.ts beim Klick selbst ab.
  neuDurchWorker = true;
  setzeUpdatePending();
  maybeShowUpdate();
}

/**
 * Ein Worker wartet: ist er NEU? (29.10.2026, Martin: „Neue Version“ blieb nach dem Neuladen stehen.)
 * Trägt er dieselbe Fassung wie diese Seite, bringt er nichts — er übernimmt still, kein Knopf. Nur ein Worker mit anderer
 * (oder unbekannter) Fassung meldet „neu“.
 */
let stillAktiviert = false;
let workerPrueft = false;
async function onWartenderWorker(wartend: ServiceWorker): Promise<void> {
  if (workerPrueft) return;
  workerPrueft = true;
  const fassung = await frageWorkerVersion(wartend);
  workerPrueft = false;
  const seite = typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "";
  if (gleicheFassung(fassung, seite)) {
    stillAktiviert = workerWechsel.uebernehmenStill(wartend);
    return;
  }
  showUpdateBanner();
}

// 2026-07-25 (Martin: "die Seite laedt immer wieder neu ... es sollte nur ein
// Button erscheinen, weil sonst der Prozess, der gerade laeuft, abbricht"):
// KEIN automatisches Anwenden mehr. Frueher (pre.858) galt "kein Anruf aktiv"
// als "es laeuft nichts" — eine viel zu enge Annahme: eine laufende Antwort,
// eine Verarbeitung im Hintergrund, ein halb getippter Gedanke zaehlten nicht.
// Jetzt liegt die Entscheidung beim Nutzer, immer.
function onUpdateReady(): void {
  void navigator.serviceWorker
    .getRegistration()
    .then((reg) => (reg?.waiting ? onWartenderWorker(reg.waiting) : showUpdateBanner()))
    .catch(() => showUpdateBanner());
}

if ("serviceWorker" in navigator) {
  // Pre.163: Update-Banner. Sobald ein neuer SW im "installed"-State ist
  // UND ein controller schon aktiv ist (= alte App läuft) zeigen wir
  // einen Banner mit Reload-Btn. Klick → skipWaiting → controllerchange
  // → existing handler reloaded.
  navigator.serviceWorker
    .register("/sw.js", { scope: "/" })
    .then((reg) => {
      const checkUpdate = (worker: ServiceWorker | null): void => {
        if (!worker) return;
        worker.addEventListener("statechange", () => {
          if (
            worker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            onUpdateReady();
          }
        });
      };
      reg.addEventListener("updatefound", () => checkUpdate(reg.installing));
      // Falls beim ersten Start schon ein waiting-Worker da ist
      if (reg.waiting && navigator.serviceWorker.controller) {
        onUpdateReady();
      }
      // Pre.332: Aggressives Update-Polling. Vorher prüfte der Browser sw.js
      // nur einmal pro Navigation ODER alle ~24h Hintergrund — User
      // beschwerte sich "ich muss erst die Seite neu laden damit das Modal
      // kommt". Jetzt: alle 60s reg.update() rufen → wenn neue sw.js da
      // ist, triggert das den existing updatefound-Listener → Banner
      // erscheint OHNE manuellen Reload. Im PWA-Standby pausiert das
      // Browser-Throttling — sobald User reinkommt, läuft das Intervall
      // weiter. Plus: bei "visibilitychange visible" sofort einmal
      // checken (deckt PWA-Resume nach Sleep).
      const pollUpdate = (): void => {
        reg.update().catch(() => undefined);
        // pre.893: Ein bereits WARTENDER Worker (Update kam während eines
        // Anrufs) wurde nach Anruf-Ende sonst NIE angewandt → der User blieb
        // dauerhaft auf der alten Version haengen (Martins „Fixes kommen nicht
        // an"). Sobald sicher (kein Anruf laeuft), den wartenden Worker
        // nachtraeglich anwenden.
        if (
          reg.waiting &&
          navigator.serviceWorker.controller &&
          !connected &&
          !workerWechsel.laedt
        ) {
          onUpdateReady();
        }
      };
      /*
       * ⚠️ 28.09.2026 (Ticket 2752: Handy wird warm, Akku leert sich) —
       *    NUR bei sichtbarer Seite. Seit das Wach-Wort lauscht, hält
       *    es die App rund um die Uhr wach, und dieser Takt fragte auch nachts
       *    60-mal je Stunde nach sw.js. Im Hintergrund braucht ihn niemand:
       *    das stille Nachladen fragt im Fünf-Minuten-Takt selbst (siehe
       *    versucheStillesNachladen), und beim Zurückkommen fragt der Hörer
       *    darunter sofort.
       */
      starteSichtbarenTakt(pollUpdate, 60_000);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") pollUpdate();
      });
      // Push-Selbstheilung: hat der User Benachrichtigungen erlaubt, das Abo
      // beim App-Start STILL erneut beim Server eintragen. Fängt rotierte
      // Endpoints, server-seitig gelöschte Rows und Account-Wechsel ab —
      // ohne diese Re-Sync zeigte die App „Push aktiv", es kam aber nie etwas.
      void import("./lib/push.js")
        .then(({ resyncSubscription }) => resyncSubscription())
        .then((r) => {
          if (!r.ok && r.reason !== "not-granted" && r.reason !== "unsupported")
            console.warn("[push] resync übersprungen:", r.reason);
        })
        .catch(() => undefined);
    })
    .catch((err) => console.warn("[sw] registration failed:", err));

  // Pre.781: Push bei OFFENER App auch als In-Page-Toast zeigen — der SW
  // postet bei jedem Push zusätzlich an alle offenen Fenster.
  navigator.serviceWorker.addEventListener("message", (ev) => {
    const d = ev.data as {
      type?: string;
      title?: string;
      body?: string;
      signal?: string;
      bleibt?: boolean;
    };
    if (d?.type !== "push-toast") return;
    const text = [d.title, d.body].filter(Boolean).join(" — ").slice(0, 180);
    // 16.10.2026 (Signal an Martin): bleibt stehen, bis er sie wegklickt.
    if (text && d.bleibt === true) showBleibtToast(d.title ?? "", d.body ?? "");
    else if (text) showToast(text, "success", 7000);
    // 2026-07-25 (Martin, Achtsamkeits-Alarm: "es ist wichtig, dass ich
    // informiert werde, weil ich gerade reaktiv war"): Ist ein Signal
    // gewaehlt, wird es hier hoerbar — abspielen kann nur die Seite, nicht
    // der Dienst-Arbeiter. Derselbe Baustein wie bei den Erinnerungen.
    if (d.signal && d.signal !== "still") {
      void spieleAlarmSignal(d.signal, d.title ?? "Achtsamkeit");
    }
  });

  // 2026-07-25 (Martin, "die Seite laedt immer wieder neu"): Uebernimmt ein
  // neuer Worker, wird NICHT mehr von selbst neu geladen — es erscheint der
  // Knopf. Hat der Nutzer ihn selbst gedrueckt, laedt reloadForUpdate() bzw.
  // das Sicherheitsnetz im Banner ohnehin; dieser Zweig war nur fuer den Fall
  // "Worker wechselt ohne Zutun" da, und genau der hat Martin die Seite unter
  // den Haenden weggezogen. Der frueher noetige Beobachter auf den
  // Anruf-Knopf (nachgeholtes Neuladen beim Auflegen) entfaellt damit.
  // Erste Installation: der allererste Worker übernimmt die Seite (clients.claim) — das ist kein Update.
  let hatteWorker = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hatteWorker) {
      hatteWorker = true;
      return;
    }
    // 2026-07-27 (Martin: „meistens muss ich zweimal draufklicken"): Hier
    // entscheidet sich alles — WARUM hat der Worker gewechselt?
    //
    // Hat der Nutzer den Knopf gedrueckt, ist der Wechsel die ANTWORT auf
    // seine Bitte: sofort neu laden. Genau das fehlte, deshalb erschien der
    // Knopf einfach wieder und erst das 3,5-Sekunden-Sicherheitsnetz beim
    // zweiten Klick lud die Seite. Seit Ticket 2653 gilt dasselbe fuer das
    // stille Nachladen — entschieden wird in worker-wechsel.ts.
    if (workerWechsel.beiWechsel()) return;
    // Ein Worker mit DERSELBEN Fassung wie die Seite hat still übernommen: nichts Neues.
    if (stillAktiviert) {
      stillAktiviert = false;
      return;
    }
    // Ohne sein Zutun bleibt es beim Knopf — die Seite darf ihm nicht unter
    // den Haenden weggezogen werden (sein Anliegen vom 25.07.).
    neuDurchWorker = true;
    setzeUpdatePending();
    maybeShowUpdate();
  });
}

renderStatusBar();
void refreshExtensionStatus();

// Pre.660 (User-Wunsch): CLI-Heartbeat in der Fußzeile. Grüner Punkt = CLI
// erreichbar & Tokens laufen (war ja zeitweise das Problem). /api/cli/health
// cached 5 Min server-seitig → wir pollen alle 5 Min + beim Laden, kein Spam.
function pollCliHealth(): void {
  const el = document.getElementById("status-cli");
  if (!el) return;
  fetch("/api/cli/health", { credentials: "same-origin" })
    .then((r) => r.json())
    .then((d: { ok?: boolean; detail?: string; checkedAt?: string }) => {
      const ok = d?.ok === true;
      el.dataset.state = ok ? "ok" : "down";
      const when = d?.checkedAt
        ? new Date(d.checkedAt).toLocaleTimeString("de-AT", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "";
      el.title =
        (ok ? "CLI erreichbar — Tokens laufen" : "CLI NICHT erreichbar") +
        (d?.detail ? ` (${d.detail})` : "") +
        (when ? ` · geprüft ${when}` : "");
    })
    .catch(() => {
      el.dataset.state = "down";
      el.title = "CLI-Status nicht abrufbar";
    });
}
pollCliHealth();
// Ticket 2752: ein Punkt in der Fußzeile — im Hintergrund sieht ihn niemand.
starteSichtbarenTakt(pollCliHealth, 5 * 60_000);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") pollCliHealth();
});

// Pre.202: Modell + Modus im Footer als klickbare Buttons → Settings.
// Pre.803 (#64): Modell wohnt jetzt im „Bot"-Tab, Modus bei „Sprache".
function openEinstellungen(): void {
  goToView("settings");
  renderStatusBar();
  void holeLaufzeitModell();
}

async function oeffneAntwortBlatt(): Promise<void> {
  const vorab = await baueModellGruppe();
  await waehleTiefe(document.getElementById("status-tiefe"), vorab, "Antwort");
}

/** Die Modell-Auswahl als Gruppe im Blatt: wählt sofort, markiert den Haken neu, schließt nichts. */
async function baueModellGruppe(): Promise<HTMLElement> {
  // ⚠️ ZWEI DINGE, DIE LEICHT ZU VERWECHSELN SIND (27.08.2026): settings.model steuert den API-Weg (nur wenn das Abo
  // scheitert, kostet Guthaben, ist das Modell, das der Bot NENNT); settings.aboModell steuert, was im ABO antwortet —
  // das meint Martin. „Automatisch“ = Vorgabe: der Server nimmt Opus für Mails, Analysen, Rechnungen, sonst Sonnet.
  // Ein Wechsel kostet einmal einen Kaltstart (jedes Modell hat seine eigene warme Sitzung).
  const ersatz = fallbackLaeuft();
  let optionen: ReadonlyArray<{ value: string; label: string; beschreibung?: string }>;
  let aktiv: () => string;
  let speichere: (wahl: string) => void;
  if (ersatz) {
    const { BACKUP_APIS, aktuelleBackup } = await import("./lib/settings/modell-status.js");
    optionen = BACKUP_APIS.map((m) => ({ value: m.id, label: m.label, beschreibung: m.beschreibung }));
    aktiv = aktuelleBackup;
    speichere = (wahl) => {
      if (!BACKUP_APIS.some((m) => m.id === wahl)) return;
      saveSettings({ ...loadSettings(), fallbackModel: wahl });
      updateFooterEffectiveModel(wahl);
      void holeLaufzeitModell();
    };
  } else {
    optionen = ABO_MODELLE.map((m) => ({ value: m.id, label: aboAnzeige(m.id), beschreibung: m.description }));
    aktiv = () => loadSettings().aboModell ?? "auto";
    speichere = (wahl) => {
      if (!istAboWahl(wahl)) return;
      saveSettings({ ...loadSettings(), aboModell: wahl });
      renderStatusBar();
    };
  }
  const gruppe = document.createElement("section");
  gruppe.className = "antwort-blatt-modell";
  gruppe.setAttribute("role", "group");
  const name = document.createElement("h3");
  name.className = "schnell-einst-name sheet-gruppe-name";
  name.id = "antwort-blatt-modell-name";
  name.textContent = "Modell";
  gruppe.setAttribute("aria-labelledby", name.id);
  const satz = document.createElement("p");
  satz.className = "schnell-einst-hinweis sheet-gruppe-satz";
  satz.textContent = blattHinweis(ersatz, ersatz ? ersatzGrund : stufenGrund) || "Wer im Abo antwortet.";
  const liste = document.createElement("div");
  liste.className = "sheet-dialog-options sheet-dialog-options--raster";
  // Die Knöpfe stehen einmal da; eine Wahl setzt nur Haken und aria-current um — der Fokus bleibt auf dem Knopf.
  const knoepfe = new Map<string, HTMLButtonElement>();
  const markiere = (): void => {
    for (const [wert, b] of knoepfe) {
      const ist = wert === aktiv();
      b.classList.toggle("is-active", ist);
      if (ist) b.setAttribute("aria-current", "true");
      else b.removeAttribute("aria-current");
    }
  };
  for (const o of optionen) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "btn btn--ghost sheet-dialog-option";
    const n = document.createElement("span");
    n.className = "sheet-dialog-option-name";
    n.textContent = o.label;
    b.append(n);
    if (o.beschreibung) {
      const sub = document.createElement("span");
      sub.className = "sheet-dialog-option-sub";
      sub.textContent = o.beschreibung;
      b.append(sub);
    }
    b.addEventListener("click", () => {
      if (o.value === aktiv()) return;
      speichere(o.value);
      markiere();
    });
    knoepfe.set(o.value, b);
    liste.appendChild(b);
  }
  markiere();
  gruppe.append(name, satz, liste);
  return gruppe;
}



// 21.10.2026 (Martin): Modell, Antwortlänge und Werkzeuge sind EIN Chip — ein Blatt: oben das Modell, darunter Länge,
// Tempo, Stille, Emojis (waehleTiefe). Die Wahl des Modells speichert sofort und lässt das Blatt offen.
document.getElementById("status-llm")?.addEventListener("click", () => {
  void oeffneAntwortBlatt();
});
haeltChipNameAktuell(document.getElementById("status-llm"));
// 19.10.2026: „Mikro Auto“ im Eingabefeld — dieselbe Einstellung wie Mikrofon-Fenster und Einstellungen, kein zweiter Besitzer.
{
  const autoChip = document.getElementById("status-automikro");
  zeigeAutoMikroChip(autoChip);
  autoChip?.addEventListener("click", () => {
    schalteAutoMikroChip(autoChip, (text) => showToast(text, "info"));
  });
  window.addEventListener("voice-v8:settings-changed", () => zeigeAutoMikroChip(autoChip));
}
// 2026-08-27: #status-mode ist entfallen (siehe oben). Der Weg zu den
// Sprach-Einstellungen fuehrt weiterhin ueber das Menue.
document.getElementById("status-ext")?.addEventListener("click", () => {
  goToView("settings");
  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent("voice-v8:settings-subtab", {
        detail: { sub: "connectors" },
      }),
    );
  }, 50);
});
// Pre.191: Extension-Status alle 30s neu pollen (Connected→Disconnected
// kommt sonst erst bei Reload sichtbar).
/*
 * 17.09.2026 — bei dunklem Display schweigt das. Ein Symbol in der
 * Fusszeile, das niemand sieht, muss nicht alle 30 Sekunden gefunkt werden;
 * beim Zurueckkommen fragt der naechste Schlag ohnehin.
 */
starteSichtbarenTakt(() => void refreshExtensionStatus(), 30_000);
// Wenn Settings im selben Tab geändert werden, refreshen wir die Bar:
window.addEventListener("storage", (ev) => {
  if (ev.key === "voice-v8.settings") renderStatusBar();
});
// Pre.186: storage-Event feuert NICHT im selben Tab — daher zusätzlich
// custom-event aus saveSettings() aufgreifen (z.B. wenn der Voice-Mode-
// Picker im Settings-Tab umgeschaltet wird, aktualisiert sich die
// Footer-Modus-Anzeige sofort).
window.addEventListener("voice-v8:settings-changed", () => {
  renderStatusBar();
  void holeLaufzeitModell();
});
// Pre.191: Extension-Status auch refreshen wenn der User zurück auf den
// Chat-Tab wechselt (er hat evtl. gerade die Extension verknüpft).
window.addEventListener(
  "voice-v8:settings-changed",
  () => void refreshExtensionStatus(),
);
// Settings werden im selben Window via saveSettings() gespeichert — kein
// storage-Event. Wir lauschen daher zusätzlich auf Tab-Switch zurück
// zum Chat (dort sieht man die Status-Bar) und refreshen.
document.querySelectorAll<HTMLElement>(".nav-item[data-tab]").forEach((t) => {
  t.addEventListener("click", () => renderStatusBar());
});

setState("idle");

/**
 * Was der Anruf-Link `pawbot://anruf?callId=…` an der App braucht
 * (lib/anruf-link.ts). EIN Weg fuer den Tipp auf die Meldung und fuer
 * „Abheben“ auf der Karte der Seite (anrufKarte unten).
 */
function anrufLinkWeg(): AnrufLinkWeg {
  return {
    darfAnrufen,
    verbunden: () => connected,
    verbindet: () => connecting,
    zumChat: () => {
      if (
        document.body.dataset.activeView &&
        document.body.dataset.activeView !== "chat"
      ) {
        switchToTab("chat");
      }
    },
    setzeAnrufId: (id) => client.setzeAnrufId(id),
    starte: () => {
      const knopf = document.getElementById(
        "dock-connect",
      ) as HTMLButtonElement | null;
      if (!knopf) return false;
      knopf.click();
      return true;
    },
    melde: (text) => showToast(text, "info", 6000),
    // Stufe 2 (02.10.2026): beim Abheben über CallKit kann derselbe Link
    // zweimal ankommen (Kaltstart) — der zweite bleibt still.
    ...behandeltSpeicher(() => {
      try {
        return window.localStorage;
      } catch {
        return null;
      }
    }),
  };
}

/**
 * 06.10.2026: Ein Fokus filtert den Anruf von Vespera (CallKit code=3) und die
 * Seite ist vorne — dann zeigt SIE die Karte „Vespera ruft an“. „Abheben“ geht
 * den Weg des Tipps auf die Meldung (anrufLinkWeg). Regeln: lib/anruf-karte.ts.
 */
const anrufKarte = erstelleAnrufKarte({
  darfAnrufen,
  // Direkt nach dem Neuladen ist das Konto noch nicht geladen — dann später nochmal.
  kontoBekannt: () => currentUser !== null,
  sichtbar: () => document.visibilityState === "visible",
  imGespraech: () => connected || connecting,
  schonBehandelt: (id) => anrufLinkWeg().schonBehandelt?.(id) ?? false,
  zeige: zeigeAnrufKarte,
  abheben: (id) => behandleAnrufLink(anrufLink(id), anrufLinkWeg()),
  // In der App NIE ein Ton (er nähme dem Mikro-Motor die Tonsitzung), nur Haptik.
  tonErlaubt: () =>
    anrufKarteTonErlaubt({
      nativ: isNativePlatform(),
      nimmtAuf: pttRecorder.isRecording(),
      tagesaufnahme: mikroSitzungsStatus().tagesaufnahme,
    }),
  ton: () => playBeep("achtung"),
  haptik: () => hapticTap(20),
  spur: meldeDiagnose,
});

// ---------------------------------------------------------------------------
// Pre.1016: Home-Screen-Widget-Deep-Links (pawbot://aufnehmen | anrufen | chat).
// Die Widget-Tasten öffnen die App über das Custom-URL-Schema; @capacitor/app
// liefert die URL beim Kaltstart (getLaunchUrl) und danach laufend (appUrlOpen).
// Nur nativ — im Browser/PWA gibt es das App-Plugin nicht (No-op).
// ---------------------------------------------------------------------------
function routePawbotDeepLink(rawUrl: string): void {
  const m = /^pawbot:\/\/([a-zA-Z]+)/.exec(rawUrl);
  if (!m) return;
  switch (m[1].toLowerCase()) {
    case "login": {
      // 2026-06-30 (Martin): Nativer Google-Login — Einmal-Token aus dem
      // Deep-Link in der WebView gegen das Sitzungs-Cookie tauschen
      // (Server: /api/auth/native-set), dann ist die App eingeloggt.
      //
      // 16.09.2026 (Ticket 2314) — WARUM der Riegel: Das Token ist EINMALIG,
      // der Deep-Link wird aber ZWEIMAL zugestellt. Erst kommt er ueber
      // `appUrlOpen`; der Tausch endet mit `redirect("/")`, also einem
      // vollen Neuladen der WebView. Danach laeuft `wirePawbotDeepLinks`
      // erneut, und `getLaunchUrl()` gibt dieselbe Start-Adresse ZURUECK
      // (sie tut das nach JEDEM Reload — so steht es dort im Kommentar).
      // Der zweite Tausch trifft ein bereits verbrauchtes Token und landet
      // auf `/?login_error=native` — die Anmeldung war erfolgreich und sieht
      // trotzdem gescheitert aus. Genau das hat Martin gesehen: „dann kam
      // das daneben."
      //
      // ⚠️ Der vorhandene `pawbot:launchHandled`-Riegel greift hier NICHT:
      //    er sitzt nur im getLaunchUrl-Zweig und wird vom appUrlOpen-Weg
      //    nie gesetzt. Deshalb ein eigener Riegel am TOKEN, der an genau
      //    den einen Tausch bindet, nicht an „irgendein Deep-Link kam schon".
      //
      // 23.09.2026 (Ticket 2314): Der Riegel liegt jetzt in localStorage
      // (lib/nativ-login-riegel.ts, dort die Begründung) — am 18.09. fehlte
      // sessionStorage bei sechs von dreizehn Neuladungen (warum, ist offen),
      // und jedes Mal wurde dasselbe verbrauchte Token erneut getauscht.
      // Hält der Riegel, meldet er das: so zeigt das Protokoll, dass ein
      // alter Link zurückkam und NICHT mehr zum Server durchging.
      //
      // LESEHILFE „Riegel hielt": Die Zeile zählt NICHT jedes Neuladen der
      // Web-Ansicht. Sie erscheint nur, wenn der Login-Link den Router
      // erneut erreicht — über getLaunchUrl also nur, wenn
      // `pawbot:launchHandled` (sessionStorage) fehlte UND die zuletzt
      // geöffnete Adresse noch der Login-Link ist. Direkt nach einer
      // Google-Anmeldung in der laufenden App höchstens einmal (das Neuladen
      // nach dem Tausch); jede spätere zählt ein Neuladen mit verlorenem
      // sessionStorage. Neuladungen mit erhaltenem sessionStorage und alle
      // nach einem späteren Deep-Link (Widget) bleiben stumm.
      const entscheid = tauschEinmal(rawUrl);
      if (entscheid.tauschen) {
        window.location.href = entscheid.ziel;
      } else if (entscheid.grund === "schon-getauscht") {
        meldeDiagnose(
          "[nativ-login] Riegel hielt: Token schon getauscht, kein zweiter Tausch",
        );
      }
      break;
    }
    case "chat":
      document.getElementById("bnav-chat")?.click();
      break;
    case "ptt":
    case "push":
      // Ticket 270: Aktionstaste — sofort Freihand-Aufnahme.
      startPttHandsFree("aktionstaste");
      break;
    case "anrufen": {
      // 15.09.2026: nur fuer den Hauptnutzer — wie der Knopf selbst.
      if (!darfAnrufen()) break;
      const btn = document.getElementById(
        "dock-connect",
      ) as HTMLButtonElement | null;
      if (btn && !connected) btn.click();
      break;
    }
    case "anruf":
      // 01.10.2026, Ticket 2728: Vespera ruft an — die Meldung "Vespera ruft
      // an" traegt pawbot://anruf?callId=…. Dieselbe Folge wie der Tipp auf den
      // Anruf-Knopf (Klingelton, Warm-Mikro frei, handleConnect), aber mit der
      // Kennung, die /api/token einmalig annimmt. Die Regeln: lib/anruf-link.ts.
      // 06.10.2026: steht fuer DIESEN Anruf die Karte der Seite, geht sie.
      anrufKarte.anderswo(anrufIdAusLink(rawUrl));
      behandleAnrufLink(rawUrl, anrufLinkWeg());
      break;
    case "aufnehmen":
      // Widget-Mikro: Overlay UND Aufnahme starten. Ohne start:true
      // oeffnete sich nur ein Blatt, und das Widget blieb auf Idle.
      //
      // 2026-08-23 (Martin: "läuft 3 Sekunden, steht pausiert"): ZUERST das
      // Warm-Mikro sperren — synchron, vor allem anderen. Im Protokoll stand
      // auf die Sekunde genau, dass Push-to-Talk beim Widget-Tipp nach dem
      // Mikrofon greift und die eben erst angelegte Aufnahme-Session sofort
      // wieder sterben laesst. Der bestehende Schutz griff erst im
      // Aufnahmestart und damit zu spaet.
      warmMikroSperrenFuerTagesaufnahme("widget-aufnehmen");
      /*
       * 2026-08-23 (Martin: "Es aendert sich nichts an den Problemen"):
       *
       *   Nach drei Anlaeufen an diesem Fehler ist klar, dass die Ursache nicht
       *   zu erraten ist. Was fehlt, ist SICHT: Kommt der Deep-Link ueberhaupt
       *   an? Welche Fassung laeuft im Geraet? Haelt gerade jemand das Mikro?
       *   Nichts davon war bisher zu sehen — die Browser-Konsole erreicht
       *   niemand, und das Serverprotokoll zeigte nur die Folgen.
       *
       *   Diese Zeile beantwortet beim naechsten Tipp alle drei Fragen auf
       *   einmal. Sie bleibt: Der Weg ueber das Widget ist der einzige, bei dem
       *   App-Kaltstart, Mikro-Uebergabe und native Engine zusammentreffen —
       *   also genau die Stelle, an der man spaeter wieder blind waere.
       */
      void import("./lib/native-recorder.js").then((rec) => {
        meldeDiagnose(
          `[widget-start] deep-link=aufnehmen ` +
            `version=${webVersionAus(__BUILD_VERSION__)} ` +
            `recorder=${rec.isRecorderAvailable() ? rec.getRecorderState() : "PLUGIN-FEHLT"} ` +
            `sichtbar=${document.visibilityState}`,
        );
      });
      void import("./lib/alltag-view.js").then((m2) =>
        m2.openRecorderOverlay({ start: true }),
      );
      break;
    case "stop":
      // Stopp vom Widget: Sitzung UND Durchgehend-Absicht beenden, dann die
      // Liste. Overlay parallel zum Stopp zeigte eine Uhr, die weiterlief,
      // während das Header-Mikro schon weg war.
      void import("./lib/native-recorder.js").then(async (rec) => {
        await rec.stopRecordingForUser();
        const m2 = await import("./lib/alltag-view.js");
        m2.openAlltagSheet();
      });
      break;
    case "alltag":
      // „Mein Alltag" ansehen (Liste), NICHT aufnehmen.
      void import("./lib/alltag-view.js").then((m2) => m2.openAlltagSheet());
      break;
    case "leben":
      document.getElementById("bnav-leben")?.click();
      break;
    case "projekte":
      document.getElementById("bnav-projekte")?.click();
      break;
    case "essenz":
      openEssenceSheet(undefined, "direktlink");
      break;
    case "aufgaben":
      // 2026-07-14 (Martin, Widget-Glocke): direkt das Aufgaben-Modal.
      // 15.09.2026: nicht mehr ueber den Fusszeilen-Knopf — den gibt es
      // jetzt nur noch beim Hauptnutzer, der Weg aus dem Widget gilt aber
      // fuer alle.
      void import("./lib/tickets-view.js").then((m) => m.openTicketsModal());
      break;
  }
}
// 2026-06-29 (Martin): Nativer Push (APNs). Holt den iPhone-Geraetetoken und
// registriert ihn beim Server (/api/push/native-token). Nur nativ. register()
// braucht das aps-environment-Entitlement; ohne wirft es -> still per catch,
// bis der Build mit Push-Capability + APNs-Key live ist.
(function wireNativePush(): void {
  interface PushPlugin {
    checkPermissions: () => Promise<{ receive: string }>;
    requestPermissions: () => Promise<{ receive: string }>;
    register: () => Promise<void>;
    addListener: (event: string, cb: (data: unknown) => void) => unknown;
  }
  const push = getCapacitorPlugin<PushPlugin>("PushNotifications");
  if (!push) return;
  try {
    push.addListener("registration", (data) => {
      const token = (data as { value?: string })?.value;
      if (!token) return;
      void fetch("/api/push/native-token", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, platform: "ios" }),
      }).catch(() => {
        /* nicht kritisch — erneuter Versuch beim naechsten Start */
      });
    });
    push.addListener("registrationError", () => {
      /* z.B. fehlendes Entitlement — still bleiben */
    });
    push.addListener("pushNotificationActionPerformed", (data) => {
      const url = (
        data as { notification?: { data?: { url?: string } } }
      )?.notification?.data?.url;
      if (typeof url === "string" && url.startsWith("pawbot://")) {
        routePawbotDeepLink(url);
      } else if (typeof url === "string" && /^https?:\/\//.test(url)) {
        // 2026-07-04 (Martin): Notification-Tipp oeffnet http-Links direkt
        // (z.B. den geplanten Musik-Song) statt nur die App zu oeffnen.
        try {
          window.open(url, "_blank", "noopener,noreferrer");
        } catch {
          document.getElementById("bnav-chat")?.click();
        }
      } else {
        document.getElementById("bnav-chat")?.click();
      }
    });
    void (async () => {
      try {
        const perm = await push.checkPermissions();
        let receive = perm?.receive;
        if (receive === "prompt" || receive === "prompt-with-rationale") {
          receive = (await push.requestPermissions())?.receive;
        }
        if (receive === "granted") await push.register();
      } catch {
        /* register braucht aps-environment-Entitlement; bis dahin still */
      }
    })();
  } catch {
    /* Plugin-/Listener-Fehler ignorieren */
  }
})();

// 02.10.2026, Ticket 2728 (Stufe 2): das VoIP-Token der App (PushKit) beim
// Server melden — damit Vespera wie ein echter Anruf klingeln kann. Nur nativ,
// und nur mit einer App-Version, die es kennt (sonst still). lib/anruf-nativ.ts.
verbindeVoipToken(getCapacitorPlugin("PawbotVoice"), (body) =>
  fetch("/api/anruf/voip-token", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then((r) => r.ok),
);
// Jede Zeile ins Server-Protokoll; „callkit lehnt ab … code=3“ bei vorderer
// Seite zusätzlich als Karte „Vespera ruft an“ (06.10.2026, lib/anruf-karte.ts).
verbindeAnrufSpur(getCapacitorPlugin("PawbotVoice"), (zeile) => {
  meldeDiagnose(zeile);
  void anrufKarte.spur(zeile);
});
// Auflegen im Anruf-Bildschirm (CallKit) = derselbe Weg wie der Auflegen-Knopf.
verbindeAnrufAuflegen(getCapacitorPlugin("PawbotVoice"), () => {
  if (connected || connecting) void handleHangup();
});

(function wirePawbotDeepLinks(): void {
  const appPlugin = getCapacitorPlugin<{
    addListener: (
      event: string,
      cb: (data: { url?: string }) => void,
    ) => unknown;
    getLaunchUrl: () => Promise<{ url?: string } | null>;
  }>("App");
  if (!appPlugin) return;
  try {
    appPlugin.addListener("appUrlOpen", (data) => {
      if (data?.url) routePawbotDeepLink(data.url);
    });
    void appPlugin
      .getLaunchUrl()
      .then((r) => {
        if (!r?.url) return;
        // getLaunchUrl liefert die URSPRUNGS-Launch-URL und gibt sie nach JEDEM
        // WebView-Reload (z.B. Versions-Update) ERNEUT zurueck. Navigation waere
        // egal, aber „aufnehmen"/„stop" sind AKTIONEN -> ein Reload wuerde sonst
        // eine PHANTOM-Aufnahme starten („startet unvermittelt"). Darum die
        // Launch-URL pro WebView-Prozess nur EINMAL verarbeiten: sessionStorage
        // uebersteht location.reload(), wird aber bei echtem Kaltstart geleert.
        // (Ein Widget-Tipp bei laufender App kommt ueber appUrlOpen, nicht hier.)
        try {
          if (sessionStorage.getItem("pawbot:launchHandled")) return;
          sessionStorage.setItem("pawbot:launchHandled", "1");
        } catch {
          /* sessionStorage fehlt -> trotzdem einmal verarbeiten */
        }
        routePawbotDeepLink(r.url);
      })
      .catch(() => {
        /* keine Start-URL */
      });
  } catch {
    /* App-Plugin nicht verfügbar */
  }
})();

// ---------------------------------------------------------------------------
// Pre.1021 / 2290: Call-bewusster „Neue Version"-Knopf. Prüft leise, ob der
// Server eine neuere Version ausliefert (Vergleich der Build-Marke aus
// /build-version.json — früher die ganze index.html — mit der laufenden
// __BUILD_VERSION__). Zeigt einen Aktualisieren-Knopf NUR, wenn KEIN Anruf
// läuft (connected=false) — während eines Gesprächs hält er sich zurück, bis
// aufgelegt ist. So reißt ein Update nie ein Gespräch ab; der Nutzer
// entscheidet, wann neu geladen wird. (Reiner Web-Teil — kein erzwungener
// Neustart durch den Server mehr nötig.)
// ---------------------------------------------------------------------------
let updatePending = false;
// Zwei Quellen für „neu“ (29.10.2026): der Versionsvergleich und ein wartender Worker mit anderer Fassung. Der Knopf zeigt
// ihre Summe — und ein Vergleich, der jetzt „gleich“ sagt, nimmt SEINE Quelle wieder zurück (früher blieb es für immer wahr).
let neuDurchVersion = false;
let neuDurchWorker = false;
let letzteServerFassung = "";
function setzeUpdatePending(): void {
  updatePending = neuDurchVersion || neuDurchWorker;
}

/**
 * Das eigentliche Neuladen — mit einem letzten Blick, wenn es auf Zuruf kam. (11.10.2026, Prüfbefund zu Ticket 2669)
 *
 * Zwischen der Freigabe („Seite neu laden“ gesagt, Ruhe) und diesem Aufruf liegen die Erreichbarkeits-Prüfung (bis 4 s)
 * und das Auffrischen der Worker, beim wartenden Worker bis 3,5 s. Fängt er darin an zu sprechen oder startet eine
 * Aufnahme, lädt nichts (lib/app-neu-laden.ts). Der Knopf „Neue Version“ und das stille Nachladen sind nicht betroffen.
 */
function reloadJetzt(): void {
  if (neuLadenDarfJetzt()) location.reload();
}

function reloadForUpdate(): void {
  const doReload = (): void => {
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.update())))
        .finally(() => reloadJetzt());
    } else {
      reloadJetzt();
    }
  };
  // 2026-07-05 (Martin): ERST pruefen, ob der Server wirklich erreichbar ist —
  // sonst laedt "Aktualisieren" ins Offline. Kurzer HEAD-Ping mit 4s-Timeout.
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 4000);
  void fetch(`/?ping=${Date.now()}`, {
    method: "HEAD",
    cache: "no-store",
    signal: ctrl.signal,
  })
    .then(() => {
      window.clearTimeout(t);
      doReload();
    })
    .catch(() => {
      window.clearTimeout(t);
      window.dispatchEvent(
        new CustomEvent("voice-v8:toast", {
          detail: {
            message:
              "Noch offline — sobald du wieder Verbindung hast, aktualisiert Vespera von selbst.",
            kind: "info",
          },
        }),
      );
    });
}

/* -------------------------------------------------------------------------
 * Die Kopfzeile kennt ihren Platz (22.09.2026) — Messen und Anwenden stehen
 * seit 05.10.2026 in lib/kopf-ordnen.ts, die Entscheidung in lib/kopf-platz.ts.
 * ---------------------------------------------------------------------- */
starteKopfOrdnung();

// Die Pille „Neue Version“ (lib/neuladen-knopf.ts): nur sichtbar, wenn ein Update bereitliegt.
/**
 * EIN Weg „neue Version laden“ — der Knopf und das Fenster „Was ist neu“
 * (lib/was-ist-neu.ts, Ticket 2925) rufen ihn beide.
 */
function ladeNeueVersion(): void {
  // Schutz gegen die Schleife (lib/neue-version-versuch.ts): hing ein erster Versuch, geht der Druck gleich den harten Weg.
  const speicher = holeVersuchSpeicher();
  const klick = versuchBeiKlick(letzteServerFassung, leseVersuchMerker(speicher), Date.now());
  schreibeVersuchMerker(speicher, klick.merker);
  if (klick.hart) {
    meldeDiagnose("[update] harter Neustart der Seite (zweiter Druck)");
    // Der Druck ist ausdrücklich (kein Zuruf, kein Takt): nur der Anruf hält ihn auf, wie bei jedem Neuladen des Knopfs.
    void hartNeuLaden({
      darf: () => !connected,
      container: "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
      caches: "caches" in window ? caches : undefined,
      neuLaden: reloadJetzt,
    });
    return;
  }
  // Der Klick ist die Anforderung: wartender Worker uebernimmt, dann laedt
  // controllerchange neu; Sicherheitsnetz und Weg ohne Worker stehen dort.
  // Ein abgebrochener „Seite neu laden“-Zuruf von eben sperrt diesen Weg nicht (lib/app-neu-laden.ts).
  neuLadenOhneZuruf();
  void workerWechsel.aktivierenUndNeuLaden();
}
const neuladenKnopf = erzeugeNeuladenKnopf({ neuLaden: ladeNeueVersion });
const updatePill = neuladenKnopf.knopf;
// 2026-08-25 (Design-Analyse): Die Pille schwebte als `position: fixed` ueber
// dem Inhalt und verdeckte Text. Dagegen gab es bereits DREI Ausgleiche
// (12.07. tiefer gesetzt, 27.07. Chat-Abstand, 19.08. Zuhause, 20.08. `.view`)
// — und trotzdem lag sie am 25.08. auf der Tiefen Frage, weil deren Banner
// weder `.chat` noch `.view` ist, sondern direkt in <main> haengt.
//
// Statt eines vierten Ausgleichs: sie schwebt nicht mehr. Sie sitzt als ruhiger
// Chip in der Kopfleiste, neben Aufnahme-Anzeige und Task-Puls — dort, wo die
// anderen Zustandsanzeigen schon stehen. Kein Inhalt muss mehr ausweichen.
const updatePillSlot =
  document.querySelector(".topbar-meta") ?? document.body;
updatePillSlot.appendChild(updatePill);

/**
 * „Was ist neu“ (Ticket 2931, Martin 02.10.2026, 13:46): ein leiser Knopf mit
 * Punkt statt eines Fensters, das von selbst aufgeht — am Fuß des Menüs, nicht
 * in der Kopfleiste. Der Anzeige-Takt (maybeShowUpdate, alle 8 s) hält nur den
 * Stand des Knopfs frisch — geöffnet wird auf Antippen (lib/was-ist-neu.ts;
 * dort steht auch der Rückweg WAS_IST_NEU_KNOPF=0, für den `zustand` weiter
 * gebraucht wird; `sichtbar` daraus entscheidet auch, ob der Takt nachfragt).
 */
const knopfAnzeige = verdrahteKnopf((von) => void wasIstNeu.oeffne(von));
// Der Chip „Neu“ über dem Eingabefeld ist weg (Martin 21.10.2026): nur der Knopf im Menü zeichnet den Stand.
const wasIstNeuAnzeige = knopfAnzeige;
const wasIstNeu = erzeugeWasIstNeu({
  updateBereit: () => updatePending && !updatePill.hidden,
  serverVersion: () => pendingServerVer,
  laufendeVersion: () =>
    typeof __BUILD_VERSION__ === "string" ? webVersionAus(__BUILD_VERSION__) : "",
  zustand: () => ({
    anruf: connected,
    sprechtaste: pttState !== "idle",
    aufnahme: webMikroOffen() || aufnahmeLaeuft(),
    ton: aktuellerTon() !== null,
    sichtbar: document.visibilityState === "visible",
  }),
  neuLaden: ladeNeueVersion,
  anzeige: wasIstNeuAnzeige,
  // Gelesen-Stand im Konto: alle Geräte zeigen dieselbe Zahl (Fehler/offline → nur der lokale Merker).
  konto: serverKontoMerker(),
  // Die Karte „Das ist neu“ im Verlauf: nur eine zugleich, die neue ersetzt die alte.
  karte: (o) => {
    refs.chat.querySelector(".neuk")?.remove();
    const karte = baueNeuKarte(o);
    insertOrdered(karte, Date.now());
    scrollChatToBottomReliably();
    karte.scrollIntoView?.({ block: "nearest" });
  },
  // Am Telefon liegt das Menü über jedem Fenster. Am Rechner bleibt die Leiste, wie sie
  // ist — closeDrawerForNavigation stellte dort voll oder Rail nach der Fensterbreite
  // neu ein; das gehört zu einem Seitenwechsel, nicht zu einem Fenster.
  menueZu: () => {
    if (!isDesktop()) closeDrawer();
  },
  fokusErsatz: () => refs.chatBtn,
  melde: meldeDiagnose,
});

function maybeShowUpdate(): void {
  // Während eines Anrufs (connected) NICHT anzeigen — wartet bis aufgelegt.
  // 2026-07-12 (Martin, "zu viel"): NICHT zeigen, wenn das Service-Worker-
  // Banner "Neue Version bereit" ohnehin da ist — sonst doppelte Anzeige.
  // 2026-07-25 (Martin: "es sollte nur ein Button erscheinen"): Der Knopf ist
  // der EINZIGE Weg. Das automatische Neuladen von 2026-07-04 ist raus — es
  // sollte die Schleife "Fix ausgeliefert, aber Geraet testet den alten Stand"
  // beenden, hat dafuer aber laufende Arbeit abgerissen. Damit entfaellt auch
  // der Kreisbrecher, den es nur wegen des Selbstladens brauchte.
  // Waehrend eines Anrufs bleibt der Knopf verborgen, und wenn das
  // Service-Worker-Banner ohnehin dasteht, zeigen wir nicht doppelt.
  // 2026-07-27 (Martin: "es stehen immer zwei verschiedene Buttons da"): Die
  // Ruecksicht auf ein zweites Update-Banner ist entfallen — es gibt keins
  // mehr. Der Service-Worker meldet ueber genau diesen Knopf. Damit haengt die
  // Sichtbarkeit nur noch an zwei Dingen: liegt etwas Neues bereit, und stoert
  // es gerade (im Anruf zeigen wir nichts).
  // Sichtbar nur bei bereitliegendem Update, im Anruf verborgen (17.10.2026, Martin: „nur dann, wenn es ein Update gibt“).
  // 8.0.0-pre.2268: keine Zahl im Knopf — er bleibt nur „Neue Version“ (Zahl rutschte auf dem Telefon unter den Text).
  const bereit = updatePending && !connected;
  neuladenKnopf.zeige(bereit, connected);
  // 2026-07-27 (Design-Audit, Punkt 7): Die Pille schwebt und reserviert sich
  // keinen Platz — im Chat verdeckte sie zwei Textzeilen mitten im Satz.
  document.body.classList.toggle("hat-update-pille", bereit);
  // „Was ist neu“: den Stand des Knopfs frisch halten — auch ohne Pille, denn was still
  // nachgeladen wurde, ist genauso neu. Billig: gefragt wird je Version des Dienstes und
  // danach höchstens alle fünf Minuten, solange die Seite zu sehen ist.
  void wasIstNeu.versuche();
}

// Gelesen-Stand des Kontos: alle 30 s bei sichtbarer Seite und sofort beim Zurückkommen in die App — liest ein
// anderes Gerät, zieht dieses binnen Sekunden nach (lib/was-ist-neu.ts: gleiche).
setInterval(() => {
  if (document.visibilityState === "visible") void wasIstNeu.gleiche();
}, KONTO_TAKT_MS);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void wasIstNeu.gleiche(true);
});
window.addEventListener("focus", () => void wasIstNeu.gleiche(true));

let pendingServerVer = "";
async function checkForUpdate(): Promise<void> {
  const running =
    typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "";
  try {
    // 8.0.0-pre.2290: statt ~66 kB index.html nur ~50 Byte JSON.
    // Fallback auf HTML-Parse, falls ein alter Deploy die Datei noch nicht hat.
    let serverVer: string | undefined;
    const jr = await fetch(`/build-version.json?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (jr.ok) {
      const j = (await jr.json()) as { v?: string };
      if (typeof j.v === "string" && j.v) serverVer = j.v;
    } else {
      const r = await fetch("/", {
        cache: "no-store",
        credentials: "same-origin",
      });
      if (!r.ok) return;
      const html = await r.text();
      const m = /app\.js\?v=([^"'&\s>]+)/.exec(html);
      serverVer = m?.[1];
    }
    if (serverVer && running) {
      letzteServerFassung = serverVer;
      neuDurchVersion = serverIstNeuer(running, serverVer);
      if (neuDurchVersion) pendingServerVer = webVersionAus(serverVer) || serverVer;
      setzeUpdatePending();
      nachKlickPruefen(serverVer, running);
    }
  } catch {
    /* offline/fehlgeschlagen — beim nächsten Mal erneut */
  }
  maybeShowUpdate();
}

/**
 * Nach einem Klick auf „Neue Version“ kam die Seite mit der alten Fassung zurück? Ein harter Versuch, dann eine klare Zeile.
 * Hart nur, wenn nichts läuft (dieselbe Regel wie jedes Neuladen: lib/app-neu-laden.ts, neuLadenDarfJetzt).
 */
function nachKlickPruefen(server: string, laufend: string): void {
  const speicher = holeVersuchSpeicher();
  const r = versuchBeimLaden(leseVersuchMerker(speicher), server, laufend, Date.now());
  // Ohne Zuruf und ohne Druck: nur bei Ruhe (dieselben Hindernisse wie das stille Nachladen, ohne Sichtbarkeit und Entwurf).
  // Bei Hindernis bleibt der Merker stehen; der nächste Takt versucht es erneut.
  if (r.aktion === "hart" && !ruheFuerSelbstLaden()) return;
  schreibeVersuchMerker(speicher, r.merker);
  if (r.aktion === "hart") {
    meldeDiagnose("[update] Seite kam mit alter Fassung zurück — ein harter Versuch");
    void hartNeuLaden({
      darf: ruheFuerSelbstLaden,
      container: "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
      caches: "caches" in window ? caches : undefined,
      neuLaden: reloadJetzt,
    });
  } else if (r.aktion === "melden") {
    meldeDiagnose("[update] harter Versuch ohne Erfolg — gemeldet");
    showToast(MELDUNG_HAENGT, "info", 12000);
  }
}

/** Ruhe für ein Neuladen, das niemand angefordert hat: nichts läuft (Anruf, Ton, Sprechtaste, Mikro, Antwort). */
function ruheFuerSelbstLaden(): boolean {
  return (
    stillNachladenHindernis({
      updateBereit: true,
      sichtbar: false,
      anruf: connected,
      ton: aktuellerTon() !== null,
      sprechtaste: pttState !== "idle",
      mikro: webMikroOffen() || aufnahmeLaeuft(),
      antwort: laufenderZug !== null,
      entwurf: false,
    }) === null && neuLadenDarfJetzt()
  );
}

/**
 * Neue Version STILL nachladen (25.09.2026, Martin). Nur verdeckt und nur,
 * wenn nichts läuft — die Regel steht in lib/stilles-update.ts. Gefragt wird
 * beim Verdecken und bei jedem Prüf-Takt; die Pille bleibt für den sichtbaren
 * Fall.
 */
let stillLaedt = false;
function versucheStillesNachladen(anlass: string): void {
  if (stillLaedt) return;
  const hindernis = stillNachladenHindernis({
    updateBereit: updatePending,
    sichtbar: document.visibilityState === "visible",
    anruf: connected,
    ton: aktuellerTon() !== null,
    sprechtaste: pttState !== "idle",
    mikro: webMikroOffen() || aufnahmeLaeuft(),
    antwort: laufenderZug !== null,
    entwurf: textField.value.trim().length > 0,
  });
  if (hindernis) return;
  stillLaedt = true;
  meldeDiagnose(stillNachladenZeile(anlass, pendingServerVer));
  // Ticket 2653: derselbe Weg wie der Knopf. Ein blosses Neuladen liess einen
  // wartenden Worker warten — die neue Seite meldete ihn wieder als neu und
  // lud fuenf Minuten spaeter erneut, den ganzen Tag lang.
  neuLadenOhneZuruf();
  void workerWechsel.aktivierenUndNeuLaden();
}

// Prüfen beim Sichtbarwerden + alle 5 Min; Anzeige-Gate alle 8 s (greift, sobald
// ein Anruf endet — reiner DOM-Toggle, vernachlässigbar).
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void checkForUpdate();
  if (document.visibilityState === "hidden") versucheStillesNachladen("verdeckt");
});
setInterval(
  () => void checkForUpdate().then(() => versucheStillesNachladen("takt")),
  5 * 60_000,
);
setInterval(gesteuert("live", maybeShowUpdate, true), 8_000);
void checkForUpdate();

// Home-Screen-Widget (native) mit frischer „Mein Alltag"-Info versorgen: beim
// Start + bei jedem Sichtbarwerden, damit das Widget aktuelle Daten zeigt, auch
// ohne die Ansicht zu öffnen. Lazy import; im Browser/alter Hülle stiller No-op.
function refreshAlltagWidgetSafe(): void {
  void import("./lib/alltag-view.js").then((m) => m.refreshAlltagWidget());
}
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") refreshAlltagWidgetSafe();
});
refreshAlltagWidgetSafe();

// Die Abspielleiste an den Ton-Leitstand haengen. Sie erscheint nur, wenn
// etwas klingt — und zeigt dann IMMER das, was wirklich laeuft, gleich welcher
// Weg den Ton erzeugt (Spur, Vorlesen, Musik, Anruf).
starteTonLeiste();
// Solange etwas klingt, schweigt die Tagesaufnahme — sonst nimmt sie die
// vorgelesene Antwort mit auf. Haengt am Leitstand, nicht am Mikrofon:
// der erste Versuch im nativen enterPlaybackHiFi konnte Vorlesen nie sehen.
starteRekorderSchonung();
